import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import i18n from './i18n';
import './App.css';
import { verifyLicenseSignatureFrontend } from './utils/licenseVerification';
import UserAuth from './components/UserAuth';
import CompactPortraitView from './components/CompactPortraitView';
import CompactVorderseiteDialog from './components/CompactVorderseiteDialog';
import FeedbackDialog from './components/FeedbackDialog';
import FeedbackStatusNotification from './components/FeedbackStatusNotification';
import VersionUpdateBadge from './components/VersionUpdateBadge';
import { initializeErrorHandler, getPendingErrors, clearPendingErrors, sendErrorToBackend } from './utils/errorHandler';
import { getFooterText, FRONTEND_VERSION, getVersionString } from './config/version';
import { getTechVersions } from './config/techVersions';
import { checkCvesForTechStack } from './utils/cveCheck';
import { checkApiCompatibility } from './utils/apiCompatibility';
import { migrateSavedState, validateMigratedState } from './utils/stateMigration';
import { computeStats, multilinearOLS, kMeans, analyzeEinflussgroessenZeitbestimmend, buildDesignMatrixEA, simpleOLSWithBand } from './utils/analysisStats';
import EgRegressionChart from './components/EgRegressionChart';
import { getDeviceId, getDeviceName, setCustomDeviceName, getCustomDeviceName } from './utils/deviceId';
import { sendConfigTelemetry } from './utils/telemetry';
import ReportContent from './components/ReportContent';
import RenameSessionDialog from './components/RenameSessionDialog';
import LicenseImportResultDialog from './components/LicenseImportResultDialog';
import {
  listSources,
  getSource,
  createSource,
  updateSourceMeta,
  updateSourceContent,
  cloneSource,
  deleteSource,
  getSourceSelection,
  updateSourceSelection
} from './services/sourceApi';

// IndexedDB-Hilfsfunktionen
const DB_NAME = 'stoppuhr_db';
const DB_VERSION = 4; // Erhöht für Feedback-Store
const STORE_MEASUREMENT_STATE = 'measurementState';
const STORE_TEMP_FILES = 'tempFiles';
const STORE_BACKUP_SYNC = 'backupSync';
const STORE_PHOTOS = 'photos';
const STORE_AUDIT_LOGS = 'audit_logs';
const STORE_FEEDBACK_PENDING = 'feedbackPending';

// Erwarteter Produktcode für diese Anwendung
const EXPECTED_PRODUCT_CODE = 'ZEITAUF-MOBIL';

// Maximale Anzahl der Sessions in der History
const MAX_SESSIONS = 20;
const BACKEND_SESSIONS_PAGE_SIZE = 100;

let dbInstance = null;

const openDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB wird nicht unterstützt'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Store für Messungszustand
      if (!db.objectStoreNames.contains(STORE_MEASUREMENT_STATE)) {
        const stateStore = db.createObjectStore(STORE_MEASUREMENT_STATE, { keyPath: 'key' });
        stateStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Store für temporäre Dateien
      if (!db.objectStoreNames.contains(STORE_TEMP_FILES)) {
        const tempStore = db.createObjectStore(STORE_TEMP_FILES, { keyPath: 'fileName' });
        tempStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Store für Backup-Synchronisation
      if (!db.objectStoreNames.contains(STORE_BACKUP_SYNC)) {
        const backupStore = db.createObjectStore(STORE_BACKUP_SYNC, { keyPath: 'key' });
        backupStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Store für Fotos
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        const photoStore = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id', autoIncrement: true });
        photoStore.createIndex('measurementId', 'measurementId', { unique: false });
        photoStore.createIndex('sessionId', 'sessionId', { unique: false });
      }
      
      // Store für Audit-Logs
      if (!db.objectStoreNames.contains(STORE_AUDIT_LOGS)) {
        const auditStore = db.createObjectStore(STORE_AUDIT_LOGS, { keyPath: 'id', autoIncrement: true });
        auditStore.createIndex('sessionId', 'sessionId', { unique: false });
        auditStore.createIndex('timestamp', 'timestamp', { unique: false });
        auditStore.createIndex('measurementNumber', 'measurementNumber', { unique: false });
        auditStore.createIndex('uploaded', 'uploaded', { unique: false });
      }
      
      // Store für ausstehende Feedbacks
      if (!db.objectStoreNames.contains(STORE_FEEDBACK_PENDING)) {
        const feedbackStore = db.createObjectStore(STORE_FEEDBACK_PENDING, { keyPath: 'id', autoIncrement: true });
        feedbackStore.createIndex('uploaded', 'uploaded', { unique: false });
        feedbackStore.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });
};

// Tab-ID für Multi-Tab-Unterstützung
// Generiert oder liest eine eindeutige Tab-ID aus sessionStorage
// Die Tab-ID überlebt Reload/Versionswechsel im selben Tab
const getOrCreateTabId = () => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    // Fallback für SSR oder Umgebungen ohne sessionStorage
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  try {
    let tabId = sessionStorage.getItem('stoppuhr_tab_id');
    if (!tabId) {
      // Generiere neue Tab-ID
      tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('stoppuhr_tab_id', tabId);
    }
    return tabId;
  } catch (error) {
    // Fallback bei Fehler (z.B. QuotaExceeded)
    console.warn('[Tab-ID] Fehler beim Zugriff auf sessionStorage:', error);
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

// Speichere Messungszustand - zuerst localStorage (schnell, synchron), dann IndexedDB (Backup)
const saveMeasurementState = async (key, value) => {
  // Multi-Tab-Unterstützung für update_resume_state
  if (key === 'update_resume_state') {
    const tabId = getOrCreateTabId();
    const perTabKey = `${key}_tab_${tabId}`;
    
    // 1. Speichere in Per-Tab-Key (localStorage)
    try {
      localStorage.setItem(`stoppuhr_${perTabKey}`, JSON.stringify(value));
    } catch (error) {
      console.warn('localStorage Fehler beim Speichern (Per-Tab-Key):', error);
    }
    
    // 2. Speichere auch in Global-Key (localStorage)
    try {
      localStorage.setItem(`stoppuhr_${key}`, JSON.stringify(value));
    } catch (error) {
      console.warn('localStorage Fehler beim Speichern (Global-Key):', error);
    }
    
    // 3. IndexedDB nur wenn online (beide Keys)
    if (typeof navigator !== 'undefined' && navigator.onLine !== false) {
      try {
        const db = await openDB();
        const transaction = db.transaction([STORE_MEASUREMENT_STATE], 'readwrite');
        const store = transaction.objectStore(STORE_MEASUREMENT_STATE);
        
        // Speichere Per-Tab-Key
        await store.put({ key: perTabKey, value, timestamp: Date.now() });
        
        // Speichere Global-Key
        await store.put({ key, value, timestamp: Date.now() });
      } catch (error) {
        // Ignoriere Fehler - localStorage ist bereits gespeichert
        console.warn('IndexedDB Backup Fehler (nicht kritisch):', error);
      }
    }
    // Offline: IndexedDB wird übersprungen (localStorage reicht aus)
    return;
  }
  
  // Für andere Keys: Standard-Verhalten (nur Global-Key)
  // 1. Immer zuerst localStorage (schnell, synchron, funktioniert sofort)
  try {
    localStorage.setItem(`stoppuhr_${key}`, JSON.stringify(value));
  } catch (error) {
    console.warn('localStorage Fehler beim Speichern:', error);
  }
  
  // 2. IndexedDB nur wenn online (offline kann langsam sein oder hängen)
  // WICHTIG: localStorage ist bereits gespeichert, IndexedDB ist nur Backup
  if (typeof navigator !== 'undefined' && navigator.onLine !== false) {
    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_MEASUREMENT_STATE], 'readwrite');
      const store = transaction.objectStore(STORE_MEASUREMENT_STATE);
      await store.put({ key, value, timestamp: Date.now() });
    } catch (error) {
      // Ignoriere Fehler - localStorage ist bereits gespeichert
      console.warn('IndexedDB Backup Fehler (nicht kritisch):', error);
    }
  }
  // Offline: IndexedDB wird übersprungen (localStorage reicht aus)
};

// Konstante für Debug-Log-Aktivierung (aus localStorage gelesen)
const getEnableDebugLogs = () => {
  try {
    const stored = localStorage.getItem('stoppuhr_enable_debug_logs');
    if (stored === null) {
      // Standard: false (immer deaktiviert, auch in Development)
      return false;
    }
    return stored === 'true';
  } catch {
    return false;
  }
};

// Agent-Ingest: fetch an 127.0.0.1:7242 nur wenn explizit aktiviert
// Verhindert net::ERR_CONNECTION_REFUSED, wenn kein Ingest-Server läuft
const getEnableAgentIngest = () => {
  try {
    return localStorage.getItem('stoppuhr_enable_agent_ingest') === 'true';
  } catch { return false; }
};

// Agent-Logging Helper (nur wenn aktiviert)
// Diese Funktion sendet Debug-Logs an den Agent-Server, aber nur wenn aktiviert
const agentLog = (endpointId, location, message, data, hypothesisId = null) => {
  // Prüfe ob Debug-Logs aktiviert sind
  if (!getEnableDebugLogs()) {
    return;
  }
  
  // In Production: Keine Logs senden (zusätzliche Sicherheit)
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  
  // Ingest nur wenn explizit aktiviert (7242 muss laufen, sonst ERR_CONNECTION_REFUSED)
  if (!getEnableAgentIngest()) {
    return;
  }
  
  // Sende Logs nur wenn aktiviert
  fetch(`http://127.0.0.1:7242/ingest/${endpointId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      location,
      message,
      data,
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'run1',
      hypothesisId: hypothesisId || 'A'
    })
  }).catch(() => {});
};

// Debug-Logging Helper (speichert auch in localStorage für UI-Anzeige)
const debugLog = (location, message, data, hypothesisId = null) => {
  // Prüfe ob Debug-Logs aktiviert sind
  if (!getEnableDebugLogs()) {
    return;
  }
  
  const logEntry = {
    location,
    message,
    data,
    hypothesisId,
    timestamp: Date.now(),
    time: new Date().toLocaleTimeString('de-DE')
  };
  
  // Speichere in localStorage (max. 100 Einträge)
  try {
    const existingLogs = JSON.parse(localStorage.getItem('stoppuhr_debug_logs') || '[]');
    existingLogs.push(logEntry);
    // Behalte nur die letzten 100 Einträge
    const limitedLogs = existingLogs.slice(-100);
    localStorage.setItem('stoppuhr_debug_logs', JSON.stringify(limitedLogs));
  } catch (error) {
    console.warn('[Debug] Fehler beim Speichern der Logs:', error);
  }
  
  // Sende auch per fetch (nur wenn aktiviert und in Development)
  if (process.env.NODE_ENV !== 'production') {
    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', location, message, data, hypothesisId);
  }
};

// Validiere State: Prüfe, ob er gültig ist
const validateMeasurementState = (state, key) => {
  if (!state) return false;
  
  // Wenn isRunning true ist, muss selectedSession vorhanden sein
  if (state.isRunning && !state.selectedSession) {
    console.warn(`[State-Validation] Ungültiger State erkannt (isRunning ohne selectedSession) für Key: ${key}`);
    return false;
  }
  
  // Wenn selectedSession vorhanden ist, muss es eine gültige ID haben
  if (state.selectedSession && !state.selectedSession.id) {
    console.warn(`[State-Validation] Ungültiger State erkannt (selectedSession ohne id) für Key: ${key}`);
    return false;
  }
  
  // Wenn measurements vorhanden sind, müssen sie ein Array sein
  if (state.measurements && !Array.isArray(state.measurements)) {
    console.warn(`[State-Validation] Ungültiger State erkannt (measurements ist kein Array) für Key: ${key}`);
    return false;
  }
  
  return true;
};

// Bereinige korrupten State (löscht aus localStorage und IndexedDB)
const cleanupCorruptedState = async (key) => {
  console.warn(`[State-Cleanup] Bereinige korrupten State für Key: ${key}`);
  try {
    localStorage.removeItem(`stoppuhr_${key}`);
    await deleteMeasurementState(key);
    console.log(`[State-Cleanup] State für Key ${key} erfolgreich bereinigt`);
  } catch (error) {
    console.error(`[State-Cleanup] Fehler beim Bereinigen des States für Key ${key}:`, error);
  }
};

// Lade Messungszustand - zuerst localStorage (schnell, synchron), dann IndexedDB (Fallback)
// WICHTIG: Validiert automatisch und bereinigt korrupte States
const loadMeasurementState = async (key) => {
  // Multi-Tab-Unterstützung für update_resume_state
  if (key === 'update_resume_state') {
    const tabId = getOrCreateTabId();
    const perTabKey = `${key}_tab_${tabId}`;
    
    // 1. Versuche zuerst Per-Tab-Key aus localStorage
    try {
      const perTabData = localStorage.getItem(`stoppuhr_${perTabKey}`);
      if (perTabData) {
        const state = JSON.parse(perTabData);
        // Validiere State - wenn ungültig, bereinige und versuche Global-Key
        if (!validateMeasurementState(state, perTabKey)) {
          await cleanupCorruptedState(perTabKey);
          // Fallback auf Global-Key weiter unten
        } else {
          // Per-Tab-Key gefunden und gültig - verwende diesen
          return state;
        }
      }
    } catch (error) {
      console.warn('localStorage Fehler beim Laden (Per-Tab-Key):', error);
      await cleanupCorruptedState(perTabKey);
    }
    
    // 2. Fallback auf Global-Key aus localStorage
    try {
      const localData = localStorage.getItem(`stoppuhr_${key}`);
      if (localData) {
        const state = JSON.parse(localData);
        // Validiere State - wenn ungültig, bereinige und gib null zurück
        if (!validateMeasurementState(state, key)) {
          await cleanupCorruptedState(key);
          return null;
        }
        // Global-Key gefunden und gültig - verwende diesen
        return state;
      }
    } catch (error) {
      console.warn('localStorage Fehler beim Laden (Global-Key):', error);
      await cleanupCorruptedState(key);
    }
    
    // 3. Fallback auf IndexedDB nur wenn online UND localStorage leer
    // WICHTIG: Offline überspringen (IndexedDB kann langsam sein oder hängen)
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      // Offline: IndexedDB überspringen (kann langsam sein)
      return null;
    }
    
    // IndexedDB-Versuch (nur wenn online) - zuerst Per-Tab-Key, dann Global-Key
    try {
      const db = await openDB();
      const transaction = db.transaction([STORE_MEASUREMENT_STATE], 'readonly');
      const store = transaction.objectStore(STORE_MEASUREMENT_STATE);
      
      // Versuche zuerst Per-Tab-Key
      const perTabRequest = store.get(perTabKey);
      
      return new Promise((resolve) => {
        // Timeout für IndexedDB (falls es hängt) - reduziert auf 500ms für schnellere Wiederherstellung
        const timeout = setTimeout(async () => {
          console.warn('IndexedDB Timeout, verwende localStorage');
          const fallback = localStorage.getItem(`stoppuhr_${perTabKey}`) || localStorage.getItem(`stoppuhr_${key}`);
          if (fallback) {
            try {
              const state = JSON.parse(fallback);
              if (!validateMeasurementState(state, key)) {
                await cleanupCorruptedState(key);
                resolve(null);
              } else {
                resolve(state);
              }
            } catch (error) {
              await cleanupCorruptedState(key);
              resolve(null);
            }
          } else {
            resolve(null);
          }
        }, 500);
        
        perTabRequest.onsuccess = async () => {
          clearTimeout(timeout);
          if (perTabRequest.result) {
            const state = perTabRequest.result.value;
            // Validiere State - wenn ungültig, versuche Global-Key
            if (!validateMeasurementState(state, perTabKey)) {
              await cleanupCorruptedState(perTabKey);
              // Versuche Global-Key
              const globalRequest = store.get(key);
              globalRequest.onsuccess = async () => {
                if (globalRequest.result) {
                  const globalState = globalRequest.result.value;
                  if (!validateMeasurementState(globalState, key)) {
                    await cleanupCorruptedState(key);
                    resolve(null);
                  } else {
                    resolve(globalState);
                  }
                } else {
                  resolve(null);
                }
              };
              globalRequest.onerror = () => resolve(null);
            } else {
              resolve(state);
            }
          } else {
            // Per-Tab-Key nicht gefunden, versuche Global-Key
            const globalRequest = store.get(key);
            globalRequest.onsuccess = async () => {
              clearTimeout(timeout);
              if (globalRequest.result) {
                const state = globalRequest.result.value;
                if (!validateMeasurementState(state, key)) {
                  await cleanupCorruptedState(key);
                  resolve(null);
                } else {
                  resolve(state);
                }
              } else {
                resolve(null);
              }
            };
            globalRequest.onerror = () => {
              clearTimeout(timeout);
              resolve(null);
            };
          }
        };
        
        perTabRequest.onerror = async () => {
          clearTimeout(timeout);
          // Fallback auf Global-Key
          const globalRequest = store.get(key);
          globalRequest.onsuccess = async () => {
            if (globalRequest.result) {
              const state = globalRequest.result.value;
              if (!validateMeasurementState(state, key)) {
                await cleanupCorruptedState(key);
                resolve(null);
              } else {
                resolve(state);
              }
            } else {
              resolve(null);
            }
          };
          globalRequest.onerror = () => resolve(null);
        };
      });
    } catch (error) {
      console.warn('IndexedDB Fehler beim Laden:', error);
      return null;
    }
  }
  
  // Für andere Keys: Standard-Verhalten (nur Global-Key)
  // 1. Versuche zuerst localStorage (schnell, synchron, funktioniert sofort)
  try {
    const localData = localStorage.getItem(`stoppuhr_${key}`);
    if (localData) {
      const state = JSON.parse(localData);
      // Validiere State - wenn ungültig, bereinige und gib null zurück
      if (!validateMeasurementState(state, key)) {
        await cleanupCorruptedState(key);
        return null;
      }
      // WICHTIG: Wenn localStorage vorhanden, IndexedDB NICHT versuchen (schneller)
      return state;
    }
  } catch (error) {
    console.warn('localStorage Fehler beim Laden:', error);
    // Korrupter State erkannt - bereinige
    await cleanupCorruptedState(key);
  }
  
  // 2. Fallback auf IndexedDB nur wenn online UND localStorage leer
  // WICHTIG: Offline überspringen (IndexedDB kann langsam sein oder hängen)
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    // Offline: IndexedDB überspringen (kann langsam sein)
    return null;
  }
  
  // IndexedDB-Versuch (nur wenn online)
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_MEASUREMENT_STATE], 'readonly');
    const store = transaction.objectStore(STORE_MEASUREMENT_STATE);
    const request = store.get(key);
    
    return new Promise((resolve) => {
      // Timeout für IndexedDB (falls es hängt) - reduziert auf 500ms für schnellere Wiederherstellung
      const timeout = setTimeout(async () => {
        console.warn('IndexedDB Timeout, verwende localStorage');
        const fallback = localStorage.getItem(`stoppuhr_${key}`);
        if (fallback) {
          try {
            const state = JSON.parse(fallback);
            if (!validateMeasurementState(state, key)) {
              await cleanupCorruptedState(key);
              resolve(null);
            } else {
              resolve(state);
            }
          } catch (error) {
            await cleanupCorruptedState(key);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      }, 500); // 500ms Timeout (reduziert von 1000ms für schnellere Wiederherstellung)
      
      request.onsuccess = async () => {
        clearTimeout(timeout);
        if (request.result) {
          const state = request.result.value;
          // Validiere State - wenn ungültig, bereinige und gib null zurück
          if (!validateMeasurementState(state, key)) {
            await cleanupCorruptedState(key);
            resolve(null);
          } else {
            resolve(state);
          }
        } else {
          resolve(null);
        }
      };
      request.onerror = async () => {
        clearTimeout(timeout);
        // Fallback auf localStorage
        const fallback = localStorage.getItem(`stoppuhr_${key}`);
        if (fallback) {
          try {
            const state = JSON.parse(fallback);
            if (!validateMeasurementState(state, key)) {
              await cleanupCorruptedState(key);
              resolve(null);
            } else {
              resolve(state);
            }
          } catch (error) {
            await cleanupCorruptedState(key);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };
    });
  } catch (error) {
    console.warn('IndexedDB Fehler, verwende localStorage:', error);
    // Fallback auf localStorage
    const fallback = localStorage.getItem(`stoppuhr_${key}`);
    if (fallback) {
      try {
        const state = JSON.parse(fallback);
        if (!validateMeasurementState(state, key)) {
          await cleanupCorruptedState(key);
          return null;
        }
        return state;
      } catch (error) {
        await cleanupCorruptedState(key);
        return null;
      }
    }
    return null;
  }
};

// Lösche Messungszustand aus IndexedDB (mit Fallback auf localStorage)
const deleteMeasurementState = async (key) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_MEASUREMENT_STATE], 'readwrite');
    const store = transaction.objectStore(STORE_MEASUREMENT_STATE);
    await store.delete(key);
  } catch (error) {
    console.warn('IndexedDB Fehler, verwende localStorage:', error);
  }
  // Auch aus localStorage löschen
  localStorage.removeItem(`stoppuhr_${key}`);
};

// Speichere temporäre Datei in IndexedDB (mit Fallback auf localStorage)
const saveTempFile = async (fileName, content) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_TEMP_FILES], 'readwrite');
    const store = transaction.objectStore(STORE_TEMP_FILES);
    await store.put({ fileName, content, timestamp: Date.now() });
  } catch (error) {
    console.warn('IndexedDB Fehler, verwende localStorage:', error);
    // Fallback auf localStorage
    localStorage.setItem(`stoppuhr_temp_${fileName}`, JSON.stringify({ content, timestamp: Date.now() }));
  }
};

// Lade temporäre Datei aus IndexedDB (mit Fallback auf localStorage)
const loadTempFile = async (fileName) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_TEMP_FILES], 'readonly');
    const store = transaction.objectStore(STORE_TEMP_FILES);
    const request = store.get(fileName);
    
    return new Promise((resolve) => {
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result.content);
        } else {
          // Fallback auf localStorage
          const fallback = localStorage.getItem(`stoppuhr_temp_${fileName}`);
          if (fallback) {
            try {
              const parsed = JSON.parse(fallback);
              resolve(parsed.content);
            } catch (e) {
              resolve(null);
            }
          } else {
            resolve(null);
          }
        }
      };
      request.onerror = () => {
        // Fallback auf localStorage
        const fallback = localStorage.getItem(`stoppuhr_temp_${fileName}`);
        if (fallback) {
          try {
            const parsed = JSON.parse(fallback);
            resolve(parsed.content);
          } catch (e) {
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };
    });
  } catch (error) {
    console.warn('IndexedDB Fehler, verwende localStorage:', error);
    // Fallback auf localStorage
    const fallback = localStorage.getItem(`stoppuhr_temp_${fileName}`);
    if (fallback) {
      try {
        const parsed = JSON.parse(fallback);
        return parsed.content;
      } catch (e) {
        return null;
      }
    }
    return null;
  }
};

// Lösche temporäre Datei aus IndexedDB (mit Fallback auf localStorage)
const deleteTempFile = async (fileName) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_TEMP_FILES], 'readwrite');
    const store = transaction.objectStore(STORE_TEMP_FILES);
    await store.delete(fileName);
  } catch (error) {
    console.warn('IndexedDB Fehler, verwende localStorage:', error);
  }
  // Auch aus localStorage löschen
  localStorage.removeItem(`stoppuhr_temp_${fileName}`);
};

// Foto-Komprimierung
const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Fehler bei der Bildkomprimierung'));
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
    reader.readAsDataURL(file);
  });
};

// Speichere Foto in IndexedDB
const savePhoto = async (measurementId, sessionId, file, fileName, skipCompression = false) => {
  try {
    console.log('[savePhoto] Starte Speicherung:', { measurementId, sessionId, fileName, skipCompression, fileType: file?.constructor?.name, fileSize: file?.size });
    
    // WICHTIG: Komprimiere und konvertiere VOR der Transaktion!
    // IndexedDB-Transaktionen werden automatisch geschlossen, wenn alle synchronen Operationen fertig sind
    // Asynchrone Operationen innerhalb der Transaktion führen zu TransactionInactiveError
    
    // Komprimiere Bild nur wenn nicht bereits komprimiert
    let compressedBlob;
    if (skipCompression && file instanceof File) {
      // File ist bereits komprimiert, verwende direkt
      compressedBlob = file;
      console.log('[savePhoto] Verwende bereits komprimiertes File:', { size: compressedBlob.size, type: compressedBlob.type });
    } else {
      // Komprimiere Bild
      console.log('[savePhoto] Komprimiere Bild...');
      compressedBlob = await compressImage(file);
      console.log('[savePhoto] Bild komprimiert:', { originalSize: file.size, compressedSize: compressedBlob.size });
    }
    
    // Konvertiere Blob zu Base64 für Speicherung (VOR der Transaktion!)
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        console.log('[savePhoto] Base64 konvertiert, Länge:', reader.result?.length);
        resolve(reader.result);
      };
      reader.onerror = (error) => {
        console.error('[savePhoto] Fehler beim Konvertieren zu Base64:', error);
        reject(error);
      };
      reader.readAsDataURL(compressedBlob);
    });
    
    // Jetzt erst die Transaktion öffnen, wenn alle asynchronen Operationen fertig sind
    const db = await openDB();
    const transaction = db.transaction([STORE_PHOTOS], 'readwrite');
    const store = transaction.objectStore(STORE_PHOTOS);
    
    const photoData = {
      measurementId,
      sessionId,
      fileName: fileName || file.name,
      fileData: base64,
      fileSize: compressedBlob.size,
      mimeType: compressedBlob.type || 'image/jpeg',
      timestamp: Date.now()
    };
    
    console.log('[savePhoto] Speichere in IndexedDB:', { measurementId, sessionId, fileName, fileSize: photoData.fileSize, base64Length: base64.length });
    
    const request = store.add(photoData);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const photoId = request.result;
        console.log('[savePhoto] Foto erfolgreich gespeichert, ID:', photoId);
        resolve(photoId);
      };
      request.onerror = (error) => {
        console.error('[savePhoto] Fehler beim Speichern in IndexedDB:', error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[savePhoto] Fehler beim Speichern des Fotos:', error);
    throw error;
  }
};

// Lade Fotos vom Backend für eine Session und speichere sie in IndexedDB
const loadPhotosFromBackend = async (sessionId, backendUrl) => {
  try {
    if (!backendUrl || !sessionId) {
      console.log('[loadPhotosFromBackend] Kein Backend oder Session-ID vorhanden');
      return;
    }
    
    console.log('[loadPhotosFromBackend] Lade Fotos vom Backend für Session:', sessionId);
    const fetchUrl = `${backendUrl}/api/photos/session/${sessionId}`;
    const response = await fetch(fetchUrl);
    if (!response.ok) {
      if (response.status === 404) {
        console.log('[loadPhotosFromBackend] Keine Fotos im Backend für Session:', sessionId);
        return;
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const photos = await response.json();
    console.log(`[loadPhotosFromBackend] ${photos.length} Fotos vom Backend geladen`);
    
    if (photos.length === 0) {
      return;
    }
    
    // Prüfe zuerst, welche Fotos bereits existieren
    const db = await openDB();
    const existingPhotos = await new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_PHOTOS], 'readonly');
      const store = transaction.objectStore(STORE_PHOTOS);
      const index = store.index('measurementId');
      const allRequests = [];
      
      // Sammle alle measurement_numbers
      const measurementNumbers = [...new Set(photos.map(p => p.measurement_number))];
      
      if (measurementNumbers.length === 0) {
        resolve([]);
        return;
      }
      
      let completed = 0;
      const allExisting = [];
      
      for (const measurementNumber of measurementNumbers) {
        const request = index.getAll(measurementNumber);
        request.onsuccess = () => {
          allExisting.push(...(request.result || []));
          completed++;
          if (completed === measurementNumbers.length) {
            resolve(allExisting);
          }
        };
        request.onerror = () => {
          console.error(`[loadPhotosFromBackend] Fehler beim Laden existierender Fotos für Messung ${measurementNumber}:`, request.error);
          completed++;
          if (completed === measurementNumbers.length) {
            resolve(allExisting);
          }
        };
      }
    });
    
    // Filtere Fotos, die noch nicht existieren
    const photosToSave = photos.filter(photo => {
      const exists = existingPhotos.some(
        p => p.measurementId === photo.measurement_number && p.fileName === photo.file_name
      );
      if (exists) {
        console.log(`[loadPhotosFromBackend] Foto ${photo.file_name} für Messung ${photo.measurement_number} bereits vorhanden, überspringe`);
      }
      return !exists;
    });
    
    if (photosToSave.length === 0) {
      console.log(`[loadPhotosFromBackend] Alle Fotos bereits vorhanden`);
      return;
    }
    
    // Speichere alle neuen Fotos in IndexedDB
    const transaction = db.transaction([STORE_PHOTOS], 'readwrite');
    const store = transaction.objectStore(STORE_PHOTOS);
    
    let savedCount = 0;
    let errorCount = 0;
    
    for (const photo of photosToSave) {
      try {
        const photoData = {
          measurementId: photo.measurement_number,
          sessionId: sessionId,
          fileName: photo.file_name,
          fileData: photo.file_data, // Bereits Base64 mit data:image/jpeg;base64, Präfix
          fileSize: photo.file_size || 0,
          mimeType: photo.mime_type || 'image/jpeg',
          timestamp: Date.now()
        };
        
        const addRequest = store.add(photoData);
        addRequest.onsuccess = () => {
          console.log(`[loadPhotosFromBackend] Foto ${photo.file_name} für Messung ${photo.measurement_number} gespeichert`);
          savedCount++;
        };
        addRequest.onerror = () => {
          console.error(`[loadPhotosFromBackend] Fehler beim Speichern von Foto ${photo.file_name}:`, addRequest.error);
          errorCount++;
        };
      } catch (error) {
        console.error(`[loadPhotosFromBackend] Fehler beim Verarbeiten von Foto ${photo.file_name}:`, error);
        errorCount++;
      }
    }
    
    // Warte auf Abschluss der Transaktion
    await new Promise((resolve) => {
      transaction.oncomplete = () => {
        console.log(`[loadPhotosFromBackend] Fotos gespeichert: ${savedCount} neu, ${photos.length - photosToSave.length} bereits vorhanden, ${errorCount} Fehler`);
        resolve();
      };
      transaction.onerror = () => {
        console.error(`[loadPhotosFromBackend] Transaktionsfehler:`, transaction.error);
        resolve();
      };
    });
  } catch (error) {
    console.error('[loadPhotosFromBackend] Fehler beim Laden der Fotos vom Backend:', error);
  }
};

// Lade Fotos für eine Messung
// FIX: Filtere auch nach sessionId, um Fotos aus anderen Sessions zu vermeiden
const loadPhotos = async (measurementId, sessionId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_PHOTOS], 'readonly');
    const store = transaction.objectStore(STORE_PHOTOS);
    const index = store.index('measurementId');
    const request = index.getAll(measurementId);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        let photos = request.result || [];
        // FIX: Filtere nach sessionId, wenn angegeben
        if (sessionId) {
          photos = photos.filter(p => p.sessionId === sessionId);
        }
        if (photos.length > 0) {
          console.log(`[loadPhotos] ${photos.length} Fotos gefunden für Messung ${measurementId} (Session: ${sessionId}):`, photos.map(p => ({ id: p.id, fileName: p.fileName, sessionId: p.sessionId, hasData: !!p.fileData })));
        }
        resolve(photos);
      };
      request.onerror = (error) => {
        console.error('[loadPhotos] Fehler beim Laden:', error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[loadPhotos] Fehler beim Laden der Fotos:', error);
    return [];
  }
};

// Lösche Foto aus IndexedDB
const deletePhoto = async (photoId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_PHOTOS], 'readwrite');
    const store = transaction.objectStore(STORE_PHOTOS);
    await store.delete(photoId);
  } catch (error) {
    console.error('Fehler beim Löschen des Fotos:', error);
    throw error;
  }
};

// Audit-Log Funktionen
const addAuditLog = async (sessionId, type, measurementNumber, oldValues, newValues, changedFields, userAction) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_AUDIT_LOGS], 'readwrite');
    const store = transaction.objectStore(STORE_AUDIT_LOGS);
    
    const auditLog = {
      sessionId,
      type, // CREATE, UPDATE, DELETE, MOVE
      measurementNumber,
      timestamp: new Date().toISOString(),
      oldValues: oldValues || null,
      newValues: newValues || null,
      changedFields: changedFields || [],
      userAction: userAction || '',
      uploaded: false
    };
    
    await store.add(auditLog);
    console.log('[AuditLog] Protokoll-Eintrag erstellt:', { type, measurementNumber, userAction });
    return auditLog;
  } catch (error) {
    console.error('[AuditLog] Fehler beim Erstellen des Protokoll-Eintrags:', error);
    // Fallback: Speichere in localStorage als Backup
    try {
      const key = `audit_log_${sessionId}_${Date.now()}_${measurementNumber}`;
      const auditLog = {
        sessionId,
        type,
        measurementNumber,
        timestamp: new Date().toISOString(),
        oldValues: oldValues || null,
        newValues: newValues || null,
        changedFields: changedFields || [],
        userAction: userAction || '',
        uploaded: false
      };
      localStorage.setItem(key, JSON.stringify(auditLog));
    } catch (fallbackError) {
      console.error('[AuditLog] Fehler beim Fallback-Speichern:', fallbackError);
    }
    throw error;
  }
};

const getAuditLogs = async (sessionId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_AUDIT_LOGS], 'readonly');
    const store = transaction.objectStore(STORE_AUDIT_LOGS);
    const index = store.index('sessionId');
    const request = index.getAll(sessionId);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const logs = request.result || [];
        // Sortiere nach Timestamp (neueste zuerst)
        logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        resolve(logs);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[AuditLog] Fehler beim Laden der Protokoll-Einträge:', error);
    // Fallback: Lade aus localStorage
    try {
      const logs = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('audit_log_')) {
          try {
            const log = JSON.parse(localStorage.getItem(key));
            if (log.sessionId === sessionId) {
              logs.push(log);
            }
          } catch (e) {
            // Ignoriere ungültige Einträge
          }
        }
      }
      logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      return logs;
    } catch (fallbackError) {
      console.error('[AuditLog] Fehler beim Fallback-Laden:', fallbackError);
      return [];
    }
  }
};

const getDeltaAuditLogs = async (sessionId) => {
  try {
    const allLogs = await getAuditLogs(sessionId);
    return allLogs.filter(log => !log.uploaded);
  } catch (error) {
    console.error('[AuditLog] Fehler beim Laden der Delta-Protokoll-Einträge:', error);
    return [];
  }
};

const markAuditLogsAsUploaded = async (sessionId, uploadedLogIds = null) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_AUDIT_LOGS], 'readwrite');
    const store = transaction.objectStore(STORE_AUDIT_LOGS);
    const index = store.index('sessionId');
    const request = index.getAll(sessionId);
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const logs = request.result || [];
        let updatedCount = 0;
        
        logs.forEach(log => {
          // Wenn uploadedLogIds angegeben, markiere nur diese
          // Sonst markiere alle nicht-uploaded Logs dieser Session
          if (uploadedLogIds) {
            if (uploadedLogIds.includes(log.id)) {
              log.uploaded = true;
              store.put(log);
              updatedCount++;
            }
          } else {
            if (!log.uploaded) {
              log.uploaded = true;
              store.put(log);
              updatedCount++;
            }
          }
        });
        
        console.log(`[AuditLog] ${updatedCount} Protokoll-Einträge als hochgeladen markiert`);
        resolve(updatedCount);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('[AuditLog] Fehler beim Markieren der Protokoll-Einträge:', error);
    // Fallback: Aktualisiere localStorage
    try {
      let updatedCount = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('audit_log_')) {
          try {
            const log = JSON.parse(localStorage.getItem(key));
            if (log.sessionId === sessionId && !log.uploaded) {
              if (!uploadedLogIds || uploadedLogIds.includes(log.id)) {
                log.uploaded = true;
                localStorage.setItem(key, JSON.stringify(log));
                updatedCount++;
              }
            }
          } catch (e) {
            // Ignoriere ungültige Einträge
          }
        }
      }
      return updatedCount;
    } catch (fallbackError) {
      console.error('[AuditLog] Fehler beim Fallback-Markieren:', fallbackError);
      return 0;
    }
  }
};

// Feedback-Funktionen
const savePendingFeedback = async (feedbackData) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_FEEDBACK_PENDING], 'readwrite');
    const store = transaction.objectStore(STORE_FEEDBACK_PENDING);
    
    const feedback = {
      ...feedbackData,
      createdAt: Date.now(),
      uploaded: false
    };
    
    const addedKey = await new Promise((resolve, reject) => {
      const req = store.add(feedback);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    const savedFeedback = { ...feedback, id: addedKey };
    debugLog('Feedback', 'Pending gespeichert', { id: addedKey, hasImages: !!feedbackData.images?.length });
    console.log('[Feedback] Feedback lokal gespeichert');
    return savedFeedback;
  } catch (error) {
    debugLog('Feedback', 'savePendingFeedback Fehler', { error: error?.message });
    console.error('[Feedback] Fehler beim Speichern des Feedbacks:', error);
    throw error;
  }
};

const getPendingFeedback = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_FEEDBACK_PENDING], 'readonly');
    const store = transaction.objectStore(STORE_FEEDBACK_PENDING);
    // Cursor direkt auf dem Store (ohne Index): Index auf Boolean 'uploaded' ist unter
    // iOS/Safari unzuverlässig und liefert keine Einträge – Store-Cursor + Filter funktioniert überall.
    const request = store.openCursor();

    return new Promise((resolve, reject) => {
      const results = [];
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.uploaded === false) {
            results.push(cursor.value);
          }
          cursor.continue();
        } else {
          debugLog('Feedback', 'Pending geladen', { count: results.length, ids: results.map(r => r.id) });
          resolve(results);
        }
      };
      request.onerror = () => {
        reject(request.error);
      };
    });
  } catch (error) {
    debugLog('Feedback', 'getPendingFeedback Fehler', { error: error?.message });
    console.error('[Feedback] Fehler beim Laden der ausstehenden Feedbacks (IndexedDB/Index):', error?.name || error?.message || error);
    return [];
  }
};

const markFeedbackAsUploaded = async (feedbackIds) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_FEEDBACK_PENDING], 'readwrite');
    const store = transaction.objectStore(STORE_FEEDBACK_PENDING);
    
    for (const id of feedbackIds) {
      const feedback = await new Promise((resolve, reject) => {
        const req = store.get(id);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      if (feedback) {
        feedback.uploaded = true;
        await new Promise((resolve, reject) => {
          const req = store.put(feedback);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        });
      }
    }
  } catch (error) {
    console.error('[Feedback] Fehler beim Markieren der Feedbacks als hochgeladen:', error);
  }
};

// Nur ein Feedback-Sync gleichzeitig (verhindert Doppelte bei mehreren Triggern)
let feedbackSyncInProgress = false;

// Synchronisiere ausstehende Feedbacks mit Backend
const syncPendingFeedback = async (backendUrl) => {
  if (!backendUrl) {
    console.log('[Feedback-Sync] Keine Backend-URL, überspringe Synchronisation');
    return { synced: 0, failed: 0 };
  }

  // Prüfe ob online ist
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    console.log('[Feedback-Sync] Gerät ist offline, überspringe Synchronisation');
    return { synced: 0, failed: 0 };
  }

  if (feedbackSyncInProgress) {
    console.log('[Feedback-Sync] Sync bereits läuft, überspringe');
    debugLog('Feedback-Sync', 'Übersprungen (Sync läuft)', {});
    return { synced: 0, failed: 0 };
  }
  feedbackSyncInProgress = true;

  try {
    const pendingFeedbacks = await getPendingFeedback();
    debugLog('Feedback-Sync', 'Start', { backendUrl: !!backendUrl, online: typeof navigator !== 'undefined' ? navigator.onLine : null, pendingCount: pendingFeedbacks.length });

    if (pendingFeedbacks.length === 0) {
      console.log('[Feedback-Sync] Keine ausstehenden Feedbacks');
      return { synced: 0, failed: 0 };
    }

    console.log(`[Feedback-Sync] Starte Synchronisation von ${pendingFeedbacks.length} ausstehenden Feedbacks`);

    const normalizedUrl = backendUrl.replace(/\/$/, '');
    const apiUrl = `${normalizedUrl}/api/feedback`;
    
    let syncedCount = 0;
    let failedCount = 0;
    const syncedIds = [];

    // Synchronisiere jedes Feedback sequenziell
    for (const feedback of pendingFeedbacks) {
      try {
        // Entferne interne Felder vor dem Senden
        const { id, createdAt, uploaded, ...feedbackData } = feedback;
        // Erfassungszeitpunkt ans Backend (ISO 8601); Backend speichert als „Erstellt“
        if (createdAt != null) {
          feedbackData.created_at = new Date(createdAt).toISOString();
        }

        console.log(`[Feedback-Sync] Sende Feedback ID ${id} an Backend`);

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(feedbackData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          debugLog('Feedback-Sync', 'Send Ergebnis', { id, ok: false, status: response.status, errorText: errorText?.substring?.(0, 200) });
          console.error(`[Feedback-Sync] Fehler beim Senden von Feedback ID ${id}:`, errorText);
          failedCount++;
          continue;
        }

        const result = await response.json();
        debugLog('Feedback-Sync', 'Send Ergebnis', { id, ok: true, status: response.status });
        console.log(`[Feedback-Sync] Feedback ID ${id} erfolgreich gesendet:`, result);
        syncedIds.push(id);
        syncedCount++;
        // Sofort markieren, damit ein paralleler Sync dasselbe Feedback nicht erneut sendet (verhindert Doppelte im Backend)
        await markFeedbackAsUploaded([id]);
        debugLog('Feedback-Sync', 'Markiert', { syncedIds: [id] });
      } catch (error) {
        debugLog('Feedback-Sync', 'Send Ergebnis', { id: feedback.id, error: error?.message });
        console.error(`[Feedback-Sync] Fehler beim Senden von Feedback ID ${feedback.id}:`, error);
        failedCount++;
        // Weiter mit nächstem Feedback
      }
    }

    if (syncedCount > 0) {
      console.log(`[Feedback-Sync] ${syncedCount} Feedbacks erfolgreich synchronisiert`);
    }

    if (failedCount > 0) {
      console.warn(`[Feedback-Sync] ${failedCount} Feedbacks konnten nicht synchronisiert werden`);
    }

    return { synced: syncedCount, failed: failedCount };
  } catch (error) {
    debugLog('Feedback-Sync', 'Sync Fehler', { error: error?.message });
    console.error('[Feedback-Sync] Fehler bei der Synchronisation:', error);
    return { synced: 0, failed: 0 };
  } finally {
    feedbackSyncInProgress = false;
  }
};

// Delta-Berechnung für Messpunkte
const getDeltaMeasurements = (measurements) => {
  // Filtere nur Messpunkte, die geändert oder neu sind
  const result = measurements.filter(m => {
    // Wenn lastUploaded nicht gesetzt ist, ist es neu
    if (!m.lastUploaded) return true;
    // Wenn lastModified > lastUploaded, wurde es geändert
    if (m.lastModified && m.lastUploaded) {
      return m.lastModified > m.lastUploaded;
    }
    // Fallback: Wenn lastModified gesetzt ist, aber lastUploaded nicht, ist es neu
    return m.lastModified !== undefined;
  });
  return result;
};

// Berechne Checksumme für Daten
const calculateChecksum = (data) => {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(16);
};

function App() {
  const { t } = useTranslation();
  // localStorage Keys (müssen vor useState definiert werden)
  const STORAGE_KEY_FOOTER_MANUALLY_HIDDEN = 'stoppuhr_footer_manually_hidden';
  const STORAGE_KEY_EMAIL = 'stoppuhr_email';
  const STORAGE_KEY_BACKEND = 'stoppuhr_backend';
  const STORAGE_KEY_USER_ID = 'stoppuhr_user_id';

  // Login & Session States
  // Prüfe, ob Benutzer bereits eingeloggt ist (z.B. nach F5-Refresh)
  // WICHTIG: savedEmail und savedBackend reichen aus (auch ohne Token, z.B. im Demo-Modus oder nach Browser-Update)
  const savedToken = typeof window !== 'undefined' ? localStorage.getItem('userToken') : null;
  const savedEmail = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY_EMAIL) : null;
  const savedBackend = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY_BACKEND) : null;
  const initialIsLoggedIn = savedEmail && savedBackend ? true : false;
  const [isLoggedIn, setIsLoggedIn] = useState(initialIsLoggedIn);
  const [isCheckingUpdateResume, setIsCheckingUpdateResume] = useState(true); // Blockiert Login-Screen während Update-Resume-Check
  const [showRegister, setShowRegister] = useState(false);
  const [email, setEmail] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY_EMAIL) || '' : ''));
  const [backendUrl, setBackendUrl] = useState(() => (typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY_BACKEND) || '' : ''));
  const [password, setPassword] = useState('');
  /** Login: Backend-URL manuell testen (Health-Check) */
  const [loginBackendUrlTestLoading, setLoginBackendUrlTestLoading] = useState(false);
  const [loginBackendUrlTestResult, setLoginBackendUrlTestResult] = useState(null); // { ok: boolean, text: string } | null
  const [userId, setUserId] = useState(() => {
    if (typeof window === 'undefined') return null;
    const id = localStorage.getItem(STORAGE_KEY_USER_ID);
    return id ? parseInt(id, 10) : null;
  });
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [isCheckingLicense, setIsCheckingLicense] = useState(false);
  const [backendAvailable, setBackendAvailable] = useState(null); // null = unbekannt, true = verfügbar, false = nicht verfügbar
  const [backendOfflineSince, setBackendOfflineSince] = useState(null); // Timestamp, wann Backend offline ging
  const [isCheckingBackend, setIsCheckingBackend] = useState(false);
  const [apiCompatible, setApiCompatible] = useState(true); // API-Kompatibilität (default: true für Graceful Degradation)
  const [compatibilityWarning, setCompatibilityWarning] = useState(null); // Warnung bei Kompatibilitätsproblemen
  const [showFooter, setShowFooter] = useState(true);
  const [footerManuallyHidden, setFooterManuallyHidden] = useState(() => {
    // Lade aus localStorage, damit der State über Remounts hinweg erhalten bleibt
    try {
      const saved = localStorage.getItem(STORAGE_KEY_FOOTER_MANUALLY_HIDDEN);
      return saved === 'true';
    } catch (e) {
      return false;
    }
  });
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionListTab, setSessionListTab] = useState('lokal'); // 'lokal' | 'backend'
  const [backendSessions, setBackendSessions] = useState([]);
  const [backendSessionsPage, setBackendSessionsPage] = useState(1);
  const [backendSessionsTotal, setBackendSessionsTotal] = useState(0);
  const [backendSearch, setBackendSearch] = useState('');
  const [backendOwnOnly, setBackendOwnOnly] = useState(true);
  const [backendSessionsLoading, setBackendSessionsLoading] = useState(false);
  const [backendResumeLoading, setBackendResumeLoading] = useState(false);
  const backendSessionsAbortRef = useRef(null);
  const backendResumeAbortRef = useRef(null);
  const [resumeNumber, setResumeNumber] = useState(0); // WA-Nr (Wiederaufnahme-Nr) - startet bei 0, wird beim Starten der Uhr um 1 erhöht
  const [vorderseiteExpanded, setVorderseiteExpanded] = useState(false);
  const [vorderseiteDialogOpen, setVorderseiteDialogOpen] = useState(false);
  const [vorderseiteData, setVorderseiteData] = useState(null);
  const [isLoadingVorderseite, setIsLoadingVorderseite] = useState(false);
  const [isSavingVorderseite, setIsSavingVorderseite] = useState(false);
  const [vorderseiteHasChanges, setVorderseiteHasChanges] = useState(false);
  const [vorderseiteLastFocusedFieldRef, setVorderseiteLastFocusedFieldRef] = useState(null);
  const vorderseiteNextTabIndexRef = useRef(0);
  const vorderseiteHeaderTabIndexRef = useRef(0);
  useEffect(() => {
    if (vorderseiteDialogOpen) {
      vorderseiteNextTabIndexRef.current = 0;
      vorderseiteHeaderTabIndexRef.current = 0;
    }
  }, [vorderseiteDialogOpen]);
  const getVorderseiteTabIndex = useCallback((key, subIndex) => {
    if (key === 'header') return 1000 + vorderseiteHeaderTabIndexRef.current++;
    return vorderseiteNextTabIndexRef.current++;
  }, []);

  // Nachkommastellen-Konfiguration
  const [decimalPlacesVorderseite, setDecimalPlacesVorderseite] = useState(() => {
    // Lade aus vorderseiteData, falls vorhanden, sonst Default: 4
    try {
      const saved = localStorage.getItem('stoppuhr_sessions');
      if (saved) {
        const sessions = JSON.parse(saved);
        const currentSession = sessions.find(s => s.id === selectedSession?.id);
        if (currentSession?.vorderseiteData?.decimal_places_vorderseite !== undefined) {
          return currentSession.vorderseiteData.decimal_places_vorderseite;
        }
      }
    } catch (e) {
      // Ignoriere Fehler
    }
    return 4;
  });
  
  const [decimalPlacesSollzeiten, setDecimalPlacesSollzeiten] = useState(() => {
    // Lade aus vorderseiteData, falls vorhanden, sonst Default: 4
    try {
      const saved = localStorage.getItem('stoppuhr_sessions');
      if (saved) {
        const sessions = JSON.parse(saved);
        const currentSession = sessions.find(s => s.id === selectedSession?.id);
        if (currentSession?.vorderseiteData?.decimal_places_sollzeiten !== undefined) {
          return currentSession.vorderseiteData.decimal_places_sollzeiten;
        }
      }
    } catch (e) {
      // Ignoriere Fehler
    }
    return 4;
  });

  // Stoppuhr States
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [measurements, setMeasurements] = useState([]);
  const [lastMeasurementTime, setLastMeasurementTime] = useState(0);
  const [activity, setActivity] = useState('');
  const [timeType, setTimeType] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_timetypes');
    if (saved) {
      try {
        const types = JSON.parse(saved);
        return types.length > 0 ? types[0].name : 'thb';
      } catch (e) {
        return 'thb';
      }
    }
    return 'thb';
  });
  const [note, setNote] = useState('');
  
  // UI States
  const [showConfig, setShowConfig] = useState(false);
  const [configTab, setConfigTab] = useState('view'); // 'view', 'keyboard', 'license', 'timetypes', 'feedback', 'about', 'dependentlists'
  const [dataSourceType, setDataSourceType] = useState('timetypes');
  const [dataSourceMode, setDataSourceMode] = useState('cloud'); // local | cloud
  const [dataSourceScope, setDataSourceScope] = useState('');
  const [dataSourceSearch, setDataSourceSearch] = useState('');
  const [dataSourceOwnOnly, setDataSourceOwnOnly] = useState(false);
  const [dataSourceList, setDataSourceList] = useState([]);
  const [dataSourceTotal, setDataSourceTotal] = useState(0);
  const [dataSourceLoading, setDataSourceLoading] = useState(false);
  const [dataSourceSelectedId, setDataSourceSelectedId] = useState(null);
  const [dataSourceSelected, setDataSourceSelected] = useState(null);
  const [dataSourceRename, setDataSourceRename] = useState('');
  const [dataSourceSelection, setDataSourceSelection] = useState(null);
  const [dataSourceError, setDataSourceError] = useState('');
  const [dataSourceInfo, setDataSourceInfo] = useState('');
  const [dataSourceSyncInProgress, setDataSourceSyncInProgress] = useState(false);
  const [aboutUpdateStatus, setAboutUpdateStatus] = useState(null); // null | 'checking' | 'applying' | 'none' | 'dev'
  const [securityCheckResult, setSecurityCheckResult] = useState(null); // null | { secureContext, protocol, hasSW, swState, cveData }
  const [storageEstimate, setStorageEstimate] = useState(null); // { usage, quota } | { error: string } | null
  const [tabsCanScrollLeft, setTabsCanScrollLeft] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false); // Standardmäßig deaktiviert
  const [debugLogs, setDebugLogs] = useState([]);
  const [showAllLogsText, setShowAllLogsText] = useState(false);
  const [writeToFileStatus, setWriteToFileStatus] = useState(null); // null | 'sending' | 'ok' | 'error'
  const [tabsCanScrollRight, setTabsCanScrollRight] = useState(false);
  const tabsContainerRef = useRef(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showFeedbackStatusNotification, setShowFeedbackStatusNotification] = useState(false);
  const [pendingFeedbackCount, setPendingFeedbackCount] = useState(0);
  const loadPendingFeedbackCount = useCallback(async () => {
    const list = await getPendingFeedback();
    setPendingFeedbackCount(list.length);
  }, []);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);
  const [customDeviceName, setCustomDeviceNameState] = useState(() => getCustomDeviceName() || '');
  const [deviceName, setDeviceName] = useState(() => getDeviceName());
  const [gradePosition, setGradePosition] = useState(() => {
    // Lade gespeicherte Einstellung oder verwende Default basierend auf Orientierung
    const saved = localStorage.getItem('stoppuhr_grade_position');
    if (saved) {
      return saved; // Verwende gespeicherte Einstellung
    }
    // Default basierend auf Orientierung: Landscape = horizontal (top), Portrait = vertikal (left)
    if (typeof window !== 'undefined') {
      const isPortraitMode = window.innerWidth <= 768 && window.matchMedia('(orientation: portrait)').matches;
      return isPortraitMode ? 'left' : 'top'; // Portrait = vertikal, Landscape = horizontal
    }
    return 'left'; // Fallback
  }); // 'left' = vertikal (neben), 'top' = horizontal (oben)
  
  // Automatische Anordnung basierend auf Position: top = row (horizontal), left = column (vertikal)
  const gradeLayout = gradePosition === 'top' ? 'row' : 'column';
  
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showCompactAnalysis, setShowCompactAnalysis] = useState(false);
  const [analysisViewMode, setAnalysisViewMode] = useState('timeType'); // 'pivot', 'timeType', 'kuerzel', 'zeitstrahl', 'statistik'
  const [statistikGroupBy, setStatistikGroupBy] = useState('kuerzel'); // 'kuerzel' | 'kuerzelE' | 'kuerzelEA'
  const [statistikDisplay, setStatistikDisplay] = useState('table'); // 'table' | 'bar'
  const [statistikTimeType, setStatistikTimeType] = useState('soll'); // 'soll' | 'ist' - Soll-Zeit (min) oder IST-Zeit (HM)
  const [eaDrillDown, setEaDrillDown] = useState(null); // { key, kuerzel, egList, selectedEG } | null
  const [zeitstrahlExpandedWaNr, setZeitstrahlExpandedWaNr] = useState(() => new Set()); // WA-Nr-Keys aufgeklappt (Start: zugeklappt)
  const [zeitstrahlHover, setZeitstrahlHover] = useState(null); // { m, rect } für Hover-Popover (Beschreibung, Notiz, EA, Fotos)
  const zeitstrahlHoverTimeoutRef = useRef(null);
  const [compactVorderseiteFontScale, setCompactVorderseiteFontScale] = useState(1); // 0.85–1.35, Buttons a/A im Dialog
  const [vorderseiteFontScale, setVorderseiteFontScale] = useState(1); // 0.85–1.35, Buttons a/A in Normal-Ansicht
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [pivotDrillDown, setPivotDrillDown] = useState(null); // { activity, timeType, measurements }
  const [compactPivotDrillDown, setCompactPivotDrillDown] = useState(null); // { activity, timeType, measurements }
  const [darkMode, setDarkMode] = useState(() => {
    // Lade aus localStorage beim Initialisieren
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('stoppuhr_dark_mode');
      const isDark = saved !== null ? saved === 'true' : false;
      // Setze body-Klasse sofort beim Initialisieren
      if (isDark) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
      return isDark;
    }
    return false;
  });
  const [showResumeNumber, setShowResumeNumber] = useState(true);
  const [showActivity, setShowActivity] = useState(true);
  const [showGrade, setShowGrade] = useState(true);
  const [showIndividualTime, setShowIndividualTime] = useState(true);
  const [showProgressTime, setShowProgressTime] = useState(true);
  const [showIndividualTimeInHeader, setShowIndividualTimeInHeader] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_individual_time_header');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [showEinflussgroessenCompact, setShowEinflussgroessenCompact] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_einflussgroessen_compact');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [enableDragDrop, setEnableDragDrop] = useState(true);
  const [showAblaufDb, setShowAblaufDb] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_ablaufdb');
    return saved !== null ? saved === 'true' : true; // Default: ON
  });
  const [showVorderseite, setShowVorderseite] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_vorderseite');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [showEaKombinationen, setShowEaKombinationen] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_show_ea_kombinationen');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [useGlobalInfluences, setUseGlobalInfluences] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_use_global_influences');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [compactViewMode, setCompactViewMode] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_compact_view_mode');
    return saved !== null ? saved === 'true' : false; // Default: OFF
  });
  const [showCompactMenu, setShowCompactMenu] = useState(false);
  const [showCompactInputs, setShowCompactInputs] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_compact_inputs_enabled');
    return saved !== null ? saved === 'true' : false; // Default: AUS
  });
  const [isPortrait, setIsPortrait] = useState(() => {
    // Prüfe initiale Orientierung
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768 && window.matchMedia('(orientation: portrait)').matches;
    }
    return false;
  });
  const [selectedKuerzel, setSelectedKuerzel] = useState(null); // Aktuell selektiertes Kürzel mit Einflussgrößen
  const [einflussgroessenValues, setEinflussgroessenValues] = useState({}); // { einflussname: wert }
  const [merke1Enabled, setMerke1Enabled] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_merke1_enabled');
    return saved !== null ? saved === 'true' : false; // Default: AUS
  });
  const [merke1Values, setMerke1Values] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_merke1_values');
    return saved ? JSON.parse(saved) : null; // { kuerzel, activity, einflussgroessenValues }
  });
  const [openDropdowns, setOpenDropdowns] = useState({}); // { einflussname: boolean } - für Dropdown-Status
  const [focusedInput, setFocusedInput] = useState({}); // { einflussname: boolean } - für Ziffernblock-Anzeige
  const [compactView, setCompactView] = useState(false);
  const [hapticFeedbackEnabled, setHapticFeedbackEnabled] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_haptic_feedback_enabled');
    return saved !== null ? saved === 'true' : true; // Default: AN
  });
  const [autoSaveTimer, setAutoSaveTimer] = useState(10); // Minuten (Standard: 10 Minuten)
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [isImportingLicense, setIsImportingLicense] = useState(false);
  const [keyboardConfig, setKeyboardConfig] = useState(() => {
    // Default: Ctrl+1-9 für Leistungsgrade, Ctrl+0 für Unterbrechung
    const defaultConfig = {};
    const grades = [0, 80, 90, 100, 105, 110, 115, 120, 125];
    grades.forEach((grade, index) => {
      defaultConfig[grade] = `Ctrl+${index + 1}`;
    });
    defaultConfig['interruption'] = 'Ctrl+0';
    return defaultConfig;
  });
  const [editingKey, setEditingKey] = useState(null);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [editActivity, setEditActivity] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editExcluded, setEditExcluded] = useState(false);
  const [editPhotos, setEditPhotos] = useState([]); // Array von { id, fileData, fileName }
  const [photoPreview, setPhotoPreview] = useState(null); // Für Lightbox
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isAutoUploading, setIsAutoUploading] = useState(false);
  const lastUploadAttemptRef = useRef(null);
  const [measurementPhotos, setMeasurementPhotos] = useState({}); // { measurementNumber: [photos] }
  const [pendingPhotos, setPendingPhotos] = useState([]); // Temporäre Fotos, die noch keinem Messpunkt zugeordnet sind
  const [editEinflussgroessenValues, setEditEinflussgroessenValues] = useState({});
  const [deletingMeasurement, setDeletingMeasurement] = useState(null);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [showWiederaufnahmenDialog, setShowWiederaufnahmenDialog] = useState(false);
  const [pendingWiederaufnahmen, setPendingWiederaufnahmen] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showBackToSessionsDialog, setShowBackToSessionsDialog] = useState(false);
  const [showDeletePhotoDialog, setShowDeletePhotoDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [saveFileName, setSaveFileName] = useState('');
  const [showRemoveLicenseDialog, setShowRemoveLicenseDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportOptions, setReportOptions] = useState({
    vorderseite: true,
    messpunkteDetail: true,
    messpunkteKompakt: false,
    messpunkteBilanzZeitarten: true,
    bilanzKuerzel: false,
    orientation: 'portrait' // 'portrait' oder 'landscape'
  });
  const [showUploadResultDialog, setShowUploadResultDialog] = useState(false);
  const [showUpdateResumeDialog, setShowUpdateResumeDialog] = useState(false);
  const [updateResumeDialogData, setUpdateResumeDialogData] = useState(null);
  const [versionBadgeDismissedByResume, setVersionBadgeDismissedByResume] = useState(false);
  const [uploadResult, setUploadResult] = useState({ type: 'success', message: '' });
  const [showLicenseImportDialog, setShowLicenseImportDialog] = useState(false);
  const [showRenameSessionDialog, setShowRenameSessionDialog] = useState(false);
  const [renameSessionName, setRenameSessionName] = useState('');
  const [licenseImportResult, setLicenseImportResult] = useState({ type: 'success', message: '', details: null });
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [isListeningEdit, setIsListeningEdit] = useState(false);
  const [isListeningEditActivity, setIsListeningEditActivity] = useState(false);
  const [recognitionEdit, setRecognitionEdit] = useState(null);
  const [isInterrupted, setIsInterrupted] = useState(false);
  const [interruptionMeasurementNumber, setInterruptionMeasurementNumber] = useState(null);
  const [savedActivity, setSavedActivity] = useState('');
  const [savedTimeType, setSavedTimeType] = useState('thb');
  const [draggedMeasurement, setDraggedMeasurement] = useState(null);
  
  const intervalRef = useRef(null);
  const uploadIntervalRef = useRef(null);
  const lastClickTimeRef = useRef(0);
  const autoSaveTimerRef = useRef(null);
  const autoSaveFileToggleRef = useRef(false);
  const updateResumeCheckInProgressRef = useRef(false); // Verhindert mehrfache Update-Resume-Prüfungen
  const updateResumeStateDeletedRef = useRef(false); // Merkt, ob der State bereits gelöscht wurde
  const updateResumeCheckExecutedRef = useRef(false); // Verhindert mehrfache Ausführung des useEffect Hooks (auch bei StrictMode)
  const updateResumeCancelInProgressRef = useRef(false); // Verhindert mehrfache Ausführung von handleUpdateResumeCancel
  const updateResumeDialogShownRef = useRef(false); // Verhindert, dass der Dialog mehrfach geöffnet wird
  const autoSaveToTempFileRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const handleAddMeasurementRef = useRef(null);
  const handleInterruptionToggleRef = useRef(null);
  const handleEditMeasurementRef = useRef(null);
  const lastStartResumeNumberRef = useRef(null); // Verhindert doppelte Aufrufe mit derselben WA-Nr
  const isHandlingStartRef = useRef(false); // Verhindert gleichzeitige Ausführung von handleStart
  const pendingWiederaufnahmeRef = useRef(null); // Verhindert Duplikate in setVorderseiteData
  const wiederaufnahmeAddCounterRef = useRef(0); // Zähler für hinzugefügte wiederaufnahmen
  const licenseFileInputRef = useRef(null);
  const ablaufDbFileInputRef = useRef(null);
  const einflussDbFileInputRef = useRef(null);
  const dependentListFileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const ziffernblockClickRef = useRef(false); // Track ob Klick auf Ziffernblock-Button war
  const ziffernblockInputElementRef = useRef(null); // Ref für aktuell fokussiertes Input-Feld (DOM-Element)
  const [pendingPhotoMeasurementNumber, setPendingPhotoMeasurementNumber] = useState(null);
  const isListeningRef = useRef(false);
  const isListeningEditRef = useRef(false);
  const isListeningEditActivityRef = useRef(false);
  // Refs für temporäre Werte beim Fokus (um alle datalist-Optionen anzuzeigen)
  const originalEinflussValuesRef = useRef({});
  const focusedEinflussRef = useRef(null);
  // Refs für normale Eingabe (nicht Bearbeitung)
  const originalEinflussInputValuesRef = useRef({});
  const focusedEinflussInputRef = useRef(null);
  // Ref für Einflussgrößen: synchroner Zugriff in Portrait, um Timing zwischen EA-Tap und Leistungsgrad-Klick zu vermeiden („Kürzel merken“/M1)
  const einflussgroessenValuesRef = useRef(null);
  const activityInputSectionRightRef = useRef(null); /* rechter Spalten-Inhalt, nicht td – für Höhenmessung */
  const activityInputSectionRightCellRef = activityInputSectionRightRef; /* Alias für Abwärtskompatibilität */
  const ablaufKuerzelListRef = useRef(null);
  
  // Grades muss VOR keyboardConfig useState definiert werden
  // 85 und 95 wurden entfernt
  const grades = [0, 80, 90, 100, 105, 110, 115, 120, 125];
  // Standard-Zeitarten
  const defaultTimeTypes = [
    { name: 'th', description: 'Hauptzeit', color: '#3b82f6' },
    { name: 'tn', description: 'Nebenzeit', color: '#10b981' },
    { name: 'thb', description: 'beeinflussbare Hauptzeit', color: '#6366f1' },
    { name: 'tnb', description: 'beeinflussbare Nebenzeit', color: '#14b8a6' },
    { name: 'thu', description: 'unbeeinflussbare Hauptzeit', color: '#8b5cf6' },
    { name: 'tnu', description: 'unbeeinflussbare Nebenzeit', color: '#06b6d4' },
    { name: 'twu', description: 'ablaufbedingte Wartezeit', color: '#f59e0b' },
    { name: 'tvs', description: 'sachliche Verteilzeit', color: '#60a5fa' },
    { name: 'tvp', description: 'persönliche Verteilzeit', color: '#ec4899' },
    { name: 'stö', description: 'Störzeit', color: '#ef4444' },
    { name: 'n__', description: 'nicht abzugeltende Zeit', color: '#22c55e' },
    { name: 'f__', description: 'fallweise abzugeltende Zeit', color: '#9ca3af' },
    { name: 'tr', description: 'Rüstzeit', color: '#a855f7' },
    { name: 'tmz', description: 'zusätzliche Tätigkeiten', color: '#fbbf24' },
    { name: 'tvk', description: 'Verteilzeit Schicht konstant', color: '#93c5fd' },
    { name: 'tvv', description: 'Verteilzeit Schicht variabel', color: '#93c5fd' },
    { name: 'x__', description: 'nicht zu verwendende Zeit', color: '#d1d5db' }
  ];

  // Lade Zeitarten aus localStorage oder verwende Standard
  const [timeTypes, setTimeTypes] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_timetypes');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Fehler beim Laden der Zeitarten, verwende Standard:', e);
        return defaultTimeTypes;
      }
    }
    return defaultTimeTypes;
  });

  const [editingTimeType, setEditingTimeType] = useState(null);
  const [newTimeTypeName, setNewTimeTypeName] = useState('');
  const [newTimeTypeDescription, setNewTimeTypeDescription] = useState('');
  const [newTimeTypeColor, setNewTimeTypeColor] = useState('#3b82f6');

  // AblaufDB (XML) States
  const [ablaufDbPath, setAblaufDbPath] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_ablaufdb_path');
    return saved || '';
  });
  const [ablaufDbName, setAblaufDbName] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_ablaufdb_name');
    return saved || '';
  });
  const [ablaufDbData, setAblaufDbData] = useState(null); // Parsed XML data
  const [einflussDbPath, setEinflussDbPath] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_einflussdb_path');
    return saved || '';
  });
  const [einflussDbName, setEinflussDbName] = useState(() => {
    const saved = localStorage.getItem('stoppuhr_einflussdb_name');
    return saved || '';
  });
  const [einflussDbData, setEinflussDbData] = useState(null); // Parsed XML data
  const [showAblaufDbDialog, setShowAblaufDbDialog] = useState(false);
  const [deleteRowConfirm, setDeleteRowConfirm] = useState(null); // { type: 'arbeitsgegenstand' | 'mitarbeiter' | 'betriebsmittel' | 'zusammenstellung', index: number, row: object }
  const [showAblaufDbEditor, setShowAblaufDbEditor] = useState(false);
  const [ablaufDbXmlContent, setAblaufDbXmlContent] = useState('');
  const [einflussDbXmlContent, setEinflussDbXmlContent] = useState('');
  const createDefaultDependentSheet = () => ({
    id: `sheet_${Date.now()}`,
    name: 'Standard',
    columns: [],
    rows: []
  });
  const [dependentListsData, setDependentListsData] = useState(() => {
    try {
      const saved = localStorage.getItem('stoppuhr_dependent_lists');
      if (!saved) return [createDefaultDependentSheet()];
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed) || parsed.length === 0) return [createDefaultDependentSheet()];
      return parsed
        .filter((sheet) => sheet && typeof sheet === 'object')
        .map((sheet, idx) => ({
          id: sheet.id || `sheet_${Date.now()}_${idx}`,
          name: (sheet.name || `Blatt ${idx + 1}`).toString(),
          columns: Array.isArray(sheet.columns) ? sheet.columns.map((c) => (c || '').toString()) : [],
          rows: Array.isArray(sheet.rows)
            ? sheet.rows.map((row) => (Array.isArray(row) ? row.map((v) => (v || '').toString()) : []))
            : []
        }));
    } catch (e) {
      return [createDefaultDependentSheet()];
    }
  });
  const [activeDependentSheetId, setActiveDependentSheetId] = useState(() => {
    try {
      return localStorage.getItem('stoppuhr_dependent_lists_active_sheet') || '';
    } catch (e) {
      return '';
    }
  });
  const [dependentListsError, setDependentListsError] = useState('');
  const [showEinflussDbEditor, setShowEinflussDbEditor] = useState(false);
  const [editingEinflussName, setEditingEinflussName] = useState('');
  const [editEinflussMesswerte, setEditEinflussMesswerte] = useState([]);
  const [showEinflussNameDialog, setShowEinflussNameDialog] = useState(false);
  const [newEinflussName, setNewEinflussName] = useState('');
  const [einflussNameDialogError, setEinflussNameDialogError] = useState('');
  const [einflussEditorError, setEinflussEditorError] = useState('');
  const [showEinflussDeleteDialog, setShowEinflussDeleteDialog] = useState(false);
  const [showEinflussUnloadDialog, setShowEinflussUnloadDialog] = useState(false);
  const [editingKuerzel, setEditingKuerzel] = useState(null); // { kuerzel, beschreibung, fullText, index }
  const [editKuerzelForm, setEditKuerzelForm] = useState({ 
    kuerzel: '', 
    kostenstelle: '', 
    zeitart: '', 
    ablauftext1: '', 
    ablauftext2: '', 
    ablauftext3: '', 
    ablauftext4: '',
    beschreibung: ''
  });
  const [editEinflussgroessen, setEditEinflussgroessen] = useState([]); // Array von { einflussname, klassewert, einheit, messwerte: [] }
  const [originalKuerzelData, setOriginalKuerzelData] = useState(null); // Original-Daten zum Vergleich
  const [showConfirmCloseDialog, setShowConfirmCloseDialog] = useState(false);

  // Aktualisiere deviceName wenn customDeviceName sich ändert
  useEffect(() => {
    setDeviceName(getDeviceName());
  }, [customDeviceName]);

  useEffect(() => {
    try {
      localStorage.setItem('stoppuhr_dependent_lists', JSON.stringify(dependentListsData));
    } catch (e) {
      // ignore quota errors here; UI reports parsing/validation errors only
    }
  }, [dependentListsData]);

  useEffect(() => {
    if (!dependentListsData.length) {
      const fallback = createDefaultDependentSheet();
      setDependentListsData([fallback]);
      setActiveDependentSheetId(fallback.id);
      return;
    }
    const exists = dependentListsData.some((sheet) => sheet.id === activeDependentSheetId);
    if (!exists) {
      setActiveDependentSheetId(dependentListsData[0].id);
    }
  }, [dependentListsData, activeDependentSheetId]);

  useEffect(() => {
    if (!activeDependentSheetId) return;
    try {
      localStorage.setItem('stoppuhr_dependent_lists_active_sheet', activeDependentSheetId);
    } catch (e) {
      // ignore storage errors
    }
  }, [activeDependentSheetId]);

  const activeDependentSheet = dependentListsData.find((sheet) => sheet.id === activeDependentSheetId) || dependentListsData[0] || null;
  const sourceTypeConfig = {
    timetypes: { format: 'json', selectionKey: 'timetypes_source_id' },
    ablaufdb: { format: 'xml', selectionKey: 'ablaufdb_source_id' },
    einflussdb: { format: 'xml', selectionKey: 'einflussdb_source_id' },
    dependentlists: { format: 'json', selectionKey: 'dependentlists_source_id' }
  };

  const getLocalSourcePayload = useCallback((sourceType) => {
    if (sourceType === 'timetypes') {
      return { format: 'json', content_json: timeTypes };
    }
    if (sourceType === 'ablaufdb') {
      const xml = (ablaufDbXmlContent || '').trim();
      if (!xml) return null;
      return { format: 'xml', content_xml: xml };
    }
    if (sourceType === 'einflussdb') {
      const xml = (einflussDbXmlContent || '').trim();
      if (!xml) return null;
      return { format: 'xml', content_xml: xml };
    }
    if (sourceType === 'dependentlists') {
      return { format: 'json', content_json: dependentListsData };
    }
    return null;
  }, [timeTypes, ablaufDbXmlContent, einflussDbXmlContent, dependentListsData]);

  const applySourceToLocal = useCallback((sourceType, sourceDetail) => {
    const parseAblaufInline = (xmlContent) => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) throw new Error(`XML-Parsing-Fehler: ${parserError.textContent}`);
      const zfnet1 = xmlDoc.querySelector('ZFNET1');
      if (!zfnet1) throw new Error('Ungültiges XML-Format: ZFNET1-Element nicht gefunden');
      return Array.from(zfnet1.querySelectorAll('ZFKUERZEL')).map((k) => {
        const ablaufkuerzel = k.querySelector('ABLAUFKUERZEL')?.textContent?.trim() || '';
        const kostenstelle = k.querySelector('KOSTENSTELLE')?.textContent?.trim() || '';
        const ablauftext1 = k.querySelector('ABLAUFTEXT1')?.textContent?.trim() || '';
        const ablauftext2 = k.querySelector('ABLAUFTEXT2')?.textContent?.trim() || '';
        const ablauftext3 = k.querySelector('ABLAUFTEXT3')?.textContent?.trim() || '';
        const ablauftext4 = k.querySelector('ABLAUFTEXT4')?.textContent?.trim() || '';
        const zeitart = k.querySelector('ZEITART')?.textContent?.trim() || '';
        const einflussgroessen = [];
        const zfeinflussgroessen = k.querySelector('ZFEINFLUSSGROESSEN');
        if (zfeinflussgroessen) {
          Array.from(zfeinflussgroessen.querySelectorAll('ZFEINFLUSSGROESSE')).forEach((eg) => {
            const einflussname = eg.querySelector('EINFLUSSNAME')?.textContent?.trim() || '';
            const klassewert = eg.querySelector('KLASSEWERT')?.textContent?.trim() || '';
            const einheit = eg.querySelector('EINHEIT')?.textContent?.trim() || '';
            const messwerte = Array.from(eg.querySelectorAll('MESSWERT')).map((mw) => mw.textContent?.trim() || '').filter((mw) => mw);
            if (einflussname) einflussgroessen.push({ einflussname, klassewert, einheit, messwerte });
          });
        }
        const beschreibung = [ablauftext1, ablauftext2, ablauftext3, ablauftext4].filter((t) => t).join(' ');
        return {
          kuerzel: ablaufkuerzel,
          kostenstelle,
          beschreibung: beschreibung || ablaufkuerzel,
          fullText: [ablauftext1, ablauftext2, ablauftext3, ablauftext4].filter((t) => t).join(' '),
          zeitart,
          einflussgroessen
        };
      }).filter((k) => k.kuerzel && k.kuerzel !== '- - - -');
    };

    const parseEinflussInline = (xmlContent) => {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) throw new Error(`XML-Parsing-Fehler: ${parserError.textContent}`);
      const zfnet2 = xmlDoc.querySelector('ZFNET2');
      if (!zfnet2) throw new Error('Ungültiges XML-Format: ZFNET2-Element nicht gefunden');
      const einflussByName = new Map();
      Array.from(zfnet2.querySelectorAll('ZFEINFLUSSGROESSE')).forEach((eg) => {
        const name = eg.querySelector('EINFLUSSNAME')?.textContent?.trim() || '';
        if (!name) return;
        const messwerte = Array.from(eg.querySelectorAll('ZFMESSWERT')).map((mwNode) => {
          const lfdnrRaw = mwNode.querySelector('LFDNR')?.textContent?.trim() || '';
          const messwert = mwNode.querySelector('MESSWERT')?.textContent?.trim() || '';
          const beschreibung = mwNode.querySelector('BESCHREIBUNG')?.textContent?.trim() || '';
          const lfdnr = Number.parseInt(lfdnrRaw, 10);
          if (!messwert) return null;
          return { lfdnr: Number.isFinite(lfdnr) ? lfdnr : null, messwert, ...(beschreibung ? { beschreibung } : {}) };
        }).filter(Boolean);
        einflussByName.set(name, { name, messwerte });
      });
      return Array.from(einflussByName.values());
    };

    if (!sourceDetail) return;
    if (sourceType === 'timetypes') {
      const types = Array.isArray(sourceDetail.content_json) ? sourceDetail.content_json : [];
      if (!types.length) return;
      setTimeTypes(types);
      localStorage.setItem('stoppuhr_timetypes', JSON.stringify(types));
      return;
    }
    if (sourceType === 'ablaufdb') {
      const xml = (sourceDetail.content_xml || '').toString();
      if (!xml.trim()) return;
      const parsed = parseAblaufInline(xml);
      setAblaufDbData(parsed);
      setAblaufDbXmlContent(xml);
      setAblaufDbName(sourceDetail?.source?.name || 'Backend-AblaufDB.xml');
      localStorage.setItem('stoppuhr_ablaufdb_xml', xml);
      localStorage.setItem('stoppuhr_ablaufdb_name', sourceDetail?.source?.name || 'Backend-AblaufDB.xml');
      localStorage.setItem('stoppuhr_ablaufdb_path', '[Backend Quelle]');
      setAblaufDbPath('[Backend Quelle]');
      return;
    }
    if (sourceType === 'einflussdb') {
      const xml = (sourceDetail.content_xml || '').toString();
      if (!xml.trim()) return;
      const parsed = parseEinflussInline(xml);
      setEinflussDbData(parsed);
      setEinflussDbXmlContent(xml);
      setEinflussDbName(sourceDetail?.source?.name || 'Backend-EinflussDB.xml');
      localStorage.setItem('stoppuhr_einflussdb_xml', xml);
      localStorage.setItem('stoppuhr_einflussdb_name', sourceDetail?.source?.name || 'Backend-EinflussDB.xml');
      localStorage.setItem('stoppuhr_einflussdb_path', '[Backend Quelle]');
      setEinflussDbPath('[Backend Quelle]');
      return;
    }
    if (sourceType === 'dependentlists') {
      const workbook = Array.isArray(sourceDetail.content_json) ? sourceDetail.content_json : [];
      if (!workbook.length) return;
      setDependentListsData(workbook);
      setActiveDependentSheetId(workbook?.[0]?.id || '');
      localStorage.setItem('stoppuhr_dependent_lists', JSON.stringify(workbook));
    }
  }, []);

  const fetchDataSourceSelection = useCallback(async () => {
    if (!backendUrl || !userId) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    try {
      const selection = await getSourceSelection({ backendUrl, userId, token });
      setDataSourceSelection(selection || null);
    } catch (e) {
      // bewusst still: UI bleibt lokal bedienbar
    }
  }, [backendUrl, userId]);

  const fetchDataSourceList = useCallback(async () => {
    if (!backendUrl || !userId) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    setDataSourceLoading(true);
    setDataSourceError('');
    try {
      const result = await listSources({
        backendUrl,
        userId,
        token,
        sourceType: dataSourceType,
        search: dataSourceSearch,
        ownOnly: dataSourceOwnOnly,
        scope: dataSourceScope,
        limit: 200,
        offset: 0
      });
      setDataSourceList(Array.isArray(result?.sources) ? result.sources : []);
      setDataSourceTotal(Number(result?.total || 0));
    } catch (e) {
      setDataSourceError(e?.message || 'Cloud-Quellen konnten nicht geladen werden.');
      setDataSourceList([]);
      setDataSourceTotal(0);
    } finally {
      setDataSourceLoading(false);
    }
  }, [backendUrl, userId, dataSourceType, dataSourceSearch, dataSourceOwnOnly, dataSourceScope]);

  useEffect(() => {
    if (!showConfig || configTab !== 'datasources') return;
    fetchDataSourceSelection();
    fetchDataSourceList();
  }, [showConfig, configTab, fetchDataSourceSelection, fetchDataSourceList]);

  useEffect(() => {
    if (!showConfig || configTab !== 'datasources') return;
    fetchDataSourceList();
  }, [dataSourceType, dataSourceSearch, dataSourceOwnOnly, dataSourceScope, showConfig, configTab, fetchDataSourceList]);

  const loadCloudSourceToLocal = useCallback(async () => {
    if (!backendUrl || !userId || !dataSourceSelectedId) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    setDataSourceSyncInProgress(true);
    setDataSourceError('');
    setDataSourceInfo('');
    try {
      const detail = await getSource({
        backendUrl,
        userId,
        token,
        sourceId: dataSourceSelectedId
      });
      applySourceToLocal(dataSourceType, detail);
      const selectionKey = sourceTypeConfig[dataSourceType]?.selectionKey;
      const nextSelection = {
        ...(dataSourceSelection || {}),
        [selectionKey]: dataSourceSelectedId
      };
      await updateSourceSelection({
        backendUrl,
        userId,
        token,
        payload: {
          device_id: getDeviceId(),
          timetypes_source_id: nextSelection.timetypes_source_id ?? null,
          ablaufdb_source_id: nextSelection.ablaufdb_source_id ?? null,
          einflussdb_source_id: nextSelection.einflussdb_source_id ?? null,
          dependentlists_source_id: nextSelection.dependentlists_source_id ?? null
        }
      });
      setDataSourceSelection(nextSelection);
      setDataSourceSelected(detail);
      setDataSourceInfo(`Quelle „${detail?.source?.name || 'Unbekannt'}“ lokal aktiviert.`);
    } catch (e) {
      setDataSourceError(e?.message || 'Cloud-Quelle konnte nicht geladen werden.');
    } finally {
      setDataSourceSyncInProgress(false);
    }
  }, [backendUrl, userId, dataSourceSelectedId, dataSourceType, applySourceToLocal, dataSourceSelection]);

  const saveLocalToCloud = useCallback(async ({ forceCreate = false } = {}) => {
    if (!backendUrl || !userId) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    setDataSourceSyncInProgress(true);
    setDataSourceError('');
    setDataSourceInfo('');
    try {
      const localPayload = getLocalSourcePayload(dataSourceType);
      if (!localPayload) {
        setDataSourceError('Keine lokalen Daten vorhanden, die gespeichert werden können.');
        return;
      }

      const current = dataSourceList.find((s) => s.id === dataSourceSelectedId);
      if (current && !forceCreate) {
        const payload = {
          if_match_etag: current.etag,
          content_json: localPayload.content_json,
          content_xml: localPayload.content_xml
        };
        await updateSourceContent({ backendUrl, userId, token, sourceId: current.id, payload });
        setDataSourceInfo(`Quelle „${current.name}“ aktualisiert.`);
      } else {
        const createPayload = {
          source_type: dataSourceType,
          scope: 'private',
          format: sourceTypeConfig[dataSourceType]?.format || localPayload.format,
          name: `${dataSourceType}-${new Date().toISOString().slice(0, 19).replace('T', ' ')}`,
          description: 'Aus lokaler Konfiguration gespeichert',
          tags: [],
          content_json: localPayload.content_json,
          content_xml: localPayload.content_xml
        };
        const created = await createSource({ backendUrl, userId, token, payload: createPayload });
        if (created?.id) setDataSourceSelectedId(created.id);
        setDataSourceInfo('Neue Cloud-Quelle aus lokalen Daten erstellt.');
      }

      await fetchDataSourceList();
      await fetchDataSourceSelection();
    } catch (e) {
      if (e?.status === 409) {
        setDataSourceError(`Konflikt erkannt (ETag). Bitte Quelle neu laden und erneut speichern.`);
      } else {
        setDataSourceError(e?.message || 'Cloud-Speichern fehlgeschlagen.');
      }
    } finally {
      setDataSourceSyncInProgress(false);
    }
  }, [backendUrl, userId, dataSourceType, dataSourceList, dataSourceSelectedId, getLocalSourcePayload, sourceTypeConfig, fetchDataSourceList, fetchDataSourceSelection]);

  const deleteSelectedCloudSource = useCallback(async () => {
    if (!backendUrl || !userId || !dataSourceSelectedId) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    setDataSourceSyncInProgress(true);
    setDataSourceError('');
    setDataSourceInfo('');
    try {
      await deleteSource({ backendUrl, userId, token, sourceId: dataSourceSelectedId });
      setDataSourceSelectedId(null);
      setDataSourceSelected(null);
      setDataSourceInfo('Cloud-Quelle gelöscht.');
      await fetchDataSourceList();
      await fetchDataSourceSelection();
    } catch (e) {
      setDataSourceError(e?.message || 'Löschen fehlgeschlagen.');
    } finally {
      setDataSourceSyncInProgress(false);
    }
  }, [backendUrl, userId, dataSourceSelectedId, fetchDataSourceList, fetchDataSourceSelection]);

  const renderDataSourcesHub = () => {
    const activeSelectionKey = sourceTypeConfig[dataSourceType]?.selectionKey;
    const activeSelectionId = dataSourceSelection?.[activeSelectionKey] || null;
    const token = localStorage.getItem('userToken');

    return (
      <div className="config-tab-content">
        <div className="ablaufdb-config">
          <h4>{t('config.datasources.title')}</h4>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '12px' }}>{t('config.datasources.desc')}</p>

          <div className="config-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['timetypes', 'ablaufdb', 'einflussdb', 'dependentlists'].map((type) => (
              <button
                key={type}
                className={`btn ${dataSourceType === type ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setDataSourceType(type)}
              >
                {t(`config.tabs.${type}`)}
              </button>
            ))}
          </div>

          <div className="config-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ marginRight: '6px' }}>{t('config.datasources.mode')}:</label>
            <button className={`btn ${dataSourceMode === 'local' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDataSourceMode('local')}>{t('config.datasources.local')}</button>
            <button className={`btn ${dataSourceMode === 'cloud' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setDataSourceMode('cloud')}>{t('config.datasources.cloud')}</button>
          </div>

          {dataSourceMode === 'local' && (
            <div className="config-item" style={{ padding: '12px', borderRadius: '8px', border: '1px solid #d1d5db' }}>
              <p style={{ marginBottom: '8px' }}>{t('config.datasources.local_hint')}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => saveLocalToCloud({ forceCreate: false })} disabled={!backendUrl || !userId || !token || dataSourceSyncInProgress}>
                  {t('config.datasources.save_to_cloud')}
                </button>
                <button className="btn btn-secondary" onClick={() => saveLocalToCloud({ forceCreate: true })} disabled={!backendUrl || !userId || !token || dataSourceSyncInProgress}>
                  {t('config.datasources.save_as_new')}
                </button>
              </div>
            </div>
          )}

          {dataSourceMode === 'cloud' && (
            <>
              <div className="config-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('config.datasources.search_placeholder')}
                  value={dataSourceSearch}
                  onChange={(e) => setDataSourceSearch(e.target.value)}
                  style={{ minWidth: '220px', flex: 1 }}
                />
                <select value={dataSourceScope} onChange={(e) => setDataSourceScope(e.target.value)}>
                  <option value="">{t('config.datasources.scope_all')}</option>
                  <option value="private">{t('config.datasources.scope_private')}</option>
                  <option value="company">{t('config.datasources.scope_company')}</option>
                  <option value="global">{t('config.datasources.scope_global')}</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input type="checkbox" checked={dataSourceOwnOnly} onChange={(e) => setDataSourceOwnOnly(e.target.checked)} />
                  {t('config.datasources.only_own')}
                </label>
                <button className="btn btn-secondary" onClick={fetchDataSourceList} disabled={dataSourceLoading || !backendUrl || !token}>
                  {t('config.datasources.refresh')}
                </button>
              </div>

              <div className="config-item" style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', fontSize: '0.85rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                  {t('config.datasources.total')}: {dataSourceTotal}
                </div>
                <div style={{ maxHeight: '220px', overflow: 'auto' }}>
                  {dataSourceLoading && <div style={{ padding: '10px 12px' }}>{t('config.datasources.loading')}</div>}
                  {!dataSourceLoading && dataSourceList.length === 0 && <div style={{ padding: '10px 12px' }}>{t('config.datasources.empty')}</div>}
                  {!dataSourceLoading && dataSourceList.map((src) => (
                    <button
                      key={src.id}
                      type="button"
                      onClick={async () => {
                        setDataSourceSelectedId(src.id);
                        setDataSourceRename(src.name || '');
                        try {
                          const detail = await getSource({ backendUrl, userId, token, sourceId: src.id });
                          setDataSourceSelected(detail);
                        } catch (e) {
                          setDataSourceError(e?.message || 'Quelle konnte nicht geladen werden.');
                        }
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        borderBottom: '1px solid #f3f4f6',
                        padding: '10px 12px',
                        background: dataSourceSelectedId === src.id ? (darkMode ? '#1f2937' : '#eff6ff') : 'transparent',
                        cursor: 'pointer'
                      }}
                    >
                      <strong>{src.name}</strong> <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>({src.scope}, v{src.version})</span>
                      {activeSelectionId === src.id && <span style={{ marginLeft: '6px', fontSize: '0.75rem' }}>• {t('config.datasources.active')}</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="config-item" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={loadCloudSourceToLocal} disabled={!dataSourceSelectedId || dataSourceSyncInProgress}>{t('config.datasources.load_local')}</button>
                <button className="btn btn-secondary" onClick={() => saveLocalToCloud({ forceCreate: false })} disabled={!dataSourceSelectedId || dataSourceSyncInProgress}>{t('config.datasources.overwrite_cloud')}</button>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (!dataSourceSelectedId) return;
                    try {
                      setDataSourceSyncInProgress(true);
                      await cloneSource({ backendUrl, userId, token, sourceId: dataSourceSelectedId });
                      await fetchDataSourceList();
                      setDataSourceInfo(t('config.datasources.cloned'));
                    } catch (e) {
                      setDataSourceError(e?.message || 'Kopieren fehlgeschlagen.');
                    } finally {
                      setDataSourceSyncInProgress(false);
                    }
                  }}
                  disabled={!dataSourceSelectedId || dataSourceSyncInProgress}
                >
                  {t('config.datasources.clone')}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    if (!dataSourceSelectedId || !dataSourceSelected?.source || !dataSourceRename.trim()) return;
                    try {
                      setDataSourceSyncInProgress(true);
                      await updateSourceMeta({
                        backendUrl,
                        userId,
                        token,
                        sourceId: dataSourceSelectedId,
                        payload: { name: dataSourceRename.trim() }
                      });
                      await fetchDataSourceList();
                      setDataSourceInfo(t('config.datasources.renamed'));
                    } catch (e) {
                      setDataSourceError(e?.message || 'Umbenennen fehlgeschlagen.');
                    } finally {
                      setDataSourceSyncInProgress(false);
                    }
                  }}
                  disabled={!dataSourceSelectedId || dataSourceSyncInProgress}
                >
                  {t('config.datasources.rename')}
                </button>
                <button className="btn btn-secondary" onClick={deleteSelectedCloudSource} disabled={!dataSourceSelectedId || dataSourceSyncInProgress}>
                  {t('config.datasources.delete')}
                </button>
              </div>
              <div className="config-item">
                <label>{t('config.datasources.rename_field')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={dataSourceRename}
                  onChange={(e) => setDataSourceRename(e.target.value)}
                  placeholder={t('config.datasources.rename_placeholder')}
                />
              </div>
            </>
          )}

          {dataSourceError && <div style={{ marginTop: '8px', color: '#991b1b', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: '8px', padding: '8px 10px' }}>{dataSourceError}</div>}
          {dataSourceInfo && <div style={{ marginTop: '8px', color: '#065f46', background: '#d1fae5', border: '1px solid #10b981', borderRadius: '8px', padding: '8px 10px' }}>{dataSourceInfo}</div>}
        </div>
      </div>
    );
  };

  const handleAddDependentSheet = () => {
    const baseName = 'Blatt';
    const usedNames = new Set(dependentListsData.map((s) => (s.name || '').trim()));
    let idx = dependentListsData.length + 1;
    let name = `${baseName} ${idx}`;
    while (usedNames.has(name)) {
      idx += 1;
      name = `${baseName} ${idx}`;
    }
    const sheet = {
      id: `sheet_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name,
      columns: [],
      rows: []
    };
    setDependentListsData((prev) => [...prev, sheet]);
    setActiveDependentSheetId(sheet.id);
    setDependentListsError('');
  };

  const handleRenameDependentSheet = (sheetId, name) => {
    setDependentListsData((prev) =>
      prev.map((sheet) => (sheet.id === sheetId ? { ...sheet, name } : sheet))
    );
  };

  

  const updateActiveDependentSheet = (updater) => {
    if (!activeDependentSheet) return;
    setDependentListsData((prev) =>
      prev.map((sheet) => (sheet.id === activeDependentSheet.id ? updater(sheet) : sheet))
    );
  };

  const handleDependentAddColumn = () => {
    updateActiveDependentSheet((sheet) => {
      const nextColumns = [...(sheet.columns || []), `Spalte ${(sheet.columns || []).length + 1}`];
      const nextRows = (sheet.rows || []).map((row) => {
        const padded = Array.isArray(row) ? [...row] : [];
        while (padded.length < nextColumns.length) padded.push('');
        return padded.slice(0, nextColumns.length);
      });
      return { ...sheet, columns: nextColumns, rows: nextRows };
    });
  };

  const handleDependentRemoveColumn = () => {
    if (!activeDependentSheet || (activeDependentSheet.columns || []).length === 0) return;
    updateActiveDependentSheet((sheet) => {
      const nextColumns = (sheet.columns || []).slice(0, -1);
      const nextRows = (sheet.rows || []).map((row) =>
        (Array.isArray(row) ? row : []).slice(0, nextColumns.length)
      );
      return { ...sheet, columns: nextColumns, rows: nextRows };
    });
  };

  const handleDependentColumnRename = (columnIdx, value) => {
    updateActiveDependentSheet((sheet) => {
      const nextColumns = [...(sheet.columns || [])];
      nextColumns[columnIdx] = value;
      return { ...sheet, columns: nextColumns };
    });
  };

  const handleDependentAddRow = () => {
    updateActiveDependentSheet((sheet) => {
      const columnCount = (sheet.columns || []).length;
      const newRow = Array.from({ length: columnCount }, () => '');
      return { ...sheet, rows: [...(sheet.rows || []), newRow] };
    });
  };

  const handleDependentRemoveRow = () => {
    updateActiveDependentSheet((sheet) => ({ ...sheet, rows: (sheet.rows || []).slice(0, -1) }));
  };

  const handleDependentCellChange = (rowIdx, colIdx, value) => {
    updateActiveDependentSheet((sheet) => {
      const nextRows = (sheet.rows || []).map((row) => (Array.isArray(row) ? [...row] : []));
      while (nextRows.length <= rowIdx) {
        nextRows.push(Array.from({ length: (sheet.columns || []).length }, () => ''));
      }
      const targetRow = nextRows[rowIdx];
      while (targetRow.length < (sheet.columns || []).length) targetRow.push('');
      targetRow[colIdx] = value;
      return { ...sheet, rows: nextRows };
    });
  };

  const parseDelimitedTable = (text) => {
    const rawLines = (text || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (!rawLines.length) return { columns: [], rows: [] };
    const delimiters = ['\t', ';', ',', '|'];
    let delimiter = '\t';
    let bestScore = -1;
    delimiters.forEach((d) => {
      const score = rawLines[0].split(d).length;
      if (score > bestScore) {
        bestScore = score;
        delimiter = d;
      }
    });
    const rows = rawLines.map((line) => line.split(delimiter).map((v) => v.trim()));
    const columnCount = Math.max(...rows.map((r) => r.length), 0);
    const normalizedRows = rows.map((r) => {
      const next = [...r];
      while (next.length < columnCount) next.push('');
      return next;
    });
    const columns = normalizedRows.shift() || [];
    return { columns, rows: normalizedRows };
  };

  const applyParsedTableToActiveSheet = ({ columns, rows }) => {
    const normalizedColumns = (columns || []).map((c) => (c || '').toString().trim()).filter((c) => c);
    if (!normalizedColumns.length) {
      setDependentListsError(t('config.dependentlists.validation_columns_required'));
      return false;
    }
    const normalizedRows = (rows || []).map((row) => {
      const next = Array.isArray(row) ? row.map((v) => (v || '').toString().trim()) : [];
      while (next.length < normalizedColumns.length) next.push('');
      return next.slice(0, normalizedColumns.length);
    });
    updateActiveDependentSheet((sheet) => ({ ...sheet, columns: normalizedColumns, rows: normalizedRows }));
    setDependentListsError('');
    return true;
  };

  const handleDependentCopyToClipboard = async () => {
    if (!activeDependentSheet) return;
    try {
      const header = (activeDependentSheet.columns || []).join('\t');
      const lines = (activeDependentSheet.rows || []).map((row) => (row || []).join('\t'));
      const payload = [header, ...lines].filter((line) => line !== '').join('\n');
      await navigator.clipboard.writeText(payload);
      setDependentListsError('');
    } catch (e) {
      setDependentListsError(t('config.dependentlists.clipboard_copy_error'));
    }
  };

  const handleDependentPasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = parseDelimitedTable(text);
      applyParsedTableToActiveSheet(parsed);
    } catch (e) {
      setDependentListsError(t('config.dependentlists.clipboard_paste_error'));
    }
  };

  const handleDependentFileLoad = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    if (!(name.endsWith('.csv') || name.endsWith('.tsv') || name.endsWith('.txt'))) {
      setDependentListsError(t('config.dependentlists.file_type_not_supported'));
      if (event.target) event.target.value = '';
      return;
    }
    try {
      const text = await file.text();
      const parsed = parseDelimitedTable(text);
      applyParsedTableToActiveSheet(parsed);
    } catch (e) {
      setDependentListsError(t('config.dependentlists.file_load_error'));
    } finally {
      if (event.target) event.target.value = '';
    }
  };

  const renderDependentListsGrid = () => {
    if (!activeDependentSheet) return null;
    const columns = activeDependentSheet.columns || [];
    const rows = activeDependentSheet.rows || [];
    return (
      <>
        <div className="config-item">
          <label>{t('config.dependentlists.exchange_label')}</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={handleDependentPasteFromClipboard}>
              📥 {t('config.dependentlists.button_paste_clipboard')}
            </button>
            <button className="btn btn-secondary" onClick={handleDependentCopyToClipboard}>
              📋 {t('config.dependentlists.button_copy_clipboard')}
            </button>
            <button className="btn btn-secondary" onClick={() => dependentListFileInputRef.current?.click()}>
              📁 {t('config.dependentlists.button_load_file')}
            </button>
            <input
              type="file"
              ref={dependentListFileInputRef}
              onChange={handleDependentFileLoad}
              accept=".csv,.tsv,.txt"
              style={{ display: 'none' }}
            />
          </div>
          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
            {t('config.dependentlists.exchange_desc')}
          </p>
        </div>

        <div className="config-item">
          <label>{t('config.dependentlists.grid_label')}</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <button className="btn btn-secondary" onClick={handleDependentAddColumn}>{t('config.dependentlists.button_add_column')}</button>
            <button className="btn btn-secondary" onClick={handleDependentRemoveColumn}>{t('config.dependentlists.button_remove_column')}</button>
            <button className="btn btn-secondary" onClick={handleDependentAddRow}>{t('config.dependentlists.button_add_row')}</button>
            <button className="btn btn-secondary" onClick={handleDependentRemoveRow}>{t('config.dependentlists.button_remove_row')}</button>
          </div>
          <div className="dependent-grid-wrap">
            <table className="einflussgroessen-table-edit einflussgroessen-table-rotated dependent-grid" style={{ marginTop: 0 }}>
              <thead>
                <tr>
                  <th className="dependent-grid-row-header">{t('config.dependentlists.row_header')}</th>
                  {columns.map((column, idx) => (
                    <th key={`dep-col-${idx}`} className="dependent-grid-col-header">
                      <input
                        type="text"
                        className="einflussgroesse-input-edit dependent-grid-header-input"
                        value={column}
                        onChange={(e) => handleDependentColumnRename(idx, e.target.value)}
                        placeholder={t('config.dependentlists.column_placeholder_short')}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => (
                  <tr key={`dep-row-${rowIdx}`} className="einflussgroesse-table-row-edit">
                    <td className="einflussgroesse-label-cell-edit dependent-grid-row-number" style={{ fontWeight: 600 }}>
                      #{rowIdx + 1}
                    </td>
                    {columns.map((_, colIdx) => (
                      <td key={`dep-cell-${rowIdx}-${colIdx}`} className="einflussgroesse-input-cell-edit dependent-grid-cell">
                        <input
                          type="text"
                          className="einflussgroesse-input-edit dependent-grid-cell-input"
                          value={(row && row[colIdx]) || ''}
                          onChange={(e) => handleDependentCellChange(rowIdx, colIdx, e.target.value)}
                          placeholder={t('config.dependentlists.cell_placeholder')}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderDependentSheetBar = () => (
    <div className="form-actions" style={{ marginTop: '16px', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
        <span style={{ fontSize: '0.85rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{t('config.dependentlists.sheet_label')}:</span>
        <select
          value={activeDependentSheet?.id || ''}
          onChange={(e) => setActiveDependentSheetId(e.target.value)}
          style={{ flex: 1, maxWidth: '320px' }}
        >
          {dependentListsData.map((sheet) => (
            <option key={sheet.id} value={sheet.id}>
              {sheet.name || t('config.dependentlists.sheet_fallback')}
            </option>
          ))}
        </select>
      </div>
      <button className="btn btn-secondary" onClick={handleAddDependentSheet}>
        ➕ {t('config.dependentlists.button_add_sheet')}
      </button>
    </div>
  );

  // Ablauf-Kürzel-Liste: Höhe an rechte Spalte koppeln (Inhalt der rechten Spalte messen, nicht die Zelle)
  useEffect(() => {
    const rightContent = activityInputSectionRightRef.current;
    const listEl = ablaufKuerzelListRef.current;
    if (!rightContent || !listEl) return;
    const syncListHeight = () => {
      const h = rightContent.offsetHeight;
      if (h > 0) {
        listEl.style.height = `${h}px`;
        listEl.style.maxHeight = `${h}px`;
      } else {
        listEl.style.height = '';
        listEl.style.maxHeight = '';
      }
    };
    const runAfterLayout = () => {
      syncListHeight();
      requestAnimationFrame(syncListHeight);
    };
    runAfterLayout();
    const ro = new ResizeObserver(runAfterLayout);
    ro.observe(rightContent);
    return () => ro.disconnect();
  }, [showAblaufDb, ablaufDbData]);

  // Debug-Log Aktivierung (nur für Development)
  const [enableDebugLogs, setEnableDebugLogs] = useState(() => {
    if (process.env.NODE_ENV === 'production') {
      return false; // In Production immer deaktiviert
    }
    const saved = localStorage.getItem('stoppuhr_enable_debug_logs');
    return saved !== null ? saved === 'true' : false; // Default: false
  });

  // OHNE Leistungsgrad erfassen: Portrait nur „Übernehmen“-Button (Grade 0), keine Leistungsgrad-Buttons (Default: false)
  const [ohneLeistungsgradErfassen, setOhneLeistungsgradErfassen] = useState(() => {
    try {
      const saved = localStorage.getItem('stoppuhr_ohne_leistungsgrad_erfassen');
      return saved === 'true';
    } catch { return false; }
  });
  const [telemetryEnabled, setTelemetryEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('stoppuhr_telemetry_enabled');
      return saved !== null ? saved === 'true' : true; // Default: AN
    } catch { return true; }
  });

  // localStorage Keys
  const STORAGE_KEY_SESSIONS = 'stoppuhr_sessions';

  // Format Time
  const formatTime = (hundredthsOfMinutes) => {
    const totalMinutes = Math.floor(hundredthsOfMinutes / 100);
    const hundredths = Math.floor(hundredthsOfMinutes % 100);
    return `${totalMinutes.toString().padStart(2, '0')}:${hundredths.toString().padStart(2, '0')}`;
  };

  // Formatierung für Vorderseite-Zeiten (in Minuten, als Dezimalzahl)
  const formatVorderseiteTime = (value, decimalPlaces) => {
    const numValue = parseFloat(value) || 0;
    return numValue.toFixed(decimalPlaces);
  };

  // Formatierung für Sollzeiten (von Hundertstel-Minuten zu Minuten, als Dezimalzahl)
  const formatSollzeit = (hundredthsOfMinutes, decimalPlaces) => {
    const minutes = (parseFloat(hundredthsOfMinutes) || 0) / 100;
    return minutes.toFixed(decimalPlaces);
  };

  const normalizeMesswerte = (values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((mw) => (typeof mw === 'string' ? mw : (mw?.messwert || '')))
      .map((v) => (v || '').toString().trim())
      .filter((v) => v);
  };

  const getDependentSheetByName = useCallback((sheetName) => {
    const target = (sheetName || '').toString().trim();
    if (!target) return null;
    return (dependentListsData || []).find((sheet) => (sheet?.name || '').trim() === target) || null;
  }, [dependentListsData]);

  const getDependentOptionValues = useCallback((einfluss, currentValues = {}) => {
    if (!einfluss || !einfluss.einflussname || !selectedKuerzel?.einflussgroessen) return [];
    const targetName = (einfluss.einflussname || '').toString().trim();
    const sheetName = (einfluss.einheit || '').toString().trim();
    if (!targetName || !sheetName) return [];
    const sheet = getDependentSheetByName(sheetName);
    if (!sheet || !Array.isArray(sheet.columns) || !Array.isArray(sheet.rows) || sheet.columns.length === 0) return [];

    const targetIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === targetName);
    if (targetIdx < 0) return [];

    const dependentFields = (selectedKuerzel.einflussgroessen || [])
      .filter((f) => f && f.einflussname)
      .map((f) => ({
        name: (f.einflussname || '').toString().trim(),
        typ: ((f.typ || f.klassewert || '').toString().trim().charAt(0) || ''),
        sheetName: (f.einheit || '').toString().trim()
      }))
      .filter((f) => (f.typ === 'l' || f.typ === 'L') && f.sheetName === sheetName && f.name);

    const filteredRows = (sheet.rows || []).filter((row) => {
      if (!Array.isArray(row)) return false;
      return dependentFields.every((field) => {
        if (field.name === targetName) return true;
        const selected = (currentValues?.[field.name] || '').toString().trim();
        if (!selected) return true;
        const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === field.name);
        if (colIdx < 0) return true;
        return ((row[colIdx] || '').toString().trim() === selected);
      });
    });

    return Array.from(new Set(
      filteredRows
        .map((row) => (row[targetIdx] || '').toString().trim())
        .filter((v) => v)
    ));
  }, [selectedKuerzel, getDependentSheetByName]);

  const applyDependentAutofillValues = useCallback((nextValues) => {
    if (!selectedKuerzel?.einflussgroessen) return nextValues;
    const result = { ...(nextValues || {}) };
    const sheets = Array.from(new Set(
      (selectedKuerzel.einflussgroessen || [])
        .map((f) => {
          const typ = ((f?.typ || f?.klassewert || '').toString().trim().charAt(0) || '');
          const sName = (f?.einheit || '').toString().trim();
          return (typ === 'l' || typ === 'L') ? sName : '';
        })
        .filter((s) => s)
    ));

    sheets.forEach((sheetName) => {
      const sheet = getDependentSheetByName(sheetName);
      if (!sheet || !Array.isArray(sheet.columns) || !Array.isArray(sheet.rows) || sheet.columns.length === 0) return;
      const fields = (selectedKuerzel.einflussgroessen || [])
        .filter((f) => {
          const typ = ((f?.typ || f?.klassewert || '').toString().trim().charAt(0) || '');
          const sName = (f?.einheit || '').toString().trim();
          return (typ === 'l' || typ === 'L') && sName === sheetName;
        })
        .map((f) => (f?.einflussname || '').toString().trim())
        .filter((n) => n);
      if (!fields.length) return;

      const matchingRows = (sheet.rows || []).filter((row) => {
        if (!Array.isArray(row)) return false;
        return fields.every((fieldName) => {
          const selected = (result[fieldName] || '').toString().trim();
          if (!selected) return true;
          const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === fieldName);
          if (colIdx < 0) return true;
          return ((row[colIdx] || '').toString().trim() === selected);
        });
      });

      if (matchingRows.length === 1) {
        const tuple = matchingRows[0];
        fields.forEach((fieldName) => {
          const current = (result[fieldName] || '').toString().trim();
          if (current) return;
          const colIdx = sheet.columns.findIndex((c) => (c || '').toString().trim() === fieldName);
          if (colIdx < 0) return;
          const tupleValue = (tuple[colIdx] || '').toString().trim();
          if (tupleValue) result[fieldName] = tupleValue;
        });
      }
    });
    return result;
  }, [selectedKuerzel, getDependentSheetByName]);

  const applyEinflussValueChange = useCallback((name, value) => {
    const key = (name || '').toString().trim();
    if (!key) return;
    setEinflussgroessenValues((prev) => {
      const nextRaw = { ...(prev || {}), [key]: value };
      const next = applyDependentAutofillValues(nextRaw);
      einflussgroessenValuesRef.current = next;
      return next;
    });
  }, [applyDependentAutofillValues]);

  const getEinflussOptionValues = (einfluss) => {
    if (!einfluss || !einfluss.einflussname) return [];
    const name = einfluss.einflussname.trim();
    if (!name) return [];
    const typRaw = (einfluss.typ || einfluss.klassewert || '').toString().trim();
    const typ = typRaw ? typRaw.charAt(0) : '';
    const isDependentListType = typ === 'l' || typ === 'L';
    const isEinflussDbType = typ === 'x' || typ === 'X';

    if (isDependentListType) {
      const fromDependentSheet = getDependentOptionValues(einfluss, einflussgroessenValues || {});
      if (fromDependentSheet.length > 0) {
        return fromDependentSheet;
      }
      return normalizeMesswerte(einfluss.messwerte);
    }

    if (isEinflussDbType) {
      const dbEntry = (einflussDbData || []).find((item) => (item?.name || '').trim() === name);
      if (dbEntry?.messwerte) {
        return normalizeMesswerte(dbEntry.messwerte);
      }
      return normalizeMesswerte(einfluss.messwerte);
    }

    // Fallback ohne Merge: nur lokale Ablauf-Messwerte verwenden, wenn kein DB-Treffer vorhanden.
    return normalizeMesswerte(einfluss.messwerte);
  };

  const getEinflussType = (einfluss) => {
    const raw = (einfluss?.typ || einfluss?.klassewert || '').toString().trim();
    return raw ? raw.charAt(0) : '';
  };

  const isGlobalEinflussType = (einfluss) => {
    const t = getEinflussType(einfluss);
    return t === 'x' || t === 'l';
  };

  const getCurrentTime = () => {
    if (!startTime) return 0;
    const elapsed = Date.now() - startTime;
    // Berechne Zeit in Hundertsteln von Minuten und runde auf ganze Zahl
    return Math.round((elapsed / 1000 / 60) * 100);
  };

  // Hilfsfunktion: Formatiere Leistungsgrad, wobei 0% nicht angezeigt wird
  // WICHTIG: Als normale Funktion (nicht useCallback), damit sie beim Update-Resume sofort verfügbar ist
  // und nicht von der Hook-Initialisierungsreihenfolge abhängt
  const formatGrade = (avgGrade) => {
    if (!avgGrade || avgGrade === '0' || avgGrade === '0.0' || parseFloat(avgGrade) === 0) {
      return '';
    }
    return `Ø ${avgGrade}%`;
  };

  // Browser-Erkennung für Safari
  const isSafari = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/chromium/.test(userAgent) && !/firefox/.test(userAgent);
  };

  const [isSpeechRecognitionSupported, setIsSpeechRecognitionSupported] = useState(false);

  const [isListeningVorderseiteNotiz, setIsListeningVorderseiteNotiz] = useState(false);
  const isListeningVorderseiteNotizRef = useRef(false);
  const handleVorderseiteSpeechToggle = useCallback(() => {
    if (!recognitionEdit || !isSpeechRecognitionSupported) return;
    if (!isListeningVorderseiteNotizRef.current) {
      try {
        isListeningVorderseiteNotizRef.current = true;
        setIsListeningVorderseiteNotiz(true);
        recognitionEdit.start();
      } catch (error) {
        console.error('[Speech Recognition] Fehler beim Starten:', error);
        isListeningVorderseiteNotizRef.current = false;
        setIsListeningVorderseiteNotiz(false);
      }
    } else {
      try {
        recognitionEdit.stop();
      } catch (e) { /* ignore */ }
      isListeningVorderseiteNotizRef.current = false;
      setIsListeningVorderseiteNotiz(false);
    }
  }, [recognitionEdit, isSpeechRecognitionSupported]);

  // Tab-ID Initialisierung für Multi-Tab-Unterstützung
  useEffect(() => {
    const tabId = getOrCreateTabId();
    if (getEnableDebugLogs()) {
      debugLog('App.jsx:Tab-ID', 'Tab-ID initialisiert', { tabId }, 'A');
    }
  }, []);

  // Zeitstrahl-Hover-Popover schließen beim Wechsel in andere Auswertungsansicht
  useEffect(() => {
    if (analysisViewMode !== 'zeitstrahl') setZeitstrahlHover(null);
  }, [analysisViewMode]);

  // Initialize Speech Recognition for main note input
  useEffect(() => {
    const speechSupported = ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) && !isSafari();
    setIsSpeechRecognitionSupported(speechSupported);
    
    if (speechSupported) {
      try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'de-DE';
      
      recognitionInstance.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript.trim() && isListeningRef.current) {
          setNote(prev => prev + (prev ? ' ' : '') + finalTranscript.trim());
        }
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        // Ignoriere 'no-speech' Fehler, da wir kontinuierlich aufnehmen
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          setIsListening(false);
          isListeningRef.current = false;
        }
      };
      
      recognitionInstance.onend = () => {
        // Wenn wir noch hören sollten (Button noch gedrückt), versuche neu zu starten
        if (isListeningRef.current) {
          setTimeout(() => {
            if (isListeningRef.current && recognitionInstance) {
              try {
                recognitionInstance.start();
              } catch (error) {
                // Ignoriere Fehler beim Neustart
                isListeningRef.current = false;
                setIsListening(false);
              }
            }
          }, 100);
        }
      };
      
      setRecognition(recognitionInstance);
        console.log('[Speech Recognition] Haupt-Recognition initialisiert');
      } catch (error) {
        console.error('[Speech Recognition] Fehler beim Initialisieren der Haupt-Recognition:', error);
        setRecognition(null);
      }
    } else {
      if (isSafari()) {
        console.warn('[Speech Recognition] Safari unterstützt die Web Speech API nicht. Bitte verwenden Sie Chrome oder Edge.');
      } else {
        console.warn('[Speech Recognition] Speech Recognition API nicht verfügbar');
      }
      setRecognition(null);
    }
  }, []);

  // Initialize Speech Recognition for edit dialog
  useEffect(() => {
    const speechSupported = ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) && !isSafari();
    
    if (speechSupported) {
      try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'de-DE';
      
      recognitionInstance.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
          if (finalTranscript.trim()) {
            if (isListeningEditActivityRef.current) {
              setEditActivity(prev => prev + (prev ? ' ' : '') + finalTranscript.trim());
            } else if (isListeningEditRef.current) {
          setEditNote(prev => prev + (prev ? ' ' : '') + finalTranscript.trim());
            }
        }
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        // Ignoriere 'no-speech' Fehler, da wir kontinuierlich aufnehmen
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          setIsListeningEdit(false);
            setIsListeningEditActivity(false);
          isListeningEditRef.current = false;
            isListeningEditActivityRef.current = false;
        }
      };
      
      recognitionInstance.onend = () => {
        // Wenn wir noch hören sollten (Button noch gedrückt), versuche neu zu starten
          if (isListeningEditRef.current || isListeningEditActivityRef.current) {
          setTimeout(() => {
              if ((isListeningEditRef.current || isListeningEditActivityRef.current) && recognitionInstance) {
              try {
                recognitionInstance.start();
              } catch (error) {
                // Ignoriere Fehler beim Neustart
                isListeningEditRef.current = false;
                  isListeningEditActivityRef.current = false;
                setIsListeningEdit(false);
                  setIsListeningEditActivity(false);
              }
            }
          }, 100);
        }
      };
      
      setRecognitionEdit(recognitionInstance);
        console.log('[Speech Recognition] Edit-Dialog Recognition initialisiert');
      } catch (error) {
        console.error('[Speech Recognition] Fehler beim Initialisieren der Edit-Dialog Recognition:', error);
        setRecognitionEdit(null);
      }
    } else {
      if (isSafari()) {
        console.warn('[Speech Recognition] Safari unterstützt die Web Speech API nicht. Bitte verwenden Sie Chrome oder Edge.');
      } else {
        console.warn('[Speech Recognition] Speech Recognition API nicht verfügbar');
      }
      setRecognitionEdit(null);
    }
  }, []);

  // Hilfsfunktion: Begrenze Sessions auf MAX_SESSIONS und sortiere nach createdAt (neueste oben)
  const limitAndSortSessions = useCallback((sessionsList) => {
    // Sortiere nach lastUpdated (wenn vorhanden), sonst createdAt (neueste zuerst)
    const sorted = [...sessionsList].sort((a, b) => {
      const dateA = new Date(a.lastUpdated || a.createdAt || 0);
      const dateB = new Date(b.lastUpdated || b.createdAt || 0);
      return dateB - dateA; // Neueste zuerst
    });
    // Begrenze auf MAX_SESSIONS
    return sorted.slice(0, MAX_SESSIONS);
  }, []);

  // Messung (Session) umbenennen: lokaler State + Sessions-Liste + optional Backend
  const handleRenameSession = useCallback(async (newName) => {
    const name = (newName || '').trim() || 'Messung (ohne Namen)';
    if (!selectedSession) return;
    setSelectedSession((prev) => (prev ? { ...prev, sessionName: name } : null));
    const updatedSessions = sessions.map((s) =>
      s.id === selectedSession.id ? { ...s, sessionName: name } : s
    );
    const limitedSessions = limitAndSortSessions(updatedSessions);
    setSessions(limitedSessions);
    try {
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    } catch (e) {
      console.warn('[RenameSession] localStorage setItem fehlgeschlagen:', e);
    }
    if (typeof navigator !== 'undefined' && navigator.onLine && backendUrl && userId && selectedSession.id) {
      const token = localStorage.getItem('userToken');
      if (token) {
        try {
          const url = `${backendUrl.replace(/\/+$/, '')}/api/user/${userId}/sessions/${selectedSession.id}`;
          const res = await fetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ session_name: name }),
          });
          if (!res.ok) {
            console.warn('[RenameSession] Backend PATCH fehlgeschlagen:', res.status, await res.text());
          }
        } catch (e) {
          console.warn('[RenameSession] Backend PATCH Fehler:', e);
        }
      }
    }
    setShowRenameSessionDialog(false);
    setRenameSessionName('');
  }, [selectedSession, sessions, limitAndSortSessions]);

  // Lade Debug-Logs aus localStorage beim Start und aktualisiere regelmäßig
  useEffect(() => {
    const loadLogs = () => {
      try {
        const savedLogs = JSON.parse(localStorage.getItem('stoppuhr_debug_logs') || '[]');
        setDebugLogs(savedLogs.slice(-50)); // Zeige nur die letzten 50
      } catch (error) {
        console.warn('[Debug] Fehler beim Laden der Logs:', error);
      }
    };
    
    loadLogs();
    // Aktualisiere alle 2 Sekunden, wenn Panel sichtbar ist
    const interval = setInterval(() => {
      if (showDebugPanel) {
        loadLogs();
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [showDebugPanel]);

  // Prüfe auf Update-Wiederherstellung VOR dem Login (beim App-Start)
  useEffect(() => {
    const logData = { 
      updateResumeCheckExecutedRef: updateResumeCheckExecutedRef.current,
      updateResumeCheckInProgressRef: updateResumeCheckInProgressRef.current,
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
      hasLocalState: !!localStorage.getItem('stoppuhr_update_resume_state')
    };
    console.log('[useEffect] checkForUpdateResumeBeforeLogin: useEffect called', logData);
    debugLog('App.jsx:1615', 'checkForUpdateResumeBeforeLogin: useEffect called', logData, 'D');
    
    // Prüfe, ob "/clean" in der URL ist - wenn ja, überspringe Update-Resume-Check
    const isCleanMode = typeof window !== 'undefined' && window.location.pathname.includes('/clean');
    // Prüfe, ob "/debugon" in der URL ist - wenn ja, aktiviere Debug-Panel
    const isDebugOnMode = typeof window !== 'undefined' && window.location.pathname.includes('/debugon');
    if (isDebugOnMode) {
      setShowDebugPanel(true);
      // Entferne "/debugon" aus der URL, damit beim nächsten Refresh der normale Modus aktiv ist
      if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
        const newUrl = window.location.pathname.replace('/debugon', '') || '/';
        window.history.replaceState({}, '', newUrl);
        console.log('[Debug] Debug-Panel aktiviert via /debugon, URL bereinigt');
      }
    }
    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1523', 'checkForUpdateResumeBeforeLogin: Checking clean mode', {isCleanMode,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A',updateResumeCheckExecuted:updateResumeCheckExecutedRef.current}, 'E');
    if (isCleanMode) {
      console.log('[Update-Resume] Clean-Modus aktiviert: Überspringe Update-Resume-Check');
      // Setze isCheckingUpdateResume auf false, damit Sessionliste/Login-Screen angezeigt wird
      setIsCheckingUpdateResume(false);
      // WICHTIG: Setze updateResumeCheckExecutedRef NICHT auf true, damit beim nächsten Refresh
      // (ohne /clean) der Check wieder ausgeführt wird und der State wiederhergestellt werden kann
      // updateResumeCheckExecutedRef.current = true; // ENTFERNT - damit State beim nächsten Refresh wiederhergestellt wird
      updateResumeCheckInProgressRef.current = false; // Setze auf false, damit kein Check läuft
      
      // Entferne "/clean" aus der URL, damit beim nächsten Refresh der normale Modus aktiv ist
      // und der State-Save wieder funktioniert
      if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
        const newUrl = window.location.pathname.replace('/clean', '') || '/';
        window.history.replaceState({}, '', newUrl);
        console.log('[Update-Resume] Clean-Modus: URL bereinigt, normaler Modus für zukünftige Refreshes aktiviert');        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1535', 'checkForUpdateResumeBeforeLogin: URL cleaned', {oldPathname:window.location.pathname,newUrl,pathnameAfter:window.location.pathname}, 'E');      }
      
      return;
    }
    
    // WICHTIG: Prüfe zuerst, ob bereits eingeloggt (VOR dem updateResumeCheckExecutedRef Check)
    // Prüfe auch localStorage, da isLoggedIn möglicherweise noch nicht aktualisiert wurde
    // WICHTIG: savedEmail und savedBackend reichen aus (auch ohne Token, z.B. im Demo-Modus oder nach Browser-Update)
    const savedToken = localStorage.getItem('userToken');
    const savedEmail = localStorage.getItem(STORAGE_KEY_EMAIL);
    const savedBackend = localStorage.getItem(STORAGE_KEY_BACKEND);
    // Wenn Email und Backend vorhanden sind, gilt der Benutzer als eingeloggt (auch ohne Token)
    // WICHTIG: Verwende !! um sicherzustellen, dass es ein Boolean ist (nicht der String-Wert von savedBackend)
    const isActuallyLoggedIn = isLoggedIn || !!(savedEmail && savedBackend);    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1448', 'checkForUpdateResumeBeforeLogin: Checking login status (early check)', {isLoggedIn,savedToken:!!savedToken,savedEmail:!!savedEmail,savedBackend:!!savedBackend,isActuallyLoggedIn}, 'A');    // WICHTIG: Setze Dialog-Ref SOFORT, BEVOR setIsLoggedIn(true) aufgerufen wird
    // Dies verhindert, dass der useEffect für checkForUpdateResume den Dialog öffnet
    // Prüfe zuerst, ob bereits ein State vorhanden ist (schnell, synchron)
    try {
      const localData = localStorage.getItem('stoppuhr_update_resume_state');
      if (localData) {
        const savedState = JSON.parse(localData);
        if (savedState && savedState.isRunning) {
          // ⚠️ WICHTIG: Frühe Ref-Setzung - NUR für checkForUpdateResume, NICHT für checkForUpdateResumeBeforeLogin
          // Setze Ref SOFORT und ATOMAR, BEVOR setIsLoggedIn(true) aufgerufen wird
          // Dies verhindert, dass checkForUpdateResume den Dialog öffnet, wenn setIsLoggedIn(true) den useEffect triggert
          // ABER: checkForUpdateResumeBeforeLogin darf diesen Ref ignorieren und den Dialog trotzdem anzeigen (siehe Zeile 2020)
          // ⚠️ NICHT ÄNDERN: Diese frühe Ref-Setzung ist notwendig, um Race Conditions mit checkForUpdateResume zu vermeiden
          updateResumeDialogShownRef.current = true;
          console.log('[Update-Resume] Ref gesetzt VOR setIsLoggedIn (frühe Prüfung), um doppelte Dialog-Öffnung zu verhindern');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1535', 'checkForUpdateResumeBeforeLogin: Ref set EARLY BEFORE setIsLoggedIn', {ref:updateResumeDialogShownRef.current}, 'B');
        }
      }
    } catch (error) {
      // Ignoriere Fehler
    }
    
    // Wenn bereits eingeloggt, führe den Check trotzdem aus (für sofortige Dialog-Anzeige)
    // WICHTIG: Setze updateResumeCheckInProgressRef VOR setIsLoggedIn, damit der useEffect nicht isCheckingUpdateResume auf false setzt
    if (isActuallyLoggedIn) {
      // Setze Flag SOFORT, damit der useEffect weiß, dass ein Check läuft
      updateResumeCheckInProgressRef.current = true;
      // Stelle sicher, dass isLoggedIn auch gesetzt ist, damit der Login-Screen nicht angezeigt wird
      if (!isLoggedIn) {
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1458', 'checkForUpdateResumeBeforeLogin: Setting isLoggedIn to true (early)', {isLoggedIn,refAlreadySet:updateResumeDialogShownRef.current}, 'A');
        setIsLoggedIn(true);
      }
      // Führe den Check trotzdem aus, auch wenn bereits eingeloggt (für sofortige Dialog-Anzeige)
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1454', 'checkForUpdateResumeBeforeLogin: Already logged in, but continuing check for immediate dialog', {isLoggedIn,savedToken:!!savedToken,savedEmail:!!savedEmail,savedBackend:!!savedBackend,isActuallyLoggedIn}, 'A');
    }
    
    // Verhindere mehrfache Ausführung (auch bei React StrictMode)
    // Diese Prüfung muss SYNCHRON sein, bevor die async Funktion startet
    // WICHTIG: Beim Browser Refresh wird updateResumeCheckExecutedRef.current zurückgesetzt (useRef behält Wert nur während der Session)
    // Daher sollte diese Prüfung beim Refresh NICHT blockieren
    // WICHTIG: Prüfe, ob ein Check bereits läuft UND abgeschlossen wurde
    // ABER: Beim Browser Refresh wird updateResumeCheckInProgressRef.current zurückgesetzt (useRef behält Wert nur während der Session)
    // Daher sollte diese Prüfung beim Refresh NICHT blockieren
    // WICHTIG: Wenn der Check bereits läuft UND abgeschlossen wurde (updateResumeCheckExecutedRef.current === true), überspringe
    // ABER: Wenn ein State vorhanden ist, führe den Check trotzdem aus (könnte ein Browser-Refresh sein)
    // Prüfe zuerst, ob ein State vorhanden ist (schnell, synchron)
    const hasLocalState = !!localStorage.getItem('stoppuhr_update_resume_state');
    if (updateResumeCheckInProgressRef.current && updateResumeCheckExecutedRef.current && !hasLocalState) {
      // Check wurde bereits abgeschlossen UND kein State vorhanden, überspringe
      debugLog('App.jsx:1827', 'checkForUpdateResumeBeforeLogin: Check already completed and no state, skipping', { checkExecuted: updateResumeCheckExecutedRef.current, checkInProgress: updateResumeCheckInProgressRef.current, hasLocalState, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
      return;
    }
    // Wenn ein State vorhanden ist, führe den Check trotzdem aus (könnte ein Browser-Refresh sein)
    if (updateResumeCheckInProgressRef.current && updateResumeCheckExecutedRef.current && hasLocalState) {
      debugLog('App.jsx:1832', 'checkForUpdateResumeBeforeLogin: Check already completed but state exists, continuing anyway (might be browser refresh)', { checkExecuted: updateResumeCheckExecutedRef.current, checkInProgress: updateResumeCheckInProgressRef.current, hasLocalState, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
      // Reset flags, damit der Check ausgeführt werden kann
      updateResumeCheckInProgressRef.current = false;
      updateResumeCheckExecutedRef.current = false;
    }
    // Wenn checkInProgress true ist, aber checkExecuted false ist, bedeutet das, dass der Check gestartet wurde, aber noch nicht abgeschlossen wurde
    // Dies kann bei React Strict Mode passieren (doppelter Render) - in diesem Fall sollten wir den Check trotzdem ausführen
    if (updateResumeCheckInProgressRef.current && !updateResumeCheckExecutedRef.current) {
      debugLog('App.jsx:1833', 'checkForUpdateResumeBeforeLogin: Check in progress but not completed, continuing anyway (might be Strict Mode double render)', { checkExecuted: updateResumeCheckExecutedRef.current, checkInProgress: updateResumeCheckInProgressRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
    }
    
    // WICHTIG: Setze updateResumeCheckExecutedRef NICHT hier, sondern erst NACH dem Check im finally-Block
    // Wenn wir es hier setzen, wird der Check beim nächsten Render blockiert, auch wenn er noch nicht abgeschlossen ist
    debugLog('App.jsx:1835', 'checkForUpdateResumeBeforeLogin: Starting check (updateResumeCheckExecutedRef will be set AFTER check completes)', { checkExecuted: updateResumeCheckExecutedRef.current, checkInProgress: updateResumeCheckInProgressRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
    
    // WICHTIG: Setze updateResumeCheckInProgressRef SOFORT (auch wenn nicht eingeloggt)
    // Dies verhindert Race Conditions mit dem useEffect, der isCheckingUpdateResume auf false setzt
    // Setze updateResumeCheckInProgressRef auf true, damit andere Checks wissen, dass dieser Check läuft
    // WICHTIG: Setze es auch, wenn es bereits true ist (für React Strict Mode), aber nur wenn checkExecuted false ist
    if (!updateResumeCheckInProgressRef.current || (updateResumeCheckInProgressRef.current && !updateResumeCheckExecutedRef.current)) {
      updateResumeCheckInProgressRef.current = true;
      debugLog('App.jsx:1845', 'checkForUpdateResumeBeforeLogin: Setting updateResumeCheckInProgressRef to true', { checkInProgress: updateResumeCheckInProgressRef.current, checkExecuted: updateResumeCheckExecutedRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
    }
    
    // Der Dialog-Ref wurde bereits oben gesetzt (BEVOR setIsLoggedIn aufgerufen wurde)
    // Daher ist hier keine weitere Prüfung nötig
    
    // WICHTIG: Setze isCheckingUpdateResume auf true, damit Login/Session-Liste nicht angezeigt werden, bevor der Check abgeschlossen ist
    console.log('[useEffect] checkForUpdateResumeBeforeLogin: Setting isCheckingUpdateResume to true', { updateResumeDialogShownRef: updateResumeDialogShownRef.current, updateResumeCheckInProgress: updateResumeCheckInProgressRef.current });
    setIsCheckingUpdateResume(true);
    
    const checkForUpdateResumeBeforeLogin = async () => {
      // Prüfe nochmal, ob Clean-Modus aktiv ist (zur Sicherheit)
      const isCleanMode = typeof window !== 'undefined' && window.location.pathname.includes('/clean');
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1639', 'checkForUpdateResumeBeforeLogin async: Checking clean mode', {isCleanMode,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'E');
      if (isCleanMode) {
        console.log('[Update-Resume] checkForUpdateResumeBeforeLogin: Clean-Modus noch aktiv, überspringe Check');
        return;
      }
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1479', 'checkForUpdateResumeBeforeLogin: Entry', {isLoggedIn,checkInProgress:updateResumeCheckInProgressRef.current}, 'A');
      // WICHTIG: updateResumeCheckInProgressRef wurde bereits VOR dieser Funktion gesetzt
      // updateResumeCheckExecutedRef wurde auch bereits geprüft und gesetzt
      // Daher können wir hier direkt weitermachen - keine weitere Prüfung nötig
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1455', 'checkForUpdateResumeBeforeLogin: Set flag to true', {}, 'B');
      try {
        // Prüfe auf gespeicherten Zustand (auch ohne Login)
        // Direkt localStorage prüfen (schnell, synchron) - beforeunload speichert dort
        let savedState = null;
        try {
          const localData = localStorage.getItem('stoppuhr_update_resume_state');
          debugLog('App.jsx:1898', 'checkForUpdateResumeBeforeLogin: Checking localStorage', { hasLocalData: !!localData, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'B');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1661', 'checkForUpdateResumeBeforeLogin: Checking localStorage', {hasLocalData:!!localData,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');
          if (localData) {
            try {
              savedState = JSON.parse(localData);
              // Validiere State - loadMeasurementState macht das bereits, aber hier als zusätzliche Sicherheit
              if (!validateMeasurementState(savedState, 'update_resume_state')) {
                savedState = null; // Markiere als ungültig
                await cleanupCorruptedState('update_resume_state');
              }
            } catch (error) {
              console.warn('[Update-Resume] Fehler beim Parsen des States:', error);
              savedState = null;
              await cleanupCorruptedState('update_resume_state');
            }
            agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1663', 'checkForUpdateResumeBeforeLogin: State loaded from localStorage', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,sessionId:savedState?.selectedSession?.id,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');
          }
        } catch (error) {
          console.warn('localStorage Fehler beim direkten Laden:', error);
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1666', 'checkForUpdateResumeBeforeLogin: Error loading from localStorage', {error:error.message,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');
          // Korrupter State erkannt - bereinige ihn automatisch
          await cleanupCorruptedState('update_resume_state');
        }
        
        // Falls nicht in localStorage, versuche IndexedDB (nur wenn online)
        // WICHTIG: Wenn localStorage vorhanden war, wird IndexedDB NICHT versucht (schneller)
        // WICHTIG: Wenn offline, wird IndexedDB NICHT versucht (kann langsam sein)
        if (!savedState && typeof navigator !== 'undefined' && navigator.onLine !== false) {
          debugLog('App.jsx:1929', 'checkForUpdateResumeBeforeLogin: Trying IndexedDB (localStorage empty, online)', { pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'B');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1664', 'checkForUpdateResumeBeforeLogin: Trying IndexedDB', {pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');
          savedState = await loadMeasurementState('update_resume_state');
          debugLog('App.jsx:1933', 'checkForUpdateResumeBeforeLogin: State loaded from IndexedDB', { hasSavedState: !!savedState, isRunning: savedState?.isRunning, sessionId: savedState?.selectedSession?.id, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'B');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1664', 'checkForUpdateResumeBeforeLogin: State loaded from IndexedDB', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,sessionId:savedState?.selectedSession?.id,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');
        } else if (!savedState && typeof navigator !== 'undefined' && navigator.onLine === false) {
          // Offline: IndexedDB überspringen (kann langsam sein)
          debugLog('App.jsx:1959', 'checkForUpdateResumeBeforeLogin: Offline, skipping IndexedDB', { pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'B');
        }
        
        // Prüfe, ob State bereits gelöscht wurde (von einer anderen Prüfung)
        if (updateResumeStateDeletedRef.current && !savedState) {
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1452', 'checkForUpdateResumeBeforeLogin: State already deleted by another check, skipping', {}, 'B');
          updateResumeCheckInProgressRef.current = false;
          setIsCheckingUpdateResume(false); // WICHTIG: Setze auf false, damit Sessionliste angezeigt wird
          return;
        }
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1449', 'checkForUpdateResumeBeforeLogin: State loaded', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,sessionId:savedState?.selectedSession?.id}, 'A');
        // WICHTIG: Wenn State nicht sofort gefunden, warte kurz und prüfe nochmal (IndexedDB könnte langsam sein)
        if (!savedState) {
          await new Promise(resolve => setTimeout(resolve, 500));
          savedState = await loadMeasurementState('update_resume_state');
        }
        
        debugLog('App.jsx:1958', 'checkForUpdateResumeBeforeLogin: Checking if state is running', { hasSavedState: !!savedState, isRunning: savedState?.isRunning, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'A');
        
        if (savedState && savedState.isRunning) {
          // Setze Flag, um weitere Prüfungen zu verhindern (Race Condition Prevention)
          // NICHT löschen, damit die Uhr später wiederhergestellt werden kann
          // Der State wird gelöscht, wenn der Benutzer zustimmt ODER ablehnt
          debugLog('App.jsx:1958', 'checkForUpdateResumeBeforeLogin: Running state found!', { sessionId: savedState.selectedSession?.id, measurementsCount: savedState.measurements?.length }, 'B');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1615', 'checkForUpdateResumeBeforeLogin: Setting flag, NOT deleting state yet', {}, 'B');
          // Prüfe nochmal, ob Flag noch gesetzt ist (könnte von anderer Prüfung zurückgesetzt worden sein)
          if (!updateResumeCheckInProgressRef.current) {
            debugLog('App.jsx:1967', 'checkForUpdateResumeBeforeLogin: Flag was reset by another check, skipping', { checkInProgress: updateResumeCheckInProgressRef.current }, 'B');
            agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1636', 'checkForUpdateResumeBeforeLogin: Flag was reset by another check, skipping', {}, 'B');
            return; // Überspringe, da andere Prüfung bereits läuft
          }
          
          console.log('[Update-Resume] Laufende Messung beim App-Start gefunden');
          debugLog('App.jsx:1980', 'checkForUpdateResumeBeforeLogin: Loading sessions from localStorage', { sessionId: savedState.selectedSession?.id }, 'B');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1643', 'checkForUpdateResumeBeforeLogin: Running measurement found', {sessionId:savedState.selectedSession?.id,measurementsCount:savedState.measurements?.length}, 'B');
          // Lade Sessions aus localStorage
          const savedSessions = localStorage.getItem(STORAGE_KEY_SESSIONS);
          debugLog('App.jsx:1986', 'checkForUpdateResumeBeforeLogin: Checking if sessions exist in localStorage', { hasSavedSessions: !!savedSessions }, 'B');
          if (savedSessions) {
            try {
              const loadedSessions = JSON.parse(savedSessions);
              let limitedSessions = limitAndSortSessions(loadedSessions);
              setSessions(limitedSessions);
              
              // Finde die Session aus dem gespeicherten Zustand
              let session = limitedSessions.find(s => s.id === savedState.selectedSession?.id);
              // Nach Lizenzablauf/Erneuerung kann die Session aus limitAndSortSessions (MAX_SESSIONS) gefallen sein – wieder in Liste aufnehmen
              if (!session && savedState.selectedSession?.id) {
                const withRestored = limitAndSortSessions([savedState.selectedSession, ...loadedSessions]);
                setSessions(withRestored);
                localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(withRestored));
                limitedSessions = withRestored;
                session = withRestored.find(s => s.id === savedState.selectedSession.id);
                debugLog('App.jsx:1994b', 'checkForUpdateResumeBeforeLogin: Session aus State wieder in Liste aufgenommen', { sessionId: savedState.selectedSession?.id, sessionFound: !!session }, 'B');
              }
              debugLog('App.jsx:1994', 'checkForUpdateResumeBeforeLogin: Looking for session', { sessionId: savedState.selectedSession?.id, loadedSessionsCount: limitedSessions.length, sessionFound: !!session }, 'B');
              if (session) {
                // Prüfe nochmal, ob State noch existiert, BEVOR wir den Dialog anzeigen
                const stateBeforeDialog = await loadMeasurementState('update_resume_state');
                if (!stateBeforeDialog) {
                  debugLog('App.jsx:1997', 'checkForUpdateResumeBeforeLogin: State deleted before dialog, skipping', {}, 'B');
                  agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1657', 'checkForUpdateResumeBeforeLogin: State deleted before dialog, skipping', {}, 'B');
                  updateResumeCheckInProgressRef.current = false;
                  setIsCheckingUpdateResume(false);
                  return; // Überspringe, da State bereits von anderer Prüfung gelöscht wurde
                }
                
                // ⚠️⚠️⚠️ KRITISCH: Diese Logik darf NICHT geändert werden! ⚠️⚠️⚠️
                // 
                // PROBLEM: updateResumeDialogShownRef wird früh gesetzt (Zeile 1793), um checkForUpdateResume zu blockieren.
                // ABER: checkForUpdateResumeBeforeLogin muss den Dialog trotzdem anzeigen können!
                //
                // LÖSUNG: Prüfe NUR den State (showUpdateResumeDialog), NICHT den Ref!
                // - Wenn State true ist → Dialog wurde bereits angezeigt → überspringe
                // - Wenn State false ist → Dialog wurde noch nicht angezeigt → zeige Dialog (auch wenn Ref true ist)
                //
                // ⚠️ WARNUNG: Wenn diese Prüfung geändert wird, um auch den Ref zu prüfen, wird der Dialog
                //    nach einem Browser-Refresh nicht mehr angezeigt, weil der Ref früh gesetzt wurde!
                // ⚠️ WARNUNG: Diese Logik wurde am 23.1.2026 gefixt, nachdem der Dialog nach Refresh nicht erschien.
                //
                // Atomarer "Test-and-Set" - nur EINE Funktion kann den Dialog öffnen
                // Diese Prüfung muss ATOMAR sein - keine async Operationen dazwischen
                const wasStateFalse = !showUpdateResumeDialog;
                
                debugLog('App.jsx:2010', 'checkForUpdateResumeBeforeLogin: Checking if dialog already shown', { ref: updateResumeDialogShownRef.current, state: showUpdateResumeDialog, wasStateFalse }, 'B');
                
                // ⚠️ KRITISCH: Überspringe NUR wenn der State bereits true ist (Dialog wurde bereits angezeigt)
                // Der Ref kann früh gesetzt werden (Zeile 1793), aber das sollte uns NICHT blockieren!
                // NICHT prüfen: !updateResumeDialogShownRef.current (würde den Dialog blockieren)
                if (!wasStateFalse) {
                  debugLog('App.jsx:2013', 'checkForUpdateResumeBeforeLogin: Dialog already shown (state is true), skipping', { ref: updateResumeDialogShownRef.current, state: showUpdateResumeDialog }, 'B');
                  console.log('[Update-Resume] checkForUpdateResumeBeforeLogin: Dialog bereits angezeigt (state ist true), überspringe', {
                    ref: updateResumeDialogShownRef.current,
                    state: showUpdateResumeDialog
                  });
                  return;
                }
                
                // Setze Ref SOFORT und ATOMAR - dies verhindert, dass die andere Funktion den Dialog öffnet
                // WICHTIG: Dies muss NACH der Prüfung, aber VOR allen anderen Operationen passieren
                updateResumeDialogShownRef.current = true;
                debugLog('App.jsx:2025', 'checkForUpdateResumeBeforeLogin: Ref SET to true, about to show dialog', { refAfter: updateResumeDialogShownRef.current, stateBefore: showUpdateResumeDialog }, 'A');
                
                agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1678', 'checkForUpdateResumeBeforeLogin: Ref SET to true', {refAfter:updateResumeDialogShownRef.current}, 'A');
                agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1678', 'checkForUpdateResumeBeforeLogin: AFTER Ref-Set, BEFORE setState', {refAfter:updateResumeDialogShownRef.current,stateBefore:showUpdateResumeDialog}, 'A');
                debugLog('App.jsx:2030', 'checkForUpdateResumeBeforeLogin: Showing dialog', { sessionId: session.id, sessionName: session.sessionName }, 'C');
                agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1681', 'checkForUpdateResumeBeforeLogin: Showing dialog', {sessionId:session.id,sessionName:session.sessionName}, 'C');
                // Zeige Dialog für Benutzerbestätigung
                setUpdateResumeDialogData({
                  sessionName: session.sessionName || 'Unbekannt',
                  measurementsCount: savedState.measurements?.length || 0,
                  resumeNumber: savedState.resumeNumber || 0,
                  savedState: savedState,
                  session: session,
                  isBeforeLogin: true
                });
                setShowUpdateResumeDialog(true);
                
                debugLog('App.jsx:2048', 'checkForUpdateResumeBeforeLogin: Dialog state set to true', { refAfterSetState: updateResumeDialogShownRef.current }, 'E');
                agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1693', 'checkForUpdateResumeBeforeLogin: AFTER setState(true)', {refAfterSetState:updateResumeDialogShownRef.current}, 'E');
                // WICHTIG: Setze updateResumeCheckExecutedRef auf true, wenn Dialog angezeigt wurde
                // Dies verhindert, dass der Check beim nächsten Render nochmal ausgeführt wird
                updateResumeCheckExecutedRef.current = true;
                debugLog('App.jsx:2055', 'checkForUpdateResumeBeforeLogin: Dialog shown, setting updateResumeCheckExecutedRef to true', { checkExecuted: updateResumeCheckExecutedRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
                // WICHTIG: isCheckingUpdateResume bleibt true, damit Login/Session-Liste nicht angezeigt werden
                // Warte auf Benutzer-Interaktion - Loading-State bleibt true, bis Dialog geschlossen wird
                return; // Warte auf Benutzer-Interaktion
              } else {
                debugLog('App.jsx:2060', 'checkForUpdateResumeBeforeLogin: Session not found in loaded sessions', { savedStateSessionId: savedState.selectedSession?.id, loadedSessionsCount: limitedSessions.length }, 'A');
                agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1565', 'checkForUpdateResumeBeforeLogin: Session not found in loaded sessions', {savedStateSessionId:savedState.selectedSession?.id,loadedSessionsCount:limitedSessions.length}, 'A');
                // Session nicht gefunden - Check abgeschlossen, erlaube Login-Screen
                updateResumeCheckInProgressRef.current = false;
                setIsCheckingUpdateResume(false);
              }
            } catch (e) {
              console.error('[Update-Resume] Fehler beim Laden der Sessions:', e);
              debugLog('App.jsx:2068', 'checkForUpdateResumeBeforeLogin: Error loading sessions', { error: e.message, stack: e.stack?.substring(0, 200) }, 'A');
              agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1567', 'checkForUpdateResumeBeforeLogin: Error loading sessions', {error:e.message}, 'A');
              // WICHTIG: Setze beide Flags auch bei Fehler
              updateResumeCheckInProgressRef.current = false;
              setIsCheckingUpdateResume(false);
            }
          } else {
            debugLog('App.jsx:2071', 'checkForUpdateResumeBeforeLogin: No saved sessions in localStorage', {}, 'A');
            agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1570', 'checkForUpdateResumeBeforeLogin: No saved sessions in localStorage', {}, 'A');
            // Keine Sessions gefunden - Check abgeschlossen, erlaube Login-Screen
            updateResumeCheckInProgressRef.current = false;
            setIsCheckingUpdateResume(false);
          }
        } else {
          // Keine laufende Messung gefunden - Check abgeschlossen
          debugLog('App.jsx:2079', 'checkForUpdateResumeBeforeLogin: No running measurement found', { hasSavedState: !!savedState, isRunning: savedState?.isRunning, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'A');
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1572', 'checkForUpdateResumeBeforeLogin: No running measurement found, setting isCheckingUpdateResume to false', {hasSavedState:!!savedState,isRunning:savedState?.isRunning}, 'A');
          // WICHTIG: Setze beide Flags, damit Sessionliste angezeigt wird
          updateResumeCheckInProgressRef.current = false;
          setIsCheckingUpdateResume(false);
          // WICHTIG: Setze updateResumeCheckExecutedRef NUR hier, wenn KEIN State gefunden wurde
          // Wenn ein State gefunden wurde und der Dialog angezeigt wurde, setze es NICHT,
          // damit beim nächsten Render (z.B. nach URL-Change) der Check nochmal ausgeführt werden kann
          updateResumeCheckExecutedRef.current = true;
          debugLog('App.jsx:2090', 'checkForUpdateResumeBeforeLogin: No state found, setting updateResumeCheckExecutedRef to true', { checkExecuted: updateResumeCheckExecutedRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
        }
      } catch (error) {
        console.error('[Update-Resume] Fehler bei Update-Wiederherstellung vor Login:', error);
        debugLog('App.jsx:2093', 'checkForUpdateResumeBeforeLogin: Error in try block', { error: error.message, stack: error.stack?.substring(0, 200), pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'A');
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1945', 'checkForUpdateResumeBeforeLogin: Error in try block', {error:error.message,stack:error.stack}, 'A');
        // WICHTIG: Setze beide Flags auch bei Fehler, damit Sessionliste angezeigt wird
        updateResumeCheckInProgressRef.current = false;
        setIsCheckingUpdateResume(false);
        // WICHTIG: Setze updateResumeCheckExecutedRef auch bei Fehler auf true,
        // damit der Check beim nächsten Render nicht nochmal ausgeführt wird (würde wieder fehlschlagen)
        updateResumeCheckExecutedRef.current = true;
        debugLog('App.jsx:2083', 'checkForUpdateResumeBeforeLogin: Error occurred, setting updateResumeCheckExecutedRef to true', { checkExecuted: updateResumeCheckExecutedRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
      } finally {
        // Reset Flag nach Abschluss (zusätzliche Sicherheit)
        debugLog('App.jsx:2085', 'checkForUpdateResumeBeforeLogin: Finally block - Check completed', { checkInProgress: updateResumeCheckInProgressRef.current, isCheckingUpdateResume, checkExecuted: updateResumeCheckExecutedRef.current, pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A' }, 'D');
        updateResumeCheckInProgressRef.current = false;
        // WICHTIG: Setze updateResumeCheckExecutedRef NICHT mehr hier im finally-Block,
        // sondern nur in den spezifischen Pfaden (kein State gefunden, Fehler, etc.)
        // Wenn ein State gefunden wurde und der Dialog angezeigt wurde, bleibt updateResumeCheckExecutedRef false,
        // damit beim nächsten Render (z.B. nach URL-Change) der Check nochmal ausgeführt werden kann
      }
    };
    
    checkForUpdateResumeBeforeLogin();
  }, []); // Nur beim App-Start, nicht abhängig von isLoggedIn

  // Setze isCheckingUpdateResume auf false, wenn isLoggedIn true wird (nach early check)
  // WICHTIG: Dieser useEffect verhindert, dass der Login-Screen angezeigt wird, wenn isLoggedIn true wird
  // ABER: Nur wenn kein Update-Resume-Dialog angezeigt wird UND kein Update-Resume-Check läuft
  // WICHTIG: Prüfe auch, ob ein Check gerade läuft (updateResumeCheckInProgressRef)
  // WICHTIG: Dieser useEffect darf isCheckingUpdateResume NICHT auf true setzen, wenn der Dialog bereits geschlossen wurde
  useEffect(() => {
    // Prüfe, ob ein Check läuft (Ref wird nicht in Dependencies verwendet, daher manuelle Prüfung)
    const checkInProgress = updateResumeCheckInProgressRef.current;
    
    console.log('[useEffect] Checking isCheckingUpdateResume state', { isLoggedIn, isCheckingUpdateResume, showUpdateResumeDialog, checkInProgress, willSetFalse: isLoggedIn && isCheckingUpdateResume && !showUpdateResumeDialog && !checkInProgress });    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1788', 'useEffect: Checking isCheckingUpdateResume state', {isLoggedIn,isCheckingUpdateResume,showUpdateResumeDialog,checkInProgress,willSetFalse:isLoggedIn && isCheckingUpdateResume && !showUpdateResumeDialog && !checkInProgress}, 'A');    // WICHTIG: Nur auf false setzen, wenn Dialog geschlossen ist UND kein Check läuft
    // Verhindere, dass isCheckingUpdateResume auf true bleibt, wenn der Dialog geschlossen wurde
    if (isLoggedIn && isCheckingUpdateResume && !showUpdateResumeDialog && !checkInProgress) {
      console.log('[useEffect] Setting isCheckingUpdateResume to false');      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:1796', 'useEffect: Setting isCheckingUpdateResume to false', {isLoggedIn,isCheckingUpdateResume,showUpdateResumeDialog,checkInProgress}, 'A');      setIsCheckingUpdateResume(false);
    }
  }, [isLoggedIn, isCheckingUpdateResume, showUpdateResumeDialog]);

  // Load sessions from localStorage
  useEffect(() => {
    const savedSessions = localStorage.getItem(STORAGE_KEY_SESSIONS);
    if (savedSessions) {
      try {
        const loadedSessions = JSON.parse(savedSessions);
        const limitedSessions = limitAndSortSessions(loadedSessions);
        setSessions(limitedSessions);
        // Speichere auch die begrenzte Liste zurück
        if (limitedSessions.length < loadedSessions.length) {
          localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
        }
      } catch (e) {
        console.error('Error loading sessions:', e);
      }
    }
    
    const savedEmailFromStorage = localStorage.getItem(STORAGE_KEY_EMAIL);
    const savedBackendFromStorage = localStorage.getItem(STORAGE_KEY_BACKEND);
    if (savedEmailFromStorage) setEmail(savedEmailFromStorage);
    if (savedBackendFromStorage) setBackendUrl(savedBackendFromStorage);
    
    // Lade gespeicherte View-Einstellungen
    const savedCompactView = localStorage.getItem('compactView');
    if (savedCompactView !== null) {
      setCompactView(savedCompactView === 'true');
    }
    
    // Lade und prüfe gespeicherte Lizenz beim Start
    const savedLicenseData = localStorage.getItem('licenseData');
    if (savedLicenseData) {
      try {
        const licenseData = JSON.parse(savedLicenseData);
        validateStoredLicense(licenseData);
      } catch (e) {
        console.error('Error loading license data:', e);
        // Bei Fehler: Lizenz-Daten entfernen
        localStorage.removeItem('licenseData');
        localStorage.removeItem('licenseStatus');
        setLicenseStatus(false);
      }
    } else {
      // Keine gespeicherte Lizenz: Demo-Modus
      setLicenseStatus(false);
    }
  }, []);

  // Antwort von POST /api/license/check (serde camelCase + Fallback)
  const pickLicenseCheckResponse = (data) => ({
    hasLicense: data?.hasLicense ?? data?.has_license,
    licenseKey: data?.licenseKey ?? data?.license_key,
    productCode: data?.productCode ?? data?.product_code,
    licenseType: data?.licenseType ?? data?.license_type,
    expiresAt: data?.expiresAt ?? data?.expires_at,
    signature: data?.signature,
    isActive: data?.isActive ?? data?.is_active,
  });

  /** Gleicher license_key: Ablauf und Signatur vom Server übernehmen (z. B. nach Backend-Verlängerung). */
  const mergeLicenseFromCheckResponse = (local, checkData) => {
    const pick = pickLicenseCheckResponse(checkData);
    if (!pick.hasLicense || !pick.licenseKey || pick.licenseKey !== local.license_key) {
      return local;
    }
    const merged = {
      ...local,
      product_code: pick.productCode || local.product_code,
      license_type: pick.licenseType || local.license_type,
      expires_at: pick.expiresAt != null && pick.expiresAt !== '' ? pick.expiresAt : local.expires_at,
    };
    if (pick.signature) merged.signature = pick.signature;
    return merged;
  };

  // Validiere gespeicherte Lizenz beim Start
  const validateStoredLicense = async (licenseData) => {
    try {
      // Prüfe Produktcode - akzeptiere nur gültige Produktcodes für diese Anwendung
      const validProductCodes = ['ZEITECE', 'ZEITAUF-MOBIL'];
      if (!validProductCodes.includes(licenseData.product_code)) {
        console.warn(`Ungültiger Produktcode: ${licenseData.product_code}. Gültige Produktcodes: ${validProductCodes.join(', ')}`);
        localStorage.removeItem('licenseData');
        localStorage.removeItem('licenseStatus');
        setLicenseStatus(false);
        setLicenseInfo(null);
        return;
      }

      let current = { ...licenseData };

      // Zuerst Backend: neues Ablaufdatum/Signatur laden (Verlängerung nur im Backend), dann lokal speichern
      const savedBackend = localStorage.getItem(STORAGE_KEY_BACKEND);
      const savedEmail = localStorage.getItem(STORAGE_KEY_EMAIL);

      if (savedBackend && savedEmail && current.license_key) {
        try {
          const savedLicenseDataForCheck = localStorage.getItem('licenseData');
          let licenseKeyForCheck = null;
          if (savedLicenseDataForCheck) {
            try {
              const licenseDataForCheck = JSON.parse(savedLicenseDataForCheck);
              licenseKeyForCheck = licenseDataForCheck.license_key || null;
            } catch (e) {
              console.warn('[License-Validate] Fehler beim Laden des Lizenz-Keys:', e);
            }
          }

          const checkController = new AbortController();
          const checkTimeoutId = setTimeout(() => checkController.abort(), 5000);

          let checkResponse;
          try {
            checkResponse = await fetch(`${savedBackend}/api/license/check`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: savedEmail,
                ...(licenseKeyForCheck && { license_key: licenseKeyForCheck }),
              }),
              signal: checkController.signal,
            });
            clearTimeout(checkTimeoutId);
          } catch (fetchError) {
            clearTimeout(checkTimeoutId);
            if (fetchError.name === 'AbortError') {
              console.warn('[License-Validate] Check-Request timeout – lokale Prüfung');
            } else {
              throw fetchError;
            }
            checkResponse = null;
          }

          if (checkResponse && checkResponse.ok) {
            const checkData = await checkResponse.json();
            const pick = pickLicenseCheckResponse(checkData);
            if (pick.hasLicense && pick.licenseKey === current.license_key) {
              const merged = mergeLicenseFromCheckResponse(current, checkData);
              const changed =
                merged.expires_at !== licenseData.expires_at ||
                merged.signature !== licenseData.signature ||
                merged.product_code !== licenseData.product_code ||
                merged.license_type !== licenseData.license_type;
              if (changed) {
                localStorage.setItem('licenseData', JSON.stringify(merged));
                console.info('[License-Validate] Lokale Lizenz aus Backend aktualisiert (Ablauf/Signatur).');
              }
              current = merged;
              setLicenseStatus(true);
              localStorage.setItem('licenseStatus', 'true');
              setLicenseInfo({
                valid: true,
                license_key: merged.license_key,
                product_code: merged.product_code,
                license_type: merged.license_type,
                expires_at: merged.expires_at,
                is_active: true,
                message: 'Lizenz ist aktiv (Backend bestätigt)',
              });
              return;
            }
          }

          // Check ohne Erfolg: Import-Endpoint (u. a. Signatur/Serverabgleich)
          const importController = new AbortController();
          const importTimeoutId = setTimeout(() => importController.abort(), 5000);

          let importResponse;
          try {
            importResponse = await fetch(`${savedBackend}/api/license/import`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                license_key: current.license_key,
                signature: current.signature,
                device_id: getDeviceId(),
                device_name: getDeviceName(),
              }),
              signal: importController.signal,
            });
            clearTimeout(importTimeoutId);
          } catch (fetchError) {
            clearTimeout(importTimeoutId);
            if (fetchError.name === 'AbortError') {
              console.warn('[License-Validate] Import-Request timeout nach 5 Sekunden - Backend nicht erreichbar, verwende lokale Lizenz');
            } else {
              throw fetchError;
            }
            importResponse = null;
          }

          if (importResponse && importResponse.ok) {
            const importData = await importResponse.json();
            if (importData.valid && importData.is_active) {
              // Backend bestätigt gültige und aktive Lizenz
              setLicenseStatus(true);
              localStorage.setItem('licenseStatus', 'true');
              // Aktualisiere Lizenz-Daten mit Backend-Antwort
              const updatedLicenseData = {
                ...current,
                product_code: importData.product_code || current.product_code,
                license_type: importData.license_type || current.license_type,
                expires_at: importData.expires_at || current.expires_at,
              };
              localStorage.setItem('licenseData', JSON.stringify(updatedLicenseData));
              current = updatedLicenseData;
              setLicenseInfo({
                valid: true,
                license_key: updatedLicenseData.license_key,
                product_code: updatedLicenseData.product_code,
                license_type: updatedLicenseData.license_type,
                expires_at: updatedLicenseData.expires_at,
                is_active: true,
                message: 'Lizenz ist aktiv (Backend bestätigt)',
              });
              return;
            } else {
              // Backend sagt, Lizenz ist nicht aktiv
              console.warn('Backend bestätigt: Lizenz ist nicht aktiv');
              localStorage.removeItem('licenseData');
              localStorage.removeItem('licenseStatus');
              setLicenseStatus(false);
              setLicenseInfo({
                valid: importData.valid || false,
                license_key: current.license_key,
                product_code: importData.product_code || current.product_code,
                license_type: importData.license_type || current.license_type,
                expires_at: importData.expires_at || current.expires_at,
                is_active: false,
                message: importData.message || 'Lizenz ist nicht aktiv',
              });
              
              // Zeige Dialog auch wenn Lizenz deaktiviert ist (beim automatischen Laden)
              setLicenseImportResult({
                type: 'warning',
                message: `⚠️ ${importData.message || 'Lizenz ist nicht aktiv'}`,
                details: {
                  license_key: current.license_key,
                  product_code: importData.product_code || current.product_code,
                  license_type: importData.license_type || current.license_type,
                  expires_at: importData.expires_at || current.expires_at,
                  is_active: false
                }
              });
              setShowLicenseImportDialog(true);
              return;
            }
          }
        } catch (backendError) {
          console.warn('Backend-Verbindung fehlgeschlagen, verwende lokale Lizenz:', backendError);
          // Bei Backend-Fehler: Verwende lokale Lizenz (Offline-Modus)
        }
      }

      // Lokale Ablaufprüfung (nach Backend-Sync enthält current ggf. neues expires_at)
      let isExpired = false;
      if (current.expires_at && current.expires_at !== 'null' && current.expires_at !== null) {
        const expiresDate = new Date(current.expires_at);
        if (!isNaN(expiresDate.getTime()) && expiresDate < new Date()) {
          isExpired = true;
        }
      }

      if (isExpired) {
        console.warn('Gespeicherte Lizenz ist abgelaufen');
        localStorage.removeItem('licenseData');
        localStorage.removeItem('licenseStatus');
        setLicenseStatus(false);
        setLicenseInfo({
          valid: false,
          license_key: current.license_key,
          product_code: current.product_code,
          license_type: current.license_type,
          expires_at: current.expires_at,
          is_active: false,
          message: 'Lizenz ist abgelaufen',
        });
        return;
      }

      // Kein Backend oder Backend-Fehler: Verwende lokale Lizenz
      setLicenseStatus(true);
      localStorage.setItem('licenseStatus', 'true');
      setLicenseInfo({
        valid: true,
        license_key: current.license_key,
        product_code: current.product_code,
        license_type: current.license_type,
        expires_at: current.expires_at,
        is_active: !isExpired,
        message: 'Lizenz ist gültig (lokal verifiziert, Offline-Modus)',
      });
    } catch (error) {
      console.error('Error validating stored license:', error);
      localStorage.removeItem('licenseData');
      localStorage.removeItem('licenseStatus');
      setLicenseStatus(false);
      setLicenseInfo(null);
    }
  };

  // Save sessions to localStorage (mit Begrenzung und Sortierung)
  useEffect(() => {
    if (sessions.length > 0) {
      const limitedSessions = limitAndSortSessions(sessions);
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
      // Wenn Sessions begrenzt oder sortiert wurden, aktualisiere den State
      if (limitedSessions.length !== sessions.length || 
          JSON.stringify(limitedSessions.map(s => s.id)) !== JSON.stringify(sessions.map(s => s.id))) {
        setSessions(limitedSessions);
      }
    }
  }, [sessions, limitAndSortSessions]);

  // Aktualisiere Sessions basierend auf licenseStatus
  useEffect(() => {
    if (sessions.length > 0) {
      let needsUpdate = false;
      let updatedSessions = sessions.map(session => {
        // Wenn keine Lizenz vorhanden ist, setze alle Sessions auf Demo
        // WICHTIG: Nur wenn wirklich keine Lizenz vorhanden ist, nicht wenn Backend offline ist
        if (licenseStatus === false) {
          // Prüfe, ob eine lokale Lizenz vorhanden ist
          const savedLicenseData = localStorage.getItem('licenseData');
          if (savedLicenseData) {
            try {
              const licenseData = JSON.parse(savedLicenseData);
              // Lokale Lizenz vorhanden - prüfe, ob sie gültig ist
              if (licenseData.license_key && licenseData.signature) {
                // Lokale Lizenz vorhanden, aber Backend offline - nicht auf Demo setzen
                // Die lokale Lizenz wird beim nächsten Online-Check validiert
                return session;
              }
            } catch (e) {
              // Fehler beim Parsen - behandle als keine Lizenz
            }
          }
          
          // Keine lokale Lizenz vorhanden - setze auf Demo
          if (!session.isDemo) {
            needsUpdate = true;
            return {
              ...session,
              isDemo: true,
              sessionName: session.sessionName?.includes('Demo-Erfassung')
                ? session.sessionName
                : `Demo-Erfassung (${session.deviceName || deviceName})`
            };
          }
        } 
        // Wenn Lizenz vorhanden ist, setze Sessions auf isDemo: false (außer echte Demo-Sessions)
        else if (licenseStatus === true) {
          // Echte Demo-Sessions (Email und Backend leer) bleiben Demo
          const isRealDemo = !session.email?.trim() && !backendUrl?.trim();
          if (!isRealDemo && session.isDemo) {
            needsUpdate = true;
            // Entferne "Demo-Erfassung" aus dem Session-Namen
            const newSessionName = session.sessionName?.replace(/^Demo-Erfassung\s*/, '') || 
              `Messung ${new Date(session.createdAt).toLocaleDateString('de-DE')} (${session.deviceName || deviceName})`;
            return {
              ...session,
              isDemo: false,
              sessionName: newSessionName
            };
          }
        }
        return session;
      });

      if (needsUpdate) {
        setSessions(updatedSessions);
        localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
        
        if (selectedSession) {
          const updatedSession = updatedSessions.find(s => s.id === selectedSession.id);
          if (updatedSession) setSelectedSession(updatedSession);
        }
      }
    }
  }, [licenseStatus, sessions, selectedSession, deviceName, backendUrl]);

  // Save email and backend to localStorage
  useEffect(() => {
    if (email) localStorage.setItem(STORAGE_KEY_EMAIL, email);
    if (backendUrl) localStorage.setItem(STORAGE_KEY_BACKEND, backendUrl);
  }, [email, backendUrl]);

  // Lade Fotos für alle Messungen, wenn Messungen oder Session geändert werden
  useEffect(() => {
    if (measurements.length > 0 && selectedSession?.id) {
      const loadAllPhotos = async () => {
        const photosMap = {};
        const currentSessionId = selectedSession.id;        agentLog('9f295211-a98e-4941-b16d-f3672384af61', 'App.jsx:1630', 'loadAllPhotos: Starting', {measurementsCount:measurements.length,currentSessionId,selectedSessionId:selectedSession?.id}, 'A');        for (const measurement of measurements) {
          if (measurement.number) {
            try {
              const photos = await loadPhotos(measurement.number, currentSessionId);
              photosMap[measurement.number] = photos.map(photo => ({
                id: photo.id,
                fileData: photo.fileData,
                fileName: photo.fileName
              }));
            } catch (error) {
              console.error(`Fehler beim Laden der Fotos für Messung ${measurement.number}:`, error);
              photosMap[measurement.number] = [];
            }
          }
        }
        setMeasurementPhotos(photosMap);
      };
      loadAllPhotos();
    } else {
      setMeasurementPhotos({});
    }
  }, [measurements, selectedSession?.id]);

  // Schließe alle Dropdowns beim Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Prüfe ob Klick außerhalb der Dropdowns war
      if (!event.target.closest('[data-dropdown-container]') && 
          !event.target.closest('[data-dropdown-item]')) {
        setOpenDropdowns({});
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Aktualisiere selectedKuerzel im Bearbeitungsdialog, wenn editActivity geändert wird
  useEffect(() => {
    if (editingMeasurement && editActivity && ablaufDbData && ablaufDbData.length > 0) {
      const matchingKuerzel = ablaufDbData.find(k => 
        k.beschreibung === editActivity || 
        k.fullText === editActivity ||
        k.kuerzel === editActivity
      );
      if (matchingKuerzel) {
        setSelectedKuerzel(matchingKuerzel);
        // Initialisiere auch leere Werte für alle Einflussgrößen des Kürzels, falls noch nicht vorhanden
        setEditEinflussgroessenValues(prev => {
          const updated = { ...prev };
          if (matchingKuerzel.einflussgroessen && Array.isArray(matchingKuerzel.einflussgroessen)) {
            matchingKuerzel.einflussgroessen.forEach(einfluss => {
              if (einfluss && einfluss.einflussname) {
                const name = einfluss.einflussname.trim();
                if (name && !updated[name]) {
                  updated[name] = '';
                }
              }
            });
          }
          return updated;
        });
      } else {
        // Kein passendes Kürzel gefunden, setze selectedKuerzel auf null
        setSelectedKuerzel(null);
      }
    } else if (editingMeasurement && !editActivity) {
      // Keine Tätigkeit eingegeben, setze selectedKuerzel auf null
      setSelectedKuerzel(null);
    }
  }, [editingMeasurement, editActivity, ablaufDbData]);

  // Backend-Verfügbarkeit prüfen (periodisch)
  useEffect(() => {
    if (!backendUrl || !isLoggedIn) {
      setBackendAvailable(null);
      return;
    }

    // WICHTIG: Wenn offline, Backend-Check überspringen
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.log('[Backend-Check] Gerät ist offline, setze Backend auf nicht verfügbar');
      setBackendAvailable(false);
      return;
    }

    // Wenn Backend als offline gilt: alle 10 s prüfen (schnellere Erholung). Sonst 30 s.
    const intervalMs = (backendAvailable === false) ? 10000 : 30000;

    // Verwende eine lokale Variable, um parallele Checks zu verhindern
    let isChecking = false;

    const checkBackendAvailability = async () => {
      if (isChecking) return; // Verhindere parallele Checks
      
      isChecking = true;
      setIsCheckingBackend(true);
      try {
        const normalizedUrl = normalizeBackendUrl(backendUrl);
        const healthCheck = await checkBackendHealth(normalizedUrl);
        
        // Backend ist verfügbar, wenn Health-Check erfolgreich ist oder zumindest nicht definitiv fehlgeschlagen
        const available = healthCheck.healthy === true || healthCheck.healthy === null;
        setBackendAvailable(available);
        
        if (available) {
          console.log('[Backend-Check] Backend ist verfügbar');
        } else {
          console.warn('[Backend-Check] Backend ist nicht verfügbar:', healthCheck.error);
        }
      } catch (error) {
        console.error('[Backend-Check] Fehler beim Prüfen der Backend-Verfügbarkeit:', error);
        setBackendAvailable(false);
      } finally {
        isChecking = false;
        setIsCheckingBackend(false);
      }
    };

    // Prüfe sofort, wenn Online-Status sich ändert
    const handleOnline = async () => {
      console.log('[Backend-Check] Gerät ist jetzt online');
      checkBackendAvailability();
    };

    const handleOffline = () => {
      console.warn('[Backend-Check] Gerät ist jetzt offline');
      setBackendAvailable(false);
    };

    // Event-Listener für Online/Offline-Status
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Sofort prüfen beim Start
    checkBackendAvailability();

    const interval = setInterval(checkBackendAvailability, intervalMs);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl, isLoggedIn, backendAvailable]);

  // Feedback-Sync: unabhängig von isLoggedIn, nur backendUrl nötig (Offline-Feedbacks sollen immer synchronisiert werden)
  useEffect(() => {
    if (!backendUrl) return;

    const handleOnlineFeedbackSync = () => {
      syncPendingFeedback(backendUrl).then((r) => {
        if (r.synced > 0) console.log('[Feedback-Sync] Nach online:', r.synced, 'synchronisiert');
        loadPendingFeedbackCount();
      }).catch((e) => console.error('[Feedback-Sync] Fehler nach online:', e));
    };
    const handleVisibilityFeedbackSync = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible' && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
        syncPendingFeedback(backendUrl).then((r) => {
          if (r.synced > 0) console.log('[Feedback-Sync] Nach Tab-Fokus:', r.synced, 'synchronisiert');
          loadPendingFeedbackCount();
        }).catch((e) => console.error('[Feedback-Sync] Fehler bei visibilitychange:', e));
      }
    };

    window.addEventListener('online', handleOnlineFeedbackSync);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', handleVisibilityFeedbackSync);

    const initialSyncTimer = setTimeout(() => {
      if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        syncPendingFeedback(backendUrl).then((r) => {
          if (r.synced > 0) console.log('[Feedback-Sync] Erster Lauf:', r.synced, 'synchronisiert');
          loadPendingFeedbackCount();
        }).catch((e) => console.error('[Feedback-Sync] Fehler beim ersten Lauf:', e));
      }
    }, 2000);

    let isSyncing = false;
    const syncInterval = setInterval(() => {
      if (!isSyncing && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
        isSyncing = true;
        syncPendingFeedback(backendUrl).finally(() => { isSyncing = false; loadPendingFeedbackCount(); });
      }
    }, 60000);

    // Einmal Sync, wenn Backend wieder verfügbar wird
    if (backendAvailable === true) {
      const backendSyncTimer = setTimeout(() => {
        syncPendingFeedback(backendUrl).then((r) => {
          if (r.synced > 0) console.log('[Feedback-Sync] Backend wieder da:', r.synced, 'synchronisiert');
          loadPendingFeedbackCount();
        }).catch((e) => console.error('[Feedback-Sync] Fehler nach Backend-Wiederherstellung:', e));
      }, 500);
      return () => {
        clearTimeout(initialSyncTimer);
        clearTimeout(backendSyncTimer);
        clearInterval(syncInterval);
        window.removeEventListener('online', handleOnlineFeedbackSync);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', handleVisibilityFeedbackSync);
      };
    }

    return () => {
      clearTimeout(initialSyncTimer);
      clearInterval(syncInterval);
      window.removeEventListener('online', handleOnlineFeedbackSync);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', handleVisibilityFeedbackSync);
    };
  }, [backendUrl, backendAvailable, loadPendingFeedbackCount]);

  // Tracke Zeitpunkt, wann Backend offline ging
  useEffect(() => {
    if (backendAvailable === false) {
      // Backend ist jetzt offline
      if (backendOfflineSince === null) {
        // Erste Offline-Erkennung: Speichere Zeitstempel
        setBackendOfflineSince(Date.now());
      }
    } else if (backendAvailable === true) {
      // Backend ist wieder online: Zeitstempel zurücksetzen
      setBackendOfflineSince(null);
    }
    // Bei backendAvailable === null (unbekannt) nichts ändern
  }, [backendAvailable, backendOfflineSince]);

  // Setze Footer-Merker zurück, wenn Backend wieder online geht
  useEffect(() => {
    if (backendAvailable === true && footerManuallyHidden) {
      // Backend ist wieder online und Footer wurde manuell versteckt: Merker zurücksetzen
      console.log('[Footer] Backend wieder online, setze footerManuallyHidden zurück');
      setFooterManuallyHidden(false);
      try {
        localStorage.removeItem(STORAGE_KEY_FOOTER_MANUALLY_HIDDEN);
      } catch (e) {
        console.error('[Footer] Fehler beim Entfernen aus localStorage:', e);
      }
    }
  }, [backendAvailable, footerManuallyHidden]);

  // Handler für Footer-Touch (in allen Modi, wenn Backend nicht verfügbar)
  const handleFooterTouch = useCallback((e) => {
    // Erlaube Wegklicken in allen Modi, wenn Backend nicht verfügbar
    console.log('[Footer] handleFooterTouch aufgerufen, backendAvailable:', backendAvailable);
    if (backendAvailable !== true) {
      e.preventDefault();
      e.stopPropagation();
      console.log('[Footer] Verstecke Footer');
      setFooterManuallyHidden(true);
      // Speichere in localStorage, damit der State über Remounts hinweg erhalten bleibt
      try {
        localStorage.setItem(STORAGE_KEY_FOOTER_MANUALLY_HIDDEN, 'true');
      } catch (e) {
        console.error('[Footer] Fehler beim Speichern in localStorage:', e);
      }
      setShowFooter(false);
    } else {
      console.log('[Footer] Footer nicht wegklickbar (Backend verfügbar)');
    }
  }, [backendAvailable]);

  // Aktualisiere Nachkommastellen-State, wenn vorderseiteData sich ändert
  useEffect(() => {
    if (vorderseiteData) {
      if (vorderseiteData.decimal_places_vorderseite !== undefined) {
        setDecimalPlacesVorderseite(vorderseiteData.decimal_places_vorderseite);
      }
      if (vorderseiteData.decimal_places_sollzeiten !== undefined) {
        setDecimalPlacesSollzeiten(vorderseiteData.decimal_places_sollzeiten);
      }
    }
  }, [vorderseiteData]);

  // Speichere selectedKuerzel und einflussgroessenValues in localStorage (pro Session)
  useEffect(() => {
    if (selectedSession?.id) {
      const storageKey = `stoppuhr_session_${selectedSession.id}_kuerzel_state`;
      const state = {
        selectedKuerzel: selectedKuerzel || null,
        einflussgroessenValues: einflussgroessenValues || {}
      };
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        console.error('[Portrait] Fehler beim Speichern der Kürzel/EA-Werte:', error);
      }
    }
  }, [selectedSession?.id, selectedKuerzel, einflussgroessenValues]);

  // Speichere activity, note und timeType in localStorage (pro Session)
  useEffect(() => {
    if (selectedSession?.id) {
      const storageKey = `stoppuhr_session_${selectedSession.id}_activity_note`;
      const state = {
        activity: activity || '',
        note: note || '',
        timeType: timeType || 'thb'
      };
      try {
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch (error) {
        console.error('[App] Fehler beim Speichern von activity/note/timeType:', error);
      }
    }
  }, [selectedSession?.id, activity, note, timeType]);

  // Speichere focusedInput in localStorage (pro Session)
  useEffect(() => {
    if (selectedSession?.id) {
      const storageKey = `stoppuhr_session_${selectedSession.id}_focused_input`;
      try {
        const focusedInputState = Object.keys(focusedInput).length > 0 ? focusedInput : null;
        if (focusedInputState) {
          localStorage.setItem(storageKey, JSON.stringify(focusedInputState));
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (error) {
        console.error('[App] Fehler beim Speichern von focusedInput:', error);
      }
    }
  }, [selectedSession?.id, focusedInput]);

  // Footer dynamisch ein-/ausblenden basierend auf Backend-Status (Portrait und Landscape)
  useEffect(() => {
    // HINWEIS: footerManuallyHidden wird automatisch zurückgesetzt, wenn Backend wieder online geht
    // (siehe useEffect oben), damit der Footer bei erneuter Offline wieder erscheint
    
    // In Kompakt-Ansicht anwenden (Portrait und Landscape)
    if (backendUrl) {
      if (backendAvailable === true) {
        // Backend verbunden: Footer ausblenden (nur wenn nicht manuell ausgeblendet)
        if (!footerManuallyHidden) {
          const timer = setTimeout(() => {
            setShowFooter(false);
          }, 2000); // 2 Sekunden Verzögerung, damit der Benutzer die Änderung sieht
          return () => clearTimeout(timer);
        } else {
          // Footer wurde manuell versteckt, stelle sicher, dass er versteckt bleibt
          setShowFooter(false);
        }
      } else {
        // Backend nicht verbunden oder Status unbekannt: Footer einblenden (außer wenn manuell ausgeblendet)
        if (!footerManuallyHidden) {
          setShowFooter(true);
        } else {
          // Footer wurde manuell versteckt, stelle sicher, dass er versteckt bleibt
          setShowFooter(false);
        }
      }
    } else {
      // Keine Backend-URL: Footer immer anzeigen (außer wenn manuell ausgeblendet)
      if (!footerManuallyHidden) {
        setShowFooter(true);
      } else {
        // Footer wurde manuell versteckt, stelle sicher, dass er versteckt bleibt
        setShowFooter(false);
      }
    }
  }, [backendAvailable, backendUrl, footerManuallyHidden]);

  // Auto-save current session
  useEffect(() => {
    if (selectedSession && measurements.length > 0) {
      const updatedSessions = sessions.map(s => 
        s.id === selectedSession.id
          ? { ...s, measurements, vorderseiteData: vorderseiteData || null, lastUpdated: new Date().toISOString() }
          : s
      );
      // Liste neu sortieren und begrenzen
      const limitedSessions = limitAndSortSessions(updatedSessions);
      setSessions(limitedSessions);
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    }
  }, [measurements, selectedSession, limitAndSortSessions, vorderseiteData]);

  // Speichere laufenden Zustand für Update-Wiederherstellung
  const saveRunningStateForUpdate = useCallback(async () => {
    if (!selectedSession || !isRunning) {
      return;
    }

    // Sammle Scroll-Positionen (optional)
    const scrollPositions = {};
    if (typeof window !== 'undefined') {
      // Scroll-Position für Messungen-Liste
      const measurementsList = document.querySelector('.measurements-list');
      if (measurementsList) {
        scrollPositions.measurementsList = {
          scrollTop: measurementsList.scrollTop,
          scrollLeft: measurementsList.scrollLeft
        };
      }
      
      // Scroll-Position für Tabs-Container
      if (tabsContainerRef.current) {
        scrollPositions.tabsContainer = {
          scrollTop: tabsContainerRef.current.scrollTop,
          scrollLeft: tabsContainerRef.current.scrollLeft
        };
      }
      
      // Scroll-Position für Tätigkeits-Liste (Ablauf-Kürzel-Liste)
      const ablaufKuerzelList = document.querySelector('.ablauf-kuerzel-list');
      if (ablaufKuerzelList) {
        scrollPositions.ablaufKuerzelList = {
          scrollTop: ablaufKuerzelList.scrollTop,
          scrollLeft: ablaufKuerzelList.scrollLeft
        };
      }
      
      // Scroll-Position für Activities-Liste (falls vorhanden, für Kompatibilität)
      const activitiesList = document.querySelector('.activities-list');
      if (activitiesList) {
        scrollPositions.activitiesList = {
          scrollTop: activitiesList.scrollTop,
          scrollLeft: activitiesList.scrollLeft
        };
      }
    }

    // Sammle Fokus-Informationen
    const focusInfo = {};
    if (typeof document !== 'undefined' && document.activeElement) {
      const activeElement = document.activeElement;
      focusInfo.elementId = activeElement.id || null;
      focusInfo.elementName = activeElement.name || null;
      focusInfo.elementType = activeElement.tagName.toLowerCase();
      focusInfo.elementClass = activeElement.className || null;
      
      // Spezielle Behandlung für Einflussgrößen-Inputs
      if (focusedEinflussInputRef.current && activeElement === focusedEinflussInputRef.current) {
        focusInfo.focusedEinfluss = focusedEinflussRef.current;
      }
      
      // Spezielle Behandlung für Ziffernblock-Inputs (Einflussgrößen-Ausprägungen)
      if (ziffernblockInputElementRef.current && activeElement === ziffernblockInputElementRef.current) {
        // Finde den einflussname aus dem focusedInput State
        const focusedEinflussname = Object.keys(focusedInput).find(key => focusedInput[key] === true);
        if (focusedEinflussname) {
          focusInfo.focusedZiffernblockEinfluss = focusedEinflussname;
        }
      }
    }
    
    // Speichere focusedInput State für Ziffernblock-Anzeige
    const focusedInputState = Object.keys(focusedInput).length > 0 ? focusedInput : null;

    const state = {
      version: FRONTEND_VERSION,
      timestamp: Date.now(),
      isRunning,
      startTime, // WICHTIG: Original startTime beibehalten
      currentTime,
      measurements,
      resumeNumber, // WICHTIG: Aktuelle WA-Nr beibehalten (wird NICHT erhöht)
      selectedSession,
      vorderseiteData,
      lastMeasurementTime,
      // Neue Felder für erweiterte Wiederherstellung
      selectedKuerzel: selectedKuerzel || null,
      einflussgroessenValues: einflussgroessenValues || {},
      merke1Values: merke1Values || null,
      activity: activity || '',
      note: note || '',
      timeType: timeType || 'thb',
      scrollPositions: Object.keys(scrollPositions).length > 0 ? scrollPositions : null,
      focusInfo: Object.keys(focusInfo).length > 0 ? focusInfo : null,
      focusedInput: focusedInputState, // State für Ziffernblock-Anzeige
    };

    try {
      await saveMeasurementState('update_resume_state', state);
    } catch (error) {
      console.error('[Update-Resume] Fehler beim Speichern des Zustands:', error);
    }
  }, [selectedSession, isRunning, startTime, currentTime, measurements, resumeNumber, vorderseiteData, lastMeasurementTime, selectedKuerzel, einflussgroessenValues, merke1Values, activity, note, timeType, focusedInput]);

  // Auto-save laufenden Zustand für Update-Wiederherstellung
  // 0. Sofort beim Start der Uhr (auch ohne Messungen)
  useEffect(() => {
    if (isRunning && selectedSession) {
      // Speichere sofort beim Start, damit der State auch bei frühem Refresh verfügbar ist
      saveRunningStateForUpdate();
    }
  }, [isRunning, selectedSession, saveRunningStateForUpdate]);

  // 1. Bei jeder Messpunkt-Änderung (sofort)
  useEffect(() => {
    if (isRunning && selectedSession && measurements.length > 0) {
      // Speichere sofort bei jeder Änderung
      saveRunningStateForUpdate();
    }
  }, [measurements, isRunning, selectedSession, saveRunningStateForUpdate]);

  // 2. Periodisches Speichern als Backup (alle 30 Sekunden)
  useEffect(() => {
    if (isRunning && selectedSession) {
      const interval = setInterval(() => {
        saveRunningStateForUpdate();
      }, 30000); // 30 Sekunden

      return () => clearInterval(interval);
    }
  }, [isRunning, selectedSession, saveRunningStateForUpdate]);

  // 3. Speichere sofort beim Browser-Refresh/Close (beforeunload)
  useEffect(() => {
    if (isRunning && selectedSession) {
      const handleBeforeUnload = () => {
        // Synchrones Speichern in localStorage (schneller als async)
        // WICHTIG: Nur die wichtigsten Daten, da beforeunload synchron sein muss
        try {
          const state = {
            version: FRONTEND_VERSION,
            timestamp: Date.now(),
            isRunning,
            startTime,
            currentTime,
            measurements,
            resumeNumber,
            selectedSession,
            vorderseiteData,
            lastMeasurementTime,
            selectedKuerzel: selectedKuerzel || null,
            einflussgroessenValues: einflussgroessenValues || {},
            merke1Values: merke1Values || null,
            activity: activity || '',
            note: note || '',
            timeType: timeType || 'thb',
          };
          // Direkt localStorage (synchron, funktioniert sofort)
          localStorage.setItem('stoppuhr_update_resume_state', JSON.stringify(state));
          console.log('[Update-Resume] beforeunload: State saved to localStorage', {
            sessionId: selectedSession?.id,
            isRunning,
            measurementsCount: measurements.length,
            hasSelectedSession: !!selectedSession,
            pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A'
          });          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2760', 'beforeunload: State saved to localStorage', {sessionId:selectedSession?.id,isRunning,measurementsCount:measurements.length,hasSelectedSession:!!selectedSession,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'A');        } catch (error) {
          console.error('[Update-Resume] Fehler beim synchronen Speichern:', error);          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2698', 'beforeunload: Error saving state', {error:error.message,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'A');        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isRunning, selectedSession, startTime, currentTime, measurements, resumeNumber, vorderseiteData, lastMeasurementTime, selectedKuerzel, einflussgroessenValues, merke1Values, activity, note, timeType]);

  // Handbuch im Hintergrund aktualisieren (bei Resume/visibilitychange), damit Offline-Cache aktuell bleibt
  const requestHandbookUpdate = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.onLine && navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'UPDATE_HANDBOOK' });
    }
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && typeof navigator !== 'undefined' && navigator.onLine) {
        requestHandbookUpdate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [requestHandbookUpdate]);

  // Service Worker Update-Handling
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerUpdate = async () => {
        const registration = await navigator.serviceWorker.ready;
        
        // Prüfe auf Updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Neue Version verfügbar
                console.log('[Service Worker] Neue Version verfügbar');
                
                // Wenn eine Messung läuft, speichere State für Update-Resume
                if (isRunning && selectedSession) {
                  console.log('[Service Worker] Speichere State für Update-Resume');
                  saveRunningStateForUpdate();
                  
                  // Zeige Badge: "Update verfügbar, wird nach Messung angewendet"
                  // (wird durch VersionUpdateBadge behandelt)
                }
              }
            });
          }
        });

        // Prüfe regelmäßig auf Updates (alle 60 Sekunden)
        const updateInterval = setInterval(() => {
          registration.update();
        }, 60000);

        return () => clearInterval(updateInterval);
      };

      handleServiceWorkerUpdate();
    }
  }, [isRunning, selectedSession, saveRunningStateForUpdate]);

  // Setze Auswertungsmodus auf Standard zurück, wenn eine neue Session erstellt oder ausgewählt wird
  useEffect(() => {
    if (selectedSession) {
      // Prüfe ob es eine neue Session ist (keine Messungen vorhanden)
      if (measurements.length === 0) {
        // Setze Auswertungsmodus auf Standard zurück, falls die Auswertung sichtbar war
        if (showAnalysis) {
          setAnalysisViewMode('timeType');
        }
      }
    }
  }, [selectedSession?.id, showAnalysis]); // Nur wenn sich die Session-ID ändert oder die Auswertung sichtbar ist

  // Speichere in temporäre Datei (IndexedDB mit Backend-Sync)
  const autoSaveToTempFile = useCallback(async () => {
    if (!selectedSession || measurements.length === 0) {
      return;
    }

    // Erstelle Datenstruktur
    const data = {
      version: "20260120",
      deviceName: deviceName,
      sessionName: selectedSession?.sessionName || '',
      resumeNumber: selectedSession?.resumeNumber ?? 0,
      timestamp: new Date().toISOString(),
      vorderseiteData: vorderseiteData || null,
      measurements: measurements.map(m => ({
        number: m.number,
        grade: m.grade,
        progressTime: m.progressTime,
        individualTime: m.individualTime,
        vorgabeTime: m.vorgabeTime || 0, // Vorgabezeit mit hochladen
        activity: m.activity,
        kuerzel: m.kuerzel || null, // Kürzel mit hochladen
        timeType: m.timeType,
        note: m.note || '',
        isNote: m.isNote || false,
        timestamp: m.timestamp,
        resumeNumber: m.resumeNumber ?? selectedSession?.resumeNumber ?? 0,
        excluded: m.excluded || false
      }))
    };

    const checksum = calculateChecksum(data);
    data.checksum = checksum;

    // Bestimme Dateiname basierend auf Toggle
    const fileName = autoSaveFileToggleRef.current ? "_$$EC2E_tmp.$$$" : "_$$ECE_tmp.$$$";
    
    // Speichere in IndexedDB
    await saveTempFile(fileName, JSON.stringify(data));
    
    // Toggle für nächstes Mal
    autoSaveFileToggleRef.current = !autoSaveFileToggleRef.current;
    
    // Lösche die andere Temp-Datei
    const otherFileName = autoSaveFileToggleRef.current ? "_$$ECE_tmp.$$$" : "_$$EC2E_tmp.$$$";
    await deleteTempFile(otherFileName);
  }, [selectedSession, measurements, deviceName, vorderseiteData]);

  // Auto-Save Timer für temporäre Dateien
  useEffect(() => {
    // Lade gespeicherten Timer-Wert (in Minuten)
    const savedTimer = localStorage.getItem('autoSaveTimer');
    if (savedTimer) {
      const timerValue = parseInt(savedTimer, 10);
      if (!isNaN(timerValue)) {
        setAutoSaveTimer(timerValue);
      }
    }

    // Starte Auto-Save Timer wenn aktiviert
    // autoSaveTimer ist in Minuten, umrechnen in Millisekunden
    if (autoSaveTimer > 0 && selectedSession && measurements.length > 0) {
      autoSaveTimerRef.current = setInterval(() => {
        autoSaveToTempFile();
      }, autoSaveTimer * 60 * 1000); // Minuten in Millisekunden umrechnen
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [autoSaveTimer, selectedSession, measurements, autoSaveToTempFile]);

  // Auto-upload im Hintergrund
  useEffect(() => {
    if (licenseStatus && selectedSession && measurements.length > 0 && backendUrl) {
      uploadIntervalRef.current = setInterval(async () => {
        await autoUpload();
      }, 30000); // Alle 30 Sekunden
    }
    
    return () => {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current);
      }
    };
  }, [licenseStatus, selectedSession, measurements, backendUrl]);

  // Initialisiere Error-Handler beim App-Start
  useEffect(() => {
    initializeErrorHandler();
    
    return () => {
      // Cleanup nicht nötig, da Error-Handler global ist
    };
  }, []);

  // Lade Backend-Version
  useEffect(() => {
    const loadBackendVersion = async () => {
      if (!backendUrl) return;
      
      try {
        const normalizedUrl = backendUrl.replace(/\/$/, '');
        const response = await fetch(`${normalizedUrl}/version`);
        if (response.ok) {
          const data = await response.json();
          setBackendVersion(data.version || null);
        }
      } catch (error) {
        console.warn('[App] Fehler beim Laden der Backend-Version:', error);
      }
    };
    
    if (backendUrl) {
      loadBackendVersion();
    }
  }, [backendUrl]);

  // Prüfe API-Kompatibilität
  useEffect(() => {
    const checkCompatibility = async () => {
      if (!backendUrl || !isLoggedIn) {
        setApiCompatible(true); // Default: kompatibel
        setCompatibilityWarning(null);
        return;
      }

      try {
        const result = await checkApiCompatibility(backendUrl, FRONTEND_VERSION);
        setApiCompatible(result.compatible !== false);
        
        if (result.warnings && result.warnings.length > 0) {
          setCompatibilityWarning(result.warnings.join('; '));
          console.warn('[Compatibility] Kompatibilitäts-Warnungen:', result.warnings);
        } else {
          setCompatibilityWarning(null);
        }

        if (result.backendVersion) {
          console.log(`[Compatibility] Backend-Version: ${result.backendVersion}, Frontend-Version: ${FRONTEND_VERSION}, Kompatibel: ${result.compatible}`);
        }
      } catch (error) {
        console.warn('[Compatibility] Fehler bei Kompatibilitätsprüfung:', error);
        // Im Fehlerfall als kompatibel annehmen (Graceful Degradation)
        setApiCompatible(true);
        setCompatibilityWarning(null);
      }
    };

    if (backendUrl && isLoggedIn) {
      checkCompatibility();
    }
  }, [backendUrl, isLoggedIn]);

  // Stelle Zustand direkt aus Daten wieder her (ohne IndexedDB)
  const restoreStateFromData = useCallback(async (savedState) => {
    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2846', 'restoreStateFromData: Entry', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,startTime:savedState?.startTime,hasSelectedSession:!!savedState?.selectedSession}, 'A');
    try {
      if (!savedState) {
        console.log('[Update-Resume] Kein gespeicherter Zustand übergeben');
        return false;
      }

      // Migriere Zustand auf aktuelle Version
      const migratedState = migrateSavedState(savedState, FRONTEND_VERSION);
      
      // Validiere migrierten Zustand
      const validation = validateMigratedState(migratedState);
      if (!validation.valid) {
        console.error('[Update-Resume] Validierungsfehler:', validation.errors);
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2859', 'restoreStateFromData: Validation failed', {errors:validation.errors}, 'A');
        return false;
      }

      if (!migratedState.isRunning) {
        console.log('[Update-Resume] Gespeicherter Zustand war nicht laufend, keine Wiederherstellung');
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2864', 'restoreStateFromData: Not running', {}, 'A');
        return false;
      }

      // Nach Lizenzablauf/Erneuerung: Gespeicherte Session in Liste sicherstellen, damit Fortsetzen funktioniert
      setSessions(prev => {
        if (!migratedState.selectedSession?.id) return prev;
        if (prev.some(s => s.id === migratedState.selectedSession.id)) return prev;
        return [migratedState.selectedSession, ...prev];
      });

      // Berechne aktuelle Zeit basierend auf startTime
      const elapsed = Date.now() - migratedState.startTime;
      const newCurrentTime = Math.round((elapsed / 1000 / 60) * 100); // In Hundertstel-Minuten      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2872', 'restoreStateFromData: Before state updates', {migratedStartTime:migratedState.startTime,migratedIsRunning:migratedState.isRunning,newCurrentTime}, 'B');      // Stelle alles wieder her
      setSelectedSession(migratedState.selectedSession);
      setResumeNumber(migratedState.resumeNumber); // KEINE Erhöhung!
      setMeasurements(migratedState.measurements || []);
      // WICHTIG: startTime und isRunning in einem Batch setzen, aber startTime zuerst
      // React batched die Updates, aber wir stellen sicher, dass startTime vor isRunning gesetzt wird
      setStartTime(migratedState.startTime); // Original startTime beibehalten
      setCurrentTime(newCurrentTime); // Neu berechnet, aber basierend auf original startTime
      setIsRunning(true); // Uhr läuft weiter - Timer-Effect wird starten, da startTime bereits gesetzt ist
      setLastMeasurementTime(migratedState.lastMeasurementTime || 0);
      setVorderseiteData(migratedState.vorderseiteData);
      
      // Stelle erweiterte Informationen wieder her
      if (migratedState.selectedKuerzel) {
        setSelectedKuerzel(migratedState.selectedKuerzel);
      }
      if (migratedState.einflussgroessenValues) {
        setEinflussgroessenValues(migratedState.einflussgroessenValues);
      }
      if (migratedState.merke1Values) {
        setMerke1Values(migratedState.merke1Values);
      }
      if (migratedState.activity !== undefined) {
        setActivity(migratedState.activity || '');
      }
      if (migratedState.note !== undefined) {
        setNote(migratedState.note || '');
      }
      if (migratedState.timeType !== undefined) {
        setTimeType(migratedState.timeType || 'thb');
      }
      
      // Stelle Scroll-Positionen wieder her (nach kurzer Verzögerung, damit DOM bereit ist)
      if (migratedState.scrollPositions && typeof window !== 'undefined') {
        setTimeout(() => {
          // Messungen-Liste
          if (migratedState.scrollPositions.measurementsList) {
            const measurementsList = document.querySelector('.measurements-list');
            if (measurementsList) {
              measurementsList.scrollTop = migratedState.scrollPositions.measurementsList.scrollTop;
              measurementsList.scrollLeft = migratedState.scrollPositions.measurementsList.scrollLeft;
            }
          }
          
          // Tabs-Container
          if (migratedState.scrollPositions.tabsContainer && tabsContainerRef.current) {
            tabsContainerRef.current.scrollTop = migratedState.scrollPositions.tabsContainer.scrollTop;
            tabsContainerRef.current.scrollLeft = migratedState.scrollPositions.tabsContainer.scrollLeft;
          }
          
          // Tätigkeits-Liste (Ablauf-Kürzel-Liste)
          if (migratedState.scrollPositions.ablaufKuerzelList) {
            const ablaufKuerzelList = document.querySelector('.ablauf-kuerzel-list');
            if (ablaufKuerzelList) {
              ablaufKuerzelList.scrollTop = migratedState.scrollPositions.ablaufKuerzelList.scrollTop;
              ablaufKuerzelList.scrollLeft = migratedState.scrollPositions.ablaufKuerzelList.scrollLeft;
            }
          }
          
          // Activities-Liste (für Kompatibilität)
          if (migratedState.scrollPositions.activitiesList) {
            const activitiesList = document.querySelector('.activities-list');
            if (activitiesList) {
              activitiesList.scrollTop = migratedState.scrollPositions.activitiesList.scrollTop;
              activitiesList.scrollLeft = migratedState.scrollPositions.activitiesList.scrollLeft;
            }
          }
        }, 100);
      }
      
      // Stelle focusedInput State wieder her (für Ziffernblock-Anzeige)
      if (migratedState.focusedInput) {
        setFocusedInput(migratedState.focusedInput);
      }
      
      // Stelle Fokus wieder her (nach kurzer Verzögerung, damit DOM bereit ist)
      if (migratedState.focusInfo && typeof document !== 'undefined') {
        setTimeout(() => {
          const focusInfo = migratedState.focusInfo;
          
          // Versuche Element zu finden
          let elementToFocus = null;
          
          // Spezielle Behandlung für Ziffernblock-Inputs (Einflussgrößen-Ausprägungen)
          if (focusInfo.focusedZiffernblockEinfluss) {
            // Suche nach dem Input-Feld mit data-einflussname Attribut
            elementToFocus = document.querySelector(`input[data-einflussname="${focusInfo.focusedZiffernblockEinfluss}"]`);
            if (elementToFocus) {
              ziffernblockInputElementRef.current = elementToFocus;
            }
          }
          
          if (!elementToFocus && focusInfo.elementId) {
            elementToFocus = document.getElementById(focusInfo.elementId);
          }
          
          if (!elementToFocus && focusInfo.elementName) {
            elementToFocus = document.querySelector(`[name="${focusInfo.elementName}"]`);
          }
          
          if (!elementToFocus && focusInfo.focusedEinfluss) {
            // Spezielle Behandlung für Einflussgrößen-Inputs
            if (focusedEinflussInputRef.current) {
              elementToFocus = focusedEinflussInputRef.current;
            }
          }
          
          // Fallback: Suche nach Klasse oder Tag
          if (!elementToFocus && focusInfo.elementClass) {
            const elements = document.getElementsByClassName(focusInfo.elementClass.split(' ')[0]);
            if (elements.length > 0) {
              elementToFocus = elements[0];
            }
          }
          
          if (elementToFocus && typeof elementToFocus.focus === 'function') {
            try {
              elementToFocus.focus();
              // Wenn es ein Ziffernblock-Input ist, setze auch ziffernblockInputElementRef und focusedInput State
              if (focusInfo.focusedZiffernblockEinfluss) {
                ziffernblockInputElementRef.current = elementToFocus;
                // Stelle sicher, dass focusedInput State gesetzt ist, damit Ziffernblock angezeigt wird
                if (migratedState.focusedInput && migratedState.focusedInput[focusInfo.focusedZiffernblockEinfluss]) {
                  // focusedInput State wurde bereits oben wiederhergestellt
                  // Aber stelle sicher, dass es für diese Einflussgröße gesetzt ist
                  setFocusedInput(prev => ({
                    ...prev,
                    [focusInfo.focusedZiffernblockEinfluss]: true
                  }));
                }
              }
              console.log('[Update-Resume] Fokus wiederhergestellt:', focusInfo);
            } catch (error) {
              console.warn('[Update-Resume] Fehler beim Wiederherstellen des Fokus:', error);
            }
          }
        }, 150);
      }
      
      console.log('[Update-Resume] Zustand erfolgreich wiederhergestellt');
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3041', 'restoreStateFromData: Success return', {}, 'A');
      return true;
    } catch (error) {
      console.error('[Update-Resume] Fehler beim Wiederherstellen des Zustands:', error);
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3044', 'restoreStateFromData: Error', {error:error?.message}, 'A');
      return false;
    }
  }, []);

  // Stelle Zustand nach Update wieder her (lädt aus IndexedDB)
  const restoreStateAfterUpdate = useCallback(async () => {
    try {      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3029', 'checkForUpdateResume: Loading state', {}, 'D');      const savedState = await loadMeasurementState('update_resume_state');      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3031', 'checkForUpdateResume: State loaded', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,hasStartTime:!!savedState?.startTime,startTime:savedState?.startTime}, 'D');      if (!savedState) {
        console.log('[Update-Resume] Kein gespeicherter Zustand gefunden');
        return false;
      }

      // Verwende restoreStateFromData für die eigentliche Wiederherstellung
      const restored = await restoreStateFromData(savedState);      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3037', 'checkForUpdateResume: Restore result', {restored}, 'A');      if (restored) {
        // Lösche Update-State nach erfolgreicher Wiederherstellung
        await deleteMeasurementState('update_resume_state');
      }
      
      return restored;
    } catch (error) {
      console.error('[Update-Resume] Fehler beim Wiederherstellen des Zustands:', error);
      return false;
    }
  }, [restoreStateFromData]);

  // Prüfe beim App-Start nach Login, ob Update-Wiederherstellung nötig ist
  // (Fallback, falls die Prüfung vor Login nicht funktioniert hat)
  useEffect(() => {
    const checkForUpdateResume = async () => {
      // Prüfe, ob Clean-Modus aktiv ist (zur Sicherheit)
      const isCleanMode = typeof window !== 'undefined' && window.location.pathname.includes('/clean');      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3164', 'checkForUpdateResume async: Checking clean mode', {isCleanMode,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'E');      if (isCleanMode) {
        console.log('[Update-Resume] checkForUpdateResume: Clean-Modus aktiv, überspringe Check');
        return;
      }      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2460', 'checkForUpdateResume: Entry', {isLoggedIn,checkInProgress:updateResumeCheckInProgressRef.current}, 'A');      if (!isLoggedIn) {        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3097', 'checkForUpdateResume: Not logged in, skipping', {isLoggedIn}, 'A');        return; // Nur wenn eingeloggt
      }

      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3068', 'checkForUpdateResume: FUNCTION ENTRY', {isLoggedIn,refAtEntry:updateResumeDialogShownRef.current,stateAtEntry:showUpdateResumeDialog,checkInProgress:updateResumeCheckInProgressRef.current}, 'B');      // WICHTIG: Prüfe ZUERST, ob checkForUpdateResumeBeforeLogin bereits läuft
      // Wenn ja, überspringe diesen Check komplett, um doppelte Dialog-Öffnung zu verhindern
      if (updateResumeCheckInProgressRef.current) {
        console.log('[Update-Resume] checkForUpdateResume: checkForUpdateResumeBeforeLogin läuft bereits, überspringe');        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3070', 'checkForUpdateResume: checkForUpdateResumeBeforeLogin in progress, SKIP', {checkInProgress:updateResumeCheckInProgressRef.current}, 'B');        return;
      }
      
      // WICHTIG: Prüfe Ref UND State ZUERST (atomar, synchron) - bevor irgendwelche async Operationen
      // Dies verhindert Race Conditions, wenn beide Funktionen gleichzeitig laufen
      if (updateResumeDialogShownRef.current || showUpdateResumeDialog) {        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3072', 'checkForUpdateResume: Dialog already shown (Ref/State), SKIP', {ref:updateResumeDialogShownRef.current,state:showUpdateResumeDialog}, 'A');        console.log('[Update-Resume] checkForUpdateResume: Dialog bereits angezeigt (Ref/State-Check), überspringe', {
          ref: updateResumeDialogShownRef.current,
          state: showUpdateResumeDialog
        });
        return;
      }

      // Wenn Dialog bereits angezeigt wird (von checkForUpdateResumeBeforeLogin), überspringe Prüfung
      if (showUpdateResumeDialog) {        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2473', 'checkForUpdateResume: Dialog already shown by checkForUpdateResumeBeforeLogin, skipping', {}, 'B');        return;
      }

      // Wenn State bereits gelöscht wurde (von checkForUpdateResumeBeforeLogin), überspringe Prüfung
      if (updateResumeStateDeletedRef.current) {        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2473', 'checkForUpdateResume: State already deleted by checkForUpdateResumeBeforeLogin, skipping', {}, 'B');        return;
      }

      // Prüfe, ob bereits eine laufende Messung wiederhergestellt wurde
      // (dann wurde der State bereits gelöscht)
      // Direkt localStorage prüfen (schnell, synchron) - beforeunload speichert dort
      let savedState = null;
      try {
        const localData = localStorage.getItem('stoppuhr_update_resume_state');        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3229', 'checkForUpdateResume: Checking localStorage', {hasLocalData:!!localData,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');        if (localData) {
          try {
            savedState = JSON.parse(localData);
            // Validiere State - loadMeasurementState macht das bereits, aber hier als zusätzliche Sicherheit
            if (!validateMeasurementState(savedState, 'update_resume_state')) {
              savedState = null; // Markiere als ungültig
              await cleanupCorruptedState('update_resume_state');
            }
          } catch (error) {
            console.warn('[Update-Resume] Fehler beim Parsen des States:', error);
            savedState = null;
            await cleanupCorruptedState('update_resume_state');
          }          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3231', 'checkForUpdateResume: State loaded from localStorage', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,sessionId:savedState?.selectedSession?.id,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');        }
      } catch (error) {
        console.warn('localStorage Fehler beim direkten Laden:', error);        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3234', 'checkForUpdateResume: Error loading from localStorage', {error:error.message,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');        // Korrupter State erkannt - bereinige ihn automatisch
        await cleanupCorruptedState('update_resume_state');
      }
      
      // Falls nicht in localStorage, versuche IndexedDB (nur wenn online)
      // WICHTIG: Wenn localStorage vorhanden war, wird IndexedDB NICHT versucht (schneller)
      // WICHTIG: Wenn offline, wird IndexedDB NICHT versucht (kann langsam sein)
      if (!savedState && typeof navigator !== 'undefined' && navigator.onLine !== false) {        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3238', 'checkForUpdateResume: Trying IndexedDB (localStorage empty, online)', {pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');        savedState = await loadMeasurementState('update_resume_state');        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3239', 'checkForUpdateResume: State loaded from IndexedDB', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,sessionId:savedState?.selectedSession?.id,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'B');      }
      // Offline: IndexedDB wird übersprungen (wird bereits in loadMeasurementState gehandhabt, aber hier für Konsistenz)
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2418', 'checkForUpdateResume: State loaded', {hasSavedState:!!savedState,isRunning:savedState?.isRunning,sessionId:savedState?.selectedSession?.id}, 'A');
      if (savedState && savedState.isRunning) {
        // Setze Flag, um weitere Prüfungen zu verhindern
        updateResumeCheckInProgressRef.current = true;
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:2422', 'checkForUpdateResume: Running measurement found, set flag', {sessionId:savedState.selectedSession?.id,measurementsCount:savedState.measurements?.length}, 'B');
        // WICHTIG: Atomarer "Test-and-Set" - nur EINE Funktion kann den Dialog öffnen
        // Prüfe Ref UND State (defensive) - wenn einer bereits true ist, überspringe
        // WICHTIG: Diese Prüfung muss ATOMAR sein - keine async Operationen dazwischen
        const wasRefFalse = !updateResumeDialogShownRef.current;
        const wasStateFalse = !showUpdateResumeDialog;
        
        if (!wasRefFalse || !wasStateFalse) {
          agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3142', 'checkForUpdateResume: Dialog bereits angezeigt, SKIP', {ref:updateResumeDialogShownRef.current,state:showUpdateResumeDialog,wasRefFalse,wasStateFalse}, 'A');
          console.log('[Update-Resume] checkForUpdateResume: Dialog bereits angezeigt, überspringe', {
            ref: updateResumeDialogShownRef.current,
            state: showUpdateResumeDialog,
            wasRefFalse,
            wasStateFalse
          });
          return;
        }
        
        // Setze Ref SOFORT und ATOMAR - dies verhindert, dass die andere Funktion den Dialog öffnet
        // WICHTIG: Dies muss NACH der Prüfung, aber VOR allen anderen Operationen passieren
        updateResumeDialogShownRef.current = true;
        
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3150', 'checkForUpdateResume: Ref SET to true', {refAfter:updateResumeDialogShownRef.current}, 'A');
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3149', 'checkForUpdateResume: AFTER Ref-Set, BEFORE setState', {refAfter:updateResumeDialogShownRef.current,stateBefore:showUpdateResumeDialog}, 'A');
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3152', 'checkForUpdateResume: Showing dialog', {sessionId:savedState.selectedSession?.id,sessionName:savedState.selectedSession?.sessionName}, 'C');
        // Zeige Dialog für Benutzerbestätigung
        setUpdateResumeDialogData({
          sessionName: savedState.selectedSession?.sessionName || 'Unbekannt',
          measurementsCount: savedState.measurements?.length || 0,
          resumeNumber: savedState.resumeNumber || 0,
          savedState: savedState,
          session: savedState.selectedSession,
          isBeforeLogin: false
        });
        setShowUpdateResumeDialog(true);
        
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3165', 'checkForUpdateResume: AFTER setState(true)', {refAfterSetState:updateResumeDialogShownRef.current}, 'E');
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3141', 'checkForUpdateResume: Dialog state set to true', {}, 'C');
        return; // Warte auf Benutzer-Interaktion
      } else {
        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3143', 'checkForUpdateResume: No running state found', {hasSavedState:!!savedState,isRunning:savedState?.isRunning}, 'A');
        // Kein laufender State - Reset Flag
        updateResumeCheckInProgressRef.current = false;
      }
    };

    // Prüfe, ob "/clean" in der URL ist - wenn ja, überspringe Update-Resume-Check
    // HINWEIS: Die URL sollte bereits im checkForUpdateResumeBeforeLogin useEffect bereinigt worden sein,
    // aber prüfe trotzdem zur Sicherheit
    const isCleanMode = typeof window !== 'undefined' && window.location.pathname.includes('/clean');    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3299', 'checkForUpdateResume useEffect: Checking clean mode', {isCleanMode,pathname:typeof window !== 'undefined' ? window.location.pathname : 'N/A'}, 'E');    if (isCleanMode) {
      console.log('[Update-Resume] Clean-Modus aktiviert: Überspringe checkForUpdateResume');
      // Setze isCheckingUpdateResume auf false, damit Sessionliste/Login-Screen angezeigt wird
      setIsCheckingUpdateResume(false);
      updateResumeCheckInProgressRef.current = false; // Setze auf false, damit kein Check läuft
      
      // Entferne "/clean" aus der URL, falls es noch vorhanden ist
      if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
        const newUrl = window.location.pathname.replace('/clean', '') || '/';
        window.history.replaceState({}, '', newUrl);        agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3308', 'checkForUpdateResume useEffect: URL cleaned', {oldPathname:window.location.pathname,newUrl,pathnameAfter:window.location.pathname}, 'E');      }
      
      return;
    }
    
    // WICHTIG: Prüfe, ob Dialog bereits angezeigt wird (von checkForUpdateResumeBeforeLogin)
    // Wenn ja, überspringe diesen Check, um doppelte Dialog-Öffnung zu verhindern
    // WICHTIG: Diese Prüfung muss SYNCHRON sein, bevor checkForUpdateResume aufgerufen wird
    // Prüfe sowohl Ref als auch State (defensive)
    const dialogAlreadyShown = showUpdateResumeDialog || updateResumeDialogShownRef.current;
    if (dialogAlreadyShown) {
      console.log('[Update-Resume] useEffect: Dialog bereits angezeigt, überspringe checkForUpdateResume', {
        showUpdateResumeDialog,
        ref: updateResumeDialogShownRef.current,
        dialogAlreadyShown
      });      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3214', 'checkForUpdateResume: useEffect SKIP - dialog already shown', {showUpdateResumeDialog,ref:updateResumeDialogShownRef.current,dialogAlreadyShown}, 'B');      return;
    }
    
    // Rufe sofort auf, keine Verzögerung für sofortige Dialog-Anzeige    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3149', 'checkForUpdateResume: useEffect triggered', {isLoggedIn,showUpdateResumeDialog}, 'A');    checkForUpdateResume();
  }, [isLoggedIn, restoreStateAfterUpdate]); // WICHTIG: showUpdateResumeDialog entfernt - verhindert doppelte Dialog-Öffnung

  // Handler für Feedback-Submit
  const handleFeedbackSubmit = async (feedbackData) => {
    setIsSubmittingFeedback(true);
    try {
      // Prüfe ob offline ist ODER Backend nicht verfügbar
      const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const backendNotAvailable = backendAvailable !== true;
      const willSaveLocal = isOffline || backendNotAvailable;
      debugLog('Feedback', 'Submit Pfad', { isOffline, backendNotAvailable, willSaveLocal });

      if (isOffline || backendNotAvailable) {
        // Offline oder Backend nicht verfügbar: Speichere direkt lokal
        console.log('[Feedback] Gerät ist offline oder Backend nicht verfügbar, speichere Feedback lokal');
        await savePendingFeedback(feedbackData);
        debugLog('Feedback', 'Lokal gespeichert (Offline/Retry)', {});
        loadPendingFeedbackCount();
        alert(t('alerts.feedback_saved_offline'));
        return { success: true, offline: true };
      }

      // Online: Versuche zu senden
      const normalizedUrl = backendUrl.replace(/\/$/, '');
      const apiUrl = `${normalizedUrl}/api/feedback`;

      console.log('[Feedback] Sende Feedback an:', apiUrl);
      console.log('[Feedback] Feedback-Daten:', {
        ...feedbackData,
        images: feedbackData.images ? `${feedbackData.images.length} Bilder` : 'keine Bilder',
        imageSizes: feedbackData.images?.map(img => `${img.file_name}: ${img.file_data?.length || 0} bytes`)
      });

      // Berechne ungefähre Payload-Größe
      const payloadSize = JSON.stringify(feedbackData).length;
      console.log('[Feedback] Payload-Größe:', payloadSize, 'Bytes');

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      console.log('[Feedback] Response Status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Feedback] Backend-Fehler:', errorText);
        throw new Error(`Backend-Fehler (${response.status}): ${errorText}`);
      }

      const result = await response.json();
      debugLog('Feedback', 'Direkt gesendet', { status: response.status });
      console.log('[Feedback] Feedback erfolgreich gesendet:', result);
      
      // Optional: Zeige Erfolgsmeldung
      alert(t('alerts.feedback_success'));
      
      return result;
    } catch (error) {
      console.error('[Feedback] Fehler beim Senden des Feedbacks:', error);
      console.error('[Feedback] Error Details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      // Speichere lokal für späteren Upload
      try {
        await savePendingFeedback(feedbackData);
        debugLog('Feedback', 'Lokal gespeichert (Offline/Retry)', {});
        loadPendingFeedbackCount();
        alert(t('alerts.feedback_saved_later'));
        // WICHTIG: Rückgabe mit success, damit Dialog sich schließt
        return { success: true, offline: true };
      } catch (saveError) {
        console.error('[Feedback] Fehler beim lokalen Speichern:', saveError);
        alert(t('alerts.feedback_error', { msg: error.message }));
        throw saveError; // Wirf den Fehler weiter, damit der Dialog nicht geschlossen wird
      }
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Telemetrie-Snapshot bauen und senden (für Close und manuellen Button)
  const buildAndSendTelemetry = useCallback(async (opts = {}) => {
    const { showFeedback = false } = opts;
    if (!backendUrl || !backendUrl.trim()) {
      if (showFeedback) alert(t('alerts.telemetry_no_backend'));
      return false;
    }
    const configSnapshot = {
      decimalPlacesVorderseite,
      decimalPlacesSollzeiten,
      darkMode,
      grade_position: gradePosition,
      show_individual_time_header: showIndividualTimeInHeader,
      show_einflussgroessen_compact: showEinflussgroessenCompact,
      show_ablaufdb: showAblaufDb,
      show_vorderseite: showVorderseite,
      show_ea_kombinationen: showEaKombinationen,
      use_global_influences: useGlobalInfluences,
      compact_view_mode: compactView ? 'compact' : 'detail',
      compact_inputs_enabled: showCompactInputs,
      merke1_enabled: merke1Enabled,
      haptic_feedback_enabled: hapticFeedbackEnabled,
      ohne_leistungsgrad_erfassen: ohneLeistungsgradErfassen,
      ablaufDbName: ablaufDbName || null,
      analysisViewMode,
      statistikGroupBy,
      statistikDisplay,
      statistikTimeType,
    };
    const ok = await sendConfigTelemetry({
      backendUrl,
      licenseKey: licenseInfo?.license_key ?? null,
      userId: typeof window !== 'undefined' ? (parseInt(localStorage.getItem(STORAGE_KEY_USER_ID) || '0', 10) || null) : null,
      config: configSnapshot,
    });
    if (showFeedback) alert(ok ? t('alerts.telemetry_sent') : t('alerts.telemetry_failed'));
    return ok;
  }, [backendUrl, decimalPlacesVorderseite, decimalPlacesSollzeiten, darkMode, gradePosition,
    showIndividualTimeInHeader, showEinflussgroessenCompact, showAblaufDb, showVorderseite, showEaKombinationen,
    compactView, showCompactInputs, merke1Enabled, hapticFeedbackEnabled, ohneLeistungsgradErfassen,
    ablaufDbName, analysisViewMode, statistikGroupBy, statistikDisplay, statistikTimeType, licenseInfo?.license_key]);

  // Beim Schließen der Konfiguration: Telemetrie senden (wenn aktiviert)
  const handleCloseConfig = useCallback(() => {
    if (telemetryEnabled) buildAndSendTelemetry({ showFeedback: false });
    setShowConfig(false);
  }, [telemetryEnabled, buildAndSendTelemetry]);

  // About-Tab: Update prüfen und anwenden (Service Worker + Cache)
  const handleAboutUpdateClick = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      setAboutUpdateStatus('none');
      return;
    }
    // Im Development-Modus (npm start) ist kein Service Worker registriert – ready würde nie auflösen
    if (process.env.NODE_ENV === 'development') {
      setAboutUpdateStatus('dev');
      return;
    }
    setAboutUpdateStatus('checking');
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      // Kurz warten, ob ein neuer Worker in 'waiting' erscheint
      await new Promise((r) => setTimeout(r, 1500));
      if (registration.waiting) {
        setAboutUpdateStatus('applying');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Reload erfolgt in index.jsx bei 'controlling' (wenn keine laufende Messung)
      } else {
        setAboutUpdateStatus('none');
      }
    } catch {
      setAboutUpdateStatus('none');
    }
  }, []);

  const handleSecurityCheckClick = useCallback(async () => {
    const secureContext = typeof window !== 'undefined' && window.isSecureContext;
    const protocol = typeof window !== 'undefined' ? (window.location?.protocol || '?') : '?';
    let hasSW = false;
    let swState = 'none';
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        hasSW = !!reg;
        swState = reg?.active ? 'active' : reg?.waiting ? 'waiting' : reg?.installing ? 'installing' : 'none';
      } catch {
        swState = 'error';
      }
    }
    const base = { secureContext, protocol, hasSW, swState };
    setSecurityCheckResult({ ...base, cveData: null });
    try {
      const versions = getTechVersions();
      const cveData = await checkCvesForTechStack(versions);
      setSecurityCheckResult((prev) => prev ? { ...prev, cveData } : { ...base, cveData });
    } catch {
      setSecurityCheckResult((prev) => prev ? { ...prev, cveData: {} } : { ...base, cveData: {} });
    }
  }, []);

  const handleCacheClearClick = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production' || !('caches' in window) || !('serviceWorker' in navigator)) return;
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      window.location.reload();
    } catch (err) {
      console.error('[Cache clear]', err);
    }
  }, []);

  const showCacheClearButton = process.env.NODE_ENV === 'production' && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

  // Prüfe Scroll-Position der Config-Tabs
  useEffect(() => {
    const checkTabsScroll = () => {
      if (tabsContainerRef.current) {
        const container = tabsContainerRef.current;
        setTabsCanScrollLeft(container.scrollLeft > 0);
        setTabsCanScrollRight(
          container.scrollLeft < container.scrollWidth - container.clientWidth - 1
        );
      }
    };

    if (showConfig && tabsContainerRef.current) {
      // Prüfe initial
      setTimeout(checkTabsScroll, 100);
      
      // Prüfe bei Resize
      window.addEventListener('resize', checkTabsScroll);
      
      return () => {
        window.removeEventListener('resize', checkTabsScroll);
      };
    }
  }, [showConfig, configTab]);

  // Speicherbelegung (navigator.storage.estimate) beim Öffnen des Sonstiges-Tabs
  useEffect(() => {
    if (configTab !== 'sonstiges') return;
    setStorageEstimate(null);
    if (typeof navigator === 'undefined' || !navigator.storage || typeof navigator.storage.estimate !== 'function') {
      setStorageEstimate({ error: 'Nicht verfügbar' });
      return;
    }
    let cancelled = false;
    navigator.storage.estimate().then(({ usage = 0, quota = 0 }) => {
      if (!cancelled) setStorageEstimate({ usage, quota });
    }).catch(() => {
      if (!cancelled) setStorageEstimate({ error: 'Fehler beim Abrufen' });
    });
    return () => { cancelled = true; };
  }, [configTab]);

  // Keyboard shortcuts for grades
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Nur verarbeiten wenn Ctrl gedrückt ist
      if (!e.ctrlKey) {
        return;
      }
      
      // Erstelle Key-String (z.B. "Ctrl+1")
      const key = `Ctrl+${e.key}`;
      
      // Prüfe ob es eine konfigurierte Tastenkombination ist
      let isConfiguredShortcut = false;
      
      if (keyboardConfig.interruption === key) {
        isConfiguredShortcut = true;
      } else {
        // Prüfe alle Leistungsgrade
        const gradesArray = [0, 80, 90, 100, 105, 110, 115, 120, 125];
        for (const grade of gradesArray) {
          if (keyboardConfig[grade] === key) {
            isConfiguredShortcut = true;
            break;
          }
        }
      }
      
      // Wenn es eine konfigurierte Tastenkombination ist, verhindere Browser-Standard-Shortcuts
      if (isConfiguredShortcut) {
        const keyCode = e.key;
        if (keyCode >= '0' && keyCode <= '9') {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
        
        // Führe die Aktion aus
        if (keyboardConfig.interruption === key) {
          if (handleInterruptionToggleRef.current) {
            handleInterruptionToggleRef.current(!isInterrupted);
          }
          return;
        }
        
        // Prüfe alle Leistungsgrade
        const gradesArray = [0, 80, 90, 100, 105, 110, 115, 120, 125];
        for (const grade of gradesArray) {
          if (keyboardConfig[grade] === key) {
            if (handleAddMeasurementRef.current) {
              handleAddMeasurementRef.current(grade);
            }
            return;
          }
        }
      }
    };
    
    // Verwende Capture-Phase und passive: false für preventDefault
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [keyboardConfig, isInterrupted]);

  // Keyboard shortcuts for main functions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prüfe ob der Benutzer in einem Input-Feld tippt
      const target = e.target;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      
      // Space - Start/Stop (nur wenn nicht in Input-Feld)
      if (e.key === ' ' && !isInputField && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        if (selectedSession) {
          if (isRunning) {
            handleStop();
          } else {
            handleStart();
          }
        }
        return;
      }
      
      // Ctrl+U - Upload
      if (e.ctrlKey && e.key === 'u' && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        if (licenseStatus && measurements.length > 0) {
          handleUploadMeasurements();
        }
        return;
      }
      
      // Alt+A - Auswertung
      if (e.altKey && e.key === 'a' && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        if (licenseStatus) {
          setShowAnalysis(!showAnalysis);
        }
        return;
      }
      
      // Ctrl+T - View Toggle (kompakt/details)
      if (e.ctrlKey && e.key === 't' && !e.altKey && !e.shiftKey) {
        // Nur verhindern wenn nicht in Input-Feld (sonst würde Ctrl+T den Tab öffnen)
        if (!isInputField) {
          e.preventDefault();
          const newValue = !compactView;
          setCompactView(newValue);
          localStorage.setItem('compactView', String(newValue));
        }
        return;
      }
      
      // Ctrl+, - Config
      if (e.ctrlKey && e.key === ',' && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setShowConfig(!showConfig);
        return;
      }
      
      // Ctrl+S - Speichern
      if (e.ctrlKey && e.key === 's' && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        if (measurements.length > 0 && selectedSession) {
          const defaultFileName = selectedSession?.sessionName 
            ? selectedSession.sessionName.replace(/[^a-zA-Z0-9]/g, '_')
            : `messpunkte_${deviceName}`;
          const dateStr = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
          const suggestedFileName = `${defaultFileName}_${dateStr}`;
          handleSaveMeasurements(suggestedFileName);
        }
        return;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [selectedSession, isRunning, licenseStatus, measurements.length, compactView, showAnalysis, deviceName]);
  
  // Timer
  useEffect(() => {    agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3365', 'Timer Effect: Entry', {isRunning,hasStartTime:!!startTime,startTime}, 'C');    if (isRunning && startTime) {      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3366', 'Timer Effect: Starting timer', {isRunning,startTime,hasIntervalRef:!!intervalRef.current}, 'C');      // Stelle sicher, dass kein alter Timer läuft
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        // Berechne Zeit in Hundertsteln von Minuten und runde auf ganze Zahl
        const time = Math.round((elapsed / 1000 / 60) * 100);
        setCurrentTime(time);
      }, 10);
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3372', 'Timer Effect: Timer started', {intervalRefSet:!!intervalRef.current}, 'C');
    } else {
      agentLog('62e7c601-cf36-412a-b516-74a3871e6061', 'App.jsx:3378', 'Timer Effect: Not starting/stopping', {isRunning,hasStartTime:!!startTime,hasIntervalRef:!!intervalRef.current}, 'C');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, startTime]);

  // License Check
  const checkLicense = async () => {
    if (!email || !backendUrl) {
      alert(t('alerts.email_backend_required'));
      return;
    }

    // WICHTIG: Wenn offline, überspringe License-Check und verwende lokale Lizenz
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.log('[License-Check] Gerät ist offline, verwende lokale Lizenz');
      const localLicense = localStorage.getItem('licenseStatus');
      if (localLicense === 'true') {
        setLicenseStatus(true);
      } else {
        setLicenseStatus(false);
      }
      setIsCheckingLicense(false);
      return;
    }

    // Prüfe zuerst, ob eine lokale Lizenz vorhanden ist
    const savedLicenseData = localStorage.getItem('licenseData');
    const hasLocalLicense = savedLicenseData && licenseStatus;

    setIsCheckingLicense(true);
    try {
      const normalizedUrl = normalizeBackendUrl(backendUrl);
      const checkUrl = buildApiUrl(normalizedUrl, '/api/license/check');
      
      console.log('[License-Check] ========== START ==========');
      console.log('[License-Check] Konfiguration:', {
        email: email,
        originalUrl: backendUrl,
        normalizedUrl: normalizedUrl,
        checkUrl: checkUrl,
        hasLocalLicense: hasLocalLicense
      });
      
      // Lade gespeicherten Lizenz-Key aus localStorage (falls vorhanden)
      const savedLicenseData = localStorage.getItem('licenseData');
      let licenseKey = null;
      if (savedLicenseData) {
        try {
          const licenseData = JSON.parse(savedLicenseData);
          licenseKey = licenseData.license_key || null;
        } catch (e) {
          console.warn('[License-Check] Fehler beim Laden des Lizenz-Keys:', e);
        }
      }
      
      const requestBody = { 
        email,
        ...(licenseKey && { license_key: licenseKey }),
        device_id: getDeviceId(),
        device_name: getDeviceName()
      };
      console.log('[License-Check] Request-Details:', {
        method: 'POST',
        url: checkUrl,
        headers: { 'Content-Type': 'application/json' },
        body: requestBody,
        hasLicenseKey: !!licenseKey
      });
      
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 Sekunden Timeout
      
      let response;
      try {
        response = await fetch(checkUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.error('[License-Check] Request timeout nach 5 Sekunden - Backend nicht erreichbar');
          throw new Error('Backend nicht erreichbar (Timeout nach 5 Sekunden)');
        }
        throw fetchError;
      }
      
      const duration = Date.now() - startTime;
      
      console.log('[License-Check] Response erhalten:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        duration: `${duration}ms`,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        let data;
        try {
          const responseText = await response.text();
          console.log('[License-Check] Response-Body (Text):', responseText);
          data = JSON.parse(responseText);
          console.log('[License-Check] Response-Body (JSON):', data);
        } catch (parseError) {
          console.error('[License-Check] Fehler beim Parsen der Response:', parseError);
          throw new Error(`Ungültige JSON-Response: ${parseError.message}`);
        }
        
        const hasLicense = data.hasLicense ?? data.has_license ?? false;
        const dKey = data.licenseKey ?? data.license_key;
        const dProduct = data.productCode ?? data.product_code;
        const dType = data.licenseType ?? data.license_type;
        const dExpires = data.expiresAt ?? data.expires_at;
        const dSig = data.signature;
        const dIsActive = data.isActive ?? data.is_active;
        console.log('[License-Check] Lizenz-Status:', {
          hasLicense: hasLicense,
          data: data
        });
        
        setLicenseStatus(hasLicense);
        
        // Aktualisiere licenseInfo basierend auf Backend-Response
        // WICHTIG: Zeige auch deaktivierte Lizenzen an (mit license_key)
        if (dKey && dProduct && dType) {
          const isActive = dIsActive !== undefined ? dIsActive : hasLicense;
          setLicenseInfo({
            valid: true,
            license_key: dKey,
            product_code: dProduct,
            license_type: dType,
            expires_at: dExpires || null,
            is_active: isActive,
            message: isActive ? 'Lizenz ist aktiv' : 'Lizenz ist deaktiviert',
          });
        } else if (dKey) {
          // Auch wenn nur license_key vorhanden ist (z.B. deaktivierte Lizenz ohne vollständige Daten)
          setLicenseInfo({
            valid: false,
            license_key: dKey,
            product_code: dProduct || null,
            license_type: dType || null,
            expires_at: dExpires || null,
            is_active: dIsActive !== undefined ? dIsActive : false,
            message: 'Lizenz-Informationen unvollständig',
          });
        } else if (hasLocalLicense) {
          // Backend gibt keine Lizenz zurück, aber lokale Lizenz vorhanden - behalte lokale licenseInfo
          // licenseInfo wird später in der else-Branch gesetzt
          console.info('[License-Check] Backend gibt keine Lizenz zurück, aber lokale Lizenz vorhanden. Behalte lokale licenseInfo.');
        } else {
          // Keine Backend-Lizenz und keine lokale Lizenz
          setLicenseInfo(null);
        }
        
        if (hasLicense) {
          localStorage.setItem('licenseStatus', 'true');
          // Speichere Lizenz-Informationen (falls verfügbar) – gleicher Key, aktualisiertes Ablaufdatum/Signatur vom Server
          if (dKey && dProduct && dType && dSig) {
            const licenseData = {
              license_key: dKey,
              product_code: dProduct,
              license_type: dType,
              expires_at: dExpires || null,
              signature: dSig,
            };
            localStorage.setItem('licenseData', JSON.stringify(licenseData));
          }
          // Aktualisiere aktuelle Session, um Demo-Modus zu entfernen
          if (selectedSession) {
            const updatedSession = {
              ...selectedSession,
              isDemo: false,
              sessionName: selectedSession.sessionName?.replace('Demo-Erfassung', 'Messung') || `Messung ${new Date(selectedSession.createdAt).toLocaleDateString('de-DE')} (${selectedSession.deviceName || deviceName})`
            };
            setSelectedSession(updatedSession);
            // Aktualisiere auch in Sessions-Liste
            const updatedSessions = sessions.map(s =>
              s.id === selectedSession.id ? updatedSession : s
            );
            setSessions(updatedSessions);
            localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
          }
          alert(t('alerts.license_found'));
        } else {
          // Backend sagt keine aktive Lizenz
          if (dIsActive === false && dKey) {
            // Lizenz existiert, aber ist deaktiviert - licenseInfo wurde bereits oben gesetzt
            setLicenseStatus(false);
            localStorage.setItem('licenseStatus', 'false');
            // Aktualisiere ALLE Sessions auf Demo-Modus
            const updatedSessions = sessions.map(session => ({
              ...session,
              isDemo: true,
              sessionName: session.sessionName?.includes('Demo-Erfassung')
                ? session.sessionName
                : `Demo-Erfassung (${session.deviceName || deviceName})`
            }));
            setSessions(updatedSessions);
            localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
            
            if (selectedSession) {
              const updatedSession = updatedSessions.find(s => s.id === selectedSession.id);
              if (updatedSession) setSelectedSession(updatedSession);
            }
            alert(t('alerts.license_inactive'));
          } else if (hasLocalLicense) {
            // Backend sagt keine Lizenz - aber wenn lokale Lizenz vorhanden ist, behalte sie
            console.info('Backend meldet keine Lizenz, aber lokale Lizenz ist vorhanden. Lokale Lizenz wird beibehalten (Offline-Modus).');
            // Stelle sicher, dass licenseInfo mit license_key gesetzt ist (aus localStorage)
            const savedLicenseData = localStorage.getItem('licenseData');
            if (savedLicenseData) {
              try {
                const localLicenseData = JSON.parse(savedLicenseData);
                setLicenseInfo({
                  valid: true,
                  license_key: localLicenseData.license_key,
                  product_code: localLicenseData.product_code,
                  license_type: localLicenseData.license_type,
                  expires_at: localLicenseData.expires_at || null,
                  is_active: licenseStatus === true, // Verwende aktuellen Status
                  message: 'Lizenz ist gültig (lokal, Offline-Modus)',
                });
              } catch (e) {
                console.error('Fehler beim Laden der lokalen Lizenz:', e);
              }
            }
            // Status nicht ändern, lokale Lizenz bleibt aktiv (kein Alert, da Offline-Modus normal ist)
          } else {
            setLicenseStatus(false);
            localStorage.setItem('licenseStatus', 'false');
            // Setze licenseInfo auf null, wenn keine Lizenz vorhanden ist
            setLicenseInfo(null);
            // Aktualisiere ALLE Sessions auf Demo-Modus
            const updatedSessions = sessions.map(session => ({
              ...session,
              isDemo: true,
              sessionName: session.sessionName?.includes('Demo-Erfassung')
                ? session.sessionName
                : `Demo-Erfassung (${session.deviceName || deviceName})`
            }));
            setSessions(updatedSessions);
            localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
            
            if (selectedSession) {
              const updatedSession = updatedSessions.find(s => s.id === selectedSession.id);
              if (updatedSession) setSelectedSession(updatedSession);
            }
            alert(t('alerts.license_not_found'));
          }
        }
        setIsCheckingLicense(false); // WICHTIG: Reset nach erfolgreichem Check
        agentLog('9f295211-a98e-4941-b16d-f3672384af61', 'App.jsx:2038', 'checkLicense: Success after setIsCheckingLicense(false)', {hasLicense:data.has_license}, 'D');
      } else {
        // Backend-Fehler: Versuche Fehlertext zu lesen
        let errorText = 'Unbekannter Fehler';
        let errorData = null;
        try {
          const responseText = await response.text();
          console.error('[License-Check] Response-Body (Fehler):', responseText);
          errorText = responseText;
          try {
            errorData = JSON.parse(responseText);
            console.error('[License-Check] Response-Body (JSON):', errorData);
          } catch (e) {
            // Nicht JSON, verwende Text
          }
        } catch (e) {
          console.error('[License-Check] Fehler beim Lesen des Fehlertexts:', e);
        }
        
        console.error('[License-Check] Backend-Fehler:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          errorData: errorData,
          url: checkUrl
        });
        
        // Bei 404: Endpunkt existiert nicht (Backend-Version zu alt?)
        if (response.status === 404) {
          console.error('[License-Check] ❌ Endpunkt nicht gefunden (404):', {
            url: checkUrl,
            normalizedUrl: normalizedUrl,
            originalUrl: backendUrl,
            message: 'Backend unterstützt diesen Endpunkt möglicherweise nicht oder URL ist falsch'
          });          agentLog('9f295211-a98e-4941-b16d-f3672384af61', 'App.jsx:2066', 'checkLicense: 404 error before return', {url:checkUrl,hasLocalLicense}, 'A');          // Wenn lokale Lizenz vorhanden ist, behalte sie (stille Warnung, da Offline-Modus normal ist)
          if (hasLocalLicense) {
            console.info('Backend-Endpunkt nicht gefunden. Lokale Lizenz wird beibehalten (Offline-Modus).');
            // Status nicht ändern
          } else {
            setLicenseStatus(false);
            localStorage.setItem('licenseStatus', 'false');
            alert(t('alerts.license_backend_not_found'));
          }
          setIsCheckingLicense(false); // WICHTIG: Reset vor return
          agentLog('9f295211-a98e-4941-b16d-f3672384af61', 'App.jsx:2082', 'checkLicense: 404 error after setIsCheckingLicense(false)', {url:checkUrl}, 'A');
          return;
        }
        
        // Andere Backend-Fehler
        if (hasLocalLicense) {
          console.warn(`Backend-Fehler (${response.status}): ${errorText}. Lokale Lizenz wird beibehalten (Offline-Modus).`);
          // Status nicht ändern
        } else {
          setLicenseStatus(false);
          localStorage.setItem('licenseStatus', 'false');
          alert(t('alerts.license_backend_error', { status: response.status, error: errorText }));
        }
        setIsCheckingLicense(false); // WICHTIG: Reset nach Backend-Fehler
        agentLog('9f295211-a98e-4941-b16d-f3672384af61', 'App.jsx:2093', 'checkLicense: Backend error after setIsCheckingLicense(false)', {status:response.status,hasLocalLicense}, 'B');
      }
    } catch (error) {
      console.error('[License-Check] Fehler:', error);
      setIsCheckingLicense(false);
      
      // Bei Timeout oder Netzwerkfehlern: Fallback auf lokale Lizenz oder Demo-Modus
      if (error.message && (error.message.includes('Timeout') || error.message.includes('nicht erreichbar') || error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.name === 'TypeError' || error.name === 'AbortError')) {
        console.warn('[License-Check] Backend nicht erreichbar (Timeout oder Netzwerkfehler), verwende lokale Lizenz oder Demo-Modus');
        const savedLicenseData = localStorage.getItem('licenseData');
        if (savedLicenseData) {
          try {
            const localLicenseData = JSON.parse(savedLicenseData);
            // Prüfe lokale Validierung (ohne Backend)
            const isValid = await verifyLicenseSignatureFrontend(localLicenseData);
            if (isValid) {
              setLicenseStatus(true);
              localStorage.setItem('licenseStatus', 'true');
              setLicenseInfo({
                valid: true,
                license_key: localLicenseData.license_key,
                product_code: localLicenseData.product_code,
                license_type: localLicenseData.license_type,
                expires_at: localLicenseData.expires_at || null,
                is_active: true,
                message: 'Lizenz ist gültig (lokal, Offline-Modus)',
              });
              console.info('[License-Check] Lokale Lizenz verwendet (Offline-Modus)');              debugLog('App.jsx:2121', 'checkLicense: Offline mode return', {hasLocalLicense:true}, 'C');              setIsCheckingLicense(false); // WICHTIG: Reset vor return
              return;
            }
          } catch (e) {
            console.error('[License-Check] Fehler bei lokaler Lizenz-Validierung:', e);
          }
        }
        // Keine gültige lokale Lizenz: Demo-Modus
          setLicenseStatus(false);
          localStorage.setItem('licenseStatus', 'false');
        setLicenseInfo(null);
        console.warn('[License-Check] Keine gültige lokale Lizenz, Demo-Modus aktiviert');        debugLog('App.jsx:2132', 'checkLicense: No local license return', {hasLocalLicense:false}, 'C');        setIsCheckingLicense(false); // WICHTIG: Reset vor return
        return;
      }
      
      // Andere Fehler
      if (hasLocalLicense) {
        console.warn(`[License-Check] Fehler beim Prüfen der Lizenz: ${error.message}. Lokale Lizenz wird beibehalten (Offline-Modus).`);
        setIsCheckingLicense(false);
        // Status nicht ändern
      } else {
        setLicenseStatus(false);
        localStorage.setItem('licenseStatus', 'false');
        setLicenseInfo(null);
        alert(t('alerts.license_check_error', { msg: error.message }));
      }
      setIsCheckingLicense(false);
    }
  };

  // Lizenz-Key in Zwischenablage kopieren
  const handleCopyLicenseKey = async () => {
    if (!licenseInfo || !licenseInfo.license_key) {
      return;
    }
    
    try {
      await navigator.clipboard.writeText(licenseInfo.license_key);
      // Kurzes visuelles Feedback
      const button = document.getElementById('copy-license-key-btn');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✓ Kopiert!';
        button.style.backgroundColor = '#10b981';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.backgroundColor = '';
        }, 2000);
      }
    } catch (error) {
      console.error('Fehler beim Kopieren:', error);
      // Fallback für ältere Browser
      const textArea = document.createElement('textarea');
      textArea.value = licenseInfo.license_key;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert(t('alerts.license_key_copied'));
      } catch (err) {
        alert(t('alerts.license_key_copy_error'));
      }
      document.body.removeChild(textArea);
    }
  };

  // Lizenz entfernen
  const handleRemoveLicense = () => {
    setShowRemoveLicenseDialog(true);
  };

  const handleConfirmRemoveLicense = () => {
    setShowRemoveLicenseDialog(false);
    setLicenseStatus(false);
    setLicenseInfo(null);
    localStorage.removeItem('licenseStatus');
    localStorage.removeItem('licenseInfo');
    localStorage.removeItem('licenseData');
    
    // Aktualisiere alle Sessions, um Demo-Modus zu aktivieren
    const savedSessions = localStorage.getItem(STORAGE_KEY_SESSIONS);
    if (savedSessions) {
      try {
        const sessions = JSON.parse(savedSessions);
        const updatedSessions = sessions.map(session => ({
          ...session,
          isDemo: true,
          sessionName: session.sessionName?.includes('Demo-Erfassung') 
            ? session.sessionName 
            : `Demo-Erfassung (${session.deviceName || deviceName})`
        }));
        setSessions(updatedSessions);
        localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
        
        if (selectedSession) {
          const updatedSelectedSession = {
            ...selectedSession,
            isDemo: true,
            sessionName: selectedSession.sessionName?.includes('Demo-Erfassung') 
              ? selectedSession.sessionName 
              : `Demo-Erfassung (${selectedSession.deviceName || deviceName})`
          };
          setSelectedSession(updatedSelectedSession);
        }
      } catch (e) {
        console.error('Error updating sessions:', e);
      }
    }
    
    // Zeige Erfolgs-Dialog
    setLicenseImportResult({ type: 'success', message: 'Lizenz wurde entfernt. Demo-Modus mit Begrenzung aktiviert.', details: null });
    setShowLicenseImportDialog(true);
  };

  const handleCancelRemoveLicense = () => {
    setShowRemoveLicenseDialog(false);
  };

  // Hilfsfunktion: Base64 zu ArrayBuffer
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Verifiziere Lizenz-Signatur (vereinfacht für Offline-Nutzung)
  const verifyLicenseSignature = async (licenseKey, productCode, licenseType, expiresAt, signature) => {
    try {
      // Public Key aus localStorage oder Umgebungsvariable
      const publicKeyPem = localStorage.getItem('license_public_key') || process.env.REACT_APP_LICENSE_PUBLIC_KEY || '';
      
      if (!publicKeyPem) {
        console.warn('Public Key nicht konfiguriert, lokale Verifizierung nicht möglich');
        // Wenn kein Public Key vorhanden, akzeptiere die Lizenz basierend auf Format-Validierung
        return true; // Temporär: Akzeptiere wenn Format korrekt ist
      }

      // Für vollständige Verifizierung benötigen wir eine RSA-Bibliothek
      // Hier akzeptieren wir die Lizenz wenn Format korrekt ist
      return true; // Temporär: Akzeptiere wenn Format korrekt ist
    } catch (error) {
      console.error('Error verifying signature:', error);
      // Bei Fehler: Akzeptiere für Offline-Nutzung (Format-Validierung)
      return true;
    }
  };

  // Lizenz-Datei importieren
  const handleImportLicense = () => {
    licenseFileInputRef.current?.click();
  };

  // Handler für Lizenz-Datei-Änderung
  const handleLicenseFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImportingLicense(true);
    setLicenseInfo(null);

    try {
      // Lese Datei als Text
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const fileContent = event.target.result.trim();
          
          if (!fileContent) {
            throw new Error('Datei ist leer');
          }

          let licenseData;
          let licenseKey;
          let productCode;
          let licenseType;
          let expiresAt;
          let signature;

          // Versuche JSON-Format (neues Format mit allen Daten)
          try {
            // Entferne mögliche BOM oder Whitespace am Anfang/Ende
            const trimmedContent = fileContent.trim();
            
            // Versuche JSON zu parsen
            licenseData = JSON.parse(trimmedContent);
            
            // Validiere JSON-Struktur
            if (!licenseData.license_key || !licenseData.product_code || !licenseData.license_type || !licenseData.signature) {
              console.error('Fehlende Felder in Lizenz-Datei:', {
                has_license_key: !!licenseData.license_key,
                has_product_code: !!licenseData.product_code,
                has_license_type: !!licenseData.license_type,
                has_signature: !!licenseData.signature,
                keys: Object.keys(licenseData)
              });
              throw new Error('Ungültiges JSON-Format: Erforderliche Felder fehlen. Gefunden: ' + Object.keys(licenseData).join(', '));
            }
            
            licenseKey = licenseData.license_key;
            productCode = licenseData.product_code;
            licenseType = licenseData.license_type;
            expiresAt = (licenseData.expires_at && licenseData.expires_at !== null && licenseData.expires_at !== 'null') 
              ? licenseData.expires_at 
              : null;
            signature = licenseData.signature;
            
            // Debug: Prüfe extrahierte Werte
            console.log('[License-Import] Extrahierte Daten aus Datei:', {
              license_key: licenseKey ? `${licenseKey.substring(0, 10)}...` : 'FEHLT',
              product_code: productCode || 'FEHLT',
              license_type: licenseType || 'FEHLT',
              expires_at: expiresAt || 'null',
              signature: signature ? `${signature.substring(0, 20)}... (Länge: ${signature.length})` : 'FEHLT',
              has_signature: !!signature,
              signature_type: typeof signature
            });
            
            // Validiere License-Key
            if (!licenseKey || licenseKey.length === 0) {
              throw new Error('Ungültiger License-Key: License-Key fehlt');
            }
            
            // Validiere Produktcode - akzeptiere nur gültige Produktcodes für diese Anwendung
            const validProductCodes = ['ZEITECE', 'ZEITAUF-MOBIL'];
            if (!validProductCodes.includes(productCode)) {
              throw new Error(`Ungültiger Produktcode: ${productCode}. Gültige Produktcodes: ${validProductCodes.join(', ')}`);
            }
            
            // Prüfe Produktcode - akzeptiere alle gültigen Produktcodes
            // ZEITECE ist ein gültiger Produktcode und sollte akzeptiert werden
            // EXPECTED_PRODUCT_CODE wird nur für die Anzeige verwendet
            
            // Validiere License-Type
            const validLicenseTypes = ['PERMANENT', 'SUBSCRIPTION', 'NFR', 'TRIAL'];
            if (!validLicenseTypes.includes(licenseType)) {
              throw new Error(`Ungültiger License-Type: ${licenseType}`);
            }
          } catch (jsonError) {
            console.error('JSON-Parsing Fehler:', jsonError);
            console.error('Dateiinhalt (erste 200 Zeichen):', fileContent.substring(0, 200));
            
            // Prüfe, ob es ein SyntaxError ist (ungültiges JSON)
            if (jsonError instanceof SyntaxError) {
              throw new Error(`Ungültiges JSON-Format: ${jsonError.message}. Bitte verwenden Sie eine .license Datei mit gültigem JSON-Format.`);
            }
            
            // Andere Fehler (z.B. Validierungsfehler)
            throw jsonError;
          }

          // ===== NEUE SICHERHEITSSCHICHT 1: Frontend-Signaturprüfung =====
          const publicKeyPem = localStorage.getItem('license_public_key') 
            || process.env.REACT_APP_LICENSE_PUBLIC_KEY 
            || '';
          
          let frontendSignatureValid = false;
          let frontendVerificationSkipped = false;
          
          if (publicKeyPem) {
            try {
              frontendSignatureValid = await verifyLicenseSignatureFrontend(
                licenseKey,
                productCode,
                licenseType,
                expiresAt,
                signature,
                publicKeyPem
              );
              
              if (!frontendSignatureValid) {
                // Signatur im Frontend ungültig - sofort ablehnen
                setLicenseImportResult({
                  type: 'error',
                  message: '❌ Signatur-Verifizierung fehlgeschlagen. Die Lizenz-Datei ist möglicherweise manipuliert oder beschädigt.',
                  details: {
                    product_code: productCode,
                    license_type: licenseType,
                    expires_at: expiresAt,
                    verification_stage: 'frontend'
                  }
                });
                setShowLicenseImportDialog(true);
                setIsImportingLicense(false);
                e.target.value = '';
                return;
              }
              console.log('[License-Import] Frontend-Signaturprüfung erfolgreich');
            } catch (frontendError) {
              // Prüfe ob es ein PKCS#1 Format ist (nicht unterstützt)
              if (frontendError.message === 'PKCS1_NOT_SUPPORTED') {
                console.info('[License-Import] PKCS#1 Format erkannt. Frontend-Verifizierung nicht unterstützt, verwende Backend-Verifizierung (normal).');
                frontendVerificationSkipped = true;
              } else {
                console.warn('[License-Import] Frontend-Signaturprüfung fehlgeschlagen:', frontendError);
                // Bei anderen Fehlern: Weiter mit Backend-Prüfung (Fallback)
                frontendVerificationSkipped = true;
              }
            }
          } else {
            console.warn('[License-Import] Public Key nicht verfügbar, überspringe Frontend-Signaturprüfung');
            frontendVerificationSkipped = true;
          }

          // ===== SICHERHEITSSCHICHT 2: Ablaufdatum prüfen =====
          let isExpired = false;
          if (expiresAt) {
            const expiresDate = new Date(expiresAt);
            if (expiresDate < new Date()) {
              isExpired = true;
              setLicenseImportResult({
                type: 'error',
                message: `❌ Lizenz ist abgelaufen (${expiresDate.toLocaleDateString('de-DE')})`,
                details: {
                  product_code: productCode,
                  license_type: licenseType,
                  expires_at: expiresAt
                }
              });
              setShowLicenseImportDialog(true);
              setIsImportingLicense(false);
              e.target.value = '';
              return;
            }
          }

          // ===== SICHERHEITSSCHICHT 3: Backend-Verifizierung (wenn verfügbar) =====
          // Versuche Backend-Verbindung wenn verfügbar
          if (backendUrl) {
            try {
              const normalizedUrl = normalizeBackendUrl(backendUrl);
              const importUrl = buildApiUrl(normalizedUrl, '/api/license/import');
              
              console.log('[License-Import] Starte Import:', {
                originalUrl: backendUrl,
                normalizedUrl: normalizedUrl,
                importUrl: importUrl
              });
              
              console.log('[License-Import] Sende Request an Backend:', {
                url: importUrl,
                license_key: licenseKey,
                license_key_length: licenseKey ? licenseKey.length : 0,
                signature: signature,
                signature_length: signature ? signature.length : 0,
                signature_preview: signature ? signature.substring(0, 30) + '...' : 'null',
                signature_type: typeof signature,
                signature_is_empty: !signature || signature.length === 0,
                signature_has_whitespace: signature ? /\s/.test(signature) : false
              });
              
              const response = await fetch(importUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  license_key: licenseKey,
                  signature: signature,
                  device_id: getDeviceId(),
                  device_name: getDeviceName(),
                }),
              });
              
              console.log('[License-Import] Backend-Response-Status:', {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok
              });

              if (response.ok) {
                const data = await response.json();
                
                console.log('[License-Import] Backend-Response:', {
                  valid: data.valid,
                  is_active: data.is_active,
                  message: data.message,
                  product_code: data.product_code,
                  license_type: data.license_type,
                  expires_at: data.expires_at
                });
                
                setLicenseInfo({
                  valid: data.valid,
                  license_key: licenseKey,
                  product_code: data.product_code,
                  license_type: data.license_type,
                  expires_at: data.expires_at,
                  is_active: data.is_active,
                  message: data.message,
                });

                if (data.valid && data.is_active) {
                  setLicenseStatus(true);
                  localStorage.setItem('licenseStatus', 'true');
                  // Speichere Lizenz-Informationen
                  const licenseData = {
                    license_key: licenseKey,
                    product_code: data.product_code,
                    license_type: data.license_type,
                    expires_at: data.expires_at,
                    signature: signature,
                    verified_at: new Date().toISOString(),
                    verified_by: 'backend'
                  };
                  localStorage.setItem('licenseData', JSON.stringify(licenseData));
                  // Aktualisiere aktuelle Session, um Demo-Modus zu entfernen
                  if (selectedSession) {
                    const updatedSession = {
                      ...selectedSession,
                      isDemo: false,
                      sessionName: selectedSession.sessionName?.replace('Demo-Erfassung', 'Messung') || `Messung ${new Date(selectedSession.createdAt).toLocaleDateString('de-DE')} (${selectedSession.deviceName || deviceName})`
                    };
                    setSelectedSession(updatedSession);
                    const updatedSessions = sessions.map(s =>
                      s.id === selectedSession.id ? updatedSession : s
                    );
                    setSessions(updatedSessions);
                    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
                  }
                  setLicenseImportResult({
                    type: 'success',
                    message: data.message,
                    details: {
                      license_key: licenseKey,
                      product_code: data.product_code,
                      license_type: data.license_type,
                      expires_at: data.expires_at,
                      is_active: data.is_active
                    }
                  });
                  setShowLicenseImportDialog(true);
                  setIsImportingLicense(false);
                  e.target.value = '';
                  return;
                } else {
                  // Backend hat valid: false zurückgegeben - zeige Fehlermeldung
                  setLicenseStatus(false);
                  setLicenseImportResult({
                    type: 'error', // WICHTIG: 'error' statt 'warning'
                    message: `❌ ${data.message || 'Lizenz-Verifizierung fehlgeschlagen'}`,
                    details: {
                      license_key: licenseKey,
                      product_code: data.product_code,
                      license_type: data.license_type,
                      expires_at: data.expires_at,
                      is_active: data.is_active,
                      verification_stage: 'backend'
                    }
                  });
                  setShowLicenseImportDialog(true);
                  setIsImportingLicense(false);
                  e.target.value = '';
                  return;
                }
              } else {
                // HTTP-Fehler vom Backend
                const errorText = await response.text().catch(() => 'Unbekannter Fehler');
                console.error('[License-Import] Backend-Fehler:', errorText);
                
                // Wenn Frontend-Signaturprüfung erfolgreich war, erlaube Offline-Import
                if (frontendSignatureValid) {
                  console.warn('[License-Import] Backend nicht erreichbar, aber Frontend-Signaturprüfung erfolgreich. Erlaube Offline-Import.');
                  // Weiter mit Offline-Import (siehe unten)
                } else {
                  // Keine Frontend-Prüfung möglich - Backend erforderlich
                  setLicenseImportResult({
                    type: 'error',
                    message: `❌ Backend-Verbindung fehlgeschlagen. Signaturprüfung nicht möglich. Bitte versuchen Sie es später erneut.`,
                    details: null
                  });
                  setShowLicenseImportDialog(true);
                  setIsImportingLicense(false);
                  e.target.value = '';
                  return;
                }
              }
            } catch (backendError) {
              console.error('[License-Import] Backend-Fehler:', backendError);
              
              // Wenn Frontend-Signaturprüfung erfolgreich war, erlaube Offline-Import
              if (frontendSignatureValid) {
                console.warn('[License-Import] Backend-Verbindung fehlgeschlagen, aber Frontend-Signaturprüfung erfolgreich. Erlaube Offline-Import.');
                // Weiter mit Offline-Import
              } else {
                setLicenseImportResult({
                  type: 'error',
                  message: `❌ Backend-Verbindung fehlgeschlagen. Signaturprüfung nicht möglich.`,
                  details: null
                });
                setShowLicenseImportDialog(true);
                setIsImportingLicense(false);
                e.target.value = '';
                return;
              }
            }
          } else {
            // Kein Backend konfiguriert
            if (!frontendSignatureValid && !frontendVerificationSkipped) {
              // Keine Signaturprüfung möglich - ablehnen
              setLicenseImportResult({
                type: 'error',
                message: '❌ Kein Backend konfiguriert und keine Frontend-Signaturprüfung möglich. Bitte konfigurieren Sie das Backend oder fügen Sie den Public Key hinzu.',
                details: null
              });
              setShowLicenseImportDialog(true);
              setIsImportingLicense(false);
              e.target.value = '';
              return;
            }
          }

          // ===== OFFLINE-IMPORT (nur wenn Frontend-Signaturprüfung erfolgreich ODER übersprungen) =====
          if (!frontendSignatureValid && !frontendVerificationSkipped) {
            // Keine Signaturprüfung möglich - ablehnen
            setLicenseImportResult({
              type: 'error',
              message: '❌ Signaturprüfung nicht möglich. Bitte konfigurieren Sie das Backend oder fügen Sie den Public Key hinzu.',
              details: null
            });
            setShowLicenseImportDialog(true);
            setIsImportingLicense(false);
            e.target.value = '';
            return;
          }
          
          // Wenn Frontend-Verifizierung übersprungen wurde, erlaube nur Backend-Verifizierung
          const hasBackendUrl = Boolean(backendUrl);
          if (frontendVerificationSkipped && !hasBackendUrl) {
            setLicenseImportResult({
              type: 'error',
              message: '❌ Frontend-Signaturprüfung nicht möglich und kein Backend konfiguriert. Bitte konfigurieren Sie das Backend.',
              details: null
            });
            setShowLicenseImportDialog(true);
            setIsImportingLicense(false);
            e.target.value = '';
            return;
          }

          // Lokale Verifizierung erfolgreich (nur wenn Frontend-Signaturprüfung erfolgreich)
          setLicenseInfo({
            valid: true,
            license_key: licenseKey,
            product_code: productCode,
            license_type: licenseType,
            expires_at: expiresAt,
            is_active: !isExpired,
            message: isExpired ? 'Lizenz ist abgelaufen' : 'Lizenz ist gültig (lokal verifiziert, Offline-Modus)',
          });

          if (!isExpired) {
            setLicenseStatus(true);
            localStorage.setItem('licenseStatus', 'true');
            // Speichere Lizenz-Informationen
            const licenseData = {
              license_key: licenseKey,
              product_code: productCode,
              license_type: licenseType,
              expires_at: expiresAt,
              signature: signature,
              verified_at: new Date().toISOString(),
              verified_by: 'frontend'
            };
            localStorage.setItem('licenseData', JSON.stringify(licenseData));
            // Aktualisiere aktuelle Session, um Demo-Modus zu entfernen
            if (selectedSession) {
              const updatedSession = {
                ...selectedSession,
                isDemo: false,
                sessionName: selectedSession.sessionName?.replace('Demo-Erfassung', 'Messung') || `Messung ${new Date(selectedSession.createdAt).toLocaleDateString('de-DE')} (${selectedSession.deviceName || deviceName})`
              };
              setSelectedSession(updatedSession);
              const updatedSessions = sessions.map(s =>
                s.id === selectedSession.id ? updatedSession : s
              );
              setSessions(updatedSessions);
              localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));
            }
            setLicenseImportResult({
              type: 'success',
              message: '✅ Lizenz erfolgreich importiert (lokal verifiziert, Offline-Modus)',
              details: {
                product_code: productCode,
                license_type: licenseType,
                expires_at: expiresAt
              }
            });
            setShowLicenseImportDialog(true);
          } else {
            setLicenseStatus(false);
            setLicenseImportResult({
              type: 'warning',
              message: 'Lizenz ist abgelaufen',
              details: {
                product_code: productCode,
                license_type: licenseType,
                expires_at: expiresAt
              }
            });
            setShowLicenseImportDialog(true);
          }
          setIsImportingLicense(false);
          if (e && e.target) {
            e.target.value = '';
          }
        } catch (error) {
          console.error('Error importing license:', error);
          setLicenseImportResult({
            type: 'error',
            message: 'Fehler beim Importieren der Lizenz: ' + (error.message || 'Unbekannter Fehler'),
            details: null
          });
          setShowLicenseImportDialog(true);
          setLicenseInfo(null);
          setIsImportingLicense(false);
          if (e && e.target) {
            e.target.value = '';
          }
        }
      };
      
      reader.onerror = () => {
        setLicenseImportResult({
          type: 'error',
          message: 'Fehler beim Lesen der Datei.',
          details: null
        });
        setShowLicenseImportDialog(true);
        setIsImportingLicense(false);
        e.target.value = '';
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing license:', error);
      alert(t('alerts.license_import_error', { msg: error.message || 'Unbekannter Fehler' }));
      setIsImportingLicense(false);
      e.target.value = '';
    }
  };

  // XML-Parsing für AblaufDB
  const parseAblaufDbXml = (xmlContent) => {
    try {
      const normalized = (xmlContent || '').trim().toLowerCase();
      if (normalized.startsWith('<!doctype html') || normalized.startsWith('<html') || normalized.includes('<script')) {
        throw new Error('Geladener Inhalt ist HTML statt XML. Bitte Datei über „Auswählen“ laden oder einen gültigen XML-Pfad (z. B. /ZAxml/ABLDEMO.XML) angeben.');
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      // Prüfe auf Parsing-Fehler
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('XML-Parsing-Fehler: ' + parserError.textContent);
      }
      
      const zfnet1 = xmlDoc.querySelector('ZFNET1');
      if (!zfnet1) {
        throw new Error('Ungültiges XML-Format: ZFNET1-Element nicht gefunden');
      }
      
      const kuerzel = Array.from(zfnet1.querySelectorAll('ZFKUERZEL')).map(k => {
        const ablaufkuerzel = k.querySelector('ABLAUFKUERZEL')?.textContent?.trim() || '';
        const kostenstelle = k.querySelector('KOSTENSTELLE')?.textContent?.trim() || '';
        const ablauftext1 = k.querySelector('ABLAUFTEXT1')?.textContent?.trim() || '';
        const ablauftext2 = k.querySelector('ABLAUFTEXT2')?.textContent?.trim() || '';
        const ablauftext3 = k.querySelector('ABLAUFTEXT3')?.textContent?.trim() || '';
        const ablauftext4 = k.querySelector('ABLAUFTEXT4')?.textContent?.trim() || '';
        const zeitart = k.querySelector('ZEITART')?.textContent?.trim() || '';
        
        const beschreibung = [ablauftext1, ablauftext2, ablauftext3, ablauftext4]
          .filter(t => t)
          .join(' ');
        
        // Parse Einflussgrößen
        const einflussgroessen = [];
        const zfeinflussgroessen = k.querySelector('ZFEINFLUSSGROESSEN');
        if (zfeinflussgroessen) {
          Array.from(zfeinflussgroessen.querySelectorAll('ZFEINFLUSSGROESSE')).forEach(eg => {
            const einflussname = eg.querySelector('EINFLUSSNAME')?.textContent?.trim() || '';
            const klassewert = eg.querySelector('KLASSEWERT')?.textContent?.trim() || '';
            const einheit = eg.querySelector('EINHEIT')?.textContent?.trim() || '';
            const messwerte = Array.from(eg.querySelectorAll('MESSWERT')).map(mw => mw.textContent?.trim() || '').filter(mw => mw);
            
            if (einflussname) {
              einflussgroessen.push({
                einflussname,
                klassewert,
                einheit,
                messwerte
              });
            }
          });
        }
        
        return {
          kuerzel: ablaufkuerzel,
          kostenstelle: kostenstelle,
          beschreibung: beschreibung || ablaufkuerzel,
          fullText: [ablauftext1, ablauftext2, ablauftext3, ablauftext4].filter(t => t).join(' '),
          zeitart: zeitart,
          einflussgroessen: einflussgroessen
        };
      }).filter(k => k.kuerzel && k.kuerzel !== '- - - -');
      
      return kuerzel;
    } catch (error) {
      console.error('Fehler beim Parsen der XML-Datei:', error);
      throw error;
    }
  };

  // XML-Parsing für EinflussDB (ALLGEMMW/XML, ZFNET2)
  const parseEinflussDbXml = (xmlContent) => {
    try {
      const normalized = (xmlContent || '').trim().toLowerCase();
      if (normalized.startsWith('<!doctype html') || normalized.startsWith('<html') || normalized.includes('<script')) {
        throw new Error('Geladener Inhalt ist HTML statt XML. Bitte Datei über „Auswählen“ laden oder einen gültigen XML-Pfad (z. B. /ZAxml/ALLGEMMW.XML) angeben.');
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('XML-Parsing-Fehler: ' + parserError.textContent);
      }

      const zfnet2 = xmlDoc.querySelector('ZFNET2');
      if (!zfnet2) {
        throw new Error('Ungültiges XML-Format: ZFNET2-Element nicht gefunden');
      }

      // Kollisionsregel: gleicher EINFLUSSNAME -> letzter Eintrag gewinnt
      const einflussByName = new Map();
      Array.from(zfnet2.querySelectorAll('ZFEINFLUSSGROESSE')).forEach((eg) => {
        const name = eg.querySelector('EINFLUSSNAME')?.textContent?.trim() || '';
        if (!name) return;

        const messwerte = Array.from(eg.querySelectorAll('ZFMESSWERT'))
          .map((mwNode) => {
            const lfdnrRaw = mwNode.querySelector('LFDNR')?.textContent?.trim() || '';
            const messwert = mwNode.querySelector('MESSWERT')?.textContent?.trim() || '';
            const beschreibung = mwNode.querySelector('BESCHREIBUNG')?.textContent?.trim() || '';
            const lfdnr = Number.parseInt(lfdnrRaw, 10);
            if (!messwert) return null;
            return {
              lfdnr: Number.isFinite(lfdnr) ? lfdnr : null,
              messwert,
              ...(beschreibung ? { beschreibung } : {})
            };
          })
          .filter(Boolean);

        einflussByName.set(name, {
          name,
          messwerte
        });
      });

      return Array.from(einflussByName.values());
    } catch (error) {
      console.error('Fehler beim Parsen der EinflussDB-XML-Datei:', error);
      throw error;
    }
  };

  const buildEinflussDbXml = (data) => {
    const escapeXml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

    const body = (Array.isArray(data) ? data : []).map((einfluss) => {
      const name = escapeXml(einfluss?.name || '');
      const rows = (Array.isArray(einfluss?.messwerte) ? einfluss.messwerte : [])
        .filter((mw) => (mw?.messwert || '').toString().trim() !== '')
        .map((mw, idx) => {
          const lfdnr = Number.isFinite(mw?.lfdnr) ? mw.lfdnr : (idx + 1);
          const messwert = escapeXml((mw?.messwert || '').toString().trim());
          const beschreibung = (mw?.beschreibung || '').toString().trim();
          const beschreibungTag = beschreibung ? `<BESCHREIBUNG>${escapeXml(beschreibung)}</BESCHREIBUNG>` : '';
          return `<ZFMESSWERT><LFDNR>${lfdnr}</LFDNR><MESSWERT>${messwert}</MESSWERT>${beschreibungTag}</ZFMESSWERT>`;
        })
        .join('');
      return `<ZFEINFLUSSGROESSE><EINFLUSSNAME>${name}</EINFLUSSNAME>${rows}</ZFEINFLUSSGROESSE>`;
    }).join('');

    return `<?xml version="1.0" encoding="UTF-8"?><ZFNET2>${body}</ZFNET2>`;
  };

  const openEinflussDbEditor = () => {
    if (!einflussDbData || einflussDbData.length === 0) return;
    const first = einflussDbData[0];
    setEditingEinflussName(first.name || '');
    setEditEinflussMesswerte(Array.isArray(first.messwerte) ? [...first.messwerte] : []);
    setEinflussEditorError('');
    setShowEinflussDbEditor(true);
  };

  const selectEinflussForEdit = (name) => {
    const selected = (einflussDbData || []).find((item) => item.name === name);
    setEditingEinflussName(name);
    setEditEinflussMesswerte(Array.isArray(selected?.messwerte) ? [...selected.messwerte] : []);
    setEinflussEditorError('');
  };

  const openAddEinflussDialog = () => {
    setNewEinflussName('');
    setEinflussNameDialogError('');
    setShowEinflussNameDialog(true);
  };

  const addEinflussEntry = () => {
    const newName = newEinflussName.trim();
    if (!newName) {
      setEinflussNameDialogError(t('config.einflussdb.editor_name_required'));
      return;
    }

    const hasCollision = (einflussDbData || []).some((item) => item.name === newName);
    if (hasCollision) {
      setEinflussNameDialogError(t('config.einflussdb.name_collision', { name: newName }));
      return;
    }

    const updatedData = [...(einflussDbData || []), { name: newName, messwerte: [] }];
    setEinflussDbData(updatedData);
    const updatedXml = buildEinflussDbXml(updatedData);
    setEinflussDbXmlContent(updatedXml);
    localStorage.setItem('stoppuhr_einflussdb_xml', updatedXml);
    setEditingEinflussName(newName);
    setEditEinflussMesswerte([]);
    setEinflussEditorError('');
    setShowEinflussNameDialog(false);
  };

  const deleteEinflussEntry = () => {
    if (!editingEinflussName) return;
    setShowEinflussDeleteDialog(true);
  };

  const confirmDeleteEinflussEntry = () => {
    if (!editingEinflussName) {
      setShowEinflussDeleteDialog(false);
      return;
    }

    const updatedData = (einflussDbData || []).filter((item) => item.name !== editingEinflussName);
    setEinflussDbData(updatedData);
    const updatedXml = buildEinflussDbXml(updatedData);
    setEinflussDbXmlContent(updatedXml);
    localStorage.setItem('stoppuhr_einflussdb_xml', updatedXml);
    setShowEinflussDeleteDialog(false);

    if (updatedData.length === 0) {
      setShowEinflussDbEditor(false);
      setEditingEinflussName('');
      setEditEinflussMesswerte([]);
      return;
    }

    const first = updatedData[0];
    setEditingEinflussName(first.name || '');
    setEditEinflussMesswerte(Array.isArray(first.messwerte) ? [...first.messwerte] : []);
  };

  const saveEinflussDbEditor = () => {
    const trimmedName = (editingEinflussName || '').trim();
    if (!trimmedName) {
      setEinflussEditorError(t('config.einflussdb.editor_name_required'));
      return;
    }

    const hasCollision = (einflussDbData || []).some((item) => item.name === trimmedName && item.name !== editingEinflussName);
    if (hasCollision) {
      setEinflussEditorError(t('config.einflussdb.name_collision', { name: trimmedName }));
      return;
    }

    const sanitizedRows = (editEinflussMesswerte || [])
      .map((row, idx) => ({
        lfdnr: Number.isFinite(Number.parseInt(row?.lfdnr, 10)) ? Number.parseInt(row.lfdnr, 10) : idx + 1,
        messwert: (row?.messwert || '').toString().trim(),
        beschreibung: (row?.beschreibung || '').toString().trim()
      }))
      .filter((row) => row.messwert !== '');

    const updatedData = (einflussDbData || []).map((item) => {
      if (item.name !== editingEinflussName) return item;
      return {
        ...item,
        name: trimmedName,
        messwerte: sanitizedRows
      };
    });

    setEinflussDbData(updatedData);
    const updatedXml = buildEinflussDbXml(updatedData);
    setEinflussDbXmlContent(updatedXml);
    localStorage.setItem('stoppuhr_einflussdb_xml', updatedXml);
    setEinflussEditorError('');
    setShowEinflussDbEditor(false);
  };

  // Lade XML-Datei aus ZAxml-Ordner oder Dateisystem
  const loadAblaufDbXml = useCallback(async (filePath = null) => {
    try {
      let xmlContent = '';
      
      if (filePath) {
        // Lade aus Dateisystem
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Datei: ${response.statusText}`);
        }
        xmlContent = await response.text();
      } else if (ablaufDbPath) {
        // Lokale Datei (aus Dateiauswahl): immer aus gespeichertem XML laden
        if (ablaufDbPath.startsWith('[Lokale Datei:')) {
          const savedXml = localStorage.getItem('stoppuhr_ablaufdb_xml');
          if (!savedXml) {
            throw new Error('Für die lokale Datei ist kein gespeicherter XML-Inhalt vorhanden. Bitte erneut über „Auswählen“ laden.');
          }
          xmlContent = savedXml;
        } else {
          // Robustheit: nackte Dateinamen nicht per fetch laden,
          // da der Dev-Server sonst häufig HTML (index.html) zurückliefert.
          const looksLikeBareFilename = !ablaufDbPath.includes('/') && !ablaufDbPath.includes('\\');
          if (looksLikeBareFilename) {
            const savedXml = localStorage.getItem('stoppuhr_ablaufdb_xml');
            if (savedXml) {
              xmlContent = savedXml;
            } else {
              throw new Error('Ungültiger XML-Pfad. Bitte vollständigen Pfad (z. B. /ZAxml/ABLDEMO.XML) angeben oder Datei über „Auswählen“ laden.');
            }
          } else {
            const response = await fetch(ablaufDbPath);
            if (!response.ok) {
              throw new Error(`Fehler beim Laden der Datei: ${response.statusText}`);
            }
            xmlContent = await response.text();
          }
        }
      } else {
        // Versuche Standard-Pfad im ZAxml-Ordner
        const defaultPath = '/ZAxml/ABLDEMO.XML';
        try {
          const response = await fetch(defaultPath);
          if (response.ok) {
            xmlContent = await response.text();
            setAblaufDbPath(defaultPath);
            setAblaufDbName('ABLDEMO.XML');
            localStorage.setItem('stoppuhr_ablaufdb_path', defaultPath);
            localStorage.setItem('stoppuhr_ablaufdb_name', 'ABLDEMO.XML');
          } else {
            throw new Error('Standard-XML-Datei nicht gefunden');
          }
        } catch (e) {
          throw new Error('Bitte wählen Sie eine XML-Datei aus');
        }
      }
      
      const parsedData = parseAblaufDbXml(xmlContent);
      setAblaufDbData(parsedData);
      setAblaufDbXmlContent(xmlContent);
      
      // Speichere XML-Inhalt in localStorage für schnelles Laden beim nächsten Start
      try {
        localStorage.setItem('stoppuhr_ablaufdb_xml', xmlContent);
      } catch (e) {
        console.warn('XML-Inhalt zu groß für localStorage, nur Pfad gespeichert');
      }
      
      return { success: true, data: parsedData, xmlContent };
    } catch (error) {
      console.error('Fehler beim Laden der AblaufDB:', error);
      return { success: false, error: error.message };
    }
  }, [ablaufDbPath]);

  const loadEinflussDbXml = useCallback(async (filePath = null) => {
    try {
      let xmlContent = '';

      if (filePath) {
        const response = await fetch(filePath);
        if (!response.ok) {
          throw new Error(`Fehler beim Laden der Datei: ${response.statusText}`);
        }
        xmlContent = await response.text();
      } else if (einflussDbPath) {
        // Lokale Datei (aus Dateiauswahl): immer aus gespeichertem XML laden
        if (einflussDbPath.startsWith('[Lokale Datei:')) {
          const savedXml = localStorage.getItem('stoppuhr_einflussdb_xml');
          if (!savedXml) {
            throw new Error('Für die lokale Datei ist kein gespeicherter XML-Inhalt vorhanden. Bitte erneut über „Auswählen“ laden.');
          }
          xmlContent = savedXml;
        } else {
          // Robustheit: nackte Dateinamen (z. B. ALLGEMMW.XML) nicht per fetch laden,
          // da der Dev-Server sonst oft HTML (index.html) zurückliefert.
          const looksLikeBareFilename = !einflussDbPath.includes('/') && !einflussDbPath.includes('\\');
          if (looksLikeBareFilename) {
            const savedXml = localStorage.getItem('stoppuhr_einflussdb_xml');
            if (savedXml) {
              xmlContent = savedXml;
            } else {
              throw new Error('Ungültiger XML-Pfad. Bitte vollständigen Pfad (z. B. /ZAxml/ALLGEMMW.XML) angeben oder Datei über „Auswählen“ laden.');
            }
          } else {
            const response = await fetch(einflussDbPath);
            if (!response.ok) {
              throw new Error(`Fehler beim Laden der Datei: ${response.statusText}`);
            }
            xmlContent = await response.text();
          }
        }
      } else {
        throw new Error('Bitte wählen Sie eine XML-Datei aus oder geben Sie einen gültigen XML-Pfad an.');
      }

      const parsedData = parseEinflussDbXml(xmlContent);
      setEinflussDbData(parsedData);
      setEinflussDbXmlContent(xmlContent);

      try {
        localStorage.setItem('stoppuhr_einflussdb_xml', xmlContent);
      } catch (e) {
        console.warn('EinflussDB-XML zu groß für localStorage, nur Pfad gespeichert');
      }

      return { success: true, data: parsedData, xmlContent };
    } catch (error) {
      console.error('Fehler beim Laden der EinflussDB:', error);
      return { success: false, error: error.message };
    }
  }, [einflussDbPath]);

  // Handler für XML-Datei-Auswahl
  const handleAblaufDbFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.xml')) {
      alert(t('alerts.xml_select'));
      return;
    }
    
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const xmlContent = event.target.result;
        const result = parseAblaufDbXml(xmlContent);
        setAblaufDbData(result);
        setAblaufDbXmlContent(xmlContent);
        setAblaufDbName(file.name);
        
        // Speichere Datei-Info
        // Hinweis: Browser erlauben aus Sicherheitsgründen keinen Zugriff auf den vollständigen Dateipfad
        // Wenn webkitRelativePath vorhanden ist, verwende es, sonst nur den Dateinamen
        // Wenn es nur der Dateiname ist (kein webkitRelativePath), speichere einen Hinweis
        const displayPath = file.webkitRelativePath ? file.webkitRelativePath : `[Lokale Datei: ${file.name}]`;
        setAblaufDbPath(displayPath);
        // Für lokale Dateiauswahl den Anzeige-/Markerpfad speichern, damit kein fetch auf Dateinamen erfolgt.
        localStorage.setItem('stoppuhr_ablaufdb_path', displayPath);
        localStorage.setItem('stoppuhr_ablaufdb_name', file.name);
        
        // Speichere XML-Inhalt für schnelles Laden beim nächsten Start
        try {
          localStorage.setItem('stoppuhr_ablaufdb_xml', xmlContent);
        } catch (e) {
          console.warn('XML-Inhalt zu groß für localStorage');
        }
        
        setShowAblaufDbDialog(false);
      };
      reader.onerror = () => {
        alert(t('alerts.xml_read_error'));
      };
      reader.readAsText(file);
    } catch (error) {
      alert(t('alerts.xml_load_error', { msg: error.message }));
    }
  };

  const handleEinflussDbFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xml')) {
      alert(t('alerts.xml_select'));
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const xmlContent = event.target.result;
        const result = parseEinflussDbXml(xmlContent);
        setEinflussDbData(result);
        setEinflussDbXmlContent(xmlContent);
        setEinflussDbName(file.name);

        const filePath = file.webkitRelativePath || file.name;
        const displayPath = file.webkitRelativePath ? file.webkitRelativePath : `[Lokale Datei: ${file.name}]`;
        setEinflussDbPath(displayPath);
        // Für lokale Dateiauswahl den Anzeige-/Markerpfad speichern, damit kein fetch auf Dateinamen erfolgt.
        localStorage.setItem('stoppuhr_einflussdb_path', displayPath);
        localStorage.setItem('stoppuhr_einflussdb_name', file.name);

        try {
          localStorage.setItem('stoppuhr_einflussdb_xml', xmlContent);
        } catch (e) {
          console.warn('EinflussDB-XML zu groß für localStorage');
        }
      };
      reader.onerror = () => {
        alert(t('alerts.xml_read_error'));
      };
      reader.readAsText(file);
    } catch (error) {
      alert(t('alerts.xml_load_error', { msg: error.message }));
    }
  };

  const handleAblaufDbUnload = () => {
    if (!window.confirm(t('alerts.confirm.ablaufdb_unload'))) return;
    setAblaufDbData(null);
    setAblaufDbXmlContent('');
    try { localStorage.removeItem('stoppuhr_ablaufdb_xml'); } catch (e) { /* ignore */ }
  };

  const handleEinflussDbUnload = () => {
    setShowEinflussUnloadDialog(true);
  };

  const confirmEinflussDbUnload = () => {
    setEinflussDbData(null);
    setEinflussDbXmlContent('');
    try { localStorage.removeItem('stoppuhr_einflussdb_xml'); } catch (e) { /* ignore */ }
    setShowEinflussUnloadDialog(false);
  };

  // Lade AblaufDB beim Start, falls gespeichert
  useEffect(() => {
    if (!ablaufDbData && isLoggedIn) {
      // Versuche zuerst, gespeicherten XML-Inhalt zu laden (schneller, funktioniert auch bei lokalen Dateien)
      const savedXml = localStorage.getItem('stoppuhr_ablaufdb_xml');
      if (savedXml) {
        try {
          const parsedData = parseAblaufDbXml(savedXml);
          setAblaufDbData(parsedData);
          setAblaufDbXmlContent(savedXml);
          console.log('✅ AblaufDB aus localStorage geladen');
          return;
        } catch (e) {
          console.warn('Fehler beim Parsen der gespeicherten AblaufDB:', e);
          // Fallback: Versuche aus Pfad zu laden
        }
      }
      
      // Fallback: Lade aus gespeichertem Pfad (nur wenn kein [Lokale Datei:]-Marker vorhanden)
      if (ablaufDbPath && !ablaufDbPath.startsWith('[Lokale Datei:')) {
        loadAblaufDbXml().catch(err => {
          console.warn('Fehler beim automatischen Laden der AblaufDB:', err);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (!einflussDbData && isLoggedIn) {
      const savedXml = localStorage.getItem('stoppuhr_einflussdb_xml');
      if (savedXml) {
        try {
          const parsedData = parseEinflussDbXml(savedXml);
          setEinflussDbData(parsedData);
          setEinflussDbXmlContent(savedXml);
          console.log('✅ EinflussDB aus localStorage geladen');
          return;
        } catch (e) {
          console.warn('Fehler beim Parsen der gespeicherten EinflussDB:', e);
        }
      }

      if (einflussDbPath && !einflussDbPath.startsWith('[Lokale Datei:')) {
        loadEinflussDbXml().catch(err => {
          console.warn('Fehler beim automatischen Laden der EinflussDB:', err);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Login
  const handleLogin = async () => {
    // Demo-Modus wenn Email und Backend URL leer sind
    if (!email.trim() && !backendUrl.trim()) {
      const demoSession = {
        id: 'demo_' + Date.now().toString(),
        email: '',
        deviceName: deviceName,
        createdAt: new Date().toISOString(),
        resumeNumber: 0, // Startet bei 0, wird beim ersten Start auf 1 erhöht
        measurements: [],
        uploaded: false,
        isDemo: true,
        sessionName: `Demo-Erfassung (${deviceName})`
      };
      setSessions([demoSession]);
      setSelectedSession(demoSession);
      setIsLoggedIn(true);
      setLicenseStatus(false);
      localStorage.setItem(STORAGE_KEY_EMAIL, '');
      localStorage.setItem(STORAGE_KEY_BACKEND, '');
      localStorage.removeItem('userToken');
      localStorage.removeItem(STORAGE_KEY_USER_ID);
      setUserId(null);
      return;
    }

    if (!email || !backendUrl) {
      alert(t('alerts.demo_mode_email'));
      return;
    }

    // Speichere Email und Backend URL
    localStorage.setItem(STORAGE_KEY_EMAIL, email);
    localStorage.setItem(STORAGE_KEY_BACKEND, backendUrl);

    // User-Login für Sessions vom Server (Token + userId)
    const normalizedUrl = (backendUrl || '').replace(/\/+$/, '');
    const loginUrl = `${normalizedUrl}/api/user/login`;
    try {
      const loginRes = await fetch(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (loginRes.ok) {
        const data = await loginRes.json();
        if (data.token && data.user && data.user.id != null) {
          localStorage.setItem('userToken', data.token);
          localStorage.setItem(STORAGE_KEY_USER_ID, String(data.user.id));
          setUserId(data.user.id);
        }
      }
      // Bei Fehler: kein Token/userId, Backend-Reiter bleibt deaktiviert
    } catch (e) {
      console.warn('User-Login fehlgeschlagen oder Backend nicht erreichbar:', e);
    }

    // License prüfen (mit Fehlerbehandlung)
    try {
      await checkLicense();
    } catch (error) {
      console.error('License check error:', error);
      // Bei Fehler: Weiter mit Demo-Modus
      setLicenseStatus(false);
      localStorage.setItem('licenseStatus', 'false');
    }

    // Sessions laden
    const savedSessions = localStorage.getItem(STORAGE_KEY_SESSIONS);
    if (savedSessions) {
      try {
        setSessions(JSON.parse(savedSessions));
      } catch (e) {
        console.error('Error loading sessions:', e);
      }
    }
    
    setIsLoggedIn(true);
  };

  // Neue Session starten
  const handleNewSession = () => {
    // Demo-Modus nur wenn Email und Backend URL leer sind (echte Demo)
    // ODER wenn keine Lizenz vorhanden ist UND Email/Backend vorhanden sind (gezwungener Demo-Modus)
    const isRealDemo = !email.trim() && !backendUrl.trim();
    const isDemo = isRealDemo || (licenseStatus === false && (email.trim() || backendUrl.trim()));
    const sessionName = isDemo 
      ? `Demo-Erfassung (${deviceName})`
      : `Messung ${new Date().toLocaleDateString('de-DE')} (${deviceName})`;
    
    const now = new Date().toISOString();
    const newSession = {
      id: Date.now().toString(),
      email: email || '',
      deviceName,
      createdAt: now,
      lastUpdated: now, // Wichtig: lastUpdated setzen, damit es oben bleibt
      resumeNumber: 0, // Startet bei 0, wird beim ersten Start auf 1 erhöht
      measurements: [],
      uploaded: false,
      isDemo: isDemo,
      sessionName: sessionName
    };
    // Neue Session oben einfügen und auf MAX_SESSIONS begrenzen
    const updatedSessions = [newSession, ...sessions];
    const limitedSessions = limitAndSortSessions(updatedSessions);
    setSessions(limitedSessions);
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    setSelectedSession(newSession);
    setResumeNumber(0); // Startet bei 0, wird beim ersten Start auf 1 erhöht
    setMeasurements([]);
    setStartTime(null);
    setCurrentTime(0);
    setIsRunning(false);
  };

  // Neuberechnung der Einzelzeiten (ausgeklammerte Messpunkte werden übersprungen)
  const recalculateIndividualTimes = (measurementsList) => {
    if (!measurementsList || measurementsList.length === 0) return measurementsList;
    
    // Verwende die Reihenfolge im Array (nicht nach number sortieren, da Drag & Drop die Reihenfolge ändert)
    // Die Reihenfolge im Array ist die korrekte Reihenfolge nach Drag & Drop
    const sorted = [...measurementsList]; // Keine Sortierung mehr, behalte Array-Reihenfolge
    
    let lastNonExcludedProgressTime = 0;
    let lastResumeNumber = null; // Track letzte WA-Nr
    
    const result = sorted.map((m) => {
      const currentResumeNumber = m.resumeNumber || 1;
      
      // Wenn ausgeklammert, behalte die ursprüngliche Einzelzeit (wird in Auswertung ignoriert)
      // Aber aktualisiere lastNonExcludedProgressTime NICHT
      if (m.excluded) {
        return m;
      }
      
      // Wenn WA-Nr das erste Mal auftaucht (oder sich geändert hat), Einzelzeit = Fortschrittszeit
      const isFirstInResume = lastResumeNumber === null || currentResumeNumber !== lastResumeNumber;
      
      // Berechne Einzelzeit basierend auf dem letzten nicht-ausgeklammerten Messpunkt
      // Oder wenn es die erste WA-Nr ist (auch wenn vorherige ausgeklammert wurden)
      // Notizen werden auch berechnet, wenn sie eine Fortschrittszeit haben
      // WICHTIG: Runde das Ergebnis, um Rundungsfehler bei der Zeitberechnung zu vermeiden
      const rawIndividualTime = (lastNonExcludedProgressTime === 0 || isFirstInResume)
        ? m.progressTime 
        : m.progressTime - lastNonExcludedProgressTime;
      const individualTime = Math.round(rawIndividualTime);
      // Aktualisiere lastNonExcludedProgressTime für den nächsten Messpunkt
      // Auch für Notizen, wenn sie eine Fortschrittszeit haben
      const oldLastNonExcludedProgressTime = lastNonExcludedProgressTime;
      const oldLastResumeNumber = lastResumeNumber;
      lastNonExcludedProgressTime = m.progressTime;
      lastResumeNumber = currentResumeNumber;
      const vorgabeTime = calculateVorgabeTime(Math.max(0, individualTime), m.grade || 0);
      return {
        ...m,
        individualTime: Math.max(0, individualTime), // Stelle sicher, dass Einzelzeit nicht negativ ist
        vorgabeTime: vorgabeTime // Vorgabezeit berechnen und speichern
      };
    });

    return result;
  };

  // Session fortsetzen
  const handleResumeSession = async (session) => {
    
    // Session-Name sicherstellen
    if (!session.sessionName) {
      const isDemo = session.isDemo || (!session.email && !backendUrl);
      session.sessionName = isDemo 
        ? `Demo-Erfassung (${session.deviceName || deviceName})`
        : `Messung ${new Date(session.createdAt).toLocaleDateString('de-DE')} (${session.deviceName || deviceName})`;
    }
    
    // WA-Nr zu allen Messpunkten hinzufügen, falls fehlt
    // WICHTIG: resumeNumber in Messungen sollte immer >= 1 sein (da Messungen nur nach Start erstellt werden)
    const updatedMeasurements = (session.measurements || []).map(m => ({
      ...m,
      resumeNumber: m.resumeNumber || session.resumeNumber || 1
    }));
    
    // Beim Laden: resumeNumber aus Session übernehmen (kann 0 sein für neue Sessions ohne Messungen)
    // Oder höchste WA-Nr aus Messungen verwenden (wenn Messungen vorhanden sind)
    const maxResumeNumber = updatedMeasurements.length > 0
      ? Math.max(...updatedMeasurements.map(m => m.resumeNumber || 1))
      : (session.resumeNumber ?? 0); // Verwende ?? statt ||, damit 0 nicht zu 1 wird
    
    // Neuberechnung der Einzelzeiten beim Laden (falls ausgeklammerte Messpunkte vorhanden)
    const recalculatedMeasurements = recalculateIndividualTimes(updatedMeasurements);    debugLog('App.jsx:4815', 'handleResumeSession: Setting session and stopping clock', {sessionId:session.id,isRunning:false}, 'E');    setSelectedSession(session);
    setResumeNumber(maxResumeNumber);
    setMeasurements(recalculatedMeasurements);
    setStartTime(null);
    setCurrentTime(0);
    setIsRunning(false);
    setVorderseiteExpanded(false); // Vorderseite standardmäßig eingeklappt
    
    // WICHTIG: Prüfe zuerst, ob die Session bereits Vorderseite-Daten enthält
    // Diese sollten verwendet werden, wenn das Backend offline ist
    if (session.vorderseiteData) {
      setVorderseiteData(session.vorderseiteData);
      
      // Aktualisiere Nachkommastellen-State aus vorderseiteData
      if (session.vorderseiteData) {
        if (session.vorderseiteData.decimal_places_vorderseite !== undefined) {
          setDecimalPlacesVorderseite(session.vorderseiteData.decimal_places_vorderseite);
        }
        if (session.vorderseiteData.decimal_places_sollzeiten !== undefined) {
          setDecimalPlacesSollzeiten(session.vorderseiteData.decimal_places_sollzeiten);
        }
      }
    } else if (!vorderseiteData) {
      // Initialisiere Vorderseite-Daten, falls noch nicht vorhanden
      setVorderseiteData(getDefaultVorderseiteData());
    }
    
    // Versuche zuerst, Vorderseite-Daten aus Temp-Dateien zu laden
    const tempVorderseiteData = await loadVorderseiteFromTempFiles();
    
    // Lade Vorderseite-Daten (entweder aus Temp oder Backend)
    // WICHTIG: Nur laden, wenn noch keine Daten vorhanden sind (aus Session oder Temp)
    let loadedVorderseiteData = null;
    if (!tempVorderseiteData && !session.vorderseiteData) {
      loadedVorderseiteData = await loadVorderseite(session.id);
    } else if (tempVorderseiteData) {
      loadedVorderseiteData = tempVorderseiteData;
    } else {
      loadedVorderseiteData = session.vorderseiteData;
    }
    
    // Stelle Kürzel und EA-Werte wieder her (pro Session)
    const storageKey = `stoppuhr_session_${session.id}_kuerzel_state`;
    try {
      const savedState = localStorage.getItem(storageKey);
      if (savedState) {
        const state = JSON.parse(savedState);
        if (state.selectedKuerzel) {
          setSelectedKuerzel(state.selectedKuerzel);
        }
        if (state.einflussgroessenValues) {
          setEinflussgroessenValues(state.einflussgroessenValues);
        }
      }
    } catch (error) {
      console.error('[Portrait] Fehler beim Laden der Kürzel/EA-Werte:', error);
    }
    
    // Stelle activity, note und timeType wieder her (pro Session)
    const activityNoteKey = `stoppuhr_session_${session.id}_activity_note`;
    try {
      const savedActivityNote = localStorage.getItem(activityNoteKey);
      if (savedActivityNote) {
        const state = JSON.parse(savedActivityNote);
        if (state.activity !== undefined) {
          setActivity(state.activity || '');
        }
        if (state.note !== undefined) {
          setNote(state.note || '');
        }
        if (state.timeType !== undefined) {
          setTimeType(state.timeType || 'thb');
        }
      }
    } catch (error) {
      console.error('[App] Fehler beim Laden von activity/note/timeType:', error);
    }
    
    // Stelle focusedInput wieder her (pro Session)
    const focusedInputKey = `stoppuhr_session_${session.id}_focused_input`;
    try {
      const savedFocusedInput = localStorage.getItem(focusedInputKey);
      if (savedFocusedInput) {
        const focusedInputState = JSON.parse(savedFocusedInput);
        setFocusedInput(focusedInputState || {});
      }
    } catch (error) {
      console.error('[App] Fehler beim Laden von focusedInput:', error);
    }
    
    // Prüfe, ob wiederaufnahmen ergänzt werden müssen
    // Verwende setTimeout, damit vorderseiteData State aktualisiert wurde
    setTimeout(() => {
      checkAndShowWiederaufnahmenDialog();
    }, 500);
    
    // Lade Fotos vom Backend, falls vorhanden
    debugLog('App.jsx:2972', 'handleResumeSession: Checking photo load conditions', {sessionId:session.id,backendUrl:!!backendUrl,isDemo:session.isDemo,willLoadPhotos:!!(backendUrl && !session.isDemo)}, 'A');
    if (backendUrl && !session.isDemo) {
      await loadPhotosFromBackend(session.id, backendUrl);
    } else {
      debugLog('App.jsx:2976', 'handleResumeSession: Skipping photo load', {sessionId:session.id,reason:!backendUrl?'no backendUrl':session.isDemo?'session is demo':'unknown'}, 'A');
    }
    
    // Session aus der Liste entfernen und oben einfügen (neueste oben)
    const sessionWithoutCurrent = sessions.filter(s => s.id !== session.id);
    const updatedSession = { 
      ...session, 
      resumeNumber: maxResumeNumber, 
      sessionName: session.sessionName, 
      measurements: updatedMeasurements,
      lastUpdated: new Date().toISOString() // Aktualisiere lastUpdated für Sortierung
    };
    // Session oben einfügen und auf MAX_SESSIONS begrenzen
    const updatedSessions = [updatedSession, ...sessionWithoutCurrent];
    const limitedSessions = limitAndSortSessions(updatedSessions);
    setSessions(limitedSessions);
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
  };

  // Backend-Sessions laden (für Reiter Backend). optionalPage: 1-basiert; wenn gesetzt, wird diese Seite geladen und backendSessionsPage danach gesetzt.
  const fetchBackendSessions = useCallback(async (optionalPage) => {
    if (!userId || !backendUrl) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    const page = optionalPage ?? backendSessionsPage;
    if (backendSessionsAbortRef.current) {
      backendSessionsAbortRef.current.abort();
    }
    const controller = new AbortController();
    backendSessionsAbortRef.current = controller;
    setBackendSessionsLoading(true);
    setBackendSessions([]);
    try {
      const normalizedUrl = (backendUrl || '').replace(/\/+$/, '');
      const params = new URLSearchParams();
      if (backendSearch.trim()) params.set('search', backendSearch.trim());
      params.set('own_only', backendOwnOnly ? 'true' : 'false');
      params.set('limit', String(BACKEND_SESSIONS_PAGE_SIZE));
      params.set('offset', String((page - 1) * BACKEND_SESSIONS_PAGE_SIZE));
      const url = `${normalizedUrl}/api/user/${userId}/sessions?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          setBackendSessions([]);
          setBackendSessionsTotal(0);
          return;
        }
        throw new Error(res.statusText);
      }
      const data = await res.json();
      setBackendSessions(data.sessions || []);
      setBackendSessionsTotal(data.total ?? 0);
      if (optionalPage != null) setBackendSessionsPage(optionalPage);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.warn('Backend-Sessions laden fehlgeschlagen:', e);
      setBackendSessions([]);
      setBackendSessionsTotal(0);
    } finally {
      if (backendSessionsAbortRef.current === controller) {
        backendSessionsAbortRef.current = null;
      }
      setBackendSessionsLoading(false);
    }
  }, [userId, backendUrl, backendSearch, backendOwnOnly, backendSessionsPage]);

  const cancelBackendSessionsFetch = useCallback(() => {
    if (backendSessionsAbortRef.current) {
      backendSessionsAbortRef.current.abort();
    }
  }, []);

  // Backend-Session vollständig laden und fortsetzen
  const handleResumeBackendSession = useCallback(async (sessionInfo) => {
    if (!userId || !backendUrl) return;
    const token = localStorage.getItem('userToken');
    if (!token) return;
    if (backendResumeAbortRef.current) {
      backendResumeAbortRef.current.abort();
    }
    const controller = new AbortController();
    backendResumeAbortRef.current = controller;
    setBackendResumeLoading(true);
    try {
      const normalizedUrl = (backendUrl || '').replace(/\/+$/, '');
      const measurementsUrl = `${normalizedUrl}/api/user/${userId}/sessions/${sessionInfo.id}/measurements`;
      const measurementsRes = await fetch(measurementsUrl, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!measurementsRes.ok) {
        if (measurementsRes.status === 403) {
          alert(t('alerts.session_no_access'));
          return;
        }
        throw new Error(measurementsRes.statusText);
      }
      const measurementsData = await measurementsRes.json();
      let vorderseiteData = measurementsData.session?.vorderseite_data ?? null;
      if (!vorderseiteData && measurementsData.session?.id) {
        try {
          const vRes = await fetch(`${normalizedUrl}/api/user/${userId}/sessions/${sessionInfo.id}/vorderseite`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
          if (vRes.ok) {
            const vData = await vRes.json();
            vorderseiteData = vData.vorderseite_data ?? null;
          }
        } catch (_) { /* optional */ }
      }
      const s = measurementsData.session;
      const measurements = (measurementsData.measurements || []).map((m) => ({
        ...m,
        number: m.number,
        grade: m.grade,
        progressTime: m.progress_time ?? m.progressTime,
        individualTime: m.individual_time ?? m.individualTime,
        vorgabeTime: m.vorgabe_time ?? m.vorgabeTime,
        activity: m.activity,
        timeType: m.time_type ?? m.timeType,
        note: m.note,
        isNote: m.is_note ?? m.isNote,
        isInterruption: m.is_interruption ?? m.isInterruption,
        excluded: m.excluded,
        resumeNumber: m.resume_number ?? s?.resume_number ?? 1,
        timestamp: m.timestamp || (s?.updated_at ? new Date(s.updated_at).toISOString() : null),
        einflussgroessen: m.einflussgroessen ?? {},
      }));
      const mappedSession = {
        id: s.id,
        sessionName: s.session_name || `Messung ${s.created_at || ''}`,
        resumeNumber: s.resume_number ?? 1,
        measurements,
        vorderseiteData,
        createdAt: s.created_at,
        lastUpdated: s.updated_at,
        deviceName: s.device_name,
        isDemo: false,
        uploaded: true,
      };
      await handleResumeSession(mappedSession);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('Backend-Session laden fehlgeschlagen:', e);
      alert(t('alerts.session_load_error'));
    } finally {
      if (backendResumeAbortRef.current === controller) {
        backendResumeAbortRef.current = null;
      }
      setBackendResumeLoading(false);
    }
  }, [userId, backendUrl, handleResumeSession]);

  const cancelBackendResume = useCallback(() => {
    if (backendResumeAbortRef.current) {
      backendResumeAbortRef.current.abort();
    }
  }, []);

  // Stoppuhr Controls
  const handleStart = () => {    debugLog('App.jsx:5063', 'handleStart called', {hasSelectedSession:!!selectedSession,isCheckingUpdateResume,showUpdateResumeDialog,measurementsCount:measurements.length,currentResumeNumber:resumeNumber,selectedSessionResumeNumber:selectedSession?.resumeNumber,isHandlingStart:isHandlingStartRef.current}, 'A');    if (!selectedSession) {      debugLog('App.jsx:5068', 'handleStart: No selectedSession, returning', {}, 'A');      return;
    }
    
    // FIX: Verhindere gleichzeitige Ausführung von handleStart
    if (isHandlingStartRef.current) {      debugLog('App.jsx:3681', 'handleStart: Already handling, skipping', {reason:'isHandlingStartRef is true'}, 'A');      return; // Überspringe, wenn bereits ausgeführt wird
    }
    isHandlingStartRef.current = true; // Setze Flag SOFORT
    
    try {
      // Stelle sicher, dass vorderseiteData initialisiert ist
      if (!vorderseiteData) {
        setVorderseiteData(getDefaultVorderseiteData());
      }
      
      // Beim Starten der Uhr: WA-Nr um 1 erhöhen
      // resumeNumber startet bei 0, wird beim ersten Start auf 1 erhöht, dann 2, 3, etc.
      const newResumeNumber = (resumeNumber || 0) + 1;
      
      // FIX: Verhindere doppelte Aufrufe mit derselben WA-Nr innerhalb von 500ms
      const currentTime = Date.now();
      if (lastStartResumeNumberRef.current && lastStartResumeNumberRef.current.resumeNumber === newResumeNumber && (currentTime - (lastStartResumeNumberRef.current.timestamp || 0)) < 500) {        debugLog('App.jsx:3694', 'handleStart: Skipping duplicate call', {newResumeNumber,lastResumeNumber:lastStartResumeNumberRef.current?.resumeNumber,timeSinceLastCall:currentTime - (lastStartResumeNumberRef.current?.timestamp || 0),reason:'Duplicate call within 500ms'}, 'A');        isHandlingStartRef.current = false; // Reset Flag
        return; // Überspringe doppelten Aufruf
      }
      // Setze Ref SOFORT, bevor setVorderseiteData aufgerufen wird
      lastStartResumeNumberRef.current = { resumeNumber: newResumeNumber, timestamp: currentTime };      debugLog('App.jsx:1425', 'Calculated newResumeNumber', {newResumeNumber,measurementsCount:measurements.length,measurementsResumeNumbers:measurements.map(m=>m.resumeNumber),selectedSessionResumeNumber:selectedSession.resumeNumber}, 'C');
      debugLog('App.jsx:1430', 'Setting newResumeNumber', {newResumeNumber,oldResumeNumber:resumeNumber}, 'B');
      // Aktualisiere Session mit neuer WA-Nr
      const updatedSession = {
        ...selectedSession,
        resumeNumber: newResumeNumber
      };
      setSelectedSession(updatedSession);
      setResumeNumber(newResumeNumber);
      
      // Aktualisiere Sessions-Liste
      const updatedSessions = sessions.map(s =>
        s.id === selectedSession.id ? updatedSession : s
      );
      setSessions(updatedSessions);
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(updatedSessions));      debugLog('App.jsx:1445', 'handleStart completed', {newResumeNumber,updatedSessionResumeNumber:updatedSession.resumeNumber}, 'B');      const now = Date.now();      debugLog('App.jsx:5007', 'handleStart: Setting isRunning=true and startTime', {startTime:now}, 'E');      setStartTime(now);
      setLastMeasurementTime(0);
      setIsRunning(true);
      
      // Speichere Startzeit in Vorderseite
      const nowDate = new Date();
      const dateStr = nowDate.toLocaleDateString('de-DE');
      const timeStr = nowDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      
      // FIX: Erhöhe Zähler atomar, um zu verfolgen, ob der erste Aufruf bereits läuft
      // Der erste Aufruf erhöht den Zähler von N auf N+1, der zweite Aufruf erhöht ihn von N+1 auf N+2
      const currentCounter = wiederaufnahmeAddCounterRef.current;
      const nextCounter = currentCounter + 1;
      wiederaufnahmeAddCounterRef.current = nextCounter;
      
      // FIX: Setze pendingWiederaufnahmeRef nur, wenn es noch nicht gesetzt ist (erster Aufruf)
      // Der zweite Aufruf überschreibt pendingWiederaufnahmeRef nicht, sodass der erste Aufruf im Callback erkannt werden kann
      if (!pendingWiederaufnahmeRef.current || 
          pendingWiederaufnahmeRef.current.nummer !== newResumeNumber ||
          pendingWiederaufnahmeRef.current.datum !== dateStr ||
          pendingWiederaufnahmeRef.current.beginnzeit !== timeStr) {
        pendingWiederaufnahmeRef.current = { nummer: newResumeNumber, datum: dateStr, beginnzeit: timeStr, counter: nextCounter };
      }      debugLog('App.jsx:3723', 'handleStart: Before updating wiederaufnahmen', {newResumeNumber,dateStr,timeStr,nowDateISO:nowDate.toISOString(),pendingWiederaufnahme:pendingWiederaufnahmeRef.current,counter:currentCounter}, 'A');      // Stelle sicher, dass vorderseiteData initialisiert ist, bevor wir es aktualisieren
      setVorderseiteData(prev => {
      // Wenn prev null ist, erstelle neue Daten
      const currentData = prev || getDefaultVorderseiteData();
      const newData = JSON.parse(JSON.stringify(currentData));      debugLog('App.jsx:3732', 'handleStart: Current wiederaufnahmen state', {prevExists:!!prev,currentWiederaufnahmen:currentData.wiederaufnahmen,currentWiederaufnahmenIsArray:Array.isArray(currentData.wiederaufnahmen),currentWiederaufnahmenLength:currentData.wiederaufnahmen?.length}, 'A');      // Stelle sicher, dass wiederaufnahmen ein Array ist
      if (!newData.wiederaufnahmen || !Array.isArray(newData.wiederaufnahmen)) {
        newData.wiederaufnahmen = [];
      }
      
      // Prüfe, ob diese WA-Nr bereits existiert
      const existingIndex = newData.wiederaufnahmen.findIndex(w => w.nummer === newResumeNumber);
      debugLog('App.jsx:3740', 'handleStart: Checking existing wiederaufnahme', {newResumeNumber,existingIndex,existingWiederaufnahmen:newData.wiederaufnahmen.map(w=>({nummer:w.nummer,beginnzeit:w.beginnzeit,endzeit:w.endzeit,datum:w.datum})),timeStr}, 'A');
      // FIX: Verhindere Duplikate - wenn die WA-Nr bereits existiert und bereits eine Beginzeit hat, überspringe das Update
      if (existingIndex >= 0) {
        // WA-Nr existiert bereits: Aktualisiere Beginzeit (nur wenn noch nicht gesetzt)
        const existingEntry = newData.wiederaufnahmen[existingIndex];
        // FIX: Verhindere Duplikate - wenn beginnzeit bereits gesetzt ist, überspringe das Update
        // (unabhängig von der Zeit, um Race-Conditions zu vermeiden)
        if (existingEntry.beginnzeit && existingEntry.beginnzeit.trim()) {          debugLog('App.jsx:3759', 'handleStart: Skipping duplicate update', {newResumeNumber,existingIndex,existingEntry,timeStr,reason:'Beginzeit already set'}, 'A');          return newData; // Keine Änderung, gib den aktuellen State zurück
        }
        
        const beforeUpdate = JSON.parse(JSON.stringify(existingEntry));
        if (!existingEntry.beginnzeit) {
        existingEntry.beginnzeit = timeStr;
        }
        if (!existingEntry.datum) {
          existingEntry.datum = dateStr;
        }        debugLog('App.jsx:3750', 'handleStart: Updated existing wiederaufnahme', {newResumeNumber,existingIndex,beforeUpdate,afterUpdate:existingEntry,timeStr,dateStr}, 'A');      } else {
        // WA-Nr existiert noch nicht: Prüfe, ob diese Kombination bereits in der Liste existiert
        // (Race-Condition: Beide Aufrufe lesen den alten State, aber einer hat bereits eine wiederaufnahme hinzugefügt)
        const duplicateByPending = newData.wiederaufnahmen.find(w => 
          w.nummer === newResumeNumber && 
          w.datum === dateStr && 
          w.beginnzeit === timeStr
        );
        
        // Wenn die Kombination bereits in der Liste existiert, überspringe das Update (zweiter Aufruf)
        if (duplicateByPending) {          debugLog('App.jsx:3760', 'handleStart: Skipping duplicate (already in list)', {newResumeNumber,dateStr,timeStr,duplicateByPending,pendingWiederaufnahme:pendingWiederaufnahmeRef.current,reason:'Duplicate already in list'}, 'A');          return newData; // Keine Änderung, gib den aktuellen State zurück
        }
        
        // FIX: Prüfe, ob pendingWiederaufnahmeRef gesetzt ist und ob wir der zweite Aufruf sind
        // Der erste Aufruf setzt pendingWiederaufnahmeRef mit counter = N+1
        // Der zweite Aufruf überschreibt pendingWiederaufnahmeRef nicht, sondern erhöht nur den Zähler auf N+2
        // Wenn currentCounter > pendingCounter, bedeutet das, dass wir der zweite Aufruf sind
        if (pendingWiederaufnahmeRef.current && 
            pendingWiederaufnahmeRef.current.nummer === newResumeNumber &&
            pendingWiederaufnahmeRef.current.datum === dateStr &&
            pendingWiederaufnahmeRef.current.beginnzeit === timeStr) {
          const pendingCounter = pendingWiederaufnahmeRef.current.counter || 0;
          const currentCounter = wiederaufnahmeAddCounterRef.current; // Aktueller Zähler (nach dem Inkrement in handleStart)          debugLog('App.jsx:3760', 'handleStart: Checking counter for duplicate', {newResumeNumber,dateStr,timeStr,pendingCounter,currentCounter,isSecondCall:currentCounter > pendingCounter,pendingWiederaufnahme:pendingWiederaufnahmeRef.current,reason:'Checking if second call by counter'}, 'A');          // Wenn der aktuelle Zähler größer ist als der pending Zähler, bedeutet das, dass wir der zweite Aufruf sind
          // (Der erste Aufruf hat pendingCounter = N+1, currentCounter = N+1
          //  Der zweite Aufruf hat pendingCounter = N+1 (vom ersten, nicht überschrieben), currentCounter = N+2)
          if (currentCounter > pendingCounter) {            debugLog('App.jsx:3760', 'handleStart: Skipping duplicate by pending (second call)', {newResumeNumber,dateStr,timeStr,pendingCounter,currentCounter,pendingWiederaufnahme:pendingWiederaufnahmeRef.current,reason:'Second call detected by counter'}, 'A');            return newData; // Keine Änderung, gib den aktuellen State zurück
          }
          // Wenn wir der erste Aufruf sind (currentCounter === pendingCounter), füge die wiederaufnahme hinzu
        }
        
        // Wenn die Kombination nicht in der Liste existiert, füge sie hinzu (erster oder einziger Aufruf)
        
        // WA-Nr existiert noch nicht: Füge neuen Eintrag hinzu
        const newEntry = {
          nummer: newResumeNumber,
          datum: dateStr,
          beginnzeit: timeStr,
          endzeit: '',
          dauer: ''
        };
        newData.wiederaufnahmen.push(newEntry);
        debugLog('App.jsx:3760', 'handleStart: Added new wiederaufnahme', {newEntry,newResumeNumber,dateStr,timeStr,allWiederaufnahmen:newData.wiederaufnahmen.map(w=>({nummer:w.nummer,beginnzeit:w.beginnzeit,endzeit:w.endzeit,datum:w.datum})),pendingWiederaufnahme:pendingWiederaufnahmeRef.current}, 'A');
      }
      
      console.log('[Vorderseite] Wiederaufnahme hinzugefügt/aktualisiert:', {
        nummer: newResumeNumber,
        datum: dateStr,
        beginnzeit: timeStr,
        wiederaufnahmenCount: newData.wiederaufnahmen.length,
        wiederaufnahmen: newData.wiederaufnahmen
      });      debugLog('App.jsx:3775', 'handleStart: Final wiederaufnahmen state', {wiederaufnahmenCount:newData.wiederaufnahmen.length,allWiederaufnahmen:newData.wiederaufnahmen}, 'A');      return newData;
    });
    
      // Speichere automatisch (ohne Alert)
      if (backendUrl && selectedSession?.id) {
        saveVorderseite(true);
      }
    } finally {
      // Reset Flags nach Abschluss (auch bei Fehlern oder frühem Return)
      isHandlingStartRef.current = false;
      // Reset pendingWiederaufnahmeRef nach einer kurzen Verzögerung, damit setVorderseiteData abgeschlossen ist
      setTimeout(() => {
        pendingWiederaufnahmeRef.current = null;
      }, 100);
    }
  };

  const handleStop = () => {
    if (measurements.length === 0) {
      setIsRunning(false);
      return;
    }

    // Zeige Stop-Dialog
    setShowStopDialog(true);
  };

  // Prüfe und zeige Dialog für fehlende wiederaufnahmen
  const checkAndShowWiederaufnahmenDialog = () => {
    if (!selectedSession || !measurements || measurements.length === 0) {
      return; // Keine Session oder keine Messpunkte
    }
    
    const currentVorderseiteData = vorderseiteData || getDefaultVorderseiteData();
    const checkResult = checkWiederaufnahmenComplete(currentVorderseiteData, measurements);
    
    if (checkResult.needsUpdate && !showWiederaufnahmenDialog) {
      // Generiere fehlende wiederaufnahmen
      const generatedWiederaufnahmen = generateWiederaufnahmenFromMeasurements(measurements);
      
      // Zeige Dialog nur, wenn noch nicht angezeigt
      setPendingWiederaufnahmen(generatedWiederaufnahmen);
      setShowWiederaufnahmenDialog(true);
    }
  };

  // Handler für Wiederaufnahmen-Dialog
  const handleApplyWiederaufnahmen = () => {    debugLog('App.jsx:3808', 'handleApplyWiederaufnahmen: Entry', {pendingWiederaufnahmenCount:pendingWiederaufnahmen.length,pendingWiederaufnahmen:pendingWiederaufnahmen}, 'B');    setVorderseiteData(prev => {
      const currentData = prev || getDefaultVorderseiteData();
      const newData = JSON.parse(JSON.stringify(currentData));      debugLog('App.jsx:3811', 'handleApplyWiederaufnahmen: Current state', {prevExists:!!prev,currentWiederaufnahmen:currentData.wiederaufnahmen,currentWiederaufnahmenIsArray:Array.isArray(currentData.wiederaufnahmen)}, 'B');      if (!newData.wiederaufnahmen || !Array.isArray(newData.wiederaufnahmen)) {
        newData.wiederaufnahmen = [];
      }
      
      // Füge fehlende wiederaufnahmen hinzu oder aktualisiere unvollständige
      pendingWiederaufnahmen.forEach(generated => {        debugLog('App.jsx:3818', 'handleApplyWiederaufnahmen: Processing generated', {generated,generatedNummer:generated.nummer,generatedBeginnzeit:generated.beginnzeit,generatedEndzeit:generated.endzeit,generatedDatum:generated.datum}, 'B');        const existingIndex = newData.wiederaufnahmen.findIndex(w => w.nummer === generated.nummer);
        
        if (existingIndex >= 0) {
          // Aktualisiere bestehende, falls unvollständig
          const beforeUpdate = JSON.parse(JSON.stringify(newData.wiederaufnahmen[existingIndex]));
          if (!newData.wiederaufnahmen[existingIndex].beginnzeit && generated.beginnzeit) {
            newData.wiederaufnahmen[existingIndex].beginnzeit = generated.beginnzeit;
          }
          // Endzeit nur setzen, wenn Beginzeit vorhanden ist
          const entry = newData.wiederaufnahmen[existingIndex];
          if (entry.beginnzeit && entry.beginnzeit.trim() && !entry.endzeit && generated.endzeit) {
            entry.endzeit = generated.endzeit;
          }
          if (!entry.datum && generated.datum) {
            entry.datum = generated.datum;
          }
          // Berechne Dauer, wenn beginnzeit und endzeit vorhanden sind
          if (entry.beginnzeit && entry.endzeit) {
            const calculatedDauer = calculateDuration(entry.beginnzeit, entry.endzeit, entry.datum);
            entry.dauer = calculatedDauer;            debugLog('App.jsx:3836', 'handleApplyWiederaufnahmen: Calculated dauer', {entryNummer:entry.nummer,beginnzeit:entry.beginnzeit,endzeit:entry.endzeit,datum:entry.datum,calculatedDauer}, 'D');          }          debugLog('App.jsx:3840', 'handleApplyWiederaufnahmen: Updated existing', {existingIndex,beforeUpdate,afterUpdate:newData.wiederaufnahmen[existingIndex]}, 'B');        } else {
          // Füge neue hinzu, aber nur wenn Beginzeit vorhanden ist (oder wenn keine Endzeit vorhanden ist)
          // Wenn nur Endzeit vorhanden ist, wird der Eintrag nicht hinzugefügt
          if (generated.beginnzeit && generated.beginnzeit.trim()) {
            // Beginzeit vorhanden: Eintrag hinzufügen (Endzeit kann leer sein)
            if (generated.beginnzeit && generated.endzeit) {
              generated.dauer = calculateDuration(generated.beginnzeit, generated.endzeit, generated.datum);              debugLog('App.jsx:3844', 'handleApplyWiederaufnahmen: Calculated dauer for new entry', {generated,calculatedDauer:generated.dauer}, 'D');            }
            newData.wiederaufnahmen.push(generated);
            debugLog('App.jsx:3846', 'handleApplyWiederaufnahmen: Added new entry', {generated,allWiederaufnahmen:newData.wiederaufnahmen.map(w=>({nummer:w.nummer,beginnzeit:w.beginnzeit,endzeit:w.endzeit,datum:w.datum,dauer:w.dauer}))}, 'B');
          } else if (!generated.endzeit || !generated.endzeit.trim()) {
            // Keine Beginzeit, aber auch keine Endzeit: Eintrag hinzufügen (wird später aktualisiert)
            newData.wiederaufnahmen.push(generated);            debugLog('App.jsx:3849', 'handleApplyWiederaufnahmen: Added entry without beginnzeit', {generated}, 'B');          }
          // Wenn nur Endzeit vorhanden ist (ohne Beginzeit), wird der Eintrag NICHT hinzugefügt
        }
      });
      
      // Sortiere nach nummer
      newData.wiederaufnahmen.sort((a, b) => a.nummer - b.nummer);      debugLog('App.jsx:3856', 'handleApplyWiederaufnahmen: Final state', {wiederaufnahmenCount:newData.wiederaufnahmen.length,allWiederaufnahmen:newData.wiederaufnahmen}, 'B');      return newData;
    });
    
    setShowWiederaufnahmenDialog(false);
    setPendingWiederaufnahmen([]);
    
    // Speichere automatisch
    if (backendUrl && selectedSession?.id) {
      saveVorderseite(true);
    }
  };

  const handleCancelWiederaufnahmen = () => {
    setShowWiederaufnahmenDialog(false);
    setPendingWiederaufnahmen([]);
  };

  // Handler für Update-Resume-Dialog
  const handleUpdateResumeConfirm = async () => {
    setShowUpdateResumeDialog(false);
    updateResumeDialogShownRef.current = false; // Reset Flag, damit Dialog wieder geöffnet werden kann
    setVersionBadgeDismissedByResume(true);
    localStorage.setItem('stoppuhr_version_badge_dismissed', FRONTEND_VERSION);
    const data = updateResumeDialogData;
    if (!data) return;    debugLog('App.jsx:4718', 'handleUpdateResumeConfirm: Entry', {isBeforeLogin:data.isBeforeLogin}, 'C');    if (data.isBeforeLogin) {
      // Auto-Login mit gespeicherten Daten
      const savedEmail = localStorage.getItem(STORAGE_KEY_EMAIL);
      const savedBackend = localStorage.getItem(STORAGE_KEY_BACKEND);
      
      if (savedEmail && savedBackend) {
        // WICHTIG: Hole State nochmal direkt, um sicherzustellen, dass er noch existiert
        const stateToRestore = data.savedState;
        
        setEmail(savedEmail);
        setBackendUrl(savedBackend);
        
        setTimeout(async () => {
          setIsLoggedIn(true);
          
          setTimeout(async () => {
            const restored = await restoreStateFromData(stateToRestore);
            if (restored) {
              // Lösche State nach erfolgreicher Wiederherstellung
              await deleteMeasurementState('update_resume_state');
            } else {
              alert(t('alerts.restore_error'));
              await deleteMeasurementState('update_resume_state');
            }
            requestHandbookUpdate();
            setIsCheckingUpdateResume(false); // Check abgeschlossen nach Restore
          }, 500);
        }, 100);
      } else {
        console.log('[Update-Resume] Keine gespeicherten Login-Daten, zeige Login-Screen');        debugLog('App.jsx:4736', 'handleUpdateResumeConfirm: No saved login data, setting isCheckingUpdateResume to false', {hasSavedEmail:!!savedEmail,hasSavedBackend:!!savedBackend}, 'C');        setIsCheckingUpdateResume(false); // Erlaube Login-Screen
      }
    } else {
      // Nach Login
      const restored = await restoreStateAfterUpdate();
      if (restored) {
        // Erfolgs-Meldung entfernt - Messung läuft einfach weiter
      } else {
        alert(t('alerts.restore_error'));
        await deleteMeasurementState('update_resume_state');
      }
      updateResumeCheckInProgressRef.current = false;      debugLog('App.jsx:4755', 'handleUpdateResumeConfirm: Reset flag after restore', {restored}, 'B');    }
    requestHandbookUpdate();
    setUpdateResumeDialogData(null);
  };

  const handleUpdateResumeCancel = useCallback(async () => {
    console.log('[Update-Resume] handleUpdateResumeCancel called', { isCheckingUpdateResume, showUpdateResumeDialog });
    // Setze Flag SOFORT am Anfang, bevor irgendwelche Checks
    // Dies verhindert Race Conditions bei gleichzeitigen Aufrufen
    if (updateResumeCancelInProgressRef.current) {
      console.log('[Update-Resume] handleUpdateResumeCancel bereits in Ausführung, ignoriere Aufruf');
      return;
    }
    updateResumeCancelInProgressRef.current = true;
    
    // Guard: Verhindere Ausführung wenn Dialog nicht geöffnet
    if (!showUpdateResumeDialog || !updateResumeDialogData) {
      updateResumeCancelInProgressRef.current = false;
      return;
    }
    
    const data = updateResumeDialogData;
    
    // WICHTIG: Sicherheitsabfrage vor Datenverlust
    const confirmed = window.confirm(t('alerts.confirm.update_resume_restart', { count: data?.measurementsCount || 0 }));
    
    if (!confirmed) {
      // Benutzer hat abgebrochen - Dialog bleibt offen, Flag zurücksetzen
      updateResumeCancelInProgressRef.current = false;
      return;
    }
    
    // Setze Dialog-State auf false, um weitere Aufrufe zu verhindern
    setShowUpdateResumeDialog(false);
    setUpdateResumeDialogData(null);
    updateResumeDialogShownRef.current = false; // Reset Flag, damit Dialog wieder geöffnet werden kann
    setVersionBadgeDismissedByResume(true);
    localStorage.setItem('stoppuhr_version_badge_dismissed', FRONTEND_VERSION);

    // WICHTIG: Setze updateResumeCheckInProgressRef ZUERST auf false, BEVOR isCheckingUpdateResume auf false gesetzt wird
    // Dies verhindert, dass der useEffect in Zeile 1519 isCheckingUpdateResume wieder auf true setzt
    updateResumeCheckInProgressRef.current = false;    debugLog('App.jsx:4763', 'handleUpdateResumeCancel: User confirmed cancel', {isBeforeLogin:data?.isBeforeLogin}, 'E');    // Lösche State, da Benutzer abgelehnt hat (sowohl bei isBeforeLogin als auch nach Login)
    await deleteMeasurementState('update_resume_state');
    updateResumeStateDeletedRef.current = true;

    // WICHTIG: Setze isCheckingUpdateResume auf false, damit Sessionliste/Login-Screen wieder angezeigt werden
    // WICHTIG: Dies muss NACH updateResumeCheckInProgressRef.current = false gesetzt werden,
    // damit der useEffect in Zeile 1519 isCheckingUpdateResume nicht wieder auf true setzt
    console.log('[Update-Resume] handleUpdateResumeCancel: BEFORE setIsCheckingUpdateResume(false)', { isCheckingUpdateResume, checkInProgress: updateResumeCheckInProgressRef.current });    debugLog('App.jsx:5538', 'handleUpdateResumeCancel: BEFORE setIsCheckingUpdateResume(false)', {isBeforeLogin:data?.isBeforeLogin,checkInProgressAfter:updateResumeCheckInProgressRef.current,currentIsCheckingUpdateResume:isCheckingUpdateResume}, 'B');    setIsCheckingUpdateResume(false);
    
    console.log('[Update-Resume] handleUpdateResumeCancel: AFTER setIsCheckingUpdateResume(false)');    debugLog('App.jsx:5542', 'handleUpdateResumeCancel: AFTER setIsCheckingUpdateResume(false)', {isBeforeLogin:data?.isBeforeLogin,checkInProgressAfter:updateResumeCheckInProgressRef.current}, 'B');    // Reset Flag nach Abschluss
    updateResumeCancelInProgressRef.current = false;
  }, [showUpdateResumeDialog, updateResumeDialogData]);

  /** Fortsetzen ohne Dialog: Lädt gespeicherten Update-Resume-State und stellt Messung wieder her (Startzeit aus letzter WA). */
  const handleResumeFromButton = useCallback(async () => {
    const restored = await restoreStateAfterUpdate();
    if (!restored) {
      alert(t('alerts.no_saved_session'));
    } else {
      requestHandbookUpdate();
    }
  }, [restoreStateAfterUpdate, requestHandbookUpdate]);

  const handleConfirmStop = () => {
    setShowStopDialog(false);
    setIsRunning(false);
    
    // Leere Activity und Note nach dem Stoppen
    setActivity('');
    setNote('');
    
    // Speichere Endzeit in Vorderseite
    const nowDate = new Date();
    const timeStr = nowDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const currentResumeNumber = resumeNumber || selectedSession?.resumeNumber || 0;
    
    setVorderseiteData(prev => {
      const currentData = prev || getDefaultVorderseiteData();
      const newData = JSON.parse(JSON.stringify(currentData));
      
      // Stelle sicher, dass wiederaufnahmen ein Array ist
      if (!newData.wiederaufnahmen || !Array.isArray(newData.wiederaufnahmen)) {
        newData.wiederaufnahmen = [];
      }
      
      // Prüfe, ob diese WA-Nr bereits existiert
      const existingIndex = newData.wiederaufnahmen.findIndex(w => w.nummer === currentResumeNumber);
      
      if (existingIndex >= 0) {
        // WA-Nr existiert bereits: Aktualisiere Endzeit nur wenn Beginzeit vorhanden ist
        const entry = newData.wiederaufnahmen[existingIndex];
        if (entry.beginnzeit && entry.beginnzeit.trim() && !entry.endzeit) {
          // Nur Endzeit setzen, wenn Beginzeit vorhanden ist
          entry.endzeit = timeStr;
        }
        // Stelle sicher, dass Datum gesetzt ist
        if (!entry.datum) {
          entry.datum = nowDate.toLocaleDateString('de-DE');
        }
        // Berechne Dauer, wenn beginnzeit und endzeit vorhanden sind
        if (entry.beginnzeit && entry.endzeit) {
          entry.dauer = calculateDuration(entry.beginnzeit, entry.endzeit, entry.datum);
        }
      } else {
        // WA-Nr existiert noch nicht: Füge neuen Eintrag NICHT hinzu, wenn keine Beginzeit vorhanden ist
        // (Endzeit kann nur gesetzt werden, wenn Beginzeit vorhanden ist)
      }
      
      return newData;
    });
    
    // Speichere automatisch (ohne Alert)
    if (backendUrl && selectedSession?.id) {
      saveVorderseite(true);
    }
    
    // Speicher-Dialog vorbereiten und anzeigen
    const defaultFileName = selectedSession?.sessionName 
      ? selectedSession.sessionName.replace(/[^a-zA-Z0-9]/g, '_')
      : `messpunkte_${deviceName}`;
    
    const dateStr = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const suggestedFileName = `${defaultFileName}_${dateStr}`;
    setSaveFileName(suggestedFileName);
    setShowSaveDialog(true);
  };

  const handleCancelStop = () => {
    setShowStopDialog(false);
  };

  // Handler für Zurück-Button: Prüft ob Uhr läuft oder Daten vorhanden
  // Handler für kompakte Ansicht
  const toggleCompactMenu = () => {
    setShowCompactMenu(prev => !prev);
  };

  const handleToggleCompactView = () => {
    const newValue = !compactViewMode;
    setCompactViewMode(newValue);
    localStorage.setItem('stoppuhr_compact_view_mode', String(newValue));
    setShowCompactMenu(false); // Menü schließen nach Toggle
  };

  const handleToggleCompactInputs = () => {
    const newValue = !showCompactInputs;
    setShowCompactInputs(newValue);
    localStorage.setItem('stoppuhr_compact_inputs_enabled', String(newValue));
    setShowCompactMenu(false); // Menü schließen nach Toggle
  };

  // Handler für Bildschirmausrichtung
  useEffect(() => {
    const checkOrientation = () => {
      const isPortraitMode = window.innerWidth <= 768 && window.matchMedia('(orientation: portrait)').matches;
      setIsPortrait(isPortraitMode);
      
      // Passe gradePosition automatisch an Orientierung an (nur wenn noch nicht manuell geändert)
      // Prüfe, ob gradePosition bereits in localStorage gespeichert wurde (manuelle Änderung)
      const savedGradePosition = localStorage.getItem('stoppuhr_grade_position');
      if (!savedGradePosition) {
        // Keine manuelle Einstellung: Setze basierend auf Orientierung
        setGradePosition(isPortraitMode ? 'left' : 'top');
      }
    };

    // Initial check
    checkOrientation();

    // Listener für Resize und Orientation Change
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Click-Outside Handler für Menü
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCompactMenu && !event.target.closest('.compact-menu-container')) {
        setShowCompactMenu(false);
      }
    };

    if (showCompactMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCompactMenu]);

  const handleBackToSessions = () => {
    // Prüfe ob Sicherheitsabfrage nötig ist
    const needsConfirmation = isRunning || 
                              measurements.length > 0 || 
                              (selectedSession && selectedSession.uploaded !== true);
    
    if (needsConfirmation) {
      // Zeige Warn-Dialog
      setShowBackToSessionsDialog(true);
    } else {
      // Direkt zurückwechseln wenn nichts zu verlieren ist
      setSelectedSession(null);
    }
  };

  const handleConfirmBackToSessions = () => {
    setShowBackToSessionsDialog(false);
    setSelectedSession(null);
  };

  const handleCancelBackToSessions = () => {
    setShowBackToSessionsDialog(false);
  };

  // Gemeinsame Funktion für Kürzel-Selektion (beide Ansichten)
  const handleKuerzelSelect = (item) => {
    setActivity(item.beschreibung || item.fullText || item.kuerzel);
    if (item.zeitart && item.zeitart.trim()) {
      const zeitartExists = timeTypes.some(tt => tt.name === item.zeitart.trim());
      if (zeitartExists) {
        setTimeType(item.zeitart.trim());
      }
    }
    setSelectedKuerzel(item);
    const previousValues = { ...(einflussgroessenValuesRef.current || einflussgroessenValues || {}) };
    const initialValues = {};
    if (item.einflussgroessen && Array.isArray(item.einflussgroessen)) {
      item.einflussgroessen.forEach(einfluss => {
        if (einfluss && einfluss.einflussname && typeof einfluss.einflussname.trim === 'function') {
          const name = einfluss.einflussname.trim();
          if (name) {
            // Semantik:
            // - useGlobalInfluences=false -> immer lokaler Reset
            // - useGlobalInfluences=true  -> nur Typ x/l behalten, X/L lokal resetten
            if (useGlobalInfluences && isGlobalEinflussType(einfluss) && previousValues[name]) {
              initialValues[name] = previousValues[name];
            } else {
              initialValues[name] = '';
            }
          }
        }
      });
    }
    const nextValues = applyDependentAutofillValues(initialValues);
    setEinflussgroessenValues(nextValues);
    einflussgroessenValuesRef.current = nextValues;
  };

  // Handler für kompakte Ansicht

  const handleCompactEinflussgroesseChange = (name, value) => {
    applyEinflussValueChange(name, value);
  };

  const handleCompactEditMeasurement = (updatedMeasurement) => {
    // Priorisiere number über id, da number eindeutig ist
    const measurement = measurements.find(m => 
      m.number === updatedMeasurement.number || 
      (updatedMeasurement.number === undefined && m.id === updatedMeasurement.id)
    );
    if (!measurement) return;

    // Sammle Einflussgrößen-Werte - nur nicht-leere Werte
    // updatedMeasurement.einflussgroessen wurde bereits im CompactEditDialog verarbeitet,
    // aber wir prüfen nochmal, um sicherzustellen, dass leere Werte entfernt werden
    const einflussgroessen = {};
    if (updatedMeasurement.einflussgroessen && typeof updatedMeasurement.einflussgroessen === 'object' && !Array.isArray(updatedMeasurement.einflussgroessen) && Object.keys(updatedMeasurement.einflussgroessen).length > 0) {
      Object.entries(updatedMeasurement.einflussgroessen).forEach(([name, value]) => {
        // Nur Werte speichern, die nicht leer sind (explizit prüfen auf String und trim)
        if (value && typeof value === 'string' && value.trim().length > 0) {
          einflussgroessen[name] = value.trim();
        }
        // Leere Werte werden nicht hinzugefügt, damit sie entfernt werden
      });
    }

    // Finde das Kürzel für die aktualisierte Aktivität
    let updatedKuerzel = measurement.kuerzel || null;
    if (updatedMeasurement.activity && ablaufDbData && ablaufDbData.length > 0) {
      const matchingKuerzel = ablaufDbData.find(k => 
        k.beschreibung === updatedMeasurement.activity || 
        k.fullText === updatedMeasurement.activity ||
        k.kuerzel === updatedMeasurement.activity
      );
      if (matchingKuerzel) {
        updatedKuerzel = matchingKuerzel.kuerzel;
      }
    }

    const updatedMeasurements = measurements.map(m => 
      m.number === measurement.number
        ? { 
            ...m, 
            activity: updatedMeasurement.activity, 
            kuerzel: updatedKuerzel, // Kürzel aktualisieren
            note: updatedMeasurement.note, 
            excluded: updatedMeasurement.excluded,
            timeType: updatedMeasurement.timeType,
            grade: updatedMeasurement.grade,
            // Explizit setzen: wenn leer, dann undefined, sonst das Objekt
            // Dies entfernt gelöschte Ausprägungen, da sie nicht in einflussgroessen enthalten sind
            einflussgroessen: Object.keys(einflussgroessen).length > 0 ? einflussgroessen : undefined
          }
        : m
    );

    const finalMeasurements = recalculateIndividualTimes(updatedMeasurements);
    setMeasurements(finalMeasurements);

    if (selectedSession) {
      const updatedSessions = sessions.map(s =>
        s.id === selectedSession.id
          ? { ...s, measurements: finalMeasurements, lastUpdated: new Date().toISOString() }
          : s
      );
      const limitedSessions = limitAndSortSessions(updatedSessions);
      setSessions(limitedSessions);
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    }
  };

  const handleCompactDeleteMeasurement = (measurement) => {
    // measurement kann ein Objekt oder eine ID sein (für Kompatibilität)
    const measurementObj = typeof measurement === 'object' 
      ? measurement 
      : measurements.find(m => m.id === measurement || m.number === measurement);
    
    if (measurementObj) {
      handleDeleteMeasurement(measurementObj);
    }
  };

  const handleCompactViewPhotos = async (measurementId) => {
    const measurement = measurements.find(m => m.id === measurementId);
    if (measurement && measurement.number && selectedSession?.id) {
      try {
        const photos = await loadPhotos(measurement.number, selectedSession.id);
        if (photos.length > 0) {
          setPhotoPreview(photos[0].fileData);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Fotos:', error);
      }
    }
  };

  const handleCompactViewNote = (measurement) => {
    if (measurement.note) {
      alert(t('alerts.note_display', { note: measurement.note }));
    }
  };

  const handleCompactVoiceClick = () => {
    if (!isSpeechRecognitionSupported) {
      if (isSafari()) {
        alert(t('alerts.speech_safari'));
      } else {
        alert(t('alerts.speech_not_supported'));
      }
      return;
    }
    if (isListening) {
      handleStopListening();
    } else {
      handleStartListening();
    }
  };

  const handleCompactNoteClick = () => {
    // Rufe handleSaveNote auf, genau wie in der Normalansicht
    handleSaveNote();
  };

  const handleCompactPhotoClick = () => {
    photoInputRef.current?.click();
  };

  // Handler für Copy Activity in Kompaktansicht
  const handleCompactCopyActivity = (measurement) => {
    handleCopyActivity(measurement);
  };

  // Handler für Foto hinzufügen zu Messung (aus Tätigkeiten-Liste)
  const handleCompactAddPhoto = (measurementNumber) => {
    // Setze temporären State für die Messung, zu der das Foto hinzugefügt werden soll
    setPendingPhotoMeasurementNumber(measurementNumber);
    photoInputRef.current?.click();
  };

  // Handler für Foto entfernen aus Messung
  const handleCompactRemovePhoto = async (measurementNumber, photoId) => {
    if (!selectedSession) return;
    
    try {
      // Lösche Foto aus IndexedDB
      const db = await openDB();
      const transaction = db.transaction([STORE_PHOTOS], 'readwrite');
      const store = transaction.objectStore(STORE_PHOTOS);
      await store.delete(photoId);
      
      // Aktualisiere measurementPhotos
      const photos = await loadPhotos(measurementNumber, selectedSession?.id);
      setMeasurementPhotos(prev => ({
        ...prev,
        [measurementNumber]: photos.map(photo => ({
          id: photo.id,
          fileData: photo.fileData,
          fileName: photo.fileName
        }))
      }));
    } catch (error) {
      console.error('Fehler beim Löschen des Fotos:', error);
      alert(t('alerts.photo_delete_error', { msg: error.message || 'Unbekannter Fehler' }));
    }
  };

  // Handler für Foto-Upload im CompactEditDialog
  const handleCompactEditPhotoUpload = async (file, measurementNumber) => {
    if (!file || !selectedSession || !measurementNumber) return;
    
    if (!file.type.startsWith('image/')) {
      alert(t('alerts.photo_select'));
      return;
    }
    
    try {
      // Komprimiere Bild
      const compressedBlob = await compressImage(file);
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedBlob);
      });
      
      // Speichere Foto in IndexedDB
      await savePhoto(measurementNumber, selectedSession.id, file, file.name);
      
      // Lade Fotos neu
      const photos = await loadPhotos(measurementNumber, selectedSession.id);
      return photos.map(photo => ({
        id: photo.id,
        fileData: photo.fileData,
        fileName: photo.fileName
      }));
    } catch (error) {
      console.error('Fehler beim Hochladen des Fotos:', error);
      throw error;
    }
  };

  // Handler für Foto-Löschen im CompactEditDialog
  const handleCompactEditPhotoDelete = async (photoId, measurementNumber) => {
    if (!selectedSession) return;
    
    try {
      await deletePhoto(photoId);
      const photos = await loadPhotos(measurementNumber, selectedSession.id);
      return photos.map(photo => ({
        id: photo.id,
        fileData: photo.fileData,
        fileName: photo.fileName
      }));
    } catch (error) {
      console.error('Fehler beim Löschen des Fotos:', error);
      throw error;
    }
  };

  // Berechne letzten Leistungsgrad
  const [lastGrade, setLastGrade] = useState(null);
  useEffect(() => {
    if (measurements.length > 0) {
      const lastMeasurement = measurements[measurements.length - 1];
      setLastGrade(lastMeasurement.grade || null);
    } else {
      setLastGrade(null);
    }
  }, [measurements]);

  const handleConfirmSave = () => {
    const finalFileName = saveFileName.trim() || saveFileName;
    if (finalFileName) {
      setShowSaveDialog(false);
      handleSaveMeasurements(finalFileName);
      setSaveFileName('');
    }
  };

  const handleCancelSave = () => {
    setShowSaveDialog(false);
    setSaveFileName('');
  };

  // Lade Vorderseite-Daten aus Temp-Dateien
  const loadVorderseiteFromTempFiles = async () => {
    try {
      // Versuche beide Temp-Dateien zu laden
      const tempFile1 = await loadTempFile("_$$ECE_tmp.$$$");
      const tempFile2 = await loadTempFile("_$$EC2E_tmp.$$$");
      
      let latestData = null;
      let latestTimestamp = 0;
      
      // Prüfe erste Temp-Datei
      if (tempFile1) {
        try {
          const data1 = JSON.parse(tempFile1);
          if (data1.vorderseiteData && data1.timestamp) {
            const timestamp = new Date(data1.timestamp).getTime();
            if (timestamp > latestTimestamp) {
              latestTimestamp = timestamp;
              latestData = data1.vorderseiteData;
            }
          }
        } catch (e) {
          console.warn('Fehler beim Parsen der ersten Temp-Datei:', e);
        }
      }
      
      // Prüfe zweite Temp-Datei
      if (tempFile2) {
        try {
          const data2 = JSON.parse(tempFile2);
          if (data2.vorderseiteData && data2.timestamp) {
            const timestamp = new Date(data2.timestamp).getTime();
            if (timestamp > latestTimestamp) {
              latestTimestamp = timestamp;
              latestData = data2.vorderseiteData;
            }
          }
        } catch (e) {
          console.warn('Fehler beim Parsen der zweiten Temp-Datei:', e);
        }
      }
      
      // Setze Vorderseite-Daten, falls gefunden
      if (latestData) {
        // Bereinige doppelte WA-Nr Einträge
        if (latestData.wiederaufnahmen && Array.isArray(latestData.wiederaufnahmen)) {
          latestData.wiederaufnahmen = deduplicateWiederaufnahmen(latestData.wiederaufnahmen);
        }
        setVorderseiteData(latestData);
        return latestData; // Gib geladene Daten zurück
      }
      
      return null; // Keine Daten gefunden
    } catch (error) {
      console.error('Fehler beim Laden der Vorderseite aus Temp-Dateien:', error);
      return null;
    }
  };

  // Vorderseite-Funktionen
  const loadVorderseite = async (sessionId) => {
    if (!sessionId || !backendUrl) {
      const defaultData = getDefaultVorderseiteData();
      setVorderseiteData(defaultData);
      return defaultData;
    }
    
    setIsLoadingVorderseite(true);
    try {
      const normalizedUrl = normalizeBackendUrl(backendUrl);
      // Verwende Admin-API (funktioniert auch für normale User, wenn sie Zugriff auf ihre eigenen Sessions haben)
      const url = `${normalizedUrl}/api/admin/sessions/${sessionId}/vorderseite`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const loadedData = data.vorderseite_data || getDefaultVorderseiteData();
        
        // Stelle sicher, dass wiederaufnahmen ein Array ist
        if (!loadedData.wiederaufnahmen || !Array.isArray(loadedData.wiederaufnahmen)) {
          loadedData.wiederaufnahmen = [];
        }
        
        // Bereinige doppelte WA-Nr Einträge in geladenen Daten
        if (loadedData.wiederaufnahmen && Array.isArray(loadedData.wiederaufnahmen)) {
          loadedData.wiederaufnahmen = deduplicateWiederaufnahmen(loadedData.wiederaufnahmen);
        }
        
        // Merge mit bestehenden Daten, falls vorhanden (um lokale Änderungen nicht zu überschreiben)
        let finalData;
        setVorderseiteData(prev => {
          if (prev && prev.wiederaufnahmen && Array.isArray(prev.wiederaufnahmen) && prev.wiederaufnahmen.length > 0) {
            // Behalte lokale Wiederaufnahmen, füge neue aus Backend hinzu
            const merged = { ...loadedData };
            const existingNumbers = new Set(prev.wiederaufnahmen.map(w => w.nummer));
            const newFromBackend = (loadedData.wiederaufnahmen || []).filter(w => !existingNumbers.has(w.nummer));
            merged.wiederaufnahmen = [...prev.wiederaufnahmen, ...newFromBackend];
            // Bereinige auch die zusammengeführten Daten
            merged.wiederaufnahmen = deduplicateWiederaufnahmen(merged.wiederaufnahmen);
            finalData = merged;
            return merged;
          }
          finalData = loadedData;
          return loadedData;
        });
        return finalData;
      } else {
        console.warn('Fehler beim Laden der Vorderseite:', response.statusText);
        // WICHTIG: Behalte bestehende Daten, wenn Backend-Fehler auftritt
        const defaultData = getDefaultVorderseiteData();
        let existingData = null;
        setVorderseiteData(prev => {
          existingData = prev || defaultData;
          return existingData;
        });
        return existingData;
      }
    } catch (error) {
      console.error('Fehler beim Laden der Vorderseite:', error);
      // WICHTIG: Behalte bestehende Daten, wenn Netzwerk-Fehler auftritt
      const defaultData = getDefaultVorderseiteData();
      let existingData = null;
      setVorderseiteData(prev => {
        existingData = prev || defaultData;
        return existingData;
      });
      return existingData;
    } finally {
      setIsLoadingVorderseite(false);
    }
  };

  const saveVorderseite = async (silent = false) => {
    if (!selectedSession?.id || !backendUrl || !vorderseiteData) return;
    
    setIsSavingVorderseite(true);
    try {
      const normalizedUrl = normalizeBackendUrl(backendUrl);
      const url = `${normalizedUrl}/api/admin/sessions/${selectedSession.id}/vorderseite`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vorderseite_data: vorderseiteData })
      });

      if (response.ok) {
        if (!silent) {
          alert(t('alerts.vorderseite_saved'));
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        if (!silent) {
          alert(t('alerts.vorderseite_error', { msg: errorData.error || response.statusText }));
        }
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Vorderseite:', error);
      if (!silent) {
        alert(t('alerts.vorderseite_error', { msg: error.message }));
      }
    } finally {
      setIsSavingVorderseite(false);
    }
  };

  const getDefaultVorderseiteData = () => {
    return {
      firma: '',
      netz: '',
      pfad: '',
      za_name: selectedSession?.sessionName || '',
      teilennr: '',
      afo: '',
      arbeitsaufgabe: '',
      stueck_pro_auftrag: '',
      kunde: '',
      kostenstelle: '',
      wiederaufnahmen: [],
      zusammenstellung: [
        { art: 'beeinflußbare Hauptzeit [thb]', zeit_min: 0, herkunft: '' },
        { art: 'unbeeinflußbare Hauptzeit [thu]', zeit_min: 0, herkunft: '' },
        { art: 'beeinflußbare Nebenzeit [tnb]', zeit_min: 0, herkunft: '' },
        { art: 'unbeeinflußbare Nebenzeit [tnu]', zeit_min: 0, herkunft: '' }
      ],
      grundzeit_tg: 0,
      erholungszeit_ter_prozent: 0,
      verteilzeit_tv_prozent: 0,
      zeit_je_einheit_te1: 0,
      te1_in_min: 0,
      bedienungsanteil_personen: 1,
      bedienungsanteil_zeit: 0,
      ruestzeit_min: 0,
      ruestzeit_herkunft: '',
      arbeitsverfahren: '',
      arbeitsgegenstand: [],
      decimal_places_vorderseite: 4,
      decimal_places_sollzeiten: 4,
      mitarbeiter: [],
      betriebsmittel: [],
      umgebungseinfluesse: '',
      entlohnung: '',
      bemerkung: '',
      bemerkung_quell_db: '',
      notiz: '',
      qualitaet: '',
      bearbeitungsdatum: new Date().toLocaleDateString('de-DE'),
      gueltig_ab: '',
      gueltig_bis: '',
      bearbeiter: '',
      geprueft: ''
    };
  };

  // Berechne Dauer aus beginnzeit und endzeit (Format: "HH:MM:SS")
  const calculateDuration = (beginnzeit, endzeit, datum) => {
    if (!beginnzeit || !endzeit || !beginnzeit.trim() || !endzeit.trim()) {
      return '';
    }
    
    try {
      // Parse Zeiten im Format "HH:MM:SS" oder "HH:MM"
      const parseTime = (timeStr) => {
        const parts = timeStr.split(':');
        if (parts.length < 2) return null;
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        const seconds = parts.length > 2 ? parseInt(parts[2], 10) : 0;
        if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
        return hours * 3600 + minutes * 60 + seconds;
      };
      
      const startSeconds = parseTime(beginnzeit);
      const endSeconds = parseTime(endzeit);
      
      if (startSeconds === null || endSeconds === null) {
        return '';
      }
      
      // Berechne Differenz (kann negativ sein, wenn über Mitternacht)
      let diffSeconds = endSeconds - startSeconds;
      
      // Wenn negativ, könnte es über Mitternacht sein (nur wenn am selben Tag)
      if (diffSeconds < 0) {
        // Prüfe, ob es wirklich über Mitternacht ist (nur wenn beide Zeiten am selben Tag)
        // Für jetzt: Addiere 24 Stunden
        diffSeconds += 24 * 3600;
      }
      
      // Konvertiere zurück zu HH:MM:SS
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = diffSeconds % 60;
      
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } catch (error) {
      console.error('Fehler beim Berechnen der Dauer:', error);
      return '';
    }
  };

  // Bereinige doppelte WA-Nr Einträge in wiederaufnahmen
  const deduplicateWiederaufnahmen = (wiederaufnahmen) => {
    if (!wiederaufnahmen || !Array.isArray(wiederaufnahmen)) {
      return [];
    }
    
    // Gruppiere nach WA-Nr
    const byNummer = {};
    wiederaufnahmen.forEach(w => {
      const nummer = w.nummer;
      if (!byNummer[nummer]) {
        byNummer[nummer] = [];
      }
      byNummer[nummer].push(w);
    });
    
    // Für jede WA-Nr: Zusammenführen der Einträge
    const result = [];
    Object.keys(byNummer).sort((a, b) => parseInt(a) - parseInt(b)).forEach(nummer => {
      const entries = byNummer[nummer];
      
      // Zusammenführen: Beginzeit vom ersten Eintrag mit Beginzeit, Endzeit vom letzten Eintrag mit Endzeit
      const merged = {
        nummer: parseInt(nummer),
        datum: '',
        beginnzeit: '',
        endzeit: '',
        dauer: ''
      };
      
      // Finde Beginzeit (vom ersten Eintrag, der eine Beginzeit hat)
      for (const entry of entries) {
        if (entry.beginnzeit && entry.beginnzeit.trim()) {
          merged.beginnzeit = entry.beginnzeit;
          if (!merged.datum && entry.datum) {
            merged.datum = entry.datum;
          }
          break;
        }
      }
      
      // Finde Endzeit (vom letzten Eintrag, der eine Endzeit hat)
      for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].endzeit && entries[i].endzeit.trim()) {
          merged.endzeit = entries[i].endzeit;
          if (!merged.datum && entries[i].datum) {
            merged.datum = entries[i].datum;
          }
          break;
        }
      }
      
      // Falls kein Datum gefunden, verwende das erste verfügbare
      if (!merged.datum) {
        for (const entry of entries) {
          if (entry.datum && entry.datum.trim()) {
            merged.datum = entry.datum;
            break;
          }
        }
      }
      
      // Falls immer noch kein Datum, verwende aktuelles Datum
      if (!merged.datum) {
        merged.datum = new Date().toLocaleDateString('de-DE');
      }
      
      result.push(merged);
    });
    
    return result;
  };

  // Analysiere Messpunkte und erstelle wiederaufnahmen
  const generateWiederaufnahmenFromMeasurements = (measurements) => {
    debugLog('App.jsx:4634', 'generateWiederaufnahmenFromMeasurements: Entry', {measurementsCount:measurements?.length,measurements:measurements?.map(m=>({number:m.number,resumeNumber:m.resumeNumber,timestamp:m.timestamp,progressTime:m.progressTime}))}, 'C');
    if (!measurements || measurements.length === 0) {
      return [];
    }
    
    // Gruppiere Messpunkte nach resumeNumber
    const measurementsByResumeNumber = {};
    measurements.forEach(m => {
      const resumeNr = m.resumeNumber || 1;
      if (!measurementsByResumeNumber[resumeNr]) {
        measurementsByResumeNumber[resumeNr] = [];
      }
      measurementsByResumeNumber[resumeNr].push(m);
    });
    debugLog('App.jsx:4647', 'generateWiederaufnahmenFromMeasurements: Grouped by resumeNumber', {resumeNumbers:Object.keys(measurementsByResumeNumber),measurementsByResumeNumber:Object.keys(measurementsByResumeNumber).reduce((acc,key)=>{acc[key]=measurementsByResumeNumber[key].map(m=>({number:m.number,timestamp:m.timestamp,progressTime:m.progressTime}));return acc;},{})}, 'C');
    // Erstelle wiederaufnahmen für jede WA-Nr
    const wiederaufnahmen = [];
    Object.keys(measurementsByResumeNumber).sort((a, b) => parseInt(a) - parseInt(b)).forEach(resumeNr => {
      const resumeMeasurements = measurementsByResumeNumber[resumeNr];
      
      // Sortiere nach timestamp (falls vorhanden) oder nach number
      resumeMeasurements.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return new Date(a.timestamp) - new Date(b.timestamp);
        }
        return a.number - b.number;
      });
      
      const firstMeasurement = resumeMeasurements[0];
      const lastMeasurement = resumeMeasurements[resumeMeasurements.length - 1];
      
      // Berechne Startzeit durch Rückwärtsrechnung
      // progressTime ist in Hundertstel Minuten, 1 Hundertstel Minute = 600ms
      let startTime = null;
      let startDate = null;
      
      if (firstMeasurement.timestamp && firstMeasurement.progressTime !== undefined) {
        const measurementTime = new Date(firstMeasurement.timestamp);
        // progressTime in Millisekunden umrechnen: progressTime * 600
        const startTimeMs = measurementTime.getTime() - (firstMeasurement.progressTime * 600);
        startDate = new Date(startTimeMs);
        startTime = startDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }
      
      // Ende-Zeit vom letzten Messpunkt
      let endTime = null;
      let endDate = null;
      
      if (lastMeasurement.timestamp) {
        endDate = new Date(lastMeasurement.timestamp);
        endTime = endDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }
      
      // Datum: Verwende Startdatum, falls vorhanden, sonst Enddatum
      const datum = startDate 
        ? startDate.toLocaleDateString('de-DE')
        : (endDate ? endDate.toLocaleDateString('de-DE') : new Date().toLocaleDateString('de-DE'));
      
      const entry = {
        nummer: parseInt(resumeNr),
        datum: datum,
        beginnzeit: startTime || '',
        endzeit: endTime || '',
        dauer: '' // Dauer kann später berechnet werden
      };
      
      wiederaufnahmen.push(entry);
      debugLog('App.jsx:4692', 'generateWiederaufnahmenFromMeasurements: Generated entry', {resumeNr,entry,firstMeasurement:{number:firstMeasurement.number,timestamp:firstMeasurement.timestamp,progressTime:firstMeasurement.progressTime},lastMeasurement:{number:lastMeasurement.number,timestamp:lastMeasurement.timestamp},startTime,startDateISO:startDate?.toISOString(),endTime,endDateISO:endDate?.toISOString(),datum}, 'C');
    });    debugLog('App.jsx:4701', 'generateWiederaufnahmenFromMeasurements: Final result', {wiederaufnahmenCount:wiederaufnahmen.length,allWiederaufnahmen:wiederaufnahmen}, 'C');    return wiederaufnahmen;
  };

  // Prüfe, ob wiederaufnahmen fehlen oder unvollständig sind
  const checkWiederaufnahmenComplete = (vorderseiteData, measurements) => {
    if (!measurements || measurements.length === 0) {
      return { needsUpdate: false, reason: 'Keine Messpunkte vorhanden' };
    }
    
    // Extrahiere alle WA-Nr aus Messpunkten
    const resumeNumbers = [...new Set(measurements.map(m => m.resumeNumber || 1))].sort((a, b) => a - b);
    
    if (resumeNumbers.length === 0) {
      return { needsUpdate: false, reason: 'Keine WA-Nr in Messpunkten gefunden' };
    }
    
    const currentWiederaufnahmen = vorderseiteData?.wiederaufnahmen || [];
    
    // Prüfe, ob alle WA-Nr vorhanden sind
    const missingResumeNumbers = resumeNumbers.filter(resumeNr => {
      return !currentWiederaufnahmen.some(w => w.nummer === resumeNr);
    });
    
    // Prüfe, ob vorhandene wiederaufnahmen unvollständig sind (fehlende Beginn- oder Endzeit)
    const incompleteWiederaufnahmen = currentWiederaufnahmen.filter(w => {
      const hasResumeNr = resumeNumbers.includes(w.nummer);
      return hasResumeNr && (!w.beginnzeit || !w.endzeit);
    });
    
    if (missingResumeNumbers.length > 0 || incompleteWiederaufnahmen.length > 0) {
      return {
        needsUpdate: true,
        missingResumeNumbers,
        incompleteResumeNumbers: incompleteWiederaufnahmen.map(w => w.nummer),
        reason: `Fehlende WA-Nr: ${missingResumeNumbers.join(', ')}, Unvollständige: ${incompleteWiederaufnahmen.map(w => w.nummer).join(', ')}`
      };
    }
    
    return { needsUpdate: false, reason: 'Alle wiederaufnahmen vorhanden und vollständig' };
  };

  const updateVorderseiteField = (path, value) => {
    setVorderseiteData(prev => {
      if (!prev) return getDefaultVorderseiteData();
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          // Prüfe ob der nächste Key eine Array-Index ist
          const nextKey = keys[i + 1];
          if (nextKey && !isNaN(parseInt(nextKey))) {
            current[keys[i]] = [];
          } else {
            current[keys[i]] = {};
          }
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addArrayItem = (arrayPath, defaultItem) => {
    setVorderseiteData(prev => {
      if (!prev) return getDefaultVorderseiteData();
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = arrayPath.split('.');
      let current = newData;
      for (let i = 0; i < keys.length; i++) {
        if (!current[keys[i]]) current[keys[i]] = [];
        current = current[keys[i]];
      }
      if (!Array.isArray(current)) current = [];
      current.push({ ...defaultItem });
      return newData;
    });
  };

  const removeArrayItem = (arrayPath, index) => {
    setVorderseiteData(prev => {
      if (!prev) return getDefaultVorderseiteData();
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = arrayPath.split('.');
      let current = newData;
      for (let i = 0; i < keys.length; i++) {
        current = current[keys[i]];
      }
      if (Array.isArray(current)) {
        current.splice(index, 1);
      }
      return newData;
    });
  };

  const handleSaveMeasurements = (customFileName = null) => {
    if (measurements.length === 0) {
      alert(t('alerts.no_measurements_to_save'));
      return;
    }

    const data = {
      deviceName: deviceName,
      sessionName: selectedSession?.sessionName || '',
      resumeNumber: selectedSession?.resumeNumber || 1,
      timestamp: new Date().toISOString(),
      vorderseiteData: vorderseiteData || null,
      measurements: measurements.map(m => ({
        number: m.number,
        grade: m.grade,
        progressTime: m.progressTime,
        individualTime: m.individualTime,
        vorgabeTime: m.vorgabeTime || 0, // Vorgabezeit mit speichern
        activity: m.activity,
        timeType: m.timeType,
        note: m.note || '',
        isNote: m.isNote || false,
        timestamp: m.timestamp,
        resumeNumber: m.resumeNumber || selectedSession?.resumeNumber || 1,
        excluded: m.excluded || false
      }))
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Dateiname bestimmen
    let fileName;
    if (customFileName) {
      fileName = customFileName.endsWith('.json') ? customFileName : `${customFileName}.json`;
    } else {
      const dateStr = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const sessionName = selectedSession?.sessionName 
        ? selectedSession.sessionName.replace(/[^a-zA-Z0-9]/g, '_')
        : 'messpunkte';
      fileName = `${sessionName}_${deviceName}_${dateStr}.json`;
    }
    
    link.download = fileName;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handler für Zurücksetzen-Button: Prüft ob Uhr läuft oder Daten vorhanden
  const handleReset = () => {
    // Prüfe ob Sicherheitsabfrage nötig ist
    const needsConfirmation = isRunning || 
                              measurements.length > 0 || 
                              (selectedSession && selectedSession.uploaded !== true);
    
    if (needsConfirmation) {
      // Zeige Warn-Dialog
      setShowResetDialog(true);
    } else {
      // Direkt zurücksetzen wenn nichts zu verlieren ist
      performReset();
    }
  };

  // Führt das tatsächliche Zurücksetzen durch
  const performReset = () => {
    setIsRunning(false);
    setStartTime(null);
    setCurrentTime(0);
    setMeasurements([]);
    setLastMeasurementTime(0);
    setActivity('');
    setTimeType(timeTypes.length > 0 ? timeTypes[0].name : 'thb');
    setNote('');
    
    // Lösche temporäre Dateien beim Zurücksetzen
    deleteTempFile("_$$ECE_tmp.$$$");
    deleteTempFile("_$$EC2E_tmp.$$$");
  };

  // Bestätigung für Zurücksetzen
  const handleConfirmReset = () => {
    setShowResetDialog(false);
    performReset();
  };

  // Abbrechen für Zurücksetzen
  const handleCancelReset = () => {
    setShowResetDialog(false);
  };

  // Unterbrechung Toggle
  const handleInterruptionToggle = (checked) => {
    setIsInterrupted(checked);
    
    if (checked) {
      // Unterbrechung starten (ON)
      if (!isRunning || !selectedSession) return;
      
      // Demo-Modus: Maximal 20 Messpunkte
      if (selectedSession?.isDemo && measurements.length >= 20) {
        alert(t('alerts.demo_max_measurements_interruption'));
        setIsInterrupted(false);
        return;
      }
      
      // Tätigkeit speichern: Wert aus dem Input-Feld nehmen (wenn vorhanden), sonst "Unterbrechung"
      const trimmedActivity = activity.trim();
      // Resolve Kürzel zu Ablauftexten
      const resolvedActivity = trimmedActivity ? resolveActivityFromKuerzel(trimmedActivity) : 'Unterbrechung';
      const activityToSave = resolvedActivity || 'Unterbrechung';
      
      // Stelle sicher, dass progressTime immer eine ganze Zahl ist
      const progressTime = getCurrentTime();
      // Stelle sicher, dass individualTime immer eine ganze Zahl ist
      const individualTime = Math.round(progressTime - lastMeasurementTime);
      
      // Verwende die WA-Nr aus dem State (wurde in handleStart aktualisiert) oder aus der Session
      const currentResumeNumber = resumeNumber || selectedSession.resumeNumber || 1;
      
      const finalIndividualTime = Math.max(0, individualTime);
      const vorgabeTime = calculateVorgabeTime(finalIndividualTime, 0); // Unterbrechung hat grade 0
      
      // Messung mit Leistungsgrad 0 für Unterbrechung
      const interruptionMeasurement = {
        number: measurements.length + 1,
        grade: 0,
        progressTime: Math.round(progressTime), // Stelle sicher, dass es eine ganze Zahl ist
        individualTime: finalIndividualTime, // Stelle sicher, dass es nicht negativ ist
        vorgabeTime: vorgabeTime, // Vorgabezeit berechnen und speichern
        activity: activityToSave,
        timeType: timeType,
        note: 'Unterbrechung',
        timestamp: new Date().toISOString(),
        resumeNumber: currentResumeNumber,
        excluded: false
      };
      
      setMeasurements([...measurements, interruptionMeasurement]);
      // Stelle sicher, dass lastMeasurementTime immer eine ganze Zahl ist
      setLastMeasurementTime(Math.round(progressTime));
      setInterruptionMeasurementNumber(interruptionMeasurement.number);
      setActivity('');
      setSavedActivity(activityToSave);
      setSavedTimeType(timeType);
    } else {
      // Unterbrechung beenden (OFF)
      if (!isRunning || !selectedSession) return;
      
      // Tätigkeit wiederherstellen
      setActivity(savedActivity);
      setTimeType(savedTimeType);
      setInterruptionMeasurementNumber(null);
    }
  };
  
  // Aktualisiere Referenz für Keyboard-Shortcuts
  handleInterruptionToggleRef.current = handleInterruptionToggle;

  // Messung hinzufügen
  const handleAddMeasurement = (grade) => {
    if (!selectedSession) return;
    
    // Wenn "M1" aktiv ist und gespeicherte Werte vorhanden sind, stelle sie wieder her
    if (merke1Enabled && merke1Values) {
      // Setze Activity wieder
      if (merke1Values.activity) {
        setActivity(merke1Values.activity);
      }
      
      // Setze Kürzel wieder, wenn vorhanden
      if (merke1Values.kuerzel && ablaufDbData) {
        // Finde das Kürzel in ablaufDbData
        const kuerzelData = ablaufDbData.find(item => item.kuerzel === merke1Values.kuerzel);
        if (kuerzelData) {
          setSelectedKuerzel(kuerzelData);
          // Setze auch die Einflussgrößen-Werte
          if (merke1Values.einflussgroessenValues) {
            setEinflussgroessenValues({ ...merke1Values.einflussgroessenValues });
            einflussgroessenValuesRef.current = { ...merke1Values.einflussgroessenValues };
          }
        }
      } else if (merke1Values.einflussgroessenValues) {
        // Wenn kein Kürzel, aber EAs vorhanden sind, setze nur die EAs
        setEinflussgroessenValues({ ...merke1Values.einflussgroessenValues });
        einflussgroessenValuesRef.current = { ...merke1Values.einflussgroessenValues };
      }
      
      console.log('[Merke1] Werte wiederhergestellt:', merke1Values);
    }
    
    // Keine blockierende Warnung mehr - Status wird visuell angezeigt
    
    // Demo-Modus: Maximal 20 Messpunkte
    if (selectedSession?.isDemo && measurements.length >= 20) {
      alert(t('alerts.demo_max_measurements'));
      return;
    }
    
    // Wenn Unterbrechung aktiv ist, deaktiviere sie
    if (isInterrupted) {
      setIsInterrupted(false);
      setInterruptionMeasurementNumber(null);
    }
    
    // Prüfe zuerst, ob "wie26" oder ähnliches eingegeben wurde
    const wieResult = resolveWieMeasurement(activity);
    let resolvedActivity = activity;
    let resolvedTimeType = timeType;
    // In Portrait: Ref nutzen (synchron), um EA-Timing zwischen Tap und Leistungsgrad-Klick zu vermeiden
    let resolvedEinflussgroessen = (isPortrait && einflussgroessenValuesRef.current != null)
      ? einflussgroessenValuesRef.current
      : einflussgroessenValues;
    let resolvedSelectedKuerzel = selectedKuerzel;
    
    if (wieResult.success) {
      // "wie" wurde erkannt, verwende die kopierten Werte
      resolvedActivity = wieResult.activity || activity;
      resolvedTimeType = wieResult.timeType || timeType;
      resolvedEinflussgroessen = wieResult.einflussgroessen || {};
      resolvedSelectedKuerzel = wieResult.selectedKuerzel || null;
      // Aktualisiere auch State für die UI (wichtig für Einflussgrößen-Anzeige)
      setActivity(resolvedActivity);
      setTimeType(resolvedTimeType);
      setEinflussgroessenValues(resolvedEinflussgroessen);
      einflussgroessenValuesRef.current = resolvedEinflussgroessen;
      setSelectedKuerzel(resolvedSelectedKuerzel);
      console.log(`[Wie-Funktion] Werte von Messung kopiert und State aktualisiert: activity="${resolvedActivity}", timeType="${resolvedTimeType}", einflussgroessen=`, resolvedEinflussgroessen);
    } else {
      // Kein "wie", resolve Kürzel zu Ablauftexten
      resolvedActivity = resolveActivityFromKuerzel(activity);
      if (resolvedActivity !== activity) {
        // Aktualisiere activity-State mit den Ablauftexten
        setActivity(resolvedActivity);
      }
    }
    
    // Verwende die WA-Nr aus dem State (wurde in handleStart aktualisiert) oder aus der Session
    // Die WA-Nr sollte nicht aus den Messungen berechnet werden, da diese noch die alte WA-Nr haben können
    const currentResumeNumber = resumeNumber || selectedSession.resumeNumber || 1;    debugLog('App.jsx:1628', 'handleAddMeasurement: WA-Nr calculation', {currentResumeNumber,resumeNumberState:resumeNumber,selectedSessionResumeNumber:selectedSession.resumeNumber,measurementsCount:measurements.length,measurementsResumeNumbers:measurements.map(m=>m.resumeNumber)}, 'D');    // Stelle sicher, dass progressTime immer eine ganze Zahl ist
    const progressTime = isRunning ? getCurrentTime() : 0;
    // Stelle sicher, dass individualTime immer eine ganze Zahl ist
    const individualTime = isRunning ? Math.round(progressTime - lastMeasurementTime) : 0;
    
    // Sammle Einflussgrößen-Werte (nur wenn Werte vorhanden sind)
    // Verwende die resolved Werte von "wie" oder die aktuellen State-Werte
    const einflussgroessen = {};
    const kuerzelToUse = resolvedSelectedKuerzel || selectedKuerzel;
    const einflussValuesToUse = resolvedEinflussgroessen || einflussgroessenValues;
    
    if (kuerzelToUse && kuerzelToUse.einflussgroessen && Array.isArray(kuerzelToUse.einflussgroessen) && einflussValuesToUse) {
      kuerzelToUse.einflussgroessen.forEach(einfluss => {
        if (einfluss && einfluss.einflussname) {
          const einflussname = einfluss.einflussname.trim();
          if (einflussname && einflussValuesToUse[einflussname]) {
            einflussgroessen[einflussname] = einflussValuesToUse[einflussname];
          }
        }
      });
    } else if (einflussValuesToUse && Object.keys(einflussValuesToUse).length > 0) {
      // Falls keine Kürzel-Definition, aber direkte Werte vorhanden (z.B. von "wie")
      Object.keys(einflussValuesToUse).forEach(key => {
        if (einflussValuesToUse[key]) {
          einflussgroessen[key] = einflussValuesToUse[key];
        }
      });
    }
    
    const now = Date.now();
    const finalIndividualTime = Math.max(0, individualTime);
    const vorgabeTime = calculateVorgabeTime(finalIndividualTime, grade);
    
    const newMeasurement = {
      number: measurements.length + 1,
      grade: grade,
      progressTime: Math.round(progressTime), // Stelle sicher, dass es eine ganze Zahl ist
      individualTime: finalIndividualTime, // Stelle sicher, dass es nicht negativ ist
      vorgabeTime: vorgabeTime, // Vorgabezeit berechnen und speichern
      activity: resolvedActivity, // Verwende resolvedActivity (entweder von "wie" oder von Kürzel-Auflösung)
      kuerzel: kuerzelToUse?.kuerzel || null, // Kürzel speichern für Auswertungen
      timeType: resolvedTimeType, // Verwende resolvedTimeType (entweder von "wie" oder aktueller Wert)
      note: '',
      timestamp: new Date().toISOString(),
      resumeNumber: currentResumeNumber,
      excluded: false,
      einflussgroessen: Object.keys(einflussgroessen).length > 0 ? einflussgroessen : undefined,
      lastModified: now, // Delta-Tracking: Zeitpunkt der Erstellung/letzten Änderung
      lastUploaded: null // Delta-Tracking: Zeitpunkt des letzten erfolgreichen Uploads
    };    debugLog('App.jsx:1643', 'handleAddMeasurement: newMeasurement created', {newMeasurementResumeNumber:newMeasurement.resumeNumber,currentResumeNumber}, 'D');
    
    // Haptic Feedback / Vibration bei Messung
    if (hapticFeedbackEnabled) {
      // Prüfe ob iPad (kein Vibrationsmotor)
      const isIPad = /iPad/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (isIPad) {
        // Akustisches Feedback für iPad
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 800; // Tonhöhe
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);
        } catch (e) {
          console.debug('[Haptic Feedback] Audio-Feedback nicht verfügbar:', e);
        }
      } else {
        // Vibration für iPhone und andere Geräte
        if (navigator.vibrate) {
          try {
            navigator.vibrate(100); // 100ms Vibration
          } catch (e) {
            console.debug('[Haptic Feedback] Vibration nicht verfügbar:', e);
          }
        }
      }
    }
    
    // Neuberechnung der Einzelzeiten nach Hinzufügen (berücksichtigt ausgeklammerte Messpunkte)
    const updatedMeasurements = [...measurements, newMeasurement];
    const recalculatedMeasurements = recalculateIndividualTimes(updatedMeasurements);
    
    // Speichere temporäre Fotos für diesen Messpunkt
    const photosToSave = [...pendingPhotos]; // Kopie für async Operation
    if (photosToSave.length > 0 && selectedSession?.id) {
      const newMeasurementNumber = newMeasurement.number;
      
      // Zeige Fotos sofort an (optimistisches Update)
      setMeasurementPhotos(prev => ({
        ...prev,
        [newMeasurementNumber]: photosToSave.map((photo, idx) => ({
          id: `temp-${newMeasurementNumber}-${idx}`, // Temporäre ID
          fileData: photo.fileData,
          fileName: photo.fileName
        }))
      }));
      
      // Lösche temporäre Fotos sofort (UI-Feedback)
      setPendingPhotos([]);
      
      // Speichere Fotos asynchron
      (async () => {
        try {
          console.log(`[Photos] Starte Speicherung von ${photosToSave.length} Fotos für Messung ${newMeasurementNumber}`);
          const savedPhotoIds = [];
          for (const pendingPhoto of photosToSave) {
            if (!pendingPhoto.file) {
              console.error('[Photos] Kein File-Objekt verfügbar für Foto:', pendingPhoto.fileName);
              continue;
            }
            console.log(`[Photos] Speichere Foto:`, { fileName: pendingPhoto.fileName, fileType: pendingPhoto.file.constructor.name, fileSize: pendingPhoto.file.size });
            // File ist bereits komprimiert, überspringe Komprimierung
            const photoId = await savePhoto(newMeasurementNumber, selectedSession.id, pendingPhoto.file, pendingPhoto.fileName, true);
            console.log(`[Photos] Foto gespeichert mit ID:`, photoId);
            savedPhotoIds.push(photoId);
          }
          console.log(`[Photos] Alle Fotos gespeichert, IDs:`, savedPhotoIds);
          
          // Lade Fotos neu für diese Messung (mit echten IDs)
          const photos = await loadPhotos(newMeasurementNumber, selectedSession.id);
          console.log(`[Photos] ${photos.length} Fotos für Messung ${newMeasurementNumber} geladen:`, photos.map(p => ({ id: p.id, fileName: p.fileName, hasData: !!p.fileData })));
          
          setMeasurementPhotos(prev => ({
            ...prev,
            [newMeasurementNumber]: photos.map(photo => ({
              id: photo.id,
              fileData: photo.fileData,
              fileName: photo.fileName
            }))
          }));
          
          // Stelle sicher, dass die Fotos auch in der Hauptliste aktualisiert werden
          // Der useEffect wird dies auch tun, aber wir aktualisieren es sofort
          if (photos.length > 0) {
            console.log(`[Photos] Fotos erfolgreich in measurementPhotos gespeichert für Messung ${newMeasurementNumber}`);
          } else {
            console.warn(`[Photos] WARNUNG: Keine Fotos geladen für Messung ${newMeasurementNumber}, obwohl ${photosToSave.length} gespeichert wurden!`);
          }
        } catch (error) {
          console.error('Fehler beim Speichern der temporären Fotos:', error);
          // Bei Fehler: Fotos aus measurementPhotos entfernen und wieder zu pendingPhotos hinzufügen
          setMeasurementPhotos(prev => {
            const updated = { ...prev };
            delete updated[newMeasurementNumber];
            return updated;
          });
          setPendingPhotos(prev => [...prev, ...photosToSave]);
        }
      })();
    }
    
    setMeasurements(recalculatedMeasurements);
    if (isRunning) {
      // Stelle sicher, dass lastMeasurementTime immer eine ganze Zahl ist
      setLastMeasurementTime(Math.round(progressTime));
    }
    
    // Aktualisiere Endzeit in Vorderseite mit dem Zeitpunkt des letzten Messpunkts
    if (newMeasurement.timestamp) {
      const measurementDate = new Date(newMeasurement.timestamp);
      const timeStr = measurementDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      
      setVorderseiteData(prev => {
        const currentData = prev || getDefaultVorderseiteData();
        const newData = JSON.parse(JSON.stringify(currentData));
        // Stelle sicher, dass wiederaufnahmen ein Array ist
        if (!newData.wiederaufnahmen || !Array.isArray(newData.wiederaufnahmen)) {
          newData.wiederaufnahmen = [];
        }
        
        // Prüfe, ob diese WA-Nr bereits existiert
        const existingIndex = newData.wiederaufnahmen.findIndex(w => w.nummer === currentResumeNumber);
        
        if (existingIndex >= 0) {
          // WA-Nr existiert bereits: Aktualisiere Endzeit (nur wenn noch nicht gesetzt)
          if (!newData.wiederaufnahmen[existingIndex].endzeit) {
          newData.wiederaufnahmen[existingIndex].endzeit = timeStr;
          }
          // Stelle sicher, dass Datum gesetzt ist
          if (!newData.wiederaufnahmen[existingIndex].datum) {
            newData.wiederaufnahmen[existingIndex].datum = measurementDate.toLocaleDateString('de-DE');
          }
          // Berechne Dauer, wenn beginnzeit und endzeit vorhanden sind
          const entry = newData.wiederaufnahmen[existingIndex];
          if (entry.beginnzeit && entry.endzeit) {
            entry.dauer = calculateDuration(entry.beginnzeit, entry.endzeit, entry.datum);
          }
        } else {
          // WA-Nr existiert noch nicht: Füge neuen Eintrag hinzu (nur mit Endzeit)
          newData.wiederaufnahmen.push({
            nummer: currentResumeNumber,
            datum: measurementDate.toLocaleDateString('de-DE'),
            beginnzeit: '',
            endzeit: timeStr,
            dauer: ''
          });
        }
        return newData;
      });
      
      // Speichere automatisch (ohne Alert)
      if (backendUrl && selectedSession?.id) {
        saveVorderseite(true);
      }
    }
    
    // Wenn "M1" aktiv ist, speichere die aktuellen Werte (NACH dem Erstellen des Messpunkts)
    // und lasse die Felder erhalten (nicht leeren)
    if (merke1Enabled) {
      const valuesToSave = {
        kuerzel: selectedKuerzel?.kuerzel || null,
        activity: activity,
        einflussgroessenValues: { ...resolvedEinflussgroessen }
      };
      setMerke1Values(valuesToSave);
      localStorage.setItem('stoppuhr_merke1_values', JSON.stringify(valuesToSave));
      console.log('[Merke1] Werte gespeichert nach Messpunkt:', valuesToSave);
      // Felder NICHT leeren, wenn M1 aktiv ist
    } else {
      // Nur wenn M1 NICHT aktiv ist, Felder leeren
    setActivity('');
    // Einflussgrößen-Werte zurücksetzen nach dem Hinzufügen
    setEinflussgroessenValues({});
    einflussgroessenValuesRef.current = {};
    // Kürzel zurücksetzen, damit EA-Felder nicht mehr angezeigt werden
    setSelectedKuerzel(null);
    }
  };
  
  // Hilfsfunktion: Suche nach Kürzel im Tätigkeitsfeld und ersetze durch Ablauftexte
  // Wenn keine Ablauf-DB vorhanden ist, wird der ursprüngliche Text zurückgegeben (alte Funktionalität bleibt erhalten)
  const resolveActivityFromKuerzel = (activityText) => {
    // Wenn kein Text vorhanden oder keine Ablauf-DB geladen, gebe Original zurück
    if (!activityText || !activityText.trim()) {
      return activityText;
    }
    
    // Wenn keine Ablauf-DB vorhanden ist, gebe Original zurück (alte Funktionalität)
    if (!ablaufDbData || !Array.isArray(ablaufDbData) || ablaufDbData.length === 0) {
      return activityText;
    }
    
    const trimmedActivity = activityText.trim();
    
    // Suche nach exaktem Kürzel-Match
    const matchingKuerzel = ablaufDbData.find(k => 
      k && k.kuerzel && k.kuerzel.trim() === trimmedActivity
    );
    
    if (matchingKuerzel) {
      // Verwende die Ablauftexte (fullText oder beschreibung)
      const resolvedActivity = matchingKuerzel.fullText || matchingKuerzel.beschreibung || matchingKuerzel.kuerzel;
      if (resolvedActivity && resolvedActivity.trim()) {
        console.log(`[Kuerzel-Resolver] Kürzel "${trimmedActivity}" gefunden, verwende Ablauftexte: "${resolvedActivity}"`);
        return resolvedActivity.trim();
      }
    }
    
    // Kein Match gefunden, verwende Original (alte Funktionalität bleibt erhalten)
    return activityText;
  };

  // Aktualisiere Referenz für Keyboard-Shortcuts
  handleAddMeasurementRef.current = handleAddMeasurement;

  // Hilfsfunktion: Prüfe ob "wie26" oder "wie" + Nummer eingegeben wurde und kopiere Tätigkeit
  // Gibt ein Objekt mit { success: boolean, activity?: string, timeType?: string, einflussgroessen?: object, selectedKuerzel?: object } zurück
  const resolveWieMeasurement = (activityText) => {
    const trimmed = activityText.trim();
    
    // Prüfe ob "wie" gefolgt von einer Zahl (z.B. "wie26", "wie 26", "wie26", etc.)
    const wieMatch = trimmed.match(/^wie\s*(\d+)$/i);
    if (wieMatch) {
      const measurementNumber = parseInt(wieMatch[1], 10);
      
      // Suche nach der Messung mit dieser Nummer
      const targetMeasurement = measurements.find(m => m.number === measurementNumber);
      
      if (targetMeasurement && targetMeasurement.activity && !targetMeasurement.isNote) {
        // Finde das zugehörige Kürzel, um die Einflussgrößen-Definitionen zu bekommen
        let resolvedSelectedKuerzel = null;
        let resolvedEinflussgroessen = {};
        
        if (ablaufDbData && ablaufDbData.length > 0) {
          const matchingKuerzel = ablaufDbData.find(k => 
            k.beschreibung === targetMeasurement.activity || 
            k.fullText === targetMeasurement.activity ||
            k.kuerzel === targetMeasurement.activity
          );
          if (matchingKuerzel && matchingKuerzel.einflussgroessen) {
            // Kürzel gefunden: Kopiere EA-Werte von der vorherigen Messung
            resolvedSelectedKuerzel = matchingKuerzel;
            
            // Kopiere EA-Werte von der vorherigen Messung, falls vorhanden
            if (targetMeasurement.einflussgroessen && Object.keys(targetMeasurement.einflussgroessen).length > 0) {
              resolvedEinflussgroessen = { ...targetMeasurement.einflussgroessen };
            }
            
            // Initialisiere auch leere Werte für alle Einflussgrößen des Kürzels
            const initialValues = { ...resolvedEinflussgroessen };
            matchingKuerzel.einflussgroessen.forEach(einfluss => {
              if (einfluss && einfluss.einflussname && typeof einfluss.einflussname.trim === 'function') {
                const name = einfluss.einflussname.trim();
                if (name && !initialValues[name]) {
                  initialValues[name] = '';
                }
              }
            });
            resolvedEinflussgroessen = initialValues;
          } else {
            // Kein passendes Kürzel gefunden: Leere EA-Werte
            resolvedSelectedKuerzel = null;
            resolvedEinflussgroessen = {};
          }
        } else {
          // Keine Ablauf-DB geladen: Leere EA-Werte
          resolvedSelectedKuerzel = null;
          resolvedEinflussgroessen = {};
        }
        
        console.log(`[Wie-Funktion] Tätigkeit von Messung #${measurementNumber} kopiert: "${targetMeasurement.activity}"`);
        
        return {
          success: true,
          activity: targetMeasurement.activity,
          timeType: targetMeasurement.timeType || 'thb',
          einflussgroessen: resolvedEinflussgroessen,
          selectedKuerzel: resolvedSelectedKuerzel
        };
      } else {
        console.warn(`[Wie-Funktion] Messung #${measurementNumber} nicht gefunden oder ist eine Notiz`);
        return { success: false };
      }
    }
    
    return { success: false }; // Kein "wie" erkannt
  };

  // Hilfsfunktion: Aktualisiere State basierend auf "wie"-Ergebnis
  const applyWieMeasurement = (wieResult) => {
    if (wieResult.success) {
      setActivity(wieResult.activity || '');
      setTimeType(wieResult.timeType || 'thb');
      setEinflussgroessenValues(wieResult.einflussgroessen || {});
      setSelectedKuerzel(wieResult.selectedKuerzel || null);
      return wieResult;
    }
    return null;
  };

  // Messung ohne Leistungsgrad hinzufügen (für Enter-Taste im Tätigkeitsfeld)
  const handleAddMeasurementWithoutGrade = () => {
    if (!selectedSession || !activity.trim()) return;
    
    // Prüfe zuerst, ob "wie26" oder ähnliches eingegeben wurde
    const wieResult = resolveWieMeasurement(activity);
    let resolvedActivity = activity;
    
    if (wieResult.success) {
      // "wie" wurde erkannt, aktualisiere State und verwende die kopierten Werte
      applyWieMeasurement(wieResult);
      // Keine neue Messung erstellen, nur kopieren
      return;
    }
    
    // Kein "wie", resolve Kürzel zu Ablauftexten
    resolvedActivity = resolveActivityFromKuerzel(activity);
    if (resolvedActivity !== activity) {
      // Aktualisiere activity-State mit den Ablauftexten
      setActivity(resolvedActivity);
    }
    
    // Keine blockierende Warnung mehr - Status wird visuell angezeigt
    
    // Demo-Modus: Maximal 20 Messpunkte
    if (selectedSession?.isDemo && measurements.length >= 20) {
      alert(t('alerts.demo_max_measurements'));
      return;
    }
    
    // Wenn Unterbrechung aktiv ist, deaktiviere sie
    if (isInterrupted) {
      setIsInterrupted(false);
      setInterruptionMeasurementNumber(null);
    }
    
    const currentResumeNumber = resumeNumber || selectedSession.resumeNumber || 1;
    // Stelle sicher, dass progressTime immer eine ganze Zahl ist
    const progressTime = isRunning ? getCurrentTime() : 0;
    // Stelle sicher, dass individualTime immer eine ganze Zahl ist
    const individualTime = isRunning ? Math.round(progressTime - lastMeasurementTime) : 0;
    
    // Sammle Einflussgrößen-Werte (nur wenn Werte vorhanden sind)
    const einflussgroessen = {};
    if (selectedKuerzel && selectedKuerzel.einflussgroessen && Array.isArray(selectedKuerzel.einflussgroessen) && einflussgroessenValues) {
      selectedKuerzel.einflussgroessen.forEach(einfluss => {
        if (einfluss && einfluss.einflussname) {
          const einflussname = einfluss.einflussname.trim();
          if (einflussname && einflussgroessenValues[einflussname]) {
            einflussgroessen[einflussname] = einflussgroessenValues[einflussname];
          }
        }
      });
    }
    
    const now = Date.now();
    const newMeasurement = {
      number: measurements.length + 1,
      grade: 0, // Kein Leistungsgrad
      progressTime: Math.round(progressTime), // Stelle sicher, dass es eine ganze Zahl ist
      individualTime: Math.max(0, individualTime), // Stelle sicher, dass es nicht negativ ist
      activity: resolvedActivity, // Verwende resolvedActivity statt activity
      kuerzel: selectedKuerzel?.kuerzel || null, // Kürzel speichern für Auswertungen
      timeType: timeType,
      note: '',
      timestamp: new Date().toISOString(),
      resumeNumber: currentResumeNumber,
      excluded: false,
      einflussgroessen: Object.keys(einflussgroessen).length > 0 ? einflussgroessen : undefined,
      lastModified: now, // Delta-Tracking: Zeitpunkt der Erstellung/letzten Änderung
      lastUploaded: null // Delta-Tracking: Zeitpunkt des letzten erfolgreichen Uploads
    };
    
    // Haptic Feedback / Vibration bei Messung
    if (hapticFeedbackEnabled) {
      // Prüfe ob iPad (kein Vibrationsmotor)
      const isIPad = /iPad/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      if (isIPad) {
        // Akustisches Feedback für iPad
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);
          
          oscillator.frequency.value = 800; // Tonhöhe
          oscillator.type = 'sine';
          
          gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
          
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);
        } catch (e) {
          console.debug('[Haptic Feedback] Audio-Feedback nicht verfügbar:', e);
        }
      } else {
        // Vibration für iPhone und andere Geräte
        if (navigator.vibrate) {
          try {
            navigator.vibrate(100); // 100ms Vibration
          } catch (e) {
            console.debug('[Haptic Feedback] Vibration nicht verfügbar:', e);
          }
        }
      }
    }
    
    // Neuberechnung der Einzelzeiten nach Hinzufügen (berücksichtigt ausgeklammerte Messpunkte)
    const updatedMeasurements = [...measurements, newMeasurement];
    const recalculatedMeasurements = recalculateIndividualTimes(updatedMeasurements);
    
    // Speichere temporäre Fotos für diesen Messpunkt
    const photosToSave = [...pendingPhotos]; // Kopie für async Operation
    if (photosToSave.length > 0 && selectedSession?.id) {
      const newMeasurementNumber = newMeasurement.number;
      
      // Zeige Fotos sofort an (optimistisches Update)
      setMeasurementPhotos(prev => ({
        ...prev,
        [newMeasurementNumber]: photosToSave.map((photo, idx) => ({
          id: `temp-${newMeasurementNumber}-${idx}`, // Temporäre ID
          fileData: photo.fileData,
          fileName: photo.fileName
        }))
      }));
      
      // Lösche temporäre Fotos sofort (UI-Feedback)
      setPendingPhotos([]);
      
      // Speichere Fotos asynchron
      (async () => {
        try {
          console.log(`[Photos] Starte Speicherung von ${photosToSave.length} Fotos für Messung ${newMeasurementNumber}`);
          const savedPhotoIds = [];
          for (const pendingPhoto of photosToSave) {
            if (!pendingPhoto.file) {
              console.error('[Photos] Kein File-Objekt verfügbar für Foto:', pendingPhoto.fileName);
              continue;
            }
            console.log(`[Photos] Speichere Foto:`, { fileName: pendingPhoto.fileName, fileType: pendingPhoto.file.constructor.name, fileSize: pendingPhoto.file.size });
            // File ist bereits komprimiert, überspringe Komprimierung
            const photoId = await savePhoto(newMeasurementNumber, selectedSession.id, pendingPhoto.file, pendingPhoto.fileName, true);
            console.log(`[Photos] Foto gespeichert mit ID:`, photoId);
            savedPhotoIds.push(photoId);
          }
          console.log(`[Photos] Alle Fotos gespeichert, IDs:`, savedPhotoIds);
          
          // Lade Fotos neu für diese Messung (mit echten IDs)
          const photos = await loadPhotos(newMeasurementNumber, selectedSession.id);
          console.log(`[Photos] ${photos.length} Fotos für Messung ${newMeasurementNumber} geladen:`, photos.map(p => ({ id: p.id, fileName: p.fileName, hasData: !!p.fileData })));
          
          setMeasurementPhotos(prev => ({
            ...prev,
            [newMeasurementNumber]: photos.map(photo => ({
              id: photo.id,
              fileData: photo.fileData,
              fileName: photo.fileName
            }))
          }));
          
          // Stelle sicher, dass die Fotos auch in der Hauptliste aktualisiert werden
          // Der useEffect wird dies auch tun, aber wir aktualisieren es sofort
          if (photos.length > 0) {
            console.log(`[Photos] Fotos erfolgreich in measurementPhotos gespeichert für Messung ${newMeasurementNumber}`);
          } else {
            console.warn(`[Photos] WARNUNG: Keine Fotos geladen für Messung ${newMeasurementNumber}, obwohl ${photosToSave.length} gespeichert wurden!`);
          }
        } catch (error) {
          console.error('Fehler beim Speichern der temporären Fotos:', error);
          // Bei Fehler: Fotos aus measurementPhotos entfernen und wieder zu pendingPhotos hinzufügen
          setMeasurementPhotos(prev => {
            const updated = { ...prev };
            delete updated[newMeasurementNumber];
            return updated;
          });
          setPendingPhotos(prev => [...prev, ...photosToSave]);
        }
      })();
    }
    
    setMeasurements(recalculatedMeasurements);
    if (isRunning) {
      // Stelle sicher, dass lastMeasurementTime immer eine ganze Zahl ist
      setLastMeasurementTime(Math.round(progressTime));
    }
    
    // Aktualisiere Endzeit in Vorderseite mit dem Zeitpunkt des letzten Messpunkts
    if (newMeasurement.timestamp) {
      const measurementDate = new Date(newMeasurement.timestamp);
      const timeStr = measurementDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      
      setVorderseiteData(prev => {
        const currentData = prev || getDefaultVorderseiteData();
        const newData = JSON.parse(JSON.stringify(currentData));
        
        // Stelle sicher, dass wiederaufnahmen ein Array ist
        if (!newData.wiederaufnahmen || !Array.isArray(newData.wiederaufnahmen)) {
          newData.wiederaufnahmen = [];
        }
        
        // Prüfe, ob diese WA-Nr bereits existiert
        const existingIndex = newData.wiederaufnahmen.findIndex(w => w.nummer === currentResumeNumber);
        
        if (existingIndex >= 0) {
          // WA-Nr existiert bereits: Aktualisiere Endzeit (nur wenn noch nicht gesetzt)
          if (!newData.wiederaufnahmen[existingIndex].endzeit) {
          newData.wiederaufnahmen[existingIndex].endzeit = timeStr;
          }
          // Stelle sicher, dass Datum gesetzt ist
          if (!newData.wiederaufnahmen[existingIndex].datum) {
            newData.wiederaufnahmen[existingIndex].datum = measurementDate.toLocaleDateString('de-DE');
          }
          // Berechne Dauer, wenn beginnzeit und endzeit vorhanden sind
          const entry = newData.wiederaufnahmen[existingIndex];
          if (entry.beginnzeit && entry.endzeit) {
            entry.dauer = calculateDuration(entry.beginnzeit, entry.endzeit, entry.datum);
          }
        } else {
          // WA-Nr existiert noch nicht: Füge neuen Eintrag hinzu (nur mit Endzeit)
          newData.wiederaufnahmen.push({
            nummer: currentResumeNumber,
            datum: measurementDate.toLocaleDateString('de-DE'),
            beginnzeit: '',
            endzeit: timeStr,
            dauer: ''
          });
        }
        
        return newData;
      });
      
      // Speichere automatisch (ohne Alert)
      if (backendUrl && selectedSession?.id) {
        saveVorderseite(true);
      }
    }
    
    setActivity('');
    // Einflussgrößen-Werte zurücksetzen nach dem Hinzufügen
    setEinflussgroessenValues({});
    // Kürzel zurücksetzen, damit EA-Felder nicht mehr angezeigt werden
    setSelectedKuerzel(null);
  };

  // Notiz als separate Messung speichern
  const handleSaveNote = () => {
    if (!selectedSession || !note.trim()) return;
    
    // Demo-Modus: Maximal 20 Messpunkte
    if (selectedSession?.isDemo && measurements.length >= 20) {
      alert(t('alerts.demo_max_measurements'));
      return;
    }
    
    // Wenn Messung läuft, verwende aktuelle Zeit, sonst 0
    // Stelle sicher, dass progressTime immer eine ganze Zahl ist
    const progressTime = isRunning ? getCurrentTime() : 0;
    
    // Verwende die WA-Nr aus dem State (wurde in handleStart aktualisiert) oder aus der Session
    const currentResumeNumber = resumeNumber || selectedSession.resumeNumber || 1;
    
    const noteMeasurement = {
      number: measurements.length + 1,
      grade: 0,
      progressTime: Math.round(progressTime), // Stelle sicher, dass es eine ganze Zahl ist
      individualTime: 0,
      activity: '',
      timeType: 'note',
      note: note,
      timestamp: new Date().toISOString(),
      isNote: true,
      resumeNumber: currentResumeNumber,
      excluded: false
    };
    
    // Neuberechnung der Einzelzeiten nach Hinzufügen (berücksichtigt ausgeklammerte Messpunkte)
    const updatedMeasurements = [...measurements, noteMeasurement];
    const recalculatedMeasurements = recalculateIndividualTimes(updatedMeasurements);
    
    setMeasurements(recalculatedMeasurements);
    setNote('');
  };

  // URL-Normalisierung: Entfernt trailing slashes, normalisiert Base-Paths
  const normalizeBackendUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    
    // Entferne Whitespace
    let normalized = url.trim();
    
    // Entferne trailing slash
    normalized = normalized.replace(/\/+$/, '');
    
    // Wenn die URL leer ist, zurückgeben
    if (!normalized) return url;
    
    // BEHALTE Base-Paths (z.B. /zeitece), da das Backend diese unterstützt
    // Das Backend kann unter einem Base-Path laufen (z.B. /zeitece/api/...)
    // Wir entfernen den Base-Path NICHT mehr, da das Backend ihn unterstützt
    
    // Debug-Log der Normalisierung (nur wenn sich etwas geändert hat)
    if (normalized !== url) {
      console.log('[URL-Normalisierung]', {
        original: url,
        normalized: normalized
      });
    }
    
    return normalized;
  };

  // Health-Check: Prüft, ob Backend erreichbar ist
  const checkBackendHealth = async (baseUrl) => {
    if (!baseUrl) {
      return { healthy: false, error: 'Keine Backend-URL angegeben' };
    }

    // navigator.onLine als Hinweis loggen, aber NICHT als harten Abbruch – ist oft unzuverlässig (Standby, VPN, …).
    // Wir versuchen fetch trotzdem, um „Offline bleibt bis Refresh“ zu vermeiden.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.warn('[Health-Check] navigator.onLine ist false, versuche trotzdem fetch (Browser kann unzuverlässig sein)');
    }

    const normalizedUrl = normalizeBackendUrl(baseUrl);
    
    // Versuche verschiedene Health-Check-Endpunkte
    // HINWEIS: /api/license/check ist POST-only, nicht für Health-Check geeignet
    // Wenn die URL einen BASE_PATH enthält (z. B. …/zeitece), aber das Backend ohne BASE_PATH
    // nur unter /health auf der Origin läuft, würden sonst nur …/zeitece/health probiert werden → 404.
    // Daher zusätzlich Origin-Fallbacks, wenn pathname nicht nur "/" ist.
    const healthEndpoints = [];
    const pushEndpoint = (url) => {
      if (url && !healthEndpoints.includes(url)) healthEndpoints.push(url);
    };
    pushEndpoint(`${normalizedUrl}/health`);
    pushEndpoint(`${normalizedUrl}/api/health`);
    try {
      const u = new URL(normalizedUrl);
      const path = u.pathname || '';
      if (path.length > 1) {
        pushEndpoint(`${u.origin}/health`);
        pushEndpoint(`${u.origin}/api/health`);
      }
    } catch (_) {
      // keine gültige URL — nur die beiden ersten Varianten
    }

    let lastError = null;
    let hasNetworkError = false;

    for (const endpoint of healthEndpoints) {
      try {
        console.log('[Health-Check] Prüfe Endpoint:', endpoint);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 Sekunden Timeout
        
        const response = await fetch(endpoint, {
          method: 'GET',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store'
        });
        
        clearTimeout(timeoutId);
        
        // Wenn wir eine Antwort bekommen (auch 404 ist besser als kein Response)
        if (response.status !== 404) {
          console.log('[Health-Check] Erfolgreich:', {
            endpoint,
            status: response.status,
            statusText: response.statusText
          });
          return { healthy: true, endpoint, status: response.status };
        }
      } catch (error) {
        // Unterscheide zwischen Network-Fehlern (Backend gestoppt/Offline) und anderen Fehlern
        if (error.name === 'AbortError') {
          console.warn('[Health-Check] Timeout für:', endpoint);
          hasNetworkError = true;
        } else {
          // Prüfe auf verschiedene Network-Fehler-Muster
          const errorMessage = error.message || '';
          const errorString = String(error);
          
          // Erkenne Network-Fehler auch ohne spezifische Meldung (iOS/Safari kann generische Fehler werfen)
          if (errorMessage.includes('Failed to fetch') || 
              errorMessage.includes('NetworkError') ||
              errorMessage.includes('Network request failed') ||
              errorMessage.includes('ERR_CONNECTION_REFUSED') ||
              errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
              errorString.includes('TypeError') ||
              errorString.includes('NetworkError') ||
              !errorMessage || // Leere Fehlermeldung kann auch Network-Fehler sein (iOS)
              error.name === 'TypeError') { // TypeError wird oft bei Network-Fehlern geworfen
            console.warn('[Health-Check] Network-Fehler (Backend möglicherweise gestoppt/Offline):', endpoint, error.message || errorString, 'Error name:', error.name);
            hasNetworkError = true;
          } else {
            console.warn('[Health-Check] Fehler für:', endpoint, error.message || errorString);
          }
        }
        // Weiter zum nächsten Endpoint
        continue;
      }
    }

    // Wenn alle Endpoints fehlgeschlagen sind:
    // - Bei Network-Fehlern: Backend ist definitiv nicht verfügbar
    // - Bei anderen Fehlern: Unbekannt, aber möglicherweise verfügbar
    if (hasNetworkError) {
      console.warn('[Health-Check] Backend nicht erreichbar (Network-Fehler)');
      return { healthy: false, error: 'Backend nicht erreichbar - möglicherweise gestoppt oder offline' };
    }
    
    // Wenn kein Endpoint funktioniert, aber keine Network-Fehler: Unbekannt
    console.warn('[Health-Check] Kein Health-Check-Endpoint erreichbar, aber Upload wird trotzdem versucht');
    return { healthy: null, error: 'Kein Health-Check-Endpoint erreichbar, aber Upload wird versucht' };
  };

  // Erstellt vollständige API-URL mit Normalisierung
  const buildApiUrl = (baseUrl, endpoint) => {
    const normalized = normalizeBackendUrl(baseUrl);
    // Stelle sicher, dass endpoint mit / beginnt
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${normalized}${normalizedEndpoint}`;
  };

  // Auto-Upload
  const autoUpload = async () => {
    // Prüfe API-Kompatibilität (non-blocking Warnung)
    if (!apiCompatible && compatibilityWarning) {
      console.warn('[Auto-Upload] API-Kompatibilitäts-Warnung:', compatibilityWarning);
      // Upload wird trotzdem versucht (Graceful Degradation)
    }

    // Prüfe Demo-Session - kein Upload für Demo-Sessions
    if (selectedSession?.isDemo) {
      console.log('[Auto-Upload] Demo-Session erkannt, Upload übersprungen');
      return;
    }
    
    if (!licenseStatus || !selectedSession || !backendUrl || measurements.length === 0) {
      return;
    }
    
    // Markiere Upload als gestartet
    setIsAutoUploading(true);
    lastUploadAttemptRef.current = Date.now();

    // Normalisiere URL und führe Health-Check durch
    const normalizedUrl = normalizeBackendUrl(backendUrl);
    const uploadUrl = buildApiUrl(normalizedUrl, '/api/measurements/upload');
    
    console.log('[Auto-Upload] Starte Upload:', {
      originalUrl: backendUrl,
      normalizedUrl: normalizedUrl,
      uploadUrl: uploadUrl,
      measurementsCount: measurements.length,
      sessionId: selectedSession.id
    });

    // Health-Check (optional, nicht blockierend)
    try {
      const healthCheck = await checkBackendHealth(normalizedUrl);
      console.log('[Auto-Upload] Health-Check Ergebnis:', healthCheck);
    } catch (healthError) {
      console.warn('[Auto-Upload] Health-Check fehlgeschlagen (nicht kritisch):', healthError);
    }

    try {
      // Stelle sicher, dass Vorderseite-Daten vorhanden sind
      const currentVorderseiteData = {
        ...(vorderseiteData || getDefaultVorderseiteData()),
        // Stelle sicher, dass Nachkommastellen-Einstellungen immer enthalten sind
        decimal_places_vorderseite: vorderseiteData?.decimal_places_vorderseite ?? decimalPlacesVorderseite ?? 4,
        decimal_places_sollzeiten: vorderseiteData?.decimal_places_sollzeiten ?? decimalPlacesSollzeiten ?? 4,
        // Device-Informationen (unsichtbar in WA-Liste)
        device_id: getDeviceId(),
        device_name: getDeviceName()
      };
      console.log('[Auto-Upload] Vorderseite-Daten:', {
        hasVorderseiteData: !!vorderseiteData,
        wiederaufnahmen: currentVorderseiteData?.wiederaufnahmen,
        wiederaufnahmenCount: currentVorderseiteData?.wiederaufnahmen?.length || 0,
        decimal_places_vorderseite: currentVorderseiteData.decimal_places_vorderseite,
        decimal_places_sollzeiten: currentVorderseiteData.decimal_places_sollzeiten
      });
      
      // Prüfe Anzahl der Messpunkte im Backend
      let backendMeasurementCount = null;
      try {
        const sessionInfoUrl = buildApiUrl(normalizedUrl, `/api/admin/sessions/${selectedSession.id}/measurements`);
        const sessionInfoResponse = await fetch(sessionInfoUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        if (sessionInfoResponse.ok) {
          const sessionInfo = await sessionInfoResponse.json();
          backendMeasurementCount = sessionInfo.session?.measurement_count || 0;
        }
      } catch (error) {
        console.warn('[Auto-Upload] Fehler beim Abrufen der Backend-Anzahl (nicht kritisch):', error);
      }
      
      // Berechne Delta-Messungen (nur geänderte/neue)
      // Wenn Backend-Anzahl nicht mit Frontend-Anzahl übereinstimmt, lade alle Messpunkte hoch
      let deltaMeasurements = getDeltaMeasurements(measurements);
      if (backendMeasurementCount !== null && backendMeasurementCount !== measurements.length) {
        console.warn(`[Auto-Upload] Anzahl-Unterschied erkannt: Frontend=${measurements.length}, Backend=${backendMeasurementCount}. Lade alle Messpunkte hoch.`);
        deltaMeasurements = measurements; // Lade alle Messpunkte hoch
      }
      console.log('[Auto-Upload] Delta-Messungen:', {
        total: measurements.length,
        delta: deltaMeasurements.length,
        deltaNumbers: deltaMeasurements.map(m => m.number),
        backendCount: backendMeasurementCount
      });
      
      // Lade Delta-Audit-Logs
      const deltaAuditLogs = await getDeltaAuditLogs(selectedSession.id);
      console.log('[Auto-Upload] Delta-Audit-Logs:', {
        count: deltaAuditLogs.length,
        types: deltaAuditLogs.map(log => log.type)
      });
      
      // FIX: Überspringe Upload, wenn keine Delta-Messungen und keine Audit-Logs vorhanden sind
      // Sonst würde ein Upload mit 0 Messungen die Session im Backend überschreiben und alle Messungen löschen
      if (deltaMeasurements.length === 0 && deltaAuditLogs.length === 0) {
        console.log('[Auto-Upload] Keine Delta-Messungen und keine Audit-Logs, Upload übersprungen');
        setIsAutoUploading(false);
        return;
      }
      
      // Lade Fotos nur für Delta-Messungen
      const photosData = [];
      for (const measurement of deltaMeasurements) {
        if (measurement.number) {
          try {
            const photos = await loadPhotos(measurement.number, selectedSession.id);
            console.log(`[Auto-Upload] Fotos für Messung ${measurement.number}:`, photos.length);
            for (const photo of photos) {
              if (!photo.fileData) {
                console.warn(`[Auto-Upload] Foto ${photo.fileName} hat keine fileData!`);
                continue;
              }
              photosData.push({
                measurement_number: measurement.number,
                file_name: photo.fileName,
                file_data: photo.fileData, // Base64 für Backend
                file_size: photo.fileSize,
                mime_type: photo.mimeType || 'image/jpeg'
              });
            }
          } catch (error) {
            console.error(`[Auto-Upload] Fehler beim Laden der Fotos für Messung ${measurement.number}:`, error);
          }
        }
      }
      console.log(`[Auto-Upload] Gesamt ${photosData.length} Fotos geladen für Upload`);
      
      // FIX: Verwende resumeNumber aus State, nicht aus selectedSession, um zu verhindern, dass 0 gesendet wird
      const currentResumeNumber = resumeNumber || selectedSession?.resumeNumber || 0;
      // Graceful Degradation: Bereite Upload-Daten vor mit Default-Werten für fehlende Felder
      const prepareMeasurementForUpload = (m) => {
        // Finde zugehörige Foto-Indizes für diese Messung
        const photoIndices = [];
        photosData.forEach((photo, idx) => {
          if (photo.measurement_number === m.number) {
            photoIndices.push(idx);
          }
        });
        
        return {
          number: m.number ?? 0,
          grade: m.grade ?? null,
          progress_time: Math.round(m.progressTime ?? 0),
          individual_time: Math.round(m.individualTime ?? 0),
          vorgabe_time: Math.round(m.vorgabeTime ?? 0), // Vorgabezeit mit hochladen
          activity: m.activity ?? '',
          time_type: m.timeType ?? 'thb',
          note: m.note || '',
          is_note: m.isNote ?? false,
          is_interruption: m.note === 'Unterbrechung' || false,
          timestamp: m.timestamp ?? new Date().toISOString(),
          resume_number: m.resumeNumber ?? selectedSession?.resumeNumber ?? 1,
          excluded: m.excluded ?? false,
          einflussgroessen: (m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen) && Object.keys(m.einflussgroessen).length > 0) ? m.einflussgroessen : null,
          photo_indices: photoIndices.length > 0 ? photoIndices : null
        };
      };
      
      const data = {
        email,
        device_name: deviceName,
        device_id: getDeviceId(),
        session_id: selectedSession.id,
        resume_number: currentResumeNumber,
        timestamp: new Date().toISOString(),
        vorderseite_data: currentVorderseiteData,
        api_version: FRONTEND_VERSION, // Sende Frontend-Version für Backend-Kompatibilitätsprüfung
        measurements: deltaMeasurements.map(m => prepareMeasurementForUpload(m)),
        photos: photosData.length > 0 ? photosData : null,
        audit_logs: deltaAuditLogs.length > 0 ? deltaAuditLogs.map(log => ({
          type_: log.type, // Backend erwartet type_ (mit Unterstrich)
          measurement_number: log.measurementNumber,
          timestamp: log.timestamp,
          old_values: log.oldValues,
          new_values: log.newValues,
          changed_fields: log.changedFields,
          user_action: log.userAction
        })) : null
      };

      // Debug: Prüfe ob einflussgroessen vorhanden sind
      console.log('[Auto-Upload] DEBUG - Vor dem Mapping:', {
        measurementsCount: measurements.length,
        firstMeasurement: measurements[0],
        firstMeasurementEinflussgroessen: measurements[0]?.einflussgroessen,
        firstMeasurementEinflussgroessenType: typeof measurements[0]?.einflussgroessen,
        firstMeasurementEinflussgroessenKeys: measurements[0]?.einflussgroessen ? Object.keys(measurements[0].einflussgroessen) : null
      });
      
      const measurementsWithEA = data.measurements.filter(m => m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen) && Object.keys(m.einflussgroessen).length > 0);
      console.log('[Auto-Upload] Sende Request:', {
        url: uploadUrl,
        dataSize: JSON.stringify(data).length,
        measurementsCount: data.measurements.length,
        measurementsWithEA: measurementsWithEA.length,
        firstMeasurementEA: data.measurements[0]?.einflussgroessen,
        firstMeasurementEAString: JSON.stringify(data.measurements[0]?.einflussgroessen),
        firstMeasurementRaw: data.measurements[0],
        fullDataString: JSON.stringify(data).substring(0, 500)
      });

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      console.log('[Auto-Upload] Response erhalten:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: uploadUrl
      });

      if (response.ok) {
        await response.json().catch(() => ({}));
        const now = Date.now();
        // Setze lastUploaded für hochgeladene Messpunkte
        setMeasurements(prev => {
          const updated = prev.map(m => {
            const wasUploaded = deltaMeasurements.some(dm => dm.number === m.number);
            if (wasUploaded) {
              return { ...m, lastUploaded: now };
            }
            return m;
          });
          return updated;
        });
        
        // Markiere Audit-Logs als hochgeladen
        if (deltaAuditLogs.length > 0) {
          const uploadedLogIds = deltaAuditLogs.map(log => log.id).filter(id => id !== undefined);
          await markAuditLogsAsUploaded(selectedSession.id, uploadedLogIds);
        }
        
        const updatedSessions = sessions.map(s =>
          s.id === selectedSession.id ? { ...s, uploaded: true, lastUpload: new Date().toISOString() } : s
        );
        // Sortiere und begrenze Sessions
        const limitedSessions = limitAndSortSessions(updatedSessions);
        setSessions(limitedSessions);
        localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
        console.log('[Auto-Upload] Erfolgreich hochgeladen:', {
          measurementsUploaded: deltaMeasurements.length,
          auditLogsUploaded: deltaAuditLogs.length
        });
      } else {
        // Logge Fehler für Debugging, aber zeige keine Alert (da Auto-Upload im Hintergrund)
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.message || errorData.error || errorText;
            } catch {
              errorMessage = errorText;
            }
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText} (Fehler beim Lesen der Response)`;
        }
        console.warn('[Auto-Upload] Fehlgeschlagen:', {
          errorMessage,
          status: response.status,
          statusText: response.statusText,
          url: uploadUrl,
          originalUrl: backendUrl,
          normalizedUrl: normalizedUrl
        });
      }
    } catch (error) {
      // Logge Fehler für Debugging, aber zeige keine Alert (da Auto-Upload im Hintergrund)
      console.error('[Auto-Upload] Fehler:', {
        error: error.message,
        stack: error.stack,
        url: uploadUrl,
        originalUrl: backendUrl,
        normalizedUrl: normalizedUrl
      });
    } finally {
      // Markiere Upload als beendet (egal ob erfolgreich oder nicht)
      setIsAutoUploading(false);
    }
  };

  // Manual Upload
  const handleUploadMeasurements = async () => {
    // Prüfe API-Kompatibilität (non-blocking Warnung)
    if (!apiCompatible && compatibilityWarning) {
      const proceed = window.confirm(t('alerts.confirm.api_compat', { warning: compatibilityWarning }));
      if (!proceed) {
        return;
      }
    }

    if (!licenseStatus) {
      setUploadResult({ type: 'error', message: 'Upload ist nur mit aktiver Lizenz möglich.' });
      setShowUploadResultDialog(true);
      return;
    }
    
    if (!backendUrl) {
      setUploadResult({ type: 'error', message: 'Keine Backend-URL konfiguriert. Bitte konfigurieren Sie die Backend-URL in den Einstellungen.' });
      setShowUploadResultDialog(true);
      return;
    }
    
    if (!selectedSession || measurements.length === 0) {
      setUploadResult({ type: 'error', message: 'Keine Messpunkte zum Hochladen vorhanden.' });
      setShowUploadResultDialog(true);
      return;
    }

    // Normalisiere URL
    const normalizedUrl = normalizeBackendUrl(backendUrl);
    const uploadUrl = buildApiUrl(normalizedUrl, '/api/measurements/upload');
    
    console.log('[Manual Upload] Starte Upload:', {
      originalUrl: backendUrl,
      normalizedUrl: normalizedUrl,
      uploadUrl: uploadUrl,
      measurementsCount: measurements.length,
      sessionId: selectedSession.id
    });

    // Health-Check vor Upload
    try {
      const healthCheck = await checkBackendHealth(normalizedUrl);
      console.log('[Manual Upload] Health-Check Ergebnis:', healthCheck);
      
      if (healthCheck.healthy === false && healthCheck.error && !healthCheck.error.includes('versucht')) {
        setUploadResult({ 
          type: 'error', 
          message: `Backend nicht erreichbar: ${healthCheck.error}. Bitte überprüfen Sie die Backend-URL.` 
        });
        setShowUploadResultDialog(true);
        return;
      }
    } catch (healthError) {
      console.warn('[Manual Upload] Health-Check fehlgeschlagen:', healthError);
      // Weiter mit Upload, da Health-Check optional ist
    }
    
    try {
      // Prüfe Anzahl der Messpunkte im Backend
      let backendMeasurementCount = null;
      try {
        const sessionInfoUrl = buildApiUrl(normalizedUrl, `/api/admin/sessions/${selectedSession.id}/measurements`);
        const sessionInfoResponse = await fetch(sessionInfoUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        if (sessionInfoResponse.ok) {
          const sessionInfo = await sessionInfoResponse.json();
          backendMeasurementCount = sessionInfo.session?.measurement_count || 0;          debugLog('App.jsx:6063', 'Backend measurement count check (manual)', {frontendCount:measurements.length,backendCount:backendMeasurementCount,match:measurements.length===backendMeasurementCount}, 'D');        }
      } catch (error) {
        console.warn('[Manual Upload] Fehler beim Abrufen der Backend-Anzahl (nicht kritisch):', error);        debugLog('App.jsx:6071', 'Backend count check failed (manual)', {error:error.message}, 'D');      }
      
      // Berechne Delta-Messungen (nur geänderte/neue)
      // Wenn Backend-Anzahl nicht mit Frontend-Anzahl übereinstimmt, lade alle Messpunkte hoch
      let deltaMeasurements = getDeltaMeasurements(measurements);
      if (backendMeasurementCount !== null && backendMeasurementCount !== measurements.length) {        debugLog('App.jsx:6078', 'Count mismatch detected, uploading all measurements (manual)', {frontendCount:measurements.length,backendCount:backendMeasurementCount,deltaCount:deltaMeasurements.length}, 'D');        console.warn(`[Manual Upload] Anzahl-Unterschied erkannt: Frontend=${measurements.length}, Backend=${backendMeasurementCount}. Lade alle Messpunkte hoch.`);
        deltaMeasurements = measurements; // Lade alle Messpunkte hoch
      }
      console.log('[Manual Upload] Delta-Messungen:', {
        total: measurements.length,
        delta: deltaMeasurements.length,
        deltaNumbers: deltaMeasurements.map(m => m.number),
        backendCount: backendMeasurementCount
      });
      
      // Lade Delta-Audit-Logs
      const deltaAuditLogs = await getDeltaAuditLogs(selectedSession.id);
      console.log('[Manual Upload] Delta-Audit-Logs:', {
        count: deltaAuditLogs.length,
        types: deltaAuditLogs.map(log => log.type)
      });
      
      // Lade Fotos nur für Delta-Messungen
      const photosData = [];
      for (const measurement of deltaMeasurements) {
        if (measurement.number) {
            try {
            const photos = await loadPhotos(measurement.number, selectedSession.id);
            console.log(`[Manual Upload] Fotos für Messung ${measurement.number}:`, photos.length, photos.map(p => ({ id: p.id, fileName: p.fileName, hasData: !!p.fileData, dataLength: p.fileData?.length })));
            for (const photo of photos) {
              if (!photo.fileData) {
                console.warn(`[Manual Upload] Foto ${photo.fileName} hat keine fileData!`);
                continue;
              }
              photosData.push({
                measurement_number: measurement.number,
                file_name: photo.fileName,
                file_data: photo.fileData, // Base64 für Backend
                file_size: photo.fileSize,
                mime_type: photo.mimeType || 'image/jpeg'
              });
            }
          } catch (error) {
            console.error(`[Manual Upload] Fehler beim Laden der Fotos für Messung ${measurement.number}:`, error);
          }
        }
      }
      console.log(`[Manual Upload] Gesamt ${photosData.length} Fotos geladen für Upload`);
      
      // Stelle sicher, dass Vorderseite-Daten vorhanden sind
      const currentVorderseiteData = {
        ...(vorderseiteData || getDefaultVorderseiteData()),
        // Stelle sicher, dass Nachkommastellen-Einstellungen immer enthalten sind
        decimal_places_vorderseite: vorderseiteData?.decimal_places_vorderseite ?? decimalPlacesVorderseite ?? 4,
        decimal_places_sollzeiten: vorderseiteData?.decimal_places_sollzeiten ?? decimalPlacesSollzeiten ?? 4,
        // Device-Informationen (unsichtbar in WA-Liste)
        device_id: getDeviceId(),
        device_name: getDeviceName()
      };
      console.log('[Manual Upload] Vorderseite-Daten:', {
        hasVorderseiteData: !!vorderseiteData,
        wiederaufnahmen: currentVorderseiteData?.wiederaufnahmen,
        wiederaufnahmenCount: currentVorderseiteData?.wiederaufnahmen?.length || 0,
        decimal_places_vorderseite: currentVorderseiteData.decimal_places_vorderseite,
        decimal_places_sollzeiten: currentVorderseiteData.decimal_places_sollzeiten
      });
      
      // FIX: Verwende resumeNumber aus State, nicht aus selectedSession, um zu verhindern, dass 0 gesendet wird
      const currentResumeNumber = resumeNumber || selectedSession?.resumeNumber || 0;
      debugLog('App.jsx:6330', 'handleUploadMeasurements: Preparing upload data', {sessionId:selectedSession.id,resumeNumberFromState:resumeNumber,resumeNumberFromSession:selectedSession?.resumeNumber,currentResumeNumber,measurementsCount:deltaMeasurements.length}, 'A');
      const data = {
        email,
        device_name: deviceName,
        device_id: getDeviceId(),
        session_id: selectedSession.id,
        resume_number: currentResumeNumber,
        timestamp: new Date().toISOString(),
        vorderseite_data: currentVorderseiteData,
        measurements: deltaMeasurements.map(m => {
          // Finde zugehörige Foto-Indizes für diese Messung
          const photoIndices = [];
          photosData.forEach((photo, idx) => {
            if (photo.measurement_number === m.number) {
              photoIndices.push(idx);
            }
          });
          
          return {
            number: m.number,
            grade: m.grade,
            progress_time: Math.round(m.progressTime),
            individual_time: Math.round(m.individualTime),
            vorgabe_time: Math.round(m.vorgabeTime || 0), // Vorgabezeit mit hochladen
            activity: m.activity,
            time_type: m.timeType,
            note: m.note || '',
            is_note: m.isNote || false,
            is_interruption: m.note === 'Unterbrechung' || false,
            timestamp: m.timestamp,
            resume_number: m.resumeNumber || selectedSession?.resumeNumber || 1,
            excluded: m.excluded || false,
            einflussgroessen: (m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen) && Object.keys(m.einflussgroessen).length > 0) ? m.einflussgroessen : null,
            photo_indices: photoIndices.length > 0 ? photoIndices : null
          };
        }),
        photos: photosData.length > 0 ? photosData : null,
        audit_logs: deltaAuditLogs.length > 0 ? deltaAuditLogs.map(log => ({
          type_: log.type, // Backend erwartet type_ (mit Unterstrich)
          measurement_number: log.measurementNumber,
          timestamp: log.timestamp,
          old_values: log.oldValues,
          new_values: log.newValues,
          changed_fields: log.changedFields,
          user_action: log.userAction
        })) : null
      };

      // Debug: Prüfe ob einflussgroessen vorhanden sind
      const measurementsWithEA = data.measurements.filter(m => m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen) && Object.keys(m.einflussgroessen).length > 0);
      console.log('[Manual Upload] Sende Request:', {
        url: uploadUrl,
        dataSize: JSON.stringify(data).length,
        measurementsCount: data.measurements.length,
        measurementsWithEA: measurementsWithEA.length,
        photosCount: photosData.length,
        photosDetails: photosData.map(p => ({ 
          measurement_number: p.measurement_number, 
          file_name: p.file_name, 
          file_data_length: p.file_data?.length, 
          file_data_preview: p.file_data?.substring(0, 50) + '...',
          file_size: p.file_size,
          mime_type: p.mime_type
        })),
        hasPhotos: !!data.photos,
        photosArrayLength: data.photos?.length || 0,
        firstMeasurementEA: data.measurements[0]?.einflussgroessen,
        firstMeasurementEAString: JSON.stringify(data.measurements[0]?.einflussgroessen),
        firstMeasurementRaw: data.measurements[0]
      });

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      console.log('[Manual Upload] Response erhalten:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: uploadUrl
      });

      if (response.ok) {
        const now = Date.now();
        // Setze lastUploaded für hochgeladene Messpunkte
        setMeasurements(prev => {
          const updated = prev.map(m => {
            const wasUploaded = deltaMeasurements.some(dm => dm.number === m.number);
            if (wasUploaded) {
              return { ...m, lastUploaded: now };
            }
            return m;
          });
          return updated;
        });
        
        // Markiere Audit-Logs als hochgeladen
        if (deltaAuditLogs.length > 0) {
          const uploadedLogIds = deltaAuditLogs.map(log => log.id).filter(id => id !== undefined);
          await markAuditLogsAsUploaded(selectedSession.id, uploadedLogIds);
        }
        
        const updatedSessions = sessions.map(s =>
          s.id === selectedSession.id ? { ...s, uploaded: true, lastUpload: new Date().toISOString() } : s
        );
        // Sortiere und begrenze Sessions
        const limitedSessions = limitAndSortSessions(updatedSessions);
        setSessions(limitedSessions);
        localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
        let successMessage = `Erfolgreich ${deltaMeasurements.length} Messpunkt(e) und ${deltaAuditLogs.length} Protokoll-Einträge hochgeladen.`;
        try {
          const pendingFeedbacks = await getPendingFeedback();
          if (pendingFeedbacks.length > 0) {
            successMessage += ` ${pendingFeedbacks.length} Feedback(s) ausstehend.`;
          }
        } catch (e) {
          // Feedback-Anzahl optional, Fehler ignorieren
        }
        setUploadResult({ type: 'success', message: successMessage });
        setShowUploadResultDialog(true);
        console.log('[Manual Upload] Erfolgreich hochgeladen:', {
          measurementsUploaded: deltaMeasurements.length,
          auditLogsUploaded: deltaAuditLogs.length
        });
      } else {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          if (errorText) {
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.message || errorData.error || errorText;
            } catch {
              errorMessage = errorText;
            }
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText} (Fehler beim Lesen der Response)`;
        }
        
        console.error('[Manual Upload] Fehlgeschlagen:', {
          errorMessage,
          status: response.status,
          statusText: response.statusText,
          url: uploadUrl,
          originalUrl: backendUrl,
          normalizedUrl: normalizedUrl
        });
        
        setUploadResult({ 
          type: 'error', 
          message: `Upload fehlgeschlagen: ${errorMessage}` 
        });
        setShowUploadResultDialog(true);
      }
    } catch (error) {
      console.error('[Manual Upload] Fehler:', {
        error: error.message,
        stack: error.stack,
        url: uploadUrl,
        originalUrl: backendUrl,
        normalizedUrl: normalizedUrl
      });
      
      setUploadResult({ 
        type: 'error', 
        message: `Upload-Fehler: ${error.message || 'Verbindungsfehler'}. Bitte überprüfen Sie die Backend-URL und die Netzwerkverbindung.` 
      });
      setShowUploadResultDialog(true);
    }
  };

  // Copy activity to input field (single click)
  const handleCopyActivity = (measurement) => {
    if (measurement.activity && !measurement.isNote) {
      setActivity(measurement.activity);
      setTimeType(measurement.timeType || 'thb');
      
      // Kopiere Einflussgrößen-Ausprägungen, falls vorhanden
      if (measurement.einflussgroessen && Object.keys(measurement.einflussgroessen).length > 0) {
        setEinflussgroessenValues({ ...measurement.einflussgroessen });
      } else {
        // Leere Einflussgrößen-Felder, wenn keine vorhanden sind
        setEinflussgroessenValues({});
      }
      
      // Finde das zugehörige Kürzel, um die Einflussgrößen-Definitionen zu bekommen
      // Verwende zuerst das gespeicherte kuerzel-Feld, falls vorhanden
      if (ablaufDbData && ablaufDbData.length > 0) {
        let matchingKuerzel = null;
        
        // Wenn kuerzel-Feld vorhanden, suche direkt danach
        if (measurement.kuerzel) {
          matchingKuerzel = ablaufDbData.find(k => 
            k && k.kuerzel && k.kuerzel.trim() === measurement.kuerzel.trim()
          );
        }
        
        // Falls nicht gefunden, suche über activity (für Rückwärtskompatibilität)
        if (!matchingKuerzel) {
          matchingKuerzel = ablaufDbData.find(k => 
          k.beschreibung === measurement.activity || 
          k.fullText === measurement.activity ||
          k.kuerzel === measurement.activity
        );
        }
        
        if (matchingKuerzel && matchingKuerzel.einflussgroessen) {
          setSelectedKuerzel(matchingKuerzel);
          
          // Initialisiere auch leere Werte für alle Einflussgrößen des Kürzels
          const initialValues = { ...measurement.einflussgroessen || {} };
          matchingKuerzel.einflussgroessen.forEach(einfluss => {
            if (einfluss && einfluss.einflussname && typeof einfluss.einflussname.trim === 'function') {
              const name = einfluss.einflussname.trim();
              if (name && !initialValues[name]) {
                initialValues[name] = '';
              }
            }
          });
          setEinflussgroessenValues(initialValues);
        } else {
          // Kein passendes Kürzel gefunden, leere Einflussgrößen
          setSelectedKuerzel(null);
          setEinflussgroessenValues({});
        }
      } else {
        // Keine AblaufDB geladen, leere Einflussgrößen
        setSelectedKuerzel(null);
        setEinflussgroessenValues({});
      }
    }
  };

  // Edit Measurement (double click)
  const handleEditMeasurement = async (measurement) => {
    setEditingMeasurement(measurement);
    setEditActivity(measurement.activity || '');
    setEditNote(measurement.note || '');
    setEditExcluded(measurement.excluded || false);
    
    // Lade Einflussgrößen-Werte aus dem Messpunkt
    const einflussValues = measurement.einflussgroessen || {};
    setEditEinflussgroessenValues(einflussValues);
    
    // Lade Fotos für diese Messung
    if (measurement.number && selectedSession?.id) {
      try {
        const photos = await loadPhotos(measurement.number, selectedSession.id);
        setEditPhotos(photos.map(photo => ({
          id: photo.id,
          fileData: photo.fileData,
          fileName: photo.fileName
        })));
      } catch (error) {
        console.error('Fehler beim Laden der Fotos:', error);
        setEditPhotos([]);
      }
    } else {
      setEditPhotos([]);
    }
    
    // Finde das zugehörige Kürzel, um die Einflussgrößen-Definitionen zu bekommen
    // Verwende zuerst das gespeicherte kuerzel-Feld, falls vorhanden
    if (ablaufDbData && ablaufDbData.length > 0) {
      let matchingKuerzel = null;
      
      // Wenn kuerzel-Feld vorhanden, suche direkt danach
      if (measurement.kuerzel) {
        matchingKuerzel = ablaufDbData.find(k => 
          k && k.kuerzel && k.kuerzel.trim() === measurement.kuerzel.trim()
        );
      }
      
      // Falls nicht gefunden, suche über activity (für Rückwärtskompatibilität)
      if (!matchingKuerzel) {
        matchingKuerzel = ablaufDbData.find(k => 
        k.beschreibung === measurement.activity || 
        k.fullText === measurement.activity ||
        k.kuerzel === measurement.activity
      );
      }
      
      if (matchingKuerzel && matchingKuerzel.einflussgroessen) {
        setSelectedKuerzel(matchingKuerzel);
      }
    }
  };

  // Handler zum Zurücksetzen von M1 (wird an CompactPortraitView übergeben)
  const handleResetMerke1 = useCallback(() => {
    if (merke1Enabled) {
      setMerke1Enabled(false);
      setMerke1Values(null);
      localStorage.setItem('stoppuhr_merke1_enabled', 'false');
      localStorage.removeItem('stoppuhr_merke1_values');
      console.log('[Merke1] Zurückgesetzt durch Klick auf alten Messpunkt');
    }
  }, [merke1Enabled]);

  // Handle click with double-click detection
  const handleMeasurementClick = (measurement, event) => {
    // Verhindere Edit-Button Klick
    if (event.target.closest('.btn-edit-measurement')) return;
    
    // Wenn auf einen alten Messpunkt geklickt wird, setze M1 zurück
    handleResetMerke1();
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    if (timeSinceLastClick < 300) {
      // Double click detected
      clearTimeout(clickTimeoutRef.current);
      handleEditMeasurement(measurement);
    } else {
      // Single click - wait to see if it's a double click
      clickTimeoutRef.current = setTimeout(() => {
        handleCopyActivity(measurement);
      }, 300);
    }
    
    lastClickTimeRef.current = now;
  };

  // Speech recognition for edit dialog - Start
  const handleStartListeningEdit = () => {
    if (!recognitionEdit) {
      if (isSafari()) {
        alert(t('alerts.speech_safari'));
      } else {
      alert(t('alerts.speech_not_supported'));
      }
      return;
    }
    
    if (!isListeningEditRef.current) {
      try {
        isListeningEditRef.current = true;
        setIsListeningEdit(true);
        recognitionEdit.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        isListeningEditRef.current = false;
        setIsListeningEdit(false);
      }
    }
  };

  // Speech recognition for edit dialog - Stop
  const handleStopListeningEdit = () => {
    if (recognitionEdit && (isListeningEditRef.current || isListeningEditActivityRef.current)) {
      try {
        isListeningEditRef.current = false;
        isListeningEditActivityRef.current = false;
        setIsListeningEdit(false);
        setIsListeningEditActivity(false);
        recognitionEdit.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        isListeningEditRef.current = false;
        isListeningEditActivityRef.current = false;
        setIsListeningEdit(false);
        setIsListeningEditActivity(false);
      }
    }
  };

  // Speech recognition for edit dialog - Start (for Activity)
  // eslint-disable-next-line no-unused-vars
  const handleStartListeningEditActivity = () => {
    if (!recognitionEdit) {
      if (isSafari()) {
        alert(t('alerts.speech_safari'));
      } else {
        alert(t('alerts.speech_not_supported'));
      }
      return;
    }
    
    if (!isListeningEditActivityRef.current) {
      try {
        isListeningEditActivityRef.current = true;
        setIsListeningEditActivity(true);
        recognitionEdit.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        isListeningEditActivityRef.current = false;
        setIsListeningEditActivity(false);
      }
    }
  };

  const handleSaveEdit = async () => {
    if (!editingMeasurement || !selectedSession) return;    debugLog('App.jsx:1800', 'handleSaveEdit called', {measurementNumber:editingMeasurement.number,oldExcluded:editingMeasurement.excluded,newExcluded:editExcluded,resumeNumber:editingMeasurement.resumeNumber}, 'A');    // Sammle Einflussgrößen-Werte (nur wenn Werte vorhanden sind)
    const einflussgroessen = {};
    if (editEinflussgroessenValues && Object.keys(editEinflussgroessenValues).length > 0) {
      Object.entries(editEinflussgroessenValues).forEach(([name, value]) => {
        if (value && value.trim()) {
          einflussgroessen[name] = value.trim();
        }
      });
    }
    
    // Finde das Kürzel für die bearbeitete Aktivität
    let updatedKuerzel = editingMeasurement.kuerzel || null;
    if (editActivity && ablaufDbData && ablaufDbData.length > 0) {
      const matchingKuerzel = ablaufDbData.find(k => 
        k.beschreibung === editActivity || 
        k.fullText === editActivity ||
        k.kuerzel === editActivity
      );
      if (matchingKuerzel) {
        updatedKuerzel = matchingKuerzel.kuerzel;
      } else {
        // Wenn keine Übereinstimmung gefunden, setze auf null
        updatedKuerzel = null;
      }
    }
    
    // Erstelle Kopie der alten Werte für Audit-Log
    const oldValues = {
      activity: editingMeasurement.activity || '',
      kuerzel: editingMeasurement.kuerzel || null,
      note: editingMeasurement.note || '',
      excluded: editingMeasurement.excluded || false,
      einflussgroessen: editingMeasurement.einflussgroessen || {}
    };
    
    // Neue Werte
    const newValues = {
      activity: editActivity,
      kuerzel: updatedKuerzel,
      note: editNote,
      excluded: editExcluded,
      einflussgroessen: Object.keys(einflussgroessen).length > 0 ? einflussgroessen : {}
    };
    
    // Finde geänderte Felder
    const changedFields = [];
    if (oldValues.activity !== newValues.activity) changedFields.push('activity');
    if (oldValues.kuerzel !== newValues.kuerzel) changedFields.push('kuerzel');
    if (oldValues.note !== newValues.note) changedFields.push('note');
    if (oldValues.excluded !== newValues.excluded) changedFields.push('excluded');
    if (JSON.stringify(oldValues.einflussgroessen) !== JSON.stringify(newValues.einflussgroessen)) {
      changedFields.push('einflussgroessen');
    }
    
    const now = Date.now();
    const updatedMeasurements = measurements.map(m => 
      m.number === editingMeasurement.number
        ? { 
            ...m, 
            activity: editActivity, 
            kuerzel: updatedKuerzel, // Kürzel aktualisieren
            note: editNote, 
            excluded: editExcluded,
            einflussgroessen: Object.keys(einflussgroessen).length > 0 ? einflussgroessen : undefined,
            lastModified: now // Delta-Tracking: Aktualisiere lastModified
          }
        : m
    );
    
    // Erstelle Audit-Log, wenn Änderungen vorhanden sind
    if (changedFields.length > 0 && selectedSession.id) {
      try {
        await addAuditLog(
          selectedSession.id,
          'UPDATE',
          editingMeasurement.number,
          oldValues,
          newValues,
          changedFields,
          'Messpunkt bearbeitet'
        );
      } catch (error) {
        console.error('[AuditLog] Fehler beim Erstellen des Protokoll-Eintrags:', error);
      }
    }
    
    // Neuberechnung der Einzelzeiten nach Bearbeiten (berücksichtigt ausgeklammerte Messpunkte)
    // WICHTIG: Immer neu berechnen, um sicherzustellen, dass die Einzelzeiten korrekt sind
    const finalMeasurements = recalculateIndividualTimes(updatedMeasurements);
    debugLog('App.jsx:1817', 'After handleSaveEdit, setting measurements', {finalCount:finalMeasurements.length,firstFew:finalMeasurements.slice(0,3).map(m=>({number:m.number,excluded:m.excluded,individualTime:m.individualTime}))}, 'C');
    setMeasurements(finalMeasurements);
    // ENTFERNT: renumberMeasurements(); // Nicht nötig beim Bearbeiten - würde zu Duplikaten führen
    
    // Aktualisiere Sessions
    if (selectedSession) {
      const updatedSessions = sessions.map(s =>
        s.id === selectedSession.id
          ? { ...s, measurements: finalMeasurements, lastUpdated: new Date().toISOString() }
          : s
      );
      // Liste neu sortieren und begrenzen
      const limitedSessions = limitAndSortSessions(updatedSessions);
      setSessions(limitedSessions);
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    }
    
    setEditingMeasurement(null);
    setEditActivity('');
    setEditNote('');
    setEditExcluded(false);
    setEditEinflussgroessenValues({});
    setEditPhotos([]);
  };

  // Foto-Upload Handler (für Edit-Dialog)
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingMeasurement || !selectedSession) return;
    
    if (!file.type.startsWith('image/')) {
      alert(t('alerts.photo_select'));
      e.target.value = '';
      return;
    }
    
    // Lade alte Fotos für Audit-Log
    const oldPhotos = await loadPhotos(editingMeasurement.number, selectedSession?.id);
    
    setIsUploadingPhoto(true);
    try {
      // Komprimiere Bild für Vorschau
      const compressedBlob = await compressImage(file);
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedBlob);
      });
      
      // Zeige Foto sofort an (optimistisches Update)
      const tempId = `temp-edit-${Date.now()}`;
      setEditPhotos(prev => [...prev, {
        id: tempId,
        fileData: base64,
        fileName: file.name
      }]);
      
      // Speichere Foto in IndexedDB
      const photoId = await savePhoto(
        editingMeasurement.number,
        selectedSession.id,
        file,
        file.name
      );
      
      // Lade Fotos neu (mit echten IDs)
      const photos = await loadPhotos(editingMeasurement.number, selectedSession?.id);
      setEditPhotos(photos.map(photo => ({
        id: photo.id,
        fileData: photo.fileData,
        fileName: photo.fileName
      })));
      
      // Aktualisiere auch measurementPhotos
      if (editingMeasurement.number) {
        setMeasurementPhotos(prev => ({
          ...prev,
          [editingMeasurement.number]: photos.map(photo => ({
            id: photo.id,
            fileData: photo.fileData,
            fileName: photo.fileName
          }))
        }));
      }
      
      // Erstelle Audit-Log für Foto-Upload
      if (selectedSession.id) {
        try {
          await addAuditLog(
            selectedSession.id,
            'UPDATE',
            editingMeasurement.number,
            { photos: oldPhotos.map(p => ({ id: p.id, fileName: p.fileName })) }, // Alte Fotos
            { photos: photos.map(p => ({ id: p.id, fileName: p.fileName })) }, // Neue Fotos
            ['photos'], // Geändertes Feld
            `Foto "${file.name}" hinzugefügt`
          );
        } catch (error) {
          console.error('[AuditLog] Fehler beim Erstellen des Protokoll-Eintrags:', error);
        }
      }
      
      // Aktualisiere lastModified für Messung
      setMeasurements(prev => prev.map(m => 
        m.number === editingMeasurement.number
          ? { ...m, lastModified: Date.now() }
          : m
      ));
      
      console.log('Foto erfolgreich gespeichert:', photoId);
    } catch (error) {
      console.error('Fehler beim Speichern des Fotos:', error);
      // Entferne temporäres Foto bei Fehler
      setEditPhotos(prev => prev.filter(photo => !photo.id.startsWith('temp-edit-')));
      alert(t('alerts.photo_save_error', { msg: error.message || 'Unbekannter Fehler' }));
    } finally {
      setIsUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Foto-Upload Handler (für temporäre Fotos im Hauptfenster oder direkt zu Messung)
  const handlePendingPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedSession) return;
    
    if (!file.type.startsWith('image/')) {
      alert(t('alerts.photo_select'));
      e.target.value = '';
      return;
    }
    
    // Wenn pendingPhotoMeasurementNumber gesetzt ist, speichere direkt zu Messung
    if (pendingPhotoMeasurementNumber) {
      try {
        setIsUploadingPhoto(true);
        const photoId = await savePhoto(
          pendingPhotoMeasurementNumber,
          selectedSession.id,
          file,
          file.name
        );
        
        // Lade Fotos neu
        const photos = await loadPhotos(pendingPhotoMeasurementNumber, selectedSession?.id);
        setMeasurementPhotos(prev => ({
          ...prev,
          [pendingPhotoMeasurementNumber]: photos.map(photo => ({
            id: photo.id,
            fileData: photo.fileData,
            fileName: photo.fileName
          }))
        }));
        
        setPendingPhotoMeasurementNumber(null);
        console.log('Foto erfolgreich zu Messung hinzugefügt:', photoId);
      } catch (error) {
        console.error('Fehler beim Speichern des Fotos:', error);
        alert(t('alerts.photo_save_error', { msg: error.message || 'Unbekannter Fehler' }));
      } finally {
        setIsUploadingPhoto(false);
        e.target.value = '';
      }
      return;
    }
    
    // Sonst: Füge zu temporären Fotos hinzu (max. 3)
    try {
      const compressedBlob = await compressImage(file);
      
      // Konvertiere Blob zu Base64 für Vorschau
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedBlob);
      });
      
      // Konvertiere komprimierten Blob zu File-Objekt für spätere Verwendung
      const compressedFile = new File([compressedBlob], file.name, { 
        type: compressedBlob.type || 'image/jpeg',
        lastModified: Date.now()
      });
      
      setPendingPhotos(prev => {
        const newPhotos = [...prev, { 
          file: compressedFile, // Verwende komprimiertes File-Objekt
          fileName: file.name, 
          fileData: base64 
        }];
        return newPhotos.slice(-3); // Max. 3 Fotos
      });
    } catch (error) {
      console.error('Fehler beim Verarbeiten des Fotos:', error);
      alert(t('alerts.photo_process_error', { msg: error.message || 'Unbekannter Fehler' }));
    } finally {
      e.target.value = '';
    }
  };

  // Temporäres Foto löschen
  const handleDeletePendingPhoto = (index) => {
    setPendingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Foto löschen Handler
  const handleDeletePhoto = (photoId) => {
    setPhotoToDelete(photoId);
    setShowDeletePhotoDialog(true);
  };

  // Foto löschen bestätigen
  const handleConfirmDeletePhoto = async () => {
    if (!photoToDelete || !selectedSession) return;
    
    // Finde die Messung, zu der das Foto gehört
    const measurementNumber = editingMeasurement?.number || null;
    if (!measurementNumber) {
      console.warn('[AuditLog] Keine Messung gefunden für Foto-Löschung');
      setShowDeletePhotoDialog(false);
      setPhotoToDelete(null);
      return;
    }
    
    // Lade alte Fotos für Audit-Log
    const oldPhotos = await loadPhotos(measurementNumber, selectedSession?.id);
    const photoToDeleteObj = oldPhotos.find(p => p.id === photoToDelete);
    
    try {
      // Prüfe ob es eine temporäre ID ist (beginnt mit "temp-")
      const isTemporaryPhoto = typeof photoToDelete === 'string' && photoToDelete.startsWith('temp-');
      
      if (!isTemporaryPhoto) {
        // Nur aus IndexedDB löschen, wenn es keine temporäre ID ist
        await deletePhoto(photoToDelete);
      }
      
      // Entferne Foto aus editPhotos
      setEditPhotos(editPhotos.filter(photo => photo.id !== photoToDelete));
      
      // Aktualisiere auch measurementPhotos
      if (editingMeasurement?.number) {
        const updatedPhotos = { ...measurementPhotos };
        if (updatedPhotos[editingMeasurement.number]) {
          updatedPhotos[editingMeasurement.number] = updatedPhotos[editingMeasurement.number].filter(
            photo => photo.id !== photoToDelete
          );
          setMeasurementPhotos(updatedPhotos);
        }
      }
      
      // Lade neue Fotos für Audit-Log
      const newPhotos = await loadPhotos(measurementNumber, selectedSession.id);
      
      // Erstelle Audit-Log für Foto-Löschung
      if (selectedSession.id && photoToDeleteObj) {
        try {
          await addAuditLog(
            selectedSession.id,
            'UPDATE',
            measurementNumber,
            { photos: oldPhotos.map(p => ({ id: p.id, fileName: p.fileName })) }, // Alte Fotos
            { photos: newPhotos.map(p => ({ id: p.id, fileName: p.fileName })) }, // Neue Fotos
            ['photos'], // Geändertes Feld
            `Foto "${photoToDeleteObj.fileName}" gelöscht`
          );
        } catch (error) {
          console.error('[AuditLog] Fehler beim Erstellen des Protokoll-Eintrags:', error);
        }
      }
      
      // Aktualisiere lastModified für Messung
      setMeasurements(prev => prev.map(m => 
        m.number === measurementNumber
          ? { ...m, lastModified: Date.now() }
          : m
      ));
      
      setShowDeletePhotoDialog(false);
      setPhotoToDelete(null);
      console.log(`[Photos] Foto ${photoToDelete} gelöscht (${isTemporaryPhoto ? 'temporär' : 'permanent'})`);
    } catch (error) {
      console.error('Fehler beim Löschen des Fotos:', error);
      alert(t('alerts.photo_delete_error', { msg: error.message || 'Unbekannter Fehler' }));
      setShowDeletePhotoDialog(false);
      setPhotoToDelete(null);
    }
  };

  // Foto löschen abbrechen
  const handleCancelDeletePhoto = () => {
    setShowDeletePhotoDialog(false);
    setPhotoToDelete(null);
  };

  // Foto-Vorschau öffnen
  // eslint-disable-next-line no-unused-vars
  const handlePhotoPreview = (fileData) => {
    setPhotoPreview(fileData);
  };

  // Drag & Drop Handler
  const handleDragStart = (e, measurement) => {    debugLog('App.jsx:1939', 'handleDragStart called', {enableDragDrop,measurementNumber:measurement.number}, 'A');    if (!enableDragDrop) return;
    setDraggedMeasurement(measurement);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', measurement.number.toString());
  };

  const handleDragOver = (e) => {    debugLog('App.jsx:1946', 'handleDragOver called', {enableDragDrop,hasDraggedMeasurement:!!draggedMeasurement}, 'B');    if (!enableDragDrop) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetMeasurement) => {
    // Console-Log für Drag & Drop Debugging
    console.log('🔵 DRAG & DROP:', {
      draggedMessNr: draggedMeasurement?.number || 'KEIN DRAG ELEMENT',
      dropPositionMessNr: targetMeasurement.number,
      enableDragDrop,
      hasDraggedMeasurement: !!draggedMeasurement
    });    debugLog('App.jsx:1952', 'handleDrop called', {enableDragDrop,hasDraggedMeasurement:!!draggedMeasurement,targetMeasurementNumber:targetMeasurement.number,draggedMeasurementNumber:draggedMeasurement?.number}, 'C');    if (!enableDragDrop || !draggedMeasurement) {
      console.warn('⚠️ DRAG & DROP ABGEBROCHEN:', { enableDragDrop, hasDraggedMeasurement: !!draggedMeasurement });
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedMeasurement.number === targetMeasurement.number) {
      setDraggedMeasurement(null);
      return;
    }

    // Da die Anzeige umgekehrt ist ([...measurements].reverse()), müssen wir die Indizes entsprechend anpassen
    // Die Messungen werden in umgekehrter Reihenfolge angezeigt, also müssen wir die Drop-Logik anpassen
    // Finde Indizes in der normalen Reihenfolge
    const draggedIndex = measurements.findIndex(m => m.number === draggedMeasurement.number);
    const targetIndex = measurements.findIndex(m => m.number === targetMeasurement.number);    debugLog('App.jsx:1970', 'handleDrop: Finding indices', {draggedIndex,targetIndex,draggedMeasurementNumber:draggedMeasurement.number,targetMeasurementNumber:targetMeasurement.number,measurementsCount:measurements.length}, 'C');    // Console-Log für Indizes
    console.log('📍 DRAG & DROP INDIZES:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      draggedIndex,
      targetIndex,
      measurementsCount: measurements.length
    });
    
    if (draggedIndex === -1 || targetIndex === -1) {
      console.error('❌ DRAG & DROP FEHLER: Ungültige Indizes', { draggedIndex, targetIndex, draggedMessNr: draggedMeasurement.number, dropPositionMessNr: targetMeasurement.number });      debugLog('App.jsx:1974', 'handleDrop: Invalid indices, aborting', {draggedIndex,targetIndex}, 'C');      setDraggedMeasurement(null);
      return;
    }

    // Verschiebe Element: Da die Anzeige umgekehrt ist ([...measurements].reverse()),
    // bedeutet "A auf B ziehen" in der Anzeige, dass A NACH B kommen soll (in der normalen Reihenfolge)
    // 
    // Beispiel: Array [1, 2, 3], Anzeige [3, 2, 1] (oben nach unten)
    // Wenn #2 auf #3 gezogen wird (in der Anzeige):
    //   - draggedIndex = 1, targetIndex = 2
    //   - Nach Entfernen: [1, 3], Element 3 ist bei Index 1
    //   - Wir wollen #2 NACH #3, also bei Index 2
    //   - adjustedTargetIndex = 2 - 1 = 1, insertIndex = 1 + 1 = 2 ✓
    //
    // Wenn #3 auf #2 gezogen wird (in der Anzeige):
    //   - draggedIndex = 2, targetIndex = 1
    //   - Nach Entfernen: [1, 2], Element 2 ist bei Index 1
    //   - Wir wollen #3 NACH #2, also bei Index 2
    //   - adjustedTargetIndex = 1 (keine Anpassung), insertIndex = 1 + 1 = 2 ✓
    
    const newMeasurements = [...measurements];
    
    // NEUE LOGIK: Zuerst einfügen, dann löschen
    // Da die Anzeige umgekehrt ist ([...measurements].reverse()),
    // bedeutet "A auf B ziehen" in der Anzeige, dass A NACH B kommen soll (in der normalen Reihenfolge)
    // 
    // Beispiel: Array [1, 2, 3], Anzeige [3, 2, 1] (oben nach unten)
    // Wenn der Benutzer #1 auf #3 zieht (in der Anzeige):
    //   - In der Anzeige: #1 ist unten, #3 ist oben
    //   - Der Benutzer zieht #1 auf #3, also soll #1 NACH #3 kommen (in der Anzeige)
    //   - In der normalen Reihenfolge: #1 ist bei Index 0, #3 ist bei Index 2
    //   - Nach dem Drop soll #1 NACH #3 kommen, also bei Index 3
    //   - draggedIndex = 0, targetIndex = 2
    //   - Schritt 1: Einfügen bei Index 3 → [1, 2, 3, 1]
    //   - Schritt 2: Löschen bei Index 0 → [2, 3, 1] ✓
    //
    // Beispiel: Array [1, 2, 3], ziehe #3 auf #1
    //   - draggedIndex = 2, targetIndex = 0
    //   - Ziel: #3 soll NACH #1 kommen, also bei Index 1
    //   - Schritt 1: Einfügen bei Index 1 → [1, 3, 2, 3]
    //   - Schritt 2: Löschen bei Index 3 (ursprünglich Index 2, nach Einfügen verschoben) → [1, 3, 2] ✓
    
    // Berechne die Zielposition: NACH dem Ziel-Element
    const insertIndex = targetIndex + 1;
    const safeInsertIndex = Math.min(insertIndex, newMeasurements.length);
    
    // Console-Log vor Einfügen
    const arrayVorEinfuegen = newMeasurements.map(m => m.number);
    console.log('📋 DRAG & DROP VOR EINFÜGEN:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      draggedIndex,
      targetIndex,
      insertIndex,
      safeInsertIndex,
      arrayVorEinfuegen,
      arrayVorEinfuegenString: `[${arrayVorEinfuegen.join(', ')}]`
    });
    console.log('📋 Array vor Einfügen (als String):', `[${arrayVorEinfuegen.join(', ')}]`);
    
    // Schritt 1: Füge das Element an der Zielposition ein (KOPIE, nicht entfernen!)
    const draggedElement = measurements[draggedIndex];
    newMeasurements.splice(safeInsertIndex, 0, draggedElement);
    
    // Console-Log nach Einfügen
    const arrayNachEinfuegen = newMeasurements.map(m => m.number);
    console.log('📥 DRAG & DROP NACH EINFÜGEN:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      draggedIndex,
      targetIndex,
      insertIndex,
      safeInsertIndex,
      arrayNachEinfuegen,
      arrayNachEinfuegenString: `[${arrayNachEinfuegen.join(', ')}]`
    });
    console.log('📥 Array nach Einfügen (als String):', `[${arrayNachEinfuegen.join(', ')}]`);
    
    // Schritt 2: Lösche das Element von der ursprünglichen Position
    // WICHTIG: Wenn draggedIndex < targetIndex, hat sich der Index nach dem Einfügen nicht verschoben
    //          Wenn draggedIndex > targetIndex, hat sich der Index um 1 nach rechts verschoben
    let deleteIndex = draggedIndex;
    if (draggedIndex > targetIndex) {
      // Nach dem Einfügen bei safeInsertIndex (<= targetIndex + 1) verschiebt sich draggedIndex um 1 nach rechts
      deleteIndex = draggedIndex + 1;
    }
    
    // Console-Log vor Löschen
    console.log('🗑️ DRAG & DROP VOR LÖSCHEN:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      deleteIndex,
      draggedIndex,
      targetIndex
    });
    
    // Lösche das Element von der ursprünglichen Position
    newMeasurements.splice(deleteIndex, 1);
    
    // Console-Log nach Löschen
    const finalArray = newMeasurements.map(m => m.number);
    const finalArrayReversed = [...newMeasurements].reverse().map(m => m.number);
    console.log('✅ DRAG & DROP NACH LÖSCHEN:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      finalArray,
      finalArrayString: `[${finalArray.join(', ')}]`,
      finalArrayReversed: finalArrayReversed,
      finalArrayReversedString: `[${finalArrayReversed.join(', ')}]`
    });
    console.log('✅ Finales Array (normal):', `[${finalArray.join(', ')}]`);
    console.log('✅ Finales Array (umgekehrt für Anzeige):', `[${finalArrayReversed.join(', ')}]`);    debugLog('App.jsx:2037', 'handleDrop: Element moved', {newMeasurementsCount:newMeasurements.length,removedNumber:draggedElement.number,insertedAt:safeInsertIndex,insertIndex,draggedIndex,targetIndex}, 'C');    // Erstelle Audit-Log für Drag & Drop (vor Renumbering, damit wir die alte Position haben)
    if (selectedSession && selectedSession.id) {
      try {
        const oldPosition = draggedIndex + 1; // Alte Position (1-basiert)
        const newPosition = safeInsertIndex + 1; // Neue Position (1-basiert, vor Renumbering)
        await addAuditLog(
          selectedSession.id,
          'MOVE',
          draggedMeasurement.number,
          { position: oldPosition }, // Alte Position
          { position: newPosition }, // Neue Position
          ['position'], // Geändertes Feld
          `Messpunkt von Position ${oldPosition} nach ${newPosition} verschoben`
        );
      } catch (error) {
        console.error('[AuditLog] Fehler beim Erstellen des Protokoll-Eintrags:', error);
      }
    }
    
    // Neuberechnung der Einzelzeiten nach Verschiebung
    const recalculatedMeasurements = recalculateIndividualTimes(newMeasurements);
    
    // Nummeriere neu und aktualisiere lastModified für verschobene Messung
    const now = Date.now();
    const renumbered = recalculatedMeasurements.map((m, index) => {
      if (m.number === draggedMeasurement.number) {
        return { ...m, number: index + 1, lastModified: now }; // Delta-Tracking: Aktualisiere lastModified
      }
      return { ...m, number: index + 1 };
    });
    
    // Console-Log nach Renumbering
    const renumberedArray = renumbered.map(m => m.number);
    const renumberedArrayReversed = [...renumbered].reverse().map(m => m.number);
    console.log('🔄 DRAG & DROP NACH RENUMBERING:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      renumberedArray,
      renumberedArrayString: `[${renumberedArray.join(', ')}]`,
      renumberedArrayReversed,
      renumberedArrayReversedString: `[${renumberedArrayReversed.join(', ')}]`
    });
    console.log('🔄 Array nach Renumbering (normal):', `[${renumberedArray.join(', ')}]`);
    console.log('🔄 Array nach Renumbering (umgekehrt für Anzeige):', `[${renumberedArrayReversed.join(', ')}]`);
    debugLog('App.jsx:1990', 'handleDrop: Setting measurements', {renumberedCount:renumbered.length,firstFew:renumbered.slice(0,3).map(m=>({number:m.number,resumeNumber:m.resumeNumber}))}, 'C');
    setMeasurements(renumbered);
    
    // Aktualisiere Sessions
    if (selectedSession) {
      const updatedSessions = sessions.map(s =>
        s.id === selectedSession.id
          ? { ...s, measurements: renumbered, lastUpdated: new Date().toISOString() }
          : s
      );
      // Liste neu sortieren und begrenzen
      const limitedSessions = limitAndSortSessions(updatedSessions);
      setSessions(limitedSessions);
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    }
    
    // Console-Log für erfolgreichen Drop
    console.log('✅ DRAG & DROP ERFOLGREICH:', {
      draggedMessNr: draggedMeasurement.number,
      dropPositionMessNr: targetMeasurement.number,
      finalMeasurementsCount: renumbered.length,
      insertedAt: safeInsertIndex
    });
    
    setDraggedMeasurement(null);    debugLog('App.jsx:2005', 'handleDrop: Completed', {renumberedCount:renumbered.length}, 'C');  };

  const handleDragEnd = () => {    if (typeof window !== 'undefined') {
      debugLog('App.jsx:2000', 'handleDragEnd called', {hadDraggedMeasurement:!!draggedMeasurement}, 'D');
    }    setDraggedMeasurement(null);
  };

  // Neu-Nummerierung der Messpunkte
  const renumberMeasurements = () => {
    setMeasurements(prev => {
      const sorted = [...prev].sort((a, b) => {
        // Sortiere nach ursprünglicher Reihenfolge (timestamp oder number)
        if (a.timestamp && b.timestamp) {
          return new Date(a.timestamp) - new Date(b.timestamp);
        }
        return a.number - b.number;
      });
      return sorted.map((m, index) => ({ ...m, number: index + 1 }));
    });
  };

  // Prüfen ob Messpunkt leer ist (kann gelöscht werden)
  const isMeasurementEmpty = (measurement) => {
    return !measurement.progressTime && 
           !measurement.individualTime && 
           !measurement.grade &&
           !measurement.activity &&
           !measurement.note;
  };

  // Messpunkt einfügen (vor dem angegebenen Messpunkt)
  const handleInsertMeasurement = (beforeNumber) => {
    if (!selectedSession) return;
    
    // Demo-Modus: Maximal 20 Messpunkte
    if (selectedSession?.isDemo && measurements.length >= 20) {
      alert(t('alerts.demo_max_measurements'));
      return;
    }
    
    const measurementIndex = measurements.findIndex(m => m.number === beforeNumber);
    const insertIndex = measurementIndex >= 0 ? measurementIndex : measurements.length;
    
    // Bestimme Fortschrittszeit basierend auf vorherigem Messpunkt
    let progressTime = 0;
    let individualTime = 0;
    if (insertIndex > 0 && measurements[insertIndex - 1]) {
      progressTime = measurements[insertIndex - 1].progressTime;
    } else if (insertIndex < measurements.length && measurements[insertIndex]) {
      progressTime = measurements[insertIndex].progressTime;
    } else if (isRunning) {
      progressTime = getCurrentTime();
    }
    
    // Verwende die WA-Nr aus dem State (wurde in handleStart aktualisiert) oder aus der Session
    const currentResumeNumber = resumeNumber || selectedSession?.resumeNumber || 1;
    
    const now = Date.now();
    const newMeasurement = {
      number: insertIndex + 1,
      grade: 0,
      progressTime: progressTime,
      individualTime: individualTime,
      activity: '',
      timeType: 'thb',
      note: '',
      timestamp: new Date().toISOString(),
      resumeNumber: currentResumeNumber,
      excluded: false,
      isNote: false,
      lastModified: now, // Delta-Tracking: Zeitpunkt der Erstellung/letzten Änderung
      lastUploaded: null // Delta-Tracking: Zeitpunkt des letzten erfolgreichen Uploads
    };

    setMeasurements(prev => {
      const newMeasurements = [...prev];
      newMeasurements.splice(insertIndex, 0, newMeasurement);
      return newMeasurements;
    });
    
    renumberMeasurements();
    
    // Aktualisiere Sessions
    const updatedSessions = sessions.map(s =>
      s.id === selectedSession.id
        ? { ...s, measurements: [...measurements, newMeasurement], lastUpdated: new Date().toISOString() }
        : s
    );
    // Liste neu sortieren und begrenzen
    const limitedSessions = limitAndSortSessions(updatedSessions);
    setSessions(limitedSessions);
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
  };

  // Messpunkt löschen (nur wenn leer)
  const handleDeleteMeasurement = (measurement) => {
    if (!isMeasurementEmpty(measurement)) {
      alert(t('alerts.measurement_delete_blocked'));
      return;
    }

    // Öffne Lösch-Dialog
    setDeletingMeasurement(measurement);
  };

  // Lösch-Dialog bestätigen
  const handleConfirmDelete = async () => {
    if (!deletingMeasurement || !selectedSession) return;

    // Erstelle Audit-Log vor dem Löschen
    if (selectedSession.id) {
      try {
        await addAuditLog(
          selectedSession.id,
          'DELETE',
          deletingMeasurement.number,
          deletingMeasurement, // Alte Werte: komplette Messung
          null, // Neue Werte: null (gelöscht)
          ['all'], // Alle Felder betroffen
          'Messpunkt gelöscht'
        );
      } catch (error) {
        console.error('[AuditLog] Fehler beim Erstellen des Protokoll-Eintrags:', error);
      }
    }

    // Filtere gelöschte Messung heraus
    const filteredMeasurements = measurements.filter(m => m.number !== deletingMeasurement.number);
    
    // Neuberechnung der Einzelzeiten nach Löschen (berücksichtigt ausgeklammerte Messpunkte)
    const recalculatedMeasurements = recalculateIndividualTimes(filteredMeasurements);
    
    setMeasurements(recalculatedMeasurements);
    renumberMeasurements();
    
    // Aktualisiere Sessions
    const updatedSessions = sessions.map(s =>
      s.id === selectedSession.id
        ? { ...s, measurements: recalculatedMeasurements, lastUpdated: new Date().toISOString() }
        : s
    );
    // Liste neu sortieren und begrenzen
    const limitedSessions = limitAndSortSessions(updatedSessions);
    setSessions(limitedSessions);
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
    
    // Schließe Dialog
    setDeletingMeasurement(null);
  };

  // Lösch-Dialog abbrechen
  const handleCancelDelete = () => {
    setDeletingMeasurement(null);
  };


  // Messpunkt ausklammern/einklammen (Toggle)
  // eslint-disable-next-line no-unused-vars
  const handleToggleExcludeMeasurement = (measurement) => {    debugLog('App.jsx:1944', 'handleToggleExcludeMeasurement called', {measurementNumber:measurement.number,currentExcluded:measurement.excluded,newExcluded:!measurement.excluded,resumeNumber:measurement.resumeNumber}, 'A');    const updatedMeasurements = measurements.map(m =>
      m.number === measurement.number
        ? { ...m, excluded: !m.excluded }
        : m
    );
    
    // Neuberechnung der Einzelzeiten nach Ausklammern/Einklammern
    const recalculatedMeasurements = recalculateIndividualTimes(updatedMeasurements);
    debugLog('App.jsx:1954', 'After recalculateIndividualTimes, setting measurements', {recalculatedCount:recalculatedMeasurements.length,firstFew:recalculatedMeasurements.slice(0,3).map(m=>({number:m.number,excluded:m.excluded,individualTime:m.individualTime}))}, 'C');
    setMeasurements(recalculatedMeasurements);
    
    // Aktualisiere Sessions
    const updatedSessions = sessions.map(s =>
      s.id === selectedSession.id
        ? { ...s, measurements: recalculatedMeasurements, lastUpdated: new Date().toISOString() }
        : s
    );
    // Liste neu sortieren und begrenzen
    const limitedSessions = limitAndSortSessions(updatedSessions);
    setSessions(limitedSessions);
    localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(limitedSessions));
  };

  const handleCancelEdit = () => {
    setEditingMeasurement(null);
    setEditActivity('');
    setEditNote('');
    setEditExcluded(false);
    setEditEinflussgroessenValues({});
    setEditPhotos([]);
  };

  // Speech recognition handlers
  // Speech recognition for main note input - Start
  const handleStartListening = () => {
    if (!recognition) {
      if (isSafari()) {
        alert(t('alerts.speech_safari'));
      } else {
      alert(t('alerts.speech_not_supported'));
      }
      return;
    }
    
    if (!isListeningRef.current) {
      try {
        isListeningRef.current = true;
        setIsListening(true);
        recognition.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        isListeningRef.current = false;
        setIsListening(false);
      }
    }
  };

  // Speech recognition for main note input - Stop
  const handleStopListening = () => {
    if (recognition && isListeningRef.current) {
      try {
        isListeningRef.current = false;
        setIsListening(false);
        recognition.stop();
      } catch (error) {
        console.error('Error stopping recognition:', error);
        isListeningRef.current = false;
        setIsListening(false);
      }
    }
  };

  // Zentrale Berechnungsroutine für Vorgabezeit
  const calculateVorgabeTime = useCallback((individualTime, grade) => {
    if (!grade || grade === 0) return 0;
    return Math.round(individualTime * (grade / 100));
  }, []);

  // Aktualisiere Zusammenstellung mit Vorgabezeiten aus der Analyse
  const updateZusammenstellungVorgabezeiten = useCallback((data, analysis) => {
    if (!data?.zusammenstellung || !analysis) return data;
    
    // Berechne Summe der Vorgabezeiten und übernehme sie in Grundzeit
    const decimalPlaces = data.decimal_places_vorderseite || 4;
    
    const updated = data.zusammenstellung.map(item => {
      // Extrahiere Zeitart aus Text (z.B. [thb] aus "beeinflußbare Hauptzeit [thb]")
      const match = item.art?.match(/\[(\w+)\]/);
      if (match) {
        const zeitart = match[1];
        const timeTypeData = analysis.byTimeType[zeitart];
        if (timeTypeData && timeTypeData.totalVorgabeTime) {
          // Umrechnung: Hundertstel Minuten -> Minuten
          return {
            ...item,
            zeit_min: parseFloat((timeTypeData.totalVorgabeTime / 100).toFixed(decimalPlaces))
          };
        }
      }
      return item;
    });
    
    const summe = updated.reduce((sum, item) => sum + (parseFloat(item.zeit_min) || 0), 0);
    const grundzeit_tg = parseFloat(summe.toFixed(decimalPlaces));
    
    // Berechne Zeit je Einheit automatisch: Grundzeit * (1 + Verteilzeit in Prozent / 100)
    // Bei 0% Verteilzeit: Zeit je Einheit = Grundzeit
    let zeit_je_einheit_te1 = data.zeit_je_einheit_te1 || 0;
    if (data.verteilzeit_tv_prozent !== undefined && data.verteilzeit_tv_prozent !== null) {
      // Zeit je Einheit = Grundzeit * (1 + Verteilzeit in Prozent / 100)
      // Bei 0% Verteilzeit: te1 = tg * (1 + 0/100) = tg
      zeit_je_einheit_te1 = parseFloat((grundzeit_tg * (1 + data.verteilzeit_tv_prozent / 100)).toFixed(decimalPlaces));
    } else if (grundzeit_tg > 0) {
      // Wenn keine Verteilzeit gesetzt ist, aber Grundzeit vorhanden, setze te1 = tg
      zeit_je_einheit_te1 = parseFloat(grundzeit_tg.toFixed(decimalPlaces));
    }
    
    // Berechne Dauer für alle Wiederaufnahmen neu, wenn beginnzeit und endzeit vorhanden sind
    const wiederaufnahmen = (data.wiederaufnahmen || []).map(entry => {
      if (entry.beginnzeit && entry.endzeit) {
        const calculatedDauer = calculateDuration(entry.beginnzeit, entry.endzeit, entry.datum);        debugLog('App.jsx:7473', 'updateZusammenstellungVorgabezeiten: Calculated dauer', {entryNummer:entry.nummer,beginnzeit:entry.beginnzeit,endzeit:entry.endzeit,datum:entry.datum,calculatedDauer,oldDauer:entry.dauer}, 'D');        return {
          ...entry,
          dauer: calculatedDauer
        };
      }
      return entry;
    });
    
    return { 
      ...data, 
      zusammenstellung: updated,
      grundzeit_tg: grundzeit_tg,
      zeit_je_einheit_te1: zeit_je_einheit_te1,
      wiederaufnahmen: wiederaufnahmen
    };
  }, []);

  // Hilfsfunktion: Berechne Durchschnitt, wobei 0-Werte ausgeschlossen werden
  const calculateAvgGrade = useCallback((grades) => {
    const nonZeroGrades = grades.filter(g => g !== null && g !== undefined && g !== 0);
    if (nonZeroGrades.length > 0) {
      return (nonZeroGrades.reduce((a, b) => a + b, 0) / nonZeroGrades.length).toFixed(1);
    }
    return 0;
  }, []);

  // Analysis
  const getAnalysis = () => {
    if (!licenseStatus) return null;

    const analysis = {
      byActivity: {},
      byTimeType: {},
      byKuerzel: {},
      byKuerzelE: {},  // Kürzel + Einflussgrößen-Namen (ohne Ausprägungen)
      byKuerzelEA: {}, // Kürzel + Einflussgrößen mit Ausprägungen (Name:Wert)
      pivot: {}, // Pivot-Tabelle: activity -> timeType -> data
      excludedTimeTypes: {}, // "x__" Zeitarten separat sammeln
      excludedKuerzels: {} // Kürzel mit "x__" Zeitarten separat sammeln
    };

    measurements.forEach(m => {
      if (m.isNote || m.excluded) return; // Notizen und ausgeklammerte nicht in Auswertung
      
      const activity = m.activity || 'Keine Tätigkeit';
      // Leere Strings explizit behandeln
      const timeType = (m.timeType === null || m.timeType === undefined || m.timeType === '') ? 'Keine Zeitart' : m.timeType;
      const kuerzel = (m.kuerzel === null || m.kuerzel === undefined || m.kuerzel === '') ? 'Kein Kürzel' : m.kuerzel;
      
      // Prüfe ob "x__" Zeitart (nicht zu verwendende Zeit)
      const isExcludedTimeType = timeType === 'x__';
      
      // By Activity (immer, auch für x__)
      if (!analysis.byActivity[activity]) {
        analysis.byActivity[activity] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
      }
      analysis.byActivity[activity].count++;
      analysis.byActivity[activity].totalTime += m.individualTime;
      analysis.byActivity[activity].totalVorgabeTime += (m.vorgabeTime || 0);
      analysis.byActivity[activity].grades.push(m.grade);
      analysis.byActivity[activity].measurements.push(m);

      // By TimeType (nur wenn NICHT x__)
      if (!isExcludedTimeType) {
        if (!analysis.byTimeType[timeType]) {
          analysis.byTimeType[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.byTimeType[timeType].count++;
        analysis.byTimeType[timeType].totalTime += m.individualTime;
        analysis.byTimeType[timeType].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.byTimeType[timeType].grades.push(m.grade);
        analysis.byTimeType[timeType].measurements.push(m);
      } else {
        // x__ Zeitarten separat sammeln
        if (!analysis.excludedTimeTypes[timeType]) {
          analysis.excludedTimeTypes[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.excludedTimeTypes[timeType].count++;
        analysis.excludedTimeTypes[timeType].totalTime += m.individualTime;
        analysis.excludedTimeTypes[timeType].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.excludedTimeTypes[timeType].grades.push(m.grade);
        analysis.excludedTimeTypes[timeType].measurements.push(m);
      }

      // By Kuerzel (nur wenn NICHT x__)
      if (!isExcludedTimeType) {
        if (!analysis.byKuerzel[kuerzel]) {
          analysis.byKuerzel[kuerzel] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.byKuerzel[kuerzel].count++;
        analysis.byKuerzel[kuerzel].totalTime += m.individualTime;
        analysis.byKuerzel[kuerzel].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.byKuerzel[kuerzel].grades.push(m.grade);
        analysis.byKuerzel[kuerzel].measurements.push(m);

        // By Kürzel+E (Kürzel und Einflussgrößen-Namen, ohne Ausprägungen)
        let eaKey = '';
        let eaKeyEA = '';
        if (m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen) && Object.keys(m.einflussgroessen).length > 0) {
          eaKey = Object.keys(m.einflussgroessen).sort().join(', ');
          eaKeyEA = Object.entries(m.einflussgroessen).sort(([a],[b]) => a.localeCompare(b)).map(([k, v]) => k + ':' + (v != null ? String(v) : '')).join(', ');
        }
        const kuerzelEKey = kuerzel + (eaKey ? ' | ' + eaKey : '');
        const kuerzelEAKey = kuerzel + (eaKeyEA ? ' | ' + eaKeyEA : '');
        if (!analysis.byKuerzelE[kuerzelEKey]) {
          analysis.byKuerzelE[kuerzelEKey] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.byKuerzelE[kuerzelEKey].count++;
        analysis.byKuerzelE[kuerzelEKey].totalTime += m.individualTime;
        analysis.byKuerzelE[kuerzelEKey].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.byKuerzelE[kuerzelEKey].grades.push(m.grade);
        analysis.byKuerzelE[kuerzelEKey].measurements.push(m);
        if (!analysis.byKuerzelEA[kuerzelEAKey]) {
          analysis.byKuerzelEA[kuerzelEAKey] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.byKuerzelEA[kuerzelEAKey].count++;
        analysis.byKuerzelEA[kuerzelEAKey].totalTime += m.individualTime;
        analysis.byKuerzelEA[kuerzelEAKey].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.byKuerzelEA[kuerzelEAKey].grades.push(m.grade);
        analysis.byKuerzelEA[kuerzelEAKey].measurements.push(m);
      } else {
        // Kürzel mit x__ Zeitarten separat sammeln
        if (!analysis.excludedKuerzels[kuerzel]) {
          analysis.excludedKuerzels[kuerzel] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.excludedKuerzels[kuerzel].count++;
        analysis.excludedKuerzels[kuerzel].totalTime += m.individualTime;
        analysis.excludedKuerzels[kuerzel].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.excludedKuerzels[kuerzel].grades.push(m.grade);
        analysis.excludedKuerzels[kuerzel].measurements.push(m);
      }

      // Pivot: activity x timeType (nur wenn NICHT x__)
      if (!isExcludedTimeType) {
        if (!analysis.pivot[activity]) {
          analysis.pivot[activity] = {};
        }
        if (!analysis.pivot[activity][timeType]) {
          analysis.pivot[activity][timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [], measurements: [] };
        }
        analysis.pivot[activity][timeType].count++;
        analysis.pivot[activity][timeType].totalTime += m.individualTime;
        analysis.pivot[activity][timeType].totalVorgabeTime += (m.vorgabeTime || 0);
        analysis.pivot[activity][timeType].grades.push(m.grade);
        analysis.pivot[activity][timeType].measurements.push(m);
      }
    });

    // Berechne Durchschnitte für byActivity (0-Werte ausschließen)
    Object.keys(analysis.byActivity).forEach(activity => {
      const grades = analysis.byActivity[activity].grades;
      analysis.byActivity[activity].avgGrade = calculateAvgGrade(grades);
    });

    // Berechne Durchschnitte für byTimeType (0-Werte ausschließen)
    Object.keys(analysis.byTimeType).forEach(timeType => {
      const grades = analysis.byTimeType[timeType].grades;
      analysis.byTimeType[timeType].avgGrade = calculateAvgGrade(grades);
    });

    // Berechne Durchschnitte für excludedTimeTypes (x__) (0-Werte ausschließen)
    Object.keys(analysis.excludedTimeTypes).forEach(timeType => {
      const grades = analysis.excludedTimeTypes[timeType].grades;
      analysis.excludedTimeTypes[timeType].avgGrade = calculateAvgGrade(grades);
    });

    // Berechne Durchschnitte und Statistik (Min, Max, Mittelwert, Std, Varianz, Spannweite, Var.-Koeff.) für byKuerzel
    // Verwende vorgabeTime (Soll-Zeit in Hundertstel-Minuten) statt individualTime
    Object.keys(analysis.byKuerzel).forEach(kuerzel => {
      const g = analysis.byKuerzel[kuerzel];
      g.avgGrade = calculateAvgGrade(g.grades);
      const times = (g.measurements || []).map(mm => mm.vorgabeTime || 0);
      Object.assign(g, computeStats(times));
    });

    // Berechne Durchschnitte und Statistik für byKuerzelE
    // Verwende vorgabeTime (Soll-Zeit in Hundertstel-Minuten) statt individualTime
    Object.keys(analysis.byKuerzelE).forEach(key => {
      const g = analysis.byKuerzelE[key];
      g.avgGrade = calculateAvgGrade(g.grades);
      const times = (g.measurements || []).map(mm => mm.vorgabeTime || 0);
      Object.assign(g, computeStats(times));
    });

    // Berechne Durchschnitte und Statistik für byKuerzelEA
    // Verwende vorgabeTime (Soll-Zeit in Hundertstel-Minuten) statt individualTime
    Object.keys(analysis.byKuerzelEA).forEach(key => {
      const g = analysis.byKuerzelEA[key];
      g.avgGrade = calculateAvgGrade(g.grades);
      const times = (g.measurements || []).map(mm => mm.vorgabeTime || 0);
      Object.assign(g, computeStats(times));
    });

    // Berechne Durchschnitte für Pivot (0-Werte ausschließen)
    Object.keys(analysis.pivot).forEach(activity => {
      Object.keys(analysis.pivot[activity]).forEach(timeType => {
        const grades = analysis.pivot[activity][timeType].grades;
        analysis.pivot[activity][timeType].avgGrade = calculateAvgGrade(grades);
      });
    });

    return analysis;
  };

  // Alte Rendering-Funktionen wurden durch Komponenten ersetzt:
  // - renderBilanzKuerzelReport -> ReportBilanz
  // - renderTaetigkeitenReport -> ReportTaetigkeiten
  // - renderReportFooterOneline -> ReportFooter

  // Rendering-Funktion für Report-Content (ersetzt durch ReportContent-Komponente)
  const renderReportContent = useCallback((userEmail = email, userDeviceName = deviceName) => {
    const analysis = getAnalysis();
    return (
      <ReportContent
        reportOptions={reportOptions}
        vorderseiteData={vorderseiteData}
        measurements={measurements}
        analysis={analysis}
        ablaufDbData={ablaufDbData}
        decimalPlacesSollzeiten={decimalPlacesSollzeiten}
        formatTime={formatTime}
        formatSollzeit={formatSollzeit}
        formatGrade={formatGrade}
        getAnalysis={getAnalysis}
        updateZusammenstellungVorgabezeiten={updateZusammenstellungVorgabezeiten}
        ablaufDbName={ablaufDbName}
        setDeleteRowConfirm={setDeleteRowConfirm}
        VorderseiteForm={VorderseiteForm}
        timeTypes={timeTypes}
        defaultTimeTypes={defaultTimeTypes}
        userEmail={userEmail}
        userDeviceName={userDeviceName}
      />
    );
  }, [reportOptions, vorderseiteData, measurements, ablaufDbData, decimalPlacesSollzeiten, ablaufDbName, formatTime, formatSollzeit, formatGrade, getAnalysis, updateZusammenstellungVorgabezeiten, setDeleteRowConfirm, VorderseiteForm, timeTypes, defaultTimeTypes, email, deviceName]);

  // Handler für Druck
  const handlePrintReport = useCallback(() => {
    // Prüfe ob mindestens eine Option ausgewählt ist
    if (!reportOptions.vorderseite && !reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt && !reportOptions.messpunkteBilanzZeitarten && !reportOptions.bilanzKuerzel) {
      alert(t('alerts.report_select_content'));
      return;
    }

    // Funktion zum Speichern der ursprünglichen Styles
    const saveOriginalStyles = () => {
      const savedStyles = {
        body: {},
        html: {},
        app: {},
        vorderseiteElement: null,
        container: null,
        reportContent: null
      };

      // Speichere body-Styles
      const body = document.body;
      const bodyComputed = window.getComputedStyle(body);
      savedStyles.body = {
        margin: body.style.margin || bodyComputed.margin,
        padding: body.style.padding || bodyComputed.padding,
        overflow: body.style.overflow || bodyComputed.overflow,
        position: body.style.position || bodyComputed.position,
        width: body.style.width || bodyComputed.width,
        height: body.style.height || bodyComputed.height
      };

      // Speichere html-Styles
      const html = document.documentElement;
      const htmlComputed = window.getComputedStyle(html);
      savedStyles.html = {
        margin: html.style.margin || htmlComputed.margin,
        padding: html.style.padding || htmlComputed.padding,
        overflow: html.style.overflow || htmlComputed.overflow,
        position: html.style.position || htmlComputed.position,
        width: html.style.width || htmlComputed.width,
        height: html.style.height || htmlComputed.height
      };

      // Speichere .app-Styles
      const appElement = document.querySelector('.app');
      if (appElement) {
        const appComputed = window.getComputedStyle(appElement);
        savedStyles.app = {
          display: appElement.style.display || appComputed.display,
          position: appElement.style.position || appComputed.position,
          width: appElement.style.width || appComputed.width,
          height: appElement.style.height || appComputed.height,
          overflow: appElement.style.overflow || appComputed.overflow
        };
      }

      // Speichere vorderseiteElement-Styles
      const vorderseiteElement = document.querySelector('.report-vorderseite .vorderseite-form');
      if (vorderseiteElement) {
        const vorderseiteComputed = window.getComputedStyle(vorderseiteElement);
        savedStyles.vorderseiteElement = {
          transform: vorderseiteElement.style.transform || vorderseiteComputed.transform,
          transformOrigin: vorderseiteElement.style.transformOrigin || vorderseiteComputed.transformOrigin,
          width: vorderseiteElement.style.width || vorderseiteComputed.width,
          height: vorderseiteElement.style.height || vorderseiteComputed.height
        };
      }

      // Speichere container-Styles
      const container = document.querySelector('.report-vorderseite');
      if (container) {
        const containerComputed = window.getComputedStyle(container);
        savedStyles.container = {
          width: container.style.width || containerComputed.width,
          maxWidth: container.style.maxWidth || containerComputed.maxWidth,
          margin: container.style.margin || containerComputed.margin,
          padding: container.style.padding || containerComputed.padding
        };
      }

      // Speichere reportContent-Styles
      const reportContent = document.querySelector('.report-content');
      if (reportContent) {
        const reportContentComputed = window.getComputedStyle(reportContent);
        savedStyles.reportContent = {
          display: reportContent.style.display || reportContentComputed.display,
          visibility: reportContent.style.visibility || reportContentComputed.visibility,
          position: reportContent.style.position || reportContentComputed.position,
          left: reportContent.style.left || reportContentComputed.left,
          top: reportContent.style.top || reportContentComputed.top,
          width: reportContent.style.width || reportContentComputed.width,
          height: reportContent.style.height || reportContentComputed.height
        };
      }

      return savedStyles;
    };

    // Option 3: Druck in iframe (nur iPad) – Hauptseite wird vom Druck-Dialog nicht berührt, verhindert Skalierung/Blur
    const doPrintViaIframe = (currentVorderseiteData) => {
      const tryClone = () => {
      const reportContentEl = document.querySelector('.report-content');
      if (!reportContentEl) {
        setShowReportDialog(false);
        return;
      }
      // Beim ersten Aufruf kann der Report-Inhalt noch nicht gepaintet sein – klonen erst nach einem Frame
      const hasContent = reportContentEl.querySelector('.report-a4-container') || reportContentEl.querySelector('.report-section');
      if (!hasContent) {
        requestAnimationFrame(() => {
          requestAnimationFrame(tryClone);
        });
        return;
      }
      // Orientierung aus reportOptions verwenden (manuelle Auswahl im Dialog)
      const isLandscape = reportOptions.orientation === 'landscape';
      const iframeWidth = isLandscape ? '297mm' : '210mm';
      const iframeHeight = isLandscape ? '210mm' : '297mm';
      const baseHref = window.location.origin + (window.location.pathname || '/').replace(/\/[^/]*$/, '/');
      const links = document.querySelectorAll('link[rel="stylesheet"]');
      const styleTags = document.querySelectorAll('head > style');
      let linksHtml = '<base href="' + baseHref + '">';
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (href) linksHtml += '<link rel="stylesheet" href="' + link.href + '">';
      });
      styleTags.forEach((style) => {
        if (style.textContent) linksHtml += '<style>' + style.textContent + '</style>';
      });
      // Inline-Fallback für Druck: beide Orientierungen (Portrait/Landscape) und volle Seitenbreite für Header
      const printFallbackCss = [
        'body{margin:0;padding:0;background:#fff;color:#000}',
        '.app{display:block !important;width:100%}',
        '.report-content{display:block !important;visibility:visible !important;position:relative !important;left:auto !important;top:auto !important;width:100%}',
        '.report-content,.report-content *{color:#111827 !important}',
        '.report-content input,.report-content textarea,.report-content select,.report-content table,.report-content th,.report-content td{background:#fff !important}',
        '.report-content .report-table th{background:#f0f0f0 !important}',
        '.report-vorderseite button,.report-vorderseite .vorderseite-form button,.btn{display:none !important}',
        '.report-a4-container{width:100%;margin:0 auto;background:#fff;color:#000;box-sizing:border-box}',
        '.report-vorderseite{width:100%;margin:0 auto;box-sizing:border-box;padding:8mm 10mm 0 10mm}',
        '.vorderseite-form,.vorderseite-header-grid{width:100%;max-width:100%;box-sizing:border-box}',
        '.vorderseite-form input,.vorderseite-form textarea,.vorderseite-form select{background:#fff;color:#000}',
        '@media print and (orientation: portrait){.report-a4-container{max-width:210mm}.report-vorderseite{max-width:180mm;margin-left:auto;margin-right:auto}}',
        '@media print and (orientation: landscape){.report-a4-container{max-width:297mm}.report-vorderseite{max-width:267mm;margin-left:auto;margin-right:auto}}',
        '@media screen{.report-a4-container{max-width:' + (isLandscape ? '297' : '210') + 'mm}.report-vorderseite{max-width:' + (isLandscape ? '267' : '180') + 'mm}}',
        '@media print{body{margin:0;padding:0}.report-content{display:block !important;visibility:visible !important}}'
      ].join('\n');
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'Druck-Vorschau');
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:' + iframeWidth + ';height:' + iframeHeight + ';border:none;';
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(
        '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=' + iframeWidth + '">' +
        linksHtml +
        '<style>' + printFallbackCss + '</style></head><body class="print-mode" style="margin:0;padding:0;background:#fff;color:#000"><div class="app"></div></body></html>'
      );
      iframeDoc.close();
      const iframeBody = iframeDoc.body;
      if (iframeBody) {
        iframeBody.style.margin = '0';
        iframeBody.style.padding = '0';
        iframeBody.style.background = '#fff';
        iframeBody.style.color = '#000';
      }
      const app = iframeDoc.querySelector('.app');
      const clone = reportContentEl.cloneNode(true);
      clone.style.display = 'block';
      clone.style.visibility = 'visible';
      clone.style.position = 'relative';
      clone.style.left = 'auto';
      clone.style.top = 'auto';
      clone.style.background = '#fff';
      clone.style.color = '#000';
      app.appendChild(clone);
      // Buttons in der Vorderseite im Klon ausblenden (Fallback falls CSS noch nicht greift)
      try {
        const btns = clone.querySelectorAll('.report-vorderseite button, .report-vorderseite .vorderseite-form button, .btn');
        btns.forEach((b) => { b.style.display = 'none'; });
      } catch (e) { /* ignore */ }
      // Sichtbarkeit: geklonte Inline-Styles (z. B. Dark-Mode) überschreiben, damit Text im iframe sichtbar ist
      try {
        const walk = (el) => {
          if (!el || !el.style) return;
          el.style.setProperty('color', '#111827', 'important');
          if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.tagName === 'TD' || el.tagName === 'TH' || el.tagName === 'TABLE') {
            el.style.setProperty('background-color', el.tagName === 'TH' && el.closest('.report-table') ? '#f0f0f0' : '#fff', 'important');
          }
          if (el.tagName === 'TD' || el.tagName === 'TH') {
            el.style.setProperty('border-color', '#333', 'important');
          }
          const ch = el.children;
          if (ch) for (let i = 0; i < ch.length; i++) walk(ch[i]);
        };
        walk(clone);
      } catch (e) { /* ignore */ }
      // Vorderseite-Skalierung im iframe (wie im Hauptdokument)
      if (reportOptions.vorderseite && currentVorderseiteData) {
        const vorderseiteEl = iframeDoc.querySelector('.report-vorderseite .vorderseite-form');
        if (vorderseiteEl) {
          const isPortrait = reportOptions.orientation === 'portrait';
          const availableWidthMM = isPortrait ? 180 : 267;
          const mmToPx = 3.7;
          const availableWidthPX = availableWidthMM * mmToPx;
          const rect = vorderseiteEl.getBoundingClientRect();
          const actualWidth = rect.width || vorderseiteEl.scrollWidth || vorderseiteEl.offsetWidth;
          let scaleFactor = 1;
          if (actualWidth > availableWidthPX) {
            scaleFactor = (availableWidthPX / actualWidth) * 0.97;
            vorderseiteEl.style.transform = 'scale(' + scaleFactor + ')';
            vorderseiteEl.style.transformOrigin = 'top left';
            const container = vorderseiteEl.closest('.report-vorderseite');
            if (container) {
              container.style.width = availableWidthPX + 'px';
              container.style.maxWidth = availableWidthMM + 'mm';
            }
          }
          // Berechnete Vorderseite-Daten in den Klon injizieren (Zusammenstellung, GrundZeit, Zeit je Einheit),
          // damit die Druckvorschau die gleichen Werte wie nach „Berechne/Vorgabezeiten aktualisieren“ zeigt
          const dp = currentVorderseiteData.decimal_places_vorderseite ?? 4;
          const fmt = (v) => (parseFloat(v) || 0).toFixed(dp);
          const zuss = currentVorderseiteData.zusammenstellung || [];
          const tables = vorderseiteEl.querySelectorAll('table');
          for (const table of tables) {
            const ths = table.querySelectorAll('thead th');
            const thText = Array.from(ths).map((t) => t.textContent || '').join(' ');
            if (thText.includes('Zeit in min') && thText.includes('Zusammenstellung')) {
              const rows = table.querySelectorAll('tbody tr');
              rows.forEach((tr, index) => {
                const inputs = tr.querySelectorAll('input');
                if (inputs.length >= 3 && zuss[index]) {
                  const item = zuss[index];
                  inputs[0].value = item.art || '';
                  inputs[1].value = fmt(item.zeit_min);
                  inputs[2].value = item.herkunft || '';
                }
              });
              break;
            }
          }
          // Grundzeit/Verteilzeit/Zeit je Einheit: Grid-Struktur (Reihenfolge: Grundzeit, Verteilzeit, Zeit je Einheit)
          const grundzeitGrid = vorderseiteEl.querySelector('.vorderseite-grundzeit-grid');
          if (grundzeitGrid) {
            const inputs = grundzeitGrid.querySelectorAll('input');
            if (inputs.length >= 3) {
              inputs[0].value = fmt(currentVorderseiteData.grundzeit_tg);
              const v = currentVorderseiteData.verteilzeit_tv_prozent;
              inputs[1].value = (v === '' || v == null) ? '' : String(v);
              inputs[2].value = fmt(currentVorderseiteData.zeit_je_einheit_te1);
            }
          } else {
            const labels = vorderseiteEl.querySelectorAll('label');
            for (const label of labels) {
              const text = (label.textContent || '').trim();
              const parent = label.parentElement;
              const input = parent ? parent.querySelector('input') : null;
              if (!input) continue;
              if (text.includes('Grundzeit tg')) {
                input.value = fmt(currentVorderseiteData.grundzeit_tg);
              } else if (text.includes('Zeit je Einheit te1')) {
                input.value = fmt(currentVorderseiteData.zeit_je_einheit_te1);
              } else if (text.includes('Verteilzeit tv') && !text.includes('%')) {
                const v = currentVorderseiteData.verteilzeit_tv_prozent;
                input.value = (v === '' || v == null) ? '' : String(v);
              }
            }
          }
        }
      }
      setShowReportDialog(false);
      const cleanup = () => {
        try {
          if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        } catch (e) {
          console.warn('[Print] Iframe-Cleanup:', e);
        }
      };
      const doPrint = () => {
        // Direkt vor print(): Farben/ Hintergründe nochmals per JS erzwingen (hilft v. a. auf PC-Browsern mit Timing der Styles)
        try {
          const root = iframeDoc.querySelector('.app');
          if (root) {
            const forcePrintStyles = (el) => {
              if (!el || !el.style) return;
              el.style.setProperty('color', '#111827', 'important');
              if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.tagName === 'TD' || el.tagName === 'TH' || el.tagName === 'TABLE') {
                el.style.setProperty('background-color', el.tagName === 'TH' && el.closest('.report-table') ? '#f0f0f0' : '#fff', 'important');
              }
              if (el.tagName === 'TD' || el.tagName === 'TH') {
                el.style.setProperty('border-color', '#333', 'important');
              }
              const ch = el.children;
              if (ch) for (let i = 0; i < ch.length; i++) forcePrintStyles(ch[i]);
            };
            forcePrintStyles(root);
          }
        } catch (e) { /* ignore */ }
        try {
          iframe.contentWindow.print();
        } catch (e) {
          console.error('[Print] Iframe print failed:', e);
          cleanup();
          return;
        }
        iframe.contentWindow.addEventListener('afterprint', cleanup, { once: true });
        setTimeout(cleanup, 4000);
      };
      // Auf iPad länger warten, damit externe Styles in der Druckvorschau geladen sind
      const printDelayMs = 1200;
      const loadFallback = setTimeout(doPrint, 2500);
      iframe.contentWindow.addEventListener('load', () => {
        clearTimeout(loadFallback);
        setTimeout(doPrint, printDelayMs);
      }, { once: true });
    };
    tryClone();
    };

    // Funktion zum Wiederherstellen der ursprünglichen Styles
    const restoreOriginalStyles = (savedStyles) => {
      if (!savedStyles) return;

      // Stelle body-Styles wieder her
      const body = document.body;
      if (savedStyles.body) {
        Object.keys(savedStyles.body).forEach(prop => {
          if (savedStyles.body[prop] && savedStyles.body[prop] !== '') {
            body.style[prop] = savedStyles.body[prop];
          } else {
            body.style.removeProperty(prop);
          }
        });
      }

      // Stelle html-Styles wieder her
      const html = document.documentElement;
      if (savedStyles.html) {
        Object.keys(savedStyles.html).forEach(prop => {
          if (savedStyles.html[prop] && savedStyles.html[prop] !== '') {
            html.style[prop] = savedStyles.html[prop];
          } else {
            html.style.removeProperty(prop);
          }
        });
      }

      // Stelle .app-Styles wieder her
      const appElement = document.querySelector('.app');
      if (appElement && savedStyles.app) {
        Object.keys(savedStyles.app).forEach(prop => {
          if (savedStyles.app[prop] && savedStyles.app[prop] !== '') {
            appElement.style[prop] = savedStyles.app[prop];
          } else {
            appElement.style.removeProperty(prop);
          }
        });
      }

      // Stelle vorderseiteElement-Styles wieder her
      const vorderseiteElement = document.querySelector('.report-vorderseite .vorderseite-form');
      if (vorderseiteElement && savedStyles.vorderseiteElement) {
        Object.keys(savedStyles.vorderseiteElement).forEach(prop => {
          if (savedStyles.vorderseiteElement[prop] && savedStyles.vorderseiteElement[prop] !== '') {
            vorderseiteElement.style[prop] = savedStyles.vorderseiteElement[prop];
          } else {
            vorderseiteElement.style.removeProperty(prop);
          }
        });
      }

      // Stelle container-Styles wieder her
      const container = document.querySelector('.report-vorderseite');
      if (container && savedStyles.container) {
        Object.keys(savedStyles.container).forEach(prop => {
          if (savedStyles.container[prop] && savedStyles.container[prop] !== '') {
            container.style[prop] = savedStyles.container[prop];
          } else {
            container.style.removeProperty(prop);
          }
        });
      }

      // Stelle reportContent-Styles wieder her
      const reportContent = document.querySelector('.report-content');
      if (reportContent && savedStyles.reportContent) {
        Object.keys(savedStyles.reportContent).forEach(prop => {
          if (savedStyles.reportContent[prop] && savedStyles.reportContent[prop] !== '') {
            reportContent.style[prop] = savedStyles.reportContent[prop];
          } else {
            reportContent.style.removeProperty(prop);
          }
        });
      }
    };

    // Funktion zum eigentlichen Drucken
    const proceedWithPrint = (currentVorderseiteData) => {
      // Einheitlicher Druckpfad für PC und iPad: immer iframe – gleiche Ränder/Darstellung, Hauptseite bleibt unberührt
      requestAnimationFrame(() => {
        setTimeout(() => {
          doPrintViaIframe(currentVorderseiteData);
        }, 100);
      });
      return;

      // --- Legacy: Hauptseiten-Druck (wird nicht mehr ausgeführt) ---
      const isIPad = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                     (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const originalStyles = saveOriginalStyles();
      
      // Viewport speichern für Wiederherstellung nach Druck (Option 1+2: verhindert geblurt/vergrößert auf iPad)
      const viewport = document.querySelector('meta[name="viewport"]');
      const savedViewportContent = viewport ? viewport.getAttribute('content') : '';
      
      // Setze einen Flag, dass wir im Druck-Modus sind
      document.body.classList.add('print-mode');
      
      // Warte kurz bevor Dialog geschlossen wird (iPad benötigt mehr Zeit)
      // WICHTIG: Auf iPad im Vollbild-Modus schließen wir den Dialog NICHT sofort,
      // sondern erst nach dem vollständigen Cleanup, um das Vollbild nicht zu verlassen
      const dialogCloseDelay = isIPad ? 100 : 0;
      setTimeout(() => {
        // Schließe Dialog (auf iPad wird dies später im Cleanup gemacht, um Vollbild zu erhalten)
        if (!isIPad) {
          setShowReportDialog(false);
        }
        
        // Warte auf nächsten Frame, damit Dialog geschlossen ist
        requestAnimationFrame(() => {
          // Berechne Skalierungsfaktor für Vorderseite, falls vorhanden
          if (reportOptions.vorderseite && currentVorderseiteData) {
            // Längere Verzögerung für iPad, damit Report vollständig gerendert ist
            const renderDelay = isIPad ? 300 : 100;
            setTimeout(() => {
              requestAnimationFrame(() => {
                const vorderseiteElement = document.querySelector('.report-vorderseite .vorderseite-form');
                if (vorderseiteElement) {
                  // Orientierung aus reportOptions verwenden (manuelle Auswahl im Dialog)
                  const isPortrait = reportOptions.orientation === 'portrait';
                  
                  // Verfügbare Breite basierend auf Orientierung:
                  // Portrait (Hochformat): 210mm - 15mm links - 15mm rechts = 180mm
                  // Landscape (Querformat): 297mm - 15mm links - 15mm rechts = 267mm
                  // WICHTIG: Standard ist Portrait, da @page size: A4 standardmäßig Portrait ist
                  const availableWidthMM = isPortrait ? 180 : 267;
                  
                  // Konvertiere mm zu px: 96 DPI = 3.779527559px/mm, verwende 3.7px/mm für Sicherheit
                  const mmToPx = 3.7;
                  const availableWidthPX = availableWidthMM * mmToPx;
                  
                  // Messe die tatsächliche Breite des Elements
                  // WICHTIG: Messe nachdem das Element gerendert wurde, aber vor dem Druck
                  const rect = vorderseiteElement.getBoundingClientRect();
                  const actualWidth = rect.width || vorderseiteElement.scrollWidth || vorderseiteElement.offsetWidth;
                  
                  console.log('[Print] Vorderseite Skalierung:', {
                    orientation: isPortrait ? 'Portrait' : 'Landscape',
                    availableWidthMM,
                    availableWidthPX: availableWidthPX.toFixed(2),
                    actualWidth: actualWidth.toFixed(2),
                    ratio: (actualWidth / availableWidthPX).toFixed(2),
                    windowSize: `${window.innerWidth}x${window.innerHeight}`
                  });
                  
                  // Berechne Skalierungsfaktor
                  let scaleFactor = 1;
                  if (actualWidth > availableWidthPX) {
                    scaleFactor = availableWidthPX / actualWidth;
                    // Füge Sicherheitsabstand hinzu (97% der verfügbaren Breite)
                    scaleFactor = scaleFactor * 0.97;
                  }
                  
                  // Wende Skalierung an, wenn nötig
                  if (scaleFactor < 1) {
                    console.log('[Print] Wende Skalierung an:', scaleFactor.toFixed(3));
                    vorderseiteElement.style.transform = `scale(${scaleFactor})`;
                    vorderseiteElement.style.transformOrigin = 'top left';
                    // Stelle sicher, dass der Container genug Platz hat
                    const container = vorderseiteElement.closest('.report-vorderseite');
                    if (container) {
                      // Setze explizite Breite, damit der Browser weiß, wie viel Platz verfügbar ist
                      container.style.width = `${availableWidthPX}px`;
                      container.style.maxWidth = `${availableWidthMM}mm`;
                    }
                  } else {
                    console.log('[Print] Keine Skalierung nötig');
                    // Stelle trotzdem sicher, dass die Breite korrekt ist
                    const container = vorderseiteElement.closest('.report-vorderseite');
                    if (container) {
                      container.style.maxWidth = `${availableWidthMM}mm`;
                    }
                  }
                }
                
                // Stelle sicher, dass Report-Content sichtbar ist
                const reportContent = document.querySelector('.report-content');
                if (reportContent) {
                  reportContent.style.display = 'block';
                  reportContent.style.visibility = 'visible';
                }
                
                // Öffne Druck-Dialog nach Verzögerung (länger für iPad)
                const printDelay = isIPad ? 400 : 150;
                setTimeout(() => {
                  requestAnimationFrame(() => {
                    // Doppelte requestAnimationFrame für iPad, um sicherzustellen, dass alles gerendert ist
                    const doPrint = () => {
                      // Option 2: Viewport vor Druck fixieren (nur iPad) – verhindert Zoom-Änderung durch Safari
                      if (isIPad && viewport) {
                        viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
                      }
                      window.print();
                      
                      // Entferne Skalierung und Flag nach Druck (längere Verzögerung für iPad)
                      // WICHTIG: Auf iPad im Vollbild-Modus benötigen wir mehr Zeit, damit das Vollbild nicht verlassen wird
                      const cleanupDelay = isIPad ? 3000 : 1000;
                      setTimeout(() => {
                        // Stelle ursprüngliche Styles wieder her (WICHTIG: vor dem Entfernen der print-mode Klasse)
                        restoreOriginalStyles(originalStyles);
                        
                        const vorderseiteElement = document.querySelector('.report-vorderseite .vorderseite-form');
                        if (vorderseiteElement) {
                          vorderseiteElement.style.transform = '';
                          vorderseiteElement.style.transformOrigin = '';
                        }
                        const container = document.querySelector('.report-vorderseite');
                        if (container) {
                          container.style.width = '';
                          container.style.maxWidth = '';
                        }
                        document.body.classList.remove('print-mode');
                        
                        // Option 1: Viewport nach Druck zurücksetzen + Reflow (verhindert geblurt/vergrößert auf iPad)
                        if (viewport) {
                          viewport.setAttribute('content', savedViewportContent || 'width=device-width, initial-scale=1');
                        }
                        void document.body.offsetHeight; // Reflow auslösen
                        window.dispatchEvent(new Event('resize'));
                        if (isIPad) {
                          document.documentElement.style.zoom = '1';
                          document.body.style.zoom = '1';
                        }
                        
                        // Stelle sicher, dass report-content wieder versteckt wird (aber nicht sofort, um Layout-Änderungen zu vermeiden)
                        const reportContent = document.querySelector('.report-content');
                        if (reportContent) {
                          // Auf iPad: Warte länger, damit Vollbild nicht verlassen wird
                          const hideContentDelay = isIPad ? 500 : 100;
                          setTimeout(() => {
                            reportContent.style.display = 'none';
                            reportContent.style.visibility = 'hidden';
                            
                            // Auf iPad: Schließe Dialog erst jetzt, nach vollständigem Cleanup, um Vollbild zu erhalten
                            if (isIPad) {
                              setShowReportDialog(false);
                            }
                          }, hideContentDelay);
                        } else {
                          // Falls report-content nicht gefunden wurde, schließe Dialog trotzdem (auf iPad)
                          if (isIPad) {
                            setTimeout(() => {
                              setShowReportDialog(false);
                            }, 500);
                          }
                        }
                      }, cleanupDelay);
                    };
                    
                    if (isIPad) {
                      requestAnimationFrame(doPrint);
                    } else {
                      doPrint();
                    }
                  });
                }, printDelay);
              });
            }, renderDelay);
          } else {
            // Keine Vorderseite, direkt drucken (längere Verzögerung für iPad)
            // Stelle sicher, dass Report-Content sichtbar ist
            const reportContent = document.querySelector('.report-content');
            if (reportContent) {
              reportContent.style.display = 'block';
              reportContent.style.visibility = 'visible';
            }
            
            const printDelay = isIPad ? 400 : 100;
            setTimeout(() => {
              requestAnimationFrame(() => {
                // Doppelte requestAnimationFrame für iPad, um sicherzustellen, dass alles gerendert ist
                const doPrint = () => {
                  // Option 2: Viewport vor Druck fixieren (nur iPad) – verhindert Zoom-Änderung durch Safari
                  if (isIPad && viewport) {
                    viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
                  }
                  window.print();
                  
                  // Entferne Flag nach Druck (längere Verzögerung für iPad)
                  // WICHTIG: Auf iPad im Vollbild-Modus benötigen wir mehr Zeit, damit das Vollbild nicht verlassen wird
                  const cleanupDelay = isIPad ? 3000 : 1000;
                  setTimeout(() => {
                    // Stelle ursprüngliche Styles wieder her (WICHTIG: vor dem Entfernen der print-mode Klasse)
                    restoreOriginalStyles(originalStyles);
                    
                    document.body.classList.remove('print-mode');
                    
                    // Option 1: Viewport nach Druck zurücksetzen + Reflow (verhindert geblurt/vergrößert auf iPad)
                    if (viewport) {
                      viewport.setAttribute('content', savedViewportContent || 'width=device-width, initial-scale=1');
                    }
                    void document.body.offsetHeight; // Reflow auslösen
                    window.dispatchEvent(new Event('resize'));
                    if (isIPad) {
                      document.documentElement.style.zoom = '1';
                      document.body.style.zoom = '1';
                    }
                    
                    // Stelle sicher, dass report-content wieder versteckt wird (aber nicht sofort, um Layout-Änderungen zu vermeiden)
                    const reportContent = document.querySelector('.report-content');
                    if (reportContent) {
                      // Auf iPad: Warte länger, damit Vollbild nicht verlassen wird
                      const hideContentDelay = isIPad ? 500 : 100;
                      setTimeout(() => {
                        reportContent.style.display = 'none';
                        reportContent.style.visibility = 'hidden';
                        
                        // Auf iPad: Schließe Dialog erst jetzt, nach vollständigem Cleanup, um Vollbild zu erhalten
                        if (isIPad) {
                          setShowReportDialog(false);
                        }
                      }, hideContentDelay);
                    } else {
                      // Falls report-content nicht gefunden wurde, schließe Dialog trotzdem (auf iPad)
                      if (isIPad) {
                        setTimeout(() => {
                          setShowReportDialog(false);
                        }, 500);
                      }
                    }
                  }, cleanupDelay);
                };
                
                if (isIPad) {
                  requestAnimationFrame(doPrint);
                } else {
                  doPrint();
                }
              });
            }, printDelay);
          }
        });
      }, dialogCloseDelay);
    };

    // Aktualisiere Vorderseite (Vorgabezeiten) vor dem Drucken, falls Vorderseite ausgewählt ist
    if (reportOptions.vorderseite && vorderseiteData && getAnalysis && updateZusammenstellungVorgabezeiten) {
      const analysis = getAnalysis();
      if (analysis) {
        const updated = updateZusammenstellungVorgabezeiten(vorderseiteData, analysis);
        // Aktualisiere Vorderseite-Daten, damit sie im Report verwendet werden
        setVorderseiteData(updated);
        // Warte länger für iPad/Vollbild, damit der State aktualisiert wird und der Report mit den neuen Daten gerendert wird
        // iPad benötigt mehr Zeit für das Rendering
        const isIPad = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                       (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const delay = isIPad ? 500 : 200;
        
        // Verwende requestAnimationFrame für besseres Timing
        requestAnimationFrame(() => {
          setTimeout(() => {
            requestAnimationFrame(() => {
              proceedWithPrint(updated);
            });
          }, delay);
        });
        return;
      }
    }
    
    // Wenn keine Vorderseite oder keine Aktualisierung nötig, direkt drucken
    proceedWithPrint(vorderseiteData);
  }, [reportOptions, vorderseiteData, getAnalysis, updateZusammenstellungVorgabezeiten, setVorderseiteData]);

  // Handler für PDF-Export (vereinfacht: verwendet window.print mit PDF-Dialog)
  const handleExportPDF = useCallback(() => {
    handlePrintReport(); // Zuerst einfache Lösung: gleiche Funktion wie Druck (Browser bietet "Als PDF speichern" an)
  }, [handlePrintReport]);

  // Login Screen - nur anzeigen, wenn Update-Resume-Check abgeschlossen ist UND kein Update-Resume-Dialog angezeigt wird
  if (!isLoggedIn && !isCheckingUpdateResume && !showUpdateResumeDialog) {    debugLog('App.jsx:8678', 'Login screen check', {isLoggedIn,isCheckingUpdateResume,showUpdateResumeDialog,willShowLogin:true}, 'A');
    debugLog('App.jsx:8682', 'Rendering login screen', {isLoggedIn,isCheckingUpdateResume,showUpdateResumeDialog}, 'A');
    // Zeige Registrierungsseite wenn gewünscht
    if (showRegister) {
      return (
        <UserAuth 
          backendUrl={backendUrl}
          onBack={() => setShowRegister(false)}
          onLogin={(user, token) => {
            // Nach erfolgreichem Login
            setEmail(user.email);
            setIsLoggedIn(true);
            setShowRegister(false);
            // Backend URL speichern falls vorhanden
            if (backendUrl) {
              localStorage.setItem('backendUrl', backendUrl);
            }
            // License prüfen (wird später beim normalen Login gemacht)
            // checkLicense().catch(err => {
            //   console.error('License check error:', err);
            //   setLicenseStatus(false);
            // });
            // Sessions laden
            const savedSessions = localStorage.getItem(STORAGE_KEY_SESSIONS);
            if (savedSessions) {
              try {
                setSessions(JSON.parse(savedSessions));
              } catch (e) {
                console.error('Error loading sessions:', e);
              }
            }
          }}
        />
      );
    }
    
    // Normale Login-Seite
    return (
      <div className="app">
        <div className="login-screen">
          <div className="login-panel">
            <div className="login-header">
              <div className="logo">⏱️</div>
              <h1>ZEITAUF-MOBIL {deviceName && `- ${deviceName}`}</h1>
            </div>
            <div className="login-form">
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="benutzer@example.com"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Backend URL:</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="url"
                    value={backendUrl}
                    onChange={(e) => {
                      setBackendUrl(e.target.value);
                      setLoginBackendUrlTestResult(null);
                    }}
                    placeholder="https://backend.example.com"
                    className="form-input"
                    style={{ flex: '1 1 200px', minWidth: 0 }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={loginBackendUrlTestLoading || !backendUrl?.trim()}
                    onClick={async () => {
                      setLoginBackendUrlTestLoading(true);
                      setLoginBackendUrlTestResult(null);
                      try {
                        const normalized = normalizeBackendUrl(backendUrl);
                        const result = await checkBackendHealth(normalized);
                        if (result.healthy === true) {
                          const detail = [result.endpoint, result.status != null ? `HTTP ${result.status}` : null]
                            .filter(Boolean)
                            .join(' — ');
                          setLoginBackendUrlTestResult({
                            ok: true,
                            text: detail ? `Erreichbar (${detail})` : 'Erreichbar',
                          });
                        } else {
                          setLoginBackendUrlTestResult({
                            ok: false,
                            text: result.error || 'Nicht erreichbar',
                          });
                        }
                      } catch (err) {
                        setLoginBackendUrlTestResult({
                          ok: false,
                          text: err?.message || String(err),
                        });
                      } finally {
                        setLoginBackendUrlTestLoading(false);
                      }
                    }}
                  >
                    {loginBackendUrlTestLoading ? '…' : 'URL testen'}
                  </button>
                </div>
                {loginBackendUrlTestResult && (
                  <div
                    role="status"
                    style={{
                      marginTop: '8px',
                      fontSize: '0.9rem',
                      color: loginBackendUrlTestResult.ok ? '#15803d' : '#b91c1c',
                    }}
                  >
                    {loginBackendUrlTestResult.ok ? '✓ ' : '✗ '}
                    {loginBackendUrlTestResult.text}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Passwort:</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort (für Sessions vom Server)"
                  className="form-input"
                  autoComplete="current-password"
                />
              </div>
              <button className="btn btn-primary btn-login" onClick={handleLogin}>
                Einloggen
              </button>
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => setShowRegister(true)}
                  style={{ fontSize: '0.9rem', padding: '8px 16px' }}
                >
                  Noch kein Konto? Registrieren
                </button>
              </div>
            </div>
            <div className="login-footer">
              <div className="login-footer-content">
                <span className="product-name">{getFooterText(t).productName}</span>
                <span className="version">{getFooterText(t).version}</span>
                <span className="footer-separator">|</span>
                <span className="copyright">{getFooterText(t).copyright}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug-Panel (immer sichtbar, wenn aktiviert)
  // eslint-disable-next-line no-unused-vars
  const debugPanel = showDebugPanel && (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: '#1a1a1a',
      color: '#fff',
      padding: '10px',
      maxHeight: '300px',
      overflowY: 'auto',
      fontSize: '11px',
      zIndex: 10000,
      borderTop: '2px solid #dc3545',
      boxShadow: '0 -2px 10px rgba(0,0,0,0.3)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0, color: '#dc3545' }}>🐛 Debug-Logs (letzte 50)</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => {
              const savedLogs = JSON.parse(localStorage.getItem('stoppuhr_debug_logs') || '[]');
              setDebugLogs(savedLogs.slice(-50));
            }}
            style={{ padding: '5px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
          >
            🔄 Aktualisieren
          </button>
          <button 
            onClick={() => {
              localStorage.removeItem('stoppuhr_debug_logs');
              setDebugLogs([]);
            }}
            style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
          >
            🗑️ Löschen
          </button>
          <button 
            onClick={() => setShowDebugPanel(false)}
            style={{ padding: '5px 10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
          >
            ✕ Schließen
          </button>
        </div>
      </div>
      <div style={{ fontFamily: 'monospace' }}>
        {debugLogs.length === 0 ? (
          <div style={{ color: '#888' }}>Keine Logs vorhanden. Warte auf Logs...</div>
        ) : (
          debugLogs.slice().reverse().map((log, idx) => (
            <div key={idx} style={{ 
              marginBottom: '5px', 
              padding: '5px', 
              backgroundColor: idx % 2 === 0 ? '#2a2a2a' : '#1a1a1a',
              borderLeft: `3px solid ${log.hypothesisId === 'A' ? '#3b82f6' : log.hypothesisId === 'B' ? '#10b981' : log.hypothesisId === 'C' ? '#f59e0b' : log.hypothesisId === 'D' ? '#ef4444' : '#8b5cf6'}`
            }}>
              <div style={{ color: '#888', fontSize: '10px' }}>{log.time} - {log.location}</div>
              <div style={{ color: '#fff', fontWeight: 'bold' }}>{log.message}</div>
              {log.data && Object.keys(log.data).length > 0 && (
                <div style={{ color: '#aaa', marginTop: '3px', fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(log.data, null, 2).substring(0, 300)}
                  {JSON.stringify(log.data, null, 2).length > 300 ? '...' : ''}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  // Session Selection Screen
  // WICHTIG: Session-Liste nicht anzeigen, wenn Update-Resume-Check läuft oder Dialog angezeigt wird
  if (!selectedSession && !isCheckingUpdateResume && !showUpdateResumeDialog) {
    return (
      <div className="app">
        <div className="session-screen">
          <div className="session-header">
            <h1>Messungen</h1>
            <div className="session-header-buttons">
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  setShowConfig(true);
                  setConfigTab('license'); // Öffne direkt den Lizenz-Tab
                }}
                title="Globale Einstellungen - Lizenzen, AblaufDB, etc."
              >
                ⚙️ {t('common.config')}
              </button>
              {process.env.NODE_ENV !== 'production' && getEnableDebugLogs() && (
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowDebugPanel(!showDebugPanel)}
                  title="Debug-Logs anzeigen/verstecken"
                  style={{ backgroundColor: showDebugPanel ? '#dc3545' : '#6c757d', color: 'white' }}
                >
                  🐛 Debug {showDebugPanel ? '(ON)' : ''}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => {
                localStorage.removeItem('userToken');
                localStorage.removeItem(STORAGE_KEY_USER_ID);
                setUserId(null);
                setIsLoggedIn(false);
              }}>
                Abmelden
              </button>
            </div>
          </div>
          <div className="sessions-list">
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
              <button 
                className="btn btn-primary btn-new-session" 
                onClick={handleNewSession}
                title={t('common.new_measurement_title')}
              >
                {t('common.new_measurement')}
              </button>
              {process.env.NODE_ENV !== 'production' && getEnableDebugLogs() && (
                <button 
                  className="btn btn-secondary" 
                  onClick={async () => {
                    if (window.confirm(t('alerts.confirm.clear_states'))) {
                      try {
                        // Lösche alle relevanten localStorage-Einträge
                        localStorage.removeItem('stoppuhr_update_resume_state');
                        // Lösche aus IndexedDB
                        await deleteMeasurementState('update_resume_state');
                        // Setze alle relevanten Refs zurück
                        updateResumeCheckInProgressRef.current = false;
                        updateResumeStateDeletedRef.current = false;
                        updateResumeDialogShownRef.current = false;
                        updateResumeCheckExecutedRef.current = false;
                        updateResumeCancelInProgressRef.current = false;
                        // Setze States zurück
                        setIsCheckingUpdateResume(false);
                        setShowUpdateResumeDialog(false);
                        setUpdateResumeDialogData(null);
                        // Lade Seite neu
                        window.location.reload();
                      } catch (error) {
                        console.error('[Reset] Fehler beim Zurücksetzen:', error);
                        alert(t('alerts.reset_error'));
                      }
                    }
                  }}
                  title={t('common.reset_state_title')}
                  style={{ backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545' }}
                >
                  {t('common.reset_state')}
                </button>
              )}
              <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className={`btn session-tab-lokal ${sessionListTab === 'lokal' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSessionListTab('lokal')}
                  aria-pressed={sessionListTab === 'lokal'}
                  title={t('session.lokal_title')}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5zm4 10h8v2H8v-2zm0-4h8v2H8v-2z" />
                  </svg>
                  {t('session.lokal')}
                </button>
                <button
                  type="button"
                  className={`btn session-tab-cloud ${sessionListTab === 'backend' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => { setSessionListTab('backend'); if (userId && localStorage.getItem('userToken')) fetchBackendSessions(); }}
                  aria-pressed={sessionListTab === 'backend'}
                  title={t('session.cloud_title')}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  {t('session.cloud')}
                </button>
              </div>
            </div>

            {sessionListTab === 'lokal' && (
              <div className="session-tab-content-lokal">
                {sessions.length > 0 && (
                  <div className="sessions">
                    <h2>{t('session.previous_measurements')}</h2>
                    {sessions.map(session => {
                      let displayDate = null;
                      let displayDateLabel = '';
                      if (session.lastUpdated) {
                        displayDate = new Date(session.lastUpdated);
                        displayDateLabel = t('session.changed');
                      } else if (session.measurements && session.measurements.length > 0) {
                        const lastMeasurement = session.measurements.reduce((latest, current) => {
                          const currentTime = current.timestamp ? new Date(current.timestamp).getTime() : 0;
                          const latestTime = latest.timestamp ? new Date(latest.timestamp).getTime() : 0;
                          return currentTime > latestTime ? current : latest;
                        });
                        if (lastMeasurement.timestamp) {
                          displayDate = new Date(lastMeasurement.timestamp);
                          displayDateLabel = t('session.last_measurement');
                        }
                      }
                      if (!displayDate && session.createdAt) {
                        displayDate = new Date(session.createdAt);
                        displayDateLabel = t('session.created');
                      }
                      return (
                        <div key={session.id} className="session-item">
                          <div className="session-info">
                            <div className="session-title">
                              {session.sessionName || `Messung vom ${new Date(session.createdAt).toLocaleString('de-DE')}`}
                              {session.isDemo && <span className="demo-badge-small">DEMO</span>}
                            </div>
                            <div className="session-details">
                              <span>WA-Nr: {session.resumeNumber}</span>
                              <span>Messpunkte: {session.measurements?.length || 0}</span>
                              {session.isDemo && <span className="demo-limit">(Max: 20)</span>}
                              {session.uploaded && <span className="uploaded">{t('session.uploaded')}</span>}
                              {displayDate && (
                                <span className="session-date" title={`Session-ID: ${session.id}`}>
                                  {displayDateLabel}: {displayDate.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                          </div>
                          <button className="btn btn-resume" onClick={() => handleResumeSession(session)}>
                            {t('session.continue')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {sessionListTab === 'backend' && (
              <div className="session-tab-content-backend">
                {!userId || !localStorage.getItem('userToken') ? (
                  <p className="session-backend-hint">Bitte anmelden (E-Mail und Passwort), um Sessions vom Server zu laden.</p>
                ) : (
                  <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
                      <input
                        type="text"
                        value={backendSearch}
                        onChange={(e) => setBackendSearch(e.target.value)}
                        placeholder="Volltextsuche (mehrere Begriffe mit Leerzeichen)"
                        className="form-input"
                        style={{ minWidth: '200px', flex: '1' }}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                        <input
                          type="checkbox"
                          checked={backendOwnOnly}
                          onChange={(e) => setBackendOwnOnly(e.target.checked)}
                        />
                        Nur eigene
                      </label>
                      <button type="button" className="btn btn-primary" onClick={() => { setBackendSessionsPage(1); fetchBackendSessions(1); }} disabled={backendSessionsLoading}>
                        Suchen
                      </button>
                      {backendSessionsLoading && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>Lade…</span>
                          <button type="button" className="btn btn-secondary" onClick={cancelBackendSessionsFetch}>Abbruch</button>
                        </span>
                      )}
                    </div>
                    {backendResumeLoading && (
                      <div style={{ marginBottom: '12px', padding: '12px', background: 'var(--bg-secondary, #f0f0f0)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span>Session wird geladen…</span>
                        <button type="button" className="btn btn-secondary" onClick={cancelBackendResume}>Abbruch</button>
                      </div>
                    )}
                    {backendSessions.length > 0 ? (
                      <div className="sessions">
                        <h2 style={{ margin: '0 0 12px 0' }}>Sessions vom Server</h2>
                        {(() => {
                          const totalPages = Math.max(1, Math.ceil(backendSessionsTotal / BACKEND_SESSIONS_PAGE_SIZE));
                          const canPrev = backendSessionsPage > 1;
                          const canNext = backendSessionsPage < totalPages && backendSessionsTotal > 0;
                          return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                              <button type="button" className="btn btn-secondary" onClick={() => fetchBackendSessions(1)} disabled={!canPrev || backendSessionsLoading} title={t('common.pagination_first')} aria-label={t('common.pagination_first')}>&#171;</button>
                              <button type="button" className="btn btn-secondary" onClick={() => fetchBackendSessions(backendSessionsPage - 1)} disabled={!canPrev || backendSessionsLoading} title={t('common.pagination_prev')} aria-label={t('common.pagination_prev')}>&#8249;</button>
                              <span style={{ textAlign: 'center', fontSize: '0.95rem' }}>Seite {backendSessionsPage} von {totalPages}</span>
                              <button type="button" className="btn btn-secondary" onClick={() => fetchBackendSessions(backendSessionsPage + 1)} disabled={!canNext || backendSessionsLoading} title={t('common.pagination_next')} aria-label={t('common.pagination_next')}>&#8250;</button>
                              <button type="button" className="btn btn-secondary" onClick={() => fetchBackendSessions(totalPages)} disabled={!canNext || backendSessionsLoading} title={t('common.pagination_last')} aria-label={t('common.pagination_last')}>&#187;</button>
                            </div>
                          );
                        })()}
                        {backendSessions.map((s) => {
                          const displayDate = s.updated_at ? new Date(s.updated_at) : (s.created_at ? new Date(s.created_at) : null);
                          return (
                            <div key={s.id} className="session-item">
                              <div className="session-info">
                                <div className="session-title">
                                  {s.session_name || `Messung ${s.created_at || s.id}`}
                                  <span className="uploaded" style={{ marginLeft: '8px' }}>Vom Server</span>
                                </div>
                                <div className="session-details">
                                  <span>WA-Nr: {s.resume_number}</span>
                                  <span>Messpunkte: {s.measurement_count ?? 0}</span>
                                  {displayDate && (
                                    <span className="session-date" title={`Session-ID: ${s.id}`}>
                                      Geändert: {displayDate.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button className="btn btn-resume" onClick={() => handleResumeBackendSession(s)} disabled={backendResumeLoading}>
                                Fortsetzen
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : !backendSessionsLoading && (
                      <p className="session-backend-hint">Keine Sessions gefunden. Suche anpassen oder „Suchen“ klicken.</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Config-Overlay auch ohne aktive Messung verfügbar */}
        {showConfig && (
          <div className="config-overlay" onClick={handleCloseConfig} style={{ position: 'fixed', zIndex: 10000 }}>
            <div className="config-panel" onClick={(e) => e.stopPropagation()}>
            <div className="config-header">
              <h3>⚙️ {t('common.config')} <span style={{ fontSize: '0.6em', fontWeight: 'normal', opacity: 0.7 }}>({getVersionString()})</span></h3>
              <button className="btn-close" onClick={handleCloseConfig}>×</button>
            </div>
              {/* Config-Content wird vom bestehenden Config-Overlay im Main App verwendet */}
              {/* Für Session Selection Screen: Verwende das gleiche Config-Overlay wie im Main App */}
              {/* Das vollständige Config-Overlay wird weiter unten im Main App gerendert */}
              {/* Hier zeigen wir nur eine Nachricht, dass Config verfügbar ist */}
              <div className="config-tabs-wrapper">
                <div className="config-tabs">
                  <button
                    className={`config-tab ${configTab === 'license' ? 'active' : ''}`}
                    onClick={() => setConfigTab('license')}
                  >
                    {t('config.tabs.license')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'ablaufdb' ? 'active' : ''}`}
                    onClick={() => setConfigTab('ablaufdb')}
                  >
                    {t('config.tabs.ablaufdb')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'einflussdb' ? 'active' : ''}`}
                    onClick={() => setConfigTab('einflussdb')}
                  >
                    {t('config.tabs.einflussdb')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'dependentlists' ? 'active' : ''}`}
                    onClick={() => setConfigTab('dependentlists')}
                  >
                    {t('config.tabs.dependentlists')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'datasources' ? 'active' : ''}`}
                    onClick={() => setConfigTab('datasources')}
                  >
                    {t('config.tabs.datasources')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'view' ? 'active' : ''}`}
                    onClick={() => setConfigTab('view')}
                  >
                    {t('config.tabs.view')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'kompakt' ? 'active' : ''}`}
                    onClick={() => setConfigTab('kompakt')}
                  >
                    {t('config.tabs.kompakt')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'sonstiges' ? 'active' : ''}`}
                    onClick={() => setConfigTab('sonstiges')}
                  >
                    {t('config.tabs.sonstiges')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'keyboard' ? 'active' : ''}`}
                    onClick={() => setConfigTab('keyboard')}
                  >
                    {t('config.tabs.keyboard')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'timetypes' ? 'active' : ''}`}
                    onClick={() => setConfigTab('timetypes')}
                  >
                    {t('config.tabs.timetypes')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'help' ? 'active' : ''}`}
                    onClick={() => setConfigTab('help')}
                  >
                    {t('config.tabs.help')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'feedback' ? 'active' : ''}`}
                    onClick={() => setConfigTab('feedback')}
                  >
                    {t('config.tabs.feedback')}
                  </button>
                  <button
                    className={`config-tab ${configTab === 'about' ? 'active' : ''}`}
                    onClick={() => setConfigTab('about')}
                  >
                    {t('config.tabs.about')}
                  </button>
                </div>
              </div>
              <div className="config-content">
                {/* Vollständiger Config-Content - gleiche Logik wie im Main App */}
                {configTab === 'license' && (
                  <div className="config-tab-content">
                    <div className="config-item">
                      <label>Lizenz-Status:</label>
                      <div className="license-status">
                        {licenseInfo && licenseInfo.license_key ? (
                          licenseInfo.is_active ? (
                            <span className="license-valid">✅ Lizenz aktiv</span>
                          ) : (
                            <span className="license-invalid">❌ Lizenz deaktiviert</span>
                          )
                        ) : (
                          <>
                            {licenseStatus === null && <span className="license-unknown">Nicht geprüft</span>}
                            {licenseStatus === false && <span className="license-invalid">❌ Keine Lizenz</span>}
                            {licenseStatus === true && <span className="license-valid">✅ Lizenz aktiv</span>}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="config-item">
                      <button
                        className="btn btn-primary license-action-btn"
                        onClick={checkLicense}
                        disabled={isCheckingLicense || !email || !backendUrl}
                        title="Lizenz prüfen - Prüft den Status der Lizenz im Backend. Erfordert Email und Backend-URL"
                      >
                        {isCheckingLicense ? 'Prüfe...' : '🔍 Prüfen'}
                      </button>
                    </div>
                    <div className="config-item license-buttons-right">
                      <button
                        className="btn btn-import-license license-action-btn"
                        onClick={() => licenseFileInputRef.current?.click()}
                        disabled={isImportingLicense || !backendUrl}
                        title="Lizenz importieren - Importiert eine .license Datei vom Hersteller. Erfordert Backend-URL"
                      >
                        {isImportingLicense ? 'Importiere...' : '📥 Importieren'}
                      </button>
                      {licenseStatus === true && (
                        <button
                          className="btn btn-danger license-action-btn"
                          onClick={handleRemoveLicense}
                          title="Lizenz entfernen - Entfernt die aktuelle Lizenz. Die Anwendung läuft danach im Demo-Modus"
                        >
                          🗑️ Entfernen
                        </button>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={licenseFileInputRef}
                      onChange={handleLicenseFileChange}
                      accept=".license"
                      style={{ display: 'none' }}
                    />
                    {licenseInfo && (
                      <div className={`license-info-box ${licenseInfo.valid && licenseInfo.is_active ? 'license-valid-box' : 'license-invalid-box'}`} style={{ marginTop: '12px', padding: '12px', backgroundColor: licenseInfo.valid && licenseInfo.is_active ? '#d1fae5' : '#fee2e2', borderRadius: '8px', fontSize: '0.9rem', border: `2px solid ${licenseInfo.valid && licenseInfo.is_active ? '#10b981' : '#ef4444'}` }}>
                        {licenseInfo.license_key && (
                          <div className="license-key-section" style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <strong className="license-key-label">Lizenz-Key:</strong>
                              <button
                                id="copy-license-key-btn"
                                onClick={handleCopyLicenseKey}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '0.85rem',
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: '500',
                                  transition: 'background-color 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                onMouseEnter={(e) => {
                                  if (e.target.style.backgroundColor !== '#10b981') {
                                    e.target.style.backgroundColor = '#2563eb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (e.target.style.backgroundColor !== '#10b981') {
                                    e.target.style.backgroundColor = '#3b82f6';
                                  }
                                }}
                              >
                                📋 Kopieren
                              </button>
                            </div>
                            <code className="license-key-code" style={{ 
                              fontSize: '1.2rem', 
                              backgroundColor: '#ffffff', 
                              padding: '8px 10px', 
                              borderRadius: '6px',
                              display: 'block',
                              wordBreak: 'break-all',
                              fontFamily: 'monospace',
                              color: '#1f2937',
                              border: '1px solid rgba(0,0,0,0.1)',
                              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                              fontWeight: 'bold',
                              textAlign: 'center'
                            }}>
                              {licenseInfo.license_key}
                            </code>
                          </div>
                        )}
                        <div className="license-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                          <div><strong className="license-detail-label">Produktcode:</strong> <span className="license-detail-value">{licenseInfo.product_code || '-'}</span></div>
                          <div><strong className="license-detail-label">Typ:</strong> <span className="license-detail-value">{licenseInfo.license_type || '-'}</span></div>
                        </div>
                        {licenseInfo.expires_at && (
                          <div className="license-detail-row" style={{ marginBottom: '8px' }}>
                            <strong className="license-detail-label">Läuft ab:</strong> <span className="license-detail-value">{new Date(licenseInfo.expires_at).toLocaleDateString('de-DE', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</span>
                          </div>
                        )}
                        <div className="license-detail-row" style={{ marginBottom: '8px' }}>
                          <strong className="license-detail-label">Status:</strong> <span className={`license-status-badge ${licenseInfo.is_active ? 'license-active' : 'license-inactive'}`} style={{ 
                            color: licenseInfo.is_active ? '#10b981' : '#ef4444',
                            fontWeight: '600'
                          }}>{licenseInfo.is_active ? '✅ Aktiv' : '❌ Inaktiv'}</span>
                        </div>
                        {licenseInfo.message && (
                          <div className="license-message" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.1)', fontStyle: 'italic', color: '#6b7280' }}>
                            {licenseInfo.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {configTab === 'ablaufdb' && (
                  <div className="config-tab-content">
                    <div className="ablaufdb-config">
                      <h4>{t('config.ablaufdb.section_title')}</h4>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                        {t('config.ablaufdb.section_desc')}
                      </p>
                      <div className="config-item">
                        <label>{t('config.ablaufdb.name_label')}</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={ablaufDbName}
                            onChange={(e) => {
                              setAblaufDbName(e.target.value);
                              localStorage.setItem('stoppuhr_ablaufdb_name', e.target.value);
                            }}
                            className="form-input"
                            placeholder={t('config.ablaufdb.name_placeholder')}
                            style={{ flex: 1 }}
                          />
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                          {t('config.ablaufdb.name_desc')}
                        </p>
                      </div>
                      <div className="config-item">
                        <label>{t('config.ablaufdb.path_label')}</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {ablaufDbPath && ablaufDbPath.startsWith('[Lokale Datei:') ? (
                              <input
                                type="text"
                                value=""
                                onChange={(e) => {
                                  setAblaufDbPath(e.target.value);
                                  localStorage.setItem('stoppuhr_ablaufdb_path', e.target.value);
                                }}
                                className="form-input"
                                placeholder={t('config.ablaufdb.path_placeholder_enter')}
                                style={{ flex: 1 }}
                              />
                            ) : (
                              <input
                                type="text"
                                value={ablaufDbPath || ''}
                                onChange={(e) => {
                                  setAblaufDbPath(e.target.value);
                                  localStorage.setItem('stoppuhr_ablaufdb_path', e.target.value);
                                }}
                                className="form-input"
                                placeholder={t('config.ablaufdb.path_placeholder')}
                                style={{ flex: 1 }}
                              />
                            )}
                            <button
                              className="btn btn-secondary"
                              onClick={() => ablaufDbFileInputRef.current?.click()}
                            >
                              {t('config.ablaufdb.button_select')}
                            </button>
                            <input
                              type="file"
                              ref={ablaufDbFileInputRef}
                              onChange={handleAblaufDbFileSelect}
                              accept=".xml"
                              style={{ display: 'none' }}
                            />
                          </div>
                          {ablaufDbPath && ablaufDbPath.startsWith('[Lokale Datei:') && (
                            <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontStyle: 'italic', padding: '8px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                              ⚠️ {t('config.ablaufdb.path_local_hint', { path: ablaufDbPath })}
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                          {t('config.ablaufdb.path_desc')}
                        </p>
                      </div>
                      {ablaufDbData && ablaufDbData.length > 0 && (
                        <div className="config-item">
                          <label>{t('config.ablaufdb.status_label')}</label>
                          <div className="ablaufdb-status-success" style={{ padding: '12px', background: '#d1fae5', borderRadius: '6px', border: '1px solid #10b981' }}>
                            <strong>{t('config.ablaufdb.status_loaded')}</strong><br/>
                            <span className="ablaufdb-kuerzel-count" style={{ fontSize: '0.9rem', color: '#065f46' }}>
                              {t('config.ablaufdb.status_kuerzel_available', { count: ablaufDbData.length })}
                            </span>
                          </div>
                        </div>
                      )}
                      {(!ablaufDbData || ablaufDbData.length === 0) && (
                        <div className="config-item">
                          <label>{t('config.ablaufdb.status_label')}</label>
                          <div className="ablaufdb-status-error" style={{ padding: '12px', background: '#fee2e2', borderRadius: '6px', border: '1px solid #ef4444' }}>
                            <strong>{t('config.ablaufdb.status_not_loaded')}</strong><br/>
                            <span style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                              {t('config.ablaufdb.status_please_load')}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="form-actions" style={{ marginTop: '20px' }}>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            const result = await loadAblaufDbXml();
                            if (result.success) {
                              alert(t('alerts.ablaufdb_loaded', { count: result.data.length }));
                            } else {
                              alert(t('alerts.ablaufdb_error', { msg: result.error }));
                            }
                          }}
                          title={t('config.ablaufdb.button_load_title')}
                        >
                          {t('config.ablaufdb.button_load')}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowAblaufDbEditor(true);
                          }}
                          disabled={!ablaufDbData || ablaufDbData.length === 0}
                          title={t('config.ablaufdb.button_edit_title')}
                        >
                          {t('config.ablaufdb.button_edit')}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handleAblaufDbUnload}
                          title={t('ablaufdb.without_db_title')}
                        >
                          {t('config.ablaufdb.button_without_db')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {configTab === 'einflussdb' && (
                  <div className="config-tab-content">
                    <div className="ablaufdb-config">
                      <h4>{t('config.einflussdb.section_title')}</h4>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                        {t('config.einflussdb.section_desc')}
                      </p>
                      <div className="config-item">
                        <label>{t('config.einflussdb.name_label')}</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={einflussDbName}
                            onChange={(e) => {
                              setEinflussDbName(e.target.value);
                              localStorage.setItem('stoppuhr_einflussdb_name', e.target.value);
                            }}
                            className="form-input"
                            placeholder={t('config.einflussdb.name_placeholder')}
                            style={{ flex: 1 }}
                          />
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                          {t('config.einflussdb.name_desc')}
                        </p>
                      </div>
                      <div className="config-item">
                        <label>{t('config.einflussdb.path_label')}</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {einflussDbPath && einflussDbPath.startsWith('[Lokale Datei:') ? (
                              <input
                                type="text"
                                value=""
                                onChange={(e) => {
                                  setEinflussDbPath(e.target.value);
                                  localStorage.setItem('stoppuhr_einflussdb_path', e.target.value);
                                }}
                                className="form-input"
                                placeholder={t('config.einflussdb.path_placeholder_enter')}
                                style={{ flex: 1 }}
                              />
                            ) : (
                              <input
                                type="text"
                                value={einflussDbPath || ''}
                                onChange={(e) => {
                                  setEinflussDbPath(e.target.value);
                                  localStorage.setItem('stoppuhr_einflussdb_path', e.target.value);
                                }}
                                className="form-input"
                                placeholder={t('config.einflussdb.path_placeholder')}
                                style={{ flex: 1 }}
                              />
                            )}
                            <button
                              className="btn btn-secondary"
                              onClick={() => einflussDbFileInputRef.current?.click()}
                            >
                              {t('config.einflussdb.button_select')}
                            </button>
                            <input
                              type="file"
                              ref={einflussDbFileInputRef}
                              onChange={handleEinflussDbFileSelect}
                              accept=".xml"
                              style={{ display: 'none' }}
                            />
                          </div>
                          {einflussDbPath && einflussDbPath.startsWith('[Lokale Datei:') && (
                            <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontStyle: 'italic', padding: '8px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                              ⚠️ {t('config.einflussdb.path_local_hint', { path: einflussDbPath })}
                            </div>
                          )}
                        </div>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                          {t('config.einflussdb.path_desc')}
                        </p>
                      </div>
                      {einflussDbData && einflussDbData.length > 0 && (
                        <div className="config-item">
                          <label>{t('config.einflussdb.status_label')}</label>
                          <div className="ablaufdb-status-success" style={{ padding: '12px', background: '#d1fae5', borderRadius: '6px', border: '1px solid #10b981' }}>
                            <strong>{t('config.einflussdb.status_loaded')}</strong><br/>
                            <span className="ablaufdb-kuerzel-count" style={{ fontSize: '0.9rem', color: '#065f46' }}>
                              {t('config.einflussdb.status_einfluss_available', { count: einflussDbData.length })}
                            </span>
                          </div>
                        </div>
                      )}
                      {(!einflussDbData || einflussDbData.length === 0) && (
                        <div className="config-item">
                          <label>{t('config.einflussdb.status_label')}</label>
                          <div className="ablaufdb-status-error" style={{ padding: '12px', background: '#fee2e2', borderRadius: '6px', border: '1px solid #ef4444' }}>
                            <strong>{t('config.einflussdb.status_not_loaded')}</strong><br/>
                            <span style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                              {t('config.einflussdb.status_please_load')}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="form-actions" style={{ marginTop: '20px' }}>
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            const result = await loadEinflussDbXml();
                            if (result.success) {
                              alert(t('alerts.einflussdb_loaded', { count: result.data.length }));
                            } else {
                              alert(t('alerts.einflussdb_error', { msg: result.error }));
                            }
                          }}
                          title={t('config.einflussdb.button_load_title')}
                        >
                          {t('config.einflussdb.button_load')}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={openEinflussDbEditor}
                          disabled={!einflussDbData || einflussDbData.length === 0}
                          title={t('config.einflussdb.button_edit_title')}
                        >
                          {t('config.einflussdb.button_edit')}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handleEinflussDbUnload}
                          title={t('config.einflussdb.button_without_db_title')}
                        >
                          {t('config.einflussdb.button_without_db')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {configTab === 'dependentlists' && (
                  <div className="config-tab-content">
                    <div className="ablaufdb-config">
                      <h4>{t('config.dependentlists.section_title')}</h4>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                        {t('config.dependentlists.section_desc')}
                      </p>

                      {activeDependentSheet && (
                        <>
                          <div className="config-item">
                            <label>{t('config.dependentlists.sheet_name_label')}</label>
                            <input
                              type="text"
                              className="form-input"
                              value={activeDependentSheet.name || ''}
                              onChange={(e) => handleRenameDependentSheet(activeDependentSheet.id, e.target.value)}
                              placeholder={t('config.dependentlists.sheet_name_placeholder')}
                            />
                          </div>
                          {renderDependentListsGrid()}
                          {dependentListsError && (
                            <div style={{ padding: '10px 12px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b', border: '1px solid #ef4444' }}>
                              {dependentListsError}
                            </div>
                          )}
                          {renderDependentSheetBar()}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {configTab === 'datasources' && renderDataSourcesHub()}
                {configTab === 'timetypes' && (
                  <div className="config-tab-content">
                    <div className="timetypes-config">
                      <div className="timetypes-header">
                        <h4>{t('config.timetypes.section_title')}</h4>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setEditingTimeType(null);
                            setNewTimeTypeName('');
                            setNewTimeTypeDescription('');
                            setNewTimeTypeColor('#3b82f6');
                          }}
                          title="Erstellt eine neue Zeitart mit Name, Beschreibung und Farbe"
                        >
                          ➕ Neue Zeitart
                        </button>
                      </div>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                        Verwalten Sie Zeitarten für die Kategorisierung von Messpunkten. Jede Zeitart hat einen Namen (max. 5 Zeichen), eine Beschreibung und eine Farbe.
                      </p>
                      
                      {editingTimeType === null && (
                        <div className="timetypes-list">
                          {timeTypes.map((type, index) => (
                            <div key={index} className="timetype-item">
                              <div className="timetype-preview" style={{ backgroundColor: type.color }}>
                                <span className="timetype-name">{type.name}</span>
                              </div>
                              <div className="timetype-info">
                                <div className="timetype-name-display">{type.name}</div>
                                <div className="timetype-description">{type.description}</div>
                              </div>
                              <div className="timetype-actions">
                                <button
                                  className="btn-icon-small"
                                  onClick={() => {
                                    setEditingTimeType(index);
                                    setNewTimeTypeName(type.name);
                                    setNewTimeTypeDescription(type.description);
                                    setNewTimeTypeColor(type.color);
                                  }}
                                  title="Bearbeiten"
                                >
                                  ✏️
                                </button>
                                <button
                                  className="btn-icon-small btn-danger"
                                  onClick={() => {
                                    if (window.confirm(t('alerts.confirm.timetype_delete', { name: type.name }))) {
                                      const updated = timeTypes.filter((_, i) => i !== index);
                                      setTimeTypes(updated);
                                      localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                    }
                                  }}
                                  title="Löschen"
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(editingTimeType !== null || (editingTimeType === null && newTimeTypeName)) && (
                        <div className="timetype-edit-form">
                          <h5>{editingTimeType !== null ? 'Zeitart bearbeiten' : 'Neue Zeitart hinzufügen'}</h5>
                          <div className="form-group">
                            <label>Name (max. 5 Zeichen):</label>
                            <input
                              type="text"
                              value={newTimeTypeName}
                              onChange={(e) => {
                                const value = e.target.value.slice(0, 5);
                                setNewTimeTypeName(value);
                              }}
                              maxLength={5}
                              className="form-input"
                              placeholder="z.B. thb"
                            />
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                              Kurzer Name für die Zeitart (maximal 5 Zeichen). Wird in der Zeitart-Auswahl angezeigt.
                            </p>
                          </div>
                          <div className="form-group">
                            <label>Beschreibung:</label>
                            <input
                              type="text"
                              value={newTimeTypeDescription}
                              onChange={(e) => setNewTimeTypeDescription(e.target.value)}
                              className="form-input"
                              placeholder="z.B. beeinflussbare Hauptzeit"
                            />
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                              Vollständige Beschreibung der Zeitart. Wird in der Zeitart-Auswahl als Tooltip angezeigt.
                            </p>
                          </div>
                          <div className="form-group">
                            <label>Farbe:</label>
                            <div className="color-picker-group">
                              <input
                                type="color"
                                value={newTimeTypeColor}
                                onChange={(e) => setNewTimeTypeColor(e.target.value)}
                                className="color-picker"
                              />
                              <input
                                type="text"
                                value={newTimeTypeColor}
                                onChange={(e) => setNewTimeTypeColor(e.target.value)}
                                className="color-input"
                                placeholder="#3b82f6"
                              />
                              <div className="color-preview" style={{ backgroundColor: newTimeTypeColor }}>
                                {newTimeTypeName || 'A'}
                              </div>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                              {t('config.timetypes.color_desc')}
                            </p>
                          </div>
                          <div className="form-actions">
                            <button
                              className="btn btn-primary"
                              onClick={() => {
                                if (!newTimeTypeName.trim()) {
                                  alert(t('alerts.timetype_name_required'));
                                  return;
                                }
                                if (editingTimeType !== null) {
                                  // Bearbeiten
                                  const updated = [...timeTypes];
                                  updated[editingTimeType] = {
                                    name: newTimeTypeName.trim(),
                                    description: newTimeTypeDescription.trim(),
                                    color: newTimeTypeColor
                                  };
                                  setTimeTypes(updated);
                                  localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                  setEditingTimeType(null);
                                } else {
                                  // Hinzufügen
                                  const updated = [...timeTypes, {
                                    name: newTimeTypeName.trim(),
                                    description: newTimeTypeDescription.trim(),
                                    color: newTimeTypeColor
                                  }];
                                  setTimeTypes(updated);
                                  localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                  setNewTimeTypeName('');
                                  setNewTimeTypeDescription('');
                                  setNewTimeTypeColor('#3b82f6');
                                }
                              }}
                              title={editingTimeType !== null ? 'Speichert die Änderungen an der Zeitart' : 'Fügt eine neue Zeitart zur Liste hinzu'}
                            >
                              {editingTimeType !== null ? t('config.timetypes.save') : t('config.timetypes.add')}
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setEditingTimeType(null);
                                setNewTimeTypeName('');
                                setNewTimeTypeDescription('');
                                setNewTimeTypeColor('#3b82f6');
                              }}
                              title="Bricht die Bearbeitung ab und verwirft alle Änderungen"
                            >
                              Abbrechen
                            </button>
                            {editingTimeType !== null && (
                              <button
                                className="btn btn-secondary"
                                onClick={() => {
                                  const updated = [...timeTypes];
                                  updated[editingTimeType] = defaultTimeTypes.find(dt => dt.name === timeTypes[editingTimeType].name) || timeTypes[editingTimeType];
                                  setTimeTypes(updated);
                                  localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                  setEditingTimeType(null);
                                }}
                                title="Auf Standard zurücksetzen"
                              >
                                🔄 Standard
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="timetypes-actions">
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            if (window.confirm(t('alerts.confirm.timetypes_reset'))) {
                              setTimeTypes(defaultTimeTypes);
                              localStorage.setItem('stoppuhr_timetypes', JSON.stringify(defaultTimeTypes));
                              setEditingTimeType(null);
                            }
                          }}
                        >
                          🔄 Alle auf Standard zurücksetzen
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {configTab === 'feedback' && (
                  <div className="config-tab-content">
                    <div className="config-item">
                      <h4>{t('config.feedback.title')}</h4>
                      <p>{t('config.feedback.intro')}</p>
                      <p>{t('config.feedback.draft_hint')}</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowFeedbackDialog(true)}
                        disabled={!backendUrl || !email}
                      >
                        {t('config.feedback.new_button')}
                      </button>
                    </div>
                    {backendUrl && email && (
                      <div className="config-item">
                        <h4>{t('config.feedback.my_title')}</h4>
                        <p>{t('config.feedback.my_desc')}</p>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setShowFeedbackStatusNotification(true)}
                        >
                          {t('config.feedback.show_status')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {configTab === 'help' && (
                  <div className="config-tab-content">
                    <div className="help-content">
                      <h4>{t('help.title')}</h4>
                      <p style={{ marginBottom: '20px', color: '#6b7280' }}>
                        {t('help.intro')}
                      </p>

                      <div className="help-section">
                        <h5>{t('help.header_buttons.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#header-buttons-oben-rechts" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">❓</div>
                          <div className="help-item-content">
                            <strong>{t('help.header_buttons.help_title')}</strong>
                            <p>{t('help.header_buttons.help_desc')}</p>
                            <span className="help-shortcut">{t('help.no_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">⚙️</div>
                          <div className="help-item-content">
                            <strong>{t('help.header_buttons.config_title')}</strong>
                            <p>{t('help.header_buttons.config_desc')}</p>
                            <span className="help-shortcut">{t('help.header_buttons.config_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">←</div>
                          <div className="help-item-content">
                            <strong>{t('help.header_buttons.back_title')}</strong>
                            <p>{t('help.header_buttons.back_desc')}</p>
                            <span className="help-shortcut">{t('help.no_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.stoppuhr.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#erfassungs-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">▶</div>
                          <div className="help-item-content">
                            <strong>{t('help.stoppuhr.start_title')}</strong>
                            <p>{t('help.stoppuhr.start_desc')}</p>
                            <span className="help-shortcut">{t('help.stoppuhr.start_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">⏸</div>
                          <div className="help-item-content">
                            <strong>{t('help.stoppuhr.stop_title')}</strong>
                            <p>{t('help.stoppuhr.stop_desc')}</p>
                            <span className="help-shortcut">{t('help.stoppuhr.stop_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">↻</div>
                          <div className="help-item-content">
                            <strong>{t('help.stoppuhr.reset_title')}</strong>
                            <p>{t('help.stoppuhr.reset_desc')}</p>
                            <span className="help-shortcut">{t('help.no_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.leistungsgrad.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#efficiency-score-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <p style={{ marginBottom: '12px', color: '#6b7280' }}>
                          {t('help.leistungsgrad.intro')}
                        </p>
                        <div className="help-grid">
                          <div className="help-item-small">
                            <strong>0%</strong> - {t('help.leistungsgrad.unterbrechung')}
                            <span className="help-shortcut-small">{t('help.leistungsgrad.shortcut_0')}</span>
                          </div>
                          {['row_80', 'row_90', 'row_100', 'row_105', 'row_110', 'row_115', 'row_120', 'row_125'].map((row, i) => (
                            <div key={row} className="help-item-small">
                              <strong>{t(`help.leistungsgrad.${row}`)}</strong>
                              <span className="help-shortcut-small">{t(`help.leistungsgrad.shortcut_${i + 1}`)}</span>
                            </div>
                          ))}
                        </div>
                        <p style={{ marginTop: '12px', fontSize: '0.85rem', color: '#9ca3af', fontStyle: 'italic' }}>
                          {t('help.keyboard_customize')}
                        </p>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.notiz.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#notiz-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">🎤</div>
                          <div className="help-item-content">
                            <strong>{t('help.notiz.voice_title')}</strong>
                            <p>{t('help.notiz.voice_desc')}</p>
                            <span className="help-shortcut">{t('help.no_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">📝</div>
                          <div className="help-item-content">
                            <strong>{t('help.notiz.note_title')}</strong>
                            <p>{t('help.notiz.note_desc')}</p>
                            <span className="help-shortcut">{t('help.notiz.note_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.daten.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#daten-management-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">☁️</div>
                          <div className="help-item-content">
                            <strong>{t('help.daten.upload_title')}</strong>
                            <p>{t('help.daten.upload_desc')}</p>
                            <span className="help-shortcut">{t('help.daten.upload_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">📊</div>
                          <div className="help-item-content">
                            <strong>{t('help.daten.auswertung_title')}</strong>
                            <p>{t('help.daten.auswertung_desc')}</p>
                            <span className="help-shortcut">{t('help.daten.auswertung_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">💾</div>
                          <div className="help-item-content">
                            <strong>{t('help.daten.save_title')}</strong>
                            <p>{t('help.daten.save_desc')}</p>
                            <span className="help-shortcut">{t('help.daten.save_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.auswertung.title')}</h5>
                        <div style={{ marginBottom: '8px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#auswertung" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link_auswertung')}
                          </a>
                        </div>
                        <p style={{ marginBottom: '8px', color: '#6b7280', fontSize: '0.9rem' }}>
                          {t('help.auswertung.pivot_stat_desc')}
                        </p>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                          {t('help.auswertung.stats_basics_prefix')} <a href="https://de.wikipedia.org/wiki/Deskriptive_Statistik" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>{t('help.auswertung.stats_wikipedia_link')}</a>
                        </p>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.messpunkt.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#insight-aktionen" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">📋</div>
                          <div className="help-item-content">
                            <strong>{t('help.messpunkt.compact_title')}</strong>
                            <p>{t('help.messpunkt.compact_desc')}</p>
                            <span className="help-shortcut">{t('help.messpunkt.compact_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.vorderseite.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#vorderseite" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">📄</div>
                          <div className="help-item-content">
                            <strong>{t('help.vorderseite.front_title')}</strong>
                            <p>{t('help.vorderseite.front_desc')}</p>
                            <span className="help-shortcut">{t('help.vorderseite.front_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.restricted.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#control-center" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <p style={{ marginBottom: '8px', color: '#6b7280', fontSize: '0.9rem' }}>
                          <Trans i18nKey="help.restricted.desc" components={{ strong: <strong /> }} />
                        </p>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.config_options.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#control-center" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">⚙️</div>
                          <div className="help-item-content">
                            <strong>{t('help.config_options.ansicht_title')}</strong>
                            <p>{t('help.config_options.ansicht_desc')}</p>
                            <span className="help-shortcut">{t('help.config_options.ansicht_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">⚙️</div>
                          <div className="help-item-content">
                            <strong>{t('help.config_options.kompakt_title')}</strong>
                            <p>{t('help.config_options.kompakt_desc')}</p>
                            <span className="help-shortcut">{t('help.config_options.kompakt_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">⚙️</div>
                          <div className="help-item-content">
                            <strong>{t('help.config_options.sonstiges_title')}</strong>
                            <p>{t('help.config_options.sonstiges_desc')}</p>
                            <span className="help-shortcut">{t('help.config_options.sonstiges_shortcut')}</span>
                          </div>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">📤</div>
                          <div className="help-item-content">
                            <strong>{t('help.config_options.telemetry_title')}</strong>
                            <p>{t('help.config_options.telemetry_desc')}</p>
                            <span className="help-shortcut">{t('help.config_options.telemetry_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-section">
                        <h5>{t('help.session.title')}</h5>
                        <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                          <a href="/ANWENDERHANDBUCH.html#aktivitäten-verwalten" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                            {t('help.handbook_link')}
                          </a>
                        </div>
                        <div className="help-item">
                          <div className="help-button-icon">➕</div>
                          <div className="help-item-content">
                            <strong>{t('help.session.new_title')}</strong>
                            <p>{t('help.session.new_desc')}</p>
                            <span className="help-shortcut">{t('help.no_shortcut')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="help-footer">
                        <p>
                          {t('help.footer_tip')}
                        </p>
                        <p style={{ marginTop: '20px', fontSize: '0.85rem', color: '#9ca3af', textAlign: 'center' }}>
                          {t('app.copyright')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {configTab === 'about' && (
                  <div className="config-tab-content">
                    <div className="config-item">
                      <h4>{t('config.about.title')}</h4>
                      <p style={{ marginBottom: '8px' }}>
                        <strong>{getFooterText(t).productName}</strong> {getVersionString()}
                      </p>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '12px' }}>
                        {t('config.about.copyright')}: {getFooterText(t).copyright}
                      </p>
                      <p style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '6px' }}>{t('config.about.tech_versions_title')}</p>
                      <ul style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '16px', paddingLeft: '20px' }}>
                        {Object.entries(getTechVersions()).map(([name, ver]) => (
                          <li key={name}>{name}: {ver}</li>
                        ))}
                      </ul>
                      <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '12px' }}>
                        {t('config.about.update_running_hint')}
                      </p>
                      <div className="config-item" style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAboutUpdateClick}
                          disabled={aboutUpdateStatus === 'checking' || aboutUpdateStatus === 'applying'}
                          title={t('config.about.update_button')}
                        >
                          {aboutUpdateStatus === 'checking' && t('config.about.update_checking')}
                          {aboutUpdateStatus === 'applying' && t('config.about.update_applying')}
                          {aboutUpdateStatus === 'none' && t('config.about.update_none')}
                          {aboutUpdateStatus === 'dev' && t('config.about.update_dev_hint')}
                          {!aboutUpdateStatus && t('config.about.update_button')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleSecurityCheckClick}
                          title={t('config.about.security_check_button')}
                        >
                          {t('config.about.security_check_button')}
                        </button>
                        {showCacheClearButton && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCacheClearClick}
                            title={t('config.about.cache_clear_hint')}
                          >
                            {t('config.about.cache_clear_button')}
                          </button>
                        )}
                      </div>
                      {securityCheckResult && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#f3f4f6', borderRadius: '8px', fontSize: '0.85rem' }}>
                          <p style={{ fontWeight: 600, marginBottom: '8px' }}>{t('config.about.security_check_title')}</p>
                          <p>{securityCheckResult.secureContext ? t('config.about.security_secure_context') : t('config.about.security_insecure_context')}</p>
                          <p>{t('config.about.security_protocol')}: {securityCheckResult.protocol}</p>
                          <p>{t('config.about.security_service_worker')}: {securityCheckResult.hasSW ? t('config.about.security_sw_active') : t('config.about.security_sw_none')}</p>
                          {securityCheckResult.cveData && (
                            <>
                              <p style={{ fontWeight: 600, marginTop: '12px' }}>{t('config.about.security_cve_title')}</p>
                              {Object.entries(securityCheckResult.cveData).map(([pkg, { vulns }]) => (
                                <p key={pkg}>{pkg}: {vulns.length === 0 ? t('config.about.security_cve_ok') : t('config.about.security_cve_found', { count: vulns.length })} {vulns.length > 0 && vulns.slice(0, 3).map((v) => v.id).join(', ')}</p>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Sonstiges: Sprachwahl immer verfügbar */}
                {configTab === 'sonstiges' && (
                  <div className="config-tab-content">
                    <div className="config-item">
                      <h4>{t('config.language.title')}</h4>
                      <label>
                        <select
                          value={i18n.language}
                          onChange={(e) => {
                            const newLang = e.target.value;
                            i18n.changeLanguage(newLang);
                            try {
                              localStorage.setItem('stoppuhr_locale', newLang);
                            } catch (err) { /* ignore */ }
                          }}
                          style={{ minWidth: '200px', padding: '6px 10px', fontSize: '0.95rem' }}
                        >
                          <option value="de">{t('config.language.de')}</option>
                          <option value="de-neu">{t('config.language.de_neu')}</option>
                          <option value="en">{t('config.language.en')}</option>
                        </select>
                      </label>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.sonstiges.language_desc')}
                      </p>
                    </div>
                    <div className="config-item" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                        {t('config.sonstiges.more_options_restricted')}
                      </p>
                    </div>
                  </div>
                )}
                {/* Für andere eingeschränkte Tabs: Zeige eine Nachricht */}
                {configTab !== 'license' && configTab !== 'ablaufdb' && configTab !== 'timetypes' && configTab !== 'help' && configTab !== 'sonstiges' && configTab !== 'about' && (
                  <div className="config-tab-content">
                    <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                      {t('config.restricted_tab.message')}
                    </p>
                    <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginTop: '8px' }}>
                      {t('config.restricted_tab.hint')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Formatiert Timestamp zu deutschem Datum/Uhrzeit-String
  const formatGermanDateTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };

  // Sendet Debug-Logs an Backend (schreibt nach .cursor/debug.log)
  const handleWriteToProjectFile = async () => {
    if (!backendUrl) {
      console.warn('[Debug] Backend-URL nicht verfügbar');
      setWriteToFileStatus('error');
      setTimeout(() => setWriteToFileStatus(null), 3000);
      return;
    }

    setWriteToFileStatus('sending');

    try {
      // Lade alle Debug-Logs aus localStorage
      const savedLogs = JSON.parse(localStorage.getItem('stoppuhr_debug_logs') || '[]');
      
      if (savedLogs.length === 0) {
        console.warn('[Debug] Keine Logs zum Senden vorhanden');
        setWriteToFileStatus('error');
        setTimeout(() => setWriteToFileStatus(null), 3000);
        return;
      }

      // Konvertiere Logs in das Format, das das Backend erwartet
      const logsForBackend = savedLogs.map(log => ({
        location: log.location || null,
        message: log.message || null,
        data: log.data || null,
        hypothesis_id: log.hypothesisId || null,
        timestamp: log.timestamp || null
      }));

      // Sende an Backend
      const response = await fetch(`${backendUrl}/api/agent-debug-logs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ logs: logsForBackend })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[Debug] Logs erfolgreich an Backend gesendet:', result);
      setWriteToFileStatus('ok');
      
      // Setze Status nach 3 Sekunden zurück
      setTimeout(() => setWriteToFileStatus(null), 3000);
    } catch (error) {
      console.error('[Debug] Fehler beim Senden der Logs:', error);
      setWriteToFileStatus('error');
      setTimeout(() => setWriteToFileStatus(null), 3000);
    }
  };

  // Debug-Panel Helper (wird am Ende der App gerendert)
  const renderDebugPanel = () => {
    if (!showDebugPanel) return null;
    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#1a1a1a',
        color: '#fff',
        padding: '10px',
        maxHeight: '300px',
        overflowY: 'auto',
        fontSize: '11px',
        zIndex: 10000,
        borderTop: '2px solid #dc3545',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ margin: 0, color: '#dc3545' }}>🐛 Debug-Logs (letzte 50)</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => {
                setShowAllLogsText(!showAllLogsText);
              }}
              style={{ padding: '5px 10px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
              title="Zeigt alle Logs in einem markierbaren Textfeld an"
            >
              📋 {showAllLogsText ? 'Logs ausblenden' : 'Alle Logs markieren'}
            </button>
            <button 
              onClick={() => {
                const savedLogs = JSON.parse(localStorage.getItem('stoppuhr_debug_logs') || '[]');
                setDebugLogs(savedLogs.slice(-50));
              }}
              style={{ padding: '5px 10px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
            >
              🔄 Aktualisieren
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem('stoppuhr_debug_logs');
                setDebugLogs([]);
              }}
              style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
            >
              🗑️ Löschen
            </button>
            {backendUrl && (
              <>
                <button
                  onClick={handleWriteToProjectFile}
                  disabled={writeToFileStatus === 'sending'}
                  style={{ padding: '5px 10px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '3px', cursor: writeToFileStatus === 'sending' ? 'wait' : 'pointer' }}
                  title="Schreibt aktuelle Debug-Logs nach .cursor/debug.log (Backend muss laufen)"
                >
                  {writeToFileStatus === 'sending' ? '…' : '📁 In Projektdatei'}
                </button>
                {writeToFileStatus === 'ok' && <span style={{ color: '#10b981', fontSize: '0.9rem' }}>Gespeichert.</span>}
                {writeToFileStatus === 'error' && <span style={{ color: '#dc2626', fontSize: '0.9rem' }}>Fehler (Backend?).</span>}
              </>
            )}
            <button 
              onClick={() => setShowDebugPanel(false)}
              style={{ padding: '5px 10px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
            >
              ✕ Schließen
            </button>
          </div>
        </div>
        {showAllLogsText ? (
          <div style={{ marginTop: '10px' }}>
            <textarea
              readOnly
              value={(() => {
                const savedLogs = JSON.parse(localStorage.getItem('stoppuhr_debug_logs') || '[]');
                return savedLogs.map((log, idx) => {
                  const dataStr = log.data ? JSON.stringify(log.data, null, 2) : '{}';
                  return `[${idx + 1}] ${log.time} - ${log.location}\n${log.message}\n${dataStr}\n---`;
                }).join('\n\n');
              })()}
              style={{
                width: '100%',
                height: '200px',
                backgroundColor: '#2a2a2a',
                color: '#fff',
                fontFamily: 'monospace',
                fontSize: '11px',
                padding: '10px',
                border: '1px solid #444',
                borderRadius: '3px',
                resize: 'vertical',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
              onClick={(e) => e.target.select()}
              onFocus={(e) => e.target.select()}
            />
            <div style={{ marginTop: '5px', fontSize: '10px', color: '#888' }}>
              💡 Tipp: Text markieren und teilen (Strg+A markiert alles)
            </div>
          </div>
        ) : (
          <div style={{ fontFamily: 'monospace' }}>
            {debugLogs.length === 0 ? (
              <div style={{ color: '#888' }}>Keine Logs vorhanden. Warte auf Logs...</div>
            ) : (
              debugLogs.slice().reverse().map((log, idx) => (
                <div key={idx} style={{ 
                  marginBottom: '5px', 
                  padding: '5px', 
                  backgroundColor: idx % 2 === 0 ? '#2a2a2a' : '#1a1a1a',
                  borderLeft: `3px solid ${log.hypothesisId === 'A' ? '#3b82f6' : log.hypothesisId === 'B' ? '#10b981' : log.hypothesisId === 'C' ? '#f59e0b' : log.hypothesisId === 'D' ? '#ef4444' : '#8b5cf6'}`
                }}>
                  <div style={{ color: '#888', fontSize: '10px' }}>{log.time} - {log.location}</div>
                  <div style={{ color: '#fff', fontWeight: 'bold' }}>{log.message}</div>
                  {log.data && Object.keys(log.data).length > 0 && (
                    <div style={{ color: '#aaa', marginTop: '3px', fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(log.data, null, 2).substring(0, 300)}
                      {JSON.stringify(log.data, null, 2).length > 300 ? '...' : ''}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // Main App
  return (
    <div className="app">
      {/* Update-Resume Dialog - FRÜH platzieren für sofortige Anzeige */}
      {showUpdateResumeDialog && updateResumeDialogData && (
        <div className="dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            // Verhindere mehrfache Aufrufe durch Event-Bubbling
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            handleUpdateResumeCancel();
          }
        }}>
          <div className="dialog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>🔄 Messung fortsetzen</h3>
            </div>
            <div className="dialog-content">
              <p style={{ marginBottom: '20px', fontSize: '1.1rem', fontWeight: '500' }}>
                Eine laufende Messung wurde gefunden:
              </p>
              <div className="dialog-info">
                <p style={{ margin: '8px 0', fontSize: '0.95rem' }}>
                  <strong>Session:</strong> {updateResumeDialogData.sessionName}
                </p>
                <p style={{ margin: '8px 0', fontSize: '0.95rem' }}>
                  <strong>Messpunkte:</strong> {updateResumeDialogData.measurementsCount}
                </p>
                <p style={{ margin: '8px 0', fontSize: '0.95rem' }}>
                  <strong>WA-Nr:</strong> {updateResumeDialogData.resumeNumber}
                </p>
              </div>
              <div className="dialog-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleUpdateResumeConfirm}
                >
                  {t('session.continue')}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    handleUpdateResumeCancel();
                  }}
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                  title={t('common.resume_restart_title')}
                >
                  {t('common.resume_restart_btn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="header">
        <div className="header-top">
          <h1>
            {selectedSession ? (
              <button
                type="button"
                className="header-session-name-btn"
                onClick={() => {
                  setRenameSessionName(selectedSession.sessionName ?? '');
                  setShowRenameSessionDialog(true);
                }}
                  title={t('common.rename_session_title')}
              >
                {selectedSession.sessionName || `Erfassung - WA ${selectedSession.resumeNumber || 1}`}
              </button>
            ) : (
              <>Erfassung - WA {selectedSession?.resumeNumber || 1}</>
            )}
            {deviceName && <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#666' }}>- {deviceName}</span>}
            {selectedSession?.isDemo && <span className="demo-badge">DEMO</span>}
            {/* Backend-Status nach dem Session-Namen */}
            {backendUrl && (
              <span 
                className={`compact-header-backend-status ${backendAvailable === true ? 'connected' : backendAvailable === false ? 'disconnected' : 'unknown'}`}
                title={
                  backendAvailable === true 
                    ? t('common.backend_connected')
                    : backendAvailable === false 
                    ? `${t('common.backend_disconnected')}${backendOfflineSince ? ` (seit ${formatGermanDateTime(backendOfflineSince)})` : ''}`
                    : t('common.backend_checking')
                }
              >
                {backendAvailable === true ? '🟢' : backendAvailable === false ? '🔴' : '🟡'}
              </span>
            )}
          </h1>
          <div className="header-actions">
            {/* Hamburger-Menü für Portrait-Modus */}
            <div className="compact-menu-container">
              <button
                className="btn-icon btn-menu"
                onClick={toggleCompactMenu}
                title={t('common.menu_open_title')}
              >
                ☰
              </button>
              
              {/* Menü-Dropdown */}
              {showCompactMenu && (
                <div className="compact-menu-dropdown">
                  <a
                    href="/ANWENDERHANDBUCH.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="compact-menu-item"
                    onClick={() => {
                      // Speichere die aktuelle URL für die Rücknavigation
                      if (typeof window !== 'undefined' && window.sessionStorage) {
                        sessionStorage.setItem('handbuch_return_url', window.location.href);
                      }
                      setShowCompactMenu(false);
                    }}
                  >
                    <span className="menu-icon">❓</span>
                    <span>{t('common.handbook')}</span>
                  </a>
                  
                  <button
                    className="compact-menu-item"
                    onClick={() => {
                      setShowConfig(!showConfig);
                      setShowCompactMenu(false);
                    }}
                  >
                    <span className="menu-icon">⚙️</span>
                    <span>{t('common.config')}</span>
                  </button>
                  
                  {selectedSession && (
                    <button
                      className="compact-menu-item"
                      onClick={() => {
                        setRenameSessionName(selectedSession.sessionName ?? '');
                        setShowRenameSessionDialog(true);
                        setShowCompactMenu(false);
                      }}
                    >
                      <span className="menu-icon">✏️</span>
                      <span>{t('common.rename_session')}</span>
                    </button>
                  )}
                  
                  <button
                    className="compact-menu-item"
                    onClick={() => {
                      setShowAblaufDbDialog(true);
                      setShowCompactMenu(false);
                    }}
                  >
                    <span className="menu-icon">🗄️</span>
                    <span>{t('compact_menu.ablaufdb')}</span>
                  </button>
                  
                  <button
                    className="compact-menu-item"
                    onClick={() => {
                      setShowFeedbackDialog(true);
                      setShowCompactMenu(false);
                    }}
                    disabled={!backendUrl || !email}
                  >
                    <span className="menu-icon">📝</span>
                    <span>{t('compact_menu.feedback')}</span>
                  </button>
                  
                  <button
                    className="compact-menu-item"
                    onClick={() => {
                      handleBackToSessions();
                      setShowCompactMenu(false);
                    }}
                  >
                    <span className="menu-icon">←</span>
                    <span>{t('compact_menu.back_to_list')}</span>
                  </button>
                  
                  {process.env.NODE_ENV !== 'production' && getEnableDebugLogs() && (
                    <>
                      <div className="compact-menu-divider"></div>
                      
                      <button
                        className="compact-menu-item"
                        onClick={() => {
                          setShowDebugPanel(!showDebugPanel);
                          setShowCompactMenu(false);
                        }}
                      >
                        <span className="menu-icon">🐛</span>
                        <span>{showDebugPanel ? t('compact_menu.debug_off') : t('compact_menu.debug_on')}</span>
                      </button>
                      
                      <div className="compact-menu-divider"></div>
                    </>
                  )}
                  
                  {licenseStatus && measurements.length > 0 && (
                    <button
                      className="compact-menu-item"
                      onClick={() => {
                        setShowCompactAnalysis(true);
                        setShowCompactMenu(false);
                      }}
                    >
                      <span className="menu-icon">📊</span>
                      <span>{t('common.auswertung')}</span>
                    </button>
                  )}
                  
                  {selectedSession && (
                    <button
                      className="compact-menu-item"
                      onClick={() => {
                        setVorderseiteDialogOpen(true);
                        // Prüfe beim Öffnen der Vorderseite
                        setTimeout(() => checkAndShowWiederaufnahmenDialog(), 100);
                        setShowCompactMenu(false);
                      }}
                    >
                      <span className="menu-icon">📄</span>
                      <span>{t('compact_menu.vorderseite')}</span>
                    </button>
                  )}
                  
                  <div className="compact-menu-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={merke1Enabled}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setMerke1Enabled(newValue);
                        localStorage.setItem('stoppuhr_merke1_enabled', String(newValue));
                        if (!newValue) {
                          // Wenn deaktiviert, lösche gespeicherte Werte
                          setMerke1Values(null);
                          localStorage.removeItem('stoppuhr_merke1_values');
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer' }}
                    />
                    <span className="menu-icon">🔖</span>
                    <span>{t('compact_menu.remember_kuerzel')}</span>
                  </div>
                  
                  {!isPortrait && (
                    <button
                      className="compact-menu-item"
                      onClick={handleToggleCompactView}
                    >
                      <span className="menu-icon">{compactViewMode ? '📱' : '🖥️'}</span>
                      <span>
                        {compactViewMode ? 'Kompakte Ansicht: AN' : 'Kompakte Ansicht: AUS'}
                      </span>
                    </button>
                  )}
                  
                  <button
                    className="compact-menu-item"
                    onClick={handleToggleCompactInputs}
                  >
                    <span className="menu-icon">✏️</span>
                    <span>
                      {showCompactInputs ? t('common.inputs_on') : t('common.inputs_off')}
                    </span>
                  </button>
                  
                  <div className="compact-menu-divider"></div>
                  
                  {licenseStatus && measurements.length > 0 && backendUrl && (
                    <button
                      className="compact-menu-item"
                      onClick={() => {
                        handleUploadMeasurements();
                        setShowCompactMenu(false);
                      }}
                      title={t('common.manual_upload_title')}
                    >
                      <span className="menu-icon">⬆️</span>
                      <span>{t('common.manual_upload')}</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Normale Buttons für Desktop (ausblenden im Portrait-Modus) */}
            <div className="header-actions-desktop">
              <a
                href="/ANWENDERHANDBUCH.html"
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-icon btn-help" 
              title={t('common.handbook_title')}
              onClick={() => {
                // Speichere die aktuelle URL für die Rücknavigation
                if (typeof window !== 'undefined' && window.sessionStorage) {
                  sessionStorage.setItem('handbuch_return_url', window.location.href);
                }
              }}
            >
              ❓
            </a>
            <button 
              className="btn-icon btn-config" 
              onClick={() => setShowConfig(!showConfig)}
              title={t('common.config_title')}
            >
              ⚙️
            </button>
            <button 
              className="btn-icon btn-db" 
              onClick={() => setShowAblaufDbDialog(true)}
              title={t('common.ablaufdb_header_title')}
            >
              🗄️
            </button>
            <button 
              className="btn-icon btn-back" 
              onClick={handleBackToSessions}
              title={t('common.back_to_list_title')}
            >
              ←
            </button>
            {process.env.NODE_ENV !== 'production' && getEnableDebugLogs() && (
              <button 
                className="btn-icon" 
                onClick={() => setShowDebugPanel(!showDebugPanel)}
                title={`${t('common.debug_panel_title')} ${showDebugPanel ? t('common.debug_panel_hide') : t('common.debug_panel_show')}`}
                style={{ backgroundColor: showDebugPanel ? '#dc3545' : 'rgba(255, 255, 255, 0.2)' }}
              >
                🐛
              </button>
            )}
            </div>
          </div>
        </div>
        
        {/* Gemeinsames Foto-Input für beide Ansichten (normal und kompakt) */}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="camera"
          onChange={handlePendingPhotoUpload}
          disabled={!selectedSession || pendingPhotos.length >= 3}
          style={{ display: 'none' }}
          id="pending-photo-upload-input"
        />

        <>
        {/* Prüfe ob kompakte Ansicht aktiviert ist und Portrait-Modus */}
        {isPortrait ? (
          <CompactPortraitView
            hapticFeedbackEnabled={hapticFeedbackEnabled}
            currentTime={currentTime}
            isRunning={isRunning}
            measurements={measurements}
            lastMeasurementTime={lastMeasurementTime}
            formatTime={formatTime}
            formatSollzeit={formatSollzeit}
            decimalPlacesSollzeiten={decimalPlacesSollzeiten}
            onStart={handleStart}
            onStop={handleStop}
            onReset={handleReset}
            showAblaufDb={showAblaufDb}
            ablaufDbData={ablaufDbData}
            einflussDbData={einflussDbData}
            dependentListsData={dependentListsData}
            selectedKuerzel={selectedKuerzel}
            onKuerzelSelect={handleKuerzelSelect}
            einflussgroessenValues={einflussgroessenValues}
            onEinflussgroesseChange={handleCompactEinflussgroesseChange}
            grades={grades}
            onGradeClick={handleAddMeasurement}
            isInterrupted={isInterrupted}
            onInterruptionToggle={handleInterruptionToggle}
            interruptionMeasurementNumber={interruptionMeasurementNumber}
            onEditMeasurement={handleCompactEditMeasurement}
            onDeleteMeasurement={handleCompactDeleteMeasurement}
            onViewPhotos={handleCompactViewPhotos}
            onViewNote={handleCompactViewNote}
            measurementPhotos={measurementPhotos}
            onAddPhoto={handleCompactAddPhoto}
            onRemovePhoto={handleCompactRemovePhoto}
            onVoiceClick={handleCompactVoiceClick}
            onNoteClick={handleCompactNoteClick}
            onPhotoClick={handleCompactPhotoClick}
            onCopyActivity={handleCompactCopyActivity}
            pendingPhotosCount={pendingPhotos.length}
            isListening={isListening}
            selectedSession={selectedSession}
            activity={activity}
            setActivity={setActivity}
            note={note}
            setNote={setNote}
            timeTypes={timeTypes}
            timeType={timeType}
            setTimeType={setTimeType}
            darkMode={darkMode}
            showInputs={showCompactInputs}
            onPhotoUpload={handleCompactEditPhotoUpload}
            onPhotoDelete={handleCompactEditPhotoDelete}
            loadPhotos={loadPhotos}
            resumeNumber={resumeNumber}
            licenseStatus={licenseStatus}
            getAnalysis={getAnalysis}
            showAnalysis={showCompactAnalysis}
            onCloseAnalysis={() => setShowCompactAnalysis(false)}
            pivotDrillDown={compactPivotDrillDown}
            setPivotDrillDown={setCompactPivotDrillDown}
            backendAvailable={backendAvailable}
            backendUrl={backendUrl}
            onResetMerke1={handleResetMerke1}
            mitLeistungsgrade={!ohneLeistungsgradErfassen}
          />
        ) : (
        <>
        <div className="time-display-section">
          <div className="time-with-controls">
            <div className="running-time">
              {formatTime(currentTime)}
              {showIndividualTimeInHeader && isRunning && measurements.length > 0 && (
                <span style={{ fontSize: '0.75em', marginLeft: '12px', opacity: 0.8 }}>
                  (EZ: {formatTime(Math.max(0, Math.round(currentTime - lastMeasurementTime)))})
                </span>
              )}
              {showIndividualTimeInHeader && isRunning && measurements.length === 0 && (
                <span style={{ fontSize: '0.75em', marginLeft: '12px', opacity: 0.8 }}>
                  (EZ: {formatTime(Math.round(currentTime))})
                </span>
              )}
            </div>
            <div className="controls">
              {!isRunning && (
                <>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleStart}
                    title={t('common.start_title')}
                  >
                    ▶ {t('common.start')}
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={handleResumeFromButton}
                    title={t('common.continue_title')}
                  >
                    ▶ {t('session.continue')}
                  </button>
                </>
              )}
              {isRunning && (
                <button 
                  className="btn btn-secondary" 
                  onClick={handleStop}
                  title={t('common.stop_title')}
                >
                  ⏸ {t('common.stop')}
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="activity-input-section">
          <table className="activity-input-section-table" role="presentation">
            <tbody>
              <tr>
                <td className="activity-input-section-left">
                  {showAblaufDb && ablaufDbData && ablaufDbData.length > 0 && (
                    <div className="ablauf-kuerzel-list" ref={ablaufKuerzelListRef}>
                      {ablaufDbData.map((item, idx) => (
                        <button
                          key={idx}
                          className="kuerzel-item"
                          onClick={() => handleKuerzelSelect(item)}
                          title={`${item.kuerzel}: ${item.beschreibung || item.fullText || ''}${item.zeitart ? ` [${item.zeitart}]` : ''}`}
                        >
                          {item.kuerzel}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
                <td className="activity-input-section-right-cell">
                  <div className="activity-input-section-right" ref={activityInputSectionRightRef}>
            <div className="activity-input-section-top">
              <div className="activity-input-wrapper">
                <input
                  type="text"
                  className="activity-input"
                  placeholder="Tätigkeit eingeben... (Enter zum Hinzufügen)"
                  value={activity}
                  onChange={(e) => setActivity(e.target.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && activity.trim() && selectedSession) {
                      e.preventDefault();
                      // Füge Messpunkt ohne Leistungsgrad hinzu
                      handleAddMeasurementWithoutGrade();
                    }
                  }}
                  disabled={!selectedSession}
                />
              </div>
              <select
                className="time-type-select"
                value={timeType}
                onChange={(e) => setTimeType(e.target.value)}
                disabled={!selectedSession}
              >
                {timeTypes.map(type => (
                  <option key={type.name} value={type.name}>
                    {type.name} - {type.description}
                  </option>
                ))}
              </select>
            </div>
            
            
            <div className="note-section">
              <textarea
                className="note-input"
                placeholder={t('common.notiz_placeholder')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && note.trim() && selectedSession) {
                    e.preventDefault();
                    // Speichere Notiz
                    handleSaveNote();
                  }
                  // Shift+Enter erlaubt Zeilenumbruch (Standard-Verhalten)
                }}
                disabled={!selectedSession}
                rows="2"
              />
              <table className="note-buttons-table">
                <tbody>
                  <tr>
                    <td>
                      <button
                        className={`btn btn-voice ${isListening ? 'listening' : ''}`}
                        onMouseDown={handleStartListening}
                        onMouseUp={handleStopListening}
                        onMouseLeave={handleStopListening}
                        onTouchStart={handleStartListening}
                        onTouchEnd={handleStopListening}
                        disabled={!recognition || !selectedSession || !isSpeechRecognitionSupported}
                        title={isSpeechRecognitionSupported 
                          ? "Sprach-Eingabe - Gedrückt halten für Spracherkennung. Erfasst gesprochene Tätigkeiten als Text"
                          : "Sprachsteuerung wird in diesem Browser nicht unterstützt. Bitte verwenden Sie Chrome oder Edge."}
                      >
                        {isListening ? '🎤 Aufnahme...' : '🎤 Sprache'}
                      </button>
                    </td>
                    <td>
                      <div className="photo-upload-pending">
                        <label 
                          htmlFor="pending-photo-upload-input" 
                          className={`btn btn-photo ${pendingPhotos.length > 0 ? 'has-photos' : ''}`}
                          style={{ cursor: pendingPhotos.length >= 3 ? 'not-allowed' : 'pointer', display: 'inline-block' }}
                          title={pendingPhotos.length >= 3 ? t('common.photo_add_max') : t('common.photo_add_title')}
                        >
                          📷 {pendingPhotos.length > 0 ? `(${pendingPhotos.length})` : ''}
                        </label>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <button
                        className="btn btn-note"
                        onClick={handleSaveNote}
                        disabled={!selectedSession || !note.trim()}
                        title={t('common.notiz_save_title')}
                      >
                        {t('common.notiz_save')}
                      </button>
                    </td>
                    <td>
                      {pendingPhotos.length > 0 && (
                        <div className="pending-photos-preview">
                          {pendingPhotos.map((photo, idx) => (
                            <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
                              <img
                                src={photo.fileData}
                                alt={photo.fileName}
                                onClick={() => setPhotoPreview(photo.fileData)}
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  border: '2px solid #e5e7eb'
                                }}
                                title={`${photo.fileName} (Klicken zum Vergrößern)`}
                              />
                              <button
                                onClick={() => handleDeletePendingPhoto(idx)}
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '-8px',
                                  background: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0
                                }}
                                title={t('common.photo_remove_title')}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* Einflussgrößen-Anzeige für selektiertes Kürzel */}
            {selectedKuerzel && selectedKuerzel.einflussgroessen && Array.isArray(selectedKuerzel.einflussgroessen) && selectedKuerzel.einflussgroessen.length > 0 && (
              <div className="einflussgroessen-section">
                {/* Rotierte Ansicht (horizontal) wenn EA Kombinationen AN ist */}
                {showEaKombinationen && (
                  <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', backdropFilter: 'blur(10px)', border: '2px solid rgba(102, 126, 234, 0.5)' }}>
                    <div style={{ marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(102, 126, 234, 0.9)', fontWeight: 600 }}>EA Kombinationen: AN (Horizontal)</div>
                    <div style={{ overflowX: 'auto', width: '100%', overflowY: 'visible' }}>
                      <table className="einflussgroessen-table-rotated" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '100%', tableLayout: 'auto' }} data-dropdown-container>
                        <thead>
                          <tr>
                            {selectedKuerzel.einflussgroessen.map((einfluss, idx) => {
                              if (!einfluss || !einfluss.einflussname) return null;
                              const einflussname = einfluss.einflussname.trim();
                              if (!einflussname) return null;
                              const einheit = einfluss.einheit?.trim() || '';
                              return (
                                <th 
                                  key={idx} 
                                  style={{ 
                                    textAlign: 'center', 
                                    padding: '4px 12px', 
                                    fontWeight: 600, 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    minWidth: '120px',
                                    verticalAlign: 'top'
                                  }}
                                >
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <span>{einflussname}</span>
                                    {einheit && <span style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '2px' }}>({einheit})</span>}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Berechne maximale Anzahl von Messwerten für Höhenanpassung
                            const maxMesswerteCount = Math.max(
                              ...selectedKuerzel.einflussgroessen
                                .filter(e => e && e.einflussname)
                                .map(e => {
                                  const messwerte = getEinflussOptionValues(e);
                                  return Math.min(messwerte.length, 10); // Maximal 10
                                }),
                              0
                            );
                            // Mindestanzahl von 6 List-Items für Höhenberechnung
                            const minListItemsCount = 6;
                            const effectiveMesswerteCount = Math.max(maxMesswerteCount, minListItemsCount);
                            
                            // Berechne Höhe: ~35px pro Item + 2px Border (35px für Touch/Padding)
                            // Ziffernblock: 4 Zeilen (3x4 Grid) à ~40px = ~160px + Padding = ~180px
                            const ziffernblockHeight = 180; // Mindesthöhe für Ziffernblock
                            const listItemsHeight = effectiveMesswerteCount > 0 
                              ? Math.min(effectiveMesswerteCount * 35 + 2, 352) // Max 10 Items * 35px + 2px = 352px
                              : minListItemsCount * 35 + 2; // Mindesthöhe für 6 Items
                            // Höhe ist Maximum aus Ziffernblock (Mindesthöhe) und List-Items
                            const calculatedHeight = Math.max(ziffernblockHeight, listItemsHeight);
                            
                            return (
                              <>
                            {/* Zeile 1: Input-Feld UND List-Items/Ziffernblock in derselben Zelle (übereinander) - nur für EA AN */}
                            <tr>
                              {selectedKuerzel.einflussgroessen.map((einfluss, idx) => {
                                if (!einfluss || !einfluss.einflussname) return null;
                                const einflussname = einfluss.einflussname.trim();
                                if (!einflussname) return null;
                                const messwerte = getEinflussOptionValues(einfluss);
                                const currentValue = einflussgroessenValues?.[einflussname] || '';
                                const displayMesswerte = messwerte.slice(0, 10);
                                
                                return (
                                  <td 
                                    key={idx} 
                                    style={{ 
                                      textAlign: 'center', 
                                      padding: '8px 12px', 
                                      border: '1px solid rgba(255, 255, 255, 0.2)',
                                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                      verticalAlign: 'top',
                                      position: 'relative',
                                      minHeight: `${calculatedHeight + 60}px`,
                                      height: `${calculatedHeight + 60}px`
                                    }}
                                  >
                                    {/* Input-Feld oben */}
                                    <input
                                      type="text"
                                      data-einflussname={einflussname}
                                      style={{
                                        width: '100%',
                                        padding: '6px 8px',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        borderRadius: '4px',
                                        fontSize: '0.875rem',
                                        background: 'rgba(255, 255, 255, 0.15)',
                                        color: 'white',
                                        backdropFilter: 'blur(10px)',
                                        marginBottom: '8px'
                                      }}
                                      placeholder={messwerte.length > 0 ? `Auswahl oder Eingabe...` : 'Wert eingeben...'}
                                      value={currentValue}
                                      onChange={(e) => {
                                        const newValue = e.target.value;
                                        applyEinflussValueChange(einflussname, newValue);
                                      }}
                                      onFocus={(e) => {
                                        ziffernblockInputElementRef.current = e.target;
                                        setFocusedInput(prev => ({
                                          ...prev,
                                          [einflussname]: true
                                        }));
                                      }}
                                      onBlur={() => {                                        debugLog('App.jsx:9552', 'Input onBlur: Entry', {einflussname,ziffernblockClick:ziffernblockClickRef.current}, 'B');                                        if (ziffernblockClickRef.current) {
                                          ziffernblockClickRef.current = false;                                          debugLog('App.jsx:9556', 'Input onBlur: Ziffernblock click detected, skipping blur', {einflussname}, 'B');                                          setTimeout(() => {
                                            // Re-focus das Input-Feld
                                            if (ziffernblockInputElementRef.current) {
                                              ziffernblockInputElementRef.current.focus();
                                            }
                                          }, 0);
                                          return;
                                        }
                                        setTimeout(() => {
                                          setFocusedInput(prev => ({
                                            ...prev,
                                            [einflussname]: false
                                          }));
                                        }, 200);
                                      }}
                                    />
                                    
                                    {/* List-Items direkt unter dem Input (wenn vorhanden) */}
                                    {displayMesswerte.length > 0 ? (
                                      <div style={{ 
                                        height: `${calculatedHeight - 4}px`,
                                        overflowY: displayMesswerte.length > effectiveMesswerteCount ? 'auto' : 'hidden',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        borderRadius: '4px',
                                        background: 'rgba(255, 255, 255, 0.05)'
                                      }}>
                                        {displayMesswerte.map((mw, mwIdx) => {
                                          const mwValue = mw || '';
                                          const isSelected = currentValue === mwValue;
                                          return (
                                            <div
                                              key={mwIdx}
                                              onClick={() => {
                                                applyEinflussValueChange(einflussname, mwValue);
                                              }}
                                              style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                                backgroundColor: isSelected ? 'rgba(102, 126, 234, 0.3)' : 'transparent',
                                                color: 'white',
                                                transition: 'background-color 0.2s',
                                                fontSize: '0.875rem'
                                              }}
                                              onMouseEnter={(e) => {
                                                if (!isSelected) {
                                                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                                }
                                              }}
                                              onMouseLeave={(e) => {
                                                if (!isSelected) {
                                                  e.target.style.backgroundColor = 'transparent';
                                                }
                                              }}
                                            >
                                              {mwValue || '(leer)'}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      /* Ziffernblock für manuelle Eingabe - nur wenn keine List-Items vorhanden */
                                      /* Ziffernblock immer rendern, aber mit visibility/opacity steuern, um Springen zu vermeiden */
                                      <div style={{
                                        height: `${ziffernblockHeight}px`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                      }}>
                                        {focusedInput[einflussname] ? (
                                          <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(3, 1fr)',
                                            gap: '4px',
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '4px',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            visibility: 'visible',
                                            opacity: 1,
                                            transition: 'opacity 0.2s ease'
                                          }}>
                                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => (
                                            <button
                                              key={num}
                                              type="button"
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                ziffernblockClickRef.current = true;                                                debugLog('App.jsx:9625', 'Ziffernblock onMouseDown: Entry', {num,einflussname}, 'B');                                              }}
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();                                                debugLog('App.jsx:9632', 'Ziffernblock onClick: Entry', {num,closureCurrentValue:currentValue,einflussname}, 'A');                                                setEinflussgroessenValues(prev => {
                                                  const stateCurrentValue = prev?.[einflussname] || '';
                                                  const newValue = stateCurrentValue + num.toString();                                                  debugLog('App.jsx:9637', 'Ziffernblock onClick: State update', {num,closureCurrentValue:currentValue,stateCurrentValue,newValue,einflussname}, 'A');
                                                  const next = applyDependentAutofillValues({
                                                    ...(prev || {}),
                                                    [einflussname]: newValue
                                                  });
                                                  einflussgroessenValuesRef.current = next;
                                                  return next;
                                                });
                                                setTimeout(() => {
                                                  ziffernblockClickRef.current = false;
                                                  // Re-focus das Input-Feld
                                                  if (ziffernblockInputElementRef.current) {
                                                    ziffernblockInputElementRef.current.focus();
                                                  }
                                                }, 0);
                                              }}
                                              style={{
                                                padding: '8px',
                                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                                borderRadius: '4px',
                                                background: 'rgba(255, 255, 255, 0.15)',
                                                color: 'white',
                                                cursor: 'pointer',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                transition: 'all 0.2s'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                              }}
                                            >
                                              {num}
                                            </button>
                                          ))}
                                          <button
                                            type="button"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              ziffernblockClickRef.current = true;
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();                                              debugLog('App.jsx:9669', 'Ziffernblock Decimal onClick: Entry', {closureCurrentValue:currentValue,einflussname}, 'A');                                              setEinflussgroessenValues(prev => {
                                                const stateCurrentValue = prev?.[einflussname] || '';                                                debugLog('App.jsx:9672', 'Ziffernblock Decimal onClick: State check', {closureCurrentValue:currentValue,stateCurrentValue,hasComma:stateCurrentValue.includes(','),hasDot:stateCurrentValue.includes('.'),einflussname}, 'A');                                                if (!stateCurrentValue.includes(',') && !stateCurrentValue.includes('.')) {
                                                  const decimalSeparator = navigator.language === 'de-DE' || navigator.language.startsWith('de') ? ',' : '.';
                                                  const newValue = stateCurrentValue + decimalSeparator;                                                  debugLog('App.jsx:9676', 'Ziffernblock Decimal onClick: Adding decimal', {stateCurrentValue,newValue,decimalSeparator,einflussname}, 'A');
                                                  const next = applyDependentAutofillValues({
                                                    ...(prev || {}),
                                                    [einflussname]: newValue
                                                  });
                                                  einflussgroessenValuesRef.current = next;
                                                  return next;
                                                }
                                                return prev || {};
                                              });
                                              setTimeout(() => {
                                                ziffernblockClickRef.current = false;
                                                if (ziffernblockInputElementRef.current) {
                                                  ziffernblockInputElementRef.current.focus();
                                                }
                                              }, 0);
                                            }}
                                            style={{
                                              padding: '8px',
                                              border: '1px solid rgba(255, 255, 255, 0.3)',
                                              borderRadius: '4px',
                                              background: 'rgba(255, 255, 255, 0.15)',
                                              color: 'white',
                                              cursor: 'pointer',
                                              fontSize: '0.9rem',
                                              fontWeight: '600',
                                              transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.target.style.background = 'rgba(255, 255, 255, 0.25)';
                                              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                                              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                            }}
                                          >
                                            {navigator.language === 'de-DE' || navigator.language.startsWith('de') ? ',' : '.'}
                                          </button>
                                          <button
                                            type="button"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              ziffernblockClickRef.current = true;
                                            }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              applyEinflussValueChange(einflussname, '');
                                              setTimeout(() => {
                                                ziffernblockClickRef.current = false;
                                                if (ziffernblockInputElementRef.current) {
                                                  ziffernblockInputElementRef.current.focus();
                                                }
                                              }, 0);
                                            }}
                                            style={{
                                              padding: '8px',
                                              border: '1px solid rgba(239, 68, 68, 0.5)',
                                              borderRadius: '4px',
                                              background: 'rgba(239, 68, 68, 0.2)',
                                              color: 'white',
                                              cursor: 'pointer',
                                              fontSize: '0.85rem',
                                              fontWeight: '600',
                                              gridColumn: 'span 2',
                                              transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                                              e.target.style.borderColor = 'rgba(239, 68, 68, 0.7)';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                                              e.target.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                                            }}
                                          >
                                            (C) Clear
                                          </button>
                                        </div>
                                        ) : (
                                          <div style={{ 
                                            color: 'rgba(255, 255, 255, 0.5)', 
                                            fontSize: '0.75rem',
                                            fontStyle: 'italic',
                                            padding: '8px',
                                            visibility: 'visible',
                                            opacity: 1,
                                            transition: 'opacity 0.2s ease',
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            textAlign: 'center',
                                            width: '100%'
                                          }}>
                                            Klicken Sie auf das Eingabefeld für Ziffernblock
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                                </>
                              );
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                
                {/* Vertikale Tabelle (4 Spalten: Einflussgrößen 1-5 | ComboBox 1-5 | Einflussgrößen 6-10 | ComboBox 6-10) wenn EA Kombinationen AUS ist */}
                {!showEaKombinationen && (() => {
                  // Teile Einflussgrößen in zwei Gruppen: 1-5 und 6-10
                  const einflussgroessenList = selectedKuerzel.einflussgroessen.filter(e => e && e.einflussname);
                  const gruppe1 = einflussgroessenList.slice(0, 5); // Einflussgrößen 1-5
                  const gruppe2 = einflussgroessenList.slice(5, 10); // Einflussgrößen 6-10
                  // Immer mindestens 5 Zeilen, damit die Höhe konstant bleibt, auch wenn nicht alle Einflussgrößen vorhanden sind
                  const maxRows = Math.max(5, Math.max(gruppe1.length, gruppe2.length));
                  
                  return (
                    <>
                      <div style={{ marginBottom: '8px', fontSize: '0.85rem', color: 'rgba(239, 68, 68, 0.9)', fontWeight: 600 }}>
                        EA Kombinationen: AUS (Vertikal - 4 Spalten)
                      </div>
                      <div style={{ minHeight: '240px', height: '240px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                          <tbody>
                            {Array.from({ length: maxRows }).map((_, rowIdx) => {
                            const einfluss1 = gruppe1[rowIdx];
                            const einfluss2 = gruppe2[rowIdx];
                            
                            return (
                              <tr key={rowIdx} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)', height: '48px', minHeight: '48px' }}>
                                {/* Spalte 1: Einflussgröße 1-5 (Name + Einheit) */}
                                <td style={{ 
                                  padding: '8px 12px', 
                                  width: '25%',
                                  textAlign: 'right',
                                  verticalAlign: 'middle',
                                  height: '48px',
                                  minHeight: '48px'
                                }}>
                                  {einfluss1 ? (() => {
                                    const einflussname = einfluss1.einflussname.trim();
                                    const einheit = einfluss1.einheit?.trim() || '';
                                    return (
                                      <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                                        {einflussname}{einheit ? ` (${einheit})` : ''}
                                      </div>
                                    );
                                  })() : <div style={{ height: '32px', visibility: 'hidden' }}>&nbsp;</div>}
                                </td>
                                
                                {/* Spalte 2: ComboBox für Einflussgröße 1-5 */}
                                <td style={{ 
                                  padding: '8px 12px', 
                                  width: '25%',
                                  verticalAlign: 'middle',
                                  height: '48px',
                                  minHeight: '48px'
                                }}>
                                  {einfluss1 ? (() => {
                                    const einflussname = einfluss1.einflussname.trim();
                                    const messwerte = getEinflussOptionValues(einfluss1);
                                    const currentValue = einflussgroessenValues?.[einflussname] || '';
                                    return (
                                      <>
                                        <input
                                          type="text"
                                          list={`einfluss-${rowIdx}-1`}
                                          value={currentValue}
                                          onFocus={(e) => {
                                            // Speichere den ursprünglichen Wert
                                            originalEinflussInputValuesRef.current[einflussname] = currentValue;
                                            focusedEinflussInputRef.current = einflussname;
                                            // Leere temporär den Wert, damit alle datalist-Optionen angezeigt werden
                                            if (currentValue) {
                                              applyEinflussValueChange(einflussname, '');
                                            }
                                          }}
                                          onBlur={(e) => {
                                            const currentValue = e.target.value;
                                            const originalValue = originalEinflussInputValuesRef.current[einflussname] || '';
                                            const stateValue = einflussgroessenValues?.[einflussname] || '';
                                            
                                            // Nur wiederherstellen, wenn:
                                            // 1. Der Input-Wert leer ist UND
                                            // 2. Der State-Wert auch leer ist UND
                                            // 3. Ursprünglich ein Wert vorhanden war
                                            // (Das bedeutet, der Benutzer hat nichts eingegeben/ausgewählt)
                                            if (!currentValue && !stateValue && originalValue) {
                                              applyEinflussValueChange(einflussname, originalValue);
                                            }
                                            // Cleanup
                                            if (focusedEinflussInputRef.current === einflussname) {
                                              focusedEinflussInputRef.current = null;
                                            }
                                          }}
                                          onInput={(e) => {
                                            // onInput wird auch bei datalist-Auswahl ausgelöst
                                            const newValue = e.target.value;
                                            // Aktualisiere den ursprünglichen Wert, damit onBlur weiß, dass sich etwas geändert hat
                                            if (originalEinflussInputValuesRef.current[einflussname] !== undefined) {
                                              originalEinflussInputValuesRef.current[einflussname] = newValue;
                                            }
                                            applyEinflussValueChange(einflussname, newValue);
                                          }}
                                          onChange={(e) => {
                                            const newValue = e.target.value;
                                            // Wenn der Benutzer tippt, wird der ursprüngliche Wert überschrieben
                                            if (originalEinflussInputValuesRef.current[einflussname] !== undefined) {
                                              originalEinflussInputValuesRef.current[einflussname] = newValue;
                                            }
                                            applyEinflussValueChange(einflussname, newValue);
                                          }}
                                          placeholder={messwerte.length > 0 ? "Auswahl oder Eingabe..." : "Wert eingeben..."}
                                          style={{
                                            width: '100%',
                                            padding: '6px 8px',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            borderRadius: '4px',
                                            fontSize: '0.875rem',
                                            background: 'rgba(255, 255, 255, 0.15)',
                                            color: 'white',
                                            backdropFilter: 'blur(10px)'
                                          }}
                                        />
                                        <datalist id={`einfluss-${rowIdx}-1`}>
                                          {messwerte.map((mw, mwIdx) => (
                                            <option key={mwIdx} value={mw || ''} />
                                          ))}
                                        </datalist>
                                      </>
                                    );
                                  })() : <div style={{ height: '32px', visibility: 'hidden' }}>&nbsp;</div>}
                                </td>
                                
                                {/* Spalte 3: Einflussgröße 6-10 (Name + Einheit) */}
                                <td style={{ 
                                  padding: '8px 12px', 
                                  width: '25%',
                                  textAlign: 'right',
                                  verticalAlign: 'middle',
                                  height: '48px',
                                  minHeight: '48px'
                                }}>
                                  {einfluss2 ? (() => {
                                    const einflussname = einfluss2.einflussname.trim();
                                    const einheit = einfluss2.einheit?.trim() || '';
                                    return (
                                      <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                                        {einflussname}{einheit ? ` (${einheit})` : ''}
                                      </div>
                                    );
                                  })() : <div style={{ height: '32px', visibility: 'hidden' }}>&nbsp;</div>}
                                </td>
                                
                                {/* Spalte 4: ComboBox für Einflussgröße 6-10 */}
                                <td style={{ 
                                  padding: '8px 12px', 
                                  width: '25%',
                                  verticalAlign: 'middle',
                                  height: '48px',
                                  minHeight: '48px'
                                }}>
                                  {einfluss2 ? (() => {
                                    const einflussname = einfluss2.einflussname.trim();
                                    const messwerte = getEinflussOptionValues(einfluss2);
                                    const currentValue = einflussgroessenValues?.[einflussname] || '';
                                    return (
                                      <>
                                        <input
                                          type="text"
                                          list={`einfluss-${rowIdx}-2`}
                                          value={currentValue}
                                          onFocus={(e) => {
                                            // Speichere den ursprünglichen Wert
                                            originalEinflussInputValuesRef.current[einflussname] = currentValue;
                                            focusedEinflussInputRef.current = einflussname;
                                            // Leere temporär den Wert, damit alle datalist-Optionen angezeigt werden
                                            if (currentValue) {
                                              applyEinflussValueChange(einflussname, '');
                                            }
                                          }}
                                          onBlur={(e) => {
                                            const currentValue = e.target.value;
                                            const originalValue = originalEinflussInputValuesRef.current[einflussname] || '';
                                            const stateValue = einflussgroessenValues?.[einflussname] || '';
                                            
                                            // Nur wiederherstellen, wenn:
                                            // 1. Der Input-Wert leer ist UND
                                            // 2. Der State-Wert auch leer ist UND
                                            // 3. Ursprünglich ein Wert vorhanden war
                                            // (Das bedeutet, der Benutzer hat nichts eingegeben/ausgewählt)
                                            if (!currentValue && !stateValue && originalValue) {
                                              applyEinflussValueChange(einflussname, originalValue);
                                            }
                                            // Cleanup
                                            if (focusedEinflussInputRef.current === einflussname) {
                                              focusedEinflussInputRef.current = null;
                                            }
                                          }}
                                          onInput={(e) => {
                                            // onInput wird auch bei datalist-Auswahl ausgelöst
                                            const newValue = e.target.value;
                                            // Aktualisiere den ursprünglichen Wert, damit onBlur weiß, dass sich etwas geändert hat
                                            if (originalEinflussInputValuesRef.current[einflussname] !== undefined) {
                                              originalEinflussInputValuesRef.current[einflussname] = newValue;
                                            }
                                            applyEinflussValueChange(einflussname, newValue);
                                          }}
                                          onChange={(e) => {
                                            const newValue = e.target.value;
                                            // Wenn der Benutzer tippt, wird der ursprüngliche Wert überschrieben
                                            if (originalEinflussInputValuesRef.current[einflussname] !== undefined) {
                                              originalEinflussInputValuesRef.current[einflussname] = newValue;
                                            }
                                            applyEinflussValueChange(einflussname, newValue);
                                          }}
                                          placeholder={messwerte.length > 0 ? "Auswahl oder Eingabe..." : "Wert eingeben..."}
                                          style={{
                                            width: '100%',
                                            padding: '6px 8px',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            borderRadius: '4px',
                                            fontSize: '0.875rem',
                                            background: 'rgba(255, 255, 255, 0.15)',
                                            color: 'white',
                                            backdropFilter: 'blur(10px)'
                                          }}
                                        />
                                        <datalist id={`einfluss-${rowIdx}-2`}>
                                          {messwerte.map((mw, mwIdx) => (
                                            <option key={mwIdx} value={mw || ''} />
                                          ))}
                                        </datalist>
                                      </>
                                    );
                                  })() : <div style={{ height: '32px', visibility: 'hidden' }}>&nbsp;</div>}
                                </td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
          </>
        )}
        </>
      </header>

      {showConfig && (
        <div className="config-overlay" onClick={handleCloseConfig}>
          <div className="config-panel" onClick={(e) => e.stopPropagation()}>
              <div className="config-header">
                <h3>⚙️ {t('common.config')} <span style={{ fontSize: '0.6em', fontWeight: 'normal', opacity: 0.7 }}>({getVersionString()})</span></h3>
                <button className="btn-close" onClick={handleCloseConfig}>×</button>
              </div>
            <div className="config-tabs-wrapper">
              {/* Scroll-Pfeil links */}
              <button
                className={`config-tabs-scroll-arrow config-tabs-scroll-arrow-left ${tabsCanScrollLeft ? 'visible' : ''}`}
                onClick={() => {
                  if (tabsContainerRef.current) {
                    tabsContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
                  }
                }}
                aria-label="Tabs nach links scrollen"
              >
                ‹
              </button>
              
              <div 
                className="config-tabs" 
                ref={tabsContainerRef}
                onScroll={(e) => {
                  const container = e.target;
                  setTabsCanScrollLeft(container.scrollLeft > 0);
                  setTabsCanScrollRight(
                    container.scrollLeft < container.scrollWidth - container.clientWidth - 1
                  );
                }}
              >
              <button
                className={`config-tab ${configTab === 'view' ? 'active' : ''}`}
                onClick={() => setConfigTab('view')}
              >
                {t('config.tabs.view')}
              </button>
              <button
                className={`config-tab ${configTab === 'kompakt' ? 'active' : ''}`}
                onClick={() => setConfigTab('kompakt')}
              >
                {t('config.tabs.kompakt')}
              </button>
              <button
                className={`config-tab ${configTab === 'sonstiges' ? 'active' : ''}`}
                onClick={() => setConfigTab('sonstiges')}
              >
                {t('config.tabs.sonstiges')}
              </button>
              <button
                className={`config-tab ${configTab === 'keyboard' ? 'active' : ''}`}
                onClick={() => setConfigTab('keyboard')}
              >
                {t('config.tabs.keyboard')}
              </button>
              <button
                className={`config-tab ${configTab === 'timetypes' ? 'active' : ''}`}
                onClick={() => setConfigTab('timetypes')}
              >
                {t('config.tabs.timetypes')}
              </button>
              <button
                className={`config-tab ${configTab === 'ablaufdb' ? 'active' : ''}`}
                onClick={() => setConfigTab('ablaufdb')}
              >
                {t('config.tabs.ablaufdb')}
              </button>
              <button
                className={`config-tab ${configTab === 'einflussdb' ? 'active' : ''}`}
                onClick={() => setConfigTab('einflussdb')}
              >
                {t('config.tabs.einflussdb')}
              </button>
              <button
                className={`config-tab ${configTab === 'dependentlists' ? 'active' : ''}`}
                onClick={() => setConfigTab('dependentlists')}
              >
                {t('config.tabs.dependentlists')}
              </button>
              <button
                className={`config-tab ${configTab === 'datasources' ? 'active' : ''}`}
                onClick={() => setConfigTab('datasources')}
              >
                {t('config.tabs.datasources')}
              </button>
              <button
                className={`config-tab ${configTab === 'license' ? 'active' : ''}`}
                onClick={() => setConfigTab('license')}
              >
                {t('config.tabs.license')}
              </button>
              <button
                className={`config-tab ${configTab === 'help' ? 'active' : ''}`}
                onClick={() => setConfigTab('help')}
              >
                {t('config.tabs.help')}
              </button>
              <button
                className={`config-tab ${configTab === 'feedback' ? 'active' : ''}`}
                onClick={() => setConfigTab('feedback')}
              >
                {t('config.tabs.feedback')}
              </button>
              <button
                className={`config-tab ${configTab === 'about' ? 'active' : ''}`}
                onClick={() => setConfigTab('about')}
              >
                {t('config.tabs.about')}
              </button>
              </div>
              
              {/* Scroll-Pfeil rechts */}
              <button
                className={`config-tabs-scroll-arrow config-tabs-scroll-arrow-right ${tabsCanScrollRight ? 'visible' : ''}`}
                onClick={() => {
                  if (tabsContainerRef.current) {
                    tabsContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
                  }
                }}
                aria-label="Tabs nach rechts scrollen"
              >
                ›
              </button>
            </div>
            <div className="config-content">
              {configTab === 'datasources' && renderDataSourcesHub()}
              {configTab === 'view' && (
                <div className="config-tab-content">
                  <div className="config-item">
                    <label>{t('config.view.grade_position_label')}</label>
                    <select value={gradePosition} onChange={(e) => {
                      setGradePosition(e.target.value);
                      localStorage.setItem('stoppuhr_grade_position', e.target.value);
                    }}>
                      <option value="left">{t('config.view.grade_position_left')}</option>
                      <option value="top">{t('config.view.grade_position_top')}</option>
                    </select>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.grade_position_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={enableDragDrop}
                        onChange={(e) => {                          debugLog('App.jsx:2552', 'enableDragDrop changed', {oldValue:enableDragDrop,newValue:e.target.checked}, 'E');                          setEnableDragDrop(e.target.checked);
                        }}
                      />
                      {t('config.view.drag_drop')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.drag_drop_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showAblaufDb}
                        onChange={(e) => {
                          setShowAblaufDb(e.target.checked);
                          localStorage.setItem('stoppuhr_show_ablaufdb', e.target.checked.toString());
                        }}
                      />
                      {t('config.view.show_ablaufdb')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.show_ablaufdb_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={darkMode}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setDarkMode(newValue);
                          localStorage.setItem('stoppuhr_dark_mode', newValue.toString());
                          if (newValue) {
                            document.body.classList.add('dark-mode');
                          } else {
                            document.body.classList.remove('dark-mode');
                          }
                        }}
                      />
                      {t('config.view.dark_mode')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.dark_mode_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={compactView}
                        onChange={(e) => {
                          setCompactView(e.target.checked);
                          localStorage.setItem('compactView', String(e.target.checked));
                        }}
                      />
                      {t('config.view.compact_view')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.compact_view_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showIndividualTimeInHeader}
                        onChange={(e) => {
                          setShowIndividualTimeInHeader(e.target.checked);
                          localStorage.setItem('stoppuhr_show_individual_time_header', e.target.checked.toString());
                        }}
                      />
                      {t('config.view.show_individual_time_header')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.show_individual_time_header_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showVorderseite}
                        onChange={(e) => {
                          setShowVorderseite(e.target.checked);
                          localStorage.setItem('stoppuhr_show_vorderseite', e.target.checked.toString());
                        }}
                      />
                      {t('config.view.show_vorderseite')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.show_vorderseite_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showEaKombinationen}
                        onChange={(e) => {
                          setShowEaKombinationen(e.target.checked);
                          localStorage.setItem('stoppuhr_show_ea_kombinationen', e.target.checked.toString());
                        }}
                      />
                      {t('config.view.show_ea_kombinationen')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.show_ea_kombinationen_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={useGlobalInfluences}
                        onChange={(e) => {
                          setUseGlobalInfluences(e.target.checked);
                          localStorage.setItem('stoppuhr_use_global_influences', e.target.checked.toString());
                        }}
                      />
                      {t('config.view.use_global_influences')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.view.use_global_influences_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>{t('config.view.auto_save_label')}</label>
                    <select
                      value={autoSaveTimer}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setAutoSaveTimer(value);
                        localStorage.setItem('autoSaveTimer', String(value));
                      }}
                    >
                      <option value="0">{t('config.view.auto_save_disabled')}</option>
                      <option value="1">{t('config.view.auto_save_minute_one')}</option>
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <option key={n} value={n}>{t('config.view.auto_save_minute_other', { count: n })}</option>
                      ))}
                    </select>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {autoSaveTimer === 0
                        ? t('config.view.auto_save_off_desc')
                        : t('config.view.auto_save_on_desc', { count: autoSaveTimer })
                      }
                    </p>
                  </div>
                </div>
              )}
              {configTab === 'kompakt' && (
                <div className="config-tab-content">
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showResumeNumber}
                        onChange={(e) => setShowResumeNumber(e.target.checked)}
                      />
                      {t('config.kompakt.show_wa_nr')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.kompakt.show_wa_nr_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showActivity}
                        onChange={(e) => setShowActivity(e.target.checked)}
                      />
                      {t('config.kompakt.show_activity')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.kompakt.show_activity_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showGrade}
                        onChange={(e) => setShowGrade(e.target.checked)}
                      />
                      {t('config.kompakt.show_grade')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.kompakt.show_grade_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showIndividualTime}
                        onChange={(e) => setShowIndividualTime(e.target.checked)}
                      />
                      {t('config.kompakt.show_individual_time')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.kompakt.show_individual_time_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showProgressTime}
                        onChange={(e) => setShowProgressTime(e.target.checked)}
                      />
                      {t('config.kompakt.show_progress_time')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.kompakt.show_progress_time_desc')}
                    </p>
                  </div>
                  <div className="config-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={showEinflussgroessenCompact}
                        onChange={(e) => {
                          setShowEinflussgroessenCompact(e.target.checked);
                          localStorage.setItem('stoppuhr_show_einflussgroessen_compact', e.target.checked.toString());
                        }}
                      />
                      {t('config.kompakt.show_einflussgroessen')}
                    </label>
                    <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                      {t('config.kompakt.show_einflussgroessen_desc')}
                    </p>
                  </div>
                </div>
              )}
              {configTab === 'keyboard' && (
                <div className="config-tab-content">
                  <div className="keyboard-config-content">
                    <div className="keyboard-config-list">
                      <div className="config-item" style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={hapticFeedbackEnabled}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setHapticFeedbackEnabled(newValue);
                              localStorage.setItem('stoppuhr_haptic_feedback_enabled', newValue.toString());
                            }}
                          />
                          {t('config.keyboard.haptic_feedback')}
                        </label>
                        <p style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>
                          {t('config.keyboard.haptic_feedback_desc')}
                        </p>
                      </div>
                      <div className="keyboard-config-item">
                        <label className="keyboard-config-label">
                          <span className="grade-label">{t('config.keyboard.key_interruption')}</span>
                          {editingKey === 'interruption' ? (
                            <div className="key-capture">
                              <input
                                type="text"
                                className="key-capture-input"
                                placeholder={t('config.keyboard.key_placeholder')}
                                autoFocus
                                onKeyDown={(e) => {
                                  e.preventDefault();
                                  const key = e.ctrlKey ? `Ctrl+${e.key}` : e.key;
                                  setKeyboardConfig(prev => ({ ...prev, interruption: key }));
                                  setEditingKey(null);
                                }}
                                onBlur={() => setEditingKey(null)}
                              />
                              <span className="key-capture-hint">{t('config.keyboard.key_capture_hint')}</span>
                            </div>
                          ) : (
                            <button
                              className="btn-keyboard-shortcut"
                              onClick={() => setEditingKey('interruption')}
                            >
                              {keyboardConfig.interruption || t('config.keyboard.not_assigned')}
                            </button>
                          )}
                        </label>
                      </div>
                      {grades.map((grade) => (
                        <div key={grade} className="keyboard-config-item">
                          <label className="keyboard-config-label">
                            <span className="grade-label">{t('config.keyboard.key_grade', { grade })}</span>
                            {editingKey === grade ? (
                              <div className="key-capture">
                                <input
                                  type="text"
                                  className="key-capture-input"
                                  placeholder={t('config.keyboard.key_placeholder')}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    e.preventDefault();
                                    const key = e.ctrlKey ? `Ctrl+${e.key}` : e.key;
                                    setKeyboardConfig(prev => ({ ...prev, [grade]: key }));
                                    setEditingKey(null);
                                  }}
                                  onBlur={() => setEditingKey(null)}
                                />
                                <span className="key-capture-hint">{t('config.keyboard.key_capture_hint')}</span>
                              </div>
                            ) : (
                              <button
                                className="btn-keyboard-shortcut"
                                onClick={() => setEditingKey(grade)}
                              >
                                {keyboardConfig[grade] || t('config.keyboard.not_assigned')}
                              </button>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="keyboard-config-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        const defaultConfig = {};
                        grades.forEach((grade, index) => {
                          defaultConfig[grade] = `Ctrl+${index + 1}`;
                        });
                        defaultConfig['interruption'] = 'Ctrl+0';
                        setKeyboardConfig(defaultConfig);
                      }}
                    >
                      {t('config.keyboard.restore_default')}
                    </button>
                  </div>
                </div>
              )}
              {configTab === 'help' && (
                <div className="config-tab-content">
                  <div className="help-content">
                    <h4>{t('help.title')}</h4>
                    <p style={{ marginBottom: '20px', color: '#6b7280' }}>
                      {t('help.intro')}
                    </p>

                    <div className="help-section">
                      <h5>{t('help.header_buttons.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#header-buttons-oben-rechts" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">❓</div>
                        <div className="help-item-content">
                          <strong>{t('help.header_buttons.help_title')}</strong>
                          <p>{t('help.header_buttons.help_desc')}</p>
                          <span className="help-shortcut">{t('help.no_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">⚙️</div>
                        <div className="help-item-content">
                          <strong>{t('help.header_buttons.config_title')}</strong>
                          <p>{t('help.header_buttons.config_desc')}</p>
                          <span className="help-shortcut">{t('help.header_buttons.config_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">←</div>
                        <div className="help-item-content">
                          <strong>{t('help.header_buttons.back_title')}</strong>
                          <p>{t('help.header_buttons.back_desc')}</p>
                          <span className="help-shortcut">{t('help.no_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.stoppuhr.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#erfassungs-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">▶</div>
                        <div className="help-item-content">
                          <strong>{t('help.stoppuhr.start_title')}</strong>
                          <p>{t('help.stoppuhr.start_desc')}</p>
                          <span className="help-shortcut">{t('help.stoppuhr.start_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">⏸</div>
                        <div className="help-item-content">
                          <strong>{t('help.stoppuhr.stop_title')}</strong>
                          <p>{t('help.stoppuhr.stop_desc')}</p>
                          <span className="help-shortcut">{t('help.stoppuhr.stop_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">↻</div>
                        <div className="help-item-content">
                          <strong>{t('help.stoppuhr.reset_title')}</strong>
                          <p>{t('help.stoppuhr.reset_desc')}</p>
                          <span className="help-shortcut">{t('help.no_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.leistungsgrad.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#efficiency-score-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <p style={{ marginBottom: '12px', color: '#6b7280' }}>
                        {t('help.leistungsgrad.intro')}
                      </p>
                      <div className="help-grid">
                        <div className="help-item-small">
                          <strong>0%</strong> - {t('help.leistungsgrad.unterbrechung')}
                          <span className="help-shortcut-small">{t('help.leistungsgrad.shortcut_0')}</span>
                        </div>
                        {['row_80', 'row_90', 'row_100', 'row_105', 'row_110', 'row_115', 'row_120', 'row_125'].map((row, i) => (
                          <div key={row} className="help-item-small">
                            <strong>{t(`help.leistungsgrad.${row}`)}</strong>
                            <span className="help-shortcut-small">{t(`help.leistungsgrad.shortcut_${i + 1}`)}</span>
                          </div>
                        ))}
                      </div>
                      <p style={{ marginTop: '12px', fontSize: '0.85rem', color: '#9ca3af', fontStyle: 'italic' }}>
                        {t('help.keyboard_customize')}
                      </p>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.notiz.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#notiz-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">🎤</div>
                        <div className="help-item-content">
                          <strong>{t('help.notiz.voice_title')}</strong>
                          <p>{t('help.notiz.voice_desc')}</p>
                          <span className="help-shortcut">{t('help.no_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">📝</div>
                        <div className="help-item-content">
                          <strong>{t('help.notiz.note_title')}</strong>
                          <p>{t('help.notiz.note_desc')}</p>
                          <span className="help-shortcut">{t('help.notiz.note_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.daten.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#daten-management-buttons" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">☁️</div>
                        <div className="help-item-content">
                          <strong>{t('help.daten.upload_title')}</strong>
                          <p>{t('help.daten.upload_desc')}</p>
                          <span className="help-shortcut">{t('help.daten.upload_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">📊</div>
                        <div className="help-item-content">
                          <strong>{t('help.daten.auswertung_title')}</strong>
                          <p>{t('help.daten.auswertung_desc')}</p>
                          <span className="help-shortcut">{t('help.daten.auswertung_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">💾</div>
                        <div className="help-item-content">
                          <strong>{t('help.daten.save_title')}</strong>
                          <p>{t('help.daten.save_desc')}</p>
                          <span className="help-shortcut">{t('help.daten.save_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.auswertung.title')}</h5>
                      <div style={{ marginBottom: '8px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#auswertung" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link_auswertung')}
                        </a>
                      </div>
                      <p style={{ marginBottom: '8px', color: '#6b7280', fontSize: '0.9rem' }}>
                        {t('help.auswertung.pivot_stat_desc')}
                      </p>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        {t('help.auswertung.stats_basics_prefix')} <a href="https://de.wikipedia.org/wiki/Deskriptive_Statistik" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>{t('help.auswertung.stats_wikipedia_link')}</a>
                      </p>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.messpunkt.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#insight-aktionen" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">📋</div>
                        <div className="help-item-content">
                          <strong>{t('help.messpunkt.compact_title')}</strong>
                          <p>{t('help.messpunkt.compact_desc')}</p>
                          <span className="help-shortcut">{t('help.messpunkt.compact_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.vorderseite.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#vorderseite" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">📄</div>
                        <div className="help-item-content">
                          <strong>{t('help.vorderseite.front_title')}</strong>
                          <p>{t('help.vorderseite.front_desc')}</p>
                          <span className="help-shortcut">{t('help.vorderseite.front_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.restricted.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#control-center" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <p style={{ marginBottom: '8px', color: '#6b7280', fontSize: '0.9rem' }}>
                        <Trans i18nKey="help.restricted.desc" components={{ strong: <strong /> }} />
                      </p>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.config_options.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#control-center" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">⚙️</div>
                        <div className="help-item-content">
                          <strong>{t('help.config_options.ansicht_title')}</strong>
                          <p>{t('help.config_options.ansicht_desc')}</p>
                          <span className="help-shortcut">{t('help.config_options.ansicht_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">⚙️</div>
                        <div className="help-item-content">
                          <strong>{t('help.config_options.kompakt_title')}</strong>
                          <p>{t('help.config_options.kompakt_desc')}</p>
                          <span className="help-shortcut">{t('help.config_options.kompakt_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">⚙️</div>
                        <div className="help-item-content">
                          <strong>{t('help.config_options.sonstiges_title')}</strong>
                          <p>{t('help.config_options.sonstiges_desc')}</p>
                          <span className="help-shortcut">{t('help.config_options.sonstiges_shortcut')}</span>
                        </div>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">📤</div>
                        <div className="help-item-content">
                          <strong>{t('help.config_options.telemetry_title')}</strong>
                          <p>{t('help.config_options.telemetry_desc')}</p>
                          <span className="help-shortcut">{t('help.config_options.telemetry_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-section">
                      <h5>{t('help.session.title')}</h5>
                      <div style={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                        <a href="/ANWENDERHANDBUCH.html#aktivitäten-verwalten" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                          {t('help.handbook_link')}
                        </a>
                      </div>
                      <div className="help-item">
                        <div className="help-button-icon">➕</div>
                        <div className="help-item-content">
                          <strong>{t('help.session.new_title')}</strong>
                          <p>{t('help.session.new_desc')}</p>
                          <span className="help-shortcut">{t('help.no_shortcut')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="help-footer">
                      <p>
                        {t('help.footer_tip')}
                      </p>
                      <p style={{ marginTop: '20px', fontSize: '0.85rem', color: '#9ca3af', textAlign: 'center' }}>
                        {t('app.copyright')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {configTab === 'feedback' && (
                <div className="config-tab-content">
                  <div className="config-item">
                    <h4>{t('config.feedback.title')}</h4>
                    <p>{t('config.feedback.intro')}</p>
                    <p>{t('config.feedback.draft_hint')}</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowFeedbackDialog(true)}
                      disabled={!backendUrl || !email}
                    >
                      {t('config.feedback.new_button')}
                    </button>
                  </div>
                  {backendUrl && email && (
                    <div className="config-item">
                      <h4>{t('config.feedback.my_title')}</h4>
                      <p>{t('config.feedback.my_desc')}</p>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowFeedbackStatusNotification(true)}
                      >
                        {t('config.feedback.show_status')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {configTab === 'about' && (
                <div className="config-tab-content">
                  <div className="config-item">
                    <h4>{t('config.about.title')}</h4>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>{getFooterText(t).productName}</strong> {getVersionString()}
                    </p>
                    <p style={{ fontSize: '0.9rem', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '12px' }}>
                      {t('config.about.copyright')}: {getFooterText(t).copyright}
                    </p>
                    <p style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '6px' }}>{t('config.about.tech_versions_title')}</p>
                    <ul style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '16px', paddingLeft: '20px' }}>
                      {Object.entries(getTechVersions()).map(([name, ver]) => (
                        <li key={name}>{name}: {ver}</li>
                      ))}
                    </ul>
                    <p style={{ fontSize: '0.8rem', color: darkMode ? '#6b7280' : '#9ca3af', marginBottom: '12px' }}>
                      {t('config.about.update_running_hint')}
                    </p>
                    <div className="config-item" style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAboutUpdateClick}
                        disabled={aboutUpdateStatus === 'checking' || aboutUpdateStatus === 'applying'}
                        title={t('config.about.update_button')}
                      >
                        {aboutUpdateStatus === 'checking' && t('config.about.update_checking')}
                        {aboutUpdateStatus === 'applying' && t('config.about.update_applying')}
                        {aboutUpdateStatus === 'none' && t('config.about.update_none')}
                        {aboutUpdateStatus === 'dev' && t('config.about.update_dev_hint')}
                        {!aboutUpdateStatus && t('config.about.update_button')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleSecurityCheckClick}
                        title={t('config.about.security_check_button')}
                      >
                        {t('config.about.security_check_button')}
                      </button>
                      {showCacheClearButton && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCacheClearClick}
                          title={t('config.about.cache_clear_hint')}
                        >
                          {t('config.about.cache_clear_button')}
                        </button>
                      )}
                    </div>
                    {securityCheckResult && (
                      <div style={{ marginTop: '12px', padding: '12px', background: darkMode ? 'rgba(55,65,81,0.5)' : '#f3f4f6', borderRadius: '8px', fontSize: '0.85rem' }}>
                        <p style={{ fontWeight: 600, marginBottom: '8px' }}>{t('config.about.security_check_title')}</p>
                        <p>{securityCheckResult.secureContext ? t('config.about.security_secure_context') : t('config.about.security_insecure_context')}</p>
                        <p>{t('config.about.security_protocol')}: {securityCheckResult.protocol}</p>
                        <p>{t('config.about.security_service_worker')}: {securityCheckResult.hasSW ? t('config.about.security_sw_active') : t('config.about.security_sw_none')}</p>
                        {securityCheckResult.cveData && (
                          <>
                            <p style={{ fontWeight: 600, marginTop: '12px' }}>{t('config.about.security_cve_title')}</p>
                            {Object.entries(securityCheckResult.cveData).map(([pkg, { vulns }]) => (
                              <p key={pkg}>{pkg}: {vulns.length === 0 ? t('config.about.security_cve_ok') : t('config.about.security_cve_found', { count: vulns.length })} {vulns.length > 0 && vulns.slice(0, 3).map((v) => v.id).join(', ')}</p>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {configTab === 'sonstiges' && (
                <div className="config-tab-content">
                  <div className="config-item">
                    <h4>{t('config.language.title')}</h4>
                    <label>
                      <select
                        value={i18n.language}
                        onChange={(e) => {
                          const newLang = e.target.value;
                          i18n.changeLanguage(newLang);
                          try {
                            localStorage.setItem('stoppuhr_locale', newLang);
                          } catch (err) { /* ignore */ }
                        }}
                        style={{ minWidth: '200px', padding: '6px 10px', fontSize: '0.95rem' }}
                      >
                        <option value="de">{t('config.language.de')}</option>
                        <option value="de-neu">{t('config.language.de_neu')}</option>
                        <option value="en">{t('config.language.en')}</option>
                      </select>
                    </label>
                    <p style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '4px' }}>
                      {t('config.sonstiges.language_desc')}
                    </p>
                  </div>
                  <div className="config-item" style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                    <h4>{t('config.sonstiges.telemetry_title')}</h4>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={telemetryEnabled}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setTelemetryEnabled(newValue);
                          try {
                            localStorage.setItem('stoppuhr_telemetry_enabled', newValue.toString());
                          } catch (err) { /* ignore */ }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span>{t('config.sonstiges.telemetry_checkbox')}</span>
                    </label>
                    <p style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '4px', marginLeft: '28px' }}>
                      {t('config.sonstiges.telemetry_description')}
                    </p>
                    <p style={{ fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280', marginTop: '12px', marginLeft: '28px' }}>
                      <button
                        type="button"
                        onClick={(e) => { if (e.shiftKey) buildAndSendTelemetry({ showFeedback: true }); }}
                        title={t('config.sonstiges.telemetry_button_title')}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                          borderRadius: '6px',
                          background: darkMode ? '#374151' : '#f3f4f6',
                          color: darkMode ? '#e5e7eb' : '#374151',
                        }}
                      >
                        {t('config.sonstiges.telemetry_button')}
                      </button>
                    </p>
                  </div>
                  <div className="config-item" style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}` }}>
                    <h4>{t('config.sonstiges.erfassung_title')}</h4>
                    <div className="config-item">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={ohneLeistungsgradErfassen}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setOhneLeistungsgradErfassen(newValue);
                            try {
                              localStorage.setItem('stoppuhr_ohne_leistungsgrad_erfassen', newValue.toString());
                            } catch (err) { /* ignore */ }
                          }}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>{t('config.sonstiges.ohne_leistungsgrad')}</span>
                      </label>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px', marginLeft: '28px' }}>
                        {t('config.sonstiges.ohne_leistungsgrad_desc')}
                      </p>
                    </div>
                  </div>
                  <div className="config-item" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                    <h4>{t('config.sonstiges.nachkommastellen_title')}</h4>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                      {t('config.sonstiges.nachkommastellen_desc')}
                    </p>
                    <div className="config-item">
                      <label>{t('config.sonstiges.nachkommastellen_vorderseite')}</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={decimalPlacesVorderseite}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value) || 4;
                          setDecimalPlacesVorderseite(newValue);
                          // Aktualisiere vorderseiteData
                          if (vorderseiteData) {
                            setVorderseiteData({
                              ...vorderseiteData,
                              decimal_places_vorderseite: newValue
                            });
                          }
                        }}
                        className="form-input"
                        style={{ width: '100px' }}
                      />
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.sonstiges.nachkommastellen_vorderseite_desc')}
                      </p>
                    </div>
                    <div className="config-item">
                      <label>{t('config.sonstiges.nachkommastellen_sollzeiten')}</label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={decimalPlacesSollzeiten}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value) || 4;
                          setDecimalPlacesSollzeiten(newValue);
                          // Aktualisiere vorderseiteData
                          if (vorderseiteData) {
                            setVorderseiteData({
                              ...vorderseiteData,
                              decimal_places_sollzeiten: newValue
                            });
                          }
                        }}
                        className="form-input"
                        style={{ width: '100px' }}
                      />
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.sonstiges.nachkommastellen_sollzeiten_desc')}
                      </p>
                    </div>
                  </div>
                  {process.env.NODE_ENV !== 'production' && (
                    <div className="config-item" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                      <h4>Debug-Logging</h4>
                      <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                        Aktivieren Sie Debug-Logging, um detaillierte Logs an den Agent-Server zu senden. Nur für Development verfügbar.
                      </p>
                      <div className="config-item">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enableDebugLogs}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              setEnableDebugLogs(newValue);
                              localStorage.setItem('stoppuhr_enable_debug_logs', newValue.toString());
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span>Debug-Logs aktivieren</span>
                        </label>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px', marginLeft: '28px' }}>
                          Wenn aktiviert, werden Debug-Logs an den Agent-Server (http://127.0.0.1:7242) gesendet. 
                          Deaktivieren Sie dies für bessere Performance in Production.
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="config-item" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                    <h4>{t('config.sonstiges.speicherbelegung_title')}</h4>
                    <p style={{ fontSize: '0.9rem', color: darkMode ? '#9ca3af' : '#6b7280', marginBottom: '8px' }}>
                      {t('config.sonstiges.speicherbelegung_desc')}
                    </p>
                    {storageEstimate === null && <p style={{ fontSize: '0.9rem', color: darkMode ? '#9ca3af' : '#6b7280' }}>{t('config.sonstiges.speicherbelegung_loading')}</p>}
                    {storageEstimate && storageEstimate.error && <p style={{ fontSize: '0.9rem', color: darkMode ? '#9ca3af' : '#6b7280' }}>{storageEstimate.error}</p>}
                    {storageEstimate && !storageEstimate.error && (
                      <p style={{ fontSize: '1rem', fontWeight: 500 }}>
                        {t('config.sonstiges.speicherbelegung_usage', {
                          used: (storageEstimate.usage / (1024 * 1024)).toFixed(1),
                          quota: (storageEstimate.quota / (1024 * 1024)).toFixed(1)
                        })}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {configTab === 'ablaufdb' && (
                <div className="config-tab-content">
                  <div className="ablaufdb-config">
                    <h4>{t('config.ablaufdb.section_title')}</h4>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                      {t('config.ablaufdb.section_desc')}
                    </p>
                    <div className="config-item">
                      <label>{t('config.ablaufdb.name_label')}</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={ablaufDbName}
                          onChange={(e) => {
                            setAblaufDbName(e.target.value);
                            localStorage.setItem('stoppuhr_ablaufdb_name', e.target.value);
                          }}
                          className="form-input"
                          placeholder={t('config.ablaufdb.name_placeholder')}
                          style={{ flex: 1 }}
                        />
                      </div>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.ablaufdb.name_desc')}
                      </p>
                    </div>
                    <div className="config-item">
                      <label>{t('config.ablaufdb.path_label')}</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {ablaufDbPath && ablaufDbPath.startsWith('[Lokale Datei:') ? (
                            <input
                              type="text"
                              value=""
                              onChange={(e) => {
                                setAblaufDbPath(e.target.value);
                                localStorage.setItem('stoppuhr_ablaufdb_path', e.target.value);
                              }}
                              className="form-input"
                              placeholder={t('config.ablaufdb.path_placeholder_enter')}
                              style={{ flex: 1 }}
                            />
                          ) : (
                            <input
                              type="text"
                              value={ablaufDbPath || ''}
                              onChange={(e) => {
                                setAblaufDbPath(e.target.value);
                                localStorage.setItem('stoppuhr_ablaufdb_path', e.target.value);
                              }}
                              className="form-input"
                              placeholder={t('config.ablaufdb.path_placeholder')}
                              style={{ flex: 1 }}
                            />
                          )}
                          <button
                            className="btn btn-secondary"
                            onClick={() => ablaufDbFileInputRef.current?.click()}
                          >
                            {t('config.ablaufdb.button_select')}
                          </button>
                          <input
                            type="file"
                            ref={ablaufDbFileInputRef}
                            onChange={handleAblaufDbFileSelect}
                            accept=".xml"
                            style={{ display: 'none' }}
                          />
                        </div>
                        {ablaufDbPath && ablaufDbPath.startsWith('[Lokale Datei:') && (
                          <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontStyle: 'italic', padding: '8px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                            ⚠️ {t('config.ablaufdb.path_local_hint', { path: ablaufDbPath })}
                          </div>
                        )}
                      </div>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.ablaufdb.path_desc')}
                      </p>
                    </div>
                    {ablaufDbData && ablaufDbData.length > 0 && (
                      <div className="config-item">
                        <label>{t('config.ablaufdb.status_label')}</label>
                        <div className="ablaufdb-status-success" style={{ padding: '12px', background: '#d1fae5', borderRadius: '6px', border: '1px solid #10b981' }}>
                          <strong>{t('config.ablaufdb.status_loaded')}</strong><br/>
                          <span className="ablaufdb-kuerzel-count" style={{ fontSize: '0.9rem', color: '#065f46' }}>
                            {t('config.ablaufdb.status_kuerzel_available', { count: ablaufDbData.length })}
                          </span>
                        </div>
                      </div>
                    )}
                    {(!ablaufDbData || ablaufDbData.length === 0) && (
                      <div className="config-item">
                        <label>{t('config.ablaufdb.status_label')}</label>
                        <div className="ablaufdb-status-error" style={{ padding: '12px', background: '#fee2e2', borderRadius: '6px', border: '1px solid #ef4444' }}>
                          <strong>{t('config.ablaufdb.status_not_loaded')}</strong><br/>
                          <span style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                            {t('config.ablaufdb.status_please_load')}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="form-actions" style={{ marginTop: '20px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          const result = await loadAblaufDbXml();
                          if (result.success) {
                            alert(t('alerts.ablaufdb_loaded', { count: result.data.length }));
                          } else {
                            alert(t('alerts.ablaufdb_error', { msg: result.error }));
                          }
                        }}
                        title={t('config.ablaufdb.button_load_title')}
                      >
                        {t('config.ablaufdb.button_load')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowAblaufDbEditor(true);
                        }}
                        disabled={!ablaufDbData || ablaufDbData.length === 0}
                        title={t('config.ablaufdb.button_edit_title')}
                      >
                        {t('config.ablaufdb.button_edit')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleAblaufDbUnload}
                        title={t('ablaufdb.without_db_title')}
                      >
                        {t('config.ablaufdb.button_without_db')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {configTab === 'einflussdb' && (
                <div className="config-tab-content">
                  <div className="ablaufdb-config">
                    <h4>{t('config.einflussdb.section_title')}</h4>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                      {t('config.einflussdb.section_desc')}
                    </p>
                    <div className="config-item">
                      <label>{t('config.einflussdb.name_label')}</label>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={einflussDbName}
                          onChange={(e) => {
                            setEinflussDbName(e.target.value);
                            localStorage.setItem('stoppuhr_einflussdb_name', e.target.value);
                          }}
                          className="form-input"
                          placeholder={t('config.einflussdb.name_placeholder')}
                          style={{ flex: 1 }}
                        />
                      </div>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.einflussdb.name_desc')}
                      </p>
                    </div>
                    <div className="config-item">
                      <label>{t('config.einflussdb.path_label')}</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {einflussDbPath && einflussDbPath.startsWith('[Lokale Datei:') ? (
                            <input
                              type="text"
                              value=""
                              onChange={(e) => {
                                setEinflussDbPath(e.target.value);
                                localStorage.setItem('stoppuhr_einflussdb_path', e.target.value);
                              }}
                              className="form-input"
                              placeholder={t('config.einflussdb.path_placeholder_enter')}
                              style={{ flex: 1 }}
                            />
                          ) : (
                            <input
                              type="text"
                              value={einflussDbPath || ''}
                              onChange={(e) => {
                                setEinflussDbPath(e.target.value);
                                localStorage.setItem('stoppuhr_einflussdb_path', e.target.value);
                              }}
                              className="form-input"
                              placeholder={t('config.einflussdb.path_placeholder')}
                              style={{ flex: 1 }}
                            />
                          )}
                          <button
                            className="btn btn-secondary"
                            onClick={() => einflussDbFileInputRef.current?.click()}
                          >
                            {t('config.einflussdb.button_select')}
                          </button>
                          <input
                            type="file"
                            ref={einflussDbFileInputRef}
                            onChange={handleEinflussDbFileSelect}
                            accept=".xml"
                            style={{ display: 'none' }}
                          />
                        </div>
                        {einflussDbPath && einflussDbPath.startsWith('[Lokale Datei:') && (
                          <div style={{ fontSize: '0.85rem', color: '#f59e0b', fontStyle: 'italic', padding: '8px', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                            ⚠️ {t('config.einflussdb.path_local_hint', { path: einflussDbPath })}
                          </div>
                        )}
                      </div>
                      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                        {t('config.einflussdb.path_desc')}
                      </p>
                    </div>
                    {einflussDbData && einflussDbData.length > 0 && (
                      <div className="config-item">
                        <label>{t('config.einflussdb.status_label')}</label>
                        <div className="ablaufdb-status-success" style={{ padding: '12px', background: '#d1fae5', borderRadius: '6px', border: '1px solid #10b981' }}>
                          <strong>{t('config.einflussdb.status_loaded')}</strong><br/>
                          <span className="ablaufdb-kuerzel-count" style={{ fontSize: '0.9rem', color: '#065f46' }}>
                            {t('config.einflussdb.status_einfluss_available', { count: einflussDbData.length })}
                          </span>
                        </div>
                      </div>
                    )}
                    {(!einflussDbData || einflussDbData.length === 0) && (
                      <div className="config-item">
                        <label>{t('config.einflussdb.status_label')}</label>
                        <div className="ablaufdb-status-error" style={{ padding: '12px', background: '#fee2e2', borderRadius: '6px', border: '1px solid #ef4444' }}>
                          <strong>{t('config.einflussdb.status_not_loaded')}</strong><br/>
                          <span style={{ fontSize: '0.9rem', color: '#991b1b' }}>
                            {t('config.einflussdb.status_please_load')}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="form-actions" style={{ marginTop: '20px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          const result = await loadEinflussDbXml();
                          if (result.success) {
                            alert(t('alerts.einflussdb_loaded', { count: result.data.length }));
                          } else {
                            alert(t('alerts.einflussdb_error', { msg: result.error }));
                          }
                        }}
                        title={t('config.einflussdb.button_load_title')}
                      >
                        {t('config.einflussdb.button_load')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={openEinflussDbEditor}
                        disabled={!einflussDbData || einflussDbData.length === 0}
                        title={t('config.einflussdb.button_edit_title')}
                      >
                        {t('config.einflussdb.button_edit')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleEinflussDbUnload}
                        title={t('config.einflussdb.button_without_db_title')}
                      >
                        {t('config.einflussdb.button_without_db')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {configTab === 'dependentlists' && (
                <div className="config-tab-content">
                  <div className="ablaufdb-config">
                    <h4>{t('config.dependentlists.section_title')}</h4>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                      {t('config.dependentlists.section_desc')}
                    </p>

                    {activeDependentSheet && (
                      <>
                        <div className="config-item">
                          <label>{t('config.dependentlists.sheet_name_label')}</label>
                          <input
                            type="text"
                            className="form-input"
                            value={activeDependentSheet.name || ''}
                            onChange={(e) => handleRenameDependentSheet(activeDependentSheet.id, e.target.value)}
                            placeholder={t('config.dependentlists.sheet_name_placeholder')}
                          />
                        </div>
                        {renderDependentListsGrid()}
                        {dependentListsError && (
                          <div style={{ padding: '10px 12px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b', border: '1px solid #ef4444' }}>
                            {dependentListsError}
                          </div>
                        )}
                        {renderDependentSheetBar()}
                      </>
                    )}
                  </div>
                </div>
              )}
              {configTab === 'license' && (
                <div className="config-tab-content">
                  <div className="config-item">
                    <label>Lizenz-Status:</label>
                    <div className="license-status">
                      {licenseInfo && licenseInfo.license_key ? (
                        // Wenn licenseInfo vorhanden ist, zeige Status basierend auf is_active
                        licenseInfo.is_active ? (
                          <span className="license-valid">✅ Lizenz aktiv</span>
                        ) : (
                          <span className="license-invalid">❌ Lizenz deaktiviert</span>
                        )
                      ) : (
                        // Wenn keine licenseInfo vorhanden ist, zeige basierend auf licenseStatus
                        <>
                          {licenseStatus === null && <span className="license-unknown">Nicht geprüft</span>}
                          {licenseStatus === false && <span className="license-invalid">❌ Keine Lizenz</span>}
                          {licenseStatus === true && <span className="license-valid">✅ Lizenz aktiv</span>}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="config-item">
                    <button
                      className="btn btn-primary license-action-btn"
                      onClick={checkLicense}
                      disabled={isCheckingLicense || !email || !backendUrl}
                      title="Lizenz prüfen - Prüft den Status der Lizenz im Backend. Erfordert Email und Backend-URL"
                    >
                      {isCheckingLicense ? 'Prüfe...' : '🔍 Prüfen'}
                    </button>
                  </div>
                  <div className="config-item license-buttons-right">
                    <button
                      className="btn btn-import-license license-action-btn"
                      onClick={() => licenseFileInputRef.current?.click()}
                      disabled={isImportingLicense || !backendUrl}
                      title="Lizenz importieren - Importiert eine .license Datei vom Hersteller. Erfordert Backend-URL"
                    >
                      {isImportingLicense ? 'Importiere...' : '📥 Importieren'}
                    </button>
                    {licenseStatus === true && (
                      <button
                        className="btn btn-danger license-action-btn"
                        onClick={handleRemoveLicense}
                        title="Lizenz entfernen - Entfernt die aktuelle Lizenz. Die Anwendung läuft danach im Demo-Modus"
                      >
                        🗑️ Entfernen
                      </button>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={licenseFileInputRef}
                    onChange={handleLicenseFileChange}
                    accept=".license"
                    style={{ display: 'none' }}
                  />
                  {licenseInfo && (
                    <div className={`license-info-box ${licenseInfo.valid && licenseInfo.is_active ? 'license-valid-box' : 'license-invalid-box'}`} style={{ marginTop: '12px', padding: '12px', backgroundColor: licenseInfo.valid && licenseInfo.is_active ? '#d1fae5' : '#fee2e2', borderRadius: '8px', fontSize: '0.9rem', border: `2px solid ${licenseInfo.valid && licenseInfo.is_active ? '#10b981' : '#ef4444'}` }}>
                      {licenseInfo.license_key && (
                        <div className="license-key-section" style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <strong className="license-key-label">Lizenz-Key:</strong>
                            <button
                              id="copy-license-key-btn"
                              onClick={handleCopyLicenseKey}
                              style={{
                                padding: '4px 12px',
                                fontSize: '0.85rem',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '500',
                                transition: 'background-color 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              onMouseEnter={(e) => {
                                if (e.target.style.backgroundColor !== '#10b981') {
                                  e.target.style.backgroundColor = '#2563eb';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (e.target.style.backgroundColor !== '#10b981') {
                                  e.target.style.backgroundColor = '#3b82f6';
                                }
                              }}
                            >
                              📋 Kopieren
                            </button>
                          </div>
                          <code className="license-key-code" style={{ 
                            fontSize: '1.2rem', 
                            backgroundColor: '#ffffff', 
                            padding: '8px 10px', 
                            borderRadius: '6px',
                            display: 'block',
                            wordBreak: 'break-all',
                            fontFamily: 'monospace',
                            color: '#1f2937',
                            border: '1px solid rgba(0,0,0,0.1)',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                            fontWeight: 'bold',
                            textAlign: 'center'
                          }}>
                            {licenseInfo.license_key}
                          </code>
                        </div>
                      )}
                      <div className="license-details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                        <div><strong className="license-detail-label">Produktcode:</strong> <span className="license-detail-value">{licenseInfo.product_code || '-'}</span></div>
                        <div><strong className="license-detail-label">Typ:</strong> <span className="license-detail-value">{licenseInfo.license_type || '-'}</span></div>
                      </div>
                      {licenseInfo.expires_at && (
                        <div className="license-detail-row" style={{ marginBottom: '8px' }}>
                          <strong className="license-detail-label">Läuft ab:</strong> <span className="license-detail-value">{new Date(licenseInfo.expires_at).toLocaleDateString('de-DE', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}</span>
                        </div>
                      )}
                      <div className="license-detail-row" style={{ marginBottom: '8px' }}>
                        <strong className="license-detail-label">Status:</strong> <span className={`license-status-badge ${licenseInfo.is_active ? 'license-active' : 'license-inactive'}`} style={{ 
                          color: licenseInfo.is_active ? '#10b981' : '#ef4444',
                          fontWeight: '600'
                        }}>{licenseInfo.is_active ? '✅ Aktiv' : '❌ Inaktiv'}</span>
                      </div>
                      {licenseInfo.message && (
                        <div className="license-message" style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.1)', fontStyle: 'italic', color: '#6b7280' }}>
                          {licenseInfo.message}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="config-item" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                    <label>Device-Name:</label>
                    <input
                      type="text"
                      value={customDeviceName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setCustomDeviceNameState(newName);
                        setCustomDeviceName(newName);
                        setDeviceName(getDeviceName());
                      }}
                      placeholder="Eigener Device-Name"
                      style={{
                        width: '100%',
                        padding: '8px',
                        fontSize: '1rem',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginTop: '4px'
                      }}
                    />
                    <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#666' }}>
                      UUID: <code style={{ fontSize: '0.8rem' }}>{getDeviceId()}</code>
                    </div>
                  </div>
                </div>
              )}
              {configTab === 'timetypes' && (
                <div className="config-tab-content">
                  <div className="timetypes-config">
                    <div className="timetypes-header">
                      <h4>{t('config.timetypes.section_title')}</h4>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setEditingTimeType(null);
                          setNewTimeTypeName('');
                          setNewTimeTypeDescription('');
                          setNewTimeTypeColor('#3b82f6');
                        }}
                        title="Erstellt eine neue Zeitart mit Name, Beschreibung und Farbe"
                      >
                        ➕ Neue Zeitart
                      </button>
                    </div>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
                      Verwalten Sie Zeitarten für die Kategorisierung von Messpunkten. Jede Zeitart hat einen Namen (max. 5 Zeichen), eine Beschreibung und eine Farbe.
                    </p>
                    
                    {editingTimeType === null && (
                      <div className="timetypes-list">
                        {timeTypes.map((type, index) => (
                          <div key={index} className="timetype-item">
                            <div className="timetype-preview" style={{ backgroundColor: type.color }}>
                              <span className="timetype-name">{type.name}</span>
                            </div>
                            <div className="timetype-info">
                              <div className="timetype-name-display">{type.name}</div>
                              <div className="timetype-description">{type.description}</div>
                            </div>
                            <div className="timetype-actions">
                              <button
                                className="btn-icon-small"
                                onClick={() => {
                                  setEditingTimeType(index);
                                  setNewTimeTypeName(type.name);
                                  setNewTimeTypeDescription(type.description);
                                  setNewTimeTypeColor(type.color);
                                }}
                                title="Bearbeiten"
                              >
                                ✏️
                              </button>
                              <button
                                className="btn-icon-small btn-danger"
                                onClick={() => {
                                  if (window.confirm(`Möchten Sie die Zeitart "${type.name}" wirklich löschen?`)) {
                                    const updated = timeTypes.filter((_, i) => i !== index);
                                    setTimeTypes(updated);
                                    localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                  }
                                }}
                                title="Löschen"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {(editingTimeType !== null || (editingTimeType === null && newTimeTypeName)) && (
                      <div className="timetype-edit-form">
                        <h5>{editingTimeType !== null ? 'Zeitart bearbeiten' : 'Neue Zeitart hinzufügen'}</h5>
                        <div className="form-group">
                          <label>Name (max. 5 Zeichen):</label>
                          <input
                            type="text"
                            value={newTimeTypeName}
                            onChange={(e) => {
                              const value = e.target.value.slice(0, 5);
                              setNewTimeTypeName(value);
                            }}
                            maxLength={5}
                            className="form-input"
                            placeholder="z.B. thb"
                          />
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                            Kurzer Name für die Zeitart (maximal 5 Zeichen). Wird in der Zeitart-Auswahl angezeigt.
                          </p>
                        </div>
                        <div className="form-group">
                          <label>Beschreibung:</label>
                          <input
                            type="text"
                            value={newTimeTypeDescription}
                            onChange={(e) => setNewTimeTypeDescription(e.target.value)}
                            className="form-input"
                            placeholder="z.B. beeinflussbare Hauptzeit"
                          />
                          <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                            Vollständige Beschreibung der Zeitart. Wird in der Zeitart-Auswahl als Tooltip angezeigt.
                          </p>
                        </div>
                        <div className="form-group">
                          <label>Farbe:</label>
                          <div className="color-picker-group">
                            <input
                              type="color"
                              value={newTimeTypeColor}
                              onChange={(e) => setNewTimeTypeColor(e.target.value)}
                              className="color-picker"
                            />
                            <input
                              type="text"
                              value={newTimeTypeColor}
                              onChange={(e) => setNewTimeTypeColor(e.target.value)}
                              className="color-input"
                              placeholder="#3b82f6"
                            />
                            <div className="color-preview" style={{ backgroundColor: newTimeTypeColor }}>
                              {newTimeTypeName || 'A'}
                            </div>
                          </div>
                            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '4px' }}>
                              {t('config.timetypes.color_desc')}
                            </p>
                        </div>
                        <div className="form-actions">
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              if (!newTimeTypeName.trim()) {
                                alert(t('alerts.timetype_name_required'));
                                return;
                              }
                              if (editingTimeType !== null) {
                                // Bearbeiten
                                const updated = [...timeTypes];
                                updated[editingTimeType] = {
                                  name: newTimeTypeName.trim(),
                                  description: newTimeTypeDescription.trim(),
                                  color: newTimeTypeColor
                                };
                                setTimeTypes(updated);
                                localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                setEditingTimeType(null);
                              } else {
                                // Hinzufügen
                                const updated = [...timeTypes, {
                                  name: newTimeTypeName.trim(),
                                  description: newTimeTypeDescription.trim(),
                                  color: newTimeTypeColor
                                }];
                                setTimeTypes(updated);
                                localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                setNewTimeTypeName('');
                                setNewTimeTypeDescription('');
                                setNewTimeTypeColor('#3b82f6');
                              }
                            }}
                            title={editingTimeType !== null ? 'Speichert die Änderungen an der Zeitart' : 'Fügt eine neue Zeitart zur Liste hinzu'}
                          >
                            {editingTimeType !== null ? t('config.timetypes.save') : t('config.timetypes.add')}
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setEditingTimeType(null);
                              setNewTimeTypeName('');
                              setNewTimeTypeDescription('');
                              setNewTimeTypeColor('#3b82f6');
                            }}
                            title="Bricht die Bearbeitung ab und verwirft alle Änderungen"
                          >
                            Abbrechen
                          </button>
                          {editingTimeType !== null && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                const updated = [...timeTypes];
                                updated[editingTimeType] = defaultTimeTypes.find(dt => dt.name === timeTypes[editingTimeType].name) || timeTypes[editingTimeType];
                                setTimeTypes(updated);
                                localStorage.setItem('stoppuhr_timetypes', JSON.stringify(updated));
                                setEditingTimeType(null);
                              }}
                              title="Auf Standard zurücksetzen"
                            >
                              🔄 Standard
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="timetypes-actions">
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          if (window.confirm(t('alerts.confirm.timetypes_reset'))) {
                            setTimeTypes(defaultTimeTypes);
                            localStorage.setItem('stoppuhr_timetypes', JSON.stringify(defaultTimeTypes));
                            setEditingTimeType(null);
                          }
                        }}
                      >
                        🔄 Alle auf Standard zurücksetzen
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div 
        className={`main-content-wrapper ${gradePosition === 'top' ? 'grades-top-layout' : 'grades-left-layout'}`}
        style={isPortrait ? { display: 'none' } : {}}
      >
        {gradePosition === 'top' && (
          <div className="performance-grades grades-top">
            <div 
              className="grades-container grades-row"
            >
              {/* "M1" Toggle-Button vor Unterbrechung */}
              <button
                className={`grade-btn merke1-btn ${merke1Enabled ? 'active' : ''}`}
                onClick={() => {
                  const newValue = !merke1Enabled;
                  setMerke1Enabled(newValue);
                  localStorage.setItem('stoppuhr_merke1_enabled', String(newValue));
                  if (!newValue) {
                    // Wenn deaktiviert, lösche gespeicherte Werte
                    setMerke1Values(null);
                    localStorage.removeItem('stoppuhr_merke1_values');
                  }
                }}
                disabled={!selectedSession}
                title={merke1Enabled ? 'M1: AN - Speichert Kürzel, Tätigkeit und EAs beim Stoppen' : 'M1: AUS - Aktiviert das Speichern von Kürzel, Tätigkeit und EAs beim Stoppen'}
              >
                M1
              </button>
              <div className="interruption-control">
                <label className="interruption-label">
                  <span className="interruption-text">
                    UB{interruptionMeasurementNumber && ` (#${interruptionMeasurementNumber})`}
                  </span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={isInterrupted}
                      onChange={(e) => handleInterruptionToggle(e.target.checked)}
                      className="toggle-input"
                      id="interruption-toggle-top"
                      disabled={!isRunning}
                      title="Unterbrechung aktivieren/deaktivieren - Markiert Messpunkte während einer Unterbrechung. Diese werden in der Auswertung separat behandelt (Tastatur: Ctrl+0)"
                    />
                    <label htmlFor="interruption-toggle-top" className="toggle-label"></label>
                  </div>
                </label>
              </div>
              {grades.map((grade) => (
                <button
                  key={grade}
                  className={`grade-btn grade-${grade}`}
                  onClick={() => handleAddMeasurement(grade)}
                  disabled={!selectedSession}
                  title={`Leistungsgrad ${grade}% - Erfasst einen Messpunkt mit Leistungsgrad ${grade}%. Die Einzelzeit wird automatisch berechnet (Tastatur: ${keyboardConfig[grade] || `Ctrl+${grades.indexOf(grade) + 1}`})`}
                >
                  {grade}
                </button>
              ))}
            </div>
          </div>
        )}
        {gradePosition === 'left' && (
          <aside 
            className="performance-grades grades-left"
          >
            <div 
              className="grades-container grades-column"
            >
              {/* "M1" Toggle-Button vor Unterbrechung */}
              <button
                className={`grade-btn merke1-btn ${merke1Enabled ? 'active' : ''}`}
                onClick={() => {
                  const newValue = !merke1Enabled;
                  setMerke1Enabled(newValue);
                  localStorage.setItem('stoppuhr_merke1_enabled', String(newValue));
                  if (!newValue) {
                    // Wenn deaktiviert, lösche gespeicherte Werte
                    setMerke1Values(null);
                    localStorage.removeItem('stoppuhr_merke1_values');
                  }
                }}
                disabled={!selectedSession}
                title={merke1Enabled ? 'M1: AN - Speichert Kürzel, Tätigkeit und EAs beim Stoppen' : 'M1: AUS - Aktiviert das Speichern von Kürzel, Tätigkeit und EAs beim Stoppen'}
              >
                M1
              </button>
              <div className="interruption-control">
                <label className="interruption-label" title="Unterbrechung aktivieren/deaktivieren - Markiert Messpunkte während einer Unterbrechung. Diese werden in der Auswertung separat behandelt (Tastatur: Ctrl+0)">
                  <span className="interruption-text">
                    UB{interruptionMeasurementNumber && ` (#${interruptionMeasurementNumber})`}
                  </span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={isInterrupted}
                      onChange={(e) => handleInterruptionToggle(e.target.checked)}
                      className="toggle-input"
                      id="interruption-toggle"
                      disabled={!isRunning}
                      title="Unterbrechung aktivieren/deaktivieren - Markiert Messpunkte während einer Unterbrechung. Diese werden in der Auswertung separat behandelt (Tastatur: Ctrl+0)"
                    />
                    <label htmlFor="interruption-toggle" className="toggle-label"></label>
                  </div>
                </label>
              </div>
              {grades.map((grade) => (
                <button
                  key={grade}
                  className={`grade-btn grade-${grade}`}
                  onClick={() => handleAddMeasurement(grade)}
                  disabled={!selectedSession}
                  title={`Leistungsgrad ${grade}% - Erfasst einen Messpunkt mit Leistungsgrad ${grade}%. Die Einzelzeit wird automatisch berechnet (Tastatur: ${keyboardConfig[grade] || `Ctrl+${grades.indexOf(grade) + 1}`})`}
                >
                  {grade}
                </button>
              ))}
            </div>
          </aside>
        )}

        <div className="measurements-section">
          {/* Vorderseite als aufklappbarer Bereich */}
          {selectedSession && showVorderseite && (
            <div style={{
              marginBottom: '10px',
              border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
              borderRadius: '8px',
              backgroundColor: darkMode ? '#1f2937' : '#ffffff',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => {
                  const willExpand = !vorderseiteExpanded;
                  setVorderseiteExpanded(willExpand);
                  // Prüfe beim Öffnen der Vorderseite
                  if (willExpand) {
                    setTimeout(() => {
                      checkAndShowWiederaufnahmenDialog();
                    }, 100);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  border: 'none',
                  background: darkMode ? '#374151' : '#f9fafb',
                  color: darkMode ? '#f3f4f6' : '#1f2937',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = darkMode ? '#4b5563' : '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = darkMode ? '#374151' : '#f9fafb';
                }}
              >
                <span>📄 Vorderseite</span>
                <span style={{ fontSize: '1.2rem' }}>
                  {vorderseiteExpanded ? '▼' : '▶'}
                </span>
              </button>
              {vorderseiteExpanded && (
                <div style={{
                  padding: '20px',
                  borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                  backgroundColor: darkMode ? '#1f2937' : '#ffffff'
                }}>
                  {isLoadingVorderseite ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div>Lade Vorderseite...</div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => setVorderseiteFontScale(s => Math.max(0.85, s - 0.1))}
                          title="Schrift kleiner"
                          style={{
                            background: darkMode ? '#374151' : '#f3f4f6',
                            border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                            borderRadius: '6px',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: darkMode ? '#f9fafb' : '#111827',
                            fontSize: '1rem',
                            fontWeight: 600
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                          }}
                        >
                          <span style={{ fontSize: '0.8em' }}>a</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setVorderseiteFontScale(s => Math.min(1.35, s + 0.1))}
                          title="Schrift größer"
                          style={{
                            background: darkMode ? '#374151' : '#f3f4f6',
                            border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                            borderRadius: '6px',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: darkMode ? '#f9fafb' : '#111827',
                            fontSize: '1.05rem',
                            fontWeight: 700
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                          }}
                        >
                          A
                        </button>
                        <button
                          type="button"
                          onClick={() => setVorderseiteFontScale(1)}
                          title="Schriftgröße Standard (1)"
                          style={{
                            background: vorderseiteFontScale === 1 ? (darkMode ? '#4b5563' : '#e5e7eb') : (darkMode ? '#374151' : '#f3f4f6'),
                            border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                            borderRadius: '6px',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: darkMode ? '#f9fafb' : '#111827',
                            fontSize: '0.95rem',
                            fontWeight: 600
                          }}
                          onMouseEnter={(e) => {
                            if (vorderseiteFontScale !== 1) {
                              e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (vorderseiteFontScale !== 1) {
                              e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                            }
                          }}
                        >
                          1
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            // Berechne optimale Schriftgröße für Seitenbreite
                            const container = document.querySelector('.vorderseite-container') || document.body;
                            const containerWidth = container.clientWidth || window.innerWidth;
                            // Schätze optimale Schriftgröße basierend auf Container-Breite
                            // Beispiel: bei 1200px Breite könnte 1.1 gut sein, bei 800px 0.95
                            const optimalScale = Math.max(0.85, Math.min(1.35, containerWidth / 1000));
                            setVorderseiteFontScale(optimalScale);
                          }}
                          title="Schriftgröße auf Seitenbreite anpassen"
                          style={{
                            background: darkMode ? '#374151' : '#f3f4f6',
                            border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                            borderRadius: '6px',
                            width: '34px',
                            height: '34px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: darkMode ? '#f9fafb' : '#111827',
                            fontSize: '0.85rem',
                            fontWeight: 600
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = darkMode ? '#4b5563' : '#e5e7eb';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                          }}
                        >
                          ⬌
                        </button>
                      </div>
                      <VorderseiteForm 
                        data={vorderseiteData || getDefaultVorderseiteData()}
                        onUpdate={updateVorderseiteField}
                        onSave={saveVorderseite}
                        isSaving={isSavingVorderseite}
                        darkMode={darkMode}
                        getAnalysis={getAnalysis}
                        updateZusammenstellungVorgabezeiten={updateZusammenstellungVorgabezeiten}
                        onDataUpdate={setVorderseiteData}
                        decimalPlacesVorderseite={decimalPlacesVorderseite}
                        formatVorderseiteTime={formatVorderseiteTime}
                        setDeleteRowConfirm={setDeleteRowConfirm}
                        fontScale={vorderseiteFontScale}
                        ablaufDbName={ablaufDbName}
                        recognitionEdit={recognitionEdit}
                        isSpeechRecognitionSupported={isSpeechRecognitionSupported}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="section-header">
            <button
              className="btn-title-toggle"
              onClick={() => {
                const newValue = !compactView;
                setCompactView(newValue);
                localStorage.setItem('compactView', String(newValue));
              }}
              title={compactView ? "Details anzeigen - Wechselt zur Detailansicht mit allen Informationen (Tastatur: Ctrl+T)" : "Kompakt anzeigen - Wechselt zur kompakten Ansicht mit weniger Informationen (Tastatur: Ctrl+T)"}
              style={{
                background: darkMode ? '#374151' : '#f3f4f6',
                border: `2px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`,
                borderRadius: '8px',
                fontWeight: '600',
                color: darkMode ? '#f9fafb' : '#1f2937',
                cursor: 'pointer',
                margin: '0',
                textAlign: 'left',
                transition: 'all 0.2s ease',
                boxShadow: darkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#667eea';
                e.target.style.color = 'white';
                e.target.style.borderColor = '#667eea';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = darkMode ? '#374151' : '#f3f4f6';
                e.target.style.color = darkMode ? '#f9fafb' : '#1f2937';
                e.target.style.borderColor = darkMode ? '#4b5563' : '#e5e7eb';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = darkMode ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
            >
              <span>{compactView ? t('common.view_compact') : t('common.view_details')}</span>
              <span style={{ fontSize: '1.2rem' }}>
                {compactView ? '▶' : '▼'}
              </span>
            </button>
            <div className="section-actions">
              {measurements.length > 0 && (
                <>
                  <span className="measurement-count">{measurements.length} erfasst</span>
                  {licenseStatus && (
                    <>
                      <button 
                        className="btn btn-upload" 
                        onClick={handleUploadMeasurements}
                        disabled={backendAvailable === false || isCheckingBackend}
                        title={
                          isCheckingBackend 
                            ? t('common.upload_checking_title')
                            : backendAvailable === false 
                              ? t('common.upload_offline_title')
                              : t('common.upload_title')
                        }
                      >
                        {isCheckingBackend ? t('common.upload_checking') : backendAvailable === false ? t('common.upload_offline') : `☁️ ${t('common.upload')}`}
                      </button>
                      <button 
                        className="btn btn-analysis" 
                        onClick={() => setShowAnalysis(!showAnalysis)}
                        title={t('common.auswertung_title')}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}
                      >
                        <span>{t('common.auswertung_with_icon')}</span>
                        <span style={{ fontSize: '1.2rem' }}>
                          {showAnalysis ? '▼' : '▶'}
                        </span>
                      </button>
                      <button 
                        className="btn btn-report" 
                        onClick={() => setShowReportDialog(true)}
                        title={t('common.report_title')}
                      >
                        {t('common.report')}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        {showAnalysis && licenseStatus && getAnalysis() && (() => {
          const analysis = getAnalysis();
          const activities = Object.keys(analysis.pivot).sort();
          const pivotTimeTypeNames = [...new Set(Object.values(analysis.pivot).flatMap(activity => Object.keys(activity)))].sort();
          
          // Berechne Gesamtzeilen und -spalten
          const rowTotals = {};
          const colTotals = {};
          let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
          
          activities.forEach(activity => {
            rowTotals[activity] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
            Object.values(analysis.pivot[activity]).forEach(cell => {
              rowTotals[activity].count += cell.count;
              rowTotals[activity].totalTime += cell.totalTime;
              rowTotals[activity].totalVorgabeTime += (cell.totalVorgabeTime || 0);
              rowTotals[activity].grades.push(...cell.grades);
              grandTotal.count += cell.count;
              grandTotal.totalTime += cell.totalTime;
              grandTotal.totalVorgabeTime += (cell.totalVorgabeTime || 0);
              grandTotal.grades.push(...cell.grades);
            });
            rowTotals[activity].avgGrade = calculateAvgGrade(rowTotals[activity].grades);
          });
          
          pivotTimeTypeNames.forEach(timeType => {
            colTotals[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
            activities.forEach(activity => {
              if (analysis.pivot[activity][timeType]) {
                const cell = analysis.pivot[activity][timeType];
                colTotals[timeType].count += cell.count;
                colTotals[timeType].totalTime += cell.totalTime;
                colTotals[timeType].totalVorgabeTime += (cell.totalVorgabeTime || 0);
                colTotals[timeType].grades.push(...cell.grades);
              }
            });
            colTotals[timeType].avgGrade = calculateAvgGrade(colTotals[timeType].grades);
          });
          
          // eslint-disable-next-line no-unused-vars
          const grandAvgGrade = calculateAvgGrade(grandTotal.grades);
          
          return (
            <div className="analysis-panel">
              <div className="analysis-header">
                <h3>{t('common.auswertung_with_icon')}</h3>
                {pivotDrillDown && (
                  <button 
                    className="btn-close-drilldown" 
                    onClick={() => setPivotDrillDown(null)}
                    title="Drill-Down schließen"
                  >
                    ✕ Zurück
                  </button>
                )}
                {!pivotDrillDown && (
                  <div className="analysis-view-mode" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'timeType' ? 'active' : ''}`}
                      onClick={() => setAnalysisViewMode('timeType')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'timeType' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'timeType' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'timeType' ? 'bold' : 'normal'
                      }}
                      title="Bilanz nach Zeitarten"
                    >
                      Bilanz Zeitarten
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'kuerzel' ? 'active' : ''}`}
                      onClick={() => setAnalysisViewMode('kuerzel')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'kuerzel' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'kuerzel' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'kuerzel' ? 'bold' : 'normal'
                      }}
                      title="Bilanz nach Kürzel"
                    >
                      Bilanz Kürzel
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'zeitstrahl' ? 'active' : ''}`}
                      onClick={() => setAnalysisViewMode('zeitstrahl')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'zeitstrahl' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'zeitstrahl' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'zeitstrahl' ? 'bold' : 'normal'
                      }}
                      title="Zeitstrahl: Pro WA-Nr aufklappbar; Hover: Beschreibung, Notiz, EA, Fotos"
                    >
                      Zeitstrahl
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'pivot' ? 'active' : ''}`}
                      onClick={() => setAnalysisViewMode('pivot')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'pivot' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'pivot' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'pivot' ? 'bold' : 'normal'
                      }}
                      title="Pivot-Tabelle"
                    >
                      Pivot-Tabelle
                    </button>
                    <button
                      className={`btn-analysis-mode ${analysisViewMode === 'statistik' ? 'active' : ''}`}
                      onClick={() => setAnalysisViewMode('statistik')}
                      style={{
                        padding: '8px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: analysisViewMode === 'statistik' ? '#667eea' : 'transparent',
                        color: analysisViewMode === 'statistik' ? 'white' : (darkMode ? '#f9fafb' : '#111827'),
                        cursor: 'pointer',
                        fontWeight: analysisViewMode === 'statistik' ? 'bold' : 'normal'
                      }}
                      title="Statistik, Regression, Clustering"
                    >
                      Statistik
                    </button>
                  </div>
                )}
              </div>
              {!pivotDrillDown ? (
                <>
                  {/* Statistik: Min, Max, Mittelwert, Std, Varianz, Variation; Kürzel / Kürzel+E; Tabelle / Balken; Regression, Clustering */}
                  {analysisViewMode === 'statistik' && (() => {
                    const groupData = statistikGroupBy === 'kuerzel' ? (analysis.byKuerzel || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
                    // Berechne Statistik dynamisch basierend auf statistikTimeType
                    const computedGroupData = {};
                    Object.keys(groupData).forEach(k => {
                      const g = groupData[k];
                      const times = (g.measurements || []).map(mm => 
                        statistikTimeType === 'soll' ? (mm.vorgabeTime || 0) : (mm.individualTime || 0)
                      );
                      computedGroupData[k] = {
                        ...g,
                        ...computeStats(times)
                      };
                    });
                    const groupKeys = Object.keys(computedGroupData).sort((a, b) => {
                      if (a === 'Kein Kürzel' || a.startsWith('Kein Kürzel')) return 1;
                      if (b === 'Kein Kürzel' || b.startsWith('Kein Kürzel')) return -1;
                      return a.localeCompare(b);
                    });
                    const maxMean = groupKeys.length ? Math.max(...groupKeys.map(k => computedGroupData[k].mean || 0)) : 1;

                    return (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '16px', marginBottom: '12px', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827' }}>Gruppierung:</span>
                          <button
                            className={`btn-analysis-mode ${statistikGroupBy === 'kuerzel' ? 'active' : ''}`}
                            onClick={() => setStatistikGroupBy('kuerzel')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikGroupBy === 'kuerzel' ? '#667eea' : 'transparent',
                              color: statistikGroupBy === 'kuerzel' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikGroupBy === 'kuerzel' ? 'bold' : 'normal'
                            }}
                          >Kürzel</button>
                          <button
                            className={`btn-analysis-mode ${statistikGroupBy === 'kuerzelE' ? 'active' : ''}`}
                            onClick={() => setStatistikGroupBy('kuerzelE')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikGroupBy === 'kuerzelE' ? '#667eea' : 'transparent',
                              color: statistikGroupBy === 'kuerzelE' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikGroupBy === 'kuerzelE' ? 'bold' : 'normal'
                            }}
                          >Kürzel + E</button>
                          <button
                            className={`btn-analysis-mode ${statistikGroupBy === 'kuerzelEA' ? 'active' : ''}`}
                            onClick={() => setStatistikGroupBy('kuerzelEA')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikGroupBy === 'kuerzelEA' ? '#667eea' : 'transparent',
                              color: statistikGroupBy === 'kuerzelEA' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikGroupBy === 'kuerzelEA' ? 'bold' : 'normal'
                            }}
                          >Kürzel + EA</button>
                          <span style={{ marginLeft: '8px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827' }}>Darstellung:</span>
                          <button
                            className={`btn-analysis-mode ${statistikDisplay === 'table' ? 'active' : ''}`}
                            onClick={() => setStatistikDisplay('table')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikDisplay === 'table' ? '#667eea' : 'transparent',
                              color: statistikDisplay === 'table' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikDisplay === 'table' ? 'bold' : 'normal'
                            }}
                          >Tabelle</button>
                          <button
                            className={`btn-analysis-mode ${statistikDisplay === 'bar' ? 'active' : ''}`}
                            onClick={() => setStatistikDisplay('bar')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikDisplay === 'bar' ? '#667eea' : 'transparent',
                              color: statistikDisplay === 'bar' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikDisplay === 'bar' ? 'bold' : 'normal'
                            }}
                          >Balken</button>
                          <span style={{ marginLeft: '8px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827' }}>Zeit:</span>
                          <button
                            className={`btn-analysis-mode ${statistikTimeType === 'soll' ? 'active' : ''}`}
                            onClick={() => setStatistikTimeType('soll')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikTimeType === 'soll' ? '#667eea' : 'transparent',
                              color: statistikTimeType === 'soll' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikTimeType === 'soll' ? 'bold' : 'normal'
                            }}
                          >Soll (min)</button>
                          <button
                            className={`btn-analysis-mode ${statistikTimeType === 'ist' ? 'active' : ''}`}
                            onClick={() => setStatistikTimeType('ist')}
                            style={{ 
                              padding: '6px 12px', 
                              fontSize: '13px',
                              border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`,
                              borderRadius: '4px',
                              backgroundColor: statistikTimeType === 'ist' ? '#667eea' : 'transparent',
                              color: statistikTimeType === 'ist' ? 'white' : (darkMode ? '#e5e7eb' : '#111827'),
                              cursor: 'pointer',
                              fontWeight: statistikTimeType === 'ist' ? 'bold' : 'normal'
                            }}
                          >IST (HM)</button>
                        </div>

                        {statistikDisplay === 'table' && (
                          <div className="analysis-table-wrapper" style={{ marginTop: '8px' }}>
                            <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{statistikGroupBy === 'kuerzel' ? 'Kürzel' : (statistikGroupBy === 'kuerzelE' ? 'Kürzel + E' : 'Kürzel + EA')}</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>n</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Min</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Max</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Mittelwert</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Std</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Varianz</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Spannweite</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Var.-Koeff. %</th>
                                </tr>
                              </thead>
                              <tbody>
                                {groupKeys.map(k => {
                                  const d = computedGroupData[k];
                                  const egKeysKuerzel = [...new Set((d.measurements || []).flatMap(m => (m.einflussgroessen && typeof m.einflussgroessen === 'object') ? Object.keys(m.einflussgroessen) : []))];
                                  const isClickable = (statistikGroupBy === 'kuerzel' || statistikGroupBy === 'kuerzelE' || statistikGroupBy === 'kuerzelEA');
                                  // Formatierung basierend auf statistikTimeType
                                  const formatValue = (value) => {
                                    if (d.n === 0 || value == null) return '–';
                                    if (statistikTimeType === 'soll') {
                                      return formatSollzeit(value, decimalPlacesSollzeiten) + ' min';
                                    } else {
                                      return formatTime(value);
                                    }
                                  };
                                  return (
                                    <tr
                                      key={k}
                                      onClick={isClickable ? () => setEaDrillDown({ key: k, kuerzel: k, egList: egKeysKuerzel, selectedEG: egKeysKuerzel[0] || null }) : undefined}
                                      style={{ cursor: isClickable ? 'pointer' : 'default' }}
                                      title={isClickable ? (egKeysKuerzel.length ? 'Klicken: Einzelanalyse (Regression, Trompetenkurve)' : '') : undefined}
                                    >
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', fontWeight: 500, color: darkMode ? '#e5e7eb' : '#111827' }} title={k}>{k.length > 36 ? k.slice(0, 33) + '…' : k}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{d.count}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.min)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.max)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.mean)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.std)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.var)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatValue(d.spanne)}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{d.varKoeff != null ? d.varKoeff.toFixed(1) + ' %' : '–'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {statistikDisplay === 'bar' && (
                          <div style={{ marginTop: '12px' }}>
                            {groupKeys.map(k => {
                              const d = computedGroupData[k];
                              const pct = maxMean > 0 ? (d.mean / maxMean) * 100 : 0;
                              const formatValue = (value) => {
                                if (d.n === 0 || value == null) return '–';
                                if (statistikTimeType === 'soll') {
                                  return formatSollzeit(value, decimalPlacesSollzeiten) + ' min';
                                } else {
                                  return formatTime(value);
                                }
                              };
                              return (
                                <div key={k} style={{ marginBottom: '10px' }}>
                                  <div style={{ fontSize: '12px', marginBottom: '2px', color: darkMode ? '#d1d5db' : '#374151' }} title={k}>{k.length > 40 ? k.slice(0, 37) + '…' : k} – Ø {formatValue(d.mean)}</div>
                                  <div style={{ height: '20px', background: darkMode ? '#374151' : '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg, #667eea, #764ba2)', borderRadius: '4px', minWidth: d.n > 0 ? '4px' : 0 }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Einzelanalyse (Regression): bei Kürzel, Kürzel+E und Kürzel+EA, aus Klick auf Statistik-Zeile */}
                        {eaDrillDown && (statistikGroupBy === 'kuerzel' || statistikGroupBy === 'kuerzelE' || statistikGroupBy === 'kuerzelEA') && (
                          <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: darkMode ? '#1f2937' : '#f9fafb' }}>
                            <button type="button" onClick={() => setEaDrillDown(null)} style={{ marginBottom: '12px', padding: '6px 12px', cursor: 'pointer' }}>← Zurück</button>
                            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: darkMode ? '#f9fafb' : '#111827' }}>Einzelanalyse: {statistikGroupBy === 'kuerzel' ? 'Kürzel' : (statistikGroupBy === 'kuerzelE' ? 'Kürzel+E' : 'Kürzel+EA')} „{eaDrillDown.kuerzel}"</h4>
                            {(eaDrillDown.egList || []).length === 0 ? (
                              <div style={{ color: darkMode ? '#9ca3af' : '#6b7280', fontSize: 13 }}>Keine Einflussgrößen in dieser Zeile.</div>
                            ) : (
                              <>
                                <label style={{ marginRight: 8, color: darkMode ? '#d1d5db' : '#374151' }}>Einflussgröße:</label>
                                <select
                                  value={eaDrillDown.selectedEG || ''}
                                  onChange={e => setEaDrillDown(prev => prev ? { ...prev, selectedEG: e.target.value } : null)}
                                  style={{ marginBottom: 12, padding: '4px 8px' }}
                                >
                                  {(() => {
                                    const mEA = (groupData[eaDrillDown.key]?.measurements || []);
                                    const { zeitbestimmend } = analyzeEinflussgroessenZeitbestimmend(mEA);
                                    return (eaDrillDown.egList || []).map(eg => (
                                      <option key={eg} value={eg}>{eg}{zeitbestimmend.includes(eg) ? '' : ' (ohne Relevanz)'}</option>
                                    ));
                                  })()}
                                </select>
                                {eaDrillDown.selectedEG && (() => {
                                  const eg = eaDrillDown.selectedEG;
                                  const measurements = (groupData[eaDrillDown.key]?.measurements || []).filter(m => m.einflussgroessen?.[eg] != null && m.einflussgroessen[eg] !== '');
                                  const categories = [...new Set(measurements.map(m => String(m.einflussgroessen[eg])))].sort();
                                  const points = measurements.map(m => ({ x: categories.indexOf(String(m.einflussgroessen[eg])) + 1, y: Number(m.vorgabeTime) || 0 }));
                                  const pointMeta = measurements.map(m => ({ number: m.number, activity: m.isNote ? `📝 ${m.note || ''}` : (m.activity || '-') }));
                                  const regression = categories.length >= 2 && points.length >= 3 ? simpleOLSWithBand(points.map(p => p.x), points.map(p => p.y)) : null;
                                  return (
                                    <EgRegressionChart
                                      points={points}
                                      pointMeta={pointMeta}
                                      categories={categories}
                                      regression={regression}
                                      formatTime={formatTime}
                                      darkMode={darkMode}
                                      width={480}
                                      height={280}
                                    />
                                  );
                                })()}
                              </>
                            )}
                          </div>
                        )}

                        {/* Einflussgrößen pro Kürzel+E / Kürzel+EA (nur Anzeige, keine Einzelanalyse – die gibt es nur bei Gruppierung Kürzel) */}
                        {((statistikGroupBy === 'kuerzelE' && Object.keys(analysis.byKuerzelE || {}).some(k => k.includes(' | '))) || (statistikGroupBy === 'kuerzelEA' && Object.keys(analysis.byKuerzelEA || {}).some(k => k.includes(' | ')))) && (
                          <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: darkMode ? '#1f2937' : '#f9fafb' }}>
                            <h4 style={{ marginTop: 0, marginBottom: '12px', color: darkMode ? '#f9fafb' : '#111827' }}>Einflussgrößen pro {statistikGroupBy === 'kuerzelEA' ? 'Kürzel+EA' : 'Kürzel+E'}</h4>
                            <div className="analysis-table-wrapper" style={{ overflowX: 'auto' }}>
                              <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                  <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                    <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{statistikGroupBy === 'kuerzelEA' ? 'Kürzel + EA' : 'Kürzel + E'}</th>
                                    <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Verwendete Einflussgrößen</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.keys(statistikGroupBy === 'kuerzelEA' ? (analysis.byKuerzelEA || {}) : (analysis.byKuerzelE || {})).sort((a,b)=>a.localeCompare(b)).map(key => {
                                    const g = (statistikGroupBy === 'kuerzelEA' ? analysis.byKuerzelEA : analysis.byKuerzelE)[key];
                                    const first = (g.measurements || [])[0];
                                    const egKeys = first && first.einflussgroessen && typeof first.einflussgroessen === 'object' ? Object.keys(first.einflussgroessen) : [];
                                    return (
                                      <tr key={key}>
                                        <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', color: darkMode ? '#e5e7eb' : '#111827' }} title={key}>{key.length > 50 ? key.slice(0, 47) + '…' : key}</td>
                                        <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '6px', color: darkMode ? '#e5e7eb' : '#111827' }}>{egKeys.join(', ') || '–'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Zeitbestimmend-Analyse und Multivariate Regression: Sollzeit ~ Einflussgrößen */}
                        {(() => {
                          const srcEA = statistikGroupBy === 'kuerzelEA' ? (analysis.byKuerzelEA || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
                          const measurementsEA = Object.values(srcEA).flatMap(gg => gg.measurements || []);
                          if (measurementsEA.length < 2) return null;
                          const { allEG, zeitbestimmend, r2PerEG } = analyzeEinflussgroessenZeitbestimmend(measurementsEA);
                          const { X, y, colNames } = buildDesignMatrixEA(measurementsEA, zeitbestimmend);
                          const res = X.length >= X[0].length ? multilinearOLS(X, y) : { beta: colNames.map(() => 0), r2: 0 };
                          const gData = statistikGroupBy === 'kuerzel' ? (analysis.byKuerzel || {}) : (statistikGroupBy === 'kuerzelE' ? (analysis.byKuerzelE || {}) : (analysis.byKuerzelEA || {}));
                          const gKeys = Object.keys(gData);
                          const points = gKeys.map(kk => { const g = gData[kk]; return [g.mean || 0, g.count || 0, parseFloat(g.avgGrade) || 0]; });
                          let clusterLabels = [];
                          if (points.length >= 3) {
                            const kVal = Math.min(3, points.length);
                            const assign = kMeans(points, kVal, 25);
                            clusterLabels = gKeys.map((key, i) => ({ key, c: assign[i] }));
                          }
                          return (
                            <div style={{ marginTop: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: darkMode ? '#1f2937' : '#f9fafb' }}>
                              <h4 style={{ marginTop: 0, marginBottom: '12px', color: darkMode ? '#f9fafb' : '#111827' }}>Zeitbestimmende Einflussgrößen (Sollzeit)</h4>
                              {allEG.length === 0 ? (
                                <div style={{ fontSize: '13px', color: darkMode ? '#d1d5db' : '#6b7280' }}>Keine Einflussgrößen in den Daten.</div>
                              ) : (
                                <>
                                  <div style={{ fontSize: '12px', marginBottom: '8px', color: darkMode ? '#d1d5db' : '#374151' }}>
                                    <strong>R² je Einflussgröße (Sollzeit ~ Ausprägung):</strong>{' '}
                                    {allEG.sort((a,b)=>(r2PerEG[b]||0)-(r2PerEG[a]||0)).map(eg => `${eg}: ${(r2PerEG[eg]||0).toFixed(3)}`).join('; ')}
                                  </div>
                                  <div style={{ fontSize: '12px', marginBottom: '12px', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                                    <strong>In weitere Analyse einbezogen (zeitbestimmend, R²≥1%):</strong> {zeitbestimmend.length ? zeitbestimmend.join(', ') : '–'}
                                  </div>
                                  {zeitbestimmend.length > 0 && res.beta && (
                                    <div style={{ fontSize: '13px', marginBottom: '12px' }}>
                                      <strong>Multivariate Regression Sollzeit ~ Einflussgrößen:</strong>{' '}
                                      y = {res.beta.map((b, i) => i === 0 ? b.toFixed(1) : (b >= 0 ? '+ ' : '- ') + Math.abs(b).toFixed(2) + '\u00B7' + (colNames[i] || '?')).join(' ')} &emsp; R² = {res.r2.toFixed(3)}
                                    </div>
                                  )}
                                </>
                              )}
                              {clusterLabels.length > 0 && (
                                <>
                                  <h4 style={{ marginTop: '16px', marginBottom: '8px', color: darkMode ? '#f9fafb' : '#111827' }}>Clustering (K-Means, k=3, Merkmale: Ø-Zeit, Anzahl, Ø-Leistungsgrad)</h4>
                                  <div style={{ fontSize: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {[0, 1, 2].map(c => (
                                      <div key={c} style={{ flex: '1 1 200px' }}>
                                        <strong>Cluster {c + 1}:</strong> {clusterLabels.filter(x => x.c === c).map(x => x.key).join(', ') || '–'}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}

                  {/* Bilanz Zeitarten */}
                  {analysisViewMode === 'timeType' && (() => {
                    const timeTypes = Object.keys(analysis.byTimeType || {}).sort((a, b) => {
                      if (a === 'Keine Zeitart') return 1;
                      if (b === 'Keine Zeitart') return -1;
                      return a.localeCompare(b);
                    });
                    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

                    timeTypes.forEach(timeType => {
                      const data = analysis.byTimeType[timeType];
                      grandTotal.count += data.count;
                      grandTotal.totalTime += data.totalTime;
                      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      grandTotal.grades.push(...data.grades);
                    });

                    // Hole Beschreibung für Zeitarten
                    const getTimeTypeDescription = (timeTypeName) => {
                      const timeTypeObj = defaultTimeTypes.find(tt => tt.name === timeTypeName);
                      return timeTypeObj ? timeTypeObj.description : '';
                    };

                    // Sammle x__ Zeitarten
                    const excludedTimeTypes = Object.keys(analysis.excludedTimeTypes || {}).sort();
                    let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                    excludedTimeTypes.forEach(timeType => {
                      const data = analysis.excludedTimeTypes[timeType];
                      excludedGrandTotal.count += data.count;
                      excludedGrandTotal.totalTime += data.totalTime;
                      excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      excludedGrandTotal.grades.push(...data.grades);
                    });
                    return (
                      <>
                        <div className="analysis-table-wrapper" style={{ marginTop: '20px' }}>
                          <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Zeitart</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Beschreibung</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Vorgabezeit (SOLL)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Ø Leistungsgrad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {timeTypes.map(timeType => {
                                const data = analysis.byTimeType[timeType];
                                const description = getTimeTypeDescription(timeType);
                                return (
                                  <tr 
                                    key={timeType}
                                    style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                    onClick={() => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        setPivotDrillDown({ timeType, measurements: data.measurements });
                                      }
                                    }}
                                    title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                    onMouseEnter={(e) => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{timeType}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatSollzeit(data.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatGrade(data.avgGrade)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}>Gesamt</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px' }}></td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{grandTotal.count}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatTime(grandTotal.totalTime)}</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* x__ Zeitarten am Ende */}
                        {excludedTimeTypes.length > 0 && (
                          <div className="analysis-table-wrapper" style={{ marginTop: '30px' }}>
                            <h4 style={{ marginBottom: '10px', color: darkMode ? '#f9fafb' : '#111827' }}>Nicht zu verwendende Zeit (x__)</h4>
                            <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Zeitart</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Beschreibung</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {excludedTimeTypes.map(timeType => {
                                  const data = analysis.excludedTimeTypes[timeType];
                                  const description = getTimeTypeDescription(timeType);
                                  return (
                                    <tr 
                                      key={timeType}
                                      style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                      onClick={() => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          setPivotDrillDown({ timeType, measurements: data.measurements });
                                        }
                                      }}
                                      title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                      onMouseEnter={(e) => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{timeType}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    </tr>
                                  );
                                })}
                                <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{excludedGrandTotal.count}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(excludedGrandTotal.totalTime)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Bilanz Kürzel */}
                  {analysisViewMode === 'kuerzel' && (() => {
                    const kuerzels = Object.keys(analysis.byKuerzel || {}).sort((a, b) => {
                      if (a === 'Kein Kürzel') return 1;
                      if (b === 'Kein Kürzel') return -1;
                      return a.localeCompare(b);
                    });
                    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };

                    kuerzels.forEach(kuerzel => {
                      const data = analysis.byKuerzel[kuerzel];
                      grandTotal.count += data.count;
                      grandTotal.totalTime += data.totalTime;
                      grandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      grandTotal.grades.push(...data.grades);
                    });

                    // Hole Beschreibung für Kürzel
                    const getKuerzelDescription = (kuerzelName) => {
                      if (kuerzelName === 'Kein Kürzel') return '';
                      const kuerzelObj = ablaufDbData.find(k => k.kuerzel === kuerzelName);
                      return kuerzelObj ? (kuerzelObj.beschreibung || kuerzelObj.fullText || '') : '';
                    };

                    return (
                      <>
                        <div className="analysis-table-wrapper" style={{ marginTop: '20px' }}>
                          <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                              <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Kürzel</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Tätigkeit</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Vorgabezeit (SOLL)</th>
                                <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Ø Leistungsgrad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {kuerzels.map(kuerzel => {
                                const data = analysis.byKuerzel[kuerzel];
                                const description = getKuerzelDescription(kuerzel);
                                return (
                                  <tr 
                                    key={kuerzel}
                                    style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                    onClick={() => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        setPivotDrillDown({ kuerzel, measurements: data.measurements });
                                      }
                                    }}
                                    title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                    onMouseEnter={(e) => {
                                      if (data.measurements && data.measurements.length > 0) {
                                        e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{kuerzel}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatSollzeit(data.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                    <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatGrade(data.avgGrade)}</td>
                                  </tr>
                                );
                              })}
                              <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{grandTotal.count}</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(grandTotal.totalTime)}</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</td>
                                <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Kürzel mit x__ Zeitarten am Ende */}
                        {Object.keys(analysis.excludedKuerzels || {}).length > 0 && (() => {
                        const excludedKuerzels = Object.keys(analysis.excludedKuerzels || {}).sort((a, b) => {
                          if (a === 'Kein Kürzel') return 1;
                          if (b === 'Kein Kürzel') return -1;
                          return a.localeCompare(b);
                        });
                        let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                        excludedKuerzels.forEach(kuerzel => {
                          const data = analysis.excludedKuerzels[kuerzel];
                          excludedGrandTotal.count += data.count;
                          excludedGrandTotal.totalTime += data.totalTime;
                          excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                          excludedGrandTotal.grades.push(...data.grades);
                        });
                        return (
                          <div className="analysis-table-wrapper" style={{ marginTop: '30px' }}>
                            <h4 style={{ marginBottom: '10px', color: darkMode ? '#f9fafb' : '#111827' }}>Kürzel mit nicht zu verwendender Zeit (x__)</h4>
                            <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                              <thead>
                                <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Kürzel</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Tätigkeit</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                                  <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {excludedKuerzels.map(kuerzel => {
                                  const data = analysis.excludedKuerzels[kuerzel];
                                  const description = getKuerzelDescription(kuerzel);
                                  return (
                                    <tr 
                                      key={kuerzel}
                                      style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                      onClick={() => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          setPivotDrillDown({ kuerzel, measurements: data.measurements });
                                        }
                                      }}
                                      title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                      onMouseEnter={(e) => {
                                        if (data.measurements && data.measurements.length > 0) {
                                          e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{kuerzel}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                      <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                    </tr>
                                  );
                                })}
                                <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{excludedGrandTotal.count}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(excludedGrandTotal.totalTime)}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                      </>
                    );
                  })()}

                  {/* Zeitstrahl: Drill-down pro WA-Nr (zugeklappt), aufgeklappt: Tätigkeiten mit positioniertem Balken */}
                  {analysisViewMode === 'zeitstrahl' && (() => {
                    const flatMeasurements = Object.values(analysis.byTimeType || {}).flatMap(d => d.measurements || []);
                    const byWaNr = {};
                    flatMeasurements.forEach(m => {
                      const key = m.resumeNumber ?? '—';
                      if (!byWaNr[key]) byWaNr[key] = [];
                      byWaNr[key].push(m);
                    });
                    const orderKeys = Object.keys(byWaNr).sort((a, b) => {
                      if (a === '—') return 1;
                      if (b === '—') return -1;
                      const na = Number(a);
                      const nb = Number(b);
                      if (!isNaN(na) && !isNaN(nb)) return na - nb;
                      return String(a).localeCompare(String(b));
                    });
                    const fallbackColor = '#9ca3af';
                    const getColor = (m) => {
                      const key = (m.timeType == null || m.timeType === '') ? 'Keine Zeitart' : m.timeType;
                      const fromConfig = (timeTypes.find(t => t.name === key) || {}).color;
                      const fromDefaults = (defaultTimeTypes.find(d => d.name === key) || {}).color;
                      return fromConfig || fromDefaults || (key === 'Keine Zeitart' ? '#94a3b8' : null) || fallbackColor;
                    };
                    const toT = (x) => {
                      const v = x?.timestamp ? new Date(x.timestamp).getTime() : 0;
                      return (typeof v === 'number' && !isNaN(v)) ? v : 0;
                    };
                    const toggleWa = (waKey) => {
                      setZeitstrahlExpandedWaNr(prev => {
                        const n = new Set(prev);
                        if (n.has(waKey)) n.delete(waKey); else n.add(waKey);
                        return n;
                      });
                    };
                    return (
                      <div className="compact-zeitstrahl" style={{ marginTop: 16 }}>
                        {orderKeys.map(waKey => {
                          const list = [...(byWaNr[waKey] || [])].sort((a, b) => toT(a) - toT(b));
                          const totalIstWa = list.reduce((s, m) => s + (m.individualTime || 0), 0);
                          const label = waKey === '—' ? 'Ohne WA-Nr' : `WA-Nr ${waKey}`;
                          const expanded = zeitstrahlExpandedWaNr.has(waKey);
                          const tätigkeitenText = list.map(m => m.kuerzel || m.activity || ('#' + (m.number ?? ''))).filter(Boolean).join(' · ') || '—';
                          return (
                            <div key={waKey} className="compact-zeitstrahl-wa-block">
                              <div
                                className="compact-zeitstrahl-wa-header"
                                onClick={() => toggleWa(waKey)}
                                title={expanded ? 'Zuklappen' : 'Aufklappen'}
                              >
                                {/* Balken als Hintergrund – stabile Position unabhängig von Text */}
                                <div className="compact-zeitstrahl-header-track-bg" aria-hidden="true">
                                  <div className="compact-zeitstrahl-track">
                                    {list.map((m, idx) => {
                                      const pct = totalIstWa > 0 ? ((m.individualTime || 0) / totalIstWa) * 100 : 0;
                                      const color = getColor(m);
                                      return (
                                        <div
                                          key={`${waKey}-${m.number}-${idx}`}
                                          className="compact-zeitstrahl-segment"
                                          style={{ width: pct + '%', backgroundColor: color, minWidth: pct > 0 ? 2 : 0 }}
                                          onMouseEnter={(e) => { clearTimeout(zeitstrahlHoverTimeoutRef.current); setZeitstrahlHover({ m, rect: e.currentTarget.getBoundingClientRect() }); }}
                                          onMouseLeave={() => { zeitstrahlHoverTimeoutRef.current = setTimeout(() => setZeitstrahlHover(null), 180); }}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="compact-zeitstrahl-wa-header-fg">
                                  <span className="compact-zeitstrahl-chevron">{expanded ? '▼' : '▶'}</span>
                                  <div className="compact-zeitstrahl-label" title={`${label} · ${formatTime(totalIstWa)}`}>
                                    {label} · {formatTime(totalIstWa)}
                                  </div>
                                  <div className="compact-zeitstrahl-header-spacer" />
                                </div>
                              </div>
                              {!expanded && (
                                <div className="compact-zeitstrahl-sublabel compact-zeitstrahl-sublabel-indent" title={tätigkeitenText}>
                                  {tätigkeitenText}
                                </div>
                              )}
                              {expanded && (
                                <div className="compact-zeitstrahl-drilldown">
                                  {list.map((m, idx) => {
                                    const pctStart = totalIstWa > 0
                                      ? (list.slice(0, idx).reduce((s, x) => s + (x.individualTime || 0), 0) / totalIstWa) * 100
                                      : 0;
                                    const pct = totalIstWa > 0 ? ((m.individualTime || 0) / totalIstWa) * 100 : 0;
                                    const color = getColor(m);
                                    const rowLabel = [m.kuerzel, m.activity, '#' + (m.number ?? '')].find(Boolean) || '—';
                                    return (
                                      <div
                                        key={`${waKey}-${m.number}-${idx}`}
                                        className="compact-zeitstrahl-activity-row"
                                      >
                                        {/* Balken als Hintergrund – stabile Fläche, Text kann umbrechen */}
                                        <div className="compact-zeitstrahl-activity-bar-bg" aria-hidden="true">
                                          <div className="compact-zeitstrahl-track compact-zeitstrahl-track-positioned">
                                            <div
                                              className="compact-zeitstrahl-segment"
                                              style={{
                                                position: 'absolute',
                                                left: pctStart + '%',
                                                width: pct + '%',
                                                height: '100%',
                                                backgroundColor: color,
                                                borderRadius: 2
                                              }}
                                              onMouseEnter={(e) => { clearTimeout(zeitstrahlHoverTimeoutRef.current); setZeitstrahlHover({ m, rect: e.currentTarget.getBoundingClientRect() }); }}
                                              onMouseLeave={() => { zeitstrahlHoverTimeoutRef.current = setTimeout(() => setZeitstrahlHover(null), 180); }}
                                            />
                                          </div>
                                        </div>
                                        <div className="compact-zeitstrahl-activity-row-fg">
                                          <span className="compact-zeitstrahl-activity-spacer" />
                                          <div
                                            className="compact-zeitstrahl-activity-label"
                                            onMouseEnter={(e) => { clearTimeout(zeitstrahlHoverTimeoutRef.current); setZeitstrahlHover({ m, rect: e.currentTarget.getBoundingClientRect() }); }}
                                            onMouseLeave={() => { zeitstrahlHoverTimeoutRef.current = setTimeout(() => setZeitstrahlHover(null), 180); }}
                                          >
                                            #{m.number} {rowLabel} · {formatTime(m.individualTime || 0)}
                                          </div>
                                          <div className="compact-zeitstrahl-activity-row-spacer" />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {zeitstrahlHover && (() => {
                          const { m, rect } = zeitstrahlHover;
                          const descZeitart = (timeTypes.find(t => t.name === m.timeType) || defaultTimeTypes.find(d => d.name === m.timeType))?.description;
                          const kuerzelMatch = (ablaufDbData && Array.isArray(ablaufDbData)) ? ablaufDbData.find(k => k.kuerzel === m.kuerzel) : null;
                          const descKuerzel = kuerzelMatch ? (kuerzelMatch.beschreibung || kuerzelMatch.fullText || '') : '';
                          const eaArr = m.einflussgroessen && typeof m.einflussgroessen === 'object' && !Array.isArray(m.einflussgroessen)
                            ? Object.entries(m.einflussgroessen).map(([k, v]) => `${k}: ${v != null ? String(v) : ''}`)
                            : [];
                          const photos = (measurementPhotos[m.number] || []);
                          let left = rect.left;
                          let top = rect.bottom + 6;
                          const w = 320;
                          if (left + w > window.innerWidth) left = window.innerWidth - w - 8;
                          if (left < 8) left = 8;
                          if (top + 340 > window.innerHeight - 8 && rect.top > 360) { top = rect.top - 344; }
                          return (
                            <div
                              className={`zeitstrahl-popover ${darkMode ? 'zeitstrahl-popover-dark' : ''}`}
                              style={{ position: 'fixed', left, top, maxWidth: w, maxHeight: 340, zIndex: 10000 }}
                              onMouseEnter={() => clearTimeout(zeitstrahlHoverTimeoutRef.current)}
                              onMouseLeave={() => setZeitstrahlHover(null)}
                            >
                              <div className="zeitstrahl-popover-header">
                                #{m.number} {m.kuerzel || m.activity || '—'} · {formatTime(m.individualTime || 0)} · {m.timeType || 'Keine Zeitart'}
                              </div>
                              {descZeitart && <div className="zeitstrahl-popover-line"><strong>Zeitart:</strong> {descZeitart}</div>}
                              {descKuerzel && <div className="zeitstrahl-popover-line"><strong>Tätigkeit:</strong> {descKuerzel}</div>}
                              {m.note && <div className="zeitstrahl-popover-line"><strong>Notiz:</strong> {m.note}</div>}
                              {eaArr.length > 0 && <div className="zeitstrahl-popover-line"><strong>Einflussgrößen:</strong> {eaArr.join(', ')}</div>}
                              {photos.length > 0 && (
                                <div className="zeitstrahl-popover-photos">
                                  {photos.map((p) => (
                                    <img
                                      key={p.id}
                                      src={p.fileData}
                                      alt={p.fileName}
                                      title={p.fileName}
                                      onClick={() => { setPhotoPreview(p.fileData); setZeitstrahlHover(null); }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {orderKeys.length === 0 && (
                          <div className="compact-zeitstrahl-empty">Keine Messdaten (ohne x__) für den Zeitstrahl.</div>
                        )}
                        {orderKeys.length > 0 && (
                          <div className="compact-zeitstrahl-hint" style={{ marginTop: 8, fontSize: '0.85rem', color: darkMode ? '#9ca3af' : '#6b7280' }}>
                            💡 Klicken zum Auf-/Zuklappen; Hover für Beschreibung, Notiz, EA, Fotos
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Pivot-Tabelle */}
                  {analysisViewMode === 'pivot' && (() => {
                    const activities = Object.keys(analysis.pivot).sort();
                    // Filtere "x__" Zeitarten aus der Pivot-Tabelle
                    const timeTypes = [...new Set(Object.values(analysis.pivot).flatMap(activity => Object.keys(activity)))].filter(tt => tt !== 'x__').sort();
                    
                    // Berechne Gesamtzeilen und -spalten
                    const rowTotals = {};
                    const colTotals = {};
                    let grandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                    
                    activities.forEach(activity => {
                      rowTotals[activity] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                      Object.keys(analysis.pivot[activity]).filter(tt => tt !== 'x__').forEach(timeType => {
                        const cell = analysis.pivot[activity][timeType];
                        if (cell) {
                          rowTotals[activity].count += cell.count;
                          rowTotals[activity].totalTime += cell.totalTime;
                          rowTotals[activity].totalVorgabeTime += (cell.totalVorgabeTime || 0);
                          rowTotals[activity].grades.push(...cell.grades);
                          grandTotal.count += cell.count;
                          grandTotal.totalTime += cell.totalTime;
                          grandTotal.totalVorgabeTime += (cell.totalVorgabeTime || 0);
                          grandTotal.grades.push(...cell.grades);
                        }
                      });
                      rowTotals[activity].avgGrade = calculateAvgGrade(rowTotals[activity].grades);
                    });
                    
                    timeTypes.forEach(timeType => {
                      colTotals[timeType] = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                      activities.forEach(activity => {
                        if (analysis.pivot[activity][timeType]) {
                          const cell = analysis.pivot[activity][timeType];
                          colTotals[timeType].count += cell.count;
                          colTotals[timeType].totalTime += cell.totalTime;
                          colTotals[timeType].totalVorgabeTime += (cell.totalVorgabeTime || 0);
                          colTotals[timeType].grades.push(...cell.grades);
                        }
                      });
                      colTotals[timeType].avgGrade = calculateAvgGrade(colTotals[timeType].grades);
                    });
                    
                    return (
                <div className="pivot-container">
                  <div className="pivot-table-wrapper">
                    <table className="pivot-table">
                      <thead>
                        <tr>
                          <th className="pivot-corner">Tätigkeit / Zeitart</th>
                          {timeTypes.map(timeType => (
                            <th key={timeType} className="pivot-col-header">
                              <div className="pivot-header-content">
                                <span className="pivot-header-title">{timeType}</span>
                              </div>
                            </th>
                          ))}
                          <th className="pivot-row-total-header">Gesamt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activities.map(activity => (
                          <tr key={activity} className="pivot-row">
                            <td className="pivot-row-header">
                              <div className="pivot-row-header-content">
                                <span className="pivot-row-title">{activity}</span>
                              </div>
                            </td>
                            {timeTypes.map(timeType => {
                              const cell = analysis.pivot[activity][timeType];
                              return (
                                <td 
                                  key={timeType} 
                                  className={`pivot-cell ${cell && cell.count > 0 ? 'has-data' : 'no-data'}`}
                                  onClick={() => {
                                    if (cell && cell.count > 0) {
                                      setPivotDrillDown({ activity, timeType, measurements: cell.measurements });
                                    }
                                  }}
                                  title={cell && cell.count > 0 ? `Klicken für Details: ${cell.count} Messpunkt(e)` : 'Keine Daten'}
                                >
                                  {cell && cell.count > 0 ? (
                                    <div className="pivot-cell-content">
                                      <div className="pivot-cell-count">{cell.count}</div>
                                      <div className="pivot-cell-time">{formatTime(cell.totalTime)}</div>
                                      <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(cell.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                                      {formatGrade(cell.avgGrade) && <div className="pivot-cell-grade">{formatGrade(cell.avgGrade)}</div>}
                                    </div>
                                  ) : (
                                    <div className="pivot-cell-empty">—</div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="pivot-row-total">
                              <div className="pivot-cell-content">
                                <div className="pivot-cell-count">{rowTotals[activity].count}</div>
                                <div className="pivot-cell-time">{formatTime(rowTotals[activity].totalTime)}</div>
                                <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(rowTotals[activity].totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                              </div>
                            </td>
                          </tr>
                        ))}
                        <tr className="pivot-total-row">
                          <td className="pivot-col-total-header">Gesamt</td>
                          {timeTypes.map(timeType => (
                            <td key={timeType} className="pivot-col-total">
                              <div className="pivot-cell-content">
                                <div className="pivot-cell-count">{colTotals[timeType].count}</div>
                                <div className="pivot-cell-time">{formatTime(colTotals[timeType].totalTime)}</div>
                                <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(colTotals[timeType].totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                              </div>
                            </td>
                          ))}
                          <td className="pivot-grand-total">
                            <div className="pivot-cell-content">
                              <div className="pivot-cell-count">{grandTotal.count}</div>
                              <div className="pivot-cell-time">{formatTime(grandTotal.totalTime)}</div>
                              <div className="pivot-cell-time" style={{ fontSize: '0.85em', opacity: 0.8 }}>SOLL: {formatSollzeit(grandTotal.totalVorgabeTime || 0, decimalPlacesSollzeiten)} min</div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="pivot-legend">
                    <div className="pivot-legend-item">
                      <span className="pivot-legend-label">Anzahl</span>
                      <span className="pivot-legend-desc">Anzahl der Messpunkte</span>
                    </div>
                    <div className="pivot-legend-item">
                      <span className="pivot-legend-label">Gesamtzeit</span>
                      <span className="pivot-legend-desc">Summe der Einzelzeiten</span>
                    </div>
                    <div className="pivot-legend-item">
                      <span className="pivot-legend-label">Ø Leistungsgrad</span>
                      <span className="pivot-legend-desc">Durchschnittlicher Leistungsgrad</span>
                    </div>
                    <div className="pivot-legend-hint">
                      💡 <strong>Tipp:</strong> Klicken Sie auf eine Zelle, um die einzelnen Messpunkte zu sehen
                    </div>
                  </div>
                </div>
                    );
                  })()}
                  
                  {/* x__ Zeitarten am Ende für Pivot */}
                  {analysisViewMode === 'pivot' && Object.keys(analysis.excludedTimeTypes || {}).length > 0 && (() => {
                    const excludedTimeTypes = Object.keys(analysis.excludedTimeTypes || {}).sort();
                    let excludedGrandTotal = { count: 0, totalTime: 0, totalVorgabeTime: 0, grades: [] };
                    excludedTimeTypes.forEach(timeType => {
                      const data = analysis.excludedTimeTypes[timeType];
                      excludedGrandTotal.count += data.count;
                      excludedGrandTotal.totalTime += data.totalTime;
                      excludedGrandTotal.totalVorgabeTime += (data.totalVorgabeTime || 0);
                      excludedGrandTotal.grades.push(...data.grades);
                    });
                    const getTimeTypeDescription = (timeTypeName) => {
                      const timeTypeObj = defaultTimeTypes.find(tt => tt.name === timeTypeName);
                      return timeTypeObj ? timeTypeObj.description : '';
                    };
                    
                    return (
                      <div className="analysis-table-wrapper" style={{ marginTop: '30px' }}>
                        <h4 style={{ marginBottom: '10px', color: darkMode ? '#f9fafb' : '#111827' }}>Nicht zu verwendende Zeit (x__)</h4>
                        <table className="analysis-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                          <thead>
                            <tr style={{ backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Zeitart</th>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Beschreibung</th>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Anzahl</th>
                              <th style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '10px', textAlign: 'center', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>Gesamtzeit (IST)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {excludedTimeTypes.map(timeType => {
                              const data = analysis.excludedTimeTypes[timeType];
                              const description = getTimeTypeDescription(timeType);
                              return (
                                <tr 
                                  key={timeType}
                                  style={{ cursor: data.measurements && data.measurements.length > 0 ? 'pointer' : 'default' }}
                                  onClick={() => {
                                    if (data.measurements && data.measurements.length > 0) {
                                      setPivotDrillDown({ timeType, measurements: data.measurements });
                                    }
                                  }}
                                  title={data.measurements && data.measurements.length > 0 ? `Klicken für Details: ${data.count} Messpunkt(e)` : ''}
                                  onMouseEnter={(e) => {
                                    if (data.measurements && data.measurements.length > 0) {
                                      e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', fontWeight: 'bold', color: darkMode ? '#e5e7eb' : '#111827' }}>{timeType}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>{description || '-'}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{data.count}</td>
                                  <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(data.totalTime)}</td>
                                </tr>
                              );
                            })}
                            <tr style={{ backgroundColor: darkMode ? '#1f2937' : '#e5e7eb', fontWeight: 'bold' }}>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}>Gesamt</td>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', color: darkMode ? '#e5e7eb' : '#111827' }}></td>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{excludedGrandTotal.count}</td>
                              <td style={{ border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, padding: '8px', textAlign: 'center', color: darkMode ? '#e5e7eb' : '#111827' }}>{formatTime(excludedGrandTotal.totalTime)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="pivot-drilldown">
                  <div className="drilldown-header">
                    <h4>
                      {pivotDrillDown.activity ? `${pivotDrillDown.activity} × ${pivotDrillDown.timeType}` : 
                       pivotDrillDown.timeType ? `Zeitart: ${pivotDrillDown.timeType}` :
                       pivotDrillDown.kuerzel ? `Kürzel: ${pivotDrillDown.kuerzel}` : 'Details'}
                    </h4>
                    <div className="drilldown-summary">
                      <span>{pivotDrillDown.measurements.length} Messpunkt(e)</span>
                      <span>Gesamtzeit (IST): {formatTime(pivotDrillDown.measurements.reduce((sum, m) => sum + m.individualTime, 0))}</span>
                      <span>Vorgabezeit (SOLL): {formatTime(pivotDrillDown.measurements.reduce((sum, m) => sum + (m.vorgabeTime || 0), 0))}</span>
                    </div>
                  </div>
                  <div className="drilldown-measurements">
                    {pivotDrillDown.measurements.map((m, idx) => (
                      <div key={idx} className="drilldown-item">
                        <div className="drilldown-item-header">
                          <span className="drilldown-number">#{m.number}</span>
                          <span className="drilldown-grade">Leistungsgrad: {m.grade}%</span>
                          <span className="drilldown-time">IST: {formatTime(m.individualTime)}</span>
                          {m.vorgabeTime && (
                            <span className="drilldown-time" style={{ fontSize: '0.9em', opacity: 0.8 }}>SOLL: {formatTime(m.vorgabeTime)}</span>
                          )}
                        </div>
                        <div className="drilldown-item-details">
                          {m.timestamp && (
                            <span>Zeitpunkt: {new Date(m.timestamp).toLocaleString('de-DE')}</span>
                          )}
                          {m.note && (
                            <span className="drilldown-note">Notiz: {m.note}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        <div className="measurements-list">
          {measurements.length === 0 ? (
            <div className="no-measurements">
              <div className="empty-icon">📊</div>
              <p>{t('common.no_measurements_yet')}</p>
              <p className="empty-hint">Starten Sie die Erfassung und drücken Sie einen Efficiency-Score-Button</p>
              {selectedSession?.isDemo && (
                <p className="demo-hint">⚠️ Demo-Modus: Maximal 20 Messpunkte möglich</p>
              )}
            </div>
          ) : (
            <>
              {selectedSession?.isDemo && measurements.length >= 20 && (
                <div className="demo-limit-warning">
                  ⚠️ Demo-Limit erreicht: 20/20 Messpunkte
                </div>
              )}
              {selectedSession?.isDemo && (
                <div className="demo-counter">
                  Messpunkte: {measurements.length}/20
                </div>
              )}
              {[...measurements].reverse().map((measurement) => (
                <div 
                  key={measurement.number} 
                  className={`measurement-item ${compactView ? 'compact' : 'detailed'} ${measurement.isNote ? 'note-item' : ''} ${measurement.excluded ? 'excluded' : ''} ${draggedMeasurement?.number === measurement.number ? 'dragging' : ''}`}
                  draggable={enableDragDrop}
                  onDragStart={(e) => {
                    console.log('🟢 DRAG START:', {
                      draggedMessNr: measurement.number,
                      enableDragDrop
                    });
                    debugLog('App.jsx:3010', 'onDragStart JSX called', {enableDragDrop,draggable:enableDragDrop,measurementNumber:measurement.number}, 'D');
                    e.stopPropagation();
                    handleDragStart(e, measurement);
                  }}
                  onDragOver={(e) => {
                    debugLog('App.jsx:3014', 'onDragOver JSX called', {enableDragDrop,draggable:enableDragDrop,measurementNumber:measurement.number,hasDraggedMeasurement:!!draggedMeasurement}, 'B');
                    e.preventDefault();
                    e.stopPropagation();
                    handleDragOver(e);
                  }}
                  onDrop={(e) => {
                    console.log('🎯 onDrop JSX Event:', {
                      dropPositionMessNr: measurement.number,
                      draggedMessNr: draggedMeasurement?.number || 'KEIN DRAG ELEMENT',
                      enableDragDrop,
                      draggable: enableDragDrop
                    });
                    debugLog('App.jsx:3019', 'onDrop JSX called', {enableDragDrop,draggable:enableDragDrop,measurementNumber:measurement.number,hasDraggedMeasurement:!!draggedMeasurement,draggedMeasurementNumber:draggedMeasurement?.number}, 'A');
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(e, measurement);
                  }}
                  onDragEnd={(e) => {
                    debugLog('App.jsx:3024', 'onDragEnd JSX called', {measurementNumber:measurement.number,hasDraggedMeasurement:!!draggedMeasurement}, 'A');
                    e.stopPropagation();
                    handleDragEnd();
                  }}
                  onClick={(e) => {
                    if (draggedMeasurement) {
                      debugLog('App.jsx:3028', 'onClick during drag - blocking', {measurementNumber:measurement.number,hasDraggedMeasurement:!!draggedMeasurement}, 'C');
                    }
                    if (draggedMeasurement) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    if (e.target.closest('.btn-edit-measurement') || 
                        e.target.closest('.btn-insert-measurement') || 
                        e.target.closest('.btn-delete-measurement')) return;
                    handleMeasurementClick(measurement, e);
                  }}
                  onDoubleClick={(e) => {
                    if (draggedMeasurement) {
                      debugLog('App.jsx:3040', 'onDoubleClick during drag - blocking', {measurementNumber:measurement.number,hasDraggedMeasurement:!!draggedMeasurement}, 'C');
                    }
                    if (draggedMeasurement) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    e.stopPropagation();
                    handleEditMeasurement(measurement);
                  }}
                >
                  {compactView ? (
                    <div className="measurement-compact-content">
                      <div className="measurement-actions-left">
                        <button
                          className="btn btn-insert-measurement"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInsertMeasurement(measurement.number);
                          }}
                          title="Messpunkt davor einfügen"
                        >
                          +
                        </button>
                        <button
                          className="btn btn-delete-measurement"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMeasurement(measurement);
                          }}
                          title="Messpunkt löschen"
                          disabled={!isMeasurementEmpty(measurement)}
                        >
                          -
                        </button>
                      </div>
                      <div className="measurement-number-small">
                        #{measurement.number}
                        {showResumeNumber && measurement.resumeNumber && measurement.resumeNumber >= 1 && (
                          <span className="resume-badge">WA{measurement.resumeNumber}</span>
                        )}
                        {measurement.excluded && <span className="excluded-badge">[ausgeklammert]</span>}
                      </div>
                      {showActivity && (
                        <div className="measurement-activity">
                          {measurement.isNote ? `📝 ${measurement.note}` : (measurement.activity || '-')}
                        </div>
                      )}
                      {/* Einflussgrößen vor dem Leistungsgrad (wenn Option aktiviert) */}
                      {showEinflussgroessenCompact && measurement.einflussgroessen && Object.keys(measurement.einflussgroessen).length > 0 && (
                        <div className="measurement-einflussgroessen-compact">
                          {Object.entries(measurement.einflussgroessen).slice(0, 3).map(([name, value]) => (
                            <span key={name} className="einflussgroesse-badge-compact">
                              {name}: {value}
                            </span>
                          ))}
                          {Object.keys(measurement.einflussgroessen).length > 3 && (
                            <span className="einflussgroesse-more">+{Object.keys(measurement.einflussgroessen).length - 3}</span>
                          )}
                        </div>
                      )}
                      {/* Foto-Thumbnail in kompakter Ansicht (vor dem Leistungsgrad) */}
                      {measurementPhotos[measurement.number] && measurementPhotos[measurement.number].length > 0 && (
                        <div className="measurement-photos-compact" style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                          {measurementPhotos[measurement.number].slice(0, 2).map((photo) => (
                            <img
                              key={photo.id}
                              src={photo.fileData}
                              alt={photo.fileName}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPhotoPreview(photo.fileData);
                              }}
                              style={{
                                width: '32px',
                                height: '32px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #e5e7eb'
                              }}
                              title={`${photo.fileName} (Klicken zum Vergrößern)`}
                            />
                          ))}
                          {measurementPhotos[measurement.number].length > 2 && (
                            <span style={{ fontSize: '0.8rem', color: '#6b7280', alignSelf: 'center' }}>
                              +{measurementPhotos[measurement.number].length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      {showGrade && !measurement.isNote && (
                        <div className="measurement-grade-compact">
                          {measurement.grade}
                        </div>
                      )}
                      {/* Zeiten (immer sichtbar) */}
                      {showIndividualTime && (
                        <div className="measurement-individual-time-compact">
                          {formatTime(measurement.individualTime)}
                        </div>
                      )}
                      {showProgressTime && (
                        <div className="measurement-time">{formatTime(measurement.progressTime)}</div>
                      )}
                      <button
                        className="btn btn-edit-measurement"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditMeasurement(measurement);
                        }}
                        title="Bearbeiten"
                      >
                        ✏️
                      </button>
                    </div>
                  ) : (
                    <div className="measurement-detailed-content">
                      <div className="measurement-header">
                        <div className="measurement-number">
                          #{measurement.number}
                          {measurement.resumeNumber && measurement.resumeNumber >= 1 && (
                            <span className="resume-badge">WA{measurement.resumeNumber}</span>
                          )}
                          {measurement.excluded && <span className="excluded-badge">[ausgeklammert]</span>}
                        </div>
                        <div className="measurement-header-actions">
                          <button
                            className="btn btn-insert-measurement"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInsertMeasurement(measurement.number);
                            }}
                            title="Messpunkt davor einfügen"
                          >
                            +
                          </button>
                          <button
                            className="btn btn-delete-measurement"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteMeasurement(measurement);
                            }}
                            title="Messpunkt löschen"
                            disabled={!isMeasurementEmpty(measurement)}
                          >
                            -
                          </button>
                          <button
                            className="btn btn-edit-measurement"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditMeasurement(measurement);
                            }}
                            title="Bearbeiten"
                          >
                            ✏️
                          </button>
                        </div>
                      </div>
                      <div className="measurement-details">
                        {showResumeNumber && measurement.resumeNumber && measurement.resumeNumber > 1 && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">WA-Nr:</span>
                            <span className="detail-value">{measurement.resumeNumber}</span>
                          </div>
                        )}
                        {showActivity && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">Tätigkeit:</span>
                            <span className="detail-value">{measurement.isNote ? `📝 ${measurement.note}` : (measurement.activity || '-')}</span>
                          </div>
                        )}
                        {showGrade && !measurement.isNote && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">Leistungsgrad:</span>
                            <span className="detail-value">{measurement.grade}</span>
                          </div>
                        )}
                        <div className="measurement-detail-item">
                          <span className="detail-label">Zeitart:</span>
                          <span className="detail-value">{measurement.timeType || '-'}</span>
                        </div>
                        {showIndividualTime && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">Einzelzeit:</span>
                            <span className="detail-value">{formatTime(measurement.individualTime)}</span>
                          </div>
                        )}
                        {showProgressTime && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">Fortschrittszeit:</span>
                            <span className="detail-value">{formatTime(measurement.progressTime)}</span>
                          </div>
                        )}
                        {measurement.note && !measurement.isNote && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">Notiz:</span>
                            <span className="detail-value">{measurement.note}</span>
                          </div>
                        )}
                        {measurement.timestamp && (
                          <div className="measurement-detail-item">
                            <span className="detail-label">Zeitstempel:</span>
                            <span className="detail-value">{new Date(measurement.timestamp).toLocaleString('de-DE')}</span>
                          </div>
                        )}
                        {showEaKombinationen && measurement.einflussgroessen && Object.keys(measurement.einflussgroessen).length > 0 && (
                          <div className="measurement-detail-item measurement-einflussgroessen">
                            <span className="detail-label">Einflussgrößen:</span>
                            <div className="einflussgroessen-display">
                              {Object.entries(measurement.einflussgroessen).map(([name, value]) => (
                                <span key={name} className="einflussgroesse-badge">
                                  {name}: {value}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Fotos in Detail-Ansicht */}
                        {measurementPhotos[measurement.number] && measurementPhotos[measurement.number].length > 0 && (
                          <div className="measurement-detail-item measurement-photos">
                            <span className="detail-label">Fotos:</span>
                            <div className="photos-display" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                              {measurementPhotos[measurement.number].map((photo) => (
                                <div key={photo.id} style={{ position: 'relative' }}>
                                  <img
                                    src={photo.fileData}
                                    alt={photo.fileName}
                                    onClick={() => setPhotoPreview(photo.fileData)}
                                    style={{
                                      width: '100px',
                                      height: '100px',
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      border: '2px solid #e5e7eb'
                                    }}
                                    title={`${photo.fileName} (Klicken zum Vergrößern)`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
          </div>
        </div>
      </div>

      {editingMeasurement && (
        <div className="edit-overlay" onClick={handleCancelEdit}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>Messpunkt #{editingMeasurement.number} bearbeiten</h3>
              <button className="btn-close" onClick={handleCancelEdit}>×</button>
            </div>
            <div className="edit-content">
              <div className="edit-field">
                <label style={{ color: darkMode ? '#f9fafb' : '#374151' }}>Tätigkeit:</label>
                <input
                  type="text"
                  value={editActivity}
                  onChange={(e) => setEditActivity(e.target.value)}
                  className="edit-input"
                  placeholder="Tätigkeit eingeben..."
                  style={{
                    background: darkMode ? '#374151' : 'white',
                    color: darkMode ? '#f9fafb' : '#111827',
                    border: `2px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                  }}
                />
                {editingMeasurement.kuerzel && (
                  <div 
                    className="edit-kuerzel-info"
                    style={{ 
                      marginTop: '4px', 
                      fontSize: '0.85rem', 
                      color: darkMode ? '#9ca3af' : '#6b7280',
                      fontStyle: 'italic'
                    }}
                  >
                    Kürzel: {editingMeasurement.kuerzel}
                  </div>
                )}
              </div>
              <div className="edit-field">
                <label style={{ color: darkMode ? '#f9fafb' : '#374151' }}>Notiz:</label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  className="edit-textarea"
                  placeholder={t('common.notiz_placeholder_short')}
                  rows="4"
                  style={{
                    background: darkMode ? '#374151' : 'white',
                    color: darkMode ? '#f9fafb' : '#111827',
                    border: `2px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                  }}
                />
                <button
                  className={`btn btn-voice ${isListeningEdit ? 'listening' : ''}`}
                  onMouseDown={handleStartListeningEdit}
                  onMouseUp={handleStopListeningEdit}
                  onMouseLeave={handleStopListeningEdit}
                  onTouchStart={handleStartListeningEdit}
                  onTouchEnd={handleStopListeningEdit}
                  disabled={!recognitionEdit || !isSpeechRecognitionSupported}
                  title={isSpeechRecognitionSupported 
                    ? "Sprach-Eingabe (gedrückt halten)"
                    : "Sprachsteuerung wird in diesem Browser nicht unterstützt. Bitte verwenden Sie Chrome oder Edge."}
                >
                  {isListeningEdit ? '🎤 Aufnahme...' : '🎤 Sprache'}
                </button>
              </div>
              {editingMeasurement && (editingMeasurement.progressTime > 0 || editingMeasurement.grade > 0) && (
                <div className="edit-field">
                  <label style={{ color: darkMode ? '#f9fafb' : '#374151' }}>
                    <input
                      type="checkbox"
                      checked={editExcluded}
                      onChange={(e) => setEditExcluded(e.target.checked)}
                      style={{
                        accentColor: darkMode ? '#818cf8' : '#667eea'
                      }}
                    />
                    {t('compact.ausgeklammert')}
                  </label>
                </div>
              )}
              {/* Einflussgrößen-Bearbeitung */}
              {selectedKuerzel && selectedKuerzel.einflussgroessen && Array.isArray(selectedKuerzel.einflussgroessen) && selectedKuerzel.einflussgroessen.length > 0 && (
                <div className="edit-field">
                  <table className="einflussgroessen-table-edit" style={{ marginTop: '8px', width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#374151' }}>Einflussgröße</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: darkMode ? '#f9fafb' : '#374151' }}>Ausprägung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedKuerzel.einflussgroessen.map((einfluss, idx) => {
                        if (!einfluss || !einfluss.einflussname) return null;
                        const einflussname = einfluss.einflussname.trim();
                        if (!einflussname) return null;
                        const einheit = einfluss.einheit?.trim() || '';
                        const messwerte = Array.isArray(einfluss.messwerte) ? einfluss.messwerte : [];
                        
                        return (
                          <tr key={idx} className="einflussgroesse-table-row-edit">
                            <td className="einflussgroesse-label-cell-edit">
                              <label htmlFor={`edit-einfluss-${idx}`} className="einflussgroesse-label-edit">
                                {einflussname}
                                {einheit && <span className="einflussgroesse-einheit-edit"> ({einheit})</span>}
                              </label>
                            </td>
                            <td className="einflussgroesse-input-cell-edit">
                              <input
                                type="text"
                                id={`edit-einfluss-${idx}`}
                                list={`edit-datalist-${idx}`}
                                className="einflussgroesse-input-edit"
                                placeholder={messwerte.length > 0 ? `Auswahl oder freie Eingabe...` : 'Wert eingeben...'}
                                value={editEinflussgroessenValues?.[einflussname] || ''}
                                style={{
                                  background: darkMode ? '#374151' : 'white',
                                  color: darkMode ? '#f9fafb' : '#111827',
                                  border: `1px solid ${darkMode ? '#4b5563' : '#d1d5db'}`
                                }}
                                onFocus={(e) => {
                                  // Speichere den ursprünglichen Wert
                                  const currentValue = editEinflussgroessenValues?.[einflussname] || '';
                                  originalEinflussValuesRef.current[einflussname] = currentValue;
                                  focusedEinflussRef.current = einflussname;
                                  // Leere temporär den Wert, damit alle datalist-Optionen angezeigt werden
                                  if (currentValue) {
                                    setEditEinflussgroessenValues(prev => ({
                                      ...(prev || {}),
                                      [einflussname]: ''
                                    }));
                                  }
                                }}
                                onBlur={(e) => {
                                  const currentValue = e.target.value;
                                  const originalValue = originalEinflussValuesRef.current[einflussname] || '';
                                  const stateValue = editEinflussgroessenValues?.[einflussname] || '';
                                  
                                  // Nur wiederherstellen, wenn:
                                  // 1. Der Input-Wert leer ist UND
                                  // 2. Der State-Wert auch leer ist UND
                                  // 3. Ursprünglich ein Wert vorhanden war
                                  // (Das bedeutet, der Benutzer hat nichts eingegeben/ausgewählt)
                                  if (!currentValue && !stateValue && originalValue) {
                                    setEditEinflussgroessenValues(prev => ({
                                      ...(prev || {}),
                                      [einflussname]: originalValue
                                    }));
                                  }
                                  // Cleanup
                                  if (focusedEinflussRef.current === einflussname) {
                                    focusedEinflussRef.current = null;
                                  }
                                }}
                                onInput={(e) => {
                                  // onInput wird auch bei datalist-Auswahl ausgelöst
                                  const newValue = e.target.value;
                                  // Aktualisiere den ursprünglichen Wert, damit onBlur weiß, dass sich etwas geändert hat
                                  if (originalEinflussValuesRef.current[einflussname] !== undefined) {
                                    originalEinflussValuesRef.current[einflussname] = newValue;
                                  }
                                  setEditEinflussgroessenValues(prev => ({
                                    ...(prev || {}),
                                    [einflussname]: newValue
                                  }));
                                }}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  // Wenn der Benutzer tippt, wird der ursprüngliche Wert überschrieben
                                  if (originalEinflussValuesRef.current[einflussname] !== undefined) {
                                    originalEinflussValuesRef.current[einflussname] = newValue;
                                  }
                                  setEditEinflussgroessenValues(prev => ({
                                    ...(prev || {}),
                                    [einflussname]: newValue
                                  }));
                                }}
                              />
                              {messwerte.length > 0 && (
                                <datalist id={`edit-datalist-${idx}`}>
                                  {messwerte.map((mw, mwIdx) => (
                                    <option key={mwIdx} value={mw || ''} />
                                  ))}
                                </datalist>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Foto-Upload und -Anzeige */}
              <div className="edit-field">
                <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block', color: darkMode ? '#f9fafb' : '#374151' }}>Fotos</label>
                <div className="photo-upload-section">
                  <input
                    type="file"
                    accept="image/*"
                    capture="camera"
                    onChange={handlePhotoUpload}
                    disabled={isUploadingPhoto}
                    style={{ display: 'none' }}
                    id="photo-upload-input"
                  />
                  <label htmlFor="photo-upload-input" className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                    {isUploadingPhoto ? '📷 Wird hochgeladen...' : '📷 Foto hinzufügen'}
                  </label>
                  {editPhotos.length > 0 && (
                    <div className="photo-preview-list" style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {editPhotos.map((photo) => (
                        <div key={photo.id} className="photo-preview-item" style={{ position: 'relative', display: 'inline-block' }}>
                          <img
                            src={photo.fileData}
                            alt={photo.fileName}
                            onClick={() => setPhotoPreview(photo.fileData)}
                            style={{
                              width: '80px',
                              height: '80px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              border: `2px solid ${darkMode ? '#4b5563' : '#e5e7eb'}`
                            }}
                            title="Klicken zum Vergrößern"
                          />
                          <button
                            onClick={() => handleDeletePhoto(photo.id)}
                            style={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                              background: darkMode ? '#dc2626' : '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = darkMode ? '#b91c1c' : '#dc2626';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = darkMode ? '#dc2626' : '#ef4444';
                            }}
                            title={t('dialog.photo_delete.title')}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="edit-info" style={{ color: darkMode ? '#d1d5db' : '#6b7280' }}>
                <div>Fortschrittszeit: {formatTime(editingMeasurement.progressTime)}</div>
                <div>Einzelzeit: {formatTime(editingMeasurement.individualTime)}</div>
                <div>Leistungsgrad: {editingMeasurement.grade}</div>
                <div>Zeitart: {editingMeasurement.timeType}</div>
                {editingMeasurement.resumeNumber && (
                  <div>Wiederaufnahme-Nr: {editingMeasurement.resumeNumber}</div>
                )}
              </div>
            </div>
            <div className="edit-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleSaveEdit}
                title="Änderungen speichern - Speichert alle Änderungen am Messpunkt (Leistungsgrad, Aktivität, Zeittyp, Notiz, Ausgeschlossen-Status). Die Einzelzeit wird automatisch neu berechnet"
              >
                {t('common.save')}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleCancelEdit}
                title="Bearbeitung abbrechen - Verwirft alle Änderungen und schließt den Bearbeitungsdialog"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Foto-Lightbox */}
      {photoPreview && (
        <div className="photo-lightbox" onClick={() => setPhotoPreview(null)}>
          <div className="photo-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="photo-lightbox-close" onClick={() => setPhotoPreview(null)}>×</button>
            <img src={photoPreview} alt="Foto-Vorschau" />
          </div>
        </div>
      )}

      {/* Foto-Lösch-Dialog */}
      {showDeletePhotoDialog && (
        <div className="edit-overlay" onClick={handleCancelDeletePhoto}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>{t('dialog.photo_delete.title')}</h3>
              <button className="btn-close" onClick={handleCancelDeletePhoto}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('dialog.photo_delete.confirm')}
              </p>
            </div>
            <div className="edit-actions">
              <button className="btn btn-danger" onClick={handleConfirmDeletePhoto}>
                {t('dialog.delete')}
              </button>
              <button className="btn btn-secondary" onClick={handleCancelDeletePhoto}>
                {t('dialog.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bestätigungsdialog: Zeile in Vorderseite löschen */}
      {deleteRowConfirm && (
        <div className="edit-overlay" onClick={() => setDeleteRowConfirm(null)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>⚠️ {t('dialog.row_delete.title')}</h3>
              <button className="btn-close" onClick={() => setDeleteRowConfirm(null)}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('vorderseite.row_delete.confirm')}
              </p>
              <div className="edit-info" style={{ marginTop: '16px' }}>
                {deleteRowConfirm.type === 'arbeitsgegenstand' && (
                  <>
                    {deleteRowConfirm.row.bennenung && <div><strong>{t('vorderseite.arbeitsgegenstand.benennung')}:</strong> {deleteRowConfirm.row.bennenung}</div>}
                    {deleteRowConfirm.row.material && <div><strong>{t('vorderseite.arbeitsgegenstand.material')}:</strong> {deleteRowConfirm.row.material}</div>}
                    {deleteRowConfirm.row.zeichn_nr && <div><strong>{t('vorderseite.arbeitsgegenstand.zeichn_nr')}:</strong> {deleteRowConfirm.row.zeichn_nr}</div>}
                  </>
                )}
                {deleteRowConfirm.type === 'mitarbeiter' && (
                  <>
                    {deleteRowConfirm.row.name && <div><strong>{t('vorderseite.mitarbeiter.name')}:</strong> {deleteRowConfirm.row.name}</div>}
                    {deleteRowConfirm.row.pers_nr && <div><strong>{t('vorderseite.mitarbeiter.pers_nr')}:</strong> {deleteRowConfirm.row.pers_nr}</div>}
                  </>
                )}
                {deleteRowConfirm.type === 'betriebsmittel' && (
                  <>
                    {deleteRowConfirm.row.bennenung_type && <div><strong>{t('vorderseite.betriebsmittel.benennung_type')}:</strong> {deleteRowConfirm.row.bennenung_type}</div>}
                    {deleteRowConfirm.row.betrieb_nr && <div><strong>{t('vorderseite.betriebsmittel.betrieb_nr')}:</strong> {deleteRowConfirm.row.betrieb_nr}</div>}
                  </>
                )}
                {deleteRowConfirm.type === 'zusammenstellung' && (
                  <>
                    {deleteRowConfirm.row.art && <div><strong>{t('vorderseite.zusammenstellung.art')}:</strong> {deleteRowConfirm.row.art}</div>}
                    {deleteRowConfirm.row.zeit_min !== undefined && deleteRowConfirm.row.zeit_min !== null && <div><strong>{t('vorderseite.zusammenstellung.zeit_min')}:</strong> {deleteRowConfirm.row.zeit_min}</div>}
                    {deleteRowConfirm.row.herkunft && <div><strong>{t('vorderseite.zusammenstellung.herkunft')}:</strong> {deleteRowConfirm.row.herkunft}</div>}
                  </>
                )}
              </div>
            </div>
            <div className="edit-actions">
              <button 
                className="btn btn-danger" 
                onClick={() => {
                  const { type, index } = deleteRowConfirm;
                  const currentData = vorderseiteData || getDefaultVorderseiteData();
                  const newArray = [...(currentData[type] || [])];
                  newArray.splice(index, 1);
                  handleFieldChange(type, newArray);
                  setDeleteRowConfirm(null);
                }}
              >
                {t('dialog.delete')}
              </button>
              <button className="btn btn-secondary" onClick={() => setDeleteRowConfirm(null)}>
                {t('dialog.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lösch-Dialog */}
      {deletingMeasurement && (
        <div className="edit-overlay" onClick={handleCancelDelete}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>{t('dialog.measurement_delete.title')}</h3>
              <button className="btn-close" onClick={handleCancelDelete}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('dialog.measurement_delete.confirm_empty', { number: deletingMeasurement.number })}
              </p>
              {deletingMeasurement.activity && (
                <div className="edit-info">
                  <div><strong>{t('dialog.measurement_delete.taetigkeit')}</strong> {deletingMeasurement.activity}</div>
                </div>
              )}
              {deletingMeasurement.note && (
                <div className="edit-info">
                  <div><strong>{t('dialog.measurement_delete.notiz')}</strong> {deletingMeasurement.note}</div>
                </div>
              )}
            </div>
            <div className="edit-actions">
              <button className="btn btn-danger" onClick={handleConfirmDelete}>
                {t('dialog.delete')}
              </button>
              <button className="btn btn-secondary" onClick={handleCancelDelete}>
                {t('dialog.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wiederaufnahmen-Dialog */}
      {showWiederaufnahmenDialog && (
        <div className="modal-overlay" onClick={handleCancelWiederaufnahmen}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>{t('dialog.wiederaufnahmen.title')}</h2>
            <p>{t('dialog.wiederaufnahmen.message', { count: pendingWiederaufnahmen.length })}</p>
            <div style={{ maxHeight: '300px', overflowY: 'auto', margin: '20px 0', border: '1px solid #e1e4e8', borderRadius: '4px', padding: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e1e4e8' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>{t('dialog.wiederaufnahmen.wa_nr')}</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>{t('dialog.wiederaufnahmen.datum')}</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>{t('dialog.wiederaufnahmen.beginn')}</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>{t('dialog.wiederaufnahmen.ende')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingWiederaufnahmen.map(w => (
                    <tr key={w.nummer} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px' }}><strong>{w.nummer}</strong></td>
                      <td style={{ padding: '8px' }}>{w.datum}</td>
                      <td style={{ padding: '8px' }}>{w.beginnzeit || '-'}</td>
                      <td style={{ padding: '8px' }}>{w.endzeit || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'pre-line' }}>
              {t('dialog.wiederaufnahmen.hint')}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={handleCancelWiederaufnahmen} style={{ padding: '10px 20px' }}>
                {t('dialog.cancel')}
              </button>
              <button 
                onClick={handleApplyWiederaufnahmen} 
                style={{ 
                  backgroundColor: '#0366d6', 
                  color: 'white', 
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {t('dialog.wiederaufnahmen.apply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop-Dialog */}
      {showStopDialog && (
        <div className="edit-overlay" onClick={handleCancelStop}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>{t('dialog.stop.title')}</h3>
              <button className="btn-close" onClick={handleCancelStop}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('dialog.stop.confirm')}
              </p>
              <div className="edit-info">
                <div><strong>{t('dialog.stop.measurements_label')}</strong> {measurements.length}</div>
                <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#6b7280' }}>
                  {t('dialog.stop.saved_hint')}
                </div>
              </div>
            </div>
            <div className="edit-actions">
              <button className="btn btn-primary" onClick={handleConfirmStop}>
                {t('dialog.stop.button')}
              </button>
              <button className="btn btn-secondary" onClick={handleCancelStop}>
                {t('dialog.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warn-Dialog: Zurück zu Sessions während Uhr läuft oder Daten vorhanden */}
      {showBackToSessionsDialog && (
        <div className="edit-overlay" onClick={handleCancelBackToSessions}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>⚠️ {t('dialog.back_to_sessions.title')}</h3>
              <button className="btn-close" onClick={handleCancelBackToSessions}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('dialog.back_to_sessions.message')}
              </p>
              <div className="edit-info">
                {isRunning && (
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#ef4444' }}>
                    ⚠️ <strong>{t('dialog.back_to_sessions.running')}</strong> {formatTime(currentTime)}
                  </div>
                )}
                {measurements.length > 0 && (
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#ef4444' }}>
                    ⚠️ <strong>{t('dialog.back_to_sessions.measurements_present')}</strong> {t('dialog.back_to_sessions.measurements_count', { count: measurements.length })}
                  </div>
                )}
                {selectedSession && selectedSession.uploaded !== true && (
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#f59e0b' }}>
                    ⚠️ <strong>{t('dialog.back_to_sessions.not_uploaded')}</strong> {t('dialog.back_to_sessions.not_uploaded_info')}
                  </div>
                )}
                <div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#6b7280' }}>
                  {t('dialog.back_to_sessions.session_kept')}
                </div>
              </div>
            </div>
            <div className="edit-actions">
              <button className="btn btn-secondary" onClick={handleCancelBackToSessions}>
                {t('dialog.cancel')}
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmBackToSessions}
              >
                {t('dialog.back_to_sessions.confirm_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warn-Dialog: Zurücksetzen mit laufender Uhr oder vorhandenen Daten */}
      {showResetDialog && (
        <div className="edit-overlay" onClick={handleCancelReset}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>⚠️ {t('dialog.reset_confirm.title')}</h3>
              <button className="btn-close" onClick={handleCancelReset}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('dialog.reset_confirm.message')}
              </p>
              <div className="edit-info">
                {isRunning && (
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#ef4444' }}>
                    ⚠️ <strong>{t('dialog.reset_confirm.running')}</strong> {formatTime(currentTime)}
                  </div>
                )}
                {measurements.length > 0 && (
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#ef4444' }}>
                    ⚠️ <strong>{t('dialog.reset_confirm.measurements_present')}</strong> {t('dialog.reset_confirm.measurements_deleted', { count: measurements.length })}
                  </div>
                )}
                {selectedSession && selectedSession.uploaded !== true && (
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', color: '#f59e0b' }}>
                    ⚠️ <strong>{t('dialog.reset_confirm.not_uploaded')}</strong> {t('dialog.reset_confirm.not_uploaded_info')}
                  </div>
                )}
                <div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#ef4444', fontWeight: 'bold' }}>
                  {t('dialog.reset_confirm.final_warning')}
                </div>
              </div>
            </div>
            <div className="edit-actions">
              <button className="btn btn-secondary" onClick={handleCancelReset}>
                {t('dialog.cancel')}
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleConfirmReset}
              >
                {t('dialog.reset_confirm.confirm_button')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warn-Dialog: Bearbeiten von nicht hochgeladener Tätigkeit */}
      {/* Warn-Dialog: Hinzufügen von Tätigkeit bei nicht hochgeladener Session */}

      {/* Save-Dialog */}
      {showSaveDialog && (
        <div className="edit-overlay" onClick={handleCancelSave}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>{t('dialog.save.title')}</h3>
              <button className="btn-close" onClick={handleCancelSave}>×</button>
            </div>
            <div className="edit-content">
              <div className="edit-field">
                <label>{t('dialog.save.filename')}</label>
                <input
                  type="text"
                  value={saveFileName}
                  onChange={(e) => setSaveFileName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && saveFileName.trim()) {
                      e.preventDefault();
                      handleConfirmSave();
                    }
                  }}
                  className="edit-input"
                  placeholder={t('dialog.save.placeholder')}
                  autoFocus
                />
                <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                  {t('dialog.save.hint')}
                </div>
              </div>
            </div>
            <div className="edit-actions">
              <button 
                className="btn btn-primary" 
                onClick={handleConfirmSave} 
                disabled={!saveFileName.trim()}
                title={t('dialog.save.button_title')}
              >
                {t('dialog.save.button')}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleCancelSave}
                title={t('dialog.save.cancel_title')}
              >
                {t('dialog.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messung umbenennen */}
      <RenameSessionDialog
        open={showRenameSessionDialog}
        sessionName={renameSessionName}
        onSessionNameChange={setRenameSessionName}
        onConfirm={() => handleRenameSession(renameSessionName)}
        onCancel={() => {
          setShowRenameSessionDialog(false);
          setRenameSessionName('');
        }}
      />

      {/* Remove License Dialog */}
      {showRemoveLicenseDialog && (
        <div className="edit-overlay" onClick={handleCancelRemoveLicense}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>{t('dialog.license_remove.title')}</h3>
              <button className="btn-close" onClick={handleCancelRemoveLicense}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6' }}>
                {t('dialog.license_remove.confirm')}
              </p>
              <div className="edit-info">
                <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#6b7280' }}>
                  {t('dialog.license_remove.demo_info')}
                </div>
              </div>
            </div>
            <div className="edit-actions">
              <button className="btn btn-danger" onClick={handleConfirmRemoveLicense}>
                {t('dialog.remove')}
              </button>
              <button className="btn btn-secondary" onClick={handleCancelRemoveLicense}>
                {t('dialog.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AblaufDB Dialog */}
      {showAblaufDbDialog && (
        <div className="edit-overlay" onClick={() => setShowAblaufDbDialog(false)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>🗄️ {t('ablaufdb.title')}</h3>
              <button className="btn-close" onClick={() => setShowAblaufDbDialog(false)}>×</button>
            </div>
            <div className="edit-content">
              {!ablaufDbData || ablaufDbData.length === 0 ? (
                <div>
                  <p style={{ marginBottom: '16px', color: '#6b7280' }}>
                    {t('ablaufdb.not_loaded')}
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => ablaufDbFileInputRef.current?.click()}
                  >
                    {t('ablaufdb.select_xml_button')}
                  </button>
                  <input
                    type="file"
                    ref={ablaufDbFileInputRef}
                    onChange={handleAblaufDbFileSelect}
                    accept=".xml"
                    style={{ display: 'none' }}
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={async () => {
                      const result = await loadAblaufDbXml();
                      if (result.success) {
                        setShowAblaufDbDialog(false);
                      } else {
                        alert(t('alerts.ablaufdb_error', { msg: result.error }));
                      }
                    }}
                    style={{ marginLeft: '8px' }}
                  >
                    {t('ablaufdb.load_from_zaxml_button')}
                  </button>
                </div>
              ) : (
                <div>
                  <div className="ablaufdb-info" style={{ marginBottom: '16px' }}>
                    <strong>{t('ablaufdb.loaded_file')}</strong> {ablaufDbName || t('ablaufdb.unknown')}<br/>
                    <strong>{t('ablaufdb.path')}</strong> {ablaufDbPath || t('ablaufdb.not_saved')}<br/>
                    <strong className="ablaufdb-kuerzel-count">{t('ablaufdb.kuerzel_count')}</strong> {ablaufDbData.length}
                  </div>
                  <div className="form-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setShowAblaufDbEditor(true);
                        setShowAblaufDbDialog(false);
                      }}
                    >
                      {t('ablaufdb.edit_button')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => ablaufDbFileInputRef.current?.click()}
                    >
                      {t('ablaufdb.load_other_button')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        handleAblaufDbUnload();
                        setShowAblaufDbDialog(false);
                      }}
                      title={t('ablaufdb.without_db_title')}
                    >
                      {t('ablaufdb.without_db')}
                    </button>
                    <input
                      type="file"
                      ref={ablaufDbFileInputRef}
                      onChange={handleAblaufDbFileSelect}
                      accept=".xml"
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bestätigungsdialog für ungespeicherte Änderungen */}
      {showConfirmCloseDialog && (
        <div className="dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowConfirmCloseDialog(false);
          }
        }}>
          <div className="dialog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>⚠️ {t('dialog.confirm_close.title')}</h3>
            </div>
            <div className="dialog-content">
              <p>{t('dialog.confirm_close.message')}</p>
            </div>
            <div className="dialog-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirmCloseDialog(false)}
              >
                {t('dialog.cancel')}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  setShowAblaufDbEditor(false);
                  setEditingKuerzel(null);
                  setOriginalKuerzelData(null);
                  setShowConfirmCloseDialog(false);
                }}
              >
                {t('dialog.confirm_close.discard')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Neue Einflussgröße */}
      {showEinflussNameDialog && (
        <div className="dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowEinflussNameDialog(false);
        }}>
          <div className="dialog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>➕ {t('config.einflussdb.editor_add_einfluss')}</h3>
            </div>
            <div className="dialog-content">
              <label style={{ display: 'block', marginBottom: '8px' }}>
                {t('config.einflussdb.prompt_new_name')}
              </label>
              <input
                type="text"
                value={newEinflussName}
                onChange={(e) => {
                  setNewEinflussName(e.target.value);
                  if (einflussNameDialogError) setEinflussNameDialogError('');
                }}
                className="form-input"
                autoFocus
              />
              {einflussNameDialogError && (
                <p style={{ color: '#b91c1c', marginTop: '8px', fontSize: '0.9rem' }}>
                  {einflussNameDialogError}
                </p>
              )}
            </div>
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={() => setShowEinflussNameDialog(false)}>
                {t('dialog.cancel')}
              </button>
              <button className="btn btn-primary" onClick={addEinflussEntry}>
                {t('dialog.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Einflussgröße löschen */}
      {showEinflussDeleteDialog && (
        <div className="dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowEinflussDeleteDialog(false);
        }}>
          <div className="dialog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>⚠️ {t('dialog.row_delete.title')}</h3>
            </div>
            <div className="dialog-content">
              <p>{t('config.einflussdb.confirm_delete', { name: editingEinflussName })}</p>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={() => setShowEinflussDeleteDialog(false)}>
                {t('dialog.cancel')}
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteEinflussEntry}>
                {t('dialog.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: EinflussDB entladen */}
      {showEinflussUnloadDialog && (
        <div className="dialog-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowEinflussUnloadDialog(false);
        }}>
          <div className="dialog-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>⚠️ {t('config.einflussdb.button_without_db')}</h3>
            </div>
            <div className="dialog-content">
              <p>{t('alerts.confirm.einflussdb_unload')}</p>
            </div>
            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={() => setShowEinflussUnloadDialog(false)}>
                {t('dialog.cancel')}
              </button>
              <button className="btn btn-danger" onClick={confirmEinflussDbUnload}>
                {t('dialog.remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EinflussDB Editor */}
      {showEinflussDbEditor && (
        <div className="edit-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowEinflussDbEditor(false);
        }}>
          <div className="edit-modal ablaufdb-editor" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>✏️ {t('config.einflussdb.editor_title')}</h3>
              <button className="btn-close" onClick={() => setShowEinflussDbEditor(false)}>×</button>
            </div>
            <div className="edit-content">
              <div style={{ marginBottom: '16px' }}>
                <strong>{t('ablaufdb.file')}</strong> {einflussDbName || t('ablaufdb.unknown')}<br/>
                <strong>{t('ablaufdb.path')}</strong> {einflussDbPath || t('ablaufdb.not_saved')}<br/>
                <strong>{t('config.einflussdb.status_einfluss_available', { count: einflussDbData?.length || 0 })}</strong>
              </div>

              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label>{t('config.einflussdb.select_einfluss')}</label>
                <select
                  value={editingEinflussName}
                  onChange={(e) => selectEinflussForEdit(e.target.value)}
                  className="form-input"
                >
                  {(einflussDbData || []).map((item, idx) => (
                    <option key={`${item.name}-${idx}`} value={item.name}>{item.name}</option>
                  ))}
                </select>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button className="btn btn-secondary" onClick={openAddEinflussDialog}>
                    {t('config.einflussdb.editor_add_einfluss')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={deleteEinflussEntry}
                    disabled={!editingEinflussName}
                  >
                    {t('config.einflussdb.editor_delete_einfluss')}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>{t('config.einflussdb.editor_name_label')}</label>
                <input
                  type="text"
                  value={editingEinflussName}
                  onChange={(e) => {
                    setEditingEinflussName(e.target.value);
                    if (einflussEditorError) setEinflussEditorError('');
                  }}
                  className="form-input"
                />
                {einflussEditorError && (
                  <p style={{ color: '#b91c1c', marginTop: '6px', fontSize: '0.9rem' }}>
                    {einflussEditorError}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>{t('config.einflussdb.editor_rows_label')}</label>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {(editEinflussMesswerte || []).map((row, rowIdx) => (
                    <div key={rowIdx} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: '8px' }}>
                      <input
                        type="number"
                        value={row?.lfdnr ?? rowIdx + 1}
                        onChange={(e) => {
                          const next = [...editEinflussMesswerte];
                          next[rowIdx] = { ...next[rowIdx], lfdnr: Number.parseInt(e.target.value, 10) || rowIdx + 1 };
                          setEditEinflussMesswerte(next);
                        }}
                        className="form-input"
                        placeholder="LFDNR"
                      />
                      <input
                        type="text"
                        value={row?.messwert || ''}
                        onChange={(e) => {
                          const next = [...editEinflussMesswerte];
                          next[rowIdx] = { ...next[rowIdx], messwert: e.target.value };
                          setEditEinflussMesswerte(next);
                        }}
                        className="form-input"
                        placeholder="MESSWERT"
                      />
                      <input
                        type="text"
                        value={row?.beschreibung || ''}
                        onChange={(e) => {
                          const next = [...editEinflussMesswerte];
                          next[rowIdx] = { ...next[rowIdx], beschreibung: e.target.value };
                          setEditEinflussMesswerte(next);
                        }}
                        className="form-input"
                        placeholder="BESCHREIBUNG (optional)"
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditEinflussMesswerte([...(editEinflussMesswerte || []), { lfdnr: (editEinflussMesswerte?.length || 0) + 1, messwert: '', beschreibung: '' }])}
                  >
                    {t('config.einflussdb.editor_add_row')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditEinflussMesswerte((editEinflussMesswerte || []).slice(0, -1))}
                    disabled={!editEinflussMesswerte || editEinflussMesswerte.length === 0}
                  >
                    {t('config.einflussdb.editor_remove_row')}
                  </button>
                </div>
              </div>

              <div className="form-actions" style={{ marginTop: '16px' }}>
                <button className="btn btn-primary" onClick={saveEinflussDbEditor}>
                  {t('config.einflussdb.editor_save')}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowEinflussDbEditor(false)}>
                  {t('dialog.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AblaufDB Editor */}
      {showAblaufDbEditor && (
        <div className="edit-overlay" onClick={(e) => {
          // Verhindere Schließen beim Klicken außerhalb - nur beim Klicken auf Overlay selbst
          if (e.target === e.currentTarget) {
            // Prüfe ob Änderungen vorhanden sind
            const hasChanges = originalKuerzelData && (
              JSON.stringify(editKuerzelForm) !== JSON.stringify(originalKuerzelData.form) ||
              JSON.stringify(editEinflussgroessen) !== JSON.stringify(originalKuerzelData.einflussgroessen)
            );
            if (hasChanges) {
              setShowConfirmCloseDialog(true);
            } else {
              setShowAblaufDbEditor(false);
              setEditingKuerzel(null);
            }
          }
        }}>
          <div className="edit-modal ablaufdb-editor" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>✏️ {t('ablaufdb.editor_title')}</h3>
              <button className="btn-close" onClick={() => {
                // Prüfe ob Änderungen vorhanden sind
                const hasChanges = originalKuerzelData && (
                  JSON.stringify(editKuerzelForm) !== JSON.stringify(originalKuerzelData.form) ||
                  JSON.stringify(editEinflussgroessen) !== JSON.stringify(originalKuerzelData.einflussgroessen)
                );
                if (hasChanges) {
                  setShowConfirmCloseDialog(true);
                } else {
                  setShowAblaufDbEditor(false);
                  setEditingKuerzel(null);
                }
              }}>×</button>
            </div>
            <div className="edit-content">
              <div style={{ marginBottom: '16px' }}>
                <strong>{t('ablaufdb.file')}</strong> {ablaufDbName || t('ablaufdb.unknown')}<br/>
                <strong>{t('ablaufdb.path')}</strong> {ablaufDbPath || t('ablaufdb.not_saved')}<br/>
                <strong className="ablaufdb-kuerzel-count">{t('ablaufdb.kuerzel_count')}</strong> {ablaufDbData?.length || 0}
              </div>
              
              {!editingKuerzel ? (
                <div>
                  <div className="form-group">
                    <label>{t('ablaufdb.select_kuerzel')}</label>
                    <div className="kuerzel-select-list">
                      {ablaufDbData && ablaufDbData.length > 0 ? (
                        ablaufDbData.map((item, idx) => (
                          <button
                            key={idx}
                            className="kuerzel-select-item"
                            onClick={() => {
                              // Parse ABLAUFTEXT1-4 aus dem XML
                              const parser = new DOMParser();
                              const xmlDoc = parser.parseFromString(ablaufDbXmlContent, 'text/xml');
                              const zfnet1 = xmlDoc.querySelector('ZFNET1');
                              const kuerzelElements = zfnet1?.querySelectorAll('ZFKUERZEL') || [];
                              const kuerzelElement = Array.from(kuerzelElements).find(k => 
                                k.querySelector('ABLAUFKUERZEL')?.textContent?.trim() === item.kuerzel
                              );
                              
                              if (kuerzelElement) {
                                const kostenstelle = kuerzelElement.querySelector('KOSTENSTELLE')?.textContent?.trim() || '';
                                const zeitart = kuerzelElement.querySelector('ZEITART')?.textContent?.trim() || '';
                                const ablauftext1 = kuerzelElement.querySelector('ABLAUFTEXT1')?.textContent?.trim() || '';
                                const ablauftext2 = kuerzelElement.querySelector('ABLAUFTEXT2')?.textContent?.trim() || '';
                                const ablauftext3 = kuerzelElement.querySelector('ABLAUFTEXT3')?.textContent?.trim() || '';
                                const ablauftext4 = kuerzelElement.querySelector('ABLAUFTEXT4')?.textContent?.trim() || '';
                                
                                // Lade Beschreibung aus ZFBESCHREIBUNGEN
                                const beschreibungen = kuerzelElement.querySelector('ZFBESCHREIBUNGEN');
                                const beschreibung1 = beschreibungen?.querySelector('BESCHREIBUNG1')?.textContent?.trim() || '';
                                const beschreibung2 = beschreibungen?.querySelector('BESCHREIBUNG2')?.textContent?.trim() || '';
                                const beschreibung3 = beschreibungen?.querySelector('BESCHREIBUNG3')?.textContent?.trim() || '';
                                const beschreibung4 = beschreibungen?.querySelector('BESCHREIBUNG4')?.textContent?.trim() || '';
                                const beschreibung = [beschreibung1, beschreibung2, beschreibung3, beschreibung4]
                                  .filter(b => b)
                                  .join('\n');
                                
                                // Lade Einflussgrößen
                                const einflussgroessen = kuerzelElement.querySelector('ZFEINFLUSSGROESSEN');
                                const einflussArray = einflussgroessen ? Array.from(einflussgroessen.querySelectorAll('ZFEINFLUSSGROESSE')).map(eg => {
                                  const einflussname = eg.querySelector('EINFLUSSNAME')?.textContent?.trim() || '';
                                  const klassewert = eg.querySelector('KLASSEWERT')?.textContent?.trim() || '';
                                  const einheit = eg.querySelector('EINHEIT')?.textContent?.trim() || '';
                                  const messwerte = Array.from(eg.querySelectorAll('MESSWERT')).map(mw => mw.textContent?.trim() || '').filter(mw => mw);
                                  return { einflussname, klassewert, einheit, messwerte };
                                }) : [];
                                
                                // Stelle sicher, dass wir mindestens 10 Zeilen haben
                                while (einflussArray.length < 10) {
                                  einflussArray.push({ einflussname: '', klassewert: '', einheit: '', messwerte: [] });
                                }
                                
                                const formData = {
                                  kuerzel: item.kuerzel,
                                  kostenstelle: kostenstelle,
                                  zeitart: zeitart,
                                  ablauftext1,
                                  ablauftext2,
                                  ablauftext3,
                                  ablauftext4,
                                  beschreibung: beschreibung || item.beschreibung || ''
                                };
                                setEditKuerzelForm(formData);
                                setEditEinflussgroessen(einflussArray);
                                setEditingKuerzel({ ...item, index: idx });
                                // Speichere Original-Daten für Vergleich
                                setOriginalKuerzelData({
                                  form: { ...formData },
                                  einflussgroessen: JSON.parse(JSON.stringify(einflussArray))
                                });
                              }
                            }}
                          >
                            <div className="kuerzel-select-kuerzel">{item.kuerzel}</div>
                            <div className="kuerzel-select-beschreibung">{item.beschreibung}</div>
                          </button>
                        ))
                      ) : (
                        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>{t('ablaufdb.no_kuerzel')}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAblaufDbEditor(false);
                        setEditingKuerzel(null);
                      }}
                    >
                      {t('ablaufdb.close')}
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="form-group">
                    <label>{t('ablaufdb.edit_kuerzel')}</label>
                    <div className="kuerzel-edit-form">
                      <div className="form-field">
                        <label>
                          {t('ablaufdb.kuerzel')}
                          <span className="help-text" title={t('ablaufdb.kuerzel_help_title')}> ℹ️</span>
                        </label>
                        <input
                          type="text"
                          value={editKuerzelForm.kuerzel}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, kuerzel: e.target.value })}
                          className="form-input"
                          maxLength={10}
                          placeholder="z.B. mess"
                          autoCapitalize="none"
                          autoCorrect="off"
                        />
                        <div className="field-help-text">{t('ablaufdb.kuerzel_max_hint')}</div>
                      </div>
                      <div className="form-field">
                        <label>
                          {t('ablaufdb.kostenstelle')}
                          <span className="help-text" title={t('ablaufdb.kostenstelle_help_title')}> ℹ️</span>
                        </label>
                        <input
                          type="text"
                          value={editKuerzelForm.kostenstelle}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, kostenstelle: e.target.value })}
                          className="form-input"
                          placeholder="z.B. 100"
                        />
                        <div className="field-help-text">{t('ablaufdb.kostenstelle_hint')}</div>
                      </div>
                      <div className="form-field">
                        <label>
                          {t('ablaufdb.zeitart')}
                          <span className="help-text" title={t('ablaufdb.zeitart_help_title')}> ℹ️</span>
                        </label>
                        <select
                          value={editKuerzelForm.zeitart}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, zeitart: e.target.value })}
                          className="form-input"
                        >
                          <option value="">{t('ablaufdb.no_zeitart')}</option>
                          {timeTypes.map(type => (
                            <option key={type.name} value={type.name}>
                              {type.name} - {type.description}
                            </option>
                          ))}
                        </select>
                        <div className="field-help-text">{t('ablaufdb.zeitart_hint')}</div>
                      </div>
                      <div className="form-field">
                        <label>
                          {t('ablaufdb.beschreibung')}
                          <span className="help-text" title={t('ablaufdb.beschreibung_help_title')}> ℹ️</span>
                        </label>
                        <textarea
                          value={editKuerzelForm.beschreibung}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, beschreibung: e.target.value })}
                          className="form-input"
                          rows={4}
                          placeholder={t('ablaufdb.beschreibung_placeholder')}
                          style={{ resize: 'vertical', minHeight: '80px' }}
                        />
                        <div className="field-help-text">{t('ablaufdb.beschreibung_hint')}</div>
                      </div>
                      <div className="form-field">
                        <label>
                          {t('ablaufdb.ablaufabschnittstext')}
                          <span className="help-text" title={t('ablaufdb.ablaufabschnittstext_help_title')}> ℹ️</span>
                        </label>
                        <input
                          type="text"
                          value={editKuerzelForm.ablauftext1}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, ablauftext1: e.target.value })}
                          className="form-input"
                          placeholder="Erste Zeile"
                        />
                        <input
                          type="text"
                          value={editKuerzelForm.ablauftext2}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, ablauftext2: e.target.value })}
                          className="form-input"
                          placeholder="Zweite Zeile"
                          style={{ marginTop: '8px' }}
                        />
                        <input
                          type="text"
                          value={editKuerzelForm.ablauftext3}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, ablauftext3: e.target.value })}
                          className="form-input"
                          placeholder="Dritte Zeile"
                          style={{ marginTop: '8px' }}
                        />
                        <input
                          type="text"
                          value={editKuerzelForm.ablauftext4}
                          onChange={(e) => setEditKuerzelForm({ ...editKuerzelForm, ablauftext4: e.target.value })}
                          className="form-input"
                          placeholder="Vierte Zeile"
                          style={{ marginTop: '8px' }}
                        />
                      </div>
                    </div>
                    
                    {/* Einflussgrößen-Matrix */}
                    <div className="form-group" style={{ marginTop: '24px' }}>
                      <label style={{ marginBottom: '12px', display: 'block', fontWeight: '600' }}>
                        {t('ablaufdb.einflussgroessen_matrix')}
                        <span className="help-text" title={t('ablaufdb.einflussgroessen_matrix_help_title')}> ℹ️</span>
                      </label>
                      <div className="field-help-text" style={{ marginBottom: '12px' }}>
                        {t('ablaufdb.ea_edit_hint')}
                        <br />
                        <span style={{ fontSize: '0.85rem' }}>
                          Externe Einflussgrößen (Einfluss-DB): Typ x/X verwendet Werte aus der Einfluss-DB.
                        </span>
                        <br />
                        <span style={{ fontSize: '0.85rem' }}>
                          {t('config.dependentlists.section_title')}: Bei Typ l/L zuerst Blatt in „Einheit“ wählen, dann Einflussname als Spalte aus dem Blatt auswählen.
                        </span>
                      </div>
                      <div className="einflussgroessen-matrix">
                        <div className="matrix-header">
                          <div className="matrix-header-cell matrix-row-header">{t('ablaufdb.einfluss')}</div>
                          <div className="matrix-header-cell matrix-row-header">K/M</div>
                          <div className="matrix-header-cell matrix-row-header">Einheit</div>
                          {Array.from({ length: 10 }, (_, i) => (
                            <div key={i} className="matrix-header-cell">{i + 1}</div>
                          ))}
                        </div>
                        {editEinflussgroessen.map((einfluss, rowIdx) => (
                          <div key={rowIdx} className="matrix-row">
                            {(() => {
                              const typePrefix = ((einfluss.klassewert || '').toString().trim().charAt(0) || '');
                              const isDependentType = typePrefix === 'l' || typePrefix === 'L';
                              const sheetName = (einfluss.einheit || '').toString().trim();
                              const sheetNames = (dependentListsData || []).map((sheet) => (sheet?.name || '').toString().trim()).filter((name) => name);
                              const selectedSheet = (dependentListsData || []).find((sheet) => (sheet?.name || '').toString().trim() === sheetName);
                              const sheetColumns = Array.isArray(selectedSheet?.columns)
                                ? selectedSheet.columns.map((c) => (c || '').toString().trim()).filter((c) => c)
                                : [];

                              if (isDependentType && sheetColumns.length > 0) {
                                return (
                                  <select
                                    className="matrix-cell matrix-cell-einfluss"
                                    value={einfluss.einflussname || ''}
                                    onChange={(e) => {
                                      const newEinfluss = [...editEinflussgroessen];
                                      newEinfluss[rowIdx] = { ...newEinfluss[rowIdx], einflussname: e.target.value };
                                      setEditEinflussgroessen(newEinfluss);
                                    }}
                                  >
                                    <option value="">{t('ablaufdb.einflussname_placeholder')}</option>
                                    {sheetColumns.map((columnName) => (
                                      <option key={`${rowIdx}-col-${columnName}`} value={columnName}>
                                        {columnName}
                                      </option>
                                    ))}
                                  </select>
                                );
                              }

                              if (isDependentType && !sheetColumns.length) {
                                return (
                                  <input
                                    type="text"
                                    className="matrix-cell matrix-cell-einfluss"
                                    value={einfluss.einflussname}
                                    onChange={(e) => {
                                      const newEinfluss = [...editEinflussgroessen];
                                      newEinfluss[rowIdx] = { ...newEinfluss[rowIdx], einflussname: e.target.value };
                                      setEditEinflussgroessen(newEinfluss);
                                    }}
                                    placeholder={sheetName ? t('config.dependentlists.column_placeholder_short') : t('ablaufdb.einflussname_placeholder')}
                                  />
                                );
                              }

                              return (
                                <input
                                  type="text"
                                  className="matrix-cell matrix-cell-einfluss"
                                  value={einfluss.einflussname}
                                  onChange={(e) => {
                                    const newEinfluss = [...editEinflussgroessen];
                                    newEinfluss[rowIdx] = { ...newEinfluss[rowIdx], einflussname: e.target.value };
                                    setEditEinflussgroessen(newEinfluss);
                                  }}
                                  placeholder={t('ablaufdb.einflussname_placeholder')}
                                />
                              );
                            })()}
                            <select
                              className="matrix-cell matrix-cell-klassewert"
                              value={einfluss.klassewert}
                              onChange={(e) => {
                                const newEinfluss = [...editEinflussgroessen];
                                newEinfluss[rowIdx] = { ...newEinfluss[rowIdx], klassewert: e.target.value };
                                setEditEinflussgroessen(newEinfluss);
                              }}
                            >
                              <option value="">-</option>
                              <option value="k">k</option>
                              <option value="z">z</option>
                              <option value="t">t</option>
                              <option value="x">x</option>
                              <option value="X">X</option>
                              <option value="l">l</option>
                              <option value="L">L</option>
                              <option value="K">K</option>
                            </select>
                            {(() => {
                              const typePrefix = ((einfluss.klassewert || '').toString().trim().charAt(0) || '');
                              const isDependentType = typePrefix === 'l' || typePrefix === 'L';
                              const sheetNames = (dependentListsData || []).map((sheet) => (sheet?.name || '').toString().trim()).filter((name) => name);
                              if (!isDependentType) {
                                return (
                                  <input
                                    type="text"
                                    className="matrix-cell matrix-cell-einheit"
                                    value={einfluss.einheit}
                                    onChange={(e) => {
                                      const newEinfluss = [...editEinflussgroessen];
                                      newEinfluss[rowIdx] = { ...newEinfluss[rowIdx], einheit: e.target.value };
                                      setEditEinflussgroessen(newEinfluss);
                                    }}
                                    placeholder={t('ablaufdb.einheit_placeholder')}
                                  />
                                );
                              }
                              return (
                                <select
                                  className="matrix-cell matrix-cell-einheit"
                                  value={einfluss.einheit || ''}
                                  onChange={(e) => {
                                    const newEinfluss = [...editEinflussgroessen];
                                    const nextEinheit = e.target.value;
                                    const currentEinflussName = (newEinfluss[rowIdx]?.einflussname || '').toString().trim();
                                    const matchingSheet = (dependentListsData || []).find((sheet) => (sheet?.name || '').toString().trim() === nextEinheit);
                                    const columns = Array.isArray(matchingSheet?.columns)
                                      ? matchingSheet.columns.map((c) => (c || '').toString().trim()).filter((c) => c)
                                      : [];
                                    const alignedEinflussName = columns.includes(currentEinflussName) ? currentEinflussName : (columns[0] || currentEinflussName || '');
                                    newEinfluss[rowIdx] = {
                                      ...newEinfluss[rowIdx],
                                      einheit: nextEinheit,
                                      einflussname: alignedEinflussName
                                    };
                                    setEditEinflussgroessen(newEinfluss);
                                  }}
                                >
                                  <option value="">{t('ablaufdb.einheit_placeholder')}</option>
                                  {sheetNames.map((name) => (
                                    <option key={`${rowIdx}-sheet-${name}`} value={name}>{name}</option>
                                  ))}
                                </select>
                              );
                            })()}
                            {Array.from({ length: 10 }, (_, colIdx) => (
                              <input
                                key={colIdx}
                                type="text"
                                className="matrix-cell matrix-cell-messwert"
                                value={einfluss.messwerte[colIdx] || ''}
                                onChange={(e) => {
                                  const newEinfluss = [...editEinflussgroessen];
                                  const newMesswerte = [...(newEinfluss[rowIdx].messwerte || [])];
                                  newMesswerte[colIdx] = e.target.value;
                                  // Entferne leere Werte am Ende
                                  while (newMesswerte.length > 0 && !newMesswerte[newMesswerte.length - 1]) {
                                    newMesswerte.pop();
                                  }
                                  newEinfluss[rowIdx] = { ...newEinfluss[rowIdx], messwerte: newMesswerte };
                                  setEditEinflussgroessen(newEinfluss);
                                }}
                                placeholder={`Wert ${colIdx + 1}`}
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: '12px' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setEditEinflussgroessen([...editEinflussgroessen, { einflussname: '', klassewert: '', einheit: '', messwerte: [] }]);
                          }}
                          style={{ fontSize: '0.85rem', padding: '6px 12px' }}
                        >
                          {t('ablaufdb.add_row')}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            if (editEinflussgroessen.length > 0) {
                              setEditEinflussgroessen(editEinflussgroessen.slice(0, -1));
                            }
                          }}
                          style={{ fontSize: '0.85rem', padding: '6px 12px', marginLeft: '8px' }}
                          disabled={editEinflussgroessen.length === 0}
                        >
                          {t('ablaufdb.remove_row')}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        try {
                          // Update XML
                          const parser = new DOMParser();
                          const xmlDoc = parser.parseFromString(ablaufDbXmlContent, 'text/xml');
                          const zfnet1 = xmlDoc.querySelector('ZFNET1');
                          const kuerzelElements = zfnet1?.querySelectorAll('ZFKUERZEL') || [];
                          const kuerzelElement = Array.from(kuerzelElements).find(k => 
                            k.querySelector('ABLAUFKUERZEL')?.textContent?.trim() === editingKuerzel.kuerzel
                          );
                          
                          if (kuerzelElement) {
                            const kuerzelNode = kuerzelElement.querySelector('ABLAUFKUERZEL');
                            if (kuerzelNode) kuerzelNode.textContent = editKuerzelForm.kuerzel.padEnd(10, ' ');
                            
                            const kostenstelleNode = kuerzelElement.querySelector('KOSTENSTELLE');
                            if (kostenstelleNode) {
                              kostenstelleNode.textContent = editKuerzelForm.kostenstelle.padEnd(20, ' ');
                            } else if (editKuerzelForm.kostenstelle) {
                              const newKostenstelle = xmlDoc.createElement('KOSTENSTELLE');
                              newKostenstelle.textContent = editKuerzelForm.kostenstelle.padEnd(20, ' ');
                              // Füge nach ABLAUFKUERZEL ein
                              const ablaufkuerzel = kuerzelElement.querySelector('ABLAUFKUERZEL');
                              if (ablaufkuerzel && ablaufkuerzel.nextSibling) {
                                kuerzelElement.insertBefore(newKostenstelle, ablaufkuerzel.nextSibling);
                              } else {
                                kuerzelElement.appendChild(newKostenstelle);
                              }
                            }
                            
                            const zeitartNode = kuerzelElement.querySelector('ZEITART');
                            if (zeitartNode) {
                              zeitartNode.textContent = editKuerzelForm.zeitart.padEnd(10, ' ');
                            } else if (editKuerzelForm.zeitart) {
                              const newZeitart = xmlDoc.createElement('ZEITART');
                              newZeitart.textContent = editKuerzelForm.zeitart.padEnd(10, ' ');
                              // Füge nach KOSTENSTELLE ein
                              const kostenstelle = kuerzelElement.querySelector('KOSTENSTELLE');
                              if (kostenstelle && kostenstelle.nextSibling) {
                                kuerzelElement.insertBefore(newZeitart, kostenstelle.nextSibling);
                              } else {
                                kuerzelElement.appendChild(newZeitart);
                              }
                            }
                            
                            const text1Node = kuerzelElement.querySelector('ABLAUFTEXT1');
                            if (text1Node) text1Node.textContent = editKuerzelForm.ablauftext1;
                            else {
                              const newText1 = xmlDoc.createElement('ABLAUFTEXT1');
                              newText1.textContent = editKuerzelForm.ablauftext1;
                              kuerzelElement.appendChild(newText1);
                            }
                            
                            const text2Node = kuerzelElement.querySelector('ABLAUFTEXT2');
                            if (text2Node) text2Node.textContent = editKuerzelForm.ablauftext2;
                            else if (editKuerzelForm.ablauftext2) {
                              const newText2 = xmlDoc.createElement('ABLAUFTEXT2');
                              newText2.textContent = editKuerzelForm.ablauftext2;
                              kuerzelElement.appendChild(newText2);
                            }
                            
                            const text3Node = kuerzelElement.querySelector('ABLAUFTEXT3');
                            if (text3Node) text3Node.textContent = editKuerzelForm.ablauftext3;
                            else if (editKuerzelForm.ablauftext3) {
                              const newText3 = xmlDoc.createElement('ABLAUFTEXT3');
                              newText3.textContent = editKuerzelForm.ablauftext3;
                              kuerzelElement.appendChild(newText3);
                            }
                            
                            const text4Node = kuerzelElement.querySelector('ABLAUFTEXT4');
                            if (text4Node) text4Node.textContent = editKuerzelForm.ablauftext4;
                            else if (editKuerzelForm.ablauftext4) {
                              const newText4 = xmlDoc.createElement('ABLAUFTEXT4');
                              newText4.textContent = editKuerzelForm.ablauftext4;
                              kuerzelElement.appendChild(newText4);
                            }
                            
                            // Update Beschreibung (ZFBESCHREIBUNGEN)
                            let beschreibungenNode = kuerzelElement.querySelector('ZFBESCHREIBUNGEN');
                            if (!beschreibungenNode && editKuerzelForm.beschreibung) {
                              beschreibungenNode = xmlDoc.createElement('ZFBESCHREIBUNGEN');
                              kuerzelElement.appendChild(beschreibungenNode);
                            }
                            if (beschreibungenNode) {
                              const beschreibungLines = editKuerzelForm.beschreibung.split('\n');
                              for (let i = 1; i <= 4; i++) {
                                const beschNode = beschreibungenNode.querySelector(`BESCHREIBUNG${i}`);
                                const beschText = beschreibungLines[i - 1] || '';
                                if (beschNode) {
                                  beschNode.textContent = beschText.padEnd(80, ' ');
                                } else if (beschText) {
                                  const newBesch = xmlDoc.createElement(`BESCHREIBUNG${i}`);
                                  newBesch.textContent = beschText.padEnd(80, ' ');
                                  beschreibungenNode.appendChild(newBesch);
                                }
                              }
                            }
                            
                            // Update Einflussgrößen (ZFEINFLUSSGROESSEN)
                            let einflussgroessenNode = kuerzelElement.querySelector('ZFEINFLUSSGROESSEN');
                            if (!einflussgroessenNode) {
                              einflussgroessenNode = xmlDoc.createElement('ZFEINFLUSSGROESSEN');
                              // Füge nach ABLAUFTEXT4 ein
                              const ablauftext4 = kuerzelElement.querySelector('ABLAUFTEXT4');
                              if (ablauftext4 && ablauftext4.nextSibling) {
                                kuerzelElement.insertBefore(einflussgroessenNode, ablauftext4.nextSibling);
                              } else {
                                kuerzelElement.appendChild(einflussgroessenNode);
                              }
                            } else {
                              // Entferne alle bestehenden Einflussgrößen
                              while (einflussgroessenNode.firstChild) {
                                einflussgroessenNode.removeChild(einflussgroessenNode.firstChild);
                              }
                            }
                            
                            // Füge alle Einflussgrößen hinzu (auch leere, um 10 zu haben)
                            for (let i = 0; i < 10; i++) {
                              const einfluss = editEinflussgroessen[i] || { einflussname: '', klassewert: '', einheit: '', messwerte: [] };
                              const einflussNode = xmlDoc.createElement('ZFEINFLUSSGROESSE');
                              
                              if (einfluss.einflussname || einfluss.messwerte.length > 0) {
                                const einflussnameNode = xmlDoc.createElement('EINFLUSSNAME');
                                einflussnameNode.textContent = einfluss.einflussname;
                                einflussNode.appendChild(einflussnameNode);
                                
                                if (einfluss.klassewert) {
                                  const klassewertNode = xmlDoc.createElement('KLASSEWERT');
                                  klassewertNode.textContent = einfluss.klassewert;
                                  einflussNode.appendChild(klassewertNode);
                                }

                                if (einfluss.einheit) {
                                  const einheitNode = xmlDoc.createElement('EINHEIT');
                                  einheitNode.textContent = einfluss.einheit;
                                  einflussNode.appendChild(einheitNode);
                                }
                                
                                if (einfluss.messwerte.length > 0) {
                                  const messwerteNode = xmlDoc.createElement('ZFMESSWERTE');
                                  einfluss.messwerte.forEach(mw => {
                                    if (mw) {
                                      const messwertNode = xmlDoc.createElement('MESSWERT');
                                      messwertNode.textContent = mw;
                                      messwerteNode.appendChild(messwertNode);
                                    }
                                  });
                                  einflussNode.appendChild(messwerteNode);
                                }
                              } else {
                                // Leere Einflussgröße
                                const einflussnameNode = xmlDoc.createElement('EINFLUSSNAME');
                                einflussnameNode.textContent = '';
                                einflussNode.appendChild(einflussnameNode);
                                const messwerteNode = xmlDoc.createElement('ZFMESSWERTE');
                                einflussNode.appendChild(messwerteNode);
                              }
                              
                              einflussgroessenNode.appendChild(einflussNode);
                            }
                            
                            // Serialize back to XML
                            const serializer = new XMLSerializer();
                            const updatedXml = serializer.serializeToString(xmlDoc);
                            setAblaufDbXmlContent(updatedXml);
                            
                            // Re-parse to update data
                            const result = parseAblaufDbXml(updatedXml);
                            setAblaufDbData(result);
                            setEditingKuerzel(null);
                            setOriginalKuerzelData(null); // Zurücksetzen nach erfolgreichem Speichern
                            
                            // Speichere aktualisierten XML-Inhalt in localStorage
                            try {
                              localStorage.setItem('stoppuhr_ablaufdb_xml', updatedXml);
                            } catch (e) {
                              console.warn('XML-Inhalt zu groß für localStorage');
                            }
                            
                            alert(t('alerts.kuerzel_updated', { kuerzel: editKuerzelForm.kuerzel }));
                          } else {
                            alert('❌ ' + t('ablaufdb.kuerzel_not_found'));
                          }
                        } catch (error) {
                          alert(t('alerts.kuerzel_update_error', { msg: error.message }));
                        }
                      }}
                    >
                      {t('ablaufdb.save')}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        // Prüfe ob Änderungen vorhanden sind
                        const hasChanges = originalKuerzelData && (
                          JSON.stringify(editKuerzelForm) !== JSON.stringify(originalKuerzelData.form) ||
                          JSON.stringify(editEinflussgroessen) !== JSON.stringify(originalKuerzelData.einflussgroessen)
                        );
                        if (hasChanges) {
                          setShowConfirmCloseDialog(true);
                        } else {
                          setShowAblaufDbEditor(false);
                          setEditingKuerzel(null);
                          setOriginalKuerzelData(null);
                        }
                      }}
                    >
                      {t('dialog.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Dialog */}
      {showUploadResultDialog && (
        <div className="edit-overlay" onClick={() => setShowUploadResultDialog(false)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-header">
              <h3>{uploadResult.type === 'success' ? 'Upload erfolgreich' : 'Upload fehlgeschlagen'}</h3>
              <button className="btn-close" onClick={() => setShowUploadResultDialog(false)}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ 
                fontSize: '1rem', 
                color: uploadResult.type === 'success' ? '#10b981' : '#ef4444', 
                lineHeight: '1.6',
                fontWeight: '600'
              }}>
                {uploadResult.type === 'success' ? '✅' : '❌'} {uploadResult.message}
              </p>
            </div>
            <div className="edit-actions">
              <button className="btn btn-primary" onClick={() => setShowUploadResultDialog(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Dialog */}
      {showReportDialog && (
        <div className="edit-overlay" onClick={() => setShowReportDialog(false)}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="edit-header">
              <h3>📄 Report erstellen</h3>
              <button className="btn-close" onClick={() => setShowReportDialog(false)}>×</button>
            </div>
            <div className="edit-content">
              <p style={{ fontSize: '1rem', color: '#374151', lineHeight: '1.6', marginBottom: '20px' }}>
                Wählen Sie die Inhalte aus, die im Report enthalten sein sollen:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={reportOptions.vorderseite}
                    onChange={(e) => setReportOptions({ ...reportOptions, vorderseite: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1rem', fontWeight: '500' }}>Vorderseite</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setReportOptions({ ...reportOptions, messpunkteDetail: false, messpunkteKompakt: false });
                      } else {
                        setReportOptions({ ...reportOptions, messpunkteDetail: true, messpunkteKompakt: false });
                      }
                    }}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1rem', fontWeight: '500' }}>Tätigkeiten</span>
                </label>
                <div style={{ marginLeft: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="taetigkeiten-view"
                      checked={reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt}
                      disabled={!reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setReportOptions({ ...reportOptions, messpunkteDetail: true, messpunkteKompakt: false });
                        }
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.9rem', color: (reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt) ? '#374151' : '#9ca3af' }}>Detail</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="taetigkeiten-view"
                      checked={reportOptions.messpunkteKompakt}
                      disabled={!reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setReportOptions({ ...reportOptions, messpunkteDetail: false, messpunkteKompakt: true });
                        }
                      }}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.9rem', color: (reportOptions.messpunkteDetail || reportOptions.messpunkteKompakt) ? '#374151' : '#9ca3af' }}>Kompakt</span>
                  </label>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={reportOptions.messpunkteBilanzZeitarten}
                    onChange={(e) => setReportOptions({ ...reportOptions, messpunkteBilanzZeitarten: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1rem', fontWeight: '500' }}>Bilanz - Zeitarten</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={reportOptions.bilanzKuerzel}
                    onChange={(e) => setReportOptions({ ...reportOptions, bilanzKuerzel: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '1rem', fontWeight: '500' }}>Bilanz-Kürzel insgesamt</span>
                </label>
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                  <label style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '8px', display: 'block', color: '#374151' }}>Orientierung:</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="report-orientation"
                        checked={reportOptions.orientation === 'portrait'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReportOptions({ ...reportOptions, orientation: 'portrait' });
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.9rem', color: '#374151' }}>Hochformat (Portrait)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="report-orientation"
                        checked={reportOptions.orientation === 'landscape'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setReportOptions({ ...reportOptions, orientation: 'landscape' });
                          }
                        }}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.9rem', color: '#374151' }}>Querformat (Landscape)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="edit-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowReportDialog(false)}>
                Abbrechen
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handlePrintReport}
                disabled={!reportOptions.vorderseite && !reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt && !reportOptions.messpunkteBilanzZeitarten && !reportOptions.bilanzKuerzel}
                title={!reportOptions.vorderseite && !reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt && !reportOptions.messpunkteBilanzZeitarten && !reportOptions.bilanzKuerzel ? 'Bitte wählen Sie mindestens einen Inhalt aus' : ''}
              >
                🖨️ Drucken
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleExportPDF}
                disabled={!reportOptions.vorderseite && !reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt && !reportOptions.messpunkteBilanzZeitarten && !reportOptions.bilanzKuerzel}
                title={!reportOptions.vorderseite && !reportOptions.messpunkteDetail && !reportOptions.messpunkteKompakt && !reportOptions.messpunkteBilanzZeitarten && !reportOptions.bilanzKuerzel ? 'Bitte wählen Sie mindestens einen Inhalt aus' : ''}
              >
                📄 Als PDF exportieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Content (nur im Druck-Modus sichtbar) */}
      {/* Report-Content: Immer gerendert, aber versteckt außer beim Drucken */}
      <div className="report-content" style={{ display: showReportDialog ? 'block' : 'none', visibility: showReportDialog ? 'visible' : 'hidden', position: 'absolute', left: '-9999px', top: 0 }}>
        {renderReportContent(email, deviceName)}
      </div>

      {/* Version Update Badge */}
      {isLoggedIn && <VersionUpdateBadge isRunning={isRunning} dismissedByResumeDialog={versionBadgeDismissedByResume} />}
      
      {/* Debug-Panel (immer sichtbar, wenn aktiviert) */}
      {renderDebugPanel()}

      <LicenseImportResultDialog
        open={showLicenseImportDialog}
        onClose={() => setShowLicenseImportDialog(false)}
        result={licenseImportResult}
      />


      <footer 
        className={`app-footer ${!showFooter ? 'footer-hidden' : ''} ${backendAvailable !== true ? 'footer-clickable' : ''}`}
        onTouchStart={(e) => {
          // Prüfe ob Klick auf interaktives Element (Button, Link)
          // Da footer-content pointer-events: none hat, sollten Klicks durchkommen
          const clickedElement = e.target;
          const isButtonOrLink = clickedElement.tagName === 'BUTTON' || 
                                 clickedElement.tagName === 'A';
          const isInsideButtonOrLink = clickedElement.closest('button, a') !== null;
          
          // Wenn auf interaktives Element geklickt wurde, nicht wegklicken
          if (isButtonOrLink || isInsideButtonOrLink) {
            console.log('[Footer] Klick auf interaktives Element, ignoriere');
            return;
          }
          
          // Ansonsten Footer wegklicken (wenn offline)
          // Klick direkt auf Footer oder auf nicht-interaktives Element im Content
          console.log('[Footer] Klick auf Footer, backendAvailable:', backendAvailable);
          if (backendAvailable !== true) {
            handleFooterTouch(e);
          }
        }}
        onClick={(e) => {
          // Prüfe ob Klick auf interaktives Element (Button, Link)
          // Da footer-content pointer-events: none hat, sollten Klicks durchkommen
          const clickedElement = e.target;
          const isButtonOrLink = clickedElement.tagName === 'BUTTON' || 
                                 clickedElement.tagName === 'A';
          const isInsideButtonOrLink = clickedElement.closest('button, a') !== null;
          
          // Wenn auf interaktives Element geklickt wurde, nicht wegklicken
          if (isButtonOrLink || isInsideButtonOrLink) {
            console.log('[Footer] Klick auf interaktives Element, ignoriere');
            return;
          }
          
          // Ansonsten Footer wegklicken (wenn offline)
          // Klick direkt auf Footer oder auf nicht-interaktives Element im Content
          console.log('[Footer] Klick auf Footer, backendAvailable:', backendAvailable);
          if (backendAvailable !== true) {
            handleFooterTouch(e);
          }
        }}
        style={{
          cursor: (backendAvailable !== true) ? 'pointer' : 'default',
          userSelect: 'none',
          pointerEvents: backendAvailable !== true ? 'auto' : 'none'
        }}
      >
        <div className="footer-content">
          <div className="footer-left">
            <span className="product-name">{getFooterText(t).productName}</span>
            <span className="version">{getFooterText(t).version}</span>
            <span className="footer-separator">|</span>
            <span className="copyright">{getFooterText(t).copyright}</span>
            {backendUrl && (
              <>
                <span className="footer-separator">|</span>
                <span 
                  className={`backend-status ${backendAvailable === true ? 'connected' : backendAvailable === false ? 'disconnected' : 'unknown'}`}
                  title={
                    backendAvailable === true 
                      ? 'Backend verbunden' 
                      : backendAvailable === false 
                      ? `Backend nicht erreichbar${backendOfflineSince ? ` (seit ${formatGermanDateTime(backendOfflineSince)})` : ''}`
                      : 'Backend-Status wird geprüft...'
                  }
                >
                  {backendAvailable === true ? '🟢' : backendAvailable === false ? '🔴' : '🟡'}
                </span>
              </>
            )}
            <span className="footer-separator">|</span>
            <button
              className="footer-feedback-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowFeedbackDialog(true);
              }}
              title="Feedback senden"
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '0.875rem',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              📝 Feedback
            </button>
          </div>
          {!isPortrait && (
          <div className="footer-right">
            <a 
              href="https://www.ie-center.de" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              www.ie-center.de
            </a>
          </div>
          )}
        </div>
      </footer>

      {/* Feedback-Dialog */}
      <FeedbackDialog
        isOpen={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        onSubmit={handleFeedbackSubmit}
        backendUrl={backendUrl}
        userEmail={email}
        frontendVersion={FRONTEND_VERSION}
        backendVersion={backendVersion}
        licenseKey={licenseInfo?.license_key || null}
        deviceId={getDeviceId()}
        deviceName={getDeviceName()}
        isSubmitting={isSubmittingFeedback}
        backendAvailable={backendAvailable}
        darkMode={darkMode}
      />

      {/* Feedback-Status-Notification */}
      {backendUrl && email && showFeedbackStatusNotification && (
        <FeedbackStatusNotification
          backendUrl={backendUrl}
          userEmail={email}
          forceVisible={true}
          pendingFeedbackCount={pendingFeedbackCount}
          onOpenLoadPendingCount={loadPendingFeedbackCount}
          onClose={() => setShowFeedbackStatusNotification(false)}
        />
      )}
      
      {/* Vorderseite-Dialog für Kompakt-Ansicht */}
      {isPortrait && vorderseiteDialogOpen && selectedSession && (
        <CompactVorderseiteDialog
          open={vorderseiteDialogOpen}
          onClose={() => setVorderseiteDialogOpen(false)}
          darkMode={darkMode}
          isLoading={isLoadingVorderseite}
          fontScale={compactVorderseiteFontScale}
          onFontScaleChange={setCompactVorderseiteFontScale}
          onFontScaleOptimal={null}
          onSave={saveVorderseite}
          isSaving={isSavingVorderseite}
          showSavedHint={false}
          getTabIndex={getVorderseiteTabIndex}
        >
          <VorderseiteForm 
            data={vorderseiteData || getDefaultVorderseiteData()}
            onUpdate={updateVorderseiteField}
            onSave={saveVorderseite}
            isSaving={isSavingVorderseite}
            darkMode={darkMode}
            getAnalysis={getAnalysis}
            updateZusammenstellungVorgabezeiten={updateZusammenstellungVorgabezeiten}
            onDataUpdate={setVorderseiteData}
            decimalPlacesVorderseite={decimalPlacesVorderseite}
            formatVorderseiteTime={formatVorderseiteTime}
            setDeleteRowConfirm={setDeleteRowConfirm}
            fontScale={compactVorderseiteFontScale}
            ablaufDbName={ablaufDbName}
            recognitionEdit={recognitionEdit}
            isSpeechRecognitionSupported={isSpeechRecognitionSupported}
            getTabIndex={getVorderseiteTabIndex}
            hideSpeechButton={true}
            isListeningVorderseiteNotizRef={isListeningVorderseiteNotizRef}
          />
        </CompactVorderseiteDialog>
      )}
    </div>
  );
}

// Vorderseite-Komponente
const VorderseiteForm = ({ data, onUpdate, onSave, isSaving, darkMode, getAnalysis, updateZusammenstellungVorgabezeiten, onDataUpdate, decimalPlacesVorderseite, formatVorderseiteTime, fontScale, ablaufDbName, hideButtons = false, recognitionEdit = null, isSpeechRecognitionSupported = false, getTabIndex = null, setDeleteRowConfirm = null, hideSpeechButton = false, isListeningVorderseiteNotizRef: externalListeningRef = null }) => {
  const { t } = useTranslation();
  const [isListeningVorderseiteNotiz, setIsListeningVorderseiteNotiz] = useState(false);
  const localListeningRef = useRef(false);
  const effectiveListeningRef = (hideSpeechButton && externalListeningRef) ? externalListeningRef : localListeningRef;
  const notizTabIndex = getTabIndex ? getTabIndex('notiz', 0) : 1000;
  const speechTabIndex = getTabIndex ? getTabIndex('speech', 0) : 1001;
  const saveTabIndex = getTabIndex ? getTabIndex('save', 0) : 1002;

  const handleFieldChange = (path, value) => {
    onUpdate(path, value);
  };

  // Hilfsfunktion: Prüft, ob eine Arbeitsgegenstand-Zeile leer ist
  const isArbeitsgegenstandRowEmpty = (row) => {
    return !row || (
      (!row.bennenung || row.bennenung.trim() === '') &&
      (!row.material || row.material.trim() === '') &&
      (!row.zustand || row.zustand.trim() === '') &&
      (!row.zeichn_nr || row.zeichn_nr.trim() === '') &&
      (!row.material_nr || row.material_nr.trim() === '') &&
      (!row.masse_gewichte || row.masse_gewichte.trim() === '')
    );
  };

  // Hilfsfunktion: Prüft, ob eine Mitarbeiter-Zeile leer ist
  const isMitarbeiterRowEmpty = (row) => {
    return !row || (
      (!row.name || row.name.trim() === '') &&
      (!row.pers_nr || row.pers_nr.trim() === '') &&
      (!row.geschlecht || row.geschlecht.trim() === '') &&
      (!row.alter || row.alter === '') &&
      (!row.dauer_aehnlicher_aufgaben || row.dauer_aehnlicher_aufgaben === '') &&
      (!row.dauer_untersucht_aufgabe || row.dauer_untersucht_aufgabe === '')
    );
  };

  // Hilfsfunktion: Prüft, ob eine Betriebsmittel-Zeile leer ist
  const isBetriebsmittelRowEmpty = (row) => {
    return !row || (
      (!row.bennenung_type || row.bennenung_type.trim() === '') &&
      (!row.anzahl || row.anzahl === '') &&
      (!row.betrieb_nr || row.betrieb_nr.trim() === '') &&
      (!row.baujahr || row.baujahr === '') &&
      (!row.technische_daten_zustand || row.technische_daten_zustand.trim() === '')
    );
  };

  const isZusammenstellungRowEmpty = (row) => {
    return (
      (!row.art || row.art.trim() === '') &&
      (!row.zeit_min || row.zeit_min === 0) &&
      (!row.herkunft || row.herkunft.trim() === '')
    );
  };

  // Helper-Funktion für Sprach-Button-Aktivierung (wird von onMouseDown und onKeyDown verwendet)
  const handleSpeechButtonToggle = () => {
    if (!recognitionEdit || !isSpeechRecognitionSupported) return;
    if (!effectiveListeningRef.current) {
      try {
        effectiveListeningRef.current = true;
        setIsListeningVorderseiteNotiz(true);
        recognitionEdit.start();
      } catch (error) {
        console.error('[Speech Recognition] Fehler beim Starten:', error);
        effectiveListeningRef.current = false;
        setIsListeningVorderseiteNotiz(false);
      }
    } else {
      if (recognitionEdit) {
        try {
          recognitionEdit.stop();
        } catch (error) {
          // Ignoriere Fehler beim Stoppen
        }
      }
      effectiveListeningRef.current = false;
      setIsListeningVorderseiteNotiz(false);
    }
  };

  // Spracheingabe-Handler für Vorderseite-Notiz
  useEffect(() => {
    if (!recognitionEdit) return;

    const originalOnResult = recognitionEdit.onresult;
    const originalOnError = recognitionEdit.onerror;
    const originalOnEnd = recognitionEdit.onend;

    recognitionEdit.onresult = (event) => {
      // Wenn wir für Vorderseite-Notiz aufnehmen, verarbeite das Ergebnis
      if (effectiveListeningRef.current) {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }
        if (finalTranscript.trim()) {
          const currentNotiz = data?.notiz || '';
          onUpdate('notiz', currentNotiz + (currentNotiz ? ' ' : '') + finalTranscript.trim());
        }
      } else {
        // Sonst rufe den ursprünglichen Handler auf (für Edit-Dialog)
        if (originalOnResult) {
          originalOnResult.call(recognitionEdit, event);
        }
      }
    };

    recognitionEdit.onerror = (event) => {
      if (effectiveListeningRef.current && event.error !== 'no-speech' && event.error !== 'aborted') {
        effectiveListeningRef.current = false;
        setIsListeningVorderseiteNotiz(false);
      }
      if (originalOnError) {
        originalOnError.call(recognitionEdit, event);
      }
    };

    recognitionEdit.onend = () => {
      if (effectiveListeningRef.current) {
        setTimeout(() => {
          if (effectiveListeningRef.current && recognitionEdit) {
            try {
              recognitionEdit.start();
            } catch (error) {
              effectiveListeningRef.current = false;
              setIsListeningVorderseiteNotiz(false);
            }
          }
        }, 100);
      } else if (originalOnEnd) {
        originalOnEnd.call(recognitionEdit);
      }
    };

    return () => {
      // Restore original handlers on unmount
      if (recognitionEdit) {
        recognitionEdit.onresult = originalOnResult;
        recognitionEdit.onerror = originalOnError;
        recognitionEdit.onend = originalOnEnd;
      }
    };
  }, [recognitionEdit, data, onUpdate, hideSpeechButton, externalListeningRef]);

  // Verwendete AblaufDB(s) ohne Dateiendung in Quell-DB eintragen, wenn das Feld leer ist
  useEffect(() => {
    const name = ablaufDbName ? String(ablaufDbName).replace(/\.[^.]*$/, '') : '';
    if (name && data && (!data.bemerkung_quell_db || data.bemerkung_quell_db === '')) {
      onUpdate('bemerkung_quell_db', name);
    }
  }, [ablaufDbName, data, data?.bemerkung_quell_db, onUpdate]);

  if (!data) return null;

  // eslint-disable-next-line no-unused-vars
  const getNestedValue = (obj, path) => {
    const keys = path.split('.');
    let current = obj;
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }
    return current;
  };

  const scale = fontScale != null ? fontScale : 1;
  return (
    <div className="vorderseite-form" style={{ fontFamily: 'Arial, sans-serif', fontSize: `${12 * scale}px`, color: darkMode ? '#f9fafb' : '#111827' }}>
      {/* Header: ein 3x2-Grid für die 6 Kopfzeilen-Felder (Firma, Netz, Pfad / ZA-Name, Teile-Nr, Afo) */}
      <div style={{ marginBottom: '8px', textAlign: 'center', borderBottom: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`, paddingBottom: '4px' }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: `${18 * scale}px`, fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Zeitaufnahmebogen</h2>
        <div className="vorderseite-header-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: 'auto auto', gap: '6px', fontSize: `${11 * scale}px`, minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <strong style={{ color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, textAlign: 'right', minWidth: '60px', width: '60px', flexShrink: 0 }}>Firma:</strong>
            <input
              type="text"
              value={data.firma || ''}
              onChange={(e) => handleFieldChange('firma', e.target.value)}
              style={{
                padding: '4px 8px',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: `${13 * scale}px`,
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <strong style={{ color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, textAlign: 'right', minWidth: '60px', width: '60px', flexShrink: 0 }}>Netz:</strong>
            <input
              type="text"
              value={data.netz || ''}
              onChange={(e) => handleFieldChange('netz', e.target.value)}
              style={{
                padding: '4px 8px',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: `${13 * scale}px`,
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <strong style={{ color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, textAlign: 'right', minWidth: '60px', width: '60px', flexShrink: 0 }}>Pfad:</strong>
            <input
              type="text"
              value={data.pfad || ''}
              onChange={(e) => handleFieldChange('pfad', e.target.value)}
              style={{
                padding: '4px 8px',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: `${13 * scale}px`,
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <strong style={{ color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, textAlign: 'right', minWidth: '80px', width: '80px', flexShrink: 0 }}>ZA-Name:</strong>
            <input
              type="text"
              value={data.za_name || ''}
              onChange={(e) => handleFieldChange('za_name', e.target.value)}
              style={{
                padding: '4px 8px',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: `${13 * scale}px`,
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <strong style={{ color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, textAlign: 'right', minWidth: '80px', width: '80px', flexShrink: 0 }}>Teile-Nr:</strong>
            <input
              type="text"
              value={data.teilennr || ''}
              onChange={(e) => handleFieldChange('teilennr', e.target.value)}
              style={{
                padding: '4px 8px',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: `${13 * scale}px`,
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <strong style={{ color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, textAlign: 'right', minWidth: '60px', width: '60px', flexShrink: 0 }}>Afo:</strong>
            <input
              type="text"
              value={data.afo || ''}
              onChange={(e) => handleFieldChange('afo', e.target.value)}
              style={{
                padding: '4px 8px',
                flex: 1,
                minWidth: 0,
                maxWidth: '100%',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: `${13 * scale}px`,
                background: darkMode ? '#374151' : 'white',
                color: darkMode ? '#f9fafb' : '#111827',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Arbeitsaufgabe und Arbeitsverfahren: Zeile 1 = Titel, Zeile 2 = Inhalt (2x2-Grid) */}
      <div className="vorderseite-arbeitsaufgabe-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto auto', gap: '4px 10px', marginBottom: '8px', alignItems: 'start' }}>
        <div style={{ fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Arbeitsaufgabe:</div>
        <div style={{ fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Arbeitsverfahren und Arbeitsmethode:</div>
        <div>
          <textarea
            value={data.arbeitsaufgabe || ''}
            onChange={(e) => handleFieldChange('arbeitsaufgabe', e.target.value)}
            style={{ width: '100%', padding: '4px', border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, borderRadius: '4px', minHeight: '40px', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <textarea
            value={data.arbeitsverfahren || ''}
            onChange={(e) => handleFieldChange('arbeitsverfahren', e.target.value)}
            style={{ width: '100%', padding: '4px', border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, borderRadius: '4px', minHeight: '40px', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {/* Stück pro Auftrag und Kostenstelle: 2x1-Grid (eine Zeile, zwei Zellen) */}
      <div className="vorderseite-stueck-kostenstelle-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto', gap: '8px', marginBottom: '8px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '2px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Stück pro Auftrag:</label>
          <input
            type="number"
            value={data.stueck_pro_auftrag || ''}
            onChange={(e) => handleFieldChange('stueck_pro_auftrag', e.target.value)}
            style={{ width: '100%', padding: '4px', border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, borderRadius: '4px', boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '2px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Kostenstelle:</label>
          <input
            type="text"
            value={data.kostenstelle || ''}
            onChange={(e) => handleFieldChange('kostenstelle', e.target.value)}
            style={{ width: '100%', padding: '4px', border: `1px solid ${darkMode ? '#4b5563' : '#ccc'}`, borderRadius: '4px', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {/* Wiederaufnahme-Bereich (volle Breite) */}
      <div style={{ marginBottom: '8px' }}>
          {/* Wiederaufnahme-Bereich */}
          <div style={{ border: '1px solid #ccc', padding: '4px', borderRadius: '4px', marginBottom: '6px' }}>
            <h3 className="vorderseite-heading-wiederaufnahmen" style={{ margin: '0 0 4px 0', fontSize: `${14 * scale}px`, fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Wiederaufnahmen</h3>
            <div style={{ overflow: 'visible', width: '100%' }}>
              <table style={{ width: '100%', fontSize: `${11 * scale}px`, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ border: 'none', padding: '4px', width: '8%', textAlign: 'left' }}>Nr</th>
                    <th style={{ border: 'none', padding: '4px', width: '22%', textAlign: 'left' }}>Datum</th>
                    <th style={{ border: 'none', padding: '4px', width: '20%', textAlign: 'left' }}>Beginn</th>
                    <th style={{ border: 'none', padding: '4px', width: '20%', textAlign: 'left' }}>Ende</th>
                    <th style={{ border: 'none', padding: '4px', width: '30%', textAlign: 'left' }}>Dauer</th>
                  </tr>
                </thead>
              <tbody>
                {(data.wiederaufnahmen && Array.isArray(data.wiederaufnahmen) && data.wiederaufnahmen.length > 0) ? (
                  data.wiederaufnahmen.map((w, index) => (
                    <tr key={index}>
                      <td style={{ border: 'none', padding: '4px', textAlign: 'right' }}>
                        <input
                          type="number"
                          value={w.nummer || 0}
                          onChange={(e) => {
                            const oldValue = w.nummer;
                            const inputValue = e.target.value;
                            const parsedValue = parseInt(inputValue) || 0;                            debugLog('App.jsx:14047', 'VorderseiteForm: Editing nummer', {index,oldValue,inputValue,parsedValue,entryBefore:w}, 'E');                            const updated = [...(data.wiederaufnahmen || [])];
                            updated[index] = { ...updated[index], nummer: parsedValue };
                            handleFieldChange('wiederaufnahmen', updated);
                          }}
                          style={{ width: '100%', padding: '2px', border: 'none', fontSize: `${11 * scale}px`, textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ border: 'none', padding: '4px', textAlign: 'right' }}>
                        <input
                          type="text"
                          value={w.datum || ''}
                          onChange={(e) => {
                            const updated = [...(data.wiederaufnahmen || [])];
                            updated[index] = { ...updated[index], datum: e.target.value };
                            handleFieldChange('wiederaufnahmen', updated);
                          }}
                          style={{ width: '100%', padding: '2px', border: 'none', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ border: 'none', padding: '4px', textAlign: 'right' }}>
                        <input
                          type="text"
                          value={w.beginnzeit || ''}
                          onChange={(e) => {
                            const updated = [...(data.wiederaufnahmen || [])];
                            updated[index] = { ...updated[index], beginnzeit: e.target.value };
                            handleFieldChange('wiederaufnahmen', updated);
                          }}
                          style={{ width: '100%', padding: '2px', border: 'none', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ border: 'none', padding: '4px', textAlign: 'right' }}>
                        <input
                          type="text"
                          value={w.endzeit || ''}
                          onChange={(e) => {
                            const updated = [...(data.wiederaufnahmen || [])];
                            updated[index] = { ...updated[index], endzeit: e.target.value };
                            handleFieldChange('wiederaufnahmen', updated);
                          }}
                          style={{ width: '100%', padding: '2px', border: 'none', textAlign: 'right' }}
                        />
                      </td>
                      <td style={{ border: 'none', padding: '4px', textAlign: 'right' }}>
                        <input
                          type="text"
                          value={w.dauer || ''}
                          onChange={(e) => {
                            const updated = [...(data.wiederaufnahmen || [])];
                            updated[index] = { ...updated[index], dauer: e.target.value };
                            handleFieldChange('wiederaufnahmen', updated);
                          }}
                          style={{ width: '100%', padding: '2px', border: 'none', textAlign: 'right' }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ border: 'none', padding: '4px', textAlign: 'center', color: '#999' }}>
                      Keine Wiederaufnahmen vorhanden
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
      </div>

      {/* Zusammenstellung-Tabelle: Zeitartenbeschreibung immer sichtbar, Zeit in min schmaler */}
      <div style={{ marginBottom: '8px' }}>
        <h3 className="vorderseite-heading-zusammenstellung" style={{ margin: '0 0 4px 0', fontSize: `${14 * scale}px`, fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>{t('vorderseite.zusammenstellung.heading_full')}</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: `${11 * scale}px`, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '4px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb', width: '60%' }}>{t('vorderseite.zusammenstellung.heading_full')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '4px', textAlign: 'right', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb', width: '15%' }}>{t('vorderseite.labels.zeit_min')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '4px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb', width: '25%' }}>{t('vorderseite.zusammenstellung.herkunft')}</th>
            </tr>
          </thead>
          <tbody>
            {(data.zusammenstellung || []).map((item, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #ccc', padding: '4px', overflow: 'visible', wordWrap: 'break-word', minWidth: 0 }}>
                  <input
                    type="text"
                    value={item.art || ''}
                    onChange={(e) => {
                      const newArray = [...(data.zusammenstellung || [])];
                      newArray[index] = { ...newArray[index], art: e.target.value };
                      handleFieldChange('zusammenstellung', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none', boxSizing: 'border-box', minWidth: 0 }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'right', width: '15%' }}>
                  <input
                    type="number"
                    step={1 / Math.pow(10, decimalPlacesVorderseite)}
                    value={formatVorderseiteTime(item.zeit_min || 0, decimalPlacesVorderseite)}
                    readOnly={!!(item.art != null && String(item.art).trim() !== '')}
                    onChange={(e) => {
                      const newArray = [...(data.zusammenstellung || [])];
                      newArray[index] = { ...newArray[index], zeit_min: parseFloat(e.target.value) || 0 };
                      handleFieldChange('zusammenstellung', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none', textAlign: 'right', boxSizing: 'border-box', maxWidth: '100%' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px', width: '25%', overflow: 'visible', wordWrap: 'break-word', minWidth: 0 }}>
                  <input
                    type="text"
                    value={item.herkunft || ''}
                    onChange={(e) => {
                      const newArray = [...(data.zusammenstellung || [])];
                      newArray[index] = { ...newArray[index], herkunft: e.target.value };
                      handleFieldChange('zusammenstellung', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none', boxSizing: 'border-box', minWidth: 0 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!hideButtons && (
          <div style={{ marginTop: '4px', marginBottom: '4px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            <button
              onClick={async () => {
                if (!getAnalysis || !updateZusammenstellungVorgabezeiten || !onDataUpdate) return;
                const analysis = getAnalysis();
                if (analysis && data) {
                  const updated = updateZusammenstellungVorgabezeiten(data, analysis);
                  onDataUpdate(updated);
                  // Automatisch speichern nach Aktualisierung
                  if (onSave) {
                    // Warte kurz, damit setVorderseiteData aktualisiert wurde
                    setTimeout(() => {
                      onSave(true); // silent = true, keine Alert
                    }, 100);
                  }
                }
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px'
              }}
              title={t('vorderseite.update_vorgabezeiten_title')}
            >
              {t('vorderseite.update_vorgabezeiten_button')}
            </button>
            <button
              onClick={() => {
                const newArray = [...(data.zusammenstellung || [])];
                newArray.push({ art: '', zeit_min: 0, herkunft: '' });
                handleFieldChange('zusammenstellung', newArray);
              }}
              style={{
                padding: '6px 12px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px'
              }}
              title={t('vorderseite.add_zusammenstellung_row_title')}
            >
          + {t('vorderseite.add_row')}
        </button>
        {(data.zusammenstellung || []).length > 0 && (
              <button
                onClick={() => {
                  const zusammenstellung = data.zusammenstellung || [];
                  const lastIndex = zusammenstellung.length - 1;
                  const lastRow = zusammenstellung[lastIndex];
                  
                  if (isZusammenstellungRowEmpty(lastRow)) {
                    // Leere Zeile direkt löschen
                    const newArray = [...zusammenstellung];
                    newArray.pop();
                    handleFieldChange('zusammenstellung', newArray);
                  } else {
                    // Sicherheitsabfrage für Zeile mit Daten
                    if (setDeleteRowConfirm) {
                      setDeleteRowConfirm({
                        type: 'zusammenstellung',
                        index: lastIndex,
                        row: lastRow
                      });
                    } else {
                      // Fallback: direkt löschen wenn kein Dialog verfügbar
                      const newArray = [...zusammenstellung];
                      newArray.pop();
                      handleFieldChange('zusammenstellung', newArray);
                    }
                  }
                }}
                style={{
                  padding: '6px 12px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  marginLeft: '8px'
                }}
                title={t('vorderseite.remove_zusammenstellung_row_title')}
              >
                − {t('vorderseite.remove_zusammenstellung_row_button')}
              </button>
            )}
          </div>
        )}
        {/* Grundzeit, Verteilzeit %, Zeit je Einheit: gleiches 3-Spalten-Grid wie „Zusammenstellung …“, Zeitwerte in Spalte 2 unter „Zeit in min“ */}
        <div className="vorderseite-grundzeit-grid" style={{ marginTop: '4px', display: 'grid', gridTemplateColumns: '60% 15% 25%', gridTemplateRows: 'auto auto auto', gap: '4px 8px', fontSize: '11px', alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <label style={{ color: darkMode ? '#f9fafb' : '#111827' }}>Grundzeit tg:</label>
          </div>
          <div>
            <input
              type="number"
              step={1 / Math.pow(10, decimalPlacesVorderseite)}
              value={formatVorderseiteTime(data.grundzeit_tg || 0, decimalPlacesVorderseite)}
              readOnly
              title={t('vorderseite.grundzeit_hint_title')}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: darkMode ? '#374151' : '#f9fafb', color: darkMode ? '#9ca3af' : '#6b7280', textAlign: 'right', boxSizing: 'border-box' }}
            />
          </div>
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <label style={{ color: darkMode ? '#f9fafb' : '#111827' }}>Verteilzeit tv (%):</label>
            <input
              className="vorderseite-verteilzeit-input"
              type="number"
              step="0.01"
              value={data.verteilzeit_tv_prozent === '' ? '' : (data.verteilzeit_tv_prozent ?? 0)}
              onChange={(e) => {
                const raw = e.target.value;
                const pct = raw === '' ? 0 : (parseFloat(raw) || 0);
                handleFieldChange('verteilzeit_tv_prozent', raw === '' ? '' : pct);
                if (data.grundzeit_tg && data.grundzeit_tg > 0) {
                  const newZeitJeEinheit = parseFloat((data.grundzeit_tg * (1 + pct / 100)).toFixed(decimalPlacesVorderseite));
                  handleFieldChange('zeit_je_einheit_te1', newZeitJeEinheit);
                } else {
                  handleFieldChange('zeit_je_einheit_te1', 0);
                }
              }}
              style={{ padding: '4px', width: '20%', minWidth: '3em', border: '1px solid #ccc', borderRadius: '4px', textAlign: 'right', boxSizing: 'border-box' }}
            />
          </div>
          <div />
          <div />
          <div style={{ textAlign: 'right' }}>
            <label style={{ color: darkMode ? '#f9fafb' : '#111827' }}>Zeit je Einheit te1:</label>
          </div>
          <div>
            <input
              type="number"
              step={1 / Math.pow(10, decimalPlacesVorderseite)}
              value={formatVorderseiteTime(data.zeit_je_einheit_te1 || 0, decimalPlacesVorderseite)}
              readOnly
              title="Wird automatisch berechnet: Grundzeit × (1 + Verteilzeit / 100)"
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: darkMode ? '#374151' : '#f9fafb', color: darkMode ? '#9ca3af' : '#6b7280', textAlign: 'right', boxSizing: 'border-box' }}
            />
          </div>
          <div />
        </div>
      </div>

      {/* Arbeitsgegenstand-Tabelle */}
      <div className="vorderseite-block-arbeitsgegenstand" style={{ marginBottom: '12px' }}>
        <h3 className="vorderseite-heading-arbeitsgegenstand" style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>{t('vorderseite.section.arbeitsgegenstand')}:</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.arbeitsgegenstand.benennung')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.arbeitsgegenstand.material')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.arbeitsgegenstand.zustand')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.labels.zeichn_nr')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.labels.material_nr')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.arbeitsgegenstand.masse_gewichte')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', width: '40px', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}></th>
            </tr>
          </thead>
          <tbody>
            {(data.arbeitsgegenstand || []).map((item, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.bennenung || ''}
                    onChange={(e) => {
                      const newArray = [...(data.arbeitsgegenstand || [])];
                      newArray[index] = { ...newArray[index], bennenung: e.target.value };
                      handleFieldChange('arbeitsgegenstand', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.material || ''}
                    onChange={(e) => {
                      const newArray = [...(data.arbeitsgegenstand || [])];
                      newArray[index] = { ...newArray[index], material: e.target.value };
                      handleFieldChange('arbeitsgegenstand', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.zustand || ''}
                    onChange={(e) => {
                      const newArray = [...(data.arbeitsgegenstand || [])];
                      newArray[index] = { ...newArray[index], zustand: e.target.value };
                      handleFieldChange('arbeitsgegenstand', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.zeichn_nr || ''}
                    onChange={(e) => {
                      const newArray = [...(data.arbeitsgegenstand || [])];
                      newArray[index] = { ...newArray[index], zeichn_nr: e.target.value };
                      handleFieldChange('arbeitsgegenstand', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.material_nr || ''}
                    onChange={(e) => {
                      const newArray = [...(data.arbeitsgegenstand || [])];
                      newArray[index] = { ...newArray[index], material_nr: e.target.value };
                      handleFieldChange('arbeitsgegenstand', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.masse_gewichte || ''}
                    onChange={(e) => {
                      const newArray = [...(data.arbeitsgegenstand || [])];
                      newArray[index] = { ...newArray[index], masse_gewichte: e.target.value };
                      handleFieldChange('arbeitsgegenstand', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      const row = data.arbeitsgegenstand[index];
                      if (isArbeitsgegenstandRowEmpty(row)) {
                        // Leere Zeile: direkt löschen
                        const newArray = [...(data.arbeitsgegenstand || [])];
                        newArray.splice(index, 1);
                        handleFieldChange('arbeitsgegenstand', newArray);
                      } else {
                        // Zeile mit Daten: Sicherheitsabfrage
                        if (setDeleteRowConfirm) {
                          setDeleteRowConfirm({ type: 'arbeitsgegenstand', index, row });
                        } else {
                          // Fallback: direkt löschen, wenn kein Dialog verfügbar
                          const newArray = [...(data.arbeitsgegenstand || [])];
                          newArray.splice(index, 1);
                          handleFieldChange('arbeitsgegenstand', newArray);
                        }
                      }
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={t('vorderseite.delete_row_title')}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={() => {
            const newArray = [...(data.arbeitsgegenstand || [])];
            newArray.push({ bennenung: '', material: '', zustand: '', zeichn_nr: '', material_nr: '', masse_gewichte: '' });
            handleFieldChange('arbeitsgegenstand', newArray);
          }}
          style={{
            marginTop: '10px',
            padding: '6px 12px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          + {t('vorderseite.add_row')}
        </button>
      </div>

      {/* Mitarbeiter-Tabelle */}
      <div className="vorderseite-block-mitarbeiter" style={{ marginBottom: '12px' }}>
        <h3 className="vorderseite-heading-mitarbeiter" style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>
          {t('vorderseite.section.mitarbeiter')}: <span style={{ fontSize: '11px', fontWeight: 'normal', fontStyle: 'italic', color: darkMode ? '#9ca3af' : '#6b7280' }}>{t('vorderseite.mitarbeiter.dsgvo_hint')}</span>
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.mitarbeiter.name')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.labels.pers_nr')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.labels.g')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.mitarbeiter.alter')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.mitarbeiter.dauer_aehnlicher_aufgaben')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.mitarbeiter.dauer_untersucht_aufg')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', width: '40px', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}></th>
            </tr>
          </thead>
          <tbody>
            {(data.mitarbeiter || []).map((item, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.name || ''}
                    onChange={(e) => {
                      const newArray = [...(data.mitarbeiter || [])];
                      newArray[index] = { ...newArray[index], name: e.target.value };
                      handleFieldChange('mitarbeiter', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.pers_nr || ''}
                    onChange={(e) => {
                      const newArray = [...(data.mitarbeiter || [])];
                      newArray[index] = { ...newArray[index], pers_nr: e.target.value };
                      handleFieldChange('mitarbeiter', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.geschlecht || ''}
                    onChange={(e) => {
                      const newArray = [...(data.mitarbeiter || [])];
                      newArray[index] = { ...newArray[index], geschlecht: e.target.value };
                      handleFieldChange('mitarbeiter', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="number"
                    value={item.alter || ''}
                    onChange={(e) => {
                      const newArray = [...(data.mitarbeiter || [])];
                      newArray[index] = { ...newArray[index], alter: parseInt(e.target.value) || '' };
                      handleFieldChange('mitarbeiter', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.dauer_aehnlicher_aufgaben || ''}
                    onChange={(e) => {
                      const newArray = [...(data.mitarbeiter || [])];
                      newArray[index] = { ...newArray[index], dauer_aehnlicher_aufgaben: e.target.value };
                      handleFieldChange('mitarbeiter', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.dauer_untersucht_aufgabe || ''}
                    onChange={(e) => {
                      const newArray = [...(data.mitarbeiter || [])];
                      newArray[index] = { ...newArray[index], dauer_untersucht_aufgabe: e.target.value };
                      handleFieldChange('mitarbeiter', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      const row = data.mitarbeiter[index];
                      if (isMitarbeiterRowEmpty(row)) {
                        // Leere Zeile: direkt löschen
                        const newArray = [...(data.mitarbeiter || [])];
                        newArray.splice(index, 1);
                        handleFieldChange('mitarbeiter', newArray);
                      } else {
                        // Zeile mit Daten: Sicherheitsabfrage
                        if (setDeleteRowConfirm) {
                          setDeleteRowConfirm({ type: 'mitarbeiter', index, row });
                        } else {
                          // Fallback: direkt löschen, wenn kein Dialog verfügbar
                          const newArray = [...(data.mitarbeiter || [])];
                          newArray.splice(index, 1);
                          handleFieldChange('mitarbeiter', newArray);
                        }
                      }
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={t('vorderseite.delete_row_title')}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={() => {
            const newArray = [...(data.mitarbeiter || [])];
            newArray.push({ name: '', pers_nr: '', geschlecht: '', alter: '', dauer_aehnlicher_aufgaben: '', dauer_untersucht_aufgabe: '' });
            handleFieldChange('mitarbeiter', newArray);
          }}
          style={{
            marginTop: '6px',
            padding: '6px 12px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          + {t('vorderseite.add_row')}
        </button>
      </div>

      {/* Betriebsmittel-Tabelle */}
      <div className="vorderseite-block-betriebsmittel" style={{ marginBottom: '12px' }}>
        <h3 className="vorderseite-heading-betriebsmittel" style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>{t('vorderseite.section.betriebsmittel')}:</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
          <thead>
            <tr>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.betriebsmittel.benennung_type')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.betriebsmittel.anzahl')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.labels.betrieb_nr')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.labels.bj')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', textAlign: 'left', color: darkMode ? '#f9fafb' : '#111827', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}>{t('vorderseite.betriebsmittel.technische_daten_zustand')}</th>
              <th style={{ border: `1px solid ${darkMode ? '#374151' : '#ccc'}`, padding: '6px', width: '40px', backgroundColor: darkMode ? '#374151' : '#f9fafb' }}></th>
            </tr>
          </thead>
          <tbody>
            {(data.betriebsmittel || []).map((item, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.bennenung_type || ''}
                    onChange={(e) => {
                      const newArray = [...(data.betriebsmittel || [])];
                      newArray[index] = { ...newArray[index], bennenung_type: e.target.value };
                      handleFieldChange('betriebsmittel', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="number"
                    value={item.anzahl || ''}
                    onChange={(e) => {
                      const newArray = [...(data.betriebsmittel || [])];
                      newArray[index] = { ...newArray[index], anzahl: parseInt(e.target.value) || '' };
                      handleFieldChange('betriebsmittel', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.betrieb_nr || ''}
                    onChange={(e) => {
                      const newArray = [...(data.betriebsmittel || [])];
                      newArray[index] = { ...newArray[index], betrieb_nr: e.target.value };
                      handleFieldChange('betriebsmittel', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="number"
                    value={item.baujahr || ''}
                    onChange={(e) => {
                      const newArray = [...(data.betriebsmittel || [])];
                      newArray[index] = { ...newArray[index], baujahr: parseInt(e.target.value) || '' };
                      handleFieldChange('betriebsmittel', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                  <input
                    type="text"
                    value={item.technische_daten_zustand || ''}
                    onChange={(e) => {
                      const newArray = [...(data.betriebsmittel || [])];
                      newArray[index] = { ...newArray[index], technische_daten_zustand: e.target.value };
                      handleFieldChange('betriebsmittel', newArray);
                    }}
                    style={{ width: '100%', padding: '4px', border: 'none' }}
                  />
                </td>
                <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      const row = data.betriebsmittel[index];
                      if (isBetriebsmittelRowEmpty(row)) {
                        // Leere Zeile: direkt löschen
                        const newArray = [...(data.betriebsmittel || [])];
                        newArray.splice(index, 1);
                        handleFieldChange('betriebsmittel', newArray);
                      } else {
                        // Zeile mit Daten: Sicherheitsabfrage
                        if (setDeleteRowConfirm) {
                          setDeleteRowConfirm({ type: 'betriebsmittel', index, row });
                        } else {
                          // Fallback: direkt löschen, wenn kein Dialog verfügbar
                          const newArray = [...(data.betriebsmittel || [])];
                          newArray.splice(index, 1);
                          handleFieldChange('betriebsmittel', newArray);
                        }
                      }
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={t('vorderseite.delete_row_title')}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          onClick={() => {
            const newArray = [...(data.betriebsmittel || [])];
            newArray.push({ bennenung_type: '', anzahl: '', betrieb_nr: '', baujahr: '', technische_daten_zustand: '' });
            handleFieldChange('betriebsmittel', newArray);
          }}
          style={{
            marginTop: '6px',
            padding: '6px 12px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          + {t('vorderseite.add_row')}
        </button>
      </div>

      {/* Weitere Felder */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Umgebungseinflüsse:</label>
            <textarea
              value={data.umgebungseinfluesse || ''}
              onChange={(e) => handleFieldChange('umgebungseinfluesse', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '48px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Entlohnung:</label>
            <input
              type="text"
              value={data.entlohnung || ''}
              onChange={(e) => handleFieldChange('entlohnung', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Bemerkung / Grund der Zeitaufnahme:</label>
            <textarea
              value={data.bemerkung || ''}
              onChange={(e) => handleFieldChange('bemerkung', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '48px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Quell-DB:</label>
            <input
              type="text"
              value={data.bemerkung_quell_db || ''}
              onChange={(e) => handleFieldChange('bemerkung_quell_db', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
        </div>
        <div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Qualität des Arbeitsergebnisses:</label>
            <input
              type="text"
              value={data.qualitaet || ''}
              onChange={(e) => handleFieldChange('qualitaet', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Bearbeitungsdatum:</label>
            <input
              type="text"
              value={data.bearbeitungsdatum || ''}
              onChange={(e) => handleFieldChange('bearbeitungsdatum', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>gültig ab:</label>
            <input
              type="text"
              value={data.gueltig_ab || ''}
              onChange={(e) => handleFieldChange('gueltig_ab', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>bis:</label>
            <input
              type="text"
              value={data.gueltig_bis || ''}
              onChange={(e) => handleFieldChange('gueltig_bis', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>Bearbeiter:</label>
            <input
              type="text"
              value={data.bearbeiter || ''}
              onChange={(e) => handleFieldChange('bearbeiter', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827' }}>geprüft:</label>
            <input
              type="text"
              value={data.geprueft || ''}
              onChange={(e) => handleFieldChange('geprueft', e.target.value)}
              style={{ width: '100%', padding: '4px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
        </div>
      </div>

      {/* Notiz-Feld am Ende */}
      <div style={{ marginBottom: '16px', marginTop: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gridTemplateRows: 'auto auto', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontWeight: 'bold', color: darkMode ? '#f9fafb' : '#111827', fontSize: `${12 * scale}px`, gridColumn: hideSpeechButton ? '1 / -1' : undefined }}>Notiz:</label>
          {!hideSpeechButton && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              tabIndex={speechTabIndex}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && !effectiveListeningRef.current) {
                  e.preventDefault();
                  handleSpeechButtonToggle();
                }
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSpeechButtonToggle();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                if (recognitionEdit && effectiveListeningRef.current) {
                  try {
                    recognitionEdit.stop();
                  } catch (error) {
                    // Ignoriere Fehler beim Stoppen
                  }
                  effectiveListeningRef.current = false;
                  setIsListeningVorderseiteNotiz(false);
                }
              }}
              onMouseLeave={(e) => {
                if (recognitionEdit && effectiveListeningRef.current) {
                  try {
                    recognitionEdit.stop();
                  } catch (error) {
                    // Ignoriere Fehler beim Stoppen
                  }
                  effectiveListeningRef.current = false;
                  setIsListeningVorderseiteNotiz(false);
                }
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                if (!recognitionEdit || !isSpeechRecognitionSupported) return;
                if (!effectiveListeningRef.current) {
                  try {
                    effectiveListeningRef.current = true;
                    setIsListeningVorderseiteNotiz(true);
                    recognitionEdit.start();
                  } catch (error) {
                    console.error('[Speech Recognition] Fehler beim Starten:', error);
                    effectiveListeningRef.current = false;
                    setIsListeningVorderseiteNotiz(false);
                  }
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                if (recognitionEdit && effectiveListeningRef.current) {
                  try {
                    recognitionEdit.stop();
                  } catch (error) {
                    // Ignoriere Fehler beim Stoppen
                  }
                  effectiveListeningRef.current = false;
                  setIsListeningVorderseiteNotiz(false);
                }
              }}
              disabled={!recognitionEdit || !isSpeechRecognitionSupported}
              className={`btn btn-voice ${isListeningVorderseiteNotiz ? 'listening' : ''}`}
              title={isSpeechRecognitionSupported 
                ? "Sprach-Eingabe (gedrückt halten)"
                : "Sprachsteuerung wird in diesem Browser nicht unterstützt. Bitte verwenden Sie Chrome oder Edge."}
              style={{
                padding: '6px 10px',
                fontSize: `${12 * scale}px`,
                background: isListeningVorderseiteNotiz ? (darkMode ? '#ef4444' : '#dc2626') : (darkMode ? '#374151' : '#f3f4f6'),
                color: darkMode ? '#f9fafb' : '#111827',
                border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
                borderRadius: '6px',
                cursor: (!recognitionEdit || !isSpeechRecognitionSupported) ? 'not-allowed' : 'pointer',
                opacity: (!recognitionEdit || !isSpeechRecognitionSupported) ? 0.5 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {isListeningVorderseiteNotiz ? '🎤 Aufnahme...' : '🎤 Sprache'}
            </button>
          </div>
          )}
          <textarea
            value={data.notiz || ''}
            onChange={(e) => handleFieldChange('notiz', e.target.value)}
            tabIndex={notizTabIndex}
            onBlur={() => {
              // Kein focus() hier – Tab-Reihenfolge nicht stören; onChange speichert bereits
            }}
            placeholder={t('common.notiz_placeholder_short')}
            rows="6"
            style={{
              gridColumn: '1 / -1',
              width: '100%',
              padding: '8px',
              border: `2px solid ${darkMode ? '#4b5563' : '#d1d5db'}`,
              borderRadius: '6px',
              fontSize: `${13 * scale}px`,
              background: darkMode ? '#374151' : 'white',
              color: darkMode ? '#f9fafb' : '#111827',
              boxSizing: 'border-box',
              minHeight: '120px',
              resize: 'vertical',
              minWidth: 0,
              maxWidth: '100%'
            }}
          />
        </div>
      </div>

      {/* Speichern-Button */}
      {!hideButtons && (
        <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
          <button
            onClick={onSave}
            disabled={isSaving}
            tabIndex={saveTabIndex}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: 'bold',
              backgroundColor: isSaving ? '#9ca3af' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {isSaving ? t('common.saving') : t('common.vorderseite_save')}
          </button>
        </div>
      )}
    </div>
  );
};

export default App;
